import { initializeApp } from "firebase/app";
import { createAppCheckFetch, initializeWebAppCheck } from "../../admin_shared/appCheck.js";
import { renderShdeedBrand, renderCloudPixelEndorsement } from "../../admin_shared/brand.js";
import { publicPrimaryLeader, type PublicPrimaryLeader } from "@shdeed/foundation";
import { openSupportImage, uploadSupportImages } from "../../admin_shared/supportAttachments.js";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { watchOperationalModeWorkspace } from "./operationalModeSubscription.js";
import {
  watchOrganizationAccess,
  chooseAuthorizedOrganization,
  canOpenOrganizationPage,
  type OrganizationAccessState,
} from "./organizationAccess.js";
import { createTenantListenerScope } from "./tenantListenerScope.js";
import { createRealtimeRenderScheduler } from "./realtimeRender.js";
import { loadedRamadanId, watchOptionalRamadanConfig } from "./ramadanReadContext.js";
import {
  buildEventEmailTemplate,
  eventDeliveryFeedback,
  eventEmailCopy,
  resolveEventEmailDraft,
  type EventEmailDraft,
} from "./eventEmailEditor.js";
import { renderEventEmailHtml } from "../../../packages/foundation/src/eventEmailTemplate.js";
import {
  collection,
  collectionGroup,
  connectFirestoreEmulator,
  deleteDoc,
  doc,
  getDoc,
  initializeFirestore,
  increment,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import {
  connectStorageEmulator,
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";

import {
  demoAnnouncements,
  demoBusinessDirectoryBusinesses,
  developmentPassword,
  directionForLocale,
  demoCommunityEvents,
  demoFoundingOrganizationSlots,
  demoIftarSponsorshipDays,
  demoIftarSponsorshipDefaults,
  demoIftarSponsorshipSlots,
  demoOrganizationCommerceConfigurations,
  demoOrganizationBusinessAffiliationProjections,
  demoOrganizationContents,
  demoOrganizationEntitlementOverrides,
  demoOrganizationIntelligenceSnapshots,
  demoOrganizationPlanCatalog,
  demoOrganizationSubscriptionConfigs,
  demoOrganizationUsageLimitSnapshots,
  demoRamadanModes,
  demoRamadanSetups,
  ramadanIftarWeekdays,
  isOrganizationContentTypeId,
  isRamadanPrayerId,
  isRamadanSetupStatus,
  memberSupportTicketCategories,
  buildOrganizationEventId,
  normalizeYouTubeVideoUrl,
  organizationEventSchemaVersion,
  ramadanAdminNoteCategories,
  demoSpaceAvailabilitySlots,
  demoSpaces,
  shdeedSupportTicketCategories,
  demoVolunteerOpportunities,
  demoVolunteerShifts,
  supportAttachmentMimeTypes,
  supportTicketPriorities,
  supportTicketStatuses,
  approvedIslamicComponentDefinitions,
  islamicComponentIds,
  organizationEntitlementIds,
  publicOrganizationProfiles,
  seedOrganizationCampuses,
  seedOrganizations,
  seedPeople,
  organizationRolePermissionMap,
  organizationRoleIds,
  ensureCurrentPersonViaHttp,
  globalPersonFromApiResponse,
  PersonIdentityError,
  personIdentityErrorMessage,
  resolveWorkspaceRouteId,
  shellCopy,
  type GlobalPerson,
  type HijriDateDisplayPreference,
  type IslamicComponentId,
  type LocaleCode,
  type OrganizationContentMediaReference,
  type OrganizationContentYoutubeReference,
  type OrganizationRoleId,
  type RamadanCampusPrayerConfiguration,
  type RamadanGuestImamConfiguration,
  type RamadanIftarCapacityByWeekday,
  type RamadanIftarWeekday,
  type RamadanIqamaPhaseConfiguration,
  type RamadanLastTenNightsConfiguration,
  type RamadanLocalizedText,
  type RamadanAdminNoteCategory,
  type RamadanAdminNoteStatus,
  type RamadanQiyamCampusConfiguration,
  type RamadanSetupStatus,
  type RamadanTahajjudNightConfiguration,
  type RamadanTaraweehCampusConfiguration,
  type SupportTicketPriority,
  type SupportTicketStatus,
  type SpaceOperatingHours,
  type WorkspaceRouteDefinition,
} from "../../../packages/foundation/src/index.js";

import {
  connectionRequestMatchesFilter,
  isConnectionRequestFilter,
  noConnectionRequestsForFilterMessage,
  noConnectionRequestsMessage,
  requestStatusClass,
  requestStatusLabel,
  summaryLabels,
  summarizeConnectionRequests,
  type ConnectionRequestFilter,
  type ConnectionRequestRow,
} from "./connectionRequests.js";
import {
  evaluateConnectionDecisionInput,
  type ConnectionDecisionInput,
} from "./connectionDecisionManager.js";
import {
  buildDefaultIftarSlotEditorInput,
  buildEmptyIftarSlotEditorInput,
  buildIftarSlotEditorInputFromFixture,
  evaluateIftarSlotEditorInput,
  type IftarSlotEditorInput,
} from "./iftarSlotEditor.js";
import {
  peopleRowManualSponsorLabel,
  peopleRowPersonLabel,
  peopleRowSearchText,
  renderPeopleProfileMeta,
  type PersonProfilePresentationRow,
} from "./peopleManagerPresenter.js";
import { watchOrganizationPeople } from "./organizationPeople.js";
import {
  approvedDailyAyahOptions,
  approvedDailyDuaOptions,
  approvedDailyHadithOptions,
  islamicComponentLabel,
  organizationIslamicComponentsEditorInputForOrganization,
  organizationIslamicComponentsEditorInputFromRow,
  organizationIslamicComponentsRequestFromEditor,
  organizationIslamicComponentsRowFromSnapshot,
  platformIslamicComponentRowFromSnapshotData,
  type OrganizationIslamicComponentsEditorInput,
  type OrganizationIslamicComponentsRow,
  type PlatformIslamicComponentRow,
} from "./islamicComponentSettings.js";
import {
  buildAnnouncementComposerInputFromFixture,
  buildDefaultAnnouncementComposerInput,
  buildEmptyAnnouncementComposerInput,
  evaluateAnnouncementComposerInput,
  type AnnouncementComposerInput,
} from "./announcementComposer.js";
import {
  buildDefaultImamContentEditorInput,
  buildEmptyImamContentEditorInput,
  buildImamContentEditorInputFromContent,
  evaluateImamContentEditorInput,
  type ImamContentEditorInput,
} from "./imamContentEditor.js";
import {
  buildDefaultEventEditorInput,
  buildEventEditorInputFromFixture,
  defaultEventEmailDeliveryTypeForEventType,
  eventWizardStepIdsForEventType,
  evaluateEventEditorInput,
  formatDateTimeLocalValue,
  fridayProgramDefaultRange,
  type EventCommunicationDeliveryType,
  type EventEditorInput,
  type EventEditorWizardStepId,
} from "./eventEditor.js";
import {
  buildDefaultRamadanModeEditorInput,
  buildEmptyRamadanModeEditorInput,
  buildRamadanModeEditorInputFromFixture,
  evaluateRamadanModeEditorInput,
  type RamadanModeEditorInput,
} from "./ramadanModeEditor.js";
import {
  buildDefaultOrganizationProfileEditorInput,
  buildEmptyOrganizationProfileEditorInput,
  buildOrganizationProfileEditorInputFromFixture,
  evaluateOrganizationProfileEditorInput,
  organizationPublicLeaderFromEditor,
  formatOrganizationProfilePhone,
  organizationCountryOptions,
  organizationRegionOptions,
  organizationTimeZoneOptions,
  type OrganizationProfileEditorDraft,
  type OrganizationProfileEditorInput,
  type OrganizationProfileSelectOption,
} from "./organizationProfileEditor.js";
import {
  buildDefaultSpaceEditorInput,
  buildEmptySpaceEditorInput,
  buildSpaceEditorInputFromFixture,
  evaluateSpaceEditorInput,
  spaceEditorStatusOptions,
  spaceEditorTypeOptions,
  spaceEditorVisibilityOptions,
  type SpaceEditorInput,
} from "./spaceEditor.js";
import {
  buildDefaultSpaceSlotEditorInput,
  buildEmptySpaceSlotEditorInput,
  buildSpaceSlotEditorInputFromFixture,
  evaluateSpaceSlotEditorInput,
  type SpaceSlotEditorInput,
} from "./spaceSlotEditor.js";
import {
  buildDefaultSpaceAvailabilityBlockEditorInput,
  buildEmptySpaceAvailabilityBlockEditorInput,
  evaluateSpaceAvailabilityBlockEditorInput,
  spaceAvailabilityBlockTypeOptions,
  type SpaceAvailabilityBlockEditorInput,
} from "./spaceAvailabilityBlockEditor.js";
import { resolveOrgAdminEnvironmentConfig } from "./environment.js";
import {
  authErrorText,
  authenticationStateFromUser,
  createFirebaseWebAuthenticationService,
  isDevelopmentOnlyAuthenticationUser,
  toAuthenticationError,
  type WebAuthenticationState,
} from "./authentication.js";
import "@carbon/styles/css/styles.css";
import "./styles.css";
import "../../admin_shared/workspacePolish.css";
import { enhanceArabicTranslation } from "./arabicTranslation.js";
import {
  bindFormValidationFeedback,
  createFormSaveLifecycle,
  isFormInteractionActive,
} from "./formLifecycle.js";
import { enhanceAdminWorkspace } from "../../admin_shared/workspacePolish.js";
import { adminFeedback } from "../../admin_shared/workspaceFeedback.js";
import {
  buildModeSaveRequest,
  canEditMode,
  localModeDate,
  modeActions,
  renderModeWorkspace,
  type ModeAction,
  type OperationalModeEditorInput,
  type OperationalModeRow,
} from "./operationalModeEditor.js";

const environmentConfig = (() => {
  try {
    return resolveOrgAdminEnvironmentConfig(import.meta.env);
  } catch (error) {
    return renderStartupConfigurationError("Organization Admin Portal", error);
  }
})();
const projectId = environmentConfig.firebase.projectId;
const useEmulator = environmentConfig.firebase.useEmulators;
const release1OrganizationIntelligenceEnabled = false;

const app = initializeApp({
  apiKey: environmentConfig.firebase.apiKey,
  appId: environmentConfig.firebase.appId,
  authDomain: environmentConfig.firebase.authDomain,
  projectId,
  storageBucket: environmentConfig.firebase.storageBucket,
});

const backendFetch = (() => {
  try {
    return createAppCheckFetch({
      apiBaseUrl: environmentConfig.apiBaseUrl,
      projectId,
      useEmulators: useEmulator,
      mode: environmentConfig.appCheck.mode,
      getToken: initializeWebAppCheck(app, environmentConfig.appCheck),
      onUnavailable: () => console.warn("app_check_token_unavailable"),
    });
  } catch (error) {
    return renderStartupConfigurationError("Organization Admin Portal", error);
  }
})();
const auth = getAuth(app);
const authenticationService = createFirebaseWebAuthenticationService(
  auth,
  environmentConfig.authProviders,
);
const db = initializeFirestore(
  app,
  useEmulator
    ? {
        experimentalForceLongPolling: true,
      }
    : {},
);
const storage = getStorage(app);

if (useEmulator) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}

function renderStartupConfigurationError(productName: string, error: unknown): never {
  const message = error instanceof Error ? error.message : "Unknown startup configuration error.";
  const appRoot = document.querySelector<HTMLElement>("#app");
  if (appRoot) {
    appRoot.innerHTML = `
      <section style="box-sizing: border-box; min-height: 100vh; padding: 48px 20px; background: #f4f4f4; color: #161616; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
        <article style="max-width: 720px; margin: 0 auto; padding: 28px; border-left: 4px solid #da1e28; background: #ffffff; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);">
          <p style="margin: 0 0 8px; color: #525252; font-size: 0.875rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;">Shdeed startup check</p>
          <h1 style="margin: 0 0 12px; font-size: clamp(1.75rem, 4vw, 2.5rem); line-height: 1.15;">${escapeHtml(productName)} needs a valid build configuration.</h1>
          <p style="margin: 0 0 16px; color: #393939; font-size: 1rem; line-height: 1.6;">The app could not start because its public Firebase/environment configuration was not included in the deployed bundle.</p>
          <pre style="overflow: auto; margin: 0; padding: 16px; border: 1px solid #e0e0e0; background: #f4f4f4; color: #a2191f; white-space: pre-wrap;">${escapeHtml(message)}</pre>
        </article>
      </section>
    `;
  }
  throw error;
}

type ShellState = {
  busy: boolean;
  announcementAcknowledgements: AnnouncementAcknowledgementRow[];
  announcementComposer: AnnouncementComposerInput;
  announcementComposerEditingId: string | null;
  announcements: AnnouncementRow[];
  commerceMessage: string;
  commerceStatus: CommerceStatusRow | undefined;
  connectionRequestFilter: ConnectionRequestFilter;
  connectionRequests: ConnectionRequestRow[];
  connectionDecisions: ConnectionDecisionRequestRow[];
  donations: DonationIntentRow[];
  donationRefunds: DonationRefundRow[];
  reconciliationMismatches: CommerceReconciliationMismatchRow[];
  reconciliationRuns: CommerceReconciliationRunRow[];
  givingFunds: GivingFundRow[];
  campaigns: CampaignRow[];
  billingError: string;
  businessAffiliations: OrganizationBusinessAffiliationRow[];
  businessDirectory: BusinessDirectoryRow[];
  businessDirectoryMessage: string;
  engagementDeliveryLogs: EngagementDeliveryLogRow[];
  eventEditor: EventEditorInput;
  eventEditorError: string;
  eventEditorMessage: string;
  eventEmailDeliveryType: EventCommunicationDeliveryType;
  eventEmailTestAddress: string;
  eventEmailDraft: EventEmailDraft;
  eventEmailTestStatus: { message: string; error: boolean } | null;
  eventImageFile: File | undefined;
  eventImageFiles: File[];
  eventMedia: EventMediaRow[];
  eventMediaMessage: string;
  eventPendingMediaPreviews: EventPendingMediaPreview[];
  eventVideoFile: File | undefined;
  eventYoutubeUrl: string;
  eventAttendance: EventAttendanceRow[];
  eventCheckInCredential: string;
  eventCheckInMessage: string;
  eventCheckInStationLabel: string;
  eventManualRegistrationSearch: string;
  eventRegistrations: EventRegistrationRow[];
  events: CommunityEventRow[];
  eventWizardOpen: boolean;
  eventWizardStepIndex: number;
  error: string;
  engagementDetailMessage: string;
  imamContentEditor: ImamContentEditorInput;
  imamContentGalleryImageFiles: File[];
  imamContentImageFile: File | undefined;
  imamContentImageDownloadUrls: Record<string, string | null>;
  imamContents: ImamContentRow[];
  iftarAttendanceDefaults: IftarAttendanceDefaultsRow | undefined;
  iftarAttendanceDefaultsEditor: IftarAttendanceDefaultsEditorInput;
  iftarAttendanceDays: IftarAttendanceDayRow[];
  iftarAttendanceMessage: string;
  iftarAttendanceRegistrations: IftarAttendanceRegistrationRow[];
  iftarSponsorshipDays: IftarSponsorshipDayRow[];
  iftarSponsorshipDayEditor: IftarSponsorshipDayEditorInput;
  iftarSponsorshipDayEditorMode: "edit" | "view";
  iftarSponsorshipDefaults: IftarSponsorshipDefaultsRow | undefined;
  iftarSponsorshipDefaultsEditor: IftarSponsorshipDefaultsEditorInput;
  iftarSponsorshipDayFilter: string;
  iftarSponsorshipCloseNote: string;
  iftarSponsorshipClosingDayId: string;
  iftarSponsorshipFinance: IftarSponsorshipFinanceRow[];
  iftarSponsorshipManualEditor: ManualIftarSponsorshipEditorInput;
  iftarSponsorshipDeletePromptId: string;
  iftarSponsorshipEditPromptId: string;
  iftarSponsorshipMessage: string;
  iftarSponsorshipSearch: string;
  iftarSponsorshipStatusFilter: string;
  iftarSponsorshipTransferPromptId: string;
  iftarTransfers: IftarTransferRow[];
  iftarSlotEditor: IftarSlotEditorInput;
  iftarSlots: IftarSponsorshipSlotRow[];
  intelligenceRequests: IntelligenceRequestRow[];
  intelligenceSnapshots: IntelligenceSnapshotRow[];
  locale: LocaleCode;
  organizationConnections: OrganizationConnectionRow[];
  organizationEntitlementOverrides: EntitlementOverrideRow[];
  operationalModeEditor: OperationalModeEditorInput;
  operationalModeError: string;
  operationalModeMessage: string;
  operationalModeProjection: OperationalModeProjectionRow | undefined;
  operationalModes: OperationalModeRow[];
  organizationPlanCatalog: OrganizationPlanRow[];
  organizationProfile: OrganizationProfileRow | undefined;
  organizationProfileEditor: OrganizationProfileEditorInput;
  organizationProfileLogoDownloadPath: string | null;
  organizationProfileLogoDownloadUrl: string | null;
  organizationProfileLogoError: string;
  organizationProfileLogoFile: File | undefined;
  organizationProfileLogoPreviewUrl: string | null;
  organizationProfileMessage: string;
  organizationProfileRequests: OrganizationProfileUpdateRequestRow[];
  organizationIslamicComponents: OrganizationIslamicComponentsRow | undefined;
  organizationIslamicComponentsEditor: OrganizationIslamicComponentsEditorInput;
  organizationIslamicComponentsMessage: string;
  organizationPrayerSettings: OrganizationPrayerSettingsRow | undefined;
  organizationPrayerSettingsEditor: OrganizationPrayerSettingsEditorInput;
  organizationPrayerSettingsMessage: string;
  organizationId: string;
  organizationRoleAssignments: OrganizationRoleAssignmentRow[];
  personProfiles: Record<string, PersonProfileRow | null | undefined>;
  memberSupportAssignmentFilter: SupportAssignmentFilter;
  memberSupportCategoryFilter: string;
  memberSupportError: string;
  memberSupportMessages: SupportTicketMessageRow[];
  memberSupportMessage: string;
  memberSupportPriorityFilter: SupportPriorityFilter;
  memberSupportSearch: string;
  memberSupportSelectedTicketId: string;
  memberSupportStatusFilter: SupportStatusFilter;
  memberSupportTickets: MemberSupportTicketRow[];
  peopleListSearch: string;
  peopleManagerSelectedPersonId: string;
  roleManagementMessage: string;
  organizationSubscription: OrganizationSubscriptionRow | undefined;
  organizationUsage: UsageLimitRow | undefined;
  platformIslamicComponents: PlatformIslamicComponentRow[];
  foundingSlot: FoundingSlotRow | undefined;
  ramadanModeEditor: RamadanModeEditorInput;
  ramadanModes: RamadanModeRow[];
  ramadanAdminNoteCategoryFilter: RamadanAdminNoteCategory | "all";
  ramadanAdminNoteDayFilter: string;
  ramadanAdminNoteEditor: RamadanAdminNoteEditorInput;
  ramadanAdminNoteMessage: string;
  ramadanAdminNotes: RamadanAdminNoteRow[];
  ramadanAdminNoteStatusFilter: RamadanAdminNoteStatus | "all";
  ramadanSetupEditor: RamadanSetupEditorInput;
  ramadanSetupEditConfirmationPending: boolean;
  ramadanHistory: RamadanHistoryRow[];
  ramadanSetupMessage: string;
  ramadanSetups: RamadanSetupRow[];
  spaceEditor: SpaceEditorInput;
  spaceAvailabilityBlockEditor: SpaceAvailabilityBlockEditorInput;
  spaceAvailabilityBlocks: SpaceAvailabilityBlockRow[];
  spaceReservationRequests: SpaceReservationRequestRow[];
  spaceReservations: SpaceReservationIntentRow[];
  spaceSlotEditor: SpaceSlotEditorInput;
  spaceSlots: SpaceAvailabilitySlotRow[];
  spaces: SpaceRow[];
  shdeedSupportAssignmentFilter: SupportAssignmentFilter;
  shdeedSupportCategoryFilter: string;
  shdeedSupportError: string;
  shdeedSupportMessages: SupportTicketMessageRow[];
  shdeedSupportMessage: string;
  shdeedSupportPriorityFilter: SupportPriorityFilter;
  shdeedSupportSearch: string;
  shdeedSupportSelectedTicketId: string;
  shdeedSupportStatusFilter: SupportStatusFilter;
  shdeedSupportTickets: ShdeedSupportTicketRow[];
  sponsorships: IftarSponsorshipAdminRow[];
  status: string;
  timelineEntries: TimelineEntryRow[];
  version: string;
  volunteerOpportunities: VolunteerOpportunityRow[];
  volunteerOpportunityEditor: VolunteerOpportunityEditorInput;
  volunteerShiftEditor: VolunteerShiftEditorInput;
  volunteerShifts: VolunteerShiftRow[];
  volunteerSignups: VolunteerSignupRow[];
  volunteerAttendances: VolunteerAttendanceRow[];
  volunteerRecognitionAwards: VolunteerRecognitionAwardRow[];
  volunteerServiceSummaries: VolunteerServiceSummaryRow[];
  userUid: string;
};

type EventPendingMediaPreview = {
  kind: "image" | "video";
  name: string;
  size: number;
  type: string;
  url: string;
};

type AuthenticationFormState = {
  busy: boolean;
  email: string;
  error: string;
  password: string;
  resetSent: boolean;
};

type EventTableSortKey =
  "attendance" | "location" | "registration" | "startsAt" | "status" | "title";

type EventLifecycleAction = "delete" | "delist";

type EventWizardStepId = EventEditorWizardStepId;

type CurrentPersonState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "resolved"; person: GlobalPerson }
  | { status: "error"; message: string };

type AccountProfileEditorState = {
  busy: boolean;
  displayName: string;
  error: string;
  message: string;
  phoneNumber: string;
  photoUrl: string;
};

type CommunityEventRow = {
  emailRequestedOnPublish?: boolean;
  publishEmailSubject?: string;
  publishEmailMessage?: string;
  archivedAt: Date | undefined;
  campusId: string | null;
  campusNameSnapshot: string | null;
  coverMediaId: string | null;
  description: string;
  descriptionAr: string | null;
  endsAt: Date | undefined;
  eventId: string;
  eventType: "standard" | "friday_program" | "janazah";
  id: string;
  imageAltText: string | null;
  imageAltTextAr: string | null;
  imageStoragePath: string | null;
  addressLine1: string | null;
  locationType: string | null;
  locationName: string;
  onlineUrl: string | null;
  registrationMode: string;
  notificationIntentId: string | null;
  notificationIntentStatus: string;
  notificationRequestedOnPublish: boolean;
  publishedAt: Date | undefined;
  scheduledPublishAt: Date | undefined;
  registrationCancellationAllowed: boolean;
  guestRegistrationEnabled: boolean;
  dependentRegistrationEnabled: boolean;
  registrationCapacity: number;
  registrationClosesAt: Date | undefined;
  registrationConfirmedCount: number;
  registrationEnabled: boolean;
  registrationOpensAt: Date | undefined;
  registrationQuestionCount: number;
  reminderPresets: string[];
  waitlistCount: number;
  waitlistEnabled: boolean;
  waiverRequired: boolean;
  attendanceCheckedInCount: number;
  startsAt: Date | undefined;
  status: string;
  summary: string;
  summaryAr: string | null;
  timezone: string;
  title: string;
  titleAr: string | null;
  updatedBy: string;
};

type VolunteerOpportunityRow = {
  archivedAt: Date | undefined;
  campusId: string | null;
  category: string;
  completedAt: Date | undefined;
  description: string;
  descriptionAr: string | null;
  endsAt: Date | undefined;
  id: string;
  imageAltText: string | null;
  imageAltTextAr: string | null;
  imageStoragePath: string | null;
  instructions: string;
  instructionsAr: string | null;
  locationName: string;
  opportunityId: string;
  publishedAt: Date | undefined;
  requirementAgePolicy: string | null;
  requirementDressCode: string | null;
  requirementPhysicalNotes: string | null;
  requirementTrainingRequired: boolean;
  startsAt: Date | undefined;
  status: string;
  title: string;
  titleAr: string | null;
  updatedBy: string;
  visibility: string;
};

type VolunteerShiftRow = {
  capacity: number;
  confirmedCount: number;
  endsAt: Date | undefined;
  id: string;
  instructions: string | null;
  instructionsAr: string | null;
  notes: string | null;
  notesAr: string | null;
  opportunityId: string;
  shiftId: string;
  startsAt: Date | undefined;
  status: string;
  timezone: string;
};

type VolunteerSignupRow = {
  cancelledAt: Date | undefined;
  cancellationActorType: string | null;
  cancelledBy: string | null;
  opportunityId: string;
  opportunityTitleSnapshot: string;
  personId: string;
  shiftEndsAt: Date | undefined;
  shiftId: string;
  shiftStartsAt: Date | undefined;
  signedUpAt: Date | undefined;
  signupId: string;
  status: string;
};

type VolunteerAttendanceRow = {
  attendanceId: string;
  checkedInAt: Date | undefined;
  checkedOutAt: Date | undefined;
  checkInMethod: string;
  checkOutMethod: string | null;
  opportunityId: string;
  personId: string;
  provisionalMinutes: number;
  shiftId: string;
  status: string;
  verifiedMinutes: number;
};

type VolunteerServiceSummaryRow = {
  lastVerifiedAt: Date | undefined;
  personId: string;
  totalVerifiedMinutes: number;
  verifiedAttendanceCount: number;
};

type VolunteerRecognitionAwardRow = {
  awardId: string;
  label: string;
  labelAr: string;
  personId: string;
  thresholdMinutes: number;
  verifiedMinutesAtAward: number;
};

type VolunteerOpportunityEditorInput = {
  opportunityId: string;
  status: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  instructions: string;
  instructionsAr: string;
  campusId: string;
  locationName: string;
  category: string;
  agePolicy: string;
  physicalNotes: string;
  dressCode: string;
  trainingRequired: string;
  imageStoragePath: string;
  imageAltText: string;
  imageAltTextAr: string;
  startsAt: string;
  endsAt: string;
};

type VolunteerShiftEditorInput = {
  opportunityId: string;
  shiftId: string;
  status: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  capacity: string;
  notes: string;
  notesAr: string;
  instructions: string;
  instructionsAr: string;
};

type EventRegistrationRow = {
  answers: unknown[];
  attendeeCount: number;
  attendees: Array<{
    attendeeId: string;
    attendeeType: string;
    name: string;
    personId: string | null;
  }>;
  cancelledAt: Date | undefined;
  eventId: string;
  id: string;
  organizationId: string;
  personId: string;
  registeredAt: Date | undefined;
  registrationId: string;
  status: string;
  updatedAt: Date | undefined;
};

type EventAttendanceRow = {
  attendanceId: string;
  checkedInAt: Date | undefined;
  checkedInBy: string;
  eventId: string;
  id: string;
  method: string;
  organizationId: string;
  personId: string;
  registrationId: string;
  stationLabel: string | null;
  status: string;
  updatedAt: Date | undefined;
};

type EventMediaRow = {
  altText: string | null;
  altTextAr: string | null;
  canonicalUrl: string | null;
  caption: string | null;
  captionAr: string | null;
  contentType: string | null;
  durationSeconds: number | null;
  eventId: string;
  fileSizeBytes: number | null;
  height: number | null;
  id: string;
  mediaId: string;
  organizationId: string;
  originalUrl: string | null;
  playbackUrl: string | null;
  position: number;
  posterStoragePath: string | null;
  storagePath: string | null;
  thumbnailUrl: string | null;
  title: string | null;
  titleAr: string | null;
  type: "image" | "uploadedVideo" | "youtube";
  updatedAt: Date | undefined;
  updatedBy: string;
  videoId: string | null;
  width: number | null;
};

type SupportAttachmentRow = {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
};

type SupportTicketMessageRow = {
  attachments: SupportAttachmentRow[];
  body: string;
  createdAt: Date | undefined;
  messageId: string;
  senderDisplayName: string;
  senderRole: string;
  senderUid: string;
  ticketId: string;
};

type MemberSupportTicketRow = {
  assignedToPersonId: string;
  category: string;
  createdAt: Date | undefined;
  description: string;
  internalPriority: SupportTicketPriority;
  lastActivityAt: Date | undefined;
  lastMessagePreview: string;
  organizationId: string;
  requesterDisplayName: string;
  requesterEmail: string;
  requesterPersonId: string;
  requesterUid: string;
  requesterUrgency: string;
  status: SupportTicketStatus;
  subject: string;
  ticketId: string;
  ticketNumber: string;
};

type ShdeedSupportTicketRow = {
  assignedToPersonId: string;
  category: string;
  createdAt: Date | undefined;
  description: string;
  internalPriority: SupportTicketPriority;
  lastActivityAt: Date | undefined;
  lastMessagePreview: string;
  organizationId: string;
  organizationNameSnapshot: string;
  requesterAdminDisplayName: string;
  requesterAdminEmail: string;
  requesterAdminPersonId: string;
  requesterUrgency: string;
  status: SupportTicketStatus;
  subject: string;
  ticketId: string;
  ticketNumber: string;
};

type SupportStatusFilter = SupportTicketStatus | "all";
type SupportPriorityFilter = SupportTicketPriority | "all";
type SupportAssignmentFilter = "all" | "assigned" | "unassigned" | "mine";

type DonationIntentRow = {
  id: string;
  amountMinor: number;
  destinationLabel: string;
  createdAt: Date | undefined;
  personId: string;
  providerPaymentIntentId: string;
  receiptId: string | null;
  refundedAmountMinor: number;
  refundPendingAmountMinor: number;
  refundStatus: string;
  remainingRefundableAmountMinor: number;
  status: string;
};

type DonationRefundRow = {
  id: string;
  amountMinor: number;
  createdAt: Date | undefined;
  originalTransactionId: string;
  personId: string;
  providerRefundId: string | null;
  status: string;
};

type CommerceReconciliationRunRow = {
  id: string;
  checkedTransactionCount: number;
  createdAt: Date | undefined;
  mismatchCount: number;
  requestedBy: string;
  status: string;
  targetTransactionId: string | null;
};

type CommerceReconciliationMismatchRow = {
  id: string;
  detectedAt: Date | undefined;
  providerPaymentIntentId: string | null;
  runId: string;
  safeDiagnosticCode: string;
  status: string;
  transactionId: string | null;
  type: string;
};

type GivingFundRow = {
  confirmedAmountMinor: number;
  confirmedDonationCount: number;
  description: string;
  descriptionAr: string | null;
  displayOrder: number;
  fundId: string;
  id: string;
  name: string;
  nameAr: string | null;
  status: string;
  type: string;
  updatedAt: Date | undefined;
  visibility: string;
};

type CampaignRow = {
  campaignId: string;
  confirmedAmountMinor: number;
  confirmedDonationCount: number;
  description: string;
  descriptionAr: string | null;
  displayOrder: number;
  fundId: string | null;
  goalAmountMinor: number | null;
  id: string;
  status: string;
  title: string;
  titleAr: string | null;
  updatedAt: Date | undefined;
  visibility: string;
};

type CommerceStatusRow = {
  chargesEnabled: boolean;
  currentlyDueCount: number;
  detailsSubmitted: boolean;
  error: string;
  eventuallyDueCount: number;
  lastProviderSyncAt: Date | undefined;
  onboardingStatus: string;
  payoutsEnabled: boolean;
  provider: string;
  readiness: string;
  restrictedReasonCode: string | null;
};

type RamadanModeRow = {
  id: string;
  seasonYear: number;
  slotPriceCents: number;
  status: string;
  summary: string;
  summaryAr: string;
  title: string;
  titleAr: string;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type RamadanSetupRow = {
  id: string;
  campusPrayerConfigs: RamadanCampusPrayerConfiguration[];
  defaultCampusId: string;
  hijriYear: number;
  startsOn: string;
  endsOn: string;
  experienceOpensAt: string | null;
  featuredRamadanContentId: string | null;
  guestImams: RamadanGuestImamConfiguration;
  status: RamadanSetupStatus;
  sponsorshipOpensAt: string | null;
  title: string;
  titleAr: string | null;
  ramadanDay: number | null;
  iqamaPhases: RamadanIqamaPhaseConfiguration[];
  lastTenNights: RamadanLastTenNightsConfiguration;
  lastTenNightsActive: boolean;
  linkedOperationalModeId: string;
  qiyam: RamadanQiyamCampusConfiguration[];
  ramadanId: string;
  tahajjudNights: RamadanTahajjudNightConfiguration[];
  taraweeh: RamadanTaraweehCampusConfiguration[];
  timezone: string;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type OperationalModeProjectionRow = {
  modeId: string | null;
  status: string;
  title: string | null;
  updatedAt: Date | undefined;
  warnings: string[];
};

type RamadanSetupEditorInput = {
  hijriYear: string;
  startsOn: string;
  endsOn: string;
  experienceOpensAt: string;
  sponsorshipOpensAt: string;
  featuredRamadanContentId: string;
  startAdjustmentDays: string;
  endAdjustmentDays: string;
  defaultCampusId: string;
  linkedOperationalModeId: string;
  status: "draft" | "ready";
  title: string;
  titleAr: string;
  message: string;
  messageAr: string;
  timezone: string;
  fajrIqama: string;
  maghribIqama: string;
  ishaIqama: string;
  taraweehRakahCount: string;
  taraweehRelativeToIshaMinutes: string;
  qiyamStartTime: string;
  qiyamStartsOn: string;
  qiyamEndsOn: string;
  lastTenStartsOn: string;
  phase1FajrIqama: string;
  phase1MaghribIqama: string;
  phase1IshaIqama: string;
  phase2FajrIqama: string;
  phase2MaghribIqama: string;
  phase2IshaIqama: string;
  phase3FajrIqama: string;
  phase3MaghribIqama: string;
  phase3IshaIqama: string;
  tahajjudSchedule: string;
  guestImamsEnabled: "true" | "false";
  guestImamProfiles: string;
};

type IftarSponsorshipDefaultsRow = {
  capacityByWeekday: RamadanIftarCapacityByWeekday;
  currency: string;
  defaultPricePerSpotMinor: number;
  fridayCapacitySpots: number;
  programEnabled: boolean;
  publicNotes: string;
  publicNotesAr: string | null;
  saturdayCapacitySpots: number;
  sponsorshipClosesDayOffset: number;
  sponsorshipClosesLocalTime: string;
  sponsorshipOpensAt: string | null;
  standardCapacitySpots: number;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type IftarSponsorshipDayRow = {
  id: string;
  capacitySpots: number;
  confirmedSpots: number;
  currency: string;
  gregorianDate: string;
  iftarDayId: string;
  pricePerSpotMinor: number;
  publicNotes: string | null;
  publicNotesAr: string | null;
  ramadanDay: number;
  reservedSpots: number;
  sponsorshipClosesAt: string;
  sponsorshipEnabled: boolean;
  sponsorshipOpensAt: string | null;
  status: string;
  updatedAt: Date | undefined;
  updatedBy: string;
  weekday: string;
};

type IftarSponsorshipDefaultsEditorInput = {
  ramadanId: string;
  programEnabled: "true" | "false";
  sundayCapacitySpots: string;
  mondayCapacitySpots: string;
  tuesdayCapacitySpots: string;
  wednesdayCapacitySpots: string;
  thursdayCapacitySpots: string;
  fridayCapacitySpots: string;
  saturdayCapacitySpots: string;
  standardCapacitySpots: string;
  defaultPriceDollars: string;
  sponsorshipClosesDayOffset: string;
  sponsorshipClosesLocalTime: string;
  sponsorshipOpensAt: string;
  publicRecognitionAllowed: "true" | "false";
  sponsorDisplayNameAllowed: "true" | "false";
  anonymousPublicRecognitionAllowed: "true" | "false";
  dedicationEnabled: "true" | "false";
  dedicationMaxLength: string;
  dedicationPublicDisplayAllowed: "true" | "false";
  publicNotes: string;
  publicNotesAr: string;
};

type IftarSponsorshipCapacityEditorKey =
  | "sundayCapacitySpots"
  | "mondayCapacitySpots"
  | "tuesdayCapacitySpots"
  | "wednesdayCapacitySpots"
  | "thursdayCapacitySpots"
  | "fridayCapacitySpots"
  | "saturdayCapacitySpots";

const ramadanWeekdayLabels: Record<RamadanIftarWeekday, string> = {
  friday: "Friday",
  monday: "Monday",
  saturday: "Saturday",
  sunday: "Sunday",
  thursday: "Thursday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
};

const iftarCapacityEditorKeysByWeekday: Record<
  RamadanIftarWeekday,
  IftarSponsorshipCapacityEditorKey
> = {
  friday: "fridayCapacitySpots",
  monday: "mondayCapacitySpots",
  saturday: "saturdayCapacitySpots",
  sunday: "sundayCapacitySpots",
  thursday: "thursdayCapacitySpots",
  tuesday: "tuesdayCapacitySpots",
  wednesday: "wednesdayCapacitySpots",
};

type IftarSponsorshipDayEditorInput = {
  iftarDayId: string;
  gregorianDate: string;
  status: "draft" | "open" | "closed";
  sponsorshipEnabled: "true" | "false";
  capacitySpots: string;
  priceDollars: string;
  sponsorshipClosesAt: string;
  sponsorshipOpensAt: string;
  publicNotes: string;
  publicNotesAr: string;
};

type IftarAttendanceDefaultsRow = {
  defaultCapacityPeople: number;
  programEnabled: boolean;
  publicNotes: string;
  publicNotesAr: string | null;
  registrationClosesDayOffset: number;
  registrationClosesLocalTime: string;
  registrationOpensAt: string | null;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type IftarAttendanceDayRow = {
  id: string;
  attendanceCapacityPeople: number;
  attendanceDayId: string;
  attendanceEnabled: boolean;
  confirmedAttendeeCount: number;
  gregorianDate: string;
  publicNotes: string | null;
  publicNotesAr: string | null;
  ramadanDay: number;
  registrationClosesAt: string;
  registrationOpensAt: string | null;
  status: string;
  updatedAt: Date | undefined;
  updatedBy: string;
  weekday: string;
};

type IftarAttendanceRegistrationRow = {
  id: string;
  attendeeCount: number;
  attendanceDayId: string;
  confirmationCode: string;
  gregorianDateSnapshot: string | null;
  personId: string;
  ramadanDaySnapshot: number | null;
  registrationId: string;
  status: string;
  updatedAt: Date | undefined;
};

type RamadanHistoryRow = {
  completedAt: Date | undefined;
  dayCount: number;
  endsOn: string;
  historyId: string;
  hijriYear: number;
  ramadanId: string;
  startsOn: string;
  status: string;
  title: string;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type IftarAttendanceDefaultsEditorInput = {
  ramadanId: string;
  programEnabled: "true" | "false";
  defaultCapacityPeople: string;
  registrationClosesDayOffset: string;
  registrationClosesLocalTime: string;
  registrationOpensAt: string;
  publicNotes: string;
  publicNotesAr: string;
};

type IftarSponsorshipSlotRow = {
  id: string;
  amountCents: number;
  dayNumber: number;
  label: string;
  labelAr: string;
  modeId: string;
  sponsorUid: string;
  status: string;
};

type IftarSponsorshipFeeLineRow = {
  active: boolean;
  amountMinor: number;
  displayOrder: number;
  feeLineId: string;
  label: string;
};

type IftarSponsorshipSpotDetailRow = {
  cuisineId: string | null;
  hostingMethod: string | null;
  note: string | null;
  otherCuisineText: string | null;
  recognitionNames: string[];
  spotId: string;
};

type IftarSponsorshipAdminRow = {
  id: string;
  basePricePerSpotMinorSnapshot: number | null;
  createdAt: Date | undefined;
  dedicationText: string | null;
  financeVerificationStatus: string;
  gregorianDate: string;
  iftarDayId: string;
  organizationFeeLinesSnapshot: IftarSponsorshipFeeLineRow[];
  paymentStatus: string;
  paymentSource: string;
  pricePerSpotMinorSnapshot: number | null;
  publicDisplayNameSnapshot: string | null;
  quantitySpots: number;
  ramadanDay: number;
  ramadanId: string;
  receiptAvailable: boolean;
  recognitionPreference: string;
  reservationStatus: string;
  safeSponsorLabel: string;
  source: string;
  spotDetails: IftarSponsorshipSpotDetailRow[];
  sponsorshipId: string;
  status: string;
  totalPaymentAmountMinor: number | null;
  updatedAt: Date | undefined;
};

type IftarSponsorshipFinanceRow = {
  amountMinor: number;
  createdAt: Date | undefined;
  grossAmountMinor: number;
  iftarDayId: string;
  organizationNetMinor: number;
  ramadanId: string;
  receiptId: string | null;
  refundPendingAmountMinor: number;
  refundStatus: string;
  refundedAmountMinor: number;
  sponsorshipId: string;
  status: string;
  transactionId: string;
};

type IftarTransferRow = {
  completedAt: Date | undefined;
  destinationGregorianDateSnapshot: string;
  destinationIftarDayId: string;
  destinationRamadanDaySnapshot: number;
  requestedAt: Date | undefined;
  requestedByRole: string;
  sourceGregorianDateSnapshot: string;
  sourceIftarDayId: string;
  sourceRamadanDaySnapshot: number;
  sponsorshipId: string;
  spotQuantity: number;
  status: string;
  transferId: string;
  updatedAt: Date | undefined;
};

type RamadanAdminNoteRow = {
  id: string;
  archivedAt: Date | undefined;
  archivedBy: string | null;
  body: string;
  category: RamadanAdminNoteCategory;
  createdAt: Date | undefined;
  createdBy: string;
  editCount: number;
  iftarDayId: string | null;
  noteId: string;
  ramadanId: string;
  sponsorshipId: string | null;
  status: RamadanAdminNoteStatus;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type RamadanAdminNoteEditorInput = {
  body: string;
  category: RamadanAdminNoteCategory;
  iftarDayId: string;
  noteId: string;
  ramadanId: string;
  sponsorshipId: string;
};

type ManualIftarSponsorshipEditorInput = {
  dedicationText: string;
  iftarDayId: string;
  manualPaymentMethod: "cash" | "check" | "external" | "officeCommitment";
  personId: string;
  quantitySpots: string;
  recognitionPreference: "none" | "publicName" | "anonymous";
  sponsorLabel: string;
};

type SpaceRow = {
  id: string;
  accessibilityFeatures: string[];
  amenities: string[];
  campusId: string | null;
  capacity: number;
  description: string;
  descriptionAr: string | null;
  hourlyPriceCents: number | null;
  locationName: string;
  name: string;
  nameAr: string | null;
  operatingHours: SpaceOperatingHours;
  pricingEnabled: boolean;
  pricingType: string;
  pricingVersion: number;
  pricingTotalPreviewCents: number | null;
  primaryImageStoragePath: string | null;
  source: string;
  status: string;
  timezone: string;
  type: string;
  visibility: string;
};

type SpaceAvailabilitySlotRow = {
  id: string;
  amountCents: number;
  endsAt: Date | undefined;
  label: string;
  labelAr: string;
  requesterUid: string;
  spaceId: string;
  startsAt: Date | undefined;
  status: string;
};

type SpaceAvailabilityBlockRow = {
  id: string;
  blockId: string;
  endsAt: Date | undefined;
  internalReason: string | null;
  label: string;
  labelAr: string | null;
  publicReason: boolean;
  spaceId: string;
  startsAt: Date | undefined;
  type: string;
};

type SpaceReservationIntentRow = {
  id: string;
  amountCents: number;
  createdAt: Date | undefined;
  providerEventId: string;
  requesterUid: string;
  slotId: string;
  spaceId: string;
  status: string;
};

type SpaceReservationRequestRow = {
  id: string;
  approvedAt: Date | undefined;
  cancelledAt: Date | undefined;
  endsAt: Date | undefined;
  guestCount: number;
  notes: string | null;
  purpose: string;
  rejectedAt: Date | undefined;
  requesterEmail: string | null;
  requesterName: string;
  requesterPersonId: string;
  reservationId: string;
  paymentStatus: string;
  pricingTotalCents: number | null;
  pricingSnapshotId: string | null;
  receiptId: string | null;
  transactionId: string | null;
  agreementRequired: boolean;
  agreementAccepted: boolean;
  reviewedAt: Date | undefined;
  reviewedBy: string | null;
  spaceId: string;
  startsAt: Date | undefined;
  status: string;
};

type OrganizationProfileRow = {
  alternateName?: string;
  primaryLeader?: PublicPrimaryLeader | null;
  id: string;
  addressLine1: string | null;
  addressLine2: string | null;
  brandColor: string;
  category: string;
  city: string;
  connectionPolicy: string;
  country: string;
  displayName: string;
  logoStoragePath: string | null;
  openConnection: boolean;
  postalCode: string;
  publicEmail: string | null;
  publicPhone: string | null;
  publicVisibilityDirectory: boolean;
  publicVisibilityDiscovery: boolean;
  publicVisibilityProfile: boolean;
  region: string;
  timeZone: string;
  websiteUrl: string | null;
  welcomeHeadline: string;
  welcomeHeadlineAr: string;
  welcomeSummary: string;
  welcomeSummaryAr: string;
};

type OrganizationProfileUpdateRequestRow = {
  id: string;
  processedAt: Date | undefined;
  requestedAt: Date | undefined;
  requestedBy: string;
  reviewReason: string | null;
  status: string;
};

type OrganizationPrayerId = "fajr" | "sunrise" | "dhuhr" | "asr" | "maghrib" | "isha" | "jumuah";

type OrganizationPrayerTimingRow = {
  prayerId: OrganizationPrayerId;
  displayTime: string | null;
  iqamaTime: string | null;
  enabled: boolean;
  source: string;
};

type OrganizationPrayerSettingsRow = {
  address: string;
  calculationMethod: number;
  enabled: boolean;
  latitudeAdjustmentMethod: number;
  provider: string;
  school: number;
  timeZone: string;
  timings: OrganizationPrayerTimingRow[];
  timingsDate: string | null;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type OrganizationPrayerSettingsEditorInput = {
  address: string;
  enabled: boolean;
  calculationMethod: string;
  school: string;
  latitudeAdjustmentMethod: string;
  timeZone: string;
  fajrIqama: string;
  dhuhrIqama: string;
  asrIqama: string;
  maghribIqama: string;
  ishaIqama: string;
  jumuahIqama: string;
};

const organizationPrayerOrder: OrganizationPrayerId[] = [
  "fajr",
  "sunrise",
  "dhuhr",
  "asr",
  "maghrib",
  "isha",
  "jumuah",
];

type OrganizationConnectionRow = {
  organizationId: string;
  personId: string;
  roles: string[];
  status: string;
  updatedAt: Date | undefined;
  uid: string;
};

type PeopleManagerRow = {
  assignment: OrganizationRoleAssignmentRow | undefined;
  canonicalRoleIds: OrganizationRoleId[];
  connection: OrganizationConnectionRow;
  personProfile: PersonProfileRow | null | undefined;
};

type PersonProfileRow = PersonProfilePresentationRow;

type OrganizationRoleAssignmentRow = {
  organizationId: string;
  personId: string;
  roleIds: OrganizationRoleId[];
  status: string;
  updatedAt: Date | undefined;
  updatedBy: string;
};

type ConnectionDecisionRequestRow = {
  decision: string;
  id: string;
  requestedBy: string;
  status: string;
  targetRequestId: string;
};

type AnnouncementRow = {
  createdBy: string;
  pushNotificationEnabled: boolean;
  id: string;
  body: string;
  bodyAr: string;
  expiresAt: Date | undefined;
  priority: string;
  publishedAt: Date | undefined;
  status: string;
  title: string;
  titleAr: string;
  updatedBy: string;
};

type ImamContentRow = {
  body: string;
  bodyAr: string | null;
  contentId: string;
  type: string;
  featured: boolean;
  galleryImage1: OrganizationContentMediaReference | null;
  galleryImage2: OrganizationContentMediaReference | null;
  galleryImage3: OrganizationContentMediaReference | null;
  imageAltText: string | null;
  imageAltTextAr: string | null;
  imageStoragePath: string | null;
  organizationId: string;
  publishedAt: Date | undefined;
  sortOrder: number;
  status: string;
  subtitle: string | null;
  subtitleAr: string | null;
  title: string;
  titleAr: string | null;
  updatedBy: string;
  youtube: OrganizationContentYoutubeReference | null;
};

type TimelineEntryRow = {
  id: string;
  announcementId: string;
  body: string;
  bodyAr: string;
  priority: string;
  publishedAt: Date | undefined;
  title: string;
  titleAr: string;
};

type EngagementDeliveryLogRow = {
  id: string;
  announcementId: string;
  channel: string;
  providerEventId: string;
  status: string;
};

type AnnouncementAcknowledgementRow = {
  id: string;
  announcementId: string;
  createdAt: Date | undefined;
  status: string;
  uid: string;
};

type IntelligenceRequestRow = {
  id: string;
  processedAt: Date | undefined;
  requestedAt: Date | undefined;
  requestedBy: string;
  snapshotId: string;
  status: string;
};

type IntelligenceSnapshotRow = {
  id: string;
  acknowledgementCount: number;
  activeSpacesCount: number;
  availableIftarSlotsCount: number;
  availableSpaceSlotsCount: number;
  connectedPeopleCount: number;
  confirmedDonationTotalCents: number;
  generatedAt: Date | undefined;
  publishedAnnouncementsCount: number;
  recommendation: string;
  recommendationAr: string;
  riskLevel: string;
  summary: string;
  summaryAr: string;
  title: string;
  titleAr: string;
  upcomingEventsCount: number;
};

type BusinessDirectoryRow = {
  businessId: string;
  categoryIds: string[];
  city: string;
  featured: boolean;
  name: string;
  status: string;
  verificationState: string;
};

type OrganizationBusinessAffiliationRow = {
  affiliationType: string;
  businessId: string;
  businessNameSnapshot: string;
  displayOrder: number;
  organizationId: string;
  status: string;
  verificationState: string;
};

type OrganizationPlanRow = {
  billingInterval: string;
  displayName: string;
  enabledEntitlementIds: string[];
  organizationLocations: number;
  planId: string;
  priceMinor: number;
  storageBytes: number;
  trialDefaultDays: number;
  trialEligible: boolean;
};

type OrganizationSubscriptionRow = {
  billingState: string;
  effectiveSubscriptionDiscountBasisPoints: number;
  foundingStatus: string;
  planId: string;
  status: string;
  trialEndsAt: Date | undefined;
  trialStatus: string;
  updatedAt: Date | undefined;
};

type EntitlementOverrideRow = {
  entitlementId: string;
  mode: string;
  reason: string;
  updatedBy: string;
};

type UsageLimitRow = {
  canUploadStorage: boolean;
  cloudAiMonthlyActionsLimit: number;
  cloudAiMonthlyActionsUsed: number;
  organizationLocationsLimit: number;
  organizationLocationsUsed: number;
  storageBytesLimit: number;
  storageBytesUsed: number;
  storageStatus: string;
};

type FoundingSlotRow = {
  discountBasisPoints: number;
  qualifiedBy: string;
  slotNumber: number;
  status: string;
};

const organizationUsers = seedPeople.filter((person) => person.connections.length > 0);

function eventEditorInputForOrganization(organizationId: string): EventEditorInput {
  const event = demoCommunityEvents.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  return event
    ? buildEventEditorInputFromFixture(event)
    : buildDefaultEventEditorInput(organizationId);
}

function announcementComposerInputForOrganization(
  organizationId: string,
): AnnouncementComposerInput {
  const announcement = demoAnnouncements.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  return announcement
    ? buildAnnouncementComposerInputFromFixture(announcement)
    : buildDefaultAnnouncementComposerInput(organizationId);
}

function imamContentEditorInputForOrganization(organizationId: string): ImamContentEditorInput {
  const content = demoOrganizationContents.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  return content
    ? buildImamContentEditorInputFromContent(content)
    : buildDefaultImamContentEditorInput(organizationId);
}

function ramadanModeEditorInputForOrganization(organizationId: string): RamadanModeEditorInput {
  const ramadanMode = demoRamadanModes.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  return ramadanMode
    ? buildRamadanModeEditorInputFromFixture(ramadanMode)
    : buildDefaultRamadanModeEditorInput();
}

function buildDefaultRamadanSetupEditorInput(organizationId: string): RamadanSetupEditorInput {
  const defaultTimeZone =
    publicOrganizationProfiles.find((candidate) => candidate.id === organizationId)?.timeZone ??
    "America/New_York";
  return {
    defaultCampusId: organizationId === "org-hvicc" ? "main-campus" : "main-campus",
    endAdjustmentDays: "0",
    endsOn: "2027-03-09",
    experienceOpensAt: "2027-01-25T00:00",
    fajrIqama: "05:45",
    featuredRamadanContentId: "ramadan-1448-message",
    guestImamProfiles: "",
    guestImamsEnabled: "false",
    hijriYear: "1448",
    ishaIqama: "21:05",
    lastTenStartsOn: "2027-02-28",
    linkedOperationalModeId: "ramadan-1448-mode",
    maghribIqama: "19:12",
    message: "Ramadan setup is approved with Taraweeh and Qiyam details ready.",
    messageAr: "تم اعتماد إعداد رمضان لهذا العام مع أوقات التراويح والقيام.",
    phase1FajrIqama: "05:45",
    phase1IshaIqama: "21:05",
    phase1MaghribIqama: "19:12",
    phase2FajrIqama: "05:35",
    phase2IshaIqama: "21:10",
    phase2MaghribIqama: "19:20",
    phase3FajrIqama: "05:25",
    phase3IshaIqama: "21:15",
    phase3MaghribIqama: "19:28",
    qiyamEndsOn: "2027-03-09",
    qiyamStartTime: "03:45",
    qiyamStartsOn: "2027-02-28",
    startAdjustmentDays: "0",
    startsOn: "2027-02-08",
    status: "ready",
    sponsorshipOpensAt: "2027-01-28T00:00",
    tahajjudSchedule:
      "2027-02-28 03:45 Tahajjud begins\n2027-03-01 03:45 Tahajjud\n2027-03-02 03:45 Tahajjud",
    taraweehRakahCount: "20",
    taraweehRelativeToIshaMinutes: "15",
    timezone: defaultTimeZone,
    title: "Ramadan 1448",
    titleAr: "رمضان 1448",
  };
}

function ramadanSetupEditorInputForOrganization(organizationId: string): RamadanSetupEditorInput {
  const setup = demoRamadanSetups.find((candidate) => candidate.organizationId === organizationId);
  if (!setup) {
    return buildDefaultRamadanSetupEditorInput(organizationId);
  }
  const defaultCampus =
    setup.campusPrayerConfigs.find((config) => config.campusId === setup.defaultCampusId) ??
    setup.campusPrayerConfigs[0];
  const prayerTime = (prayerId: string): string =>
    defaultCampus?.prayerOverrides.find((override) => override.prayerId === prayerId)?.iqamaTime ??
    "";
  const phasePrayerTime = (
    phaseId: RamadanIqamaPhaseConfiguration["phaseId"],
    prayerId: string,
    fallback: string,
  ): string =>
    setup.iqamaPhases
      ?.find((phase) => phase.phaseId === phaseId)
      ?.prayerOverrides.find((override) => override.prayerId === prayerId)?.iqamaTime ?? fallback;
  const taraweeh = setup.taraweeh.find((config) => config.campusId === setup.defaultCampusId);
  const qiyam = setup.qiyam.find((config) => config.campusId === setup.defaultCampusId);
  const tahajjudSchedule = (setup.tahajjudNights ?? [])
    .filter((night) => night.enabled)
    .map(
      (night) =>
        `${night.gregorianDate} ${night.startTime}${night.notes?.en ? ` ${night.notes.en}` : ""}`,
    )
    .join("\n");
  const guestImamProfiles = (setup.guestImams?.profiles ?? [])
    .map((profile) =>
      [
        profile.displayName.en,
        profile.title?.en ?? "",
        profile.bio?.en ?? "",
        profile.photoUrl ?? "",
      ].join(" | "),
    )
    .join("\n");
  const fallbackFajr = prayerTime("fajr");
  const fallbackMaghrib = prayerTime("maghrib");
  const fallbackIsha = prayerTime("isha");

  return {
    defaultCampusId: setup.defaultCampusId,
    endAdjustmentDays: String(setup.endAdjustmentDays),
    endsOn: setup.endsOn,
    experienceOpensAt: localDateTimeInputValue(setup.experienceOpensAt),
    fajrIqama: fallbackFajr,
    featuredRamadanContentId: setup.featuredRamadanContentId ?? "",
    guestImamProfiles,
    guestImamsEnabled: setup.guestImams?.enabled ? "true" : "false",
    hijriYear: String(setup.hijriYear),
    ishaIqama: fallbackIsha,
    lastTenStartsOn: setup.lastTenNights.approvedStartsOn ?? "",
    linkedOperationalModeId: setup.linkedOperationalModeId,
    maghribIqama: fallbackMaghrib,
    message: setup.home.message.en,
    messageAr: setup.home.message.ar ?? "",
    phase1FajrIqama: phasePrayerTime("firstTen", "fajr", fallbackFajr),
    phase1IshaIqama: phasePrayerTime("firstTen", "isha", fallbackIsha),
    phase1MaghribIqama: phasePrayerTime("firstTen", "maghrib", fallbackMaghrib),
    phase2FajrIqama: phasePrayerTime("secondTen", "fajr", fallbackFajr),
    phase2IshaIqama: phasePrayerTime("secondTen", "isha", fallbackIsha),
    phase2MaghribIqama: phasePrayerTime("secondTen", "maghrib", fallbackMaghrib),
    phase3FajrIqama: phasePrayerTime("lastTen", "fajr", fallbackFajr),
    phase3IshaIqama: phasePrayerTime("lastTen", "isha", fallbackIsha),
    phase3MaghribIqama: phasePrayerTime("lastTen", "maghrib", fallbackMaghrib),
    qiyamEndsOn: qiyam?.endsOn ?? "",
    qiyamStartTime: qiyam?.startTime ?? "",
    qiyamStartsOn: qiyam?.startsOn ?? "",
    startAdjustmentDays: String(setup.startAdjustmentDays),
    startsOn: setup.startsOn,
    status: setup.status === "draft" ? "draft" : "ready",
    sponsorshipOpensAt: localDateTimeInputValue(setup.sponsorshipOpensAt),
    tahajjudSchedule,
    taraweehRakahCount: taraweeh?.rakahCount === null ? "" : String(taraweeh?.rakahCount ?? ""),
    taraweehRelativeToIshaMinutes:
      taraweeh?.relativeToIshaMinutes === null ? "" : String(taraweeh?.relativeToIshaMinutes ?? ""),
    timezone: setup.timezone,
    title: setup.home.title.en,
    titleAr: setup.home.title.ar ?? "",
  };
}

function defaultIftarSponsorshipCapacityByWeekday(input: {
  friday: number;
  saturday: number;
  standard: number;
}): RamadanIftarCapacityByWeekday {
  return {
    friday: input.friday,
    monday: input.standard,
    saturday: input.saturday,
    sunday: input.standard,
    thursday: input.standard,
    tuesday: input.standard,
    wednesday: input.standard,
  };
}

function iftarSponsorshipCapacityByWeekdayFromData(data: {
  capacityByWeekday?: unknown;
  fridayCapacitySpots?: unknown;
  saturdayCapacitySpots?: unknown;
  standardCapacitySpots?: unknown;
}): RamadanIftarCapacityByWeekday {
  const fallback = defaultIftarSponsorshipCapacityByWeekday({
    friday: typeof data.fridayCapacitySpots === "number" ? data.fridayCapacitySpots : 0,
    saturday: typeof data.saturdayCapacitySpots === "number" ? data.saturdayCapacitySpots : 0,
    standard: typeof data.standardCapacitySpots === "number" ? data.standardCapacitySpots : 0,
  });
  const source = isRecord(data.capacityByWeekday) ? data.capacityByWeekday : {};

  return ramadanIftarWeekdays.reduce<RamadanIftarCapacityByWeekday>((capacity, weekday) => {
    const value = source[weekday];
    capacity[weekday] = typeof value === "number" ? value : fallback[weekday];
    return capacity;
  }, {} as RamadanIftarCapacityByWeekday);
}

function iftarSponsorshipCapacityByWeekdayFromEditor(
  editor: IftarSponsorshipDefaultsEditorInput,
): RamadanIftarCapacityByWeekday {
  return ramadanIftarWeekdays.reduce<RamadanIftarCapacityByWeekday>((capacity, weekday) => {
    const key = iftarCapacityEditorKeysByWeekday[weekday];
    capacity[weekday] = requireInteger(editor[key], `${ramadanWeekdayLabels[weekday]} capacity`);
    return capacity;
  }, {} as RamadanIftarCapacityByWeekday);
}

function renderIftarCapacityWeekdayFields(): string {
  return ramadanIftarWeekdays
    .map((weekday) => {
      const key = iftarCapacityEditorKeysByWeekday[weekday];
      return `<label>
        ${ramadanWeekdayLabels[weekday]} spots
        <input data-iftar-sponsorship-defaults-field="${key}" type="number" min="0" max="1000" value="${escapeHtml(
          state.iftarSponsorshipDefaultsEditor[key],
        )}" />
      </label>`;
    })
    .join("");
}

function renderIftarCapacityWeekdaySummary(
  capacityByWeekday: RamadanIftarCapacityByWeekday,
): string {
  return ramadanIftarWeekdays
    .map((weekday) => `${ramadanWeekdayLabels[weekday]} ${capacityByWeekday[weekday]}`)
    .join(" · ");
}

function renderRamadanSetupSponsorshipDefaultsEditor(): string {
  const editor = state.iftarSponsorshipDefaultsEditor;
  return `
    <section class="ramadan-editor-section">
      <h3>Iftar sponsorship defaults</h3>
      <input data-iftar-sponsorship-defaults-field="ramadanId" type="hidden" value="${escapeHtml(
        editor.ramadanId,
      )}" />
      <div class="editor-grid">
        <label>
          Sponsorship enabled
          <select data-iftar-sponsorship-defaults-field="programEnabled">
            <option value="true" ${editor.programEnabled === "true" ? "selected" : ""}>Enabled</option>
            <option value="false" ${editor.programEnabled === "false" ? "selected" : ""}>Paused</option>
          </select>
        </label>
        <label>
          Spot price USD
          <input data-iftar-sponsorship-defaults-field="defaultPriceDollars" type="number" min="0" max="100000" step="0.01" value="${escapeHtml(
            editor.defaultPriceDollars,
          )}" />
        </label>
        <label>
          Sponsorship closes
          <input data-iftar-sponsorship-defaults-field="sponsorshipClosesLocalTime" type="time" value="${escapeHtml(
            editor.sponsorshipClosesLocalTime,
          )}" />
        </label>
        <label>
          Close day offset
          <input data-iftar-sponsorship-defaults-field="sponsorshipClosesDayOffset" type="number" min="-60" max="0" value="${escapeHtml(
            editor.sponsorshipClosesDayOffset,
          )}" />
        </label>
      </div>
      <div class="editor-grid ramadan-weekday-capacity-grid">
        ${renderIftarCapacityWeekdayFields()}
      </div>
      <label>
        Sponsorship opens at
        <input data-iftar-sponsorship-defaults-field="sponsorshipOpensAt" type="datetime-local" value="${escapeHtml(
          editor.sponsorshipOpensAt,
        )}" />
      </label>
      <div class="editor-grid">
        <label>
          Public recognition
          <select data-iftar-sponsorship-defaults-field="publicRecognitionAllowed">
            <option value="true" ${editor.publicRecognitionAllowed === "true" ? "selected" : ""}>Default on</option>
            <option value="false" ${editor.publicRecognitionAllowed === "false" ? "selected" : ""}>Off</option>
          </select>
        </label>
        <label>
          Sponsor names
          <select data-iftar-sponsorship-defaults-field="sponsorDisplayNameAllowed">
            <option value="true" ${editor.sponsorDisplayNameAllowed === "true" ? "selected" : ""}>Allowed</option>
            <option value="false" ${editor.sponsorDisplayNameAllowed === "false" ? "selected" : ""}>Hidden</option>
          </select>
        </label>
        <label>
          Anonymous recognition
          <select data-iftar-sponsorship-defaults-field="anonymousPublicRecognitionAllowed">
            <option value="true" ${
              editor.anonymousPublicRecognitionAllowed === "true" ? "selected" : ""
            }>Allowed</option>
            <option value="false" ${
              editor.anonymousPublicRecognitionAllowed === "false" ? "selected" : ""
            }>Off</option>
          </select>
        </label>
      </div>
      <div class="editor-grid">
        <label>
          Dedication
          <select data-iftar-sponsorship-defaults-field="dedicationEnabled">
            <option value="true" ${editor.dedicationEnabled === "true" ? "selected" : ""}>Enabled</option>
            <option value="false" ${editor.dedicationEnabled === "false" ? "selected" : ""}>Off</option>
          </select>
        </label>
        <label>
          Dedication max length
          <input data-iftar-sponsorship-defaults-field="dedicationMaxLength" type="number" min="0" max="280" value="${escapeHtml(
            editor.dedicationMaxLength,
          )}" />
        </label>
        <label>
          Public dedication display
          <select data-iftar-sponsorship-defaults-field="dedicationPublicDisplayAllowed">
            <option value="true" ${
              editor.dedicationPublicDisplayAllowed === "true" ? "selected" : ""
            }>Allowed</option>
            <option value="false" ${
              editor.dedicationPublicDisplayAllowed === "false" ? "selected" : ""
            }>Off</option>
          </select>
        </label>
      </div>
      <label>
        Sponsorship day message
        <textarea data-iftar-sponsorship-defaults-field="publicNotes" maxlength="240">${escapeHtml(
          editor.publicNotes,
        )}</textarea>
      </label>
      <label>
        Arabic sponsorship day message
        <textarea data-iftar-sponsorship-defaults-field="publicNotesAr" maxlength="240" dir="rtl">${escapeHtml(
          editor.publicNotesAr,
        )}</textarea>
      </label>
    </section>`;
}

function buildDefaultIftarSponsorshipDefaultsEditorInput(
  organizationId: string,
): IftarSponsorshipDefaultsEditorInput {
  const setup = demoRamadanSetups.find((candidate) => candidate.organizationId === organizationId);
  const capacityByWeekday = defaultIftarSponsorshipCapacityByWeekday({
    friday: 6,
    saturday: 5,
    standard: 2,
  });
  return {
    anonymousPublicRecognitionAllowed: "true",
    dedicationEnabled: "true",
    dedicationMaxLength: "120",
    dedicationPublicDisplayAllowed: "false",
    defaultPriceDollars: "500.00",
    fridayCapacitySpots: String(capacityByWeekday.friday),
    mondayCapacitySpots: String(capacityByWeekday.monday),
    programEnabled: "true",
    publicNotes: "Configuration preview only; sponsorship reservations and payment open later.",
    publicNotesAr: "هذه معاينة لتكوين الرعاية فقط؛ يبدأ الحجز والدفع لاحقاً.",
    publicRecognitionAllowed: "true",
    ramadanId: setup?.ramadanId ?? "ramadan-1448",
    saturdayCapacitySpots: String(capacityByWeekday.saturday),
    sponsorDisplayNameAllowed: "true",
    sponsorshipClosesDayOffset: "-1",
    sponsorshipClosesLocalTime: "17:00",
    sponsorshipOpensAt: "",
    standardCapacitySpots: String(capacityByWeekday.monday),
    sundayCapacitySpots: String(capacityByWeekday.sunday),
    thursdayCapacitySpots: String(capacityByWeekday.thursday),
    tuesdayCapacitySpots: String(capacityByWeekday.tuesday),
    wednesdayCapacitySpots: String(capacityByWeekday.wednesday),
  };
}

function iftarSponsorshipDefaultsEditorInputForOrganization(
  organizationId: string,
): IftarSponsorshipDefaultsEditorInput {
  const defaults = demoIftarSponsorshipDefaults.find(
    (candidate) => candidate.organizationId === organizationId,
  );
  if (!defaults) {
    return buildDefaultIftarSponsorshipDefaultsEditorInput(organizationId);
  }
  const capacityByWeekday = iftarSponsorshipCapacityByWeekdayFromData(defaults);

  return {
    anonymousPublicRecognitionAllowed: defaults.recognition.anonymousPublicRecognitionAllowed
      ? "true"
      : "false",
    dedicationEnabled: defaults.dedication.enabled ? "true" : "false",
    dedicationMaxLength: String(defaults.dedication.maxLength),
    dedicationPublicDisplayAllowed: defaults.dedication.publicDisplayAllowed ? "true" : "false",
    defaultPriceDollars: minorToDollarInput(defaults.defaultPricePerSpotMinor),
    fridayCapacitySpots: String(capacityByWeekday.friday),
    mondayCapacitySpots: String(capacityByWeekday.monday),
    programEnabled: defaults.programEnabled ? "true" : "false",
    publicNotes: defaults.publicNotes?.en ?? "",
    publicNotesAr: defaults.publicNotes?.ar ?? "",
    publicRecognitionAllowed: defaults.recognition.publicRecognitionAllowed ? "true" : "false",
    ramadanId: defaults.ramadanId,
    saturdayCapacitySpots: String(capacityByWeekday.saturday),
    sponsorDisplayNameAllowed: defaults.recognition.sponsorDisplayNameAllowed ? "true" : "false",
    sponsorshipClosesDayOffset: String(defaults.sponsorshipClosesDayOffset),
    sponsorshipClosesLocalTime: defaults.sponsorshipClosesLocalTime,
    sponsorshipOpensAt: defaults.sponsorshipOpensAt ?? "",
    standardCapacitySpots: String(capacityByWeekday.monday),
    sundayCapacitySpots: String(capacityByWeekday.sunday),
    thursdayCapacitySpots: String(capacityByWeekday.thursday),
    tuesdayCapacitySpots: String(capacityByWeekday.tuesday),
    wednesdayCapacitySpots: String(capacityByWeekday.wednesday),
  };
}

function buildDefaultIftarAttendanceDefaultsEditorInput(
  organizationId: string,
): IftarAttendanceDefaultsEditorInput {
  return {
    defaultCapacityPeople: "180",
    programEnabled: "true",
    publicNotes: "Register so the team can plan seating and food accurately.",
    publicNotesAr: "سجل حتى يتمكن الفريق من تنظيم المقاعد والطعام بدقة.",
    ramadanId: defaultRamadanIdForOrganization(organizationId),
    registrationClosesDayOffset: "0",
    registrationClosesLocalTime: "16:00",
    registrationOpensAt: "",
  };
}

function defaultRamadanIdForOrganization(organizationId: string): string {
  const setup = demoRamadanSetups.find((candidate) => candidate.organizationId === organizationId);
  return setup?.ramadanId ?? "ramadan-1448";
}

function buildDefaultRamadanAdminNoteEditorInput(
  organizationId: string,
): RamadanAdminNoteEditorInput {
  return {
    body: "",
    category: "operations",
    iftarDayId: "",
    noteId: "",
    ramadanId: defaultRamadanIdForOrganization(organizationId),
    sponsorshipId: "",
  };
}

function buildDefaultIftarSponsorshipDayEditorInput(
  organizationId: string,
): IftarSponsorshipDayEditorInput {
  const day = demoIftarSponsorshipDays.find(
    (candidate) => candidate.organizationId === organizationId,
  );
  return day
    ? iftarSponsorshipDayEditorInputFromRow(iftarSponsorshipDayRowFromData(day, day.id))
    : {
        capacitySpots: "2",
        gregorianDate: "2027-02-08",
        iftarDayId: "iftar-2027-02-08",
        priceDollars: "500.00",
        publicNotes: "",
        publicNotesAr: "",
        sponsorshipClosesAt: "2027-02-07T17:00",
        sponsorshipEnabled: "true",
        sponsorshipOpensAt: "",
        status: "draft",
      };
}

function buildDefaultOperationalModeEditorInput(): OperationalModeEditorInput {
  return {
    modeId: "",
    displayName: "",
    displayNameAr: "",
    title: "",
    titleAr: "",
    subtitle: "",
    subtitleAr: "",
    emphasizedEntryIds: "",
    startsAt: "",
    endsAt: "",
    prioritizedModuleIds: "events,donations",
    priority: "50",
    quickActionIds: "",
    status: "draft",
    type: "generic",
    visibility: "connected",
  };
}

function iftarSlotEditorInputForOrganization(organizationId: string): IftarSlotEditorInput {
  const slot = demoIftarSponsorshipSlots.find(
    (candidate) => candidate.organizationId === organizationId && candidate.status === "available",
  );

  return slot ? buildIftarSlotEditorInputFromFixture(slot) : buildDefaultIftarSlotEditorInput();
}

function spaceEditorInputForOrganization(organizationId: string): SpaceEditorInput {
  const space = demoSpaces.find((candidate) => candidate.organizationId === organizationId);

  return space ? buildSpaceEditorInputFromFixture(space) : buildDefaultSpaceEditorInput();
}

function spaceSlotEditorInputForOrganization(organizationId: string): SpaceSlotEditorInput {
  const slot = demoSpaceAvailabilitySlots.find(
    (candidate) => candidate.organizationId === organizationId && candidate.status === "available",
  );

  return slot ? buildSpaceSlotEditorInputFromFixture(slot) : buildDefaultSpaceSlotEditorInput();
}

function dateTimeLocalValue(value: Date | string | null | undefined): string {
  if (!value) {
    return "";
  }
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 16);
}

function localDateTimeInputValue(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}$/u.test(value)) {
    return `${value}T00:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/u.test(value)) {
    return value.slice(0, 16);
  }
  return dateTimeLocalValue(value);
}

function buildEmptyVolunteerOpportunityEditorInput(): VolunteerOpportunityEditorInput {
  return {
    agePolicy: "",
    campusId: "",
    category: "hospitality",
    description: "",
    descriptionAr: "",
    dressCode: "",
    endsAt: "",
    imageAltText: "",
    imageAltTextAr: "",
    imageStoragePath: "",
    instructions: "",
    instructionsAr: "",
    locationName: "",
    opportunityId: "",
    physicalNotes: "",
    startsAt: "",
    status: "draft",
    title: "",
    titleAr: "",
    trainingRequired: "false",
  };
}

function volunteerOpportunityEditorInputForOrganization(
  organizationId: string,
): VolunteerOpportunityEditorInput {
  const opportunity = demoVolunteerOpportunities.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  if (!opportunity) {
    return buildEmptyVolunteerOpportunityEditorInput();
  }

  return {
    agePolicy: opportunity.requirements.agePolicy ?? "",
    campusId: opportunity.campusId ?? "",
    category: opportunity.category,
    description: opportunity.description,
    descriptionAr: opportunity.descriptionAr ?? "",
    dressCode: opportunity.requirements.dressCode ?? "",
    endsAt: dateTimeLocalValue(opportunity.endsAt),
    imageAltText: opportunity.image.altText ?? "",
    imageAltTextAr: opportunity.image.altTextAr ?? "",
    imageStoragePath: opportunity.image.storagePath ?? "",
    instructions: opportunity.instructions,
    instructionsAr: opportunity.instructionsAr ?? "",
    locationName: opportunity.locationName,
    opportunityId: opportunity.opportunityId,
    physicalNotes: opportunity.requirements.physicalNotes ?? "",
    startsAt: dateTimeLocalValue(opportunity.startsAt),
    status: opportunity.status,
    title: opportunity.title,
    titleAr: opportunity.titleAr ?? "",
    trainingRequired: opportunity.requirements.trainingRequired ? "true" : "false",
  };
}

function volunteerOpportunityEditorInputFromRow(
  opportunity: VolunteerOpportunityRow,
): VolunteerOpportunityEditorInput {
  return {
    agePolicy: opportunity.requirementAgePolicy ?? "",
    campusId: opportunity.campusId ?? "",
    category: opportunity.category,
    description: opportunity.description,
    descriptionAr: opportunity.descriptionAr ?? "",
    dressCode: opportunity.requirementDressCode ?? "",
    endsAt: dateTimeLocalValue(opportunity.endsAt),
    imageAltText: opportunity.imageAltText ?? "",
    imageAltTextAr: opportunity.imageAltTextAr ?? "",
    imageStoragePath: opportunity.imageStoragePath ?? "",
    instructions: opportunity.instructions,
    instructionsAr: opportunity.instructionsAr ?? "",
    locationName: opportunity.locationName,
    opportunityId: opportunity.opportunityId,
    physicalNotes: opportunity.requirementPhysicalNotes ?? "",
    startsAt: dateTimeLocalValue(opportunity.startsAt),
    status: opportunity.status,
    title: opportunity.title,
    titleAr: opportunity.titleAr ?? "",
    trainingRequired: opportunity.requirementTrainingRequired ? "true" : "false",
  };
}

function buildEmptyVolunteerShiftEditorInput(): VolunteerShiftEditorInput {
  return {
    capacity: "5",
    endsAt: "",
    instructions: "",
    instructionsAr: "",
    notes: "",
    notesAr: "",
    opportunityId: "",
    shiftId: "",
    startsAt: "",
    status: "open",
    timezone: "America/New_York",
  };
}

function volunteerShiftEditorInputForOrganization(
  organizationId: string,
): VolunteerShiftEditorInput {
  const shift = demoVolunteerShifts.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  if (!shift) {
    return buildEmptyVolunteerShiftEditorInput();
  }

  return {
    capacity: String(shift.capacity),
    endsAt: dateTimeLocalValue(shift.endsAt),
    instructions: shift.instructions ?? "",
    instructionsAr: shift.instructionsAr ?? "",
    notes: shift.notes ?? "",
    notesAr: shift.notesAr ?? "",
    opportunityId: shift.opportunityId,
    shiftId: shift.shiftId,
    startsAt: dateTimeLocalValue(shift.startsAt),
    status: shift.status,
    timezone: shift.timezone,
  };
}

function volunteerShiftEditorInputFromRow(shift: VolunteerShiftRow): VolunteerShiftEditorInput {
  return {
    capacity: String(shift.capacity),
    endsAt: dateTimeLocalValue(shift.endsAt),
    instructions: shift.instructions ?? "",
    instructionsAr: shift.instructionsAr ?? "",
    notes: shift.notes ?? "",
    notesAr: shift.notesAr ?? "",
    opportunityId: shift.opportunityId,
    shiftId: shift.shiftId,
    startsAt: dateTimeLocalValue(shift.startsAt),
    status: shift.status,
    timezone: shift.timezone,
  };
}

function organizationProfileEditorInputForOrganization(
  organizationId: string,
): OrganizationProfileEditorInput {
  const profile = publicOrganizationProfiles.find((candidate) => candidate.id === organizationId);

  return profile
    ? buildOrganizationProfileEditorInputFromFixture(profile)
    : buildDefaultOrganizationProfileEditorInput();
}

function organizationPrayerSettingsEditorInputForOrganization(
  organizationId: string,
): OrganizationPrayerSettingsEditorInput {
  return {
    address: defaultOrganizationPrayerAddress(organizationId),
    asrIqama: "",
    calculationMethod: "2",
    dhuhrIqama: "",
    enabled: true,
    fajrIqama: "",
    ishaIqama: "",
    jumuahIqama: "",
    latitudeAdjustmentMethod: "3",
    maghribIqama: "",
    school: "0",
    timeZone: "America/New_York",
  };
}

let unsubscribeStatus: Unsubscribe | undefined;
let unsubscribeConnectionRequests: Unsubscribe | undefined;
let unsubscribeConnectionDecisionRequests: Unsubscribe | undefined;
let unsubscribeEvents: Unsubscribe | undefined;
let unsubscribeEventRegistrations: Unsubscribe | undefined;
let unsubscribeEventAttendance: Unsubscribe | undefined;
let unsubscribeEventMedia: Unsubscribe | undefined;
let unsubscribeDonations: Unsubscribe | undefined;
let unsubscribeDonationRefunds: Unsubscribe | undefined;
let unsubscribeReconciliationRuns: Unsubscribe | undefined;
let unsubscribeReconciliationMismatches: Unsubscribe | undefined;
let unsubscribeGivingFunds: Unsubscribe | undefined;
let unsubscribeCampaigns: Unsubscribe | undefined;
let unsubscribeCommerceStatus: Unsubscribe | undefined;
let unsubscribeOrganizationPlans: Unsubscribe | undefined;
let unsubscribeOrganizationSubscription: Unsubscribe | undefined;
let unsubscribeOrganizationEntitlementOverrides: Unsubscribe | undefined;
let unsubscribeOrganizationUsage: Unsubscribe | undefined;
let unsubscribeFoundingSlot: Unsubscribe | undefined;
let unsubscribeOrganizationProfile: Unsubscribe | undefined;
let unsubscribeOrganizationProfileRequests: Unsubscribe | undefined;
let unsubscribePlatformIslamicComponents: Unsubscribe | undefined;
let unsubscribeOrganizationIslamicComponents: Unsubscribe | undefined;
let unsubscribeOrganizationPrayerSettings: Unsubscribe | undefined;
let unsubscribeOrganizationConnectionRecords: Unsubscribe[] = [];
let unsubscribeModeWorkspace: Unsubscribe | undefined;
let unsubscribePersonProfiles: Unsubscribe[] = [];
let unsubscribeMemberSupportTickets: Unsubscribe | undefined;
let unsubscribeMemberSupportMessages: Unsubscribe | undefined;
let unsubscribeShdeedSupportTickets: Unsubscribe | undefined;
let unsubscribeShdeedSupportMessages: Unsubscribe | undefined;
let unsubscribeBusinessDirectory: Unsubscribe | undefined;
let unsubscribeBusinessAffiliations: Unsubscribe | undefined;
let unsubscribeRamadanModes: Unsubscribe | undefined;
let unsubscribeRamadanSetups: Unsubscribe | undefined;
let unsubscribeIftarAttendanceDefaults: Unsubscribe | undefined;
let unsubscribeIftarAttendanceDays: Unsubscribe | undefined;
let unsubscribeIftarAttendanceRegistrations: Unsubscribe | undefined;
let unsubscribeIftarSponsorshipDefaults: Unsubscribe | undefined;
let unsubscribeIftarSponsorshipDays: Unsubscribe | undefined;
let unsubscribeIftarSlots: Unsubscribe | undefined;
let unsubscribeSponsorships: Unsubscribe | undefined;
let unsubscribeIftarSponsorshipFinance: Unsubscribe | undefined;
let unsubscribeIftarTransfers: Unsubscribe | undefined;
let unsubscribeRamadanAdminNotes: Unsubscribe[] = [];
let unsubscribeSpaces: Unsubscribe | undefined;
let unsubscribeSpaceAvailabilityBlocks: Unsubscribe[] = [];
let unsubscribeSpaceReservationRequests: Unsubscribe[] = [];
let unsubscribeSpaceSlots: Unsubscribe | undefined;
let unsubscribeSpaceReservations: Unsubscribe | undefined;
let unsubscribeVolunteerOpportunities: Unsubscribe | undefined;
let unsubscribeVolunteerShifts: Unsubscribe[] = [];
let unsubscribeVolunteerSignups: Unsubscribe[] = [];
let unsubscribeVolunteerAttendances: Unsubscribe[] = [];
let unsubscribeVolunteerServiceSummaries: Unsubscribe | undefined;
let unsubscribeVolunteerRecognitionAwards: Unsubscribe | undefined;
let unsubscribeAnnouncements: Unsubscribe | undefined;
let unsubscribeImamContent: Unsubscribe | undefined;
let unsubscribeTimelineEntries: Unsubscribe | undefined;
let unsubscribeEngagementDeliveryLogs: Unsubscribe | undefined;
let unsubscribeAnnouncementAcknowledgements: Unsubscribe | undefined;
let unsubscribeIntelligenceRequests: Unsubscribe | undefined;
let unsubscribeIntelligenceSnapshots: Unsubscribe | undefined;
let eventEditorRevision = 0;
const eventWizardSteps: Array<{
  id: EventWizardStepId;
  label: string;
  summary: string;
}> = [
  {
    id: "template",
    label: "Template",
    summary: "Choose the event workflow.",
  },
  {
    id: "details",
    label: "Details",
    summary: "Add bilingual title, summary, and description.",
  },
  {
    id: "schedule",
    label: "Schedule",
    summary: "Set time, place, status, and publish date.",
  },
  {
    id: "registration",
    label: "Registration",
    summary: "Configure capacity, waitlist, guests, questions, and waivers.",
  },
  {
    id: "media",
    label: "Media",
    summary: "Upload images, video, or YouTube media.",
  },
  {
    id: "communication",
    label: "Communication",
    summary: "Review the email and push copy before sending.",
  },
  {
    id: "review",
    label: "Review",
    summary: "Confirm and save.",
  },
];

function eventWizardStepsForEditor(): typeof eventWizardSteps {
  const stepIds = new Set(eventWizardStepIdsForEventType(state.eventEditor.eventType));
  return eventWizardSteps.filter((step) => stepIds.has(step.id));
}

let announcementComposerRevision = 0;
let imamContentEditorRevision = 0;
let ramadanModeEditorRevision = 0;
let ramadanSetupEditorRevision = 0;
let operationalModeEditorRevision = 0;
let operationalModeEditorDirty = false;
let operationalModeRenderBypass = false;
let operationalModeOriginal: OperationalModeRow | null = null;
let operationalModeEditorScope = "";
let operationalModeNewId = "";
let operationalModeBusy = false;
let operationalModeDiscardPending = false;
let operationalModePendingAction: { modeId: string; action: ModeAction } | null = null;
let operationalModesLoading = true;
let operationalModesLoadError = "";
const iftarSponsorshipDefaultsEditorRevision = 0;
let iftarSponsorshipDayEditorRevision = 0;
let ramadanAdminNoteEditorRevision = 0;
let iftarSlotEditorRevision = 0;
let organizationProfileEditorRevision = 0;
let organizationIslamicComponentsEditorRevision = 0;
let organizationPrayerSettingsEditorRevision = 0;
let spaceEditorRevision = 0;
let spaceAvailabilityBlockEditorRevision = 0;
let spaceSlotEditorRevision = 0;
let volunteerOpportunityEditorRevision = 0;
let volunteerShiftEditorRevision = 0;
let ramadanAdminNoteListenerRows = new Map<string, Map<string, RamadanAdminNoteRow>>();
let spaceAvailabilityBlockListenerRows = new Map<string, SpaceAvailabilityBlockRow[]>();
let spaceReservationRequestListenerRows = new Map<string, SpaceReservationRequestRow[]>();
let volunteerShiftListenerRows = new Map<string, VolunteerShiftRow[]>();
let volunteerSignupListenerRows = new Map<string, VolunteerSignupRow[]>();
let volunteerAttendanceListenerRows = new Map<string, VolunteerAttendanceRow[]>();

function organizationPlanRowFromData(data: Record<string, unknown>): OrganizationPlanRow {
  const entitlements = isStringBooleanRecord(data.defaultEntitlements)
    ? data.defaultEntitlements
    : {};
  const limits = isRecord(data.defaultLimits) ? data.defaultLimits : {};

  return {
    billingInterval: typeof data.billingInterval === "string" ? data.billingInterval : "-",
    displayName: typeof data.displayName === "string" ? data.displayName : "-",
    enabledEntitlementIds: organizationEntitlementIds.filter(
      (entitlementId) => entitlements[entitlementId] === true,
    ),
    organizationLocations:
      typeof limits.organizationLocations === "number" ? limits.organizationLocations : 0,
    planId: typeof data.planId === "string" ? data.planId : "-",
    priceMinor: typeof data.priceMinor === "number" ? data.priceMinor : 0,
    storageBytes: typeof limits.storageBytes === "number" ? limits.storageBytes : 0,
    trialDefaultDays:
      isRecord(data.trial) && typeof data.trial.defaultDays === "number"
        ? data.trial.defaultDays
        : 0,
    trialEligible: isRecord(data.trial) && data.trial.eligible === true,
  };
}

function organizationSubscriptionRowFromData(
  data: Record<string, unknown>,
): OrganizationSubscriptionRow {
  return {
    billingState: typeof data.billingState === "string" ? data.billingState : "-",
    effectiveSubscriptionDiscountBasisPoints:
      typeof data.effectiveSubscriptionDiscountBasisPoints === "number"
        ? data.effectiveSubscriptionDiscountBasisPoints
        : 0,
    foundingStatus: typeof data.foundingStatus === "string" ? data.foundingStatus : "-",
    planId: typeof data.planId === "string" ? data.planId : "-",
    status: typeof data.status === "string" ? data.status : "-",
    trialEndsAt: optionalDateFromFirestore(data.trialEndsAt),
    trialStatus: typeof data.trialStatus === "string" ? data.trialStatus : "-",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
  };
}

function businessDirectoryRowFromData(data: Record<string, unknown>): BusinessDirectoryRow {
  const location = isRecord(data.location) ? data.location : {};
  const verification = isRecord(data.verification) ? data.verification : {};
  const featured = isRecord(data.featured) ? data.featured : {};

  return {
    businessId: typeof data.businessId === "string" ? data.businessId : "-",
    categoryIds: stringArrayFromData(data.categoryIds),
    city: typeof location.city === "string" ? location.city : "-",
    featured: featured.isFeatured === true,
    name: typeof data.name === "string" ? data.name : "-",
    status: typeof data.status === "string" ? data.status : "-",
    verificationState: typeof verification.state === "string" ? verification.state : "unverified",
  };
}

function organizationBusinessAffiliationRowFromData(
  data: Record<string, unknown>,
): OrganizationBusinessAffiliationRow {
  return {
    affiliationType: typeof data.affiliationType === "string" ? data.affiliationType : "-",
    businessId: typeof data.businessId === "string" ? data.businessId : "-",
    businessNameSnapshot:
      typeof data.businessNameSnapshot === "string" ? data.businessNameSnapshot : "-",
    displayOrder: typeof data.displayOrder === "number" ? data.displayOrder : 100,
    organizationId: typeof data.organizationId === "string" ? data.organizationId : "-",
    status: typeof data.status === "string" ? data.status : "-",
    verificationState:
      typeof data.businessVerificationStateSnapshot === "string"
        ? data.businessVerificationStateSnapshot
        : "unverified",
  };
}

function entitlementOverrideRowFromData(data: Record<string, unknown>): EntitlementOverrideRow {
  return {
    entitlementId: typeof data.entitlementId === "string" ? data.entitlementId : "-",
    mode: typeof data.mode === "string" ? data.mode : "-",
    reason: typeof data.reason === "string" ? data.reason : "-",
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function usageLimitRowFromData(data: Record<string, unknown>): UsageLimitRow {
  return {
    canUploadStorage: data.canUploadStorage === true,
    cloudAiMonthlyActionsLimit:
      typeof data.cloudAiMonthlyActionsLimit === "number" ? data.cloudAiMonthlyActionsLimit : 0,
    cloudAiMonthlyActionsUsed:
      typeof data.cloudAiMonthlyActionsUsed === "number" ? data.cloudAiMonthlyActionsUsed : 0,
    organizationLocationsLimit:
      typeof data.organizationLocationsLimit === "number" ? data.organizationLocationsLimit : 0,
    organizationLocationsUsed:
      typeof data.organizationLocationsUsed === "number" ? data.organizationLocationsUsed : 0,
    storageBytesLimit: typeof data.storageBytesLimit === "number" ? data.storageBytesLimit : 0,
    storageBytesUsed: typeof data.storageBytesUsed === "number" ? data.storageBytesUsed : 0,
    storageStatus: typeof data.storageStatus === "string" ? data.storageStatus : "-",
  };
}

function foundingSlotRowFromData(data: Record<string, unknown>): FoundingSlotRow {
  return {
    discountBasisPoints:
      typeof data.discountBasisPoints === "number" ? data.discountBasisPoints : 0,
    qualifiedBy: typeof data.qualifiedBy === "string" ? data.qualifiedBy : "-",
    slotNumber: typeof data.slotNumber === "number" ? data.slotNumber : 0,
    status: typeof data.status === "string" ? data.status : "-",
  };
}

function subscriptionFixtureForOrganization(
  organizationId: string,
): OrganizationSubscriptionRow | undefined {
  const fixture = demoOrganizationSubscriptionConfigs.find(
    (subscription) => subscription.organizationId === organizationId,
  );
  return fixture
    ? organizationSubscriptionRowFromData(fixture as unknown as Record<string, unknown>)
    : undefined;
}

function usageFixtureForOrganization(organizationId: string): UsageLimitRow | undefined {
  const fixture = demoOrganizationUsageLimitSnapshots.find(
    (usage) => usage.organizationId === organizationId,
  );
  return fixture ? usageLimitRowFromData(fixture as unknown as Record<string, unknown>) : undefined;
}

function foundingSlotFixtureForOrganization(organizationId: string): FoundingSlotRow | undefined {
  const fixture = demoFoundingOrganizationSlots.find(
    (slot) => slot.organizationId === organizationId,
  );
  return fixture
    ? foundingSlotRowFromData(fixture as unknown as Record<string, unknown>)
    : undefined;
}

function createShellState(organizationId: string): ShellState {
  return {
    announcementAcknowledgements: [],
    announcementComposer: announcementComposerInputForOrganization(organizationId),
    announcementComposerEditingId: null,
    announcements: [],
    billingError: "",
    busy: false,
    commerceMessage: "",
    commerceStatus: commerceStatusForOrganization(organizationId),
    connectionDecisions: [],
    connectionRequestFilter: "pending",
    connectionRequests: [],
    donations: [],
    donationRefunds: [],
    reconciliationMismatches: [],
    reconciliationRuns: [],
    givingFunds: [],
    campaigns: [],
    businessAffiliations: demoOrganizationBusinessAffiliationProjections
      .filter((affiliation) => affiliation.organizationId === organizationId)
      .map((affiliation) =>
        organizationBusinessAffiliationRowFromData(
          affiliation as unknown as Record<string, unknown>,
        ),
      ),
    businessDirectory: demoBusinessDirectoryBusinesses.map((business) =>
      businessDirectoryRowFromData(business as unknown as Record<string, unknown>),
    ),
    businessDirectoryMessage: "",
    engagementDeliveryLogs: [],
    eventEditor: eventEditorInputForOrganization(organizationId),
    eventEditorError: "",
    eventEditorMessage: "",
    eventEmailDeliveryType: "eventPublished",
    eventEmailTestAddress: "",
    eventEmailDraft: { subject: null, message: null },
    eventEmailTestStatus: null,
    eventImageFile: undefined,
    eventImageFiles: [],
    eventMedia: [],
    eventMediaMessage: "",
    eventPendingMediaPreviews: [],
    eventVideoFile: undefined,
    eventYoutubeUrl: "",
    eventAttendance: [],
    eventCheckInCredential: "",
    eventCheckInMessage: "",
    eventCheckInStationLabel: "front desk",
    eventManualRegistrationSearch: "",
    eventRegistrations: [],
    events: [],
    eventWizardOpen: false,
    eventWizardStepIndex: 0,
    error: "",
    engagementDetailMessage: "",
    imamContentEditor: imamContentEditorInputForOrganization(organizationId),
    imamContentGalleryImageFiles: [],
    imamContentImageFile: undefined,
    imamContentImageDownloadUrls: {},
    imamContents: [],
    iftarAttendanceDefaults: undefined,
    iftarAttendanceDefaultsEditor: buildDefaultIftarAttendanceDefaultsEditorInput(organizationId),
    iftarAttendanceDays: [],
    iftarAttendanceMessage: "",
    iftarAttendanceRegistrations: [],
    iftarSponsorshipDays: [],
    iftarSponsorshipDayEditor: buildDefaultIftarSponsorshipDayEditorInput(organizationId),
    iftarSponsorshipDayEditorMode: "edit",
    iftarSponsorshipDayFilter: "",
    iftarSponsorshipCloseNote: "",
    iftarSponsorshipClosingDayId: "",
    iftarSponsorshipDefaults: undefined,
    iftarSponsorshipDefaultsEditor:
      iftarSponsorshipDefaultsEditorInputForOrganization(organizationId),
    iftarSponsorshipFinance: [],
    iftarSponsorshipManualEditor: {
      dedicationText: "",
      iftarDayId: "iftar-2027-02-08",
      manualPaymentMethod: "cash",
      personId: "dev-hvicc-member",
      quantitySpots: "1",
      recognitionPreference: "publicName",
      sponsorLabel: "HVICC member",
    },
    iftarSponsorshipDeletePromptId: "",
    iftarSponsorshipEditPromptId: "",
    iftarSponsorshipMessage: "",
    iftarSponsorshipSearch: "",
    iftarSponsorshipStatusFilter: "all",
    iftarSponsorshipTransferPromptId: "",
    iftarTransfers: [],
    iftarSlotEditor: iftarSlotEditorInputForOrganization(organizationId),
    iftarSlots: [],
    intelligenceRequests: [],
    intelligenceSnapshots: demoOrganizationIntelligenceSnapshots
      .filter((snapshot) => snapshot.organizationId === organizationId)
      .map((snapshot) => ({
        id: snapshot.id,
        acknowledgementCount: snapshot.acknowledgementCount,
        activeSpacesCount: snapshot.activeSpacesCount,
        availableIftarSlotsCount: snapshot.availableIftarSlotsCount,
        availableSpaceSlotsCount: snapshot.availableSpaceSlotsCount,
        connectedPeopleCount: snapshot.connectedPeopleCount,
        confirmedDonationTotalCents: snapshot.confirmedDonationTotalCents,
        generatedAt: new Date(snapshot.generatedAt),
        publishedAnnouncementsCount: snapshot.publishedAnnouncementsCount,
        recommendation: snapshot.recommendation,
        recommendationAr: snapshot.recommendationAr,
        riskLevel: snapshot.riskLevel,
        summary: snapshot.summary,
        summaryAr: snapshot.summaryAr,
        title: snapshot.title,
        titleAr: snapshot.titleAr,
        upcomingEventsCount: snapshot.upcomingEventsCount,
      })),
    locale: "en",
    organizationConnections: [],
    organizationEntitlementOverrides: demoOrganizationEntitlementOverrides
      .filter((override) => override.organizationId === organizationId)
      .map((override) =>
        entitlementOverrideRowFromData(override as unknown as Record<string, unknown>),
      ),
    operationalModeEditor: buildDefaultOperationalModeEditorInput(),
    operationalModeError: "",
    operationalModeMessage: "",
    operationalModeProjection: undefined,
    operationalModes: [],
    organizationPlanCatalog: demoOrganizationPlanCatalog.map((plan) =>
      organizationPlanRowFromData(plan as unknown as Record<string, unknown>),
    ),
    organizationProfile: undefined,
    organizationProfileEditor: organizationProfileEditorInputForOrganization(organizationId),
    organizationProfileLogoDownloadPath: null,
    organizationProfileLogoDownloadUrl: null,
    organizationProfileLogoError: "",
    organizationProfileLogoFile: undefined,
    organizationProfileLogoPreviewUrl: null,
    organizationProfileMessage: "",
    organizationProfileRequests: [],
    organizationIslamicComponents: undefined,
    organizationIslamicComponentsEditor:
      organizationIslamicComponentsEditorInputForOrganization(organizationId),
    organizationIslamicComponentsMessage: "",
    organizationPrayerSettings: undefined,
    organizationPrayerSettingsEditor:
      organizationPrayerSettingsEditorInputForOrganization(organizationId),
    organizationPrayerSettingsMessage: "",
    organizationId,
    organizationRoleAssignments: [],
    personProfiles: {},
    memberSupportAssignmentFilter: "all",
    memberSupportCategoryFilter: "all",
    memberSupportError: "",
    memberSupportMessages: [],
    memberSupportMessage: "",
    memberSupportPriorityFilter: "all",
    memberSupportSearch: "",
    memberSupportSelectedTicketId: "",
    memberSupportStatusFilter: "all",
    memberSupportTickets: [],
    peopleListSearch: "",
    peopleManagerSelectedPersonId: "",
    roleManagementMessage: "",
    organizationSubscription: subscriptionFixtureForOrganization(organizationId),
    organizationUsage: usageFixtureForOrganization(organizationId),
    platformIslamicComponents: approvedIslamicComponentDefinitions,
    foundingSlot: foundingSlotFixtureForOrganization(organizationId),
    ramadanModeEditor: ramadanModeEditorInputForOrganization(organizationId),
    ramadanModes: [],
    ramadanAdminNoteCategoryFilter: "all",
    ramadanAdminNoteDayFilter: "",
    ramadanAdminNoteEditor: buildDefaultRamadanAdminNoteEditorInput(organizationId),
    ramadanAdminNoteMessage: "",
    ramadanAdminNotes: [],
    ramadanAdminNoteStatusFilter: "active",
    ramadanSetupEditor: ramadanSetupEditorInputForOrganization(organizationId),
    ramadanSetupEditConfirmationPending: false,
    ramadanHistory: [],
    ramadanSetupMessage: "",
    ramadanSetups: [],
    spaceAvailabilityBlockEditor: buildDefaultSpaceAvailabilityBlockEditorInput(),
    spaceAvailabilityBlocks: [],
    spaceEditor: spaceEditorInputForOrganization(organizationId),
    spaceReservationRequests: [],
    spaceReservations: [],
    spaceSlotEditor: spaceSlotEditorInputForOrganization(organizationId),
    spaceSlots: [],
    spaces: [],
    shdeedSupportAssignmentFilter: "all",
    shdeedSupportCategoryFilter: "all",
    shdeedSupportError: "",
    shdeedSupportMessages: [],
    shdeedSupportMessage: "",
    shdeedSupportPriorityFilter: "all",
    shdeedSupportSearch: "",
    shdeedSupportSelectedTicketId: "",
    shdeedSupportStatusFilter: "all",
    shdeedSupportTickets: [],
    sponsorships: [],
    status: "Waiting for emulator data...",
    timelineEntries: [],
    volunteerOpportunities: [],
    volunteerOpportunityEditor: volunteerOpportunityEditorInputForOrganization(organizationId),
    volunteerShiftEditor: volunteerShiftEditorInputForOrganization(organizationId),
    volunteerShifts: [],
    volunteerSignups: [],
    volunteerAttendances: [],
    volunteerRecognitionAwards: [],
    volunteerServiceSummaries: [],
    userUid: "",
    version: "-",
  };
}

const state = createShellState("");
const tenantListeners = createTenantListenerScope();
const onSnapshot = tenantListeners.onSnapshot;
let organizationAccess: OrganizationAccessState = { organizations: [], loading: true, error: "" };
let unsubscribeOrganizationAccess: Unsubscribe | undefined;
let loadedOrganizationRoles = "";
let retiringTenant = false;
let supportStaff: { personId: string; displayName: string }[] = [];
let connectedRamadanId: string | undefined;
let authenticationState: WebAuthenticationState = { status: "initializing" };
let authenticationSafetyMessage = "";
let currentPersonState: CurrentPersonState = { status: "idle" };
let currentPersonRequestId = 0;
let currentRamadanHistoryRequestId = 0;
let activeTaskWindowId: string | null = null;
let activeEventOperationsWindowEventId: string | null = null;
let eventTableSortKey: EventTableSortKey = "startsAt";
let eventTableSortDirection: "asc" | "desc" = "asc";
let eventListSearch = "";
let eventListPage = 1;
let eventRegistrationPage = 1;
let eventWaitlistPage = 1;
let eventAttendancePage = 1;
const eventListPageSize = 10;
const eventOperationsPageSize = 8;
const genericListPageSize = 12;
const genericListUiState = new Map<string, { page: number; query: string }>();
type SearchableCollectionUiState = {
  query: string;
  sortColumnIndex: number | null;
  sortDirection: "asc" | "desc";
};
const searchableCollectionUiState = new Map<string, SearchableCollectionUiState>();
let accountProfileEditorOpen = false;
const authenticationForm: AuthenticationFormState = {
  busy: false,
  email: "",
  error: "",
  password: "",
  resetSent: false,
};
const accountProfileEditor: AccountProfileEditorState = {
  busy: false,
  displayName: "",
  error: "",
  message: "",
  phoneNumber: "",
  photoUrl: "",
};

function formatAmount(amountCents: number): string {
  const amount = amountCents / 100;
  return Number.isInteger(amount) ? `$${amount.toFixed(0)}` : `$${amount.toFixed(2)}`;
}

function formatOptionalAmount(amountCents: number | null): string {
  return amountCents === null ? "No legacy price" : `${formatAmount(amountCents)}/hr`;
}

function spaceOperatingHoursFromData(value: unknown): SpaceOperatingHours {
  const record = isRecord(value) ? value : {};

  return {
    friday: typeof record.friday === "string" ? record.friday : "closed",
    monday: typeof record.monday === "string" ? record.monday : "closed",
    saturday: typeof record.saturday === "string" ? record.saturday : "closed",
    sunday: typeof record.sunday === "string" ? record.sunday : "closed",
    thursday: typeof record.thursday === "string" ? record.thursday : "closed",
    tuesday: typeof record.tuesday === "string" ? record.tuesday : "closed",
    wednesday: typeof record.wednesday === "string" ? record.wednesday : "closed",
  };
}

function stringArrayFromData(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function compactSpaceHours(hours: SpaceOperatingHours): string {
  const weekday = [hours.monday, hours.tuesday, hours.wednesday, hours.thursday, hours.friday];
  const weekend = [hours.saturday, hours.sunday];
  const weekdayLabel = weekday.every((value) => value === weekday[0]) ? weekday[0] : "varies";
  const weekendLabel = weekend.every((value) => value === weekend[0]) ? weekend[0] : "varies";
  return `Weekdays ${weekdayLabel}; weekends ${weekendLabel}`;
}

function minorToDollarInput(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

function parseDollarInputToMinor(value: string): number {
  const normalized = value.trim();
  if (!/^\d+(\.\d{1,2})?$/u.test(normalized)) {
    throw new Error("Amount must be a valid dollar value.");
  }
  const [dollars = "0", cents = ""] = normalized.split(".");
  return Number.parseInt(dollars, 10) * 100 + Number.parseInt(cents.padEnd(2, "0"), 10);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function donationDestinationLabel(data: Record<string, unknown>): string {
  if (typeof data.campaignTitleSnapshot === "string" && data.campaignTitleSnapshot.length > 0) {
    return data.campaignTitleSnapshot;
  }

  if (typeof data.fundNameSnapshot === "string" && data.fundNameSnapshot.length > 0) {
    return data.fundNameSnapshot;
  }

  return "General donation";
}

function formValue(data: FormData, name: string, fallback: string): string {
  const value = data.get(name);
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
}

function nullableFormValue(data: FormData, name: string): string | null {
  const value = data.get(name);
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function formIntegerValue(data: FormData, name: string, fallback: number): number {
  const value = Number.parseInt(formValue(data, name, String(fallback)), 10);
  return Number.isSafeInteger(value) && value >= 0 ? value : fallback;
}

function formNullableUsdMinorValue(data: FormData, name: string): number | null {
  const rawValue = nullableFormValue(data, name);
  if (rawValue === null) {
    return null;
  }

  const amount = Number.parseFloat(rawValue);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }

  return Math.round(amount * 100);
}

function timestampFromFormDate(data: FormData, name: string): Timestamp | null {
  const rawValue = nullableFormValue(data, name);
  if (rawValue === null) {
    return null;
  }

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

function optionalDateFromFirestore(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (typeof value === "string") {
    const millis = Date.parse(value);
    return Number.isNaN(millis) ? undefined : new Date(millis);
  }

  return undefined;
}

function addDaysToLocalDate(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function daysBetweenLocalDates(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00.000Z`);
  const endDate = new Date(`${end}T00:00:00.000Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 0;
  }
  return Math.floor((endDate.getTime() - startDate.getTime()) / 86_400_000);
}

function ramadanDayCount(startsOn: string, endsOn: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(startsOn) || !/^\d{4}-\d{2}-\d{2}$/u.test(endsOn)) {
    return 0;
  }
  return Math.max(daysBetweenLocalDates(startsOn, endsOn) + 1, 0);
}

function buildDefaultTahajjudSchedule(startsOn: string, endsOn: string, startTime: string): string {
  const dayCount = Math.min(ramadanDayCount(startsOn, endsOn), 10);
  return Array.from({ length: dayCount }, (_, index) => {
    const date = addDaysToLocalDate(startsOn, index);
    return `${date} ${startTime} Tahajjud`;
  }).join("\n");
}

function localizedTextFromData(value: unknown, fallback = ""): RamadanLocalizedText {
  const record = isRecord(value) ? value : {};
  return {
    ar: typeof record.ar === "string" && record.ar.length > 0 ? record.ar : null,
    en: typeof record.en === "string" && record.en.length > 0 ? record.en : fallback,
  };
}

function optionalLocalizedTextFromData(value: unknown): RamadanLocalizedText | null {
  if (!isRecord(value)) {
    return null;
  }

  const text = localizedTextFromData(value);
  return text.en.length > 0 || text.ar !== null ? text : null;
}

function campusPrayerConfigsFromData(
  value: unknown,
  defaultCampusId: string,
): RamadanCampusPrayerConfiguration[] {
  if (!Array.isArray(value)) {
    return [emptyCampusPrayerConfig(defaultCampusId)];
  }

  const configs = value.flatMap((item): RamadanCampusPrayerConfiguration[] => {
    if (!isRecord(item)) {
      return [];
    }
    const campusId = typeof item.campusId === "string" ? item.campusId : defaultCampusId;
    const prayerOverrides = Array.isArray(item.prayerOverrides)
      ? item.prayerOverrides.flatMap(
          (override): RamadanCampusPrayerConfiguration["prayerOverrides"] => {
            if (!isRecord(override) || !isRamadanPrayerId(override.prayerId)) {
              return [];
            }
            return [
              {
                displayTime:
                  typeof override.displayTime === "string" && override.displayTime.length > 0
                    ? override.displayTime
                    : null,
                enabled: override.enabled === true,
                iqamaTime:
                  typeof override.iqamaTime === "string" && override.iqamaTime.length > 0
                    ? override.iqamaTime
                    : null,
                note: optionalLocalizedTextFromData(override.note),
                prayerId: override.prayerId,
              },
            ];
          },
        )
      : [];
    const sunrise = isRecord(item.sunrise) ? item.sunrise : {};
    return [
      {
        campusId,
        prayerOverrides,
        sunrise: {
          display: sunrise.display === true,
          label: optionalLocalizedTextFromData(sunrise.label),
        },
        timezone:
          typeof item.timezone === "string" && item.timezone.length > 0 ? item.timezone : null,
      },
    ];
  });

  return configs.length > 0 ? configs : [emptyCampusPrayerConfig(defaultCampusId)];
}

function emptyCampusPrayerConfig(campusId: string): RamadanCampusPrayerConfiguration {
  return {
    campusId,
    prayerOverrides: [],
    sunrise: { display: false, label: null },
    timezone: null,
  };
}

function lastTenNightsFromData(value: unknown): RamadanLastTenNightsConfiguration {
  const record = isRecord(value) ? value : {};
  return {
    approvedStartsOn:
      typeof record.approvedStartsOn === "string" && record.approvedStartsOn.length > 0
        ? record.approvedStartsOn
        : null,
    enabled: record.enabled !== false,
    homeMessage: localizedTextFromData(record.homeMessage, "Last ten nights"),
    homeTitle: localizedTextFromData(record.homeTitle, "Last ten nights"),
    qiyamPriority: record.qiyamPriority !== false,
  };
}

function taraweehConfigsFromData(
  value: unknown,
  defaultCampusId: string,
): RamadanTaraweehCampusConfiguration[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item): RamadanTaraweehCampusConfiguration[] => {
    if (!isRecord(item)) {
      return [];
    }
    return [
      {
        campusId: typeof item.campusId === "string" ? item.campusId : defaultCampusId,
        enabled: item.enabled === true,
        notes: optionalLocalizedTextFromData(item.notes),
        rakahCount: typeof item.rakahCount === "number" ? item.rakahCount : null,
        relativeToIshaMinutes:
          typeof item.relativeToIshaMinutes === "number" ? item.relativeToIshaMinutes : null,
        startTime:
          typeof item.startTime === "string" && item.startTime.length > 0 ? item.startTime : null,
      },
    ];
  });
}

function qiyamConfigsFromData(
  value: unknown,
  defaultCampusId: string,
): RamadanQiyamCampusConfiguration[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item): RamadanQiyamCampusConfiguration[] => {
    if (!isRecord(item)) {
      return [];
    }
    return [
      {
        campusId: typeof item.campusId === "string" ? item.campusId : defaultCampusId,
        enabled: item.enabled === true,
        endsOn: typeof item.endsOn === "string" && item.endsOn.length > 0 ? item.endsOn : null,
        notes: optionalLocalizedTextFromData(item.notes),
        startsOn:
          typeof item.startsOn === "string" && item.startsOn.length > 0 ? item.startsOn : null,
        startTime:
          typeof item.startTime === "string" && item.startTime.length > 0 ? item.startTime : null,
      },
    ];
  });
}

function iqamaPhasesFromData(value: unknown): RamadanIqamaPhaseConfiguration[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item): RamadanIqamaPhaseConfiguration[] => {
    if (!isRecord(item) || !isRamadanIqamaPhaseId(item.phaseId)) {
      return [];
    }
    const prayerOverrides = prayerOverridesFromData(item.prayerOverrides);
    if (
      typeof item.startsOn !== "string" ||
      item.startsOn.length === 0 ||
      typeof item.endsOn !== "string" ||
      item.endsOn.length === 0
    ) {
      return [];
    }
    return [
      {
        endsOn: item.endsOn,
        label: localizedTextFromData(item.label, iqamaPhaseLabel(item.phaseId)),
        phaseId: item.phaseId,
        prayerOverrides,
        startsOn: item.startsOn,
      },
    ];
  });
}

function tahajjudNightsFromData(value: unknown): RamadanTahajjudNightConfiguration[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item): RamadanTahajjudNightConfiguration[] => {
    if (
      !isRecord(item) ||
      typeof item.nightId !== "string" ||
      typeof item.gregorianDate !== "string" ||
      typeof item.startTime !== "string"
    ) {
      return [];
    }
    return [
      {
        enabled: item.enabled !== false,
        gregorianDate: item.gregorianDate,
        nightId: item.nightId,
        notes: optionalLocalizedTextFromData(item.notes),
        startTime: item.startTime,
      },
    ];
  });
}

function guestImamsFromData(value: unknown): RamadanGuestImamConfiguration {
  if (!isRecord(value) || !Array.isArray(value.profiles)) {
    return { enabled: false, profiles: [] };
  }

  return {
    enabled: value.enabled === true,
    profiles: value.profiles
      .flatMap((item): RamadanGuestImamConfiguration["profiles"] => {
        if (!isRecord(item) || typeof item.imamId !== "string") {
          return [];
        }
        const displayName = localizedTextFromData(item.displayName, "");
        if (!displayName.en) {
          return [];
        }
        return [
          {
            bio: optionalLocalizedTextFromData(item.bio),
            displayName,
            enabled: item.enabled !== false,
            imamId: item.imamId,
            photoUrl:
              typeof item.photoUrl === "string" && item.photoUrl.length > 0 ? item.photoUrl : null,
            sortOrder: typeof item.sortOrder === "number" ? item.sortOrder : 0,
            title: optionalLocalizedTextFromData(item.title),
          },
        ];
      })
      .sort((left, right) => left.sortOrder - right.sortOrder),
  };
}

function prayerOverridesFromData(
  value: unknown,
): RamadanCampusPrayerConfiguration["prayerOverrides"] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((override): RamadanCampusPrayerConfiguration["prayerOverrides"] => {
    if (!isRecord(override) || !isRamadanPrayerId(override.prayerId)) {
      return [];
    }
    return [
      {
        displayTime:
          typeof override.displayTime === "string" && override.displayTime.length > 0
            ? override.displayTime
            : null,
        enabled: override.enabled === true,
        iqamaTime:
          typeof override.iqamaTime === "string" && override.iqamaTime.length > 0
            ? override.iqamaTime
            : null,
        note: optionalLocalizedTextFromData(override.note),
        prayerId: override.prayerId,
      },
    ];
  });
}

function isRamadanIqamaPhaseId(value: unknown): value is RamadanIqamaPhaseConfiguration["phaseId"] {
  return value === "firstTen" || value === "secondTen" || value === "lastTen";
}

function iqamaPhaseLabel(phaseId: RamadanIqamaPhaseConfiguration["phaseId"]): string {
  if (phaseId === "firstTen") {
    return "First 10 days";
  }
  if (phaseId === "secondTen") {
    return "Second 10 days";
  }
  return "Last 10 days";
}

function operationalModeRowFromData(data: Record<string, unknown>): OperationalModeRow {
  const home = isRecord(data.home) ? data.home : {};
  const displayName = isRecord(data.displayName) ? data.displayName : {};
  const timeline = isRecord(data.timeline) ? data.timeline : {};
  const pinnedRefs = Array.isArray(home.pinnedRefs)
    ? home.pinnedRefs.flatMap((value): Array<{ collection: string; id: string }> => {
        if (
          !isRecord(value) ||
          typeof value.collection !== "string" ||
          typeof value.id !== "string"
        ) {
          return [];
        }
        return [{ collection: value.collection, id: value.id }];
      })
    : [];
  return {
    configuration: {
      activationPolicy: data.activationPolicy,
      featureConfig: data.featureConfig,
      navigation: data.navigation,
      home: {
        ...home,
        countdownTargetAt: optionalDateFromFirestore(home.countdownTargetAt)?.toISOString() ?? null,
      },
    },
    actionShortcutIds: stringArrayFromData(home.actionShortcutIds),
    displayName: typeof displayName.en === "string" ? displayName.en : "-",
    displayNameAr: typeof displayName.ar === "string" ? displayName.ar : null,
    emphasizedAnnouncementIds: stringArrayFromData(timeline.emphasizedAnnouncementIds),
    emphasizedEntryIds: stringArrayFromData(timeline.emphasizedEntryIds),
    emphasizedEventIds: stringArrayFromData(timeline.emphasizedEventIds),
    endsAt: optionalDateFromFirestore(data.endsAt),
    id: typeof data.modeId === "string" ? data.modeId : "-",
    modeId: typeof data.modeId === "string" ? data.modeId : "-",
    pinnedRefs,
    priority: typeof data.priority === "number" ? data.priority : 0,
    prioritizedModuleIds: stringArrayFromData(home.prioritizedModuleIds),
    priorityTags: stringArrayFromData(timeline.priorityTags),
    startsAt: optionalDateFromFirestore(data.startsAt),
    status: typeof data.status === "string" ? data.status : "-",
    subtitle: typeof home.subtitle === "string" ? home.subtitle : "",
    subtitleAr: typeof home.subtitleAr === "string" ? home.subtitleAr : null,
    title: typeof home.title === "string" ? home.title : "-",
    titleAr: typeof home.titleAr === "string" ? home.titleAr : null,
    type: typeof data.type === "string" ? data.type : "-",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
    visibility: typeof data.visibility === "string" ? data.visibility : "-",
  };
}

function operationalModeEditorInputFromRow(mode: OperationalModeRow): OperationalModeEditorInput {
  return {
    displayName: mode.displayName,
    displayNameAr: mode.displayNameAr ?? "",
    emphasizedEntryIds: mode.emphasizedEntryIds.join(","),
    endsAt: localModeDate(mode.endsAt),
    modeId: mode.modeId,
    prioritizedModuleIds: mode.prioritizedModuleIds.join(","),
    priority: String(mode.priority),
    quickActionIds: mode.actionShortcutIds.join(","),
    startsAt: localModeDate(mode.startsAt),
    status:
      mode.status === "active" ? "active" : mode.status === "scheduled" ? "scheduled" : "draft",
    subtitle: mode.subtitle,
    subtitleAr: mode.subtitleAr ?? "",
    title: mode.title,
    titleAr: mode.titleAr ?? "",
    type: mode.type,
    visibility: mode.visibility === "private" ? "private" : "connected",
  };
}

function ramadanSetupRowFromData(data: Record<string, unknown>, id: string): RamadanSetupRow {
  const home = isRecord(data.home) ? data.home : {};
  const title = isRecord(home.title) ? home.title : {};
  const computed = isRecord(data.computed) ? data.computed : {};
  const defaultCampusId =
    typeof data.defaultCampusId === "string" && data.defaultCampusId.length > 0
      ? data.defaultCampusId
      : "main-campus";
  const status = isRamadanSetupStatus(data.status) ? data.status : "draft";
  return {
    campusPrayerConfigs: campusPrayerConfigsFromData(data.campusPrayerConfigs, defaultCampusId),
    defaultCampusId,
    endsOn: typeof data.endsOn === "string" ? data.endsOn : "-",
    experienceOpensAt: typeof data.experienceOpensAt === "string" ? data.experienceOpensAt : null,
    featuredRamadanContentId:
      typeof data.featuredRamadanContentId === "string" ? data.featuredRamadanContentId : null,
    guestImams: guestImamsFromData(data.guestImams),
    hijriYear: typeof data.hijriYear === "number" ? data.hijriYear : 0,
    id,
    iqamaPhases: iqamaPhasesFromData(data.iqamaPhases),
    lastTenNights: lastTenNightsFromData(data.lastTenNights),
    lastTenNightsActive: computed.lastTenNightsActive === true,
    linkedOperationalModeId:
      typeof data.linkedOperationalModeId === "string" ? data.linkedOperationalModeId : "-",
    qiyam: qiyamConfigsFromData(data.qiyam, defaultCampusId),
    ramadanId: typeof data.ramadanId === "string" ? data.ramadanId : id,
    ramadanDay: typeof computed.ramadanDay === "number" ? computed.ramadanDay : null,
    startsOn: typeof data.startsOn === "string" ? data.startsOn : "-",
    status,
    sponsorshipOpensAt:
      typeof data.sponsorshipOpensAt === "string" ? data.sponsorshipOpensAt : null,
    tahajjudNights: tahajjudNightsFromData(data.tahajjudNights),
    taraweeh: taraweehConfigsFromData(data.taraweeh, defaultCampusId),
    timezone: typeof data.timezone === "string" ? data.timezone : "America/New_York",
    title: typeof title.en === "string" ? title.en : id,
    titleAr: typeof title.ar === "string" ? title.ar : null,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function ramadanHistoryRowFromData(data: Record<string, unknown>, id: string): RamadanHistoryRow {
  const setupSnapshot = isRecord(data.setupSnapshot) ? data.setupSnapshot : {};
  const setupRow = ramadanSetupRowFromData(setupSnapshot, id);
  const startsOn =
    typeof data.startsOn === "string"
      ? data.startsOn
      : setupRow.startsOn !== "-"
        ? setupRow.startsOn
        : "-";
  const endsOn =
    typeof data.endsOn === "string" ? data.endsOn : setupRow.endsOn !== "-" ? setupRow.endsOn : "-";
  return {
    completedAt: optionalDateFromFirestore(data.completedAt),
    dayCount: ramadanDayCount(startsOn, endsOn),
    endsOn,
    historyId: typeof data.historyId === "string" ? data.historyId : id,
    hijriYear: typeof data.hijriYear === "number" ? data.hijriYear : setupRow.hijriYear,
    ramadanId: typeof data.ramadanId === "string" ? data.ramadanId : setupRow.ramadanId,
    startsOn,
    status: typeof data.status === "string" ? data.status : "completed",
    title: setupRow.title,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function iftarSponsorshipDefaultsRowFromData(
  data: Record<string, unknown>,
): IftarSponsorshipDefaultsRow {
  const publicNotes = isRecord(data.publicNotes) ? data.publicNotes : {};
  return {
    capacityByWeekday: iftarSponsorshipCapacityByWeekdayFromData(data),
    currency: typeof data.currency === "string" ? data.currency : "usd",
    defaultPricePerSpotMinor:
      typeof data.defaultPricePerSpotMinor === "number" ? data.defaultPricePerSpotMinor : 0,
    fridayCapacitySpots:
      typeof data.fridayCapacitySpots === "number" ? data.fridayCapacitySpots : 0,
    programEnabled: data.programEnabled === true,
    publicNotes: typeof publicNotes.en === "string" ? publicNotes.en : "",
    publicNotesAr: typeof publicNotes.ar === "string" ? publicNotes.ar : null,
    saturdayCapacitySpots:
      typeof data.saturdayCapacitySpots === "number" ? data.saturdayCapacitySpots : 0,
    sponsorshipClosesDayOffset:
      typeof data.sponsorshipClosesDayOffset === "number" ? data.sponsorshipClosesDayOffset : 0,
    sponsorshipClosesLocalTime:
      typeof data.sponsorshipClosesLocalTime === "string" ? data.sponsorshipClosesLocalTime : "",
    sponsorshipOpensAt:
      typeof data.sponsorshipOpensAt === "string" ? data.sponsorshipOpensAt : null,
    standardCapacitySpots:
      typeof data.standardCapacitySpots === "number" ? data.standardCapacitySpots : 0,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function iftarSponsorshipDayRowFromData(
  data: Record<string, unknown>,
  id: string,
): IftarSponsorshipDayRow {
  const publicNotes = isRecord(data.publicNotes) ? data.publicNotes : {};
  return {
    capacitySpots: typeof data.capacitySpots === "number" ? data.capacitySpots : 0,
    confirmedSpots: typeof data.confirmedSpots === "number" ? data.confirmedSpots : 0,
    currency: typeof data.currency === "string" ? data.currency : "usd",
    gregorianDate: typeof data.gregorianDate === "string" ? data.gregorianDate : "-",
    id,
    iftarDayId: typeof data.iftarDayId === "string" ? data.iftarDayId : id,
    pricePerSpotMinor: typeof data.pricePerSpotMinor === "number" ? data.pricePerSpotMinor : 0,
    publicNotes: typeof publicNotes.en === "string" ? publicNotes.en : null,
    publicNotesAr: typeof publicNotes.ar === "string" ? publicNotes.ar : null,
    ramadanDay: typeof data.ramadanDay === "number" ? data.ramadanDay : 0,
    reservedSpots: typeof data.reservedSpots === "number" ? data.reservedSpots : 0,
    sponsorshipClosesAt:
      typeof data.sponsorshipClosesAt === "string" ? data.sponsorshipClosesAt : "",
    sponsorshipEnabled: data.sponsorshipEnabled === true,
    sponsorshipOpensAt:
      typeof data.sponsorshipOpensAt === "string" ? data.sponsorshipOpensAt : null,
    status: typeof data.status === "string" ? data.status : "-",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
    weekday: typeof data.weekday === "string" ? data.weekday : "-",
  };
}

function iftarSponsorshipDayEditorInputFromRow(
  row: IftarSponsorshipDayRow,
): IftarSponsorshipDayEditorInput {
  return {
    capacitySpots: String(row.capacitySpots),
    gregorianDate: row.gregorianDate,
    iftarDayId: row.id,
    priceDollars: minorToDollarInput(row.pricePerSpotMinor),
    publicNotes: row.publicNotes ?? "",
    publicNotesAr: row.publicNotesAr ?? "",
    sponsorshipClosesAt: row.sponsorshipClosesAt,
    sponsorshipEnabled: row.sponsorshipEnabled ? "true" : "false",
    sponsorshipOpensAt: row.sponsorshipOpensAt ?? "",
    status: row.status === "open" || row.status === "closed" ? row.status : "draft",
  };
}

function iftarAttendanceDefaultsRowFromData(
  data: Record<string, unknown>,
): IftarAttendanceDefaultsRow {
  const publicNotes = isRecord(data.publicNotes) ? data.publicNotes : {};
  return {
    defaultCapacityPeople:
      typeof data.defaultCapacityPeople === "number" ? data.defaultCapacityPeople : 0,
    programEnabled: data.programEnabled === true,
    publicNotes: typeof publicNotes.en === "string" ? publicNotes.en : "",
    publicNotesAr: typeof publicNotes.ar === "string" ? publicNotes.ar : null,
    registrationClosesDayOffset:
      typeof data.registrationClosesDayOffset === "number" ? data.registrationClosesDayOffset : 0,
    registrationClosesLocalTime:
      typeof data.registrationClosesLocalTime === "string" ? data.registrationClosesLocalTime : "",
    registrationOpensAt:
      typeof data.registrationOpensAt === "string" ? data.registrationOpensAt : null,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function iftarAttendanceDayRowFromData(
  data: Record<string, unknown>,
  id: string,
): IftarAttendanceDayRow {
  const publicNotes = isRecord(data.publicNotes) ? data.publicNotes : {};
  return {
    attendanceCapacityPeople:
      typeof data.attendanceCapacityPeople === "number" ? data.attendanceCapacityPeople : 0,
    attendanceDayId: typeof data.attendanceDayId === "string" ? data.attendanceDayId : id,
    attendanceEnabled: data.attendanceEnabled === true,
    confirmedAttendeeCount:
      typeof data.confirmedAttendeeCount === "number" ? data.confirmedAttendeeCount : 0,
    gregorianDate: typeof data.gregorianDate === "string" ? data.gregorianDate : "-",
    id,
    publicNotes: typeof publicNotes.en === "string" ? publicNotes.en : null,
    publicNotesAr: typeof publicNotes.ar === "string" ? publicNotes.ar : null,
    ramadanDay: typeof data.ramadanDay === "number" ? data.ramadanDay : 0,
    registrationClosesAt:
      typeof data.registrationClosesAt === "string" ? data.registrationClosesAt : "",
    registrationOpensAt:
      typeof data.registrationOpensAt === "string" ? data.registrationOpensAt : null,
    status: typeof data.status === "string" ? data.status : "-",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
    weekday: typeof data.weekday === "string" ? data.weekday : "-",
  };
}

function iftarAttendanceRegistrationRowFromData(
  data: Record<string, unknown>,
  id: string,
): IftarAttendanceRegistrationRow {
  return {
    attendeeCount: typeof data.attendeeCount === "number" ? data.attendeeCount : 0,
    attendanceDayId: typeof data.attendanceDayId === "string" ? data.attendanceDayId : "-",
    confirmationCode: typeof data.confirmationCode === "string" ? data.confirmationCode : "-",
    gregorianDateSnapshot:
      typeof data.gregorianDateSnapshot === "string" ? data.gregorianDateSnapshot : null,
    id,
    personId: typeof data.personId === "string" ? data.personId : "-",
    ramadanDaySnapshot:
      typeof data.ramadanDaySnapshot === "number" ? data.ramadanDaySnapshot : null,
    registrationId: typeof data.registrationId === "string" ? data.registrationId : id,
    status: typeof data.status === "string" ? data.status : "-",
    updatedAt: optionalDateFromFirestore(data.updatedAt),
  };
}

function iftarSponsorshipAdminRowFromData(
  data: Record<string, unknown>,
  id: string,
): IftarSponsorshipAdminRow {
  return {
    id,
    basePricePerSpotMinorSnapshot:
      typeof data.basePricePerSpotMinorSnapshot === "number"
        ? data.basePricePerSpotMinorSnapshot
        : null,
    createdAt: optionalDateFromFirestore(data.createdAt),
    dedicationText: typeof data.dedicationText === "string" ? data.dedicationText : null,
    financeVerificationStatus:
      typeof data.financeVerificationStatus === "string"
        ? data.financeVerificationStatus
        : "notRequired",
    gregorianDate: typeof data.gregorianDate === "string" ? data.gregorianDate : "",
    iftarDayId: typeof data.iftarDayId === "string" ? data.iftarDayId : "-",
    organizationFeeLinesSnapshot: iftarSponsorshipFeeLinesFromData(
      data.organizationFeeLinesSnapshot,
    ),
    paymentStatus: typeof data.paymentStatus === "string" ? data.paymentStatus : "unknown",
    paymentSource: typeof data.paymentSource === "string" ? data.paymentSource : "onlineStripe",
    pricePerSpotMinorSnapshot:
      typeof data.pricePerSpotMinorSnapshot === "number" ? data.pricePerSpotMinorSnapshot : null,
    publicDisplayNameSnapshot:
      typeof data.publicDisplayNameSnapshot === "string" ? data.publicDisplayNameSnapshot : null,
    quantitySpots: typeof data.quantitySpots === "number" ? data.quantitySpots : 0,
    ramadanDay: typeof data.ramadanDay === "number" ? data.ramadanDay : 0,
    ramadanId: typeof data.ramadanId === "string" ? data.ramadanId : "-",
    receiptAvailable: data.receiptAvailable === true,
    recognitionPreference:
      typeof data.recognitionPreference === "string" ? data.recognitionPreference : "none",
    reservationStatus:
      typeof data.reservationStatus === "string" ? data.reservationStatus : "unknown",
    safeSponsorLabel:
      typeof data.safeSponsorLabel === "string" ? data.safeSponsorLabel : "Private sponsor",
    source: typeof data.source === "string" ? data.source : "-",
    spotDetails: iftarSponsorshipSpotDetailsFromData(data.spotDetails),
    sponsorshipId: typeof data.sponsorshipId === "string" ? data.sponsorshipId : id,
    status: typeof data.status === "string" ? data.status : "-",
    totalPaymentAmountMinor:
      typeof data.totalPaymentAmountMinor === "number" ? data.totalPaymentAmountMinor : null,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
  };
}

function iftarSponsorshipFeeLinesFromData(value: unknown): IftarSponsorshipFeeLineRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((line): IftarSponsorshipFeeLineRow | null => {
      if (!isRecord(line)) {
        return null;
      }
      const label = localizedTextFromData(line.label, "Fee");
      return {
        active: line.active === true,
        amountMinor: typeof line.amountMinor === "number" ? line.amountMinor : 0,
        displayOrder: typeof line.displayOrder === "number" ? line.displayOrder : 0,
        feeLineId: typeof line.feeLineId === "string" ? line.feeLineId : "fee",
        label: state.locale === "ar" && label.ar ? label.ar : label.en,
      };
    })
    .filter((line): line is IftarSponsorshipFeeLineRow => line !== null)
    .sort((left, right) => left.displayOrder - right.displayOrder);
}

function iftarSponsorshipSpotDetailsFromData(value: unknown): IftarSponsorshipSpotDetailRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((spot, index): IftarSponsorshipSpotDetailRow | null => {
      if (!isRecord(spot)) {
        return null;
      }
      const recognitionNames = Array.isArray(spot.recognitionNames)
        ? spot.recognitionNames.filter((name): name is string => typeof name === "string")
        : [];
      return {
        cuisineId: typeof spot.cuisineId === "string" ? spot.cuisineId : null,
        hostingMethod: typeof spot.hostingMethod === "string" ? spot.hostingMethod : null,
        note: typeof spot.note === "string" ? spot.note : null,
        otherCuisineText: typeof spot.otherCuisineText === "string" ? spot.otherCuisineText : null,
        recognitionNames,
        spotId: typeof spot.spotId === "string" ? spot.spotId : `spot-${index + 1}`,
      };
    })
    .filter((spot): spot is IftarSponsorshipSpotDetailRow => spot !== null);
}

function iftarSponsorshipFeeSummary(row: IftarSponsorshipAdminRow): string {
  const activeFees = row.organizationFeeLinesSnapshot.filter((line) => line.active);
  if (activeFees.length === 0) {
    return "";
  }
  return activeFees.map((line) => `${line.label}: ${formatAmount(line.amountMinor)}`).join("; ");
}

function iftarSponsorshipCostSummary(row: IftarSponsorshipAdminRow): string {
  const parts = [
    row.basePricePerSpotMinorSnapshot === null
      ? ""
      : `base ${formatAmount(row.basePricePerSpotMinorSnapshot)}`,
    iftarSponsorshipFeeSummary(row),
    row.pricePerSpotMinorSnapshot === null
      ? ""
      : `per spot ${formatAmount(row.pricePerSpotMinorSnapshot)}`,
    row.totalPaymentAmountMinor === null
      ? ""
      : `total ${formatAmount(row.totalPaymentAmountMinor)}`,
  ].filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(" · ") : "-";
}

function iftarSponsorshipPaymentSummary(row: IftarSponsorshipAdminRow): string {
  return `${row.paymentSource} · ${row.paymentStatus} · ${row.reservationStatus}`;
}

function iftarSponsorshipSpotSummary(row: IftarSponsorshipAdminRow): string {
  if (row.spotDetails.length === 0) {
    return "No per-spot details";
  }
  return row.spotDetails
    .map((spot, index) => {
      const cuisine =
        spot.cuisineId === "other" && spot.otherCuisineText
          ? spot.otherCuisineText
          : spot.cuisineId || "cuisine not set";
      const recognition = spot.recognitionNames.length
        ? spot.recognitionNames.join("/")
        : "no recognition names";
      const note = spot.note ? `; note: ${spot.note}` : "";
      return `Spot ${index + 1}: ${spot.hostingMethod || "hosting not set"}; ${cuisine}; ${recognition}${note}`;
    })
    .join(" | ");
}

function escapeCsvCell(value: unknown): string {
  const text = String(value ?? "");
  return /[",\n\r]/u.test(text) ? `"${text.replace(/"/gu, '""')}"` : text;
}

function iftarSponsorshipFinanceRowFromData(
  data: Record<string, unknown>,
  id: string,
): IftarSponsorshipFinanceRow | undefined {
  if (data.transactionType !== "iftarSponsorship") {
    return undefined;
  }

  return {
    amountMinor: typeof data.amountMinor === "number" ? data.amountMinor : 0,
    createdAt: optionalDateFromFirestore(data.createdAt),
    grossAmountMinor: typeof data.grossAmountMinor === "number" ? data.grossAmountMinor : 0,
    iftarDayId: typeof data.iftarDayId === "string" ? data.iftarDayId : "-",
    organizationNetMinor:
      typeof data.organizationNetMinor === "number" ? data.organizationNetMinor : 0,
    ramadanId: typeof data.ramadanId === "string" ? data.ramadanId : "-",
    receiptId: typeof data.receiptId === "string" ? data.receiptId : null,
    refundPendingAmountMinor:
      typeof data.refundPendingAmountMinor === "number" ? data.refundPendingAmountMinor : 0,
    refundStatus: typeof data.refundStatus === "string" ? data.refundStatus : "none",
    refundedAmountMinor:
      typeof data.refundedAmountMinor === "number" ? data.refundedAmountMinor : 0,
    sponsorshipId: typeof data.sponsorshipId === "string" ? data.sponsorshipId : "-",
    status: typeof data.status === "string" ? data.status : "-",
    transactionId: typeof data.transactionId === "string" ? data.transactionId : id,
  };
}

function iftarTransferRowFromData(data: Record<string, unknown>, id: string): IftarTransferRow {
  return {
    completedAt: optionalDateFromFirestore(data.completedAt),
    destinationGregorianDateSnapshot:
      typeof data.destinationGregorianDateSnapshot === "string"
        ? data.destinationGregorianDateSnapshot
        : "-",
    destinationIftarDayId:
      typeof data.destinationIftarDayId === "string" ? data.destinationIftarDayId : "-",
    destinationRamadanDaySnapshot:
      typeof data.destinationRamadanDaySnapshot === "number"
        ? data.destinationRamadanDaySnapshot
        : 0,
    requestedAt: optionalDateFromFirestore(data.requestedAt),
    requestedByRole: typeof data.requestedByRole === "string" ? data.requestedByRole : "-",
    sourceGregorianDateSnapshot:
      typeof data.sourceGregorianDateSnapshot === "string" ? data.sourceGregorianDateSnapshot : "-",
    sourceIftarDayId: typeof data.sourceIftarDayId === "string" ? data.sourceIftarDayId : "-",
    sourceRamadanDaySnapshot:
      typeof data.sourceRamadanDaySnapshot === "number" ? data.sourceRamadanDaySnapshot : 0,
    sponsorshipId: typeof data.sponsorshipId === "string" ? data.sponsorshipId : "-",
    spotQuantity: typeof data.spotQuantity === "number" ? data.spotQuantity : 0,
    status: typeof data.status === "string" ? data.status : "-",
    transferId: typeof data.transferId === "string" ? data.transferId : id,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
  };
}

function ramadanAdminNoteRowFromData(
  data: Record<string, unknown>,
  id: string,
): RamadanAdminNoteRow {
  const category =
    typeof data.category === "string" &&
    ramadanAdminNoteCategories.includes(data.category as RamadanAdminNoteCategory)
      ? (data.category as RamadanAdminNoteCategory)
      : "operations";
  const status = data.status === "archived" ? "archived" : "active";

  return {
    archivedAt: optionalDateFromFirestore(data.archivedAt),
    archivedBy: typeof data.archivedBy === "string" ? data.archivedBy : null,
    body: typeof data.body === "string" ? data.body : "",
    category,
    createdAt: optionalDateFromFirestore(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "-",
    editCount: typeof data.editCount === "number" ? data.editCount : 0,
    id,
    iftarDayId: typeof data.iftarDayId === "string" ? data.iftarDayId : null,
    noteId: typeof data.noteId === "string" ? data.noteId : id,
    ramadanId: typeof data.ramadanId === "string" ? data.ramadanId : "-",
    sponsorshipId: typeof data.sponsorshipId === "string" ? data.sponsorshipId : null,
    status,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function operationalModeProjectionRowFromData(
  data: Record<string, unknown>,
): OperationalModeProjectionRow {
  const primaryMode = isRecord(data.primaryMode) ? data.primaryMode : undefined;
  const home = primaryMode && isRecord(primaryMode.home) ? primaryMode.home : undefined;
  return {
    modeId: primaryMode && typeof primaryMode.modeId === "string" ? primaryMode.modeId : null,
    status: typeof data.status === "string" ? data.status : "normal",
    title: home && typeof home.title === "string" ? home.title : null,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    warnings: Array.isArray(data.warnings)
      ? data.warnings.filter((warning): warning is string => typeof warning === "string")
      : [],
  };
}

function stringArrayCount(value: unknown): number {
  return Array.isArray(value) ? value.filter((item) => typeof item === "string").length : 0;
}

function commerceStatusFromData(data: Record<string, unknown> | undefined): CommerceStatusRow {
  const requirements = isRecord(data?.requirements) ? data.requirements : undefined;

  return {
    chargesEnabled: data?.chargesEnabled === true,
    currentlyDueCount: stringArrayCount(requirements?.currentlyDue),
    detailsSubmitted: data?.detailsSubmitted === true,
    error: "",
    eventuallyDueCount: stringArrayCount(requirements?.eventuallyDue),
    lastProviderSyncAt: optionalDateFromFirestore(data?.lastProviderSyncAt),
    onboardingStatus:
      typeof data?.onboardingStatus === "string" ? data.onboardingStatus : "notStarted",
    payoutsEnabled: data?.payoutsEnabled === true,
    provider: typeof data?.provider === "string" ? data.provider : "stripe",
    readiness: typeof data?.readiness === "string" ? data.readiness : "notConnected",
    restrictedReasonCode:
      typeof requirements?.disabledReason === "string" && requirements.disabledReason.length > 0
        ? requirements.disabledReason
        : null,
  };
}

function commerceStatusForOrganization(organizationId: string): CommerceStatusRow {
  const fixture = demoOrganizationCommerceConfigurations.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  return commerceStatusFromData(fixture);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringBooleanRecord(value: unknown): value is Record<string, boolean> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === "boolean");
}

function commerceReadinessClass(readiness: string): string {
  if (readiness === "ready") {
    return "status-accepted";
  }

  if (readiness === "restricted") {
    return "status-rejected";
  }

  return "status-pending";
}

function commerceRecordStatusClass(status: string): string {
  if (["confirmed", "matched", "completed", "succeeded"].includes(status)) {
    return "status-accepted";
  }

  if (["failed", "cancelled", "mismatched", "error"].includes(status)) {
    return "status-rejected";
  }

  return "status-pending";
}

function yesNo(value: boolean, localeCode: LocaleCode): string {
  if (localeCode === "ar") {
    return value ? "نعم" : "لا";
  }

  return value ? "yes" : "no";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function shortTechnicalId(value: string): string {
  const normalized = value.trim();
  if (normalized.length <= 18) {
    return normalized;
  }

  return `${normalized.slice(0, 10)}...${normalized.slice(-5)}`;
}

function renderTechnicalId(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "";
  }

  return `<small class="technical-id"><span>${escapeHtml(label)}</span><code title="${escapeHtml(
    normalized,
  )}">${escapeHtml(shortTechnicalId(normalized))}</code></small>`;
}

function profileAuditRequestLabel(requestId: string): string {
  const normalized = requestId.toLowerCase();
  if (normalized.includes("logo")) {
    return "Logo update";
  }

  if (normalized.includes("profile")) {
    return "Profile update";
  }

  const firstSegment = requestId.split(/[_-]/u).filter(Boolean)[0] ?? "Request";
  return `${firstSegment.slice(0, 1).toUpperCase()}${firstSegment.slice(1)} request`;
}

function initialsFor(value: string): string {
  const words = value
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (words.length === 0) {
    return "S";
  }

  const firstWord = words[0] ?? "S";

  if (words.length === 1) {
    return firstWord.slice(0, 2).toUpperCase();
  }

  const lastWord = words[words.length - 1] ?? firstWord;
  return `${firstWord.slice(0, 1)}${lastWord.slice(0, 1)}`.toUpperCase();
}

function renderOrganizationLogo(input: {
  brandColor: string;
  className?: string;
  displayName: string;
  logoStoragePath: string | null;
  logoUrl: string | null;
}): string {
  const className = input.className ? ` ${input.className}` : "";
  const style = `style="--organization-logo-color: ${escapeHtml(input.brandColor)}"`;

  if (input.logoUrl) {
    return `<img alt="${escapeHtml(`${input.displayName} logo`)}" class="organization-logo${className}" src="${escapeHtml(
      input.logoUrl,
    )}" title="${escapeHtml(input.displayName)}" ${style} />`;
  }

  return `<span aria-label="${escapeHtml(`${input.displayName} logo fallback`)}" class="organization-logo organization-logo-fallback${className}" title="${escapeHtml(
    input.displayName,
  )}" ${style}>${escapeHtml(initialsFor(input.displayName))}</span>`;
}

type ShellCopyForLocale = (typeof shellCopy)[LocaleCode];

function renderProfileContactLink(label: string, href: string, external = false): string {
  return `<a class="profile-contact-link" href="${escapeHtml(href)}"${
    external ? ' rel="noopener noreferrer" target="_blank"' : ""
  }>${escapeHtml(label)}</a>`;
}

function renderOrganizationProfileContactLinks(
  profile: OrganizationProfileRow,
  copy: ShellCopyForLocale,
): string {
  const links: string[] = [];

  if (profile.publicEmail) {
    links.push(renderProfileContactLink(profile.publicEmail, `mailto:${profile.publicEmail}`));
  }

  if (profile.publicPhone) {
    const phoneDigits = profile.publicPhone.replace(/[^\d]/gu, "");
    const phoneHref = phoneDigits.length === 10 ? `tel:+1${phoneDigits}` : `tel:${phoneDigits}`;
    links.push(renderProfileContactLink(profile.publicPhone, phoneHref));
  }

  if (profile.websiteUrl) {
    links.push(renderProfileContactLink(profile.websiteUrl, profile.websiteUrl, true));
  }

  if (links.length === 0) {
    return `<span class="profile-detail-empty">${escapeHtml(copy.organizationNoPublicContact)}</span>`;
  }

  return links.join("");
}

function renderProfileVisibilityBadge(label: string, value: boolean, locale: LocaleCode): string {
  return `<span class="status-pill ${value ? "status-accepted" : "status-pending"}">${escapeHtml(
    label,
  )}: ${yesNo(value, locale)}</span>`;
}

function renderOrganizationProfileDetails(input: {
  copy: ShellCopyForLocale;
  locale: LocaleCode;
  locationSummary: string;
  profile: OrganizationProfileRow | undefined;
}): string {
  const { copy, locale, locationSummary, profile } = input;
  const policyLabel = profile
    ? profile.openConnection
      ? copy.policyOpen
      : copy.policyApprovalRequired
    : "-";
  const policyClass = profile?.openConnection ? "status-accepted" : "status-pending";

  return `<dl class="organization-profile-details">
    <div class="profile-detail-row">
      <dt>${copy.organizationLocationDetails}</dt>
      <dd>
        <span class="profile-detail-primary">${escapeHtml(locationSummary)}</span>
        <span class="profile-detail-secondary">${copy.organizationTimeZone}: <code>${escapeHtml(
          profile?.timeZone ?? "-",
        )}</code></span>
      </dd>
    </div>
    <div class="profile-detail-row">
      <dt>${copy.organizationPublicContact}</dt>
      <dd class="profile-contact-list">${
        profile
          ? renderOrganizationProfileContactLinks(profile, copy)
          : `<span class="profile-detail-empty">${escapeHtml(copy.organizationNoPublicContact)}</span>`
      }</dd>
    </div>
    <div class="profile-detail-row">
      <dt>${copy.organizationConnectionPolicy}</dt>
      <dd class="profile-policy-detail">
        <span class="status-pill ${policyClass}">${escapeHtml(policyLabel)}</span>
        <span class="profile-detail-secondary">${copy.connectionPolicy}: <code>${escapeHtml(
          profile?.connectionPolicy ?? "-",
        )}</code></span>
      </dd>
    </div>
    <div class="profile-detail-row">
      <dt>${copy.organizationPublicVisibility}</dt>
      <dd class="profile-badge-list">
        ${renderProfileVisibilityBadge(
          copy.organizationVisibilityDirectory,
          profile?.publicVisibilityDirectory ?? false,
          locale,
        )}
        ${renderProfileVisibilityBadge(
          copy.organizationVisibilityDiscovery,
          profile?.publicVisibilityDiscovery ?? false,
          locale,
        )}
        ${renderProfileVisibilityBadge(
          copy.organizationVisibilityProfile,
          profile?.publicVisibilityProfile ?? false,
          locale,
        )}
      </dd>
    </div>
  </dl>`;
}

function enhanceCarbonDesignSystem(appRoot: HTMLElement): void {
  enhanceAdminWorkspace(appRoot, state.locale);
  appRoot.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
    if (
      button.classList.contains("event-template-card") ||
      button.classList.contains("request-summary-filter")
    ) {
      return;
    }
    button.classList.add("cds--btn", "shd-carbon-button");
    if (
      button.classList.contains("danger-action") ||
      button.classList.contains("status-rejected")
    ) {
      button.classList.add("cds--btn--danger");
      return;
    }
    if (
      button.classList.contains("secondary-action") ||
      button.classList.contains("inline-action") ||
      button.id === "auth-reset" ||
      button.id === "auth-apple"
    ) {
      button.classList.add("cds--btn--secondary");
      return;
    }
    button.classList.add("cds--btn--primary");
  });

  appRoot
    .querySelectorAll<HTMLInputElement>('input:not([type="checkbox"]):not([type="radio"])')
    .forEach((input) => {
      input.classList.add("cds--text-input");
    });
  appRoot.querySelectorAll<HTMLTextAreaElement>("textarea").forEach((textarea) => {
    textarea.classList.add("cds--text-area");
  });
  appRoot.querySelectorAll<HTMLSelectElement>("select").forEach((select) => {
    select.classList.add("cds--select-input");
  });

  appRoot.querySelectorAll<HTMLElement>(".pill, .live-pill, .status-pill").forEach((tag) => {
    tag.classList.add("cds--tag", "shd-status-tag");
  });
  appRoot.querySelectorAll<HTMLElement>(".banner").forEach((banner) => {
    banner.classList.add("cds--inline-notification", "shd-inline-notification");
  });
  appRoot.querySelectorAll<HTMLTableElement>("table").forEach((table) => {
    table.classList.add("cds--data-table", "cds--data-table--zebra", "shd-carbon-table");
  });
  appRoot.querySelectorAll<HTMLElement>(".panel").forEach((panel) => {
    panel.classList.add("cds--tile", "shd-carbon-surface");
  });
}

function enhanceRequiredFieldIndicators(appRoot: HTMLElement): void {
  const requiredLabel = state.locale === "ar" ? "مطلوب" : "Required";
  const requiredControls = appRoot.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("input[required], select[required], textarea[required]");

  requiredControls.forEach((control) => {
    control.setAttribute("aria-required", "true");

    const label = control.closest("label");
    if (!label) {
      return;
    }

    label.classList.add("required-field-label");
    const hasMarker = Array.from(label.children).some((child) =>
      child.classList.contains("required-field-indicator"),
    );
    if (hasMarker) {
      return;
    }

    const marker = document.createElement("span");
    marker.className = "required-field-indicator";
    marker.dataset.requiredIndicator = "true";
    marker.setAttribute("aria-hidden", "true");
    marker.textContent = requiredLabel;
    label.append(marker);
  });
}

type TaskWindowDefinition = {
  launchLabel: string;
  subtitle: string;
  title: string;
};

const orgAdminTaskWindowDefinitions = new Map<string, TaskWindowDefinition>([
  [
    "business-affiliation-form",
    {
      launchLabel: "Manage affiliation",
      subtitle: "Connect this organization to an active public business record.",
      title: "Business affiliation",
    },
  ],
  [
    "organization-profile-editor-form",
    {
      launchLabel: "Edit organization profile",
      subtitle: "Submit safe public profile updates through backend review.",
      title: "Organization profile",
    },
  ],
  [
    "organization-prayer-settings-form",
    {
      launchLabel: "Configure prayer times",
      subtitle: "Calculate daily prayer times and publish admin-entered Iqama times.",
      title: "Prayer times",
    },
  ],
  [
    "organization-islamic-components-form",
    {
      launchLabel: "Configure Islamic components",
      subtitle: "Choose which Islamic foundations are available in the mobile app.",
      title: "Islamic components",
    },
  ],
  [
    "imam-content-editor-form",
    {
      launchLabel: "Edit leadership profile",
      subtitle: "Prepare organization-scoped content with bilingual copy.",
      title: "Leadership profile",
    },
  ],
  [
    "people-role-editor-form",
    {
      launchLabel: "Open person details",
      subtitle: "View organization connection details and manage fixed V1 roles.",
      title: "Person account",
    },
  ],
  [
    "event-editor-form",
    {
      launchLabel: "Open event editor",
      subtitle: "Create or update community events without losing the page context.",
      title: "Community event",
    },
  ],
  [
    "event-qr-check-in-form",
    {
      launchLabel: "Open QR check-in",
      subtitle: "Validate an event check-in credential from a focused station window.",
      title: "Event check-in",
    },
  ],
  [
    "giving-fund-form",
    {
      launchLabel: "Create giving fund",
      subtitle: "Configure a giving fund for donations and campaigns.",
      title: "Giving fund",
    },
  ],
  [
    "campaign-form",
    {
      launchLabel: "Create campaign",
      subtitle: "Set up a fundraising campaign with org-scoped visibility.",
      title: "Campaign",
    },
  ],
  [
    "operational-mode-editor-form",
    {
      launchLabel: "Open mode editor",
      subtitle: "Create or update operational mode messaging.",
      title: "Operational mode",
    },
  ],
  [
    "ramadan-setup-editor-form",
    {
      launchLabel: "Open Ramadan setup",
      subtitle: "Configure Ramadan schedule, prayer, and operational metadata.",
      title: "Ramadan setup",
    },
  ],
  [
    "iftar-sponsorship-day-form",
    {
      launchLabel: "Edit sponsorship day",
      subtitle: "Tune capacity, state, and sponsorship copy for one day.",
      title: "Iftar sponsorship day",
    },
  ],
  [
    "ramadan-admin-note-form",
    {
      launchLabel: "Open note editor",
      subtitle: "Create or update private Ramadan admin notes.",
      title: "Ramadan admin note",
    },
  ],
  [
    "ramadan-mode-editor-form",
    {
      launchLabel: "Open spot price",
      subtitle: "Set the public Iftar sponsorship spot price.",
      title: "Spot price",
    },
  ],
  [
    "iftar-slot-editor-form",
    {
      launchLabel: "Open iftar slot editor",
      subtitle: "Create or adjust sponsorship slot availability.",
      title: "Iftar slot",
    },
  ],
  [
    "manual-iftar-sponsorship-form",
    {
      launchLabel: "Add manual sponsorship",
      subtitle: "Record a manual iftar sponsorship with finance-safe fields.",
      title: "Manual sponsorship",
    },
  ],
  [
    "space-editor-form",
    {
      launchLabel: "Open space editor",
      subtitle: "Create or update reservable organization spaces.",
      title: "Space",
    },
  ],
  [
    "space-availability-block-editor-form",
    {
      launchLabel: "Open availability block",
      subtitle: "Define recurring space availability windows.",
      title: "Availability block",
    },
  ],
  [
    "space-slot-editor-form",
    {
      launchLabel: "Open space slot",
      subtitle: "Create or update a concrete reservable slot.",
      title: "Space slot",
    },
  ],
  [
    "volunteer-opportunity-editor-form",
    {
      launchLabel: "Open opportunity editor",
      subtitle: "Create or update volunteer opportunities.",
      title: "Volunteer opportunity",
    },
  ],
  [
    "volunteer-shift-editor-form",
    {
      launchLabel: "Open shift editor",
      subtitle: "Create or update volunteer shifts and capacity.",
      title: "Volunteer shift",
    },
  ],
  [
    "announcement-composer-form",
    {
      launchLabel: "Compose announcement",
      subtitle: "Draft and publish organization-scoped announcements.",
      title: "Announcement",
    },
  ],
]);

function renderTaskWindowShell(): string {
  if (!activeTaskWindowId) {
    return "";
  }

  const definition = taskWindowDefinitionFor(activeTaskWindowId);
  return `
    <section
      class="task-window-overlay"
      data-task-window-overlay="${escapeHtml(activeTaskWindowId)}"
      role="presentation"
    >
      <div
        aria-labelledby="task-window-title"
        aria-modal="true"
        class="task-window ${activeTaskWindowId === "operational-mode-editor-form" ? "mode-task-window" : ""}"
        role="dialog"
      >
        <header class="task-window-header">
          <div>
            <p class="eyebrow">${activeTaskWindowId === "operational-mode-editor-form" ? (state.locale === "ar" ? "الأوضاع التشغيلية" : "Operational modes") : "Action window"}</p>
            <h2 id="task-window-title">${escapeHtml(definition.title)}</h2>
            ${definition.subtitle ? `<p class="muted">${escapeHtml(definition.subtitle)}</p>` : ""}
          </div>
          <button
            aria-label="${state.locale === "ar" ? "إغلاق" : "Close"}"
            class="secondary-action inline-action icon-action task-window-close"
            data-task-window-close
            data-tooltip="${state.locale === "ar" ? "إغلاق" : "Close"}"
            title="${state.locale === "ar" ? "إغلاق" : "Close"}"
            type="button"
          >${renderCloseIcon()}</button>
        </header>
        <p class="form-feedback" data-form-feedback role="status" hidden></p>
        <div
          class="task-window-body"
          data-scroll-window-id="task:${escapeHtml(activeTaskWindowId)}"
          id="task-window-body"
        ></div>
      </div>
    </section>
  `;
}

function renderEventOperationsWindow(): string {
  const event = eventRowForId(activeEventOperationsWindowEventId);
  if (!event) {
    return "";
  }

  const actionCopy = eventLifecycleActionCopy(state.locale);
  const title = state.locale === "ar" && event.titleAr ? event.titleAr : event.title;
  const summary = state.locale === "ar" && event.summaryAr ? event.summaryAr : event.summary;
  const startsAt = event.startsAt ? event.startsAt.toLocaleString(state.locale) : "-";
  const location = [event.campusNameSnapshot, event.locationName]
    .filter((part) => typeof part === "string" && part.length > 0)
    .join(" · ");
  const registrationSummary = event.registrationEnabled
    ? `${event.registrationConfirmedCount}/${event.registrationCapacity} confirmed · ${event.waitlistCount} waitlisted`
    : "No registration";
  const checkInSummary = `${event.attendanceCheckedInCount}/${event.registrationConfirmedCount} checked in`;
  const canManageEvents = selectedContextCanManageEvents();
  const canCheckInEvents = selectedContextCanCheckInEvents();
  const notificationTemplate = currentEventEmailTemplate();
  const notificationDisabled = state.busy || event.status !== "published";
  const delistDisabled =
    state.busy || !canManageEvents || event.status === "draft" || event.status === "archived";
  const deleteDisabled = state.busy || !canManageEvents || event.status === "archived";

  return `
    <section
      class="task-window-overlay"
      data-event-operations-overlay="${escapeHtml(event.eventId)}"
      role="presentation"
    >
      <div
        aria-labelledby="event-operations-title"
        aria-modal="true"
        class="task-window event-action-window"
        role="dialog"
      >
        <header class="task-window-header">
          <div>
            <p class="eyebrow">Event actions</p>
            <h2 id="event-operations-title">${escapeHtml(title)}</h2>
            <p class="muted">${escapeHtml(summary)}</p>
          </div>
          <div class="event-window-header-actions">
            ${
              canManageEvents
                ? `<button
                    aria-label="${escapeHtml(`${actionCopy.makeInvisible}: ${title}`)}"
                    class="secondary-action inline-action icon-action"
                    data-event-delist="${escapeHtml(event.eventId)}"
                    data-tooltip="${escapeHtml(actionCopy.makeInvisible)}"
                    title="${escapeHtml(actionCopy.makeInvisible)}"
                    type="button"
                    ${delistDisabled ? "disabled" : ""}
                  >${renderEventActionIcon("delist")}</button>
                  <button
                    aria-label="${escapeHtml(`${actionCopy.delete}: ${title}`)}"
                    class="danger-action inline-action icon-action"
                    data-event-delete="${escapeHtml(event.eventId)}"
                    data-tooltip="${escapeHtml(actionCopy.delete)}"
                    title="${escapeHtml(actionCopy.delete)}"
                    type="button"
                    ${deleteDisabled ? "disabled" : ""}
                  >${renderEventActionIcon("delete")}</button>`
                : ""
            }
            <button
              aria-label="Close"
              class="secondary-action inline-action icon-action task-window-close"
              data-event-operations-close
              data-tooltip="Close"
              title="Close"
              type="button"
            >${renderCloseIcon()}</button>
          </div>
        </header>
        <div
          class="task-window-body"
          data-scroll-window-id="event:${escapeHtml(event.eventId)}"
        >
          <dl class="event-action-summary">
            <div>
              <dt>When</dt>
              <dd>${escapeHtml(startsAt)}</dd>
            </div>
            <div>
              <dt>Where</dt>
              <dd>${escapeHtml(location || "-")}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>${escapeHtml(event.status)}</dd>
            </div>
            <div>
              <dt>Event ID</dt>
              <dd>${escapeHtml(event.eventId)}</dd>
            </div>
            <div>
              <dt>Registration</dt>
              <dd>${escapeHtml(registrationSummary)}</dd>
            </div>
            <div>
              <dt>Check-in</dt>
              <dd>${escapeHtml(checkInSummary)}</dd>
            </div>
          </dl>
          <div class="event-action-grid" aria-label="Event actions">
            ${
              canManageEvents
                ? `<button data-event-operations-edit="${escapeHtml(
                    event.eventId,
                  )}" type="button">Edit event</button>`
                : ""
            }
            ${
              canManageEvents
                ? `<button class="secondary-action" data-event-operations-export="${escapeHtml(
                    event.eventId,
                  )}" data-include-responses="false" type="button">Export</button>`
                : ""
            }
            ${
              canManageEvents
                ? `<button class="secondary-action" data-event-operations-export="${escapeHtml(
                    event.eventId,
                  )}" data-include-responses="true" type="button">Export with responses</button>`
                : ""
            }
            ${
              canCheckInEvents
                ? `<button class="secondary-action" data-event-operations-check-in="${escapeHtml(
                    event.eventId,
                  )}" type="button">Open QR check-in</button>`
                : ""
            }
          </div>
          ${
            canManageEvents
              ? `<section class="event-communication-panel">
                  <div class="panel-header-actions">
                    <div>
                      <h3>Notifications</h3>
                      <p class="meta">Review and edit the email before resending. Push uses the same subject and message.</p>
                    </div>
                    <span class="status-pill ${
                      event.status === "published" ? "status-accepted" : "status-pending"
                    }">${escapeHtml(event.status)}</span>
                  </div>
                  ${renderEventEmailTemplateEditor(notificationTemplate, "event-operations-email")}
                  <div class="editor-actions event-communication-actions">
                    <button
                      data-event-operations-send-email="${escapeHtml(event.eventId)}"
                      type="button"
                      ${notificationDisabled ? "disabled" : ""}
                    >Send email</button>
                    <button
                      class="secondary-action"
                      data-event-operations-send-push="${escapeHtml(event.eventId)}"
                      type="button"
                      ${notificationDisabled ? "disabled" : ""}
                    >Send device push notification</button>
                    <button
                      class="secondary-action"
                      data-event-operations-send-both="${escapeHtml(event.eventId)}"
                      type="button"
                      ${notificationDisabled ? "disabled" : ""}
                    >Send email + push</button>
                  </div>
                  ${
                    event.status === "published"
                      ? ""
                      : `<p class="meta">Publish the event before sending member notifications.</p>`
                  }
                </section>`
              : ""
          }
          ${
            state.eventCheckInMessage
              ? `<p class="status status-inline">${escapeHtml(state.eventCheckInMessage)}</p>`
              : ""
          }
          ${
            canManageEvents || canCheckInEvents
              ? ""
              : `<p class="meta">You do not have permission to manage event operations for this event.</p>`
          }
        </div>
      </div>
    </section>
  `;
}

function renderEventActionIcon(
  type:
    | "add"
    | "checkIn"
    | "delete"
    | "delist"
    | "disable"
    | "download"
    | "edit"
    | "move"
    | "notify"
    | "open"
    | "read",
): string {
  if (type === "add") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>`;
  }

  if (type === "move") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M7 7h10" />
      <path d="m14 4 3 3-3 3" />
      <path d="M17 17H7" />
      <path d="m10 14-3 3 3 3" />
    </svg>`;
  }

  if (type === "open") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M9 10V7a4 4 0 0 1 7.5-2" />
      <path d="M12 14v3" />
    </svg>`;
  }

  if (type === "download") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M12 4v10" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 20h14" />
    </svg>`;
  }

  if (type === "read") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.75" />
    </svg>`;
  }

  if (type === "notify") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M5 9.5v5l4 2.5v-10L5 9.5Z" />
      <path d="M9 7h3l6-3v16l-6-3H9" />
      <path d="M18 9.5a3 3 0 0 1 0 5" />
    </svg>`;
  }

  if (type === "delist") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M3 3l18 18" />
      <path d="M10.7 5.3A9.8 9.8 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-2.9 4.1" />
      <path d="M6.6 6.6A16.7 16.7 0 0 0 2.5 12s3.5 7 9.5 7a9.8 9.8 0 0 0 4.1-.9" />
      <path d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
    </svg>`;
  }

  if (type === "edit") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M4 20h4.5L19 9.5 14.5 5 4 15.5V20Z" />
      <path d="m13.5 6 4.5 4.5" />
    </svg>`;
  }

  if (type === "disable") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      <path d="M12 14v3" />
    </svg>`;
  }

  if (type === "delete") {
    return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M4 7h16" />
      <path d="M9 7V5h6v2" />
      <path d="M7 7l1 13h8l1-13" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>`;
  }

  return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
    <path d="M4 9V5h4" />
    <path d="M16 5h4v4" />
    <path d="M20 15v4h-4" />
    <path d="M8 19H4v-4" />
    <path d="m8 12 2.5 2.5L16 9" />
  </svg>`;
}

function renderCloseIcon(): string {
  return `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </svg>`;
}

function renderRamadanCapacityBar(input: {
  available: number;
  confirmed: number;
  label: string;
  reserved: number;
  total: number;
}): string {
  const total = Math.max(input.total, 0);
  const confirmedPct = total > 0 ? Math.min((Math.max(input.confirmed, 0) / total) * 100, 100) : 0;
  const reservedPct =
    total > 0 ? Math.min((Math.max(input.reserved, 0) / total) * 100, 100 - confirmedPct) : 0;
  const availablePct = total > 0 ? Math.max(100 - confirmedPct - reservedPct, 0) : 0;
  return `
    <section class="ramadan-capacity-card">
      <div>
        <strong>${escapeHtml(input.label)}</strong>
        <small>${input.confirmed} confirmed · ${input.reserved} reserved · ${input.available} available</small>
      </div>
      <div class="ramadan-capacity-bar" aria-hidden="true">
        <span class="is-confirmed" style="width:${confirmedPct.toFixed(2)}%"></span>
        <span class="is-reserved" style="width:${reservedPct.toFixed(2)}%"></span>
        <span class="is-available" style="width:${availablePct.toFixed(2)}%"></span>
      </div>
    </section>`;
}

function renderRamadanIqamaPhaseEditor(
  label: string,
  prefix: "phase1" | "phase2" | "phase3",
): string {
  const fajrKey = `${prefix}FajrIqama` as keyof RamadanSetupEditorInput;
  const maghribKey = `${prefix}MaghribIqama` as keyof RamadanSetupEditorInput;
  const ishaKey = `${prefix}IshaIqama` as keyof RamadanSetupEditorInput;
  return `
    <fieldset class="ramadan-phase-card">
      <legend>${escapeHtml(label)}</legend>
      <label>
        Fajr
        <input data-ramadan-setup-editor-field="${fajrKey}" type="time" value="${escapeHtml(
          String(state.ramadanSetupEditor[fajrKey]),
        )}" />
      </label>
      <label>
        Maghrib
        <input data-ramadan-setup-editor-field="${maghribKey}" type="time" value="${escapeHtml(
          String(state.ramadanSetupEditor[maghribKey]),
        )}" />
      </label>
      <label>
        Isha
        <input data-ramadan-setup-editor-field="${ishaKey}" type="time" value="${escapeHtml(
          String(state.ramadanSetupEditor[ishaKey]),
        )}" />
      </label>
    </fieldset>`;
}

function renderEventCreationWizard(input: {
  organizationCampuses: typeof seedOrganizationCampuses;
  selectedEditorEvent: CommunityEventRow | undefined;
}): string {
  const steps = eventWizardStepsForEditor();
  const stepIndex = Math.min(Math.max(state.eventWizardStepIndex, 0), steps.length - 1);
  if (stepIndex !== state.eventWizardStepIndex) {
    state.eventWizardStepIndex = stepIndex;
  }
  const step = steps[stepIndex] ?? {
    id: "template",
    label: "Template",
    summary: "Choose the event workflow.",
  };
  const isLastStep = stepIndex === steps.length - 1;
  const template = currentEventEmailTemplate();

  return `<form id="event-editor-form" class="editor-form event-wizard" novalidate>
    <header class="event-wizard-hero">
      <div>
        <p class="eyebrow">Guided event creation</p>
        <h3>${escapeHtml(step.label)}</h3>
        <p class="muted">${escapeHtml(step.summary)}</p>
      </div>
      <span class="status-pill status-accepted">${escapeHtml(eventTypeLabel(state.eventEditor.eventType))}</span>
    </header>
    <ol class="event-wizard-steps" aria-label="Event creation steps">
      ${steps
        .map(
          (candidate, index) => `<li class="${
            index === stepIndex ? "is-active" : index < stepIndex ? "is-complete" : ""
          }">
            <button
              data-event-wizard-step="${index}"
              type="button"
              ${index > stepIndex + 1 ? "disabled" : ""}
            >
              <span>${index + 1}</span>
              ${escapeHtml(candidate.label)}
            </button>
          </li>`,
        )
        .join("")}
    </ol>
    <section class="event-wizard-panel">
      ${renderEventWizardStep({
        organizationCampuses: input.organizationCampuses,
        selectedEditorEvent: input.selectedEditorEvent,
        stepId: step.id,
        template,
      })}
    </section>
    <div class="event-wizard-footer editor-actions">
      <button
        class="secondary-action"
        data-event-wizard-prev
        type="button"
        ${stepIndex === 0 ? "disabled" : ""}
      >Back</button>
      <button
        id="reset-event-form"
        class="secondary-action"
        data-event-reset-form
        type="button"
      >${shellCopy[state.locale].resetEventForm}</button>
      ${
        isLastStep
          ? `<button type="submit" ${state.busy ? "disabled" : ""}>${
              shellCopy[state.locale].saveEvent
            }</button>`
          : `<button data-event-wizard-next type="button" ${state.busy ? "disabled" : ""}>Next</button>`
      }
    </div>
    ${
      state.eventEditorError
        ? `<p class="error status-inline" role="alert">${escapeHtml(state.eventEditorError)}</p>`
        : ""
    }
    ${
      state.eventEditorMessage
        ? `<p class="status status-inline">${escapeHtml(state.eventEditorMessage)}</p>`
        : ""
    }
  </form>`;
}

function renderEventWizardStep(input: {
  organizationCampuses: typeof seedOrganizationCampuses;
  selectedEditorEvent: CommunityEventRow | undefined;
  stepId: EventWizardStepId;
  template: { message: string; subject: string };
}): string {
  if (input.stepId === "template") {
    return `<div class="event-template-grid" role="list">
      ${(["standard", "friday_program", "janazah"] as const)
        .map((eventType) => {
          const selected = state.eventEditor.eventType === eventType;
          const title = eventTypeLabel(eventType);
          const summary =
            eventType === "friday_program"
              ? "Friday program from Jumu'ah through evening and Isha."
              : eventType === "janazah"
                ? "Urgent funeral and burial notice with fast email and push."
                : "General event with optional registration, media, and reminders.";
          return `<button
            class="event-template-card ${selected ? "is-selected" : ""}"
            data-event-wizard-template="${eventType}"
            role="listitem"
            type="button"
          >
            <span>${escapeHtml(title)}</span>
            <small>${escapeHtml(summary)}</small>
          </button>`;
        })
        .join("")}
    </div>
    <div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventStatus}
        <select data-event-editor-field="status" name="status" required>
          ${renderEventStatusOptions()}
        </select>
      </label>
      ${
        state.eventEditor.status === "scheduled"
          ? `<label>
              ${shellCopy[state.locale].eventScheduledPublishDate}
              <input
                data-event-editor-field="scheduledPublishDate"
                name="scheduledPublishDate"
                required
                type="date"
                value="${escapeHtml(state.eventEditor.scheduledPublishDate)}"
              />
              <small>${shellCopy[state.locale].eventScheduledPublishDateHelp}</small>
            </label>`
          : ""
      }
    </div>`;
  }

  if (input.stepId === "details") {
    return `<div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventTitle}
        <input
          data-event-editor-field="title"
          maxlength="140"
          name="title"
          required
          value="${escapeHtml(state.eventEditor.title)}"
        />
      </label>
      <label>
        ${shellCopy[state.locale].eventTitleAr}
        <input
          data-event-editor-field="titleAr"
          dir="rtl"
          maxlength="140"
          name="titleAr"
          value="${escapeHtml(state.eventEditor.titleAr)}"
        />
      </label>
    </div>
    <label>
      ${shellCopy[state.locale].eventSummary}
      <textarea
        data-event-editor-field="summary"
        maxlength="300"
        name="summary"
        required
      >${escapeHtml(state.eventEditor.summary)}</textarea>
    </label>
    <label>
      ${shellCopy[state.locale].eventSummaryAr}
      <textarea
        data-event-editor-field="summaryAr"
        dir="rtl"
        maxlength="300"
        name="summaryAr"
      >${escapeHtml(state.eventEditor.summaryAr)}</textarea>
    </label>
    <label>
      ${shellCopy[state.locale].eventDescription}
      <textarea
        data-event-editor-field="description"
        maxlength="3000"
        name="description"
        required
      >${escapeHtml(state.eventEditor.description)}</textarea>
    </label>
    <label>
      ${shellCopy[state.locale].eventDescriptionAr}
      <textarea
        data-event-editor-field="descriptionAr"
        dir="rtl"
        maxlength="3000"
        name="descriptionAr"
      >${escapeHtml(state.eventEditor.descriptionAr)}</textarea>
    </label>`;
  }

  if (input.stepId === "schedule") {
    return `<div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventStartsAt}
        <input
          data-event-editor-field="startsAt"
          name="startsAt"
          required
          type="datetime-local"
          value="${escapeHtml(state.eventEditor.startsAt)}"
        />
      </label>
      <label>
        ${shellCopy[state.locale].eventEndsAt}
        <input
          data-event-editor-field="endsAt"
          name="endsAt"
          required
          type="datetime-local"
          value="${escapeHtml(state.eventEditor.endsAt)}"
        />
      </label>
    </div>
    <div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventTimezone}
        <select data-event-editor-field="timezone" name="timezone" required>
          ${renderOrganizationProfileSelectOptions(
            organizationTimeZoneOptions,
            state.eventEditor.timezone,
          )}
        </select>
      </label>
      <label>
        ${shellCopy[state.locale].eventCampus}
        <select data-event-editor-field="campusId" name="campusId">
          <option value="">${shellCopy[state.locale].eventNoCampus}</option>
          ${input.organizationCampuses
            .map(
              (campus) =>
                `<option value="${escapeHtml(campus.campusId)}" ${
                  state.eventEditor.campusId === campus.campusId ? "selected" : ""
                }>${escapeHtml(campus.name)}</option>`,
            )
            .join("")}
        </select>
      </label>
    </div>
    <label>
      ${shellCopy[state.locale].eventLocation}
      <input
        data-event-editor-field="locationName"
        maxlength="160"
        name="locationName"
        required
        value="${escapeHtml(state.eventEditor.locationName)}"
      />
    </label>
    <div class="editor-grid">
      <label>
        Location type
        <select data-event-editor-field="locationType" name="locationType" required>
          <option value="physical" ${
            state.eventEditor.locationType === "physical" ? "selected" : ""
          }>Physical</option>
          <option value="virtual" ${
            state.eventEditor.locationType === "virtual" ? "selected" : ""
          }>Virtual</option>
          <option value="hybrid" ${
            state.eventEditor.locationType === "hybrid" ? "selected" : ""
          }>Hybrid</option>
        </select>
      </label>
      <label>
        Address / directions
        <input
          data-event-editor-field="addressLine1"
          maxlength="240"
          value="${escapeHtml(state.eventEditor.addressLine1)}"
        />
      </label>
    </div>
    <label>
      Online URL
      <input
        data-event-editor-field="onlineUrl"
        maxlength="1000"
        value="${escapeHtml(state.eventEditor.onlineUrl)}"
      />
    </label>`;
  }

  if (input.stepId === "registration") {
    return `<div class="editor-grid">
      <label>
        Registration mode
        <select data-event-editor-field="registrationMode" name="registrationMode" required>
          <option value="none" ${
            state.eventEditor.registrationMode === "none" ? "selected" : ""
          }>No registration</option>
          <option value="free" ${
            state.eventEditor.registrationMode === "free" ? "selected" : ""
          }>Free registration</option>
        </select>
      </label>
      <label>
        ${shellCopy[state.locale].eventRegistrationCapacity}
        <input
          data-event-editor-field="registrationCapacity"
          min="0"
          max="100000"
          ${state.eventEditor.registrationEnabled ? "required" : ""}
          step="1"
          type="number"
          value="${escapeHtml(state.eventEditor.registrationCapacity)}"
        />
      </label>
    </div>
    <div class="event-switch-grid">
      <label class="checkbox-row">
        <input
          data-event-editor-field="registrationEnabled"
          type="checkbox"
          ${state.eventEditor.registrationEnabled ? "checked" : ""}
        />
        ${shellCopy[state.locale].eventRegistrationEnabled}
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="waitlistEnabled"
          type="checkbox"
          ${state.eventEditor.waitlistEnabled ? "checked" : ""}
        />
        ${shellCopy[state.locale].eventWaitlistEnabled}
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="registrationCancellationAllowed"
          type="checkbox"
          ${state.eventEditor.registrationCancellationAllowed ? "checked" : ""}
        />
        ${shellCopy[state.locale].eventRegistrationCancellationAllowed}
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="guestRegistrationEnabled"
          type="checkbox"
          ${state.eventEditor.guestRegistrationEnabled ? "checked" : ""}
        />
        Allow guests
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="dependentRegistrationEnabled"
          type="checkbox"
          ${state.eventEditor.dependentRegistrationEnabled ? "checked" : ""}
        />
        Allow linked dependents
      </label>
    </div>
    <div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventRegistrationOpensAt}
        <input
          data-event-editor-field="registrationOpensAt"
          type="datetime-local"
          value="${escapeHtml(state.eventEditor.registrationOpensAt)}"
        />
      </label>
      <label>
        ${shellCopy[state.locale].eventRegistrationClosesAt}
        <input
          data-event-editor-field="registrationClosesAt"
          type="datetime-local"
          value="${escapeHtml(state.eventEditor.registrationClosesAt)}"
        />
      </label>
    </div>
    <section class="form-section">
      <h3>Registration operations</h3>
      <label>
        Registration question
        <input
          data-event-editor-field="registrationQuestionLabel"
          maxlength="180"
          placeholder="Dietary notes, accessibility needs, etc."
          value="${escapeHtml(state.eventEditor.registrationQuestionLabel)}"
        />
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="registrationQuestionRequired"
          type="checkbox"
          ${state.eventEditor.registrationQuestionRequired ? "checked" : ""}
        />
        Required question
      </label>
      <label class="checkbox-row">
        <input
          data-event-editor-field="waiverRequired"
          type="checkbox"
          ${state.eventEditor.waiverRequired ? "checked" : ""}
        />
        Require event waiver
      </label>
      <label>
        Waiver title
        <input
          data-event-editor-field="waiverTitle"
          maxlength="180"
          ${state.eventEditor.waiverRequired ? "required" : ""}
          value="${escapeHtml(state.eventEditor.waiverTitle)}"
        />
      </label>
      <label>
        Waiver body
        <textarea
          data-event-editor-field="waiverBody"
          maxlength="12000"
          ${state.eventEditor.waiverRequired ? "required" : ""}
        >${escapeHtml(state.eventEditor.waiverBody)}</textarea>
      </label>
      <div class="event-switch-grid">
        <label class="checkbox-row">
          <input
            data-event-editor-field="reminderOneDayBefore"
            type="checkbox"
            ${state.eventEditor.reminderOneDayBefore ? "checked" : ""}
          />
          1 day before
        </label>
        <label class="checkbox-row">
          <input
            data-event-editor-field="reminderTwoHoursBefore"
            type="checkbox"
            ${state.eventEditor.reminderTwoHoursBefore ? "checked" : ""}
          />
          2 hours before
        </label>
      </div>
    </section>`;
  }

  if (input.stepId === "media") {
    return `<div class="editor-grid">
      <label>
        ${shellCopy[state.locale].eventImageAlt}
        <input
          data-event-editor-field="imageAltText"
          maxlength="180"
          name="imageAltText"
          value="${escapeHtml(state.eventEditor.imageAltText)}"
        />
      </label>
      <label>
        ${shellCopy[state.locale].eventImageAltAr}
        <input
          data-event-editor-field="imageAltTextAr"
          dir="rtl"
          maxlength="180"
          name="imageAltTextAr"
          value="${escapeHtml(state.eventEditor.imageAltTextAr)}"
        />
      </label>
    </div>
    ${renderEventMediaManager(input.selectedEditorEvent)}`;
  }

  if (input.stepId === "communication") {
    return `<label class="checkbox-row">
      <input
        data-event-editor-field="notificationRequestedOnPublish"
        type="checkbox"
        ${state.eventEditor.notificationRequestedOnPublish ? "checked" : ""}
      />
      <span>
        ${state.locale === "ar" ? "إرسال إشعار دفع عند النشر" : "Send a push notification when published"}
      </span>
    </label>
    <label class="checkbox-row">
      <input data-event-editor-field="emailRequestedOnPublish" type="checkbox" ${state.eventEditor.emailRequestedOnPublish ? "checked" : ""} />
      <span>${state.locale === "ar" ? "إرسال بريد إلكتروني عند النشر" : "Send an email when published"}</span>
    </label>
    ${state.eventEditor.emailRequestedOnPublish ? renderEventEmailTemplateEditor(input.template, "event-wizard-email") : ""}`;
  }

  return `<div class="event-review-grid">
    <section>
      <h3>Event</h3>
      <dl class="event-review-list">
        <div>
          <dt>Title</dt>
          <dd>${escapeHtml(state.eventEditor.title || "-")}</dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>${escapeHtml(eventTypeLabel(state.eventEditor.eventType))}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>${escapeHtml(state.eventEditor.status)}</dd>
        </div>
        <div>
          <dt>When</dt>
          <dd>${escapeHtml(eventEditorDateRangeSummary())}</dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>${escapeHtml(eventEditorLocationSummary())}</dd>
        </div>
        <div>
          <dt>Registration</dt>
          <dd>${escapeHtml(eventEditorRegistrationSummary())}</dd>
        </div>
      </dl>
    </section>
    <section>
      <h3>${state.locale === "ar" ? "البريد الإلكتروني" : "Email"}</h3>
      ${state.eventEditor.emailRequestedOnPublish ? renderEventEmailPreview(input.template) : `<p>${state.locale === "ar" ? "لن يتم إرسال بريد إلكتروني عند النشر." : "No email will be sent when published."}</p>`}
    </section>
  </div>`;
}

function renderEventStatusOptions(): string {
  const copy = shellCopy[state.locale];
  const options: Array<[EventEditorInput["status"], string]> = [
    ["draft", copy.eventDraft],
    ["scheduled", copy.eventScheduled],
    ["published", copy.eventPublished],
    ["registration_closed", copy.eventRegistrationClosedStatus],
    ["cancelled", copy.eventCancelled],
    ["completed", copy.eventCompleted],
    ["archived", copy.eventArchived],
  ];
  return options
    .map(
      ([value, label]) =>
        `<option value="${value}" ${
          state.eventEditor.status === value ? "selected" : ""
        }>${escapeHtml(label)}</option>`,
    )
    .join("");
}

function eventEmailDeliveryTypeLabel(type: EventCommunicationDeliveryType): string {
  const copy = eventEmailCopy[state.locale];
  if (type === "urgentNotice") {
    return copy.urgent;
  }
  if (type === "materialUpdate") {
    return copy.update;
  }
  return copy.published;
}

function eventEmailDeliveryOptionsHtml(): string {
  return (["eventPublished", "materialUpdate", "urgentNotice"] as const)
    .map(
      (type) =>
        `<option value="${type}" ${
          state.eventEmailDeliveryType === type ? "selected" : ""
        }>${escapeHtml(eventEmailDeliveryTypeLabel(type))}</option>`,
    )
    .join("");
}

function setEventEmailDeliveryType(value: string, input: { refreshTemplate?: boolean } = {}): void {
  state.eventEmailDeliveryType =
    value === "materialUpdate" || value === "urgentNotice" ? value : "eventPublished";
  if (input.refreshTemplate) state.eventEmailTestStatus = null;
}

function renderEventEmailTemplateEditor(
  template: { message: string; subject: string },
  idPrefix: string,
): string {
  const copy = eventEmailCopy[state.locale];
  const feedback = state.eventEmailTestStatus;
  return `<section class="event-email-template-panel">
    <div class="event-email-composer">
      <div class="event-email-toolbar">
        <label>${copy.delivery}
          <select data-event-email-template-field="deliveryType" id="${idPrefix}-delivery-type">
            ${eventEmailDeliveryOptionsHtml()}
          </select>
        </label>
        <button class="secondary-action" data-event-email-reset type="button" ${state.busy ? "disabled" : ""}>${copy.reset}</button>
      </div>
      <label>${copy.subject}
        <input data-event-email-template-field="subject" id="${idPrefix}-subject" maxlength="180" dir="auto" required value="${escapeHtml(template.subject)}" />
      </label>
      <label>${copy.body}
        <textarea data-event-email-template-field="message" id="${idPrefix}-message" maxlength="4000" rows="14" dir="auto" required>${escapeHtml(template.message)}</textarea>
      </label>
      <div class="event-email-test-row">
        <label>${copy.testAddress}
          <input data-event-email-template-field="testAddress" id="${idPrefix}-test-address" inputmode="email" maxlength="254" placeholder="name@example.com" type="email" dir="ltr" value="${escapeHtml(state.eventEmailTestAddress)}" />
        </label>
        <button data-event-email-test-send type="button" ${state.busy ? "disabled" : ""}>${state.busy ? copy.sending : copy.send}</button>
      </div>
      <div data-event-email-feedback aria-live="polite">
        ${feedback ? `<p class="${feedback.error ? "error" : "status"} status-inline" role="${feedback.error ? "alert" : "status"}">${escapeHtml(feedback.message)}</p>` : ""}
      </div>
    </div>
    <section class="event-email-preview-pane" aria-label="${copy.preview}">
      <h3>${copy.preview}</h3>
      ${renderEventEmailPreview(template)}
    </section>
  </section>`;
}

function eventEmailPreviewHtml(template: { message: string; subject: string }): string {
  return renderEventEmailHtml({
    bodyText: template.message,
    subject: template.subject,
    locale: state.locale,
    organizationName: selectedOrganization().displayName,
    organizationLogoUrl: state.organizationProfileLogoDownloadUrl ?? null,
    organizationAccent: null,
    eventType: state.eventEditor.eventType,
    communicationType: state.eventEmailDeliveryType,
  });
}

function renderEventEmailPreview(template: { message: string; subject: string }): string {
  return `<iframe class="event-email-preview" title="${eventEmailCopy[state.locale].preview}" sandbox="" referrerpolicy="no-referrer" srcdoc="${escapeHtml(eventEmailPreviewHtml(template))}"></iframe>`;
}

function refreshEventEmailPreview(): void {
  const html = eventEmailPreviewHtml(currentEventEmailTemplate());
  document.querySelectorAll<HTMLIFrameElement>("iframe.event-email-preview").forEach((frame) => {
    frame.srcdoc = html;
  });
}

function eventEditorDateRangeSummary(): string {
  const startsAt = state.eventEditor.startsAt.trim()
    ? new Date(state.eventEditor.startsAt)
    : undefined;
  const endsAt = state.eventEditor.endsAt.trim() ? new Date(state.eventEditor.endsAt) : undefined;

  if (!startsAt || !Number.isFinite(startsAt.getTime())) {
    return "Time to be confirmed";
  }

  const start = startsAt.toLocaleString(state.locale);
  const end =
    endsAt && Number.isFinite(endsAt.getTime()) ? endsAt.toLocaleString(state.locale) : "";
  return end ? `${start} - ${end}` : start;
}

function eventEditorLocationSummary(): string {
  return (
    [state.eventEditor.campusNameSnapshot, state.eventEditor.locationName]
      .filter((part) => typeof part === "string" && part.trim().length > 0)
      .join(" · ") || "-"
  );
}

function eventEditorRegistrationSummary(): string {
  if (!state.eventEditor.registrationEnabled) {
    return "No registration";
  }

  const capacity = state.eventEditor.registrationCapacity.trim() || "not set";
  const waitlist = state.eventEditor.waitlistEnabled ? "waitlist enabled" : "no waitlist";
  return `Free registration · capacity ${capacity} · ${waitlist}`;
}

function eventLifecycleActionCopy(locale: LocaleCode): {
  delete: string;
  deleteConfirm: string;
  deleteDone: string;
  delistDone: string;
  makeInvisible: string;
  makeInvisibleConfirm: string;
} {
  return locale === "ar"
    ? {
        delete: "حذف",
        deleteConfirm:
          "حذف هذه الفعالية؟ سيتم أرشفتها وإخفاؤها عن الأعضاء مع الاحتفاظ بالسجل التشغيلي.",
        deleteDone: "تم حذف الفعالية.",
        delistDone: "تم إخفاء الفعالية.",
        makeInvisible: "إخفاء",
        makeInvisibleConfirm: "إخفاء هذه الفعالية عن الأعضاء؟ سيتم نقلها إلى المسودة.",
      }
    : {
        delete: "Delete",
        deleteConfirm:
          "Delete this event? It will be archived and hidden from members while preserving operational history.",
        deleteDone: "Event deleted.",
        delistDone: "Event hidden.",
        makeInvisible: "Make invisible",
        makeInvisibleConfirm: "Make this event invisible to members? It will move back to draft.",
      };
}

function eventTypeLabel(
  eventType: CommunityEventRow["eventType"] | EventEditorInput["eventType"],
): string {
  if (eventType === "friday_program") {
    return "Friday program";
  }
  if (eventType === "janazah") {
    return "Janazah / urgent funeral";
  }
  return "Standard event";
}

export function eventCommunicationTemplate(
  event: CommunityEventRow,
  type: "eventPublished" | "urgentNotice",
): { message: string; subject: string } {
  const title = state.locale === "ar" && event.titleAr ? event.titleAr : event.title;
  const when = event.startsAt ? event.startsAt.toLocaleString(state.locale) : "";
  const location = [event.campusNameSnapshot, event.locationName]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(" · ");
  const organization = selectedOrganization();

  if (event.eventType === "janazah" || type === "urgentNotice") {
    return {
      message: [
        "إنا لله وإنا إليه راجعون",
        "",
        "Dear Family and Friends,",
        "",
        "Inna lillahi wa inna ilayhi raji'un.",
        "",
        `With deep sadness, we announce the passing of [Name].`,
        "",
        `Janazah prayer: ${when || "[Day/date/time]"} at ${location || "[Location]"}.`,
        "",
        "Burial:",
        "[Cemetery name]",
        "[Address]",
        "",
        "Food / condolences:",
        "[Details]",
        "",
        "Azza:",
        "[Day/date/time/location]",
        "",
        "May Allah forgive them, have mercy on them, and grant them the highest level of Jannah. Ameen.",
        "",
        "الأهل والأصدقاء الكرام",
        "",
        "إنا لله وإنا إليه راجعون",
        "",
        "ببالغ الحزن والأسى، ننعي إليكم وفاة [الاسم].",
        "",
        "ستقام صلاة الجنازة: [اليوم/التاريخ/الوقت] في [الموقع].",
        "",
        "وسيكون الدفن في:",
        "[اسم المقبرة]",
        "[العنوان]",
      ].join("\n"),
      subject: `Urgent Janazah notice: ${title}`,
    };
  }

  if (event.eventType === "friday_program") {
    return {
      message: [
        `Activities for Friday, ${event.startsAt ? event.startsAt.toLocaleDateString("en-US") : "[date]"}`,
        "",
        "12:45 Recitation of Surah Al Kahf by [Name]",
        "1:30 Jumaa prayer",
        "[Asr time] Asr prayer",
        "[Maghrib time] Maghrib Prayer",
        "[Isha time] Isha Prayer",
        "",
        "Program note:",
        event.summary ||
          "[Add the sermon, speaker, topic, youth program, dinner, or family activity details.]",
        "",
        `Download our ${organization.displayName} app:`,
        "iOS: [App Store link]",
        "Android: [Google Play link]",
        "",
        'Prophet (SAW) said: "On (the day of) Jummah is an hour during which Allah gives to a Muslim servant standing in prayer whatever he asks for."',
      ].join("\n"),
      subject: `Activities for Friday at ${organization.displayName}`,
    };
  }

  return {
    message: [
      "Assalamu alaikum,",
      "",
      `${organization.displayName} invites you to ${title}.`,
      "",
      when ? `Time: ${when}` : "Time: [Day/date/time]",
      location ? `Location: ${location}` : "Location: [Location]",
      event.registrationEnabled
        ? "Registration is open in the Shdeed app."
        : "No registration is required.",
      "",
      event.summary || "[Short event summary]",
      event.description && event.description !== event.summary ? event.description : "",
      "",
      "Open the Shdeed app for details.",
    ].join("\n"),
    subject: `You're invited: ${title}`,
  };
}

function eventEmailTemplateFromEditor(): { message: string; subject: string } {
  return buildEventEmailTemplate({
    editor: state.eventEditor,
    organizationName: selectedOrganization().displayName,
    locale: state.locale,
    deliveryType: state.eventEmailDeliveryType,
  });
}

function syncEventEmailTemplateFromEditor(input: { force?: boolean } = {}): void {
  if (input.force) {
    state.eventEmailDraft = { subject: null, message: null };
    state.eventEmailTestStatus = null;
  }
}

function currentEventEmailTemplate(): { message: string; subject: string } {
  return resolveEventEmailDraft(eventEmailTemplateFromEditor(), state.eventEmailDraft);
}

function setEventEmailTemplateField(key: "message" | "subject", value: string): void {
  if (value !== currentEventEmailTemplate()[key]) {
    state.eventEmailDraft[key] = value;
    state.eventEmailTestStatus = null;
  }
}

function eventTableSortableHeader(key: EventTableSortKey, label: string): string {
  const isActive = eventTableSortKey === key;
  const indicator = isActive ? (eventTableSortDirection === "asc" ? "ASC" : "DESC") : "SORT";
  const ariaSort = isActive
    ? eventTableSortDirection === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return `<th aria-sort="${ariaSort}" scope="col">
    <button
      class="table-sort"
      data-event-sort="${key}"
      title="Sort by ${escapeHtml(label)}"
      type="button"
    >
      <span>${escapeHtml(label)}</span>
      <span aria-hidden="true" class="table-sort-indicator">${indicator}</span>
    </button>
  </th>`;
}

function sortValueForEvent(event: CommunityEventRow, key: EventTableSortKey): number | string {
  switch (key) {
    case "attendance":
      return event.attendanceCheckedInCount;
    case "location":
      return `${event.campusNameSnapshot ?? ""} ${event.locationName}`.trim().toLowerCase();
    case "registration":
      return event.registrationConfirmedCount;
    case "startsAt":
      return event.startsAt?.getTime() ?? 0;
    case "status":
      return event.status.toLowerCase();
    case "title":
      return (state.locale === "ar" && event.titleAr ? event.titleAr : event.title).toLowerCase();
  }
}

function sortedCommunityEventsForDisplay(): CommunityEventRow[] {
  return filteredCommunityEventsForDisplay(state.events).sort((left, right) => {
    const leftValue = sortValueForEvent(left, eventTableSortKey);
    const rightValue = sortValueForEvent(right, eventTableSortKey);
    const direction = eventTableSortDirection === "asc" ? 1 : -1;

    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue), state.locale) * direction;
  });
}

function filteredCommunityEventsForDisplay(events: CommunityEventRow[]): CommunityEventRow[] {
  const query = eventListSearch.trim().toLowerCase();
  if (!query) {
    return [...events];
  }

  return events.filter((event) => {
    const searchable = [
      event.eventId,
      eventTypeLabel(event.eventType),
      event.title,
      event.titleAr,
      event.summary,
      event.summaryAr,
      event.description,
      event.descriptionAr,
      event.status,
      event.campusNameSnapshot,
      event.locationName,
      event.addressLine1,
    ]
      .filter((part): part is string => typeof part === "string" && part.length > 0)
      .join(" ")
      .toLowerCase();

    return searchable.includes(query);
  });
}

type PaginatedItems<T> = {
  end: number;
  items: T[];
  page: number;
  pageSize: number;
  start: number;
  total: number;
  totalPages: number;
};

function paginateItems<T>(items: T[], requestedPage: number, pageSize: number): PaginatedItems<T> {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(Math.trunc(requestedPage) || 1, 1), totalPages);
  const startIndex = (page - 1) * pageSize;
  const pageItems = items.slice(startIndex, startIndex + pageSize);

  return {
    end: pageItems.length === 0 ? 0 : startIndex + pageItems.length,
    items: pageItems,
    page,
    pageSize,
    start: pageItems.length === 0 ? 0 : startIndex + 1,
    total,
    totalPages,
  };
}

function renderPaginationControls(
  target: string,
  pagination: PaginatedItems<unknown>,
  itemLabel: string,
): string {
  if (pagination.total === 0) {
    return "";
  }

  const status = `Showing ${pagination.start}-${pagination.end} of ${pagination.total} ${itemLabel}`;
  if (pagination.totalPages <= 1) {
    return `<p class="list-pagination-status">${escapeHtml(status)}</p>`;
  }

  return `<nav class="list-pagination" aria-label="${escapeHtml(`${itemLabel} pagination`)}">
    <p>${escapeHtml(status)}</p>
    <div>
      <button
        class="secondary-action inline-action"
        data-list-pagination="${escapeHtml(target)}"
        data-list-page="${pagination.page - 1}"
        type="button"
        ${pagination.page <= 1 ? "disabled" : ""}
      >Previous</button>
      <span class="status-pill status-pending">Page ${pagination.page} of ${
        pagination.totalPages
      }</span>
      <button
        class="secondary-action inline-action"
        data-list-pagination="${escapeHtml(target)}"
        data-list-page="${pagination.page + 1}"
        type="button"
        ${pagination.page >= pagination.totalPages ? "disabled" : ""}
      >Next</button>
    </div>
  </nav>`;
}

function searchLabelForCollection(heading: string): string {
  return state.locale === "ar" ? `بحث في ${heading}` : `Search ${heading}`;
}

function noSearchMatchesLabel(heading: string): string {
  return state.locale === "ar"
    ? `لا توجد نتائج مطابقة في ${heading}.`
    : `No ${heading.toLowerCase()} match the search.`;
}

function collectionHeading(container: HTMLElement | null, fallback: string): string {
  const heading = container?.querySelector<HTMLElement>("h2, h3, h4")?.textContent?.trim();
  const ariaLabel = container?.getAttribute("aria-label")?.trim();
  return heading || ariaLabel || fallback;
}

function collectionSearchKey(
  kind: "list" | "table",
  element: HTMLElement,
  heading: string,
  index: number,
): string {
  const elementId = element.id.trim();
  if (elementId) {
    return `${kind}:${elementId}`;
  }

  const container = element.closest<HTMLElement>("[id]");
  return `${kind}:${container?.id || "workspace"}:${heading}:${index}`;
}

function collectionSearchState(key: string): SearchableCollectionUiState {
  const existing = searchableCollectionUiState.get(key);
  if (existing) {
    return existing;
  }

  const created: SearchableCollectionUiState = {
    query: "",
    sortColumnIndex: null,
    sortDirection: "asc",
  };
  searchableCollectionUiState.set(key, created);
  return created;
}

function normalizedSearchText(value: string): string {
  return value.toLocaleLowerCase(state.locale).trim();
}

function tableCellSortText(row: HTMLTableRowElement, columnIndex: number): string {
  const cell = row.cells.item(columnIndex);
  return (cell?.dataset.sortValue ?? cell?.textContent ?? "").replace(/\s+/g, " ").trim();
}

function numericSortValue(text: string): number | null {
  const normalized = text.replace(/[$,%\s,]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function dateSortValue(text: string): number | null {
  if (!/[/:,-]|\b(?:am|pm)\b/i.test(text)) {
    return null;
  }
  const value = Date.parse(text);
  return Number.isNaN(value) ? null : value;
}

function compareTableSortText(left: string, right: string): number {
  if (left.length === 0 && right.length === 0) {
    return 0;
  }
  if (left.length === 0) {
    return 1;
  }
  if (right.length === 0) {
    return -1;
  }

  const leftNumber = numericSortValue(left);
  const rightNumber = numericSortValue(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }

  const leftDate = dateSortValue(left);
  const rightDate = dateSortValue(right);
  if (leftDate !== null && rightDate !== null) {
    return leftDate - rightDate;
  }

  return left.localeCompare(right, state.locale, {
    numeric: true,
    sensitivity: "base",
  });
}

function enhanceSortableTableHeaders(
  table: HTMLTableElement,
  uiState: SearchableCollectionUiState,
  applyTableState: () => void,
): HTMLButtonElement[] {
  const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>("thead th"));
  const sortButtons: HTMLButtonElement[] = [];

  headers.forEach((header, columnIndex) => {
    if (
      header.colSpan > 1 ||
      header.querySelector("[data-event-sort], [data-generic-table-sort]")
    ) {
      return;
    }

    const label = header.textContent?.replace(/\s+/g, " ").trim() ?? "";
    if (!label) {
      return;
    }
    if (header.dataset.tableNoSort === "true" || /^actions?$/iu.test(label)) {
      return;
    }

    const labelSpan = document.createElement("span");
    labelSpan.append(...Array.from(header.childNodes));

    const indicator = document.createElement("span");
    indicator.className = "table-sort-indicator";
    indicator.setAttribute("aria-hidden", "true");

    const button = document.createElement("button");
    button.className = "table-sort";
    button.dataset.genericTableSort = String(columnIndex);
    button.title = `Sort by ${label}`;
    button.type = "button";
    button.append(labelSpan, indicator);

    header.replaceChildren(button);
    header.setAttribute("aria-sort", "none");
    sortButtons[columnIndex] = button;

    button.addEventListener("click", () => {
      if (uiState.sortColumnIndex === columnIndex) {
        uiState.sortDirection = uiState.sortDirection === "asc" ? "desc" : "asc";
      } else {
        uiState.sortColumnIndex = columnIndex;
        uiState.sortDirection = "asc";
      }
      applyTableState();
    });
  });

  return sortButtons;
}

function enhanceSearchableDataCollections(appRoot: HTMLElement): void {
  const tables = Array.from(
    appRoot.querySelectorAll<HTMLTableElement>(
      ".workspace-pages table, #task-window-body table, [data-event-operations-overlay] table",
    ),
  );

  tables.forEach((table, index) => {
    if (
      table.dataset.searchEnhanced === "true" ||
      table.classList.contains("support-table") ||
      table.closest("nav, .auth-workspace") ||
      (table.classList.contains("events-table") &&
        table
          .closest<HTMLElement>("article, section, .panel")
          ?.querySelector("#event-list-search")) ||
      (table.classList.contains("people-table") &&
        table
          .closest<HTMLElement>("article, section, .panel")
          ?.querySelector("#people-list-search"))
    ) {
      return;
    }

    const tableBody = table.tBodies.item(0);
    const rows = Array.from(tableBody?.querySelectorAll<HTMLTableRowElement>("tr") ?? []);
    if (rows.length === 0) {
      return;
    }

    table.dataset.searchEnhanced = "true";
    const scrollContainer = table.closest<HTMLElement>(".table-scroll") ?? table;
    const container =
      scrollContainer.closest<HTMLElement>("article, section, .panel, [role='region']") ??
      scrollContainer.parentElement;
    const heading = collectionHeading(container, table.getAttribute("aria-label") ?? "table");
    const key = collectionSearchKey("table", table, heading, index);
    const uiState = collectionSearchState(key);
    const panelOwnsSearch =
      container?.querySelector("#event-list-search, #people-list-search") !== null;
    let input: HTMLInputElement | null = null;
    let empty: HTMLParagraphElement | null = null;

    if (!panelOwnsSearch) {
      const toolbar = document.createElement("div");
      toolbar.className = "generic-list-toolbar collection-search-toolbar";
      toolbar.innerHTML = `
        <label class="list-search">
          <span>${escapeHtml(searchLabelForCollection(heading))}</span>
          <input maxlength="160" value="${escapeHtml(uiState.query)}" />
        </label>
      `;
      empty = document.createElement("p");
      empty.className = "collection-search-empty";
      empty.hidden = true;
      empty.textContent = noSearchMatchesLabel(heading);

      scrollContainer.before(toolbar);
      scrollContainer.after(empty);
      input = toolbar.querySelector<HTMLInputElement>("input");
    }

    let sortButtons: HTMLButtonElement[] = [];
    const updateSortIndicators = () => {
      sortButtons.forEach((button, columnIndex) => {
        const active = uiState.sortColumnIndex === columnIndex;
        const indicator = button.querySelector<HTMLElement>(".table-sort-indicator");
        if (indicator) {
          indicator.textContent = active
            ? uiState.sortDirection === "asc"
              ? "ASC"
              : "DESC"
            : "SORT";
        }
        button
          .closest("th")
          ?.setAttribute(
            "aria-sort",
            active ? (uiState.sortDirection === "asc" ? "ascending" : "descending") : "none",
          );
      });
    };
    const applyTableState = () => {
      const query = normalizedSearchText(uiState.query);
      let visibleCount = 0;
      const sortedRows = rows
        .map((row, originalIndex) => ({ originalIndex, row }))
        .sort((left, right) => {
          if (uiState.sortColumnIndex === null) {
            return left.originalIndex - right.originalIndex;
          }
          const comparison = compareTableSortText(
            tableCellSortText(left.row, uiState.sortColumnIndex),
            tableCellSortText(right.row, uiState.sortColumnIndex),
          );
          const stableComparison = comparison || left.originalIndex - right.originalIndex;
          return uiState.sortDirection === "asc" ? stableComparison : -stableComparison;
        });

      sortedRows.forEach(({ row }) => tableBody?.append(row));
      updateSortIndicators();

      sortedRows.forEach(({ row }) => {
        const matches =
          query.length === 0 || normalizedSearchText(row.textContent ?? "").includes(query);
        row.hidden = !matches;
        if (matches) {
          visibleCount += 1;
        }
      });

      if (empty) {
        empty.hidden = query.length === 0 || visibleCount > 0;
      }
    };
    sortButtons = enhanceSortableTableHeaders(table, uiState, applyTableState);

    input?.addEventListener("input", () => {
      uiState.query = input.value;
      applyTableState();
    });
    applyTableState();
  });

  const lists = Array.from(
    appRoot.querySelectorAll<HTMLUListElement>(
      ".workspace-pages ul.status-list, .workspace-pages ul.event-media-list, #task-window-body ul.status-list, #task-window-body ul.event-media-list, [data-event-operations-overlay] ul.status-list, [data-event-operations-overlay] ul.event-media-list",
    ),
  );

  lists.forEach((list, index) => {
    if (list.dataset.searchEnhanced === "true" || list.closest("nav, .auth-workspace")) {
      return;
    }

    const items = Array.from(list.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (items.length <= 1) {
      return;
    }

    list.dataset.searchEnhanced = "true";
    const container =
      list.closest<HTMLElement>("article, section, .panel, [role='region']") ?? list.parentElement;
    const heading = collectionHeading(container, "list");
    const key = collectionSearchKey("list", list, heading, index);
    const uiState = collectionSearchState(key);
    const toolbar = document.createElement("div");
    toolbar.className = "generic-list-toolbar collection-search-toolbar";
    toolbar.innerHTML = `
      <label class="list-search">
        <span>${escapeHtml(searchLabelForCollection(heading))}</span>
        <input maxlength="160" value="${escapeHtml(uiState.query)}" />
      </label>
    `;
    const empty = document.createElement("p");
    empty.className = "collection-search-empty";
    empty.hidden = true;
    empty.textContent = noSearchMatchesLabel(heading);

    list.before(toolbar);
    list.after(empty);

    const input = toolbar.querySelector<HTMLInputElement>("input");
    const applySearch = () => {
      const query = normalizedSearchText(uiState.query);
      let visibleCount = 0;

      items.forEach((item) => {
        const matches =
          query.length === 0 || normalizedSearchText(item.textContent ?? "").includes(query);
        item.hidden = !matches;
        if (matches) {
          visibleCount += 1;
        }
      });

      empty.hidden = query.length === 0 || visibleCount > 0;
    };

    input?.addEventListener("input", () => {
      uiState.query = input.value;
      applySearch();
    });
    applySearch();
  });
}

function enhanceLongRequestLists(appRoot: HTMLElement): void {
  const lists = Array.from(appRoot.querySelectorAll<HTMLUListElement>("ul.request-list"));

  lists.forEach((list, index) => {
    const items = Array.from(list.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    if (items.length <= 1 || list.dataset.genericListEnhanced === "true") {
      return;
    }

    const container = list.closest<HTMLElement>("article, section, .panel") ?? list.parentElement;
    const heading = container?.querySelector<HTMLElement>("h2, h3")?.textContent?.trim() ?? "list";
    const key = list.id || `${container?.id || "list"}-${index}-${heading}`;
    const uiState = genericListUiState.get(key) ?? { page: 1, query: "" };
    genericListUiState.set(key, uiState);
    list.dataset.genericListEnhanced = "true";
    const shouldPaginate = items.length > genericListPageSize;

    const toolbar = document.createElement("div");
    toolbar.className = "generic-list-toolbar";
    toolbar.innerHTML = `
      <label class="list-search">
        <span>${escapeHtml(searchLabelForCollection(heading))}</span>
        <input maxlength="160" value="${escapeHtml(uiState.query)}" />
      </label>
    `;

    const pagination = shouldPaginate ? document.createElement("nav") : null;
    const empty = shouldPaginate ? null : document.createElement("p");
    if (pagination) {
      pagination.className = "list-pagination";
      pagination.setAttribute("aria-label", `${heading} pagination`);
      pagination.innerHTML = `
        <p></p>
        <div>
          <button class="secondary-action inline-action" type="button">Previous</button>
          <span class="status-pill status-pending"></span>
          <button class="secondary-action inline-action" type="button">Next</button>
        </div>
      `;
    }
    if (empty) {
      empty.className = "collection-search-empty";
      empty.hidden = true;
      empty.textContent = noSearchMatchesLabel(heading);
    }

    list.before(toolbar);
    if (pagination) {
      list.after(pagination);
    } else if (empty) {
      list.after(empty);
    }

    const input = toolbar.querySelector<HTMLInputElement>("input");
    const status = pagination?.querySelector<HTMLParagraphElement>("p") ?? null;
    const buttons = pagination?.querySelectorAll<HTMLButtonElement>("button") ?? null;
    const pageLabel = pagination?.querySelector<HTMLSpanElement>("span") ?? null;
    const previousButton = buttons?.item(0) ?? null;
    const nextButton = buttons?.item(1) ?? null;

    const applyListState = () => {
      const query = normalizedSearchText(uiState.query);
      const matchingItems = items.filter((item) =>
        query ? normalizedSearchText(item.textContent ?? "").includes(query) : true,
      );
      const totalPages = Math.max(1, Math.ceil(matchingItems.length / genericListPageSize));
      uiState.page = Math.min(Math.max(uiState.page, 1), totalPages);
      const startIndex = shouldPaginate ? (uiState.page - 1) * genericListPageSize : 0;
      const visibleItems = new Set(
        shouldPaginate
          ? matchingItems.slice(startIndex, startIndex + genericListPageSize)
          : matchingItems,
      );
      items.forEach((item) => {
        item.hidden = !visibleItems.has(item);
      });

      const start = matchingItems.length === 0 ? 0 : startIndex + 1;
      const end = shouldPaginate
        ? Math.min(startIndex + genericListPageSize, matchingItems.length)
        : matchingItems.length;
      if (status) {
        status.textContent =
          matchingItems.length === 0
            ? noSearchMatchesLabel(heading)
            : `Showing ${start}-${end} of ${matchingItems.length}`;
      }
      if (pageLabel) {
        pageLabel.textContent = `Page ${uiState.page} of ${totalPages}`;
      }
      if (previousButton) {
        previousButton.disabled = uiState.page <= 1;
      }
      if (nextButton) {
        nextButton.disabled = uiState.page >= totalPages;
      }
      if (empty) {
        empty.hidden = query.length === 0 || matchingItems.length > 0;
      }
    };

    input?.addEventListener("input", () => {
      uiState.query = input.value;
      uiState.page = 1;
      applyListState();
    });
    previousButton?.addEventListener("click", () => {
      uiState.page -= 1;
      applyListState();
    });
    nextButton?.addEventListener("click", () => {
      uiState.page += 1;
      applyListState();
    });
    applyListState();
  });
}

function prepareTaskWindows(appRoot: HTMLElement): void {
  const forms = Array.from(
    appRoot.querySelectorAll<HTMLFormElement>(
      ".workspace:not(.auth-workspace) form.editor-form, .workspace:not(.auth-workspace) form.stacked-form",
    ),
  );

  for (const form of forms) {
    if (!form.id || form.closest(".auth-panel") || form.classList.contains("compact-form")) {
      continue;
    }

    const definition = taskWindowDefinitionFor(form.id);
    const source = document.createElement("div");
    source.className = "task-window-source";
    source.dataset.taskWindowSource = form.id;
    form.before(source);
    source.append(form);

    if (form.dataset.taskWindowLaunch !== "manual") {
      const launch = document.createElement("button");
      launch.className = "secondary-action task-window-launch";
      launch.dataset.taskWindowOpen = form.id;
      launch.type = "button";
      launch.textContent = definition.launchLabel;
      source.before(launch);
    }
  }

  if (!activeTaskWindowId) {
    return;
  }

  const activeSource = appRoot.querySelector<HTMLElement>(
    `[data-task-window-source="${CSS.escape(activeTaskWindowId)}"]`,
  );
  const taskWindowBody = appRoot.querySelector<HTMLElement>("#task-window-body");
  if (!activeSource || !taskWindowBody) {
    activeTaskWindowId = null;
    return;
  }

  activeSource.classList.add("is-active");
  taskWindowBody.replaceChildren(...Array.from(activeSource.childNodes));
}

function bindTaskWindowControls(appRoot: HTMLElement): void {
  appRoot.querySelectorAll<HTMLButtonElement>("[data-task-window-open]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskWindowId = button.dataset.taskWindowOpen ?? null;
      if (taskWindowId === "ramadan-setup-editor-form" && isCurrentRamadanSetupActiveForEditing()) {
        state.ramadanSetupEditConfirmationPending = true;
        state.ramadanSetupMessage =
          "Ramadan is active. Confirm before reopening setup and making changes.";
        activeTaskWindowId = null;
        render();
        return;
      }
      if (taskWindowId === "organization-profile-editor-form") {
        resetOrganizationProfileEditor();
      }
      if (taskWindowId === "organization-prayer-settings-form") {
        resetOrganizationPrayerSettingsEditor();
      }
      if (taskWindowId === "organization-islamic-components-form") {
        resetOrganizationIslamicComponentsEditor();
      }
      activeTaskWindowId = taskWindowId;
      state.ramadanSetupEditConfirmationPending = false;
      render();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-task-window-close]").forEach((button) => {
    button.addEventListener("click", () => {
      if (activeTaskWindowId === "operational-mode-editor-form") {
        closeOperationalModeEditor();
        return;
      }
      activeTaskWindowId = null;
      render();
    });
  });
}

function bindEventOperationsWindowControls(appRoot: HTMLElement): void {
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-create]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventEditorWindow();
    });
  });
  appRoot
    .querySelector<HTMLInputElement>("#event-list-search")
    ?.addEventListener("input", (event) => {
      eventListSearch = (event.target as HTMLInputElement).value;
      eventListPage = 1;
      renderPreservingTaskWindowScroll();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-list-pagination]").forEach((button) => {
    button.addEventListener("click", () => {
      const page = Number(button.dataset.listPage ?? "1");
      if (!Number.isFinite(page)) {
        return;
      }
      switch (button.dataset.listPagination) {
        case "eventAttendance":
          eventAttendancePage = page;
          break;
        case "eventRegistrations":
          eventRegistrationPage = page;
          break;
        case "eventWaitlist":
          eventWaitlistPage = page;
          break;
        case "events":
          eventListPage = page;
          break;
        default:
          return;
      }
      renderPreservingTaskWindowScroll();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const sortKey = button.dataset.eventSort as EventTableSortKey | undefined;
      if (!sortKey) {
        return;
      }

      if (eventTableSortKey === sortKey) {
        eventTableSortDirection = eventTableSortDirection === "asc" ? "desc" : "asc";
      } else {
        eventTableSortKey = sortKey;
        eventTableSortDirection = sortKey === "startsAt" ? "asc" : "asc";
      }
      eventListPage = 1;
      render();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-read]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventOperationsWindow(button.dataset.eventRead ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventEditorWindow(button.dataset.eventEdit ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-notify]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventOperationsWindow(button.dataset.eventNotify ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-delist]").forEach((button) => {
    button.addEventListener("click", () => {
      void requestEventLifecycleAction(button.dataset.eventDelist ?? "", "delist");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-delete]").forEach((button) => {
    button.addEventListener("click", () => {
      void requestEventLifecycleAction(button.dataset.eventDelete ?? "", "delete");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-check-in]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventOperationsWindow(button.dataset.eventCheckIn ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-operations-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      openEventEditorWindow(button.dataset.eventOperationsEdit ?? "");
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-event-operations-check-in]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        openEventCheckInWindow(button.dataset.eventOperationsCheckIn ?? "");
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-event-operations-export]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        resetEventEditor(state.organizationId, button.dataset.eventOperationsExport ?? "");
        activeEventOperationsWindowEventId = null;
        void exportEventAttendeesFromEditor({
          includeResponses: button.dataset.includeResponses === "true",
        });
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>(
      "[data-event-operations-send-email], [data-event-operations-send-push], [data-event-operations-send-both]",
    )
    .forEach((button) => {
      button.addEventListener("click", () => {
        const eventId =
          button.dataset.eventOperationsSendEmail ??
          button.dataset.eventOperationsSendPush ??
          button.dataset.eventOperationsSendBoth ??
          "";
        const root = button.closest<HTMLElement>("[data-event-operations-overlay]");
        syncEventEmailTemplateInputs(root ?? undefined);
        const template = currentEventEmailTemplate();
        const channels = button.dataset.eventOperationsSendEmail
          ? ["email"]
          : button.dataset.eventOperationsSendPush
            ? ["push"]
            : ["email", "push"];
        void sendEventCommunicationFromEditor({
          audience: "connectedPeople",
          channels,
          eventId,
          message: template.message,
          subject: template.subject,
          type: state.eventEmailDeliveryType,
        });
      });
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-operations-close]").forEach((button) => {
    button.addEventListener("click", () => {
      activeEventOperationsWindowEventId = null;
      render();
    });
  });
  appRoot
    .querySelector<HTMLElement>("[data-event-operations-overlay]")
    ?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) {
        activeEventOperationsWindowEventId = null;
        render();
      }
    });
}

function taskWindowDefinitionFor(formId: string): TaskWindowDefinition {
  if (formId === "operational-mode-editor-form") {
    return {
      launchLabel: state.locale === "ar" ? "وضع جديد" : "New mode",
      title: operationalModeOriginal
        ? state.locale === "ar"
          ? "تعديل الوضع"
          : "Edit mode"
        : state.locale === "ar"
          ? "وضع جديد"
          : "New mode",
      subtitle: operationalModeOriginal
        ? state.locale === "ar"
          ? operationalModeOriginal.displayNameAr || operationalModeOriginal.displayName
          : operationalModeOriginal.displayName
        : "",
    };
  }
  return (
    orgAdminTaskWindowDefinitions.get(formId) ?? {
      launchLabel: "Open action window",
      subtitle: "Complete this action in a focused workspace.",
      title: humanizeIdentifier(formId.replace(/-form$/u, "")),
    }
  );
}

function humanizeIdentifier(value: string): string {
  return value
    .replaceAll("-", " ")
    .replaceAll(/([a-z])([A-Z])/gu, "$1 $2")
    .toLowerCase();
}

function selectedPerson() {
  if (!useEmulator || auth.currentUser?.uid === state.userUid) {
    const person = resolvedCurrentPerson();
    const authenticatedUser =
      authenticationState.status === "authenticated" ? authenticationState.user : undefined;

    if (
      !person ||
      !authenticatedUser ||
      person.uid !== authenticatedUser.uid ||
      state.userUid !== authenticatedUser.uid
    ) {
      throw new Error("Your authenticated administrator session could not be verified.");
    }

    return {
      email: person.email.default ?? authenticatedUser.email ?? "Authenticated account",
      uid: person.uid,
    };
  }

  const person = organizationUsers.find((candidate) => candidate.uid === state.userUid);

  if (!person) {
    throw new Error(`Unknown development identity: ${state.userUid}`);
  }

  return person;
}

function selectedOrganization() {
  const organization = organizationAccess.organizations.find(
    (candidate) => candidate.id === state.organizationId,
  );

  if (!organization) {
    throw new Error("Organization access has not been verified.");
  }

  return organization;
}

function selectedRamadanSetup(): RamadanSetupRow | undefined {
  return (
    state.ramadanSetups.find((setup) => setup.status === "active") ??
    state.ramadanSetups.find((setup) => setup.status === "ready") ??
    state.ramadanSetups[0]
  );
}

function currentRamadanIdForAdmin(): string {
  return selectedRamadanSetup()?.ramadanId || state.iftarSponsorshipDefaultsEditor.ramadanId;
}

function isCurrentRamadanSetupActiveForEditing(): boolean {
  const setup = selectedRamadanSetup();
  if (!setup) {
    return false;
  }

  return (
    setup.status === "active" ||
    state.operationalModes.some(
      (mode) => mode.modeId === setup.linkedOperationalModeId && mode.status === "active",
    ) ||
    state.operationalModeProjection?.modeId === setup.linkedOperationalModeId
  );
}

function canonicalRoleIdsForSelectedContext(): readonly string[] {
  return (
    organizationAccess.organizations.find((candidate) => candidate.id === state.organizationId)
      ?.roleIds ?? []
  );
}

function selectedContextCanReadFinance(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("financeAdmin");
}

function selectedContextCanViewIftarSponsorshipAdmin(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("financeAdmin")
  );
}

function selectedContextCanManageOrganizationSettings(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("organizationAdmin");
}

function selectedContextCanManageBusinessDirectory(): boolean {
  return selectedContextCanManageOrganizationSettings();
}

function selectedContextCanViewRamadanCommandCenter(): boolean {
  return selectedContextCanViewIftarSponsorshipAdmin();
}

function selectedContextCanCreateManualIftarSponsorship(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("organizationAdmin");
}

function selectedContextCanTransferIftarSponsorship(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("organizationAdmin");
}

function selectedContextCanManageRamadanAdminNotes(): boolean {
  return selectedContextCanManageOrganizationSettings();
}

function selectedContextCanManageSupport(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("communicationsAdmin")
  );
}

function selectedContextCanReadBilling(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("financeAdmin")
  );
}

function selectedContextCanRequestRefund(): boolean {
  return canonicalRoleIdsForSelectedContext().includes("organizationOwner");
}

function selectedContextCanManageEvents(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("eventsAdmin")
  );
}

function selectedContextCanCheckInEvents(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return selectedContextCanManageEvents() || roleIds.includes("eventCheckInStaff");
}

function selectedContextCanManageSpaces(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("spacesAdmin")
  );
}

function selectedContextCanManageVolunteers(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return (
    roleIds.includes("organizationOwner") ||
    roleIds.includes("organizationAdmin") ||
    roleIds.includes("volunteerCoordinator")
  );
}

function selectAuthenticatedIdentity(uid: string): void {
  state.userUid = uid;
}

function organizationPreferenceKey(uid: string): string {
  return `org-admin:${projectId}:${uid}:organization`;
}

function retireOrganizationContext(nextOrganizationId?: string): void {
  if (retiringTenant) return;
  retiringTenant = true;
  realtimeRenderer.dispose();
  unsubscribeOrganizationAccess?.();
  unsubscribeModeWorkspace?.();
  unsubscribeIftarSponsorshipDefaults?.();
  unsubscribeIftarAttendanceDefaults?.();
  tenantListeners.dispose();
  currentRamadanHistoryRequestId += 1;
  currentPersonRequestId += 1;
  activeTaskWindowId = null;
  clearEventPendingMediaPreviews();
  if (state.organizationProfileLogoPreviewUrl)
    URL.revokeObjectURL(state.organizationProfileLogoPreviewUrl);
  if (nextOrganizationId !== undefined && auth.currentUser) {
    try {
      sessionStorage.setItem(organizationPreferenceKey(auth.currentUser.uid), nextOrganizationId);
    } catch {
      /* Optional preference. */
    }
  }
  document.querySelector("#app")?.replaceChildren();
  // A new document is the tenant boundary: no old editor, promise, upload or
  // nested listener is reused in the next tenant. The preference is revalidated.
  window.location.reload();
}

function selectAuthorizedOrganization(organizationId: string): void {
  if (state.busy || operationalModeBusy || organizationId === state.organizationId) return;
  if (
    !organizationAccess.organizations.some((organization) => organization.id === organizationId)
  ) {
    render();
    return;
  }
  retireOrganizationContext(organizationId);
}

function discoverAuthorizedOrganizations(uid: string): void {
  unsubscribeOrganizationAccess?.();
  organizationAccess = { organizations: [], loading: true, error: "" };
  unsubscribeOrganizationAccess = watchOrganizationAccess({
    db,
    uid,
    onChange: (access) => {
      if (retiringTenant || auth.currentUser?.uid !== uid) return;
      organizationAccess = access;
      const current = access.organizations.find(
        (organization) => organization.id === state.organizationId,
      );
      if (state.organizationId) {
        if (!current || JSON.stringify(current.roleIds) !== loadedOrganizationRoles) {
          retireOrganizationContext(
            chooseAuthorizedOrganization(access.organizations, state.organizationId),
          );
          return;
        }
      } else if (!access.loading) {
        let preferred: string | null = null;
        try {
          preferred = sessionStorage.getItem(organizationPreferenceKey(uid));
        } catch {
          /* Optional preference. */
        }
        const id = chooseAuthorizedOrganization(access.organizations, preferred);
        if (id) {
          const locale = state.locale;
          Object.assign(state, createShellState(id), { locale, userUid: uid });
          loadedOrganizationRoles = JSON.stringify(selectedOrganization().roleIds);
          connectOrganizationAdminPortal();
        }
      }
      renderRealtime();
    },
  });
  renderRealtime();
}

function renderOrganizationAccessGate(appRoot: HTMLElement, direction: "ltr" | "rtl"): void {
  const ar = state.locale === "ar";
  const title = organizationAccess.loading
    ? ar
      ? "جارٍ التحقق من صلاحيات المؤسسة"
      : "Checking organization access"
    : organizationAccess.error
      ? ar
        ? "تعذر التحقق من صلاحيات المؤسسة"
        : "Could not verify organization access"
      : ar
        ? "لا توجد مؤسسة مصرح لك بإدارتها"
        : "No authorized organization";
  const message = organizationAccess.loading
    ? ar
      ? "يرجى الانتظار."
      : "Please wait."
    : organizationAccess.error
      ? ar
        ? "تحقق من الاتصال ثم حاول مرة أخرى."
        : "Check your connection, then try again."
      : ar
        ? "هذا الحساب لا يملك دوراً إدارياً نشطاً في أي مؤسسة."
        : "This account has no active organization staff role.";
  appRoot.innerHTML = `<main class="auth-shell" dir="${direction}"><section class="auth-panel" aria-live="polite">
    <h1>${title}</h1><p>${message}</p>
    <label>${ar ? "اللغة" : "Language"}<select id="locale"><option value="en" ${ar ? "" : "selected"}>English</option><option value="ar" ${ar ? "selected" : ""}>العربية</option></select></label>
    ${organizationAccess.error ? `<button id="retry-organization-access" type="button">${ar ? "حاول مرة أخرى" : "Try again"}</button>` : ""}
    <button id="auth-sign-out" type="button">${ar ? "تسجيل الخروج" : "Sign out"}</button>
  </section></main>`;
  bindAuthenticationHandlers(appRoot);
  appRoot.querySelector("#retry-organization-access")?.addEventListener("click", () => {
    if (auth.currentUser) discoverAuthorizedOrganizations(auth.currentUser.uid);
  });
}

function eventRowForId(eventId: string | null | undefined): CommunityEventRow | undefined {
  if (!eventId) {
    return undefined;
  }

  return state.events.find((event) => event.eventId === eventId || event.id === eventId);
}

function eventEditorInputFromRow(liveEvent: CommunityEventRow): EventEditorInput {
  return buildEventEditorInputFromFixture({
    campusId: liveEvent.campusId,
    campusNameSnapshot: liveEvent.campusNameSnapshot,
    description: liveEvent.description,
    descriptionAr: liveEvent.descriptionAr,
    endsAt: liveEvent.endsAt?.toISOString() ?? new Date().toISOString(),
    eventId: liveEvent.eventId,
    eventType: liveEvent.eventType,
    id: liveEvent.id,
    image: {
      altText: liveEvent.imageAltText,
      altTextAr: liveEvent.imageAltTextAr,
      storagePath: liveEvent.imageStoragePath,
    },
    addressLine1: liveEvent.addressLine1,
    locationType:
      liveEvent.locationType === "virtual" || liveEvent.locationType === "hybrid"
        ? liveEvent.locationType
        : "physical",
    locationName: liveEvent.locationName,
    onlineUrl: liveEvent.onlineUrl,
    notificationRequestedOnPublish: liveEvent.notificationRequestedOnPublish,
    emailRequestedOnPublish: liveEvent.emailRequestedOnPublish === true,
    registrationMode: liveEvent.registrationMode === "none" ? "none" : "free",
    registrationCancellationAllowed: liveEvent.registrationCancellationAllowed,
    guestRegistrationEnabled: liveEvent.guestRegistrationEnabled,
    dependentRegistrationEnabled: liveEvent.dependentRegistrationEnabled,
    registrationCapacity: liveEvent.registrationCapacity,
    registrationClosesAt: liveEvent.registrationClosesAt?.toISOString() ?? null,
    registrationConfirmedCount: liveEvent.registrationConfirmedCount,
    registrationEnabled: liveEvent.registrationEnabled,
    registrationOpensAt: liveEvent.registrationOpensAt?.toISOString() ?? null,
    waitlistCount: liveEvent.waitlistCount,
    waitlistEnabled: liveEvent.waitlistEnabled,
    attendanceCheckedInCount: liveEvent.attendanceCheckedInCount,
    scheduledPublishAt: liveEvent.scheduledPublishAt?.toISOString() ?? null,
    startsAt: liveEvent.startsAt?.toISOString() ?? new Date().toISOString(),
    status:
      liveEvent.status === "archived" ||
      liveEvent.status === "cancelled" ||
      liveEvent.status === "completed" ||
      liveEvent.status === "draft" ||
      liveEvent.status === "registration_closed" ||
      liveEvent.status === "scheduled"
        ? liveEvent.status
        : "published",
    summary: liveEvent.summary,
    summaryAr: liveEvent.summaryAr,
    timezone: liveEvent.timezone,
    title: liveEvent.title,
    titleAr: liveEvent.titleAr,
  });
}

function clearEventPendingMediaPreviews(): void {
  for (const preview of state.eventPendingMediaPreviews) {
    URL.revokeObjectURL(preview.url);
  }
  state.eventPendingMediaPreviews = [];
}

function refreshEventPendingMediaPreviews(): void {
  clearEventPendingMediaPreviews();
  const imageFiles =
    state.eventImageFiles.length > 0
      ? state.eventImageFiles
      : state.eventImageFile
        ? [state.eventImageFile]
        : [];

  state.eventPendingMediaPreviews = [
    ...imageFiles.map((file) => ({
      kind: "image" as const,
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
    })),
    ...(state.eventVideoFile
      ? [
          {
            kind: "video" as const,
            name: state.eventVideoFile.name,
            size: state.eventVideoFile.size,
            type: state.eventVideoFile.type,
            url: URL.createObjectURL(state.eventVideoFile),
          },
        ]
      : []),
  ];
}

function resetEventEditorTransientState(): void {
  clearEventPendingMediaPreviews();
  state.eventEditorError = "";
  state.eventEditorMessage = "";
  state.eventEmailDeliveryType = defaultEventEmailDeliveryTypeForEventType(
    state.eventEditor.eventType,
  );
  state.eventEmailTestAddress = "";
  state.eventEmailDraft = { subject: null, message: null };
  state.eventEmailTestStatus = null;
  const savedEvent = eventRowForId(state.eventEditor.eventId);
  if (savedEvent) {
    state.eventEmailDraft = {
      subject: savedEvent.publishEmailSubject ?? null,
      message: savedEvent.publishEmailMessage ?? null,
    };
  }
  state.eventImageFile = undefined;
  state.eventImageFiles = [];
  state.eventVideoFile = undefined;
  state.eventYoutubeUrl = "";
  state.eventMediaMessage = "";
  state.eventCheckInCredential = "";
  state.eventCheckInMessage = "";
  state.eventManualRegistrationSearch = "";
  state.eventWizardOpen = false;
  state.eventWizardStepIndex = 0;
  eventRegistrationPage = 1;
  eventWaitlistPage = 1;
  eventAttendancePage = 1;
}

function resetEventEditor(organizationId = state.organizationId, eventId?: string): void {
  eventEditorRevision += 1;
  const liveEvent =
    eventRowForId(eventId) ??
    eventRowForId(state.eventEditor.eventId) ??
    state.events.find((event) => event.eventId && event.id === event.eventId);

  state.eventEditor =
    liveEvent && liveEvent.startsAt && liveEvent.endsAt
      ? eventEditorInputFromRow(liveEvent)
      : eventEditorInputForOrganization(organizationId);
  resetEventEditorTransientState();
  connectEventMedia();
  connectEventRegistrations();
  connectEventAttendance();
}

function clearEventEditorForCreate(organizationId = state.organizationId): void {
  eventEditorRevision += 1;
  state.eventEditor = buildCreateEventEditorInput(organizationId);
  resetEventEditorTransientState();
  connectEventMedia();
  connectEventRegistrations();
  connectEventAttendance();
}

function buildCreateEventEditorInput(organizationId: string): EventEditorInput {
  const input = buildDefaultEventEditorInput(organizationId);
  input.eventId = buildOrganizationEventId({
    organizationId,
    startsAt: new Date(),
    suffix: `admin-${Date.now().toString(36)}`,
    title: "new event",
  });
  return {
    ...input,
    addressLine1: "",
    campusId: "",
    campusNameSnapshot: "",
    dependentRegistrationEnabled: false,
    description: "",
    descriptionAr: "",
    endsAt: "",
    eventType: "standard",
    guestRegistrationEnabled: false,
    imageAltText: "",
    imageAltTextAr: "",
    imageStoragePath: "",
    locationName: "",
    locationType: "physical",
    notificationRequestedOnPublish: false,
    onlineUrl: "",
    registrationCancellationAllowed: false,
    registrationCapacity: "",
    registrationClosesAt: "",
    registrationEnabled: false,
    registrationMode: "none",
    registrationOpensAt: "",
    registrationQuestionLabel: "",
    registrationQuestionRequired: false,
    reminderOneDayBefore: false,
    reminderTwoHoursBefore: false,
    scheduledPublishDate: "",
    startsAt: "",
    status: "published",
    summary: "",
    summaryAr: "",
    timezone: state.organizationProfile?.timeZone ?? "America/New_York",
    title: "",
    titleAr: "",
    waitlistEnabled: false,
    waiverBody: "",
    waiverRequired: false,
    waiverTitle: "",
  };
}

function applyEventTypeDefaults(eventType: EventEditorInput["eventType"]): void {
  const forceTemplateCopy = eventRowForId(state.eventEditor.eventId) === undefined;
  state.eventEditor.eventType = eventType;
  state.eventEmailDeliveryType = defaultEventEmailDeliveryTypeForEventType(eventType);
  if (eventType === "standard") {
    return;
  }

  const now = new Date();
  const fridayRange = eventType === "friday_program" ? fridayProgramDefaultRange(now) : undefined;
  const startsAt =
    state.eventEditor.startsAt.trim().length > 0
      ? new Date(state.eventEditor.startsAt)
      : eventType === "friday_program"
        ? fridayRange!.startsAt
        : roundedFutureDate(now, 2);
  const endsAt =
    state.eventEditor.endsAt.trim().length > 0
      ? new Date(state.eventEditor.endsAt)
      : eventType === "friday_program"
        ? fridayRange!.endsAt
        : roundedFutureDate(startsAt, 3);

  if (eventType === "friday_program") {
    setEventTemplateText("title", "Friday Program", forceTemplateCopy);
    setEventTemplateText("titleAr", "برنامج الجمعة", forceTemplateCopy);
    setEventTemplateText(
      "summary",
      "Jumu'ah through evening program for the connected community.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "summaryAr",
      "برنامج الجمعة من صلاة الجمعة حتى المساء للمجتمع المتصل.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "description",
      "Friday program starting with Jumu'ah and continuing through the evening. Add the detailed prayer, food, speaker, youth, and Isha schedule before publishing.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "descriptionAr",
      "برنامج الجمعة يبدأ بصلاة الجمعة ويستمر حتى المساء. أضف تفاصيل الصلوات والطعام والمتحدث والشباب والعشاء قبل النشر.",
      forceTemplateCopy,
    );
    setEventTemplateText("locationName", "Main hall", forceTemplateCopy);
    if (state.eventEditor.status === "scheduled") {
      state.eventEditor.scheduledPublishDate = dateInputValue(daysBefore(startsAt, 2));
    } else {
      state.eventEditor.status = "published";
      state.eventEditor.scheduledPublishDate = "";
    }
  } else {
    setEventTemplateText("title", "Janazah notice", forceTemplateCopy);
    setEventTemplateText("titleAr", "إشعار جنازة", forceTemplateCopy);
    setEventTemplateText(
      "summary",
      "Urgent funeral and burial notice for the connected community.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "summaryAr",
      "إشعار عاجل بالجنازة والدفن للمجتمع المتصل.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "description",
      "A community member has passed away. Add the Janazah prayer time, funeral home details, burial location, and family condolences information before sending.",
      forceTemplateCopy,
    );
    setEventTemplateText(
      "descriptionAr",
      "توفي أحد أفراد المجتمع. أضف وقت صلاة الجنازة وتفاصيل بيت الجنازة وموقع الدفن ومعلومات العزاء قبل الإرسال.",
      forceTemplateCopy,
    );
    setEventTemplateText("locationName", "Masjid / burial location", forceTemplateCopy);
    state.eventEditor.status = "published";
    state.eventEditor.scheduledPublishDate = "";
  }

  if (Number.isFinite(startsAt.getTime()) && state.eventEditor.startsAt.trim().length === 0) {
    state.eventEditor.startsAt = formatDateTimeLocalValue(startsAt);
  }
  if (Number.isFinite(endsAt.getTime()) && state.eventEditor.endsAt.trim().length === 0) {
    state.eventEditor.endsAt = formatDateTimeLocalValue(endsAt);
  }

  state.eventEditor.notificationRequestedOnPublish = false;
  state.eventEditor.emailRequestedOnPublish = false;
  state.eventEditor.registrationCancellationAllowed = false;
  state.eventEditor.registrationCapacity = "";
  state.eventEditor.registrationClosesAt = "";
  state.eventEditor.registrationEnabled = false;
  state.eventEditor.registrationMode = "none";
  state.eventEditor.registrationOpensAt = "";
  state.eventEditor.registrationQuestionLabel = "";
  state.eventEditor.registrationQuestionRequired = false;
  state.eventEditor.waitlistEnabled = false;
  state.eventEditor.guestRegistrationEnabled = false;
  state.eventEditor.dependentRegistrationEnabled = false;
  state.eventEditor.reminderOneDayBefore = false;
  state.eventEditor.reminderTwoHoursBefore = false;

  if (
    state.eventEditor.eventId.includes("new-event") &&
    state.eventEditor.title.trim().length > 0 &&
    Number.isFinite(startsAt.getTime())
  ) {
    state.eventEditor.eventId = buildOrganizationEventId({
      organizationId: state.organizationId,
      startsAt,
      suffix: `admin-${Date.now().toString(36)}`,
      title: state.eventEditor.title,
    });
  }

  syncEventEmailTemplateFromEditor({ force: true });
}

function setEventTemplateText(
  key: keyof EventEditorInput,
  value: string,
  forceTemplateCopy = false,
): void {
  const current = state.eventEditor[key];
  if (typeof current === "string" && (forceTemplateCopy || current.trim().length === 0)) {
    (state.eventEditor as unknown as Record<string, string>)[key] = value;
  }
}

function roundedFutureDate(date: Date, hoursFromNow: number): Date {
  const result = new Date(date.getTime() + hoursFromNow * 60 * 60 * 1000);
  result.setMinutes(result.getMinutes() < 30 ? 30 : 0, 0, 0);
  if (result.getMinutes() === 0) {
    result.setHours(result.getHours() + 1);
  }
  return result;
}

function daysBefore(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

function dateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function resetEventCreationWizard(): void {
  clearEventEditorForCreate();
  state.eventWizardOpen = true;
  state.eventWizardStepIndex = 0;
  syncEventEmailTemplateFromEditor({ force: true });
  activeEventOperationsWindowEventId = null;
  activeTaskWindowId = "event-editor-form";
  state.busy = false;
  state.error = "";
  render();
}

function openEventCreationWizard(): void {
  resetEventCreationWizard();
}

function openEventEditorWindow(eventId?: string): void {
  if (eventId) {
    resetEventEditor(state.organizationId, eventId);
    syncEventEmailTemplateFromEditor();
  } else {
    openEventCreationWizard();
    return;
  }
  activeEventOperationsWindowEventId = null;
  activeTaskWindowId = "event-editor-form";
  render();
}

function openEventCheckInWindow(eventId: string): void {
  resetEventEditor(state.organizationId, eventId);
  activeEventOperationsWindowEventId = null;
  activeTaskWindowId = "event-qr-check-in-form";
  render();
}

function openEventOperationsWindow(eventId: string): void {
  if (!eventRowForId(eventId)) {
    return;
  }

  resetEventEditor(state.organizationId, eventId);
  syncEventEmailTemplateFromEditor();
  activeTaskWindowId = null;
  activeEventOperationsWindowEventId = eventId;
  render();
}

async function requestEventLifecycleAction(
  eventId: string,
  action: EventLifecycleAction,
): Promise<void> {
  const event = eventRowForId(eventId);
  const actionCopy = eventLifecycleActionCopy(state.locale);
  if (!event || !selectedContextCanManageEvents()) {
    return;
  }

  const confirmed = window.confirm(
    action === "delist" ? actionCopy.makeInvisibleConfirm : actionCopy.deleteConfirm,
  );
  if (!confirmed) {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const eventRef = doc(db, "organizations", state.organizationId, "events", event.id);
    const eventSnapshot = await getDoc(eventRef);
    if (!eventSnapshot.exists()) {
      throw new Error("Event could not be found.");
    }

    const eventData = eventSnapshot.data();
    const currentStatus = typeof eventData.status === "string" ? eventData.status : event.status;

    if (action === "delist" && currentStatus === "archived") {
      throw new Error("Archived events cannot be made visible or invisible.");
    }

    if (action === "delete" && currentStatus === "archived") {
      state.status = actionCopy.deleteDone;
      activeEventOperationsWindowEventId = null;
      return;
    }

    const nextStatus = action === "delist" ? "draft" : "archived";
    await updateDoc(eventRef, {
      archivedAt: action === "delete" ? serverTimestamp() : null,
      publishedAt: null,
      scheduledPublishAt: null,
      status: nextStatus,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    const localEvent = eventRowForId(eventId);
    if (localEvent) {
      localEvent.archivedAt = action === "delete" ? new Date() : undefined;
      localEvent.publishedAt = undefined;
      localEvent.scheduledPublishAt = undefined;
      localEvent.status = nextStatus;
      localEvent.updatedBy = person.uid;
    }

    if (state.eventEditor.eventId === event.eventId) {
      state.eventEditor.status = nextStatus;
      state.eventEditor.scheduledPublishDate = "";
      state.eventEditorMessage = "";
      state.eventEditorError = "";
      if (state.eventWizardOpen) {
        state.eventWizardOpen = false;
        activeTaskWindowId = null;
      }
    }

    state.status =
      action === "delist"
        ? `${actionCopy.delistDone} ${event.title}`
        : `${actionCopy.deleteDone} ${event.title}`;
    if (action === "delete") {
      activeEventOperationsWindowEventId = null;
    }
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function clearAnnouncementComposer(): void {
  announcementComposerRevision += 1;
  state.announcementComposer = buildEmptyAnnouncementComposerInput();
  state.announcementComposerEditingId = null;
}

function openNewAnnouncementComposer(): void {
  announcementComposerRevision += 1;
  state.announcementComposer = buildEmptyAnnouncementComposerInput();
  state.announcementComposerEditingId = null;
  state.error = "";
  activeTaskWindowId = "announcement-composer-form";
  render();
}

function openAnnouncementComposerForEdit(announcementId: string): void {
  const announcement = state.announcements.find((item) => item.id === announcementId);
  if (!announcement) {
    return;
  }

  announcementComposerRevision += 1;
  state.announcementComposer = {
    announcementId: announcement.id,
    body: announcement.body,
    bodyAr: announcement.bodyAr,
    expiresAt: formatDateTimeLocalInputValue(announcement.expiresAt),
    priority: announcement.priority === "urgent" ? "urgent" : "normal",
    pushNotificationEnabled: announcement.pushNotificationEnabled,
    title: announcement.title,
    titleAr: announcement.titleAr,
  };
  state.announcementComposerEditingId = announcement.id;
  state.error = "";
  activeTaskWindowId = "announcement-composer-form";
  render();
}

function formatDateTimeLocalInputValue(date: Date | undefined): string {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (value: number) => String(value).padStart(2, "0");

  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
}

function openAnnouncementDetail(announcementId: string): void {
  const announcement = state.announcements.find((item) => item.id === announcementId);
  if (!announcement) {
    return;
  }

  const title =
    state.locale === "ar" ? announcement.titleAr || announcement.title : announcement.title;
  const body = state.locale === "ar" ? announcement.bodyAr : announcement.body;
  state.engagementDetailMessage = `${title}: ${body}`;
  render();
}

function isAnnouncementExpired(announcement: AnnouncementRow, now = new Date()): boolean {
  return Boolean(announcement.expiresAt && announcement.expiresAt.getTime() <= now.getTime());
}

function announcementStatusLabel(announcement: AnnouncementRow): string {
  const copy = shellCopy[state.locale];

  if (announcement.status === "archived") {
    return copy.announcementArchived;
  }

  if (isAnnouncementExpired(announcement)) {
    return copy.announcementExpired;
  }

  return copy.announcementPublished;
}

function announcementStatusPillClass(announcement: AnnouncementRow): string {
  if (announcement.status === "archived" || isAnnouncementExpired(announcement)) {
    return "status-rejected";
  }

  return "status-accepted";
}

function announcementExpirationLabel(announcement: AnnouncementRow): string {
  if (!announcement.expiresAt) {
    return shellCopy[state.locale].announcementNeverExpires;
  }

  return announcement.expiresAt.toLocaleString(state.locale);
}

function clearImamContentEditor(): void {
  imamContentEditorRevision += 1;
  state.imamContentEditor = buildEmptyImamContentEditorInput();
  state.imamContentGalleryImageFiles = [];
  state.imamContentImageFile = undefined;
}

function buildImamContentEditorInputFromRow(content: ImamContentRow): ImamContentEditorInput {
  return buildImamContentEditorInputFromContent({
    body: content.body,
    bodyAr: content.bodyAr,
    contentId: content.contentId,
    type: content.type,
    featured: content.featured,
    galleryImage1: content.galleryImage1,
    galleryImage2: content.galleryImage2,
    galleryImage3: content.galleryImage3,
    image: {
      altText: content.imageAltText,
      altTextAr: content.imageAltTextAr,
      storagePath: content.imageStoragePath,
    },
    sortOrder: content.sortOrder,
    status: content.status,
    subtitle: content.subtitle,
    subtitleAr: content.subtitleAr,
    title: content.title,
    titleAr: content.titleAr,
    youtube: content.youtube,
  });
}

function nextImamContentId(
  contentType: "imam_profile" | "ramadan_message" = "imam_profile",
): string {
  const existingIds = new Set(state.imamContents.map((content) => content.contentId));
  const hijriYear = state.ramadanSetupEditor.hijriYear.trim() || "1448";
  const baseId =
    contentType === "ramadan_message" ? `ramadan-${hijriYear}-message` : "imam-profile";
  if (!existingIds.has(baseId)) {
    return baseId;
  }

  let index = state.imamContents.length + 1;
  let candidate = `${baseId}-${index}`;
  while (existingIds.has(candidate)) {
    index += 1;
    candidate = `${baseId}-${index}`;
  }

  return candidate;
}

function openNewImamContentEditor(
  contentType: "imam_profile" | "ramadan_message" = "imam_profile",
): void {
  imamContentEditorRevision += 1;
  const contentId = nextImamContentId(contentType);
  const nextSortOrder =
    state.imamContents.reduce((max, content) => Math.max(max, content.sortOrder), 0) + 10;
  state.imamContentEditor = {
    ...buildEmptyImamContentEditorInput(),
    body:
      contentType === "ramadan_message"
        ? "Share your organization's Ramadan welcome, schedule highlights, and important member guidance."
        : "",
    contentId,
    contentType,
    sortOrder: String(nextSortOrder),
    subtitle: contentType === "ramadan_message" ? "Ramadan welcome and guidance" : "",
    title: contentType === "ramadan_message" ? `Ramadan ${state.ramadanSetupEditor.hijriYear}` : "",
  };
  state.imamContentGalleryImageFiles = [];
  state.imamContentImageFile = undefined;
  state.error = "";
  activeTaskWindowId = "imam-content-editor-form";
  render();
}

function openImamContentEditor(contentId: string): void {
  const content = state.imamContents.find((item) => item.contentId === contentId);
  if (!content) {
    return;
  }

  imamContentEditorRevision += 1;
  state.imamContentEditor = buildImamContentEditorInputFromRow(content);
  state.imamContentGalleryImageFiles = [];
  state.imamContentImageFile = undefined;
  state.error = "";
  activeTaskWindowId = "imam-content-editor-form";
  render();
}

function clearRamadanModeEditor(): void {
  ramadanModeEditorRevision += 1;
  state.ramadanModeEditor = buildEmptyRamadanModeEditorInput();
}

function resetRamadanSetupEditor(organizationId = state.organizationId): void {
  ramadanSetupEditorRevision += 1;
  state.ramadanSetupEditor = ramadanSetupEditorInputForOrganization(organizationId);
  state.ramadanSetupMessage = "";
}

function resetOperationalModeEditor(): void {
  operationalModeEditorRevision += 1;
  operationalModeEditorDirty = false;
  operationalModeOriginal = null;
  operationalModeEditorScope = "";
  operationalModeNewId = "";
  operationalModeDiscardPending = false;
  operationalModePendingAction = null;
  operationalModeBusy = false;
  state.operationalModeEditor = buildDefaultOperationalModeEditorInput();
  state.operationalModeError = "";
  state.operationalModeMessage = "";
  if (activeTaskWindowId === "operational-mode-editor-form") activeTaskWindowId = null;
}

function currentModeScope(): string {
  return `${auth.currentUser?.uid ?? ""}:${state.organizationId}`;
}

function formInteractionScope(): string {
  return JSON.stringify([
    authenticationState.status,
    currentModeScope(),
    canonicalRoleIdsForSelectedContext(),
    state.locale,
    window.location.hash,
    activeTaskWindowId,
    activeEventOperationsWindowEventId,
    accountProfileEditorOpen,
  ]);
}

let renderedFormScope = "";
const formSaves = createFormSaveLifecycle({
  scope: formInteractionScope,
  locale: () => state.locale,
  error: (form) =>
    adminFeedback(
      form.id.startsWith("member-support-")
        ? state.memberSupportError
        : form.id.startsWith("shdeed-support-")
          ? state.shdeedSupportError
          : state.error,
      state.locale,
    ),
  saved: () => {
    activeTaskWindowId = null;
    state.error = "";
    render();
  },
});

const realtimeRenderer = createRealtimeRenderScheduler({
  render: () => render(),
  blocked: () => {
    const root = document.querySelector<HTMLElement>("#app");
    return document.hidden || formSaves.holdsRender() || !!(root && isFormInteractionActive(root));
  },
  requestFrame: (callback) => requestAnimationFrame(callback),
  cancelFrame: (id) => cancelAnimationFrame(id),
});

function renderRealtime(): void {
  // Authorization/context changes must never wait behind a draft or hidden tab.
  if (renderedFormScope !== formInteractionScope()) render();
  else realtimeRenderer.request();
}

function submitTaskForm(event: SubmitEvent, operation: () => Promise<boolean>): void {
  event.preventDefault();
  const form = event.currentTarget;
  if (form instanceof HTMLFormElement) void formSaves.run(form, operation);
}

function openOperationalMode(mode: OperationalModeRow | null): void {
  if (operationalModeBusy || !selectedContextCanManageOrganizationSettings()) return;
  if (mode && !canEditMode(mode)) return;
  resetOperationalModeEditor();
  operationalModeEditorScope = currentModeScope();
  operationalModeOriginal = mode ? structuredClone(mode) : null;
  operationalModeNewId = mode?.modeId ?? `mode-${crypto.randomUUID()}`;
  state.operationalModeEditor = mode
    ? operationalModeEditorInputFromRow(mode)
    : buildDefaultOperationalModeEditorInput();
  activeTaskWindowId = "operational-mode-editor-form";
  renderOperationalModeEditorUpdate();
  document
    .querySelector<HTMLInputElement>(
      '#task-window-body [data-operational-mode-editor-field="displayName"]',
    )
    ?.focus();
}

function editOperationalMode(modeId: string): void {
  const mode = state.operationalModes.find((candidate) => candidate.modeId === modeId);
  if (mode) openOperationalMode(mode);
}

function closeOperationalModeEditor(discard = false): void {
  if (operationalModeBusy) return;
  if (operationalModeEditorDirty && !discard) {
    operationalModeDiscardPending = true;
    renderOperationalModeEditorUpdate();
    return;
  }
  resetOperationalModeEditor();
  renderOperationalModeEditorUpdate();
}

function clearIftarSlotEditor(): void {
  iftarSlotEditorRevision += 1;
  state.iftarSlotEditor = buildEmptyIftarSlotEditorInput();
}

function clearSpaceEditor(): void {
  spaceEditorRevision += 1;
  state.spaceEditor = buildEmptySpaceEditorInput();
}

function clearSpaceAvailabilityBlockEditor(): void {
  spaceAvailabilityBlockEditorRevision += 1;
  state.spaceAvailabilityBlockEditor = buildEmptySpaceAvailabilityBlockEditorInput();
}

function clearSpaceSlotEditor(): void {
  spaceSlotEditorRevision += 1;
  state.spaceSlotEditor = buildEmptySpaceSlotEditorInput();
}

function resetVolunteerOpportunityEditor(organizationId = state.organizationId): void {
  volunteerOpportunityEditorRevision += 1;
  const liveOpportunity = state.volunteerOpportunities.find(
    (opportunity) => opportunity.status === "published" || opportunity.status === "draft",
  );
  state.volunteerOpportunityEditor = liveOpportunity
    ? volunteerOpportunityEditorInputFromRow(liveOpportunity)
    : volunteerOpportunityEditorInputForOrganization(organizationId);
}

function resetVolunteerShiftEditor(organizationId = state.organizationId): void {
  volunteerShiftEditorRevision += 1;
  const liveShift = state.volunteerShifts.find((shift) => shift.status === "open");
  state.volunteerShiftEditor = liveShift
    ? volunteerShiftEditorInputFromRow(liveShift)
    : volunteerShiftEditorInputForOrganization(organizationId);
}

function resetOrganizationProfileEditor(organizationId = state.organizationId): void {
  organizationProfileEditorRevision += 1;
  const liveProfile = state.organizationProfile;

  state.organizationProfileEditor =
    liveProfile && liveProfile.id === organizationId
      ? organizationProfileEditorInputFromRow(liveProfile)
      : organizationProfileEditorInputForOrganization(organizationId);
  setOrganizationProfileLogoFile(undefined);
  state.organizationProfileMessage = "";
}

function resetOrganizationPrayerSettingsEditor(organizationId = state.organizationId): void {
  organizationPrayerSettingsEditorRevision += 1;
  state.organizationPrayerSettingsEditor =
    state.organizationPrayerSettings && state.organizationPrayerSettings.provider === "aladhan"
      ? organizationPrayerSettingsEditorInputFromRow(state.organizationPrayerSettings)
      : organizationPrayerSettingsEditorInputFromProfile(organizationId);
  state.organizationPrayerSettingsMessage = "";
}

function resetOrganizationIslamicComponentsEditor(organizationId = state.organizationId): void {
  organizationIslamicComponentsEditorRevision += 1;
  state.organizationIslamicComponentsEditor = state.organizationIslamicComponents
    ? organizationIslamicComponentsEditorInputFromRow(state.organizationIslamicComponents)
    : organizationIslamicComponentsEditorInputForOrganization(organizationId);
  state.organizationIslamicComponentsMessage = "";
}

function clearOrganizationPrayerSettingsEditor(organizationId = state.organizationId): void {
  organizationPrayerSettingsEditorRevision += 1;
  state.organizationPrayerSettingsEditor =
    organizationPrayerSettingsEditorInputFromProfile(organizationId);
  state.organizationPrayerSettingsMessage = "";
}

function clearOrganizationProfileEditor(): void {
  organizationProfileEditorRevision += 1;
  state.organizationProfileEditor = buildEmptyOrganizationProfileEditorInput();
  setOrganizationProfileLogoFile(undefined);
  state.organizationProfileMessage = "";
}

function setOrganizationProfileLogoFile(file: File | undefined): void {
  if (state.organizationProfileLogoPreviewUrl) {
    URL.revokeObjectURL(state.organizationProfileLogoPreviewUrl);
  }

  state.organizationProfileLogoFile = file;
  state.organizationProfileLogoPreviewUrl = file ? URL.createObjectURL(file) : null;
}

function organizationProfileEditorInputFromRow(
  profile: OrganizationProfileRow,
): OrganizationProfileEditorInput {
  return buildOrganizationProfileEditorInputFromFixture({
    alternateName: profile.alternateName,
    primaryLeader: profile.primaryLeader,
    address: {
      city: profile.city,
      country: profile.country,
      line1: profile.addressLine1,
      line2: profile.addressLine2,
      postalCode: profile.postalCode,
      region: profile.region,
    },
    brandColor: profile.brandColor,
    branding: {
      logoStoragePath: profile.logoStoragePath,
    },
    category: profile.category === "Community center" ? "Community center" : "Islamic center",
    city: profile.city,
    connectionPolicy: profile.connectionPolicy,
    displayName: profile.displayName,
    publicContact: {
      email: profile.publicEmail,
      phone: profile.publicPhone,
      websiteUrl: profile.websiteUrl,
    },
    publicVisibility: {
      directory: profile.publicVisibilityDirectory,
      discovery: profile.publicVisibilityDiscovery,
      profile: profile.publicVisibilityProfile,
    },
    timeZone: profile.timeZone,
    welcomeHeadline: profile.welcomeHeadline,
    welcomeHeadlineAr: profile.welcomeHeadlineAr,
    welcomeSummary: profile.welcomeSummary,
    welcomeSummaryAr: profile.welcomeSummaryAr,
  });
}

function organizationProfileRowFromDraft(
  organizationId: string,
  draft: OrganizationProfileEditorDraft,
  logoStoragePath: string | null,
): OrganizationProfileRow {
  return {
    alternateName: draft.alternateName,
    primaryLeader: organizationPublicLeaderFromEditor(draft),
    addressLine1: draft.addressLine1,
    addressLine2: draft.addressLine2,
    brandColor: draft.brandColor,
    category: draft.category,
    city: draft.city,
    connectionPolicy: draft.connectionPolicy,
    country: draft.country,
    displayName: draft.displayName,
    id: organizationId,
    logoStoragePath,
    openConnection: draft.openConnection,
    postalCode: draft.postalCode,
    publicEmail: draft.publicEmail,
    publicPhone: draft.publicPhone,
    publicVisibilityDirectory: draft.publicVisibilityDirectory,
    publicVisibilityDiscovery: draft.publicVisibilityDiscovery,
    publicVisibilityProfile: draft.publicVisibilityProfile,
    region: draft.region,
    timeZone: draft.timeZone,
    websiteUrl: draft.websiteUrl,
    welcomeHeadline: draft.welcomeHeadline,
    welcomeHeadlineAr: draft.welcomeHeadlineAr,
    welcomeSummary: draft.welcomeSummary,
    welcomeSummaryAr: draft.welcomeSummaryAr,
  };
}

function defaultOrganizationPrayerAddress(organizationId: string): string {
  if (!useEmulator) return "";
  if (organizationId === "org-hvicc") {
    return "3680 Lexington Ave, Mohegan Lake, NY 10547, US";
  }

  const organization = seedOrganizations.find((candidate) => candidate.id === organizationId);
  return organization ? `${organization.city}, US` : "New York, NY, US";
}

function organizationPrayerAddressFromProfile(
  organizationId: string,
  profile: OrganizationProfileRow | undefined,
): string {
  if (!profile || profile.id !== organizationId) {
    return defaultOrganizationPrayerAddress(organizationId);
  }

  const address = [
    profile.addressLine1,
    profile.addressLine2,
    profile.city,
    profile.region,
    profile.postalCode,
    profile.country,
  ]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(", ");

  return address || defaultOrganizationPrayerAddress(organizationId);
}

function organizationPrayerSettingsEditorInputFromProfile(
  organizationId: string,
): OrganizationPrayerSettingsEditorInput {
  return {
    ...organizationPrayerSettingsEditorInputForOrganization(organizationId),
    address: organizationPrayerAddressFromProfile(organizationId, state.organizationProfile),
    timeZone:
      state.organizationProfile?.id === organizationId
        ? state.organizationProfile.timeZone
        : "America/New_York",
  };
}

function organizationPrayerSettingsEditorInputFromRow(
  row: OrganizationPrayerSettingsRow,
): OrganizationPrayerSettingsEditorInput {
  const iqamaTime = (prayerId: OrganizationPrayerId): string =>
    row.timings.find((timing) => timing.prayerId === prayerId)?.iqamaTime ?? "";

  return {
    address: row.address,
    asrIqama: iqamaTime("asr"),
    calculationMethod: String(row.calculationMethod),
    dhuhrIqama: iqamaTime("dhuhr"),
    enabled: row.enabled,
    fajrIqama: iqamaTime("fajr"),
    ishaIqama: iqamaTime("isha"),
    jumuahIqama: iqamaTime("jumuah"),
    latitudeAdjustmentMethod:
      row.latitudeAdjustmentMethod === 1 || row.latitudeAdjustmentMethod === 2
        ? String(row.latitudeAdjustmentMethod)
        : "3",
    maghribIqama: iqamaTime("maghrib"),
    school: row.school === 1 ? "1" : "0",
    timeZone: row.timeZone,
  };
}

function organizationPrayerSettingsRowFromSnapshot(snapshot: {
  data(): Record<string, unknown> | undefined;
}): OrganizationPrayerSettingsRow | undefined {
  const data = snapshot.data();

  if (!data) {
    return undefined;
  }

  return {
    address:
      typeof data.address === "string"
        ? data.address
        : defaultOrganizationPrayerAddress(state.organizationId),
    calculationMethod: typeof data.calculationMethod === "number" ? data.calculationMethod : 2,
    enabled: data.enabled !== false,
    latitudeAdjustmentMethod:
      typeof data.latitudeAdjustmentMethod === "number" ? data.latitudeAdjustmentMethod : 3,
    provider: typeof data.provider === "string" ? data.provider : "aladhan",
    school: typeof data.school === "number" ? data.school : 0,
    timeZone: typeof data.timeZone === "string" ? data.timeZone : "America/New_York",
    timings: organizationPrayerTimingRowsFromValue(data.timings),
    timingsDate: typeof data.timingsDate === "string" ? data.timingsDate : null,
    updatedAt: optionalDateFromFirestore(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
  };
}

function organizationPrayerTimingRowsFromValue(value: unknown): OrganizationPrayerTimingRow[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item): OrganizationPrayerTimingRow[] => {
    if (!isRecord(item) || !isOrganizationPrayerId(item.prayerId)) {
      return [];
    }

    return [
      {
        displayTime: typeof item.displayTime === "string" ? item.displayTime : null,
        enabled: item.enabled === true,
        iqamaTime: typeof item.iqamaTime === "string" ? item.iqamaTime : null,
        prayerId: item.prayerId,
        source: typeof item.source === "string" ? item.source : "aladhan",
      },
    ];
  });
}

function isOrganizationPrayerId(value: unknown): value is OrganizationPrayerId {
  return (
    value === "fajr" ||
    value === "sunrise" ||
    value === "dhuhr" ||
    value === "asr" ||
    value === "maghrib" ||
    value === "isha" ||
    value === "jumuah"
  );
}

async function signInSelectedPerson(): Promise<void> {
  const person = selectedPerson();
  if (auth.currentUser?.uid === person.uid) {
    return;
  }

  if (!useEmulator) {
    throw new Error("Your authenticated administrator session changed. Please sign in again.");
  }

  await authenticationService.signInWithEmailPassword({
    email: person.email,
    password: developmentPassword,
  });
}

function connectRealtimeStatus(): void {
  unsubscribeStatus?.();
  const ref = doc(db, "organizations", state.organizationId, "demoRealtime", "status");

  unsubscribeStatus = onSnapshot(
    ref,
    (snapshot) => {
      const data = snapshot.data();
      state.status = typeof data?.message === "string" ? data.message : "No demo status found.";
      state.version = typeof data?.version === "number" ? String(data.version) : "-";
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.status = "Permission denied or emulator unavailable.";
      state.version = "-";
      renderRealtime();
    },
  );
}

function connectConnectionRequests(): void {
  unsubscribeConnectionRequests?.();
  const requestsQuery = query(
    collection(db, "connectionRequests"),
    where("organizationId", "==", state.organizationId),
  );

  unsubscribeConnectionRequests = onSnapshot(
    requestsQuery,
    (snapshot) => {
      state.connectionRequests = snapshot.docs.map((request) => {
        const data = request.data();
        return {
          id: request.id,
          locale: typeof data.locale === "string" ? data.locale : "-",
          requestedRole: typeof data.requestedRole === "string" ? data.requestedRole : "-",
          status: typeof data.status === "string" ? data.status : "-",
          uid: typeof data.uid === "string" ? data.uid : "-",
        };
      });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.connectionRequests = [];
      renderRealtime();
    },
  );
}

function connectConnectionDecisionRequests(): void {
  unsubscribeConnectionDecisionRequests?.();
  const decisionsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "connectionDecisionRequests",
  );

  unsubscribeConnectionDecisionRequests = onSnapshot(
    decisionsQuery,
    (snapshot) => {
      state.connectionDecisions = snapshot.docs
        .map((decision) => {
          const data = decision.data();
          return {
            decision: typeof data.decision === "string" ? data.decision : "-",
            id: decision.id,
            requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "-",
            status: typeof data.status === "string" ? data.status : "-",
            targetRequestId: typeof data.targetRequestId === "string" ? data.targetRequestId : "-",
          };
        })
        .sort((left, right) => right.id.localeCompare(left.id));
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.connectionDecisions = [];
      renderRealtime();
    },
  );
}

function connectOrganizationProfile(): void {
  unsubscribeOrganizationProfile?.();
  const profileRef = doc(db, "organizationDirectory", state.organizationId);

  void getDoc(profileRef)
    .then((snapshot) => {
      state.organizationProfile = organizationProfileRowFromSnapshot(snapshot);
      renderRealtime();
    })
    .catch((error: unknown) => {
      state.error = error instanceof Error ? error.message : String(error);
      renderRealtime();
    });

  unsubscribeOrganizationProfile = onSnapshot(
    profileRef,
    (snapshot) => {
      state.organizationProfile = organizationProfileRowFromSnapshot(snapshot);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.organizationProfile = undefined;
      renderRealtime();
    },
  );
}

function organizationProfileRowFromSnapshot(snapshot: {
  data(): Record<string, unknown> | undefined;
  id: string;
}): OrganizationProfileRow | undefined {
  const data = snapshot.data();

  if (!data) {
    return undefined;
  }

  const address = recordValue(data.address);
  const publicContact = recordValue(data.publicContact);
  const publicVisibility = recordValue(data.publicVisibility);
  const branding = recordValue(data.branding);
  const city =
    typeof address.city === "string"
      ? address.city
      : typeof data.city === "string"
        ? data.city
        : "-";
  const country =
    typeof address.country === "string"
      ? address.country
      : typeof data.country === "string"
        ? data.country
        : "US";

  return {
    alternateName: typeof data.alternateName === "string" ? data.alternateName : "",
    primaryLeader: publicPrimaryLeader(data.primaryLeader),
    addressLine1: nullableProfileText(address.line1),
    addressLine2: nullableProfileText(address.line2),
    brandColor: typeof data.brandColor === "string" ? data.brandColor : "#116466",
    category: typeof data.category === "string" ? data.category : "-",
    city,
    connectionPolicy: typeof data.connectionPolicy === "string" ? data.connectionPolicy : "-",
    country,
    displayName: typeof data.displayName === "string" ? data.displayName : snapshot.id,
    id: snapshot.id,
    logoStoragePath: nullableProfileText(branding.logoStoragePath),
    openConnection: data.openConnection === true,
    postalCode: typeof address.postalCode === "string" ? address.postalCode : "",
    publicEmail: nullableProfileText(publicContact.email),
    publicPhone: nullableProfileText(publicContact.phone),
    publicVisibilityDirectory: publicVisibility.directory !== false,
    publicVisibilityDiscovery: publicVisibility.discovery !== false,
    publicVisibilityProfile: publicVisibility.profile !== false,
    region: typeof address.region === "string" ? address.region : "",
    timeZone: typeof data.timeZone === "string" ? data.timeZone : "America/New_York",
    websiteUrl: nullableProfileText(publicContact.websiteUrl),
    welcomeHeadline: typeof data.welcomeHeadline === "string" ? data.welcomeHeadline : "-",
    welcomeHeadlineAr:
      typeof data.welcomeHeadlineAr === "string"
        ? data.welcomeHeadlineAr
        : typeof data.welcomeHeadline === "string"
          ? data.welcomeHeadline
          : "-",
    welcomeSummary: typeof data.welcomeSummary === "string" ? data.welcomeSummary : "-",
    welcomeSummaryAr:
      typeof data.welcomeSummaryAr === "string"
        ? data.welcomeSummaryAr
        : typeof data.welcomeSummary === "string"
          ? data.welcomeSummary
          : "-",
  };
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function nullableProfileText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function ensureOrganizationProfileLogoDownloadUrl(logoStoragePath: string | null): void {
  const normalizedPath = logoStoragePath?.trim() || null;

  if (normalizedPath === state.organizationProfileLogoDownloadPath) {
    return;
  }

  state.organizationProfileLogoDownloadPath = normalizedPath;
  state.organizationProfileLogoDownloadUrl = null;
  state.organizationProfileLogoError = "";

  if (!normalizedPath) {
    return;
  }

  void getDownloadURL(storageRef(storage, normalizedPath))
    .then((url) => {
      if (state.organizationProfileLogoDownloadPath !== normalizedPath) {
        return;
      }

      state.organizationProfileLogoDownloadUrl = url;
      state.organizationProfileLogoError = "";
      setOrganizationProfileLogoFile(undefined);
      renderRealtime();
    })
    .catch((error: unknown) => {
      if (state.organizationProfileLogoDownloadPath !== normalizedPath) {
        return;
      }

      state.organizationProfileLogoDownloadUrl = null;
      state.organizationProfileLogoError =
        error instanceof Error ? error.message : "Logo image could not be loaded.";
      renderRealtime();
    });
}

function connectOrganizationProfileRequests(): void {
  unsubscribeOrganizationProfileRequests?.();
  const requestsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "organizationProfileUpdateRequests",
  );

  unsubscribeOrganizationProfileRequests = onSnapshot(
    requestsQuery,
    (snapshot) => {
      state.organizationProfileRequests = snapshot.docs
        .map((request) => {
          const data = request.data();
          return {
            id: request.id,
            processedAt: optionalDateFromFirestore(data.processedAt),
            requestedAt: optionalDateFromFirestore(data.requestedAt),
            requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "-",
            reviewReason: nullableProfileText(data.reviewReason),
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.requestedAt?.getTime() ?? 0;
          const rightTime = right.requestedAt?.getTime() ?? 0;
          return rightTime - leftTime || right.id.localeCompare(left.id);
        });
      const latestRequest = state.organizationProfileRequests[0];
      if (latestRequest?.status === "rejected") {
        state.organizationProfileMessage = `Latest profile update was rejected: ${
          latestRequest.reviewReason ?? "invalid request"
        }.`;
      } else if (latestRequest?.status === "completed" && state.organizationProfileMessage) {
        state.organizationProfileMessage = "Profile update completed.";
      }
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.organizationProfileRequests = [];
      renderRealtime();
    },
  );
}

function connectOrganizationPrayerSettings(): void {
  unsubscribeOrganizationPrayerSettings?.();
  const settingsRef = doc(db, "organizations", state.organizationId, "config", "prayer");

  unsubscribeOrganizationPrayerSettings = onSnapshot(
    settingsRef,
    (snapshot) => {
      state.organizationPrayerSettings = organizationPrayerSettingsRowFromSnapshot(snapshot);
      if (!activeTaskWindowId || activeTaskWindowId !== "organization-prayer-settings-form") {
        state.organizationPrayerSettingsEditor =
          state.organizationPrayerSettings &&
          state.organizationPrayerSettings.provider === "aladhan"
            ? organizationPrayerSettingsEditorInputFromRow(state.organizationPrayerSettings)
            : organizationPrayerSettingsEditorInputFromProfile(state.organizationId);
      }
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.organizationPrayerSettings = undefined;
      renderRealtime();
    },
  );
}

function connectPlatformIslamicComponents(): void {
  unsubscribePlatformIslamicComponents?.();
  const platformRef = collection(db, "platformIslamicComponents");

  unsubscribePlatformIslamicComponents = onSnapshot(
    platformRef,
    (snapshot) => {
      const rows = snapshot.docs.flatMap((documentSnapshot): PlatformIslamicComponentRow[] => {
        const row = platformIslamicComponentRowFromSnapshotData(
          documentSnapshot.id,
          documentSnapshot.data(),
        );
        return row ? [row] : [];
      });

      state.platformIslamicComponents =
        rows.length > 0
          ? rows.sort((left, right) => left.sortOrder - right.sortOrder)
          : approvedIslamicComponentDefinitions;
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.platformIslamicComponents = approvedIslamicComponentDefinitions;
      renderRealtime();
    },
  );
}

function connectOrganizationIslamicComponents(): void {
  unsubscribeOrganizationIslamicComponents?.();
  const settingsRef = doc(db, "organizations", state.organizationId, "config", "islamicComponents");

  unsubscribeOrganizationIslamicComponents = onSnapshot(
    settingsRef,
    (snapshot) => {
      state.organizationIslamicComponents = organizationIslamicComponentsRowFromSnapshot(snapshot);
      if (!activeTaskWindowId || activeTaskWindowId !== "organization-islamic-components-form") {
        state.organizationIslamicComponentsEditor = state.organizationIslamicComponents
          ? organizationIslamicComponentsEditorInputFromRow(state.organizationIslamicComponents)
          : organizationIslamicComponentsEditorInputForOrganization(state.organizationId);
      }
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.organizationIslamicComponents = undefined;
      renderRealtime();
    },
  );
}

function connectOrganizationConnections(): void {
  for (const unsubscribe of unsubscribeOrganizationConnectionRecords) {
    unsubscribe();
  }
  disconnectPersonProfiles();

  unsubscribeOrganizationConnectionRecords = [];
  state.organizationConnections = [];
  state.personProfiles = {};
  const connectionIndexRef = collection(
    db,
    "organizations",
    state.organizationId,
    "connectionIndex",
  );

  const unsubscribe = onSnapshot(
    connectionIndexRef,
    (snapshot) => {
      state.organizationConnections = snapshot.docs
        .map((connectionSnapshot) => organizationConnectionRowFromData(connectionSnapshot.data()))
        .filter((row): row is OrganizationConnectionRow => row !== undefined)
        .sort((left, right) => left.uid.localeCompare(right.uid));
      connectPersonProfilesForConnections();
      if (
        state.peopleManagerSelectedPersonId &&
        !state.organizationConnections.some(
          (connection) => connection.personId === state.peopleManagerSelectedPersonId,
        )
      ) {
        state.peopleManagerSelectedPersonId = "";
      }
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      renderRealtime();
    },
  );

  unsubscribeOrganizationConnectionRecords.push(unsubscribe);
}

function disconnectPersonProfiles(): void {
  for (const unsubscribe of unsubscribePersonProfiles) {
    unsubscribe();
  }
  unsubscribePersonProfiles = [];
}

function connectPersonProfilesForConnections(): void {
  disconnectPersonProfiles();
  const scope = currentModeScope();
  const personIds = [
    ...new Set(
      state.organizationConnections
        .map((connection) => connection.personId)
        .filter((personId) => personId.trim().length > 0),
    ),
  ];

  // Resolve only minimal tenant-owned identities; global profiles stay owner-only.
  state.personProfiles = Object.fromEntries(personIds.map((personId) => [personId, undefined]));
  const authenticatedUid = auth.currentUser?.uid;

  unsubscribePersonProfiles.push(
    watchOrganizationPeople({
      organizationId: state.organizationId,
      load: (body) => postAuthenticatedFunction("listOrganizationPeople", body),
      onChange: (people) => {
        if (retiringTenant || scope !== currentModeScope()) return;
        state.personProfiles = Object.fromEntries(personIds.map((id) => [id, people[id] ?? null]));
        renderRealtime();
      },
      onError: () => {
        if (retiringTenant || scope !== currentModeScope()) return;
        state.error =
          state.locale === "ar"
            ? "تعذر تحميل بيانات الأشخاص. حاول مرة أخرى."
            : "People details could not be loaded. Please try again.";
        renderRealtime();
      },
    }),
  );

  for (const personId of personIds.filter((personId) => personId === authenticatedUid)) {
    state.personProfiles[personId] = undefined;
    const personRef = doc(db, "people", personId);
    const unsubscribe = onSnapshot(
      personRef,
      (snapshot) => {
        if (scope !== currentModeScope()) return;
        state.personProfiles = {
          ...state.personProfiles,
          [personId]: snapshot.exists()
            ? personProfileRowFromData(personId, snapshot.data())
            : null,
        };
        renderRealtime();
      },
      (error) => {
        if (scope !== currentModeScope()) return;
        state.error = error.message;
        state.personProfiles = {
          ...state.personProfiles,
          [personId]: null,
        };
        renderRealtime();
      },
    );
    unsubscribePersonProfiles.push(unsubscribe);
  }
}

function connectOrganizationRoleAssignments(): void {
  const scope = currentModeScope();
  unsubscribeModeWorkspace?.();
  unsubscribeModeWorkspace = watchOperationalModeWorkspace({
    db,
    organizationId: state.organizationId,
    uid: auth.currentUser?.uid ?? "",
    onChange: (snapshot) => {
      if (scope !== currentModeScope()) return;
      state.organizationRoleAssignments = snapshot.roles
        .map(organizationRoleAssignmentRowFromData)
        .filter((row): row is OrganizationRoleAssignmentRow => row !== undefined)
        .sort((left, right) => left.personId.localeCompare(right.personId));
      state.operationalModes = snapshot.modes
        .map(operationalModeRowFromData)
        .sort((left, right) => {
          const rank = ["active", "scheduled", "draft", "completed", "archived"];
          return (
            rank.indexOf(left.status) - rank.indexOf(right.status) ||
            left.displayName.localeCompare(right.displayName)
          );
        });
      state.operationalModeProjection = snapshot.projection
        ? operationalModeProjectionRowFromData(snapshot.projection)
        : undefined;
      operationalModesLoading = snapshot.loading;
      operationalModesLoadError = snapshot.error;
      if (!snapshot.loading && !selectedContextCanManageOrganizationSettings())
        resetOperationalModeEditor();
      renderRealtime();
    },
    onDiagnostic: (phase, code) => console.warn("operational_modes_read", { phase, code }),
  });
}

function connectMemberSupportTickets(): void {
  unsubscribeMemberSupportTickets?.();
  unsubscribeMemberSupportMessages?.();
  state.memberSupportTickets = [];
  state.memberSupportMessages = [];
  state.memberSupportSelectedTicketId = "";

  const ticketsRef = collection(db, "organizations", state.organizationId, "memberSupportTickets");
  unsubscribeMemberSupportTickets = onSnapshot(
    ticketsRef,
    (snapshot) => {
      state.memberSupportTickets = snapshot.docs
        .map((ticketSnapshot) =>
          memberSupportTicketRowFromData(ticketSnapshot.id, ticketSnapshot.data()),
        )
        .filter((row): row is MemberSupportTicketRow => row !== undefined)
        .sort(
          (left, right) =>
            (right.lastActivityAt?.getTime() ?? 0) - (left.lastActivityAt?.getTime() ?? 0),
        );
      if (
        state.memberSupportSelectedTicketId &&
        !state.memberSupportTickets.some(
          (ticket) => ticket.ticketId === state.memberSupportSelectedTicketId,
        )
      ) {
        state.memberSupportSelectedTicketId = "";
      }
      connectMemberSupportMessages();
      renderRealtime();
    },
    (error) => {
      state.memberSupportError = error.message;
      renderRealtime();
    },
  );
}

let memberSupportMessageScope = "";
function connectMemberSupportMessages(): void {
  const nextScope = state.memberSupportSelectedTicketId
    ? `${currentModeScope()}/${state.memberSupportSelectedTicketId}`
    : "";
  if (nextScope === memberSupportMessageScope) return;
  memberSupportMessageScope = nextScope;
  unsubscribeMemberSupportMessages?.();
  state.memberSupportMessages = [];
  const ticketId = state.memberSupportSelectedTicketId;
  if (!ticketId) {
    return;
  }

  const messagesRef = collection(
    db,
    "organizations",
    state.organizationId,
    "memberSupportTickets",
    ticketId,
    "messages",
  );
  unsubscribeMemberSupportMessages = onSnapshot(
    messagesRef,
    (snapshot) => {
      state.memberSupportMessages = snapshot.docs
        .map((messageSnapshot) =>
          supportTicketMessageRowFromData(messageSnapshot.id, messageSnapshot.data()),
        )
        .filter((row): row is SupportTicketMessageRow => row !== undefined)
        .sort(
          (left, right) => (left.createdAt?.getTime() ?? 0) - (right.createdAt?.getTime() ?? 0),
        );
      renderRealtime();
    },
    (error) => {
      state.memberSupportError = error.message;
      renderRealtime();
    },
  );
}

function connectShdeedSupportTickets(): void {
  unsubscribeShdeedSupportTickets?.();
  unsubscribeShdeedSupportMessages?.();
  state.shdeedSupportTickets = [];
  state.shdeedSupportMessages = [];
  state.shdeedSupportSelectedTicketId = "";

  const ticketsRef = query(
    collection(db, "platformSupportTickets"),
    where("organizationId", "==", state.organizationId),
  );
  unsubscribeShdeedSupportTickets = onSnapshot(
    ticketsRef,
    (snapshot) => {
      state.shdeedSupportTickets = snapshot.docs
        .map((ticketSnapshot) =>
          shdeedSupportTicketRowFromData(ticketSnapshot.id, ticketSnapshot.data()),
        )
        .filter((row): row is ShdeedSupportTicketRow => row !== undefined)
        .sort(
          (left, right) =>
            (right.lastActivityAt?.getTime() ?? 0) - (left.lastActivityAt?.getTime() ?? 0),
        );
      if (
        state.shdeedSupportSelectedTicketId &&
        !state.shdeedSupportTickets.some(
          (ticket) => ticket.ticketId === state.shdeedSupportSelectedTicketId,
        )
      ) {
        state.shdeedSupportSelectedTicketId = "";
      }
      connectShdeedSupportMessages();
      renderRealtime();
    },
    (error) => {
      state.shdeedSupportError = error.message;
      renderRealtime();
    },
  );
}

let shdeedSupportMessageScope = "";
function connectShdeedSupportMessages(): void {
  const nextScope = state.shdeedSupportSelectedTicketId
    ? `${currentModeScope()}/${state.shdeedSupportSelectedTicketId}`
    : "";
  if (nextScope === shdeedSupportMessageScope) return;
  shdeedSupportMessageScope = nextScope;
  unsubscribeShdeedSupportMessages?.();
  state.shdeedSupportMessages = [];
  const ticketId = state.shdeedSupportSelectedTicketId;
  if (!ticketId) {
    return;
  }

  const messagesRef = collection(db, "platformSupportTickets", ticketId, "messages");
  unsubscribeShdeedSupportMessages = onSnapshot(
    messagesRef,
    (snapshot) => {
      state.shdeedSupportMessages = snapshot.docs
        .map((messageSnapshot) =>
          supportTicketMessageRowFromData(messageSnapshot.id, messageSnapshot.data()),
        )
        .filter((row): row is SupportTicketMessageRow => row !== undefined)
        .sort(
          (left, right) => (left.createdAt?.getTime() ?? 0) - (right.createdAt?.getTime() ?? 0),
        );
      renderRealtime();
    },
    (error) => {
      state.shdeedSupportError = error.message;
      renderRealtime();
    },
  );
}

function memberSupportTicketRowFromData(
  id: string,
  data: Record<string, unknown>,
): MemberSupportTicketRow | undefined {
  const organizationId = stringField(data.organizationId);
  const ticketId = stringField(data.ticketId) || id;
  const ticketNumber = stringField(data.ticketNumber);
  const subject = stringField(data.subject);
  if (organizationId !== state.organizationId || !ticketId || !ticketNumber || !subject) {
    return undefined;
  }

  return {
    assignedToPersonId: stringField(data.assignedToPersonId),
    category: stringField(data.category) || "Other",
    createdAt: optionalDateFromFirestore(data.createdAt),
    description: stringField(data.description),
    internalPriority: supportPriorityFromData(data.internalPriority),
    lastActivityAt:
      optionalDateFromFirestore(data.lastActivityAt) ?? optionalDateFromFirestore(data.updatedAt),
    lastMessagePreview: stringField(data.lastMessagePreview),
    organizationId,
    requesterDisplayName:
      stringField(data.requesterDisplayName) || stringField(data.requesterPersonId) || "-",
    requesterEmail: stringField(data.requesterEmail),
    requesterPersonId: stringField(data.requesterPersonId),
    requesterUid: stringField(data.requesterUid),
    requesterUrgency: stringField(data.requesterUrgency) || "Normal",
    status: supportStatusFromData(data.status),
    subject,
    ticketId,
    ticketNumber,
  };
}

function shdeedSupportTicketRowFromData(
  id: string,
  data: Record<string, unknown>,
): ShdeedSupportTicketRow | undefined {
  const organizationId = stringField(data.organizationId);
  const ticketId = stringField(data.ticketId) || id;
  const ticketNumber = stringField(data.ticketNumber);
  const subject = stringField(data.subject);
  if (organizationId !== state.organizationId || !ticketId || !ticketNumber || !subject) {
    return undefined;
  }

  return {
    assignedToPersonId: stringField(data.assignedToPersonId),
    category: stringField(data.category) || "Other",
    createdAt: optionalDateFromFirestore(data.createdAt),
    description: stringField(data.description),
    internalPriority: supportPriorityFromData(data.internalPriority),
    lastActivityAt:
      optionalDateFromFirestore(data.lastActivityAt) ?? optionalDateFromFirestore(data.updatedAt),
    lastMessagePreview: stringField(data.lastMessagePreview),
    organizationId,
    organizationNameSnapshot:
      stringField(data.organizationNameSnapshot) || selectedOrganization().displayName,
    requesterAdminDisplayName:
      stringField(data.requesterAdminDisplayName) ||
      stringField(data.requesterAdminPersonId) ||
      "-",
    requesterAdminEmail: stringField(data.requesterAdminEmail),
    requesterAdminPersonId: stringField(data.requesterAdminPersonId),
    requesterUrgency: stringField(data.requesterUrgency) || "Normal",
    status: supportStatusFromData(data.status),
    subject,
    ticketId,
    ticketNumber,
  };
}

function supportTicketMessageRowFromData(
  id: string,
  data: Record<string, unknown>,
): SupportTicketMessageRow | undefined {
  const ticketId = stringField(data.ticketId);
  const body = stringField(data.body);
  if (!ticketId || !body) {
    return undefined;
  }

  return {
    attachments: supportAttachmentRowsFromData(data.attachments),
    body,
    createdAt: optionalDateFromFirestore(data.createdAt),
    messageId: stringField(data.messageId) || id,
    senderDisplayName: stringField(data.senderDisplayName) || stringField(data.senderUid) || "-",
    senderRole: stringField(data.senderRole),
    senderUid: stringField(data.senderUid),
    ticketId,
  };
}

function supportAttachmentRowsFromData(value: unknown): SupportAttachmentRow[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (!isRecord(item)) {
        return undefined;
      }
      const fileName = stringField(item.fileName);
      const mimeType = stringField(item.mimeType);
      const storagePath = stringField(item.storagePath);
      const sizeBytes = numberField(item.sizeBytes);
      if (!fileName || !mimeType || !storagePath || sizeBytes <= 0) {
        return undefined;
      }
      return { fileName, mimeType, sizeBytes, storagePath };
    })
    .filter((item): item is SupportAttachmentRow => item !== undefined);
}

function supportStatusFromData(value: unknown): SupportTicketStatus {
  return typeof value === "string" && (supportTicketStatuses as readonly string[]).includes(value)
    ? (value as SupportTicketStatus)
    : "Open";
}

function supportPriorityFromData(value: unknown): SupportTicketPriority {
  return typeof value === "string" && (supportTicketPriorities as readonly string[]).includes(value)
    ? (value as SupportTicketPriority)
    : "Normal";
}

function organizationConnectionRowFromData(
  data: Record<string, unknown>,
): OrganizationConnectionRow | undefined {
  const uid = stringField(data.uid) || stringField(data.personId);
  const organizationId = stringField(data.organizationId) || state.organizationId;
  if (!uid || organizationId !== state.organizationId) {
    return undefined;
  }

  return {
    organizationId,
    personId: stringField(data.personId) || uid,
    roles: stringArrayField(data.roles),
    status: stringField(data.status) || "-",
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
    uid,
  };
}

function organizationRoleAssignmentRowFromData(
  data: Record<string, unknown>,
): OrganizationRoleAssignmentRow | undefined {
  const organizationId = stringField(data.organizationId);
  const personId = stringField(data.personId);
  if (!organizationId || organizationId !== state.organizationId || !personId) {
    return undefined;
  }

  return {
    organizationId,
    personId,
    roleIds: stringArrayField(data.roleIds).filter(isOrganizationRoleId),
    status: stringField(data.status) || "-",
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
    updatedBy: stringField(data.revokedBy) || stringField(data.assignedBy) || "-",
  };
}

function personProfileRowFromData(
  personId: string,
  data: Record<string, unknown> | undefined,
): PersonProfileRow | null {
  if (!data) {
    return null;
  }
  const email = recordValue(data.email);
  return {
    displayName: nullableProfileText(data.displayName),
    email: nullableProfileText(email.normalized),
    phoneNumber: nullableProfileText(data.phoneNumber),
    photoUrl: nullableProfileText(data.photoUrl),
    status: stringField(data.status) || "unknown",
    uid: stringField(data.uid) || personId,
  };
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function numberField(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function stringArrayField(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function isOrganizationRoleId(value: string): value is OrganizationRoleId {
  return organizationRoleIds.includes(value as OrganizationRoleId);
}

function renderPeopleManagerPanel(
  title: string,
  connectionDecisionsLabel: string,
  organizationDisplayName: string,
): string {
  const rows = peopleManagerRows();
  const pendingConnectionDecisions = state.connectionDecisions.filter((decision) =>
    isPendingConnectionDecisionStatus(decision.status),
  );
  const normalizedSearch = state.peopleListSearch.trim().toLowerCase();
  const filteredRows = normalizedSearch
    ? rows.filter((row) =>
        peopleRowSearchText(row, {
          legacyRolesLabel: legacyRolesLabel(row.connection),
          roleLabels: row.canonicalRoleIds.map(roleLabel),
        }).includes(normalizedSearch),
      )
    : rows;
  const canDisableAccounts = selectedContextCanDisablePeopleAccounts();

  return `
    <div class="list-toolbar">
      <div>
        <h2>${title}</h2>
        <p class="meta">${
          filteredRows.length
        } / ${rows.length} ${state.locale === "ar" ? "شخص" : "people"}</p>
      </div>
      <div class="list-toolbar-actions">
        <label class="list-search">
          <span>${state.locale === "ar" ? "البحث عن أشخاص" : "Search people"}</span>
          <input
            id="people-list-search"
            maxlength="160"
            placeholder="${state.locale === "ar" ? "الاسم، الحالة، الدور" : "Name, status, role"}"
            value="${escapeHtml(state.peopleListSearch)}"
          />
        </label>
      </div>
    </div>
    ${state.roleManagementMessage ? `<p class="status-inline">${escapeHtml(state.roleManagementMessage)}</p>` : ""}
    ${renderPeopleRoleEditorForm(rows)}
    ${
      rows.length === 0
        ? `<p class="meta">No connected people for ${escapeHtml(organizationDisplayName)}.</p>`
        : filteredRows.length === 0
          ? `<p class="meta">No people match "${escapeHtml(state.peopleListSearch)}".</p>`
          : `<div class="table-scroll" role="region" aria-label="People">
	            <table class="data-table people-table">
	              <thead>
	                <tr>
	                  <th scope="col">Person</th>
	                  <th scope="col">Status</th>
	                  <th scope="col">Roles</th>
	                  <th scope="col">Last update</th>
	                  <th scope="col">Actions</th>
	                </tr>
              </thead>
              <tbody>
                ${filteredRows
                  .map((row) => {
                    const personLabel = peopleRowPersonLabel(row);
                    const statusClass =
                      row.connection.status === "active"
                        ? "status-accepted"
                        : row.connection.status === "suspended" ||
                            row.connection.status === "disconnected" ||
                            row.connection.status === "rejected"
                          ? "status-rejected"
                          : "status-pending";
                    const disableDisabled =
                      state.busy ||
                      !canDisableAccounts ||
                      row.connection.personId === state.userUid ||
                      row.connection.status === "suspended" ||
                      row.connection.status === "disconnected";
                    const disableTooltip =
                      row.connection.personId === state.userUid
                        ? "You cannot disable your own account"
                        : canDisableAccounts
                          ? "Disable account"
                          : "Requires organization admin authority";

                    return `<tr>
	                      <td>
	                        <strong>${escapeHtml(personLabel)}</strong>
	                        ${renderPeopleProfileMeta(row)}
	                      </td>
                      <td>
                        <span class="status-pill ${statusClass}">${escapeHtml(
                          row.connection.status,
                        )}</span>
	                      </td>
	                      <td>${renderPeopleRoleChips(row.canonicalRoleIds)}</td>
	                      <td>${escapeHtml(peopleRowUpdatedAtLabel(row))}</td>
	                      <td>
	                        <div class="table-actions">
	                          <button
	                            aria-label="${escapeHtml(`View ${personLabel}`)}"
	                            class="secondary-action inline-action row-text-action"
	                            data-people-view="${escapeHtml(row.connection.personId)}"
	                            data-tooltip="View"
	                            title="View"
	                            type="button"
	                          >View</button>
	                          <button
	                            aria-label="${escapeHtml(`Edit roles for ${personLabel}`)}"
	                            class="secondary-action inline-action row-text-action"
	                            data-people-edit="${escapeHtml(row.connection.personId)}"
	                            data-tooltip="Edit roles"
	                            title="Edit roles"
	                            type="button"
	                            ${state.busy ? "disabled" : ""}
	                          >Roles</button>
	                          <button
	                            aria-label="${escapeHtml(`Disable ${personLabel}`)}"
	                            class="danger-action inline-action row-text-action"
	                            data-people-disable="${escapeHtml(row.connection.personId)}"
	                            data-tooltip="${escapeHtml(disableTooltip)}"
	                            title="${escapeHtml(disableTooltip)}"
	                            type="button"
	                            ${disableDisabled ? "disabled" : ""}
	                          >Disable</button>
	                        </div>
	                      </td>
                    </tr>`;
                  })
                  .join("")}
              </tbody>
            </table>
          </div>`
    }
    <p class="meta">${connectionDecisionsLabel}: ${pendingConnectionDecisions.length}</p>
    ${
      pendingConnectionDecisions.length === 0
        ? ""
        : `<ul class="request-list">
          ${pendingConnectionDecisions
            .slice(0, 3)
            .map(
              (decision) => `<li>
                <span>${escapeHtml(decision.targetRequestId)}<br><small>${escapeHtml(
                  decision.requestedBy,
                )}</small></span>
                <span class="status-pill ${
                  decision.status === "completed" ? "status-accepted" : "status-pending"
                }">${escapeHtml(decision.decision)} · ${escapeHtml(decision.status)}</span>
              </li>`,
            )
            .join("")}
        </ul>`
    }
    <p class="meta">Role and account writes are backend-authoritative and audited.</p>
  `;
}

function isPendingConnectionDecisionStatus(status: string): boolean {
  return status === "requested" || status === "pending";
}

function roleAssignmentForPerson(personId: string): OrganizationRoleAssignmentRow | undefined {
  return state.organizationRoleAssignments.find(
    (assignment) =>
      assignment.personId === personId && assignment.organizationId === state.organizationId,
  );
}

function peopleManagerRows(): PeopleManagerRow[] {
  return state.organizationConnections
    .map((connection) => {
      const assignment = roleAssignmentForPerson(connection.personId);
      const roleIds =
        assignment?.status === "active" && assignment.roleIds.length > 0 ? assignment.roleIds : [];
      const canonicalRoleIds: OrganizationRoleId[] = ["connectedPerson", ...roleIds];

      return {
        assignment,
        canonicalRoleIds,
        connection,
        personProfile: state.personProfiles[connection.personId],
      };
    })
    .sort((left, right) => {
      const statusWeight = (status: string) =>
        status === "active" ? 0 : status === "pending" ? 1 : status === "suspended" ? 2 : 3;
      const statusDelta =
        statusWeight(left.connection.status) - statusWeight(right.connection.status);
      if (statusDelta !== 0) {
        return statusDelta;
      }
      return peopleRowPersonLabel(left).localeCompare(peopleRowPersonLabel(right));
    });
}

function peopleRowForPersonId(
  personId: string,
  rows = peopleManagerRows(),
): PeopleManagerRow | undefined {
  return rows.find((row) => row.connection.personId === personId);
}

function selectedPeopleManagerRow(rows = peopleManagerRows()): PeopleManagerRow | undefined {
  return (
    peopleRowForPersonId(state.peopleManagerSelectedPersonId, rows) ??
    peopleRowForPersonId(state.userUid, rows) ??
    rows[0]
  );
}

function peopleRowUpdatedAtLabel(row: PeopleManagerRow): string {
  return (
    row.assignment?.updatedAt?.toLocaleString(state.locale) ??
    row.connection.updatedAt?.toLocaleString(state.locale) ??
    "-"
  );
}

function legacyRolesLabel(connection: OrganizationConnectionRow): string {
  return connection.roles.length ? connection.roles.join(", ") : "No legacy roles";
}

function renderPeopleRoleChips(roleIds: readonly OrganizationRoleId[]): string {
  return `<div class="role-chip-list">${roleIds
    .map(
      (roleId) =>
        `<span class="status-pill status-accepted">${escapeHtml(roleLabel(roleId))}</span>`,
    )
    .join("")}</div>`;
}

function selectedContextCanAssignOperationalRoles(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("organizationAdmin");
}

function selectedContextCanAssignProtectedRoles(): boolean {
  return canonicalRoleIdsForSelectedContext().includes("organizationOwner");
}

function selectedContextCanDisablePeopleAccounts(): boolean {
  const roleIds = canonicalRoleIdsForSelectedContext();
  return roleIds.includes("organizationOwner") || roleIds.includes("organizationAdmin");
}

function roleIsProtected(roleId: OrganizationRoleId): boolean {
  return (
    roleId === "organizationOwner" || roleId === "organizationAdmin" || roleId === "financeAdmin"
  );
}

function renderPeopleRoleEditorForm(rows: PeopleManagerRow[]): string {
  const row = selectedPeopleManagerRow(rows);
  if (!row) {
    return `<form id="people-role-editor-form" class="editor-form" data-task-window-launch="manual">
      <p class="meta">No connected person is selected.</p>
    </form>`;
  }

  const canAssignOperationalRoles = selectedContextCanAssignOperationalRoles();
  const canAssignProtectedRoles = selectedContextCanAssignProtectedRoles();
  const targetHasProtectedRoles = row.canonicalRoleIds.some(
    (roleId) => roleId !== "connectedPerson" && roleIsProtected(roleId),
  );
  const saveDisabled =
    state.busy ||
    !canAssignOperationalRoles ||
    (targetHasProtectedRoles && !canAssignProtectedRoles);
  const disableDisabled =
    state.busy ||
    !selectedContextCanDisablePeopleAccounts() ||
    row.connection.personId === state.userUid ||
    row.connection.status === "suspended" ||
    row.connection.status === "disconnected";
  const personLabel = peopleRowPersonLabel(row);
  const roleOptions = organizationRoleIds.filter((roleId) => roleId !== "connectedPerson");
  const profile = row.personProfile;
  const email = profile && profile !== null ? profile.email : null;
  const phone = profile && profile !== null ? profile.phoneNumber : null;
  const profileStatus =
    profile === undefined
      ? "Loading profile..."
      : profile === null
        ? "Profile unavailable"
        : profile.status;

  return `<form
    id="people-role-editor-form"
    class="editor-form"
    data-task-window-launch="manual"
  >
    <section class="form-section">
      <h3>${escapeHtml(personLabel)}</h3>
      <dl class="event-action-summary people-detail-summary">
        <div>
          <dt>Profile</dt>
          <dd>${escapeHtml(profileStatus)}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>${escapeHtml(email ?? "-")}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>${escapeHtml(phone ?? "-")}</dd>
        </div>
        <div>
          <dt>Person ID</dt>
          <dd>${escapeHtml(row.connection.personId)}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>${escapeHtml(row.connection.status)}</dd>
        </div>
        <div>
          <dt>Canonical roles</dt>
          <dd>${escapeHtml(row.canonicalRoleIds.map(roleLabel).join(", "))}</dd>
        </div>
        <div>
          <dt>Legacy roles</dt>
          <dd>${escapeHtml(legacyRolesLabel(row.connection))}</dd>
        </div>
      </dl>
      <input name="targetPersonId" type="hidden" value="${escapeHtml(row.connection.personId)}" />
    </section>
    <section class="form-section">
      <h3>Roles</h3>
      <div class="role-checkbox-grid">
        ${roleOptions
          .map((roleId) => {
            const protectedRole = roleIsProtected(roleId);
            const disabled = protectedRole && !canAssignProtectedRoles;
            const checked = row.canonicalRoleIds.includes(roleId);
            return `<label class="checkbox-row role-checkbox-row">
              <input
                name="roleIds"
                type="checkbox"
                value="${escapeHtml(roleId)}"
                ${checked ? "checked" : ""}
                ${disabled ? "disabled" : ""}
              />
              <span>
                ${escapeHtml(roleLabel(roleId))}
                <small>${escapeHtml(rolePermissionSummary(roleId))}</small>
                ${protectedRole ? `<small>Protected role · owner only</small>` : ""}
              </span>
            </label>`;
          })
          .join("")}
      </div>
      ${
        !canAssignOperationalRoles
          ? `<p class="meta">Role changes require organization admin authority.</p>`
          : targetHasProtectedRoles && !canAssignProtectedRoles
            ? `<p class="meta">This account has a protected role. Only an organization owner can change it.</p>`
            : `<p class="meta">Saving replaces this person's active privileged role set for ${escapeHtml(
                state.organizationId,
              )}. Connected person access comes from the active organization connection.</p>`
      }
    </section>
    <div class="editor-actions">
      <button type="submit" ${saveDisabled ? "disabled" : ""}>Save roles</button>
      <button
        class="danger-action"
        data-people-disable="${escapeHtml(row.connection.personId)}"
        type="button"
        ${disableDisabled ? "disabled" : ""}
      >
        Disable account
      </button>
    </div>
  </form>`;
}

function rolePermissionSummary(roleId: OrganizationRoleId): string {
  const permissions = organizationRolePermissionMap[roleId] ?? [];
  if (permissions.length === 0) {
    return "No privileged permissions.";
  }

  return permissions.map(permissionLabel).join("; ");
}

function permissionLabel(permissionId: string): string {
  switch (permissionId) {
    case "organization.readPrivate":
      return "read organization workspace";
    case "organization.updateProfile":
      return "update profile";
    case "organization.manageCampuses":
      return "manage campuses";
    case "connections.review":
      return "review connection requests";
    case "connections.approve":
      return "approve/reject connections";
    case "roles.read":
      return "view roles";
    case "roles.assign":
      return "assign operational roles";
    case "roles.assignPrivileged":
      return "assign owner/admin/finance roles";
    case "people.disableAccount":
      return "disable accounts";
    case "content.manage":
      return "manage content";
    case "announcements.create":
      return "create announcements";
    case "announcements.publish":
      return "publish announcements";
    case "events.manage":
      return "manage events";
    case "events.checkIn":
      return "check in event attendees";
    case "finance.read":
      return "read finance";
    case "finance.refund":
      return "issue refunds";
    case "spaces.manage":
      return "manage spaces";
    case "volunteers.manage":
      return "manage volunteers";
    case "directory.affiliations.manage":
      return "manage business affiliations";
    case "support.manage":
      return "manage support tickets";
    case "settings.manage":
      return "manage settings";
    case "billing.read":
      return "read billing";
    default:
      return permissionId;
  }
}

function roleLabel(roleId: string): string {
  const label = (en: string, ar: string) => (state.locale === "ar" ? ar : en);
  switch (roleId) {
    case "organizationOwner":
      return label("Organization owner", "مالك المؤسسة");
    case "organizationAdmin":
      return label("Organization admin", "مسؤول المؤسسة");
    case "communicationsAdmin":
      return label("Communications admin", "مسؤول التواصل");
    case "eventsAdmin":
      return label("Events admin", "مسؤول الفعاليات");
    case "eventCheckInStaff":
      return label("Event check-in staff", "موظف تسجيل الحضور");
    case "financeAdmin":
      return label("Finance admin", "المسؤول المالي");
    case "spacesAdmin":
      return label("Spaces admin", "مسؤول المساحات");
    case "volunteerCoordinator":
      return label("Volunteer coordinator", "منسق المتطوعين");
    case "connectedPerson":
      return label("Connected person", "شخص متصل");
    default:
      return roleId;
  }
}

function renderCommerceTestModeChecklist(commerceStatus: CommerceStatusRow | undefined): string {
  const readiness = commerceStatus?.readiness ?? "unknown";
  const connectReady = readiness === "ready" || readiness === "restricted";
  const items = [
    {
      label: "Stripe Connect test onboarding",
      ready: connectReady,
    },
    {
      label: "Donations, refunds, receipts",
      ready: state.donations.length > 0 || state.donationRefunds.length > 0,
    },
    {
      label: "Detect-only reconciliation",
      ready: state.reconciliationRuns.length > 0,
    },
    {
      label: "Iftar and Spaces payment paths",
      ready: true,
    },
  ];

  return `<div class="status-grid commerce-test-mode-grid" aria-label="Commerce test-mode readiness">
    <span>
      <strong>Staging test-mode only</strong>
      <small>Live payments remain blocked until production Stripe credentials, webhooks, legal/compliance, and owner QA are complete.</small>
    </span>
    ${items
      .map(
        (item) => `<span>
          <strong>${escapeHtml(item.label)}</strong>
          <small>${item.ready ? "Ready for QA" : "Needs staging exercise"}</small>
        </span>`,
      )
      .join("")}
  </div>`;
}

function connectBusinessDirectory(): void {
  unsubscribeBusinessDirectory?.();
  const businessesQuery = query(
    collection(db, "businesses"),
    where("source", "==", "experience_forty_four"),
    where("status", "==", "active"),
    where("publicVisibility.directory", "==", true),
    where("publicVisibility.discovery", "==", true),
  );

  unsubscribeBusinessDirectory = onSnapshot(
    businessesQuery,
    (snapshot) => {
      state.businessDirectory = snapshot.docs
        .map((business) => businessDirectoryRowFromData(business.data()))
        .sort((left, right) => {
          if (left.featured !== right.featured) {
            return left.featured ? -1 : 1;
          }
          return left.name.localeCompare(right.name);
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.businessDirectory = demoBusinessDirectoryBusinesses.map((business) =>
        businessDirectoryRowFromData(business as unknown as Record<string, unknown>),
      );
      renderRealtime();
    },
  );
}

function connectBusinessAffiliations(): void {
  unsubscribeBusinessAffiliations?.();

  unsubscribeBusinessAffiliations = onSnapshot(
    collection(db, "organizations", state.organizationId, "businessAffiliations"),
    (snapshot) => {
      state.businessAffiliations = snapshot.docs
        .map((affiliation) => organizationBusinessAffiliationRowFromData(affiliation.data()))
        .sort((left, right) => left.displayOrder - right.displayOrder);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.businessAffiliations = demoOrganizationBusinessAffiliationProjections
        .filter((affiliation) => affiliation.organizationId === state.organizationId)
        .map((affiliation) =>
          organizationBusinessAffiliationRowFromData(
            affiliation as unknown as Record<string, unknown>,
          ),
        );
      renderRealtime();
    },
  );
}

function connectEvents(): void {
  unsubscribeEvents?.();
  const eventsQuery = selectedContextCanManageEvents()
    ? collection(db, "organizations", state.organizationId, "events")
    : query(
        collection(db, "organizations", state.organizationId, "events"),
        where("organizationId", "==", state.organizationId),
        where("status", "==", "published"),
        where("visibility", "==", "connected"),
      );

  unsubscribeEvents = onSnapshot(
    eventsQuery,
    (snapshot) => {
      state.events = snapshot.docs
        .map((event) => {
          const data = event.data();
          const startsAt = data.startsAt instanceof Timestamp ? data.startsAt.toDate() : undefined;
          const endsAt = data.endsAt instanceof Timestamp ? data.endsAt.toDate() : undefined;
          const publishedAt =
            data.publishedAt instanceof Timestamp ? data.publishedAt.toDate() : undefined;
          const archivedAt =
            data.archivedAt instanceof Timestamp ? data.archivedAt.toDate() : undefined;
          const registrationOpensAt =
            data.registrationOpensAt instanceof Timestamp
              ? data.registrationOpensAt.toDate()
              : undefined;
          const scheduledPublishAt =
            data.scheduledPublishAt instanceof Timestamp
              ? data.scheduledPublishAt.toDate()
              : undefined;
          const registrationClosesAt =
            data.registrationClosesAt instanceof Timestamp
              ? data.registrationClosesAt.toDate()
              : undefined;
          const image =
            typeof data.image === "object" && data.image !== null
              ? (data.image as Record<string, unknown>)
              : {};
          return {
            archivedAt,
            campusId: typeof data.campusId === "string" ? data.campusId : null,
            campusNameSnapshot:
              typeof data.campusNameSnapshot === "string" ? data.campusNameSnapshot : null,
            coverMediaId: typeof data.coverMediaId === "string" ? data.coverMediaId : null,
            description: typeof data.description === "string" ? data.description : "-",
            descriptionAr: typeof data.descriptionAr === "string" ? data.descriptionAr : null,
            endsAt,
            eventId: typeof data.eventId === "string" ? data.eventId : event.id,
            eventType:
              data.eventType === "friday_program" || data.eventType === "janazah"
                ? data.eventType
                : "standard",
            id: event.id,
            imageAltText:
              "altText" in image && typeof image.altText === "string" ? image.altText : null,
            imageAltTextAr:
              "altTextAr" in image && typeof image.altTextAr === "string" ? image.altTextAr : null,
            imageStoragePath:
              "storagePath" in image && typeof image.storagePath === "string"
                ? image.storagePath
                : null,
            addressLine1: typeof data.addressLine1 === "string" ? data.addressLine1 : null,
            locationType: typeof data.locationType === "string" ? data.locationType : null,
            locationName: typeof data.locationName === "string" ? data.locationName : "-",
            onlineUrl: typeof data.onlineUrl === "string" ? data.onlineUrl : null,
            registrationMode:
              typeof data.registrationMode === "string"
                ? data.registrationMode
                : data.registrationEnabled === true
                  ? "free"
                  : "none",
            notificationIntentId:
              typeof data.notificationIntentId === "string" ? data.notificationIntentId : null,
            notificationIntentStatus:
              typeof data.notificationIntentStatus === "string"
                ? data.notificationIntentStatus
                : "none",
            notificationRequestedOnPublish:
              typeof data.notificationRequestedOnPublish === "boolean"
                ? data.notificationRequestedOnPublish
                : false,
            emailRequestedOnPublish: data.emailRequestedOnPublish === true,
            publishEmailSubject:
              typeof data.publishEmailSubject === "string" ? data.publishEmailSubject : undefined,
            publishEmailMessage:
              typeof data.publishEmailMessage === "string" ? data.publishEmailMessage : undefined,
            publishedAt,
            registrationCancellationAllowed:
              typeof data.registrationCancellationAllowed === "boolean"
                ? data.registrationCancellationAllowed
                : true,
            guestRegistrationEnabled:
              typeof data.guestRegistrationEnabled === "boolean"
                ? data.guestRegistrationEnabled
                : true,
            dependentRegistrationEnabled:
              typeof data.dependentRegistrationEnabled === "boolean"
                ? data.dependentRegistrationEnabled
                : true,
            registrationCapacity:
              typeof data.registrationCapacity === "number" ? data.registrationCapacity : 0,
            registrationClosesAt,
            registrationConfirmedCount:
              typeof data.registrationConfirmedCount === "number"
                ? data.registrationConfirmedCount
                : 0,
            registrationEnabled:
              typeof data.registrationEnabled === "boolean" ? data.registrationEnabled : false,
            registrationOpensAt,
            registrationQuestionCount:
              typeof data.registrationQuestionCount === "number"
                ? data.registrationQuestionCount
                : Array.isArray(data.registrationQuestions)
                  ? data.registrationQuestions.length
                  : 0,
            reminderPresets: Array.isArray(data.reminderPresets)
              ? data.reminderPresets.filter(
                  (preset): preset is string => typeof preset === "string",
                )
              : [],
            waitlistCount: typeof data.waitlistCount === "number" ? data.waitlistCount : 0,
            waitlistEnabled:
              typeof data.waitlistEnabled === "boolean" ? data.waitlistEnabled : false,
            waiverRequired: data.waiverRequired === true,
            attendanceCheckedInCount:
              typeof data.attendanceCheckedInCount === "number" ? data.attendanceCheckedInCount : 0,
            scheduledPublishAt,
            startsAt,
            status: typeof data.status === "string" ? data.status : "-",
            summary: typeof data.summary === "string" ? data.summary : "-",
            summaryAr: typeof data.summaryAr === "string" ? data.summaryAr : null,
            timezone: typeof data.timezone === "string" ? data.timezone : "America/New_York",
            title: typeof data.title === "string" ? data.title : event.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : null,
            updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.startsAt?.getTime() ?? 0;
          const rightTime = right.startsAt?.getTime() ?? 0;
          return leftTime - rightTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.events = [];
      renderRealtime();
    },
  );
}

function connectEventRegistrations(): void {
  unsubscribeEventRegistrations?.();
  state.eventRegistrations = [];

  if (!selectedContextCanCheckInEvents()) {
    unsubscribeEventRegistrations = undefined;
    return;
  }

  const eventId = state.eventEditor.eventId.trim();
  if (!eventId) {
    unsubscribeEventRegistrations = undefined;
    return;
  }

  const registrationsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "events",
    eventId,
    "registrations",
  );

  unsubscribeEventRegistrations = onSnapshot(
    registrationsQuery,
    (snapshot) => {
      state.eventRegistrations = snapshot.docs
        .map((registration) => {
          const data = registration.data();
          const registeredAt =
            data.registeredAt instanceof Timestamp ? data.registeredAt.toDate() : undefined;
          const cancelledAt =
            data.cancelledAt instanceof Timestamp ? data.cancelledAt.toDate() : undefined;
          const updatedAt =
            data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
          const attendees = Array.isArray(data.attendees)
            ? data.attendees
                .filter(
                  (attendee): attendee is Record<string, unknown> =>
                    typeof attendee === "object" && attendee !== null,
                )
                .map((attendee) => ({
                  attendeeId: typeof attendee.attendeeId === "string" ? attendee.attendeeId : "-",
                  attendeeType:
                    typeof attendee.attendeeType === "string" ? attendee.attendeeType : "-",
                  name: typeof attendee.name === "string" ? attendee.name : "-",
                  personId: typeof attendee.personId === "string" ? attendee.personId : null,
                }))
            : [];

          return {
            answers: Array.isArray(data.answers) ? data.answers : [],
            attendeeCount:
              typeof data.attendeeCount === "number"
                ? data.attendeeCount
                : Math.max(1, attendees.length),
            attendees,
            cancelledAt,
            eventId: typeof data.eventId === "string" ? data.eventId : eventId,
            id: registration.id,
            organizationId:
              typeof data.organizationId === "string" ? data.organizationId : state.organizationId,
            personId: typeof data.personId === "string" ? data.personId : registration.id,
            registeredAt,
            registrationId:
              typeof data.registrationId === "string" ? data.registrationId : registration.id,
            status: typeof data.status === "string" ? data.status : "-",
            updatedAt,
          };
        })
        .filter(
          (registration) =>
            registration.organizationId === state.organizationId &&
            registration.eventId === eventId,
        )
        .sort((left, right) => {
          const leftTime = left.registeredAt?.getTime() ?? 0;
          const rightTime = right.registeredAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.eventRegistrations = [];
      renderRealtime();
    },
  );
}

function connectEventAttendance(): void {
  unsubscribeEventAttendance?.();
  state.eventAttendance = [];

  if (!selectedContextCanCheckInEvents()) {
    unsubscribeEventAttendance = undefined;
    return;
  }

  const eventId = state.eventEditor.eventId.trim();
  if (!eventId) {
    unsubscribeEventAttendance = undefined;
    return;
  }

  const attendanceQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "events",
    eventId,
    "attendance",
  );

  unsubscribeEventAttendance = onSnapshot(
    attendanceQuery,
    (snapshot) => {
      state.eventAttendance = snapshot.docs
        .map((attendance) => {
          const data = attendance.data();
          const checkedInAt =
            data.checkedInAt instanceof Timestamp ? data.checkedInAt.toDate() : undefined;
          const updatedAt =
            data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;

          return {
            attendanceId: typeof data.attendanceId === "string" ? data.attendanceId : attendance.id,
            checkedInAt,
            checkedInBy: typeof data.checkedInBy === "string" ? data.checkedInBy : "-",
            eventId: typeof data.eventId === "string" ? data.eventId : eventId,
            id: attendance.id,
            method: typeof data.method === "string" ? data.method : "-",
            organizationId:
              typeof data.organizationId === "string" ? data.organizationId : state.organizationId,
            personId: typeof data.personId === "string" ? data.personId : attendance.id,
            registrationId:
              typeof data.registrationId === "string" ? data.registrationId : attendance.id,
            stationLabel: typeof data.stationLabel === "string" ? data.stationLabel : null,
            status: typeof data.status === "string" ? data.status : "-",
            updatedAt,
          };
        })
        .filter(
          (attendance) =>
            attendance.organizationId === state.organizationId && attendance.eventId === eventId,
        )
        .sort((left, right) => {
          const leftTime = left.checkedInAt?.getTime() ?? 0;
          const rightTime = right.checkedInAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.eventAttendance = [];
      renderRealtime();
    },
  );
}

function connectEventMedia(): void {
  unsubscribeEventMedia?.();
  state.eventMedia = [];

  if (!selectedContextCanManageEvents()) {
    unsubscribeEventMedia = undefined;
    return;
  }

  const eventId = state.eventEditor.eventId.trim();
  if (!eventId) {
    unsubscribeEventMedia = undefined;
    return;
  }

  const mediaQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "events",
    eventId,
    "media",
  );

  unsubscribeEventMedia = onSnapshot(
    mediaQuery,
    (snapshot) => {
      state.eventMedia = snapshot.docs
        .map((media) => eventMediaRowFromData(media.id, media.data(), eventId))
        .filter((media): media is EventMediaRow => media !== null)
        .filter(
          (media) => media.organizationId === state.organizationId && media.eventId === eventId,
        )
        .sort((left, right) => {
          if (left.position !== right.position) {
            return left.position - right.position;
          }
          return left.mediaId.localeCompare(right.mediaId);
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.eventMedia = [];
      renderRealtime();
    },
  );
}

function eventMediaRowFromData(
  id: string,
  data: Record<string, unknown>,
  fallbackEventId: string,
): EventMediaRow | null {
  const type = data.type;
  if (type !== "image" && type !== "uploadedVideo" && type !== "youtube") {
    return null;
  }

  const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;

  return {
    altText: typeof data.altText === "string" ? data.altText : null,
    altTextAr: typeof data.altTextAr === "string" ? data.altTextAr : null,
    canonicalUrl: typeof data.canonicalUrl === "string" ? data.canonicalUrl : null,
    caption: typeof data.caption === "string" ? data.caption : null,
    captionAr: typeof data.captionAr === "string" ? data.captionAr : null,
    contentType: typeof data.contentType === "string" ? data.contentType : null,
    durationSeconds: typeof data.durationSeconds === "number" ? data.durationSeconds : null,
    eventId: typeof data.eventId === "string" ? data.eventId : fallbackEventId,
    fileSizeBytes: typeof data.fileSizeBytes === "number" ? data.fileSizeBytes : null,
    height: typeof data.height === "number" ? data.height : null,
    id,
    mediaId: typeof data.mediaId === "string" ? data.mediaId : id,
    organizationId:
      typeof data.organizationId === "string" ? data.organizationId : state.organizationId,
    originalUrl: typeof data.originalUrl === "string" ? data.originalUrl : null,
    playbackUrl: typeof data.playbackUrl === "string" ? data.playbackUrl : null,
    position: typeof data.position === "number" ? data.position : 0,
    posterStoragePath: typeof data.posterStoragePath === "string" ? data.posterStoragePath : null,
    storagePath: typeof data.storagePath === "string" ? data.storagePath : null,
    thumbnailUrl: typeof data.thumbnailUrl === "string" ? data.thumbnailUrl : null,
    title: typeof data.title === "string" ? data.title : null,
    titleAr: typeof data.titleAr === "string" ? data.titleAr : null,
    type,
    updatedAt,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
    videoId: typeof data.videoId === "string" ? data.videoId : null,
    width: typeof data.width === "number" ? data.width : null,
  };
}

function connectDonations(): void {
  unsubscribeDonations?.();

  if (!selectedContextCanReadFinance()) {
    state.donations = [];
    unsubscribeDonations = undefined;
    return;
  }

  const donationsQuery = collection(db, "organizations", state.organizationId, "transactions");

  unsubscribeDonations = onSnapshot(
    donationsQuery,
    (snapshot) => {
      state.donations = snapshot.docs
        .map((donation) => {
          const data = donation.data();
          const createdAt =
            data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined;
          return {
            id: donation.id,
            amountMinor: typeof data.amountMinor === "number" ? data.amountMinor : 0,
            createdAt,
            destinationLabel: donationDestinationLabel(data),
            personId: typeof data.personId === "string" ? data.personId : "-",
            providerPaymentIntentId:
              typeof data.providerPaymentIntentId === "string" ? data.providerPaymentIntentId : "-",
            receiptId: typeof data.receiptId === "string" ? data.receiptId : null,
            refundedAmountMinor:
              typeof data.refundedAmountMinor === "number" ? data.refundedAmountMinor : 0,
            refundPendingAmountMinor:
              typeof data.refundPendingAmountMinor === "number" ? data.refundPendingAmountMinor : 0,
            refundStatus: typeof data.refundStatus === "string" ? data.refundStatus : "none",
            remainingRefundableAmountMinor:
              typeof data.remainingRefundableAmountMinor === "number"
                ? data.remainingRefundableAmountMinor
                : 0,
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .filter((donation) => donation.id.startsWith("txn_"))
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.donations = [];
      renderRealtime();
    },
  );
}

function connectDonationRefunds(): void {
  unsubscribeDonationRefunds?.();

  if (!selectedContextCanReadFinance()) {
    state.donationRefunds = [];
    unsubscribeDonationRefunds = undefined;
    return;
  }

  unsubscribeDonationRefunds = onSnapshot(
    collection(db, "organizations", state.organizationId, "refunds"),
    (snapshot) => {
      state.donationRefunds = snapshot.docs
        .map((refund) => {
          const data = refund.data();
          return {
            id: refund.id,
            amountMinor: typeof data.amountMinor === "number" ? data.amountMinor : 0,
            createdAt: optionalDateFromFirestore(data.createdAt),
            originalTransactionId:
              typeof data.originalTransactionId === "string" ? data.originalTransactionId : "-",
            personId: typeof data.personId === "string" ? data.personId : "-",
            providerRefundId:
              typeof data.providerRefundId === "string" ? data.providerRefundId : null,
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.donationRefunds = [];
      renderRealtime();
    },
  );
}

function connectCommerceReconciliation(): void {
  unsubscribeReconciliationRuns?.();
  unsubscribeReconciliationMismatches?.();

  if (!selectedContextCanReadFinance()) {
    state.reconciliationRuns = [];
    state.reconciliationMismatches = [];
    unsubscribeReconciliationRuns = undefined;
    unsubscribeReconciliationMismatches = undefined;
    return;
  }

  unsubscribeReconciliationRuns = onSnapshot(
    collection(db, "organizations", state.organizationId, "reconciliationRuns"),
    (snapshot) => {
      state.reconciliationRuns = snapshot.docs
        .map((run) => {
          const data = run.data();
          return {
            id: run.id,
            checkedTransactionCount:
              typeof data.checkedTransactionCount === "number" ? data.checkedTransactionCount : 0,
            createdAt: optionalDateFromFirestore(data.createdAt),
            mismatchCount: typeof data.mismatchCount === "number" ? data.mismatchCount : 0,
            requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "-",
            status: typeof data.status === "string" ? data.status : "-",
            targetTransactionId:
              typeof data.targetTransactionId === "string" ? data.targetTransactionId : null,
          };
        })
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.reconciliationRuns = [];
      renderRealtime();
    },
  );

  unsubscribeReconciliationMismatches = onSnapshot(
    query(collectionGroup(db, "mismatches"), where("organizationId", "==", state.organizationId)),
    (snapshot) => {
      state.reconciliationMismatches = snapshot.docs
        .map((mismatch) => {
          const data = mismatch.data();
          return {
            id: mismatch.id,
            detectedAt: optionalDateFromFirestore(data.detectedAt),
            providerPaymentIntentId:
              typeof data.providerPaymentIntentId === "string"
                ? data.providerPaymentIntentId
                : null,
            runId: typeof data.runId === "string" ? data.runId : "-",
            safeDiagnosticCode:
              typeof data.safeDiagnosticCode === "string" ? data.safeDiagnosticCode : "-",
            status: typeof data.status === "string" ? data.status : "-",
            transactionId: typeof data.transactionId === "string" ? data.transactionId : null,
            type: typeof data.type === "string" ? data.type : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.detectedAt?.getTime() ?? 0;
          const rightTime = right.detectedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.reconciliationMismatches = [];
      renderRealtime();
    },
  );
}

function connectGivingDestinations(): void {
  unsubscribeGivingFunds?.();
  unsubscribeCampaigns?.();

  if (!selectedContextCanReadFinance()) {
    state.givingFunds = [];
    state.campaigns = [];
    unsubscribeGivingFunds = undefined;
    unsubscribeCampaigns = undefined;
    return;
  }

  unsubscribeGivingFunds = onSnapshot(
    collection(db, "organizations", state.organizationId, "givingFunds"),
    (snapshot) => {
      state.givingFunds = snapshot.docs
        .map((fund) => {
          const data = fund.data();
          const updatedAt =
            data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
          return {
            id: fund.id,
            confirmedAmountMinor:
              typeof data.confirmedAmountMinor === "number" ? data.confirmedAmountMinor : 0,
            confirmedDonationCount:
              typeof data.confirmedDonationCount === "number" ? data.confirmedDonationCount : 0,
            description: typeof data.description === "string" ? data.description : "",
            descriptionAr: typeof data.descriptionAr === "string" ? data.descriptionAr : null,
            displayOrder: typeof data.displayOrder === "number" ? data.displayOrder : 0,
            fundId: typeof data.fundId === "string" ? data.fundId : fund.id,
            name: typeof data.name === "string" ? data.name : fund.id,
            nameAr: typeof data.nameAr === "string" ? data.nameAr : null,
            status: typeof data.status === "string" ? data.status : "-",
            type: typeof data.type === "string" ? data.type : "-",
            updatedAt,
            visibility: typeof data.visibility === "string" ? data.visibility : "-",
          };
        })
        .sort((left, right) => left.displayOrder - right.displayOrder);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.givingFunds = [];
      renderRealtime();
    },
  );

  unsubscribeCampaigns = onSnapshot(
    collection(db, "organizations", state.organizationId, "campaigns"),
    (snapshot) => {
      state.campaigns = snapshot.docs
        .map((campaign) => {
          const data = campaign.data();
          const updatedAt =
            data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
          return {
            id: campaign.id,
            campaignId: typeof data.campaignId === "string" ? data.campaignId : campaign.id,
            confirmedAmountMinor:
              typeof data.confirmedAmountMinor === "number" ? data.confirmedAmountMinor : 0,
            confirmedDonationCount:
              typeof data.confirmedDonationCount === "number" ? data.confirmedDonationCount : 0,
            description: typeof data.description === "string" ? data.description : "",
            descriptionAr: typeof data.descriptionAr === "string" ? data.descriptionAr : null,
            displayOrder: typeof data.displayOrder === "number" ? data.displayOrder : 0,
            fundId: typeof data.fundId === "string" ? data.fundId : null,
            goalAmountMinor: typeof data.goalAmountMinor === "number" ? data.goalAmountMinor : null,
            status: typeof data.status === "string" ? data.status : "-",
            title: typeof data.title === "string" ? data.title : campaign.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : null,
            updatedAt,
            visibility: typeof data.visibility === "string" ? data.visibility : "-",
          };
        })
        .sort((left, right) => left.displayOrder - right.displayOrder);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.campaigns = [];
      renderRealtime();
    },
  );
}

function connectCommerceStatus(): void {
  unsubscribeCommerceStatus?.();

  if (!selectedContextCanReadFinance()) {
    state.commerceMessage = "";
    state.commerceStatus = undefined;
    unsubscribeCommerceStatus = undefined;
    renderRealtime();
    return;
  }

  const commerceRef = doc(db, "organizations", state.organizationId, "config", "commerce");

  unsubscribeCommerceStatus = onSnapshot(
    commerceRef,
    (snapshot) => {
      state.commerceMessage = "";
      state.commerceStatus = commerceStatusFromData(snapshot.data());
      renderRealtime();
    },
    (error) => {
      state.commerceMessage = error.message;
      state.commerceStatus = undefined;
      renderRealtime();
    },
  );
}

function connectOrganizationBilling(): void {
  unsubscribeOrganizationPlans?.();
  unsubscribeOrganizationSubscription?.();
  unsubscribeOrganizationEntitlementOverrides?.();
  unsubscribeOrganizationUsage?.();
  unsubscribeFoundingSlot?.();

  state.billingError = "";

  unsubscribeOrganizationPlans = onSnapshot(
    collection(db, "plans"),
    (snapshot) => {
      state.organizationPlanCatalog = snapshot.docs
        .map((plan) => organizationPlanRowFromData(plan.data()))
        .sort((left, right) => left.priceMinor - right.priceMinor);
      renderRealtime();
    },
    (error) => {
      state.billingError = error.message;
      state.organizationPlanCatalog = [];
      renderRealtime();
    },
  );

  if (!selectedContextCanReadBilling()) {
    state.organizationSubscription = undefined;
    state.organizationEntitlementOverrides = [];
    state.organizationUsage = undefined;
    state.foundingSlot = undefined;
    unsubscribeOrganizationSubscription = undefined;
    unsubscribeOrganizationEntitlementOverrides = undefined;
    unsubscribeOrganizationUsage = undefined;
    unsubscribeFoundingSlot = undefined;
    renderRealtime();
    return;
  }

  unsubscribeOrganizationSubscription = onSnapshot(
    doc(db, "organizations", state.organizationId, "config", "subscription"),
    (snapshot) => {
      state.organizationSubscription = snapshot.exists()
        ? organizationSubscriptionRowFromData(snapshot.data())
        : subscriptionFixtureForOrganization(state.organizationId);
      renderRealtime();
    },
    (error) => {
      state.billingError = error.message;
      state.organizationSubscription = undefined;
      renderRealtime();
    },
  );

  unsubscribeOrganizationEntitlementOverrides = onSnapshot(
    collection(db, "organizations", state.organizationId, "entitlementOverrides"),
    (snapshot) => {
      state.organizationEntitlementOverrides = snapshot.docs
        .map((override) => entitlementOverrideRowFromData(override.data()))
        .sort((left, right) => left.entitlementId.localeCompare(right.entitlementId));
      renderRealtime();
    },
    (error) => {
      state.billingError = error.message;
      state.organizationEntitlementOverrides = [];
      renderRealtime();
    },
  );

  unsubscribeOrganizationUsage = onSnapshot(
    doc(db, "organizations", state.organizationId, "usage", "current"),
    (snapshot) => {
      state.organizationUsage = snapshot.exists()
        ? usageLimitRowFromData(snapshot.data())
        : usageFixtureForOrganization(state.organizationId);
      renderRealtime();
    },
    (error) => {
      state.billingError = error.message;
      state.organizationUsage = undefined;
      renderRealtime();
    },
  );

  unsubscribeFoundingSlot = onSnapshot(
    doc(db, "foundingOrganizationSlots", state.organizationId),
    (snapshot) => {
      state.foundingSlot = snapshot.exists()
        ? foundingSlotRowFromData(snapshot.data())
        : foundingSlotFixtureForOrganization(state.organizationId);
      renderRealtime();
    },
    (error) => {
      state.billingError = error.message;
      state.foundingSlot = undefined;
      renderRealtime();
    },
  );
}

function connectRamadanModes(): void {
  unsubscribeRamadanModes?.();
  const modesQuery = collection(db, "organizations", state.organizationId, "ramadanModes");

  unsubscribeRamadanModes = onSnapshot(
    modesQuery,
    (snapshot) => {
      state.ramadanModes = snapshot.docs
        .map((mode) => {
          const data = mode.data();
          const updatedAt =
            data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
          return {
            id: mode.id,
            seasonYear: typeof data.seasonYear === "number" ? data.seasonYear : 0,
            slotPriceCents: typeof data.slotPriceCents === "number" ? data.slotPriceCents : 0,
            status: typeof data.status === "string" ? data.status : "-",
            summary: typeof data.summary === "string" ? data.summary : "-",
            summaryAr: typeof data.summaryAr === "string" ? data.summaryAr : "-",
            title: typeof data.title === "string" ? data.title : mode.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : mode.id,
            updatedAt,
            updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.updatedAt?.getTime() ?? 0;
          const rightTime = right.updatedAt?.getTime() ?? 0;

          if (leftTime !== rightTime) {
            return rightTime - leftTime;
          }

          return right.seasonYear - left.seasonYear;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.ramadanModes = [];
      renderRealtime();
    },
  );
}

function connectRamadanSetups(): void {
  unsubscribeRamadanSetups?.();

  if (selectedContextCanReadFinance() && !selectedContextCanManageOrganizationSettings()) {
    void connectFinanceRamadanContext();
    return;
  }

  const setupsQuery = collection(db, "organizations", state.organizationId, "ramadan");

  unsubscribeRamadanSetups = onSnapshot(
    setupsQuery,
    (snapshot) => {
      state.ramadanSetups = snapshot.docs
        .map((setup) => ramadanSetupRowFromData(setup.data(), setup.id))
        .sort((left, right) => {
          if (left.status !== right.status) {
            return left.status.localeCompare(right.status);
          }
          return right.hijriYear - left.hijriYear;
        });
      connectLoadedRamadanChildren();
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.ramadanSetups = [];
      connectLoadedRamadanChildren();
      renderRealtime();
    },
  );
}

async function connectFinanceRamadanContext(): Promise<void> {
  const scope = currentModeScope();
  const organizationId = state.organizationId;
  try {
    const result = await postAuthenticatedFunction("resolveRamadanReadContext", { organizationId });
    if (retiringTenant || scope !== currentModeScope()) return;
    state.ramadanSetups = [];
    connectLoadedRamadanChildren();
    if (
      result.organizationId !== organizationId ||
      typeof result.ramadanId !== "string" ||
      !/^ramadan-\d+$/.test(result.ramadanId)
    ) {
      renderRealtime();
      return;
    }
    unsubscribeRamadanSetups = onSnapshot(
      doc(db, "organizations", organizationId, "ramadan", result.ramadanId),
      (snapshot) => {
        state.ramadanSetups = snapshot.exists()
          ? [ramadanSetupRowFromData(snapshot.data(), snapshot.id)]
          : [];
        connectLoadedRamadanChildren();
        renderRealtime();
      },
      (error) => {
        state.error = error.message;
        state.ramadanSetups = [];
        connectLoadedRamadanChildren();
        renderRealtime();
      },
    );
  } catch (error) {
    if (retiringTenant || scope !== currentModeScope()) return;
    state.ramadanSetups = [];
    connectLoadedRamadanChildren();
    state.error = error instanceof Error ? error.message : "ramadan_context_unavailable";
    renderRealtime();
  }
}

function connectLoadedRamadanChildren(): void {
  const id = selectedRamadanSetup()?.ramadanId;
  if (id === connectedRamadanId) return;
  connectedRamadanId = id;
  if (id) {
    state.iftarSponsorshipDefaultsEditor.ramadanId = id;
    state.iftarAttendanceDefaultsEditor.ramadanId = id;
    state.ramadanAdminNoteEditor.ramadanId = id;
  }
  state.iftarSponsorshipDefaults = undefined;
  state.iftarSponsorshipDays = [];
  state.iftarAttendanceDefaults = undefined;
  state.iftarAttendanceDays = [];
  state.iftarAttendanceRegistrations = [];
  state.sponsorships = [];
  state.iftarTransfers = [];
  state.ramadanAdminNotes = [];
  connectIftarSponsorshipConfiguration();
  connectIftarAttendance();
  connectIftarSponsorshipIntents();
  connectIftarTransfers();
  if (selectedContextCanManageRamadanAdminNotes()) connectRamadanAdminNotes();
}

function connectRamadanHistory(): void {
  const requestId = currentRamadanHistoryRequestId + 1;
  currentRamadanHistoryRequestId = requestId;
  void refreshRamadanHistory(requestId, state.organizationId);
}

async function refreshRamadanHistory(requestId: number, organizationId: string): Promise<void> {
  if (!selectedContextCanManageOrganizationSettings()) {
    state.ramadanHistory = [];
    renderRealtime();
    return;
  }

  try {
    const result = await postAuthenticatedFunction("listRamadanHistory", { organizationId });
    if (requestId !== currentRamadanHistoryRequestId || organizationId !== state.organizationId) {
      return;
    }

    const historyRows = Array.isArray(result.history) ? result.history : [];
    state.ramadanHistory = historyRows
      .filter(isRecord)
      .map((history) =>
        ramadanHistoryRowFromData(
          history,
          typeof history.historyId === "string"
            ? history.historyId
            : typeof history.ramadanId === "string"
              ? history.ramadanId
              : "ramadan-history",
        ),
      )
      .sort((left, right) => {
        if (left.hijriYear !== right.hijriYear) {
          return right.hijriYear - left.hijriYear;
        }
        return right.ramadanId.localeCompare(left.ramadanId);
      });
    renderRealtime();
  } catch (error) {
    if (requestId !== currentRamadanHistoryRequestId || organizationId !== state.organizationId) {
      return;
    }

    state.error = error instanceof Error ? error.message : String(error);
    state.ramadanHistory = [];
    renderRealtime();
  }
}

function connectIftarSponsorshipConfiguration(): void {
  unsubscribeIftarSponsorshipDefaults?.();
  unsubscribeIftarSponsorshipDays?.();

  const ramadanId = loadedRamadanId(state.ramadanSetups, selectedRamadanSetup()?.ramadanId);
  if (!ramadanId) return;
  const organizationId = state.organizationId;
  const daysQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "ramadan",
    ramadanId,
    "iftarDays",
  );

  unsubscribeIftarSponsorshipDefaults = watchOptionalRamadanConfig({
    db,
    organizationId,
    ramadanId,
    kind: "iftarConfig",
    resolve: () =>
      postAuthenticatedFunction("resolveRamadanConfigAvailability", { organizationId, ramadanId }),
    onData: (data) => {
      state.iftarSponsorshipDefaults = data ? iftarSponsorshipDefaultsRowFromData(data) : undefined;
      renderRealtime();
    },
    onError: (error) => {
      state.error = error.message;
      state.iftarSponsorshipDefaults = undefined;
      renderRealtime();
    },
  });

  unsubscribeIftarSponsorshipDays = onSnapshot(
    daysQuery,
    (snapshot) => {
      state.iftarSponsorshipDays = snapshot.docs
        .map((day) => iftarSponsorshipDayRowFromData(day.data(), day.id))
        .sort((left, right) => {
          if (left.gregorianDate !== right.gregorianDate) {
            return left.gregorianDate.localeCompare(right.gregorianDate);
          }
          return left.id.localeCompare(right.id);
        });
      if (
        state.iftarSponsorshipDays.length > 0 &&
        !state.iftarSponsorshipDays.some(
          (day) => day.id === state.iftarSponsorshipDayEditor.iftarDayId,
        )
      ) {
        const firstDay = state.iftarSponsorshipDays[0];
        if (!firstDay) {
          return;
        }
        state.iftarSponsorshipDayEditor = iftarSponsorshipDayEditorInputFromRow(firstDay);
      }
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarSponsorshipDays = [];
      renderRealtime();
    },
  );
}

export function connectIftarAttendance(): void {
  unsubscribeIftarAttendanceDefaults?.();
  unsubscribeIftarAttendanceDays?.();
  unsubscribeIftarAttendanceRegistrations?.();

  const ramadanId = loadedRamadanId(state.ramadanSetups, selectedRamadanSetup()?.ramadanId);
  if (!ramadanId) return;
  const organizationId = state.organizationId;
  const daysQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "ramadan",
    ramadanId,
    "iftarAttendanceDays",
  );
  const registrationsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "ramadan",
    ramadanId,
    "iftarAttendanceRegistrations",
  );

  unsubscribeIftarAttendanceDefaults = watchOptionalRamadanConfig({
    db,
    organizationId,
    ramadanId,
    kind: "iftarAttendanceConfig",
    resolve: () =>
      postAuthenticatedFunction("resolveRamadanConfigAvailability", { organizationId, ramadanId }),
    onData: (data) => {
      state.iftarAttendanceDefaults = data ? iftarAttendanceDefaultsRowFromData(data) : undefined;
      renderRealtime();
    },
    onError: (error) => {
      state.error = error.message;
      state.iftarAttendanceDefaults = undefined;
      renderRealtime();
    },
  });

  unsubscribeIftarAttendanceDays = onSnapshot(
    daysQuery,
    (snapshot) => {
      state.iftarAttendanceDays = snapshot.docs
        .map((day) => iftarAttendanceDayRowFromData(day.data(), day.id))
        .sort((left, right) => {
          if (left.gregorianDate !== right.gregorianDate) {
            return left.gregorianDate.localeCompare(right.gregorianDate);
          }
          return left.id.localeCompare(right.id);
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarAttendanceDays = [];
      renderRealtime();
    },
  );

  unsubscribeIftarAttendanceRegistrations = onSnapshot(
    registrationsQuery,
    (snapshot) => {
      state.iftarAttendanceRegistrations = snapshot.docs
        .map((registration) =>
          iftarAttendanceRegistrationRowFromData(registration.data(), registration.id),
        )
        .sort((left, right) => {
          const leftDate = left.gregorianDateSnapshot ?? "";
          const rightDate = right.gregorianDateSnapshot ?? "";
          if (leftDate !== rightDate) {
            return leftDate.localeCompare(rightDate);
          }
          return left.personId.localeCompare(right.personId);
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarAttendanceRegistrations = [];
      renderRealtime();
    },
  );
}

function connectIftarSponsorshipSlots(): void {
  unsubscribeIftarSlots?.();
  const slotsQuery = collection(db, "organizations", state.organizationId, "iftarSponsorshipSlots");

  unsubscribeIftarSlots = onSnapshot(
    slotsQuery,
    (snapshot) => {
      state.iftarSlots = snapshot.docs
        .map((slot) => {
          const data = slot.data();
          return {
            id: slot.id,
            amountCents: typeof data.amountCents === "number" ? data.amountCents : 0,
            dayNumber: typeof data.dayNumber === "number" ? data.dayNumber : 0,
            label: typeof data.label === "string" ? data.label : slot.id,
            labelAr: typeof data.labelAr === "string" ? data.labelAr : slot.id,
            modeId: typeof data.modeId === "string" ? data.modeId : "-",
            sponsorUid: typeof data.sponsorUid === "string" ? data.sponsorUid : "-",
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => left.dayNumber - right.dayNumber);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarSlots = [];
      renderRealtime();
    },
  );
}

function connectIftarSponsorshipIntents(): void {
  unsubscribeSponsorships?.();

  if (!selectedContextCanViewIftarSponsorshipAdmin()) {
    state.sponsorships = [];
    unsubscribeSponsorships = undefined;
    return;
  }

  const ramadanId = loadedRamadanId(state.ramadanSetups, selectedRamadanSetup()?.ramadanId);
  if (!ramadanId) return;
  const sponsorshipsQuery = query(
    collection(
      db,
      "organizations",
      state.organizationId,
      "ramadan",
      ramadanId,
      "iftarSponsorshipAdmin",
    ),
    where("organizationId", "==", state.organizationId),
    where("ramadanId", "==", ramadanId),
    where("source", "==", "experience_thirty_five"),
  );

  unsubscribeSponsorships = onSnapshot(
    sponsorshipsQuery,
    (snapshot) => {
      state.sponsorships = snapshot.docs
        .map((sponsorship) => iftarSponsorshipAdminRowFromData(sponsorship.data(), sponsorship.id))
        .sort((left, right) => {
          const leftTime = left.updatedAt?.getTime() ?? left.createdAt?.getTime() ?? 0;
          const rightTime = right.updatedAt?.getTime() ?? right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.sponsorships = [];
      renderRealtime();
    },
  );
}

function connectIftarSponsorshipFinance(): void {
  unsubscribeIftarSponsorshipFinance?.();

  if (!selectedContextCanReadFinance()) {
    state.iftarSponsorshipFinance = [];
    unsubscribeIftarSponsorshipFinance = undefined;
    return;
  }

  unsubscribeIftarSponsorshipFinance = onSnapshot(
    collection(db, "organizations", state.organizationId, "transactions"),
    (snapshot) => {
      state.iftarSponsorshipFinance = snapshot.docs
        .map((transaction) =>
          iftarSponsorshipFinanceRowFromData(transaction.data(), transaction.id),
        )
        .filter((row): row is IftarSponsorshipFinanceRow => row !== undefined)
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarSponsorshipFinance = [];
      renderRealtime();
    },
  );
}

function connectIftarTransfers(): void {
  unsubscribeIftarTransfers?.();

  if (!selectedContextCanViewRamadanCommandCenter()) {
    state.iftarTransfers = [];
    unsubscribeIftarTransfers = undefined;
    return;
  }

  const ramadanId = loadedRamadanId(state.ramadanSetups, selectedRamadanSetup()?.ramadanId);
  if (!ramadanId) return;
  const transfersQuery = query(
    collection(db, "organizations", state.organizationId, "ramadan", ramadanId, "iftarTransfers"),
    where("organizationId", "==", state.organizationId),
    where("ramadanId", "==", ramadanId),
    where("source", "==", "experience_thirty_six"),
  );

  unsubscribeIftarTransfers = onSnapshot(
    transfersQuery,
    (snapshot) => {
      state.iftarTransfers = snapshot.docs
        .map((transfer) => iftarTransferRowFromData(transfer.data(), transfer.id))
        .sort((left, right) => {
          const leftTime = left.updatedAt?.getTime() ?? left.requestedAt?.getTime() ?? 0;
          const rightTime = right.updatedAt?.getTime() ?? right.requestedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.iftarTransfers = [];
      renderRealtime();
    },
  );
}

function unsubscribeRamadanAdminNoteListeners(): void {
  for (const unsubscribe of unsubscribeRamadanAdminNotes) {
    unsubscribe();
  }
  unsubscribeRamadanAdminNotes = [];
}

function selectedRamadanAdminNoteCategories(): RamadanAdminNoteCategory[] {
  return state.ramadanAdminNoteCategoryFilter === "all"
    ? [...ramadanAdminNoteCategories]
    : [state.ramadanAdminNoteCategoryFilter];
}

function selectedRamadanAdminNoteStatuses(): RamadanAdminNoteStatus[] {
  return state.ramadanAdminNoteStatusFilter === "all"
    ? ["active", "archived"]
    : [state.ramadanAdminNoteStatusFilter];
}

function currentRamadanAdminNoteRamadanId(): string {
  return (
    loadedRamadanId(state.ramadanSetups, state.ramadanAdminNoteEditor.ramadanId.trim()) ??
    selectedRamadanSetup()?.ramadanId ??
    ""
  );
}

function syncRamadanAdminNotesFromListenerRows(): void {
  const rows = Array.from(ramadanAdminNoteListenerRows.values()).flatMap((listenerRows) =>
    Array.from(listenerRows.values()),
  );
  const byId = new Map(rows.map((row) => [row.noteId, row]));

  state.ramadanAdminNotes = Array.from(byId.values()).sort((left, right) => {
    const leftTime = left.updatedAt?.getTime() ?? left.createdAt?.getTime() ?? 0;
    const rightTime = right.updatedAt?.getTime() ?? right.createdAt?.getTime() ?? 0;
    return rightTime - leftTime;
  });
}

function connectRamadanAdminNotes(): void {
  unsubscribeRamadanAdminNoteListeners();
  ramadanAdminNoteListenerRows = new Map();

  if (!selectedContextCanManageRamadanAdminNotes()) {
    state.ramadanAdminNotes = [];
    return;
  }

  const ramadanId = currentRamadanAdminNoteRamadanId();
  if (!loadedRamadanId(state.ramadanSetups, ramadanId)) return;

  for (const category of selectedRamadanAdminNoteCategories()) {
    for (const status of selectedRamadanAdminNoteStatuses()) {
      const listenerKey = `${category}:${status}`;
      const notesQuery = query(
        collection(db, "organizations", state.organizationId, "ramadan", ramadanId, "adminNotes"),
        where("organizationId", "==", state.organizationId),
        where("ramadanId", "==", ramadanId),
        where("source", "==", "experience_thirty_seven"),
        where("category", "==", category),
        where("status", "==", status),
      );

      unsubscribeRamadanAdminNotes.push(
        onSnapshot(
          notesQuery,
          (snapshot) => {
            ramadanAdminNoteListenerRows.set(
              listenerKey,
              new Map(
                snapshot.docs.map((note) => [
                  note.id,
                  ramadanAdminNoteRowFromData(note.data(), note.id),
                ]),
              ),
            );
            syncRamadanAdminNotesFromListenerRows();
            renderRealtime();
          },
          (error) => {
            state.error = error.message;
            ramadanAdminNoteListenerRows.delete(listenerKey);
            syncRamadanAdminNotesFromListenerRows();
            renderRealtime();
          },
        ),
      );
    }
  }
}

function unsubscribeVolunteerShiftListeners(): void {
  for (const unsubscribe of unsubscribeVolunteerShifts) {
    unsubscribe();
  }
  unsubscribeVolunteerShifts = [];
}

function unsubscribeVolunteerSignupListeners(): void {
  for (const unsubscribe of unsubscribeVolunteerSignups) {
    unsubscribe();
  }
  unsubscribeVolunteerSignups = [];
}

function unsubscribeVolunteerAttendanceListeners(): void {
  for (const unsubscribe of unsubscribeVolunteerAttendances) {
    unsubscribe();
  }
  unsubscribeVolunteerAttendances = [];
}

function unsubscribeVolunteerSummaryListeners(): void {
  unsubscribeVolunteerServiceSummaries?.();
  unsubscribeVolunteerRecognitionAwards?.();
  unsubscribeVolunteerServiceSummaries = undefined;
  unsubscribeVolunteerRecognitionAwards = undefined;
}

function volunteerOpportunityRowFromData(
  data: Record<string, unknown>,
  id: string,
): VolunteerOpportunityRow {
  const requirements = isRecord(data.requirements) ? data.requirements : {};
  const image = isRecord(data.image) ? data.image : {};

  return {
    archivedAt: optionalDateFromFirestore(data.archivedAt),
    campusId: typeof data.campusId === "string" ? data.campusId : null,
    category: typeof data.category === "string" ? data.category : "-",
    completedAt: optionalDateFromFirestore(data.completedAt),
    description: typeof data.description === "string" ? data.description : "-",
    descriptionAr: typeof data.descriptionAr === "string" ? data.descriptionAr : null,
    endsAt: optionalDateFromFirestore(data.endsAt),
    id,
    imageAltText: typeof image.altText === "string" ? image.altText : null,
    imageAltTextAr: typeof image.altTextAr === "string" ? image.altTextAr : null,
    imageStoragePath: typeof image.storagePath === "string" ? image.storagePath : null,
    instructions: typeof data.instructions === "string" ? data.instructions : "-",
    instructionsAr: typeof data.instructionsAr === "string" ? data.instructionsAr : null,
    locationName: typeof data.locationName === "string" ? data.locationName : "-",
    opportunityId: typeof data.opportunityId === "string" ? data.opportunityId : id,
    publishedAt: optionalDateFromFirestore(data.publishedAt),
    requirementAgePolicy:
      typeof requirements.agePolicy === "string" ? requirements.agePolicy : null,
    requirementDressCode:
      typeof requirements.dressCode === "string" ? requirements.dressCode : null,
    requirementPhysicalNotes:
      typeof requirements.physicalNotes === "string" ? requirements.physicalNotes : null,
    requirementTrainingRequired: requirements.trainingRequired === true,
    startsAt: optionalDateFromFirestore(data.startsAt),
    status: typeof data.status === "string" ? data.status : "-",
    title: typeof data.title === "string" ? data.title : id,
    titleAr: typeof data.titleAr === "string" ? data.titleAr : null,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
    visibility: typeof data.visibility === "string" ? data.visibility : "-",
  };
}

function volunteerShiftRowFromData(data: Record<string, unknown>, id: string): VolunteerShiftRow {
  return {
    capacity: typeof data.capacity === "number" ? data.capacity : 0,
    confirmedCount: typeof data.confirmedCount === "number" ? data.confirmedCount : 0,
    endsAt: optionalDateFromFirestore(data.endsAt),
    id,
    instructions: typeof data.instructions === "string" ? data.instructions : null,
    instructionsAr: typeof data.instructionsAr === "string" ? data.instructionsAr : null,
    notes: typeof data.notes === "string" ? data.notes : null,
    notesAr: typeof data.notesAr === "string" ? data.notesAr : null,
    opportunityId: typeof data.opportunityId === "string" ? data.opportunityId : "-",
    shiftId: typeof data.shiftId === "string" ? data.shiftId : id,
    startsAt: optionalDateFromFirestore(data.startsAt),
    status: typeof data.status === "string" ? data.status : "-",
    timezone: typeof data.timezone === "string" ? data.timezone : "America/New_York",
  };
}

function volunteerSignupRowFromData(data: Record<string, unknown>, id: string): VolunteerSignupRow {
  return {
    cancelledAt: optionalDateFromFirestore(data.cancelledAt),
    cancellationActorType:
      typeof data.cancellationActorType === "string" ? data.cancellationActorType : null,
    cancelledBy: typeof data.cancelledBy === "string" ? data.cancelledBy : null,
    opportunityId: typeof data.opportunityId === "string" ? data.opportunityId : "-",
    opportunityTitleSnapshot:
      typeof data.opportunityTitleSnapshot === "string" ? data.opportunityTitleSnapshot : "-",
    personId: typeof data.personId === "string" ? data.personId : id,
    shiftEndsAt: optionalDateFromFirestore(data.shiftEndsAt),
    shiftId: typeof data.shiftId === "string" ? data.shiftId : "-",
    shiftStartsAt: optionalDateFromFirestore(data.shiftStartsAt),
    signedUpAt: optionalDateFromFirestore(data.signedUpAt),
    signupId: typeof data.signupId === "string" ? data.signupId : id,
    status: typeof data.status === "string" ? data.status : "-",
  };
}

function volunteerAttendanceRowFromData(
  data: Record<string, unknown>,
  id: string,
): VolunteerAttendanceRow {
  return {
    attendanceId: typeof data.attendanceId === "string" ? data.attendanceId : id,
    checkedInAt: optionalDateFromFirestore(data.checkedInAt),
    checkedOutAt: optionalDateFromFirestore(data.checkedOutAt),
    checkInMethod: typeof data.checkInMethod === "string" ? data.checkInMethod : "-",
    checkOutMethod: typeof data.checkOutMethod === "string" ? data.checkOutMethod : null,
    opportunityId: typeof data.opportunityId === "string" ? data.opportunityId : "-",
    personId: typeof data.personId === "string" ? data.personId : id,
    provisionalMinutes: typeof data.provisionalMinutes === "number" ? data.provisionalMinutes : 0,
    shiftId: typeof data.shiftId === "string" ? data.shiftId : "-",
    status: typeof data.status === "string" ? data.status : "-",
    verifiedMinutes: typeof data.verifiedMinutes === "number" ? data.verifiedMinutes : 0,
  };
}

function volunteerServiceSummaryRowFromData(
  data: Record<string, unknown>,
  id: string,
): VolunteerServiceSummaryRow {
  return {
    lastVerifiedAt: optionalDateFromFirestore(data.lastVerifiedAt),
    personId: typeof data.personId === "string" ? data.personId : id,
    totalVerifiedMinutes:
      typeof data.totalVerifiedMinutes === "number" ? data.totalVerifiedMinutes : 0,
    verifiedAttendanceCount:
      typeof data.verifiedAttendanceCount === "number" ? data.verifiedAttendanceCount : 0,
  };
}

function volunteerRecognitionAwardRowFromData(
  data: Record<string, unknown>,
  id: string,
): VolunteerRecognitionAwardRow {
  return {
    awardId: typeof data.awardId === "string" ? data.awardId : id,
    label: typeof data.label === "string" ? data.label : id,
    labelAr: typeof data.labelAr === "string" ? data.labelAr : id,
    personId: typeof data.personId === "string" ? data.personId : "-",
    thresholdMinutes: typeof data.thresholdMinutes === "number" ? data.thresholdMinutes : 0,
    verifiedMinutesAtAward:
      typeof data.verifiedMinutesAtAward === "number" ? data.verifiedMinutesAtAward : 0,
  };
}

function syncVolunteerShiftsFromListenerRows(): void {
  state.volunteerShifts = Array.from(volunteerShiftListenerRows.values())
    .flat()
    .sort((left, right) => (left.startsAt?.getTime() ?? 0) - (right.startsAt?.getTime() ?? 0));
}

function syncVolunteerSignupsFromListenerRows(): void {
  state.volunteerSignups = Array.from(volunteerSignupListenerRows.values())
    .flat()
    .sort(
      (left, right) => (left.shiftStartsAt?.getTime() ?? 0) - (right.shiftStartsAt?.getTime() ?? 0),
    );
}

function syncVolunteerAttendancesFromListenerRows(): void {
  state.volunteerAttendances = Array.from(volunteerAttendanceListenerRows.values())
    .flat()
    .sort(
      (left, right) => (left.checkedInAt?.getTime() ?? 0) - (right.checkedInAt?.getTime() ?? 0),
    );
}

function connectVolunteerOpportunities(): void {
  unsubscribeVolunteerOpportunities?.();
  unsubscribeVolunteerShiftListeners();
  unsubscribeVolunteerSignupListeners();
  unsubscribeVolunteerAttendanceListeners();
  unsubscribeVolunteerSummaryListeners();
  volunteerShiftListenerRows = new Map();
  volunteerSignupListenerRows = new Map();
  volunteerAttendanceListenerRows = new Map();
  state.volunteerOpportunities = [];
  state.volunteerShifts = [];
  state.volunteerSignups = [];
  state.volunteerAttendances = [];
  state.volunteerRecognitionAwards = [];
  state.volunteerServiceSummaries = [];

  if (!selectedContextCanManageVolunteers()) {
    return;
  }

  connectVolunteerSummaries();

  unsubscribeVolunteerOpportunities = onSnapshot(
    collection(db, "organizations", state.organizationId, "volunteerOpportunities"),
    (snapshot) => {
      state.volunteerOpportunities = snapshot.docs
        .map((opportunity) => volunteerOpportunityRowFromData(opportunity.data(), opportunity.id))
        .sort((left, right) => left.title.localeCompare(right.title));
      connectVolunteerShifts();
      resetVolunteerOpportunityEditor();
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.volunteerOpportunities = [];
      unsubscribeVolunteerShiftListeners();
      unsubscribeVolunteerSignupListeners();
      unsubscribeVolunteerAttendanceListeners();
      volunteerShiftListenerRows = new Map();
      volunteerSignupListenerRows = new Map();
      volunteerAttendanceListenerRows = new Map();
      state.volunteerShifts = [];
      state.volunteerSignups = [];
      state.volunteerAttendances = [];
      renderRealtime();
    },
  );
}

function connectVolunteerShifts(): void {
  unsubscribeVolunteerShiftListeners();
  unsubscribeVolunteerSignupListeners();
  unsubscribeVolunteerAttendanceListeners();
  volunteerShiftListenerRows = new Map();
  volunteerSignupListenerRows = new Map();
  volunteerAttendanceListenerRows = new Map();
  state.volunteerShifts = [];
  state.volunteerSignups = [];
  state.volunteerAttendances = [];

  for (const opportunity of state.volunteerOpportunities) {
    const listenerKey = opportunity.id;
    unsubscribeVolunteerShifts.push(
      onSnapshot(
        collection(
          db,
          "organizations",
          state.organizationId,
          "volunteerOpportunities",
          opportunity.id,
          "shifts",
        ),
        (snapshot) => {
          volunteerShiftListenerRows.set(
            listenerKey,
            snapshot.docs.map((shift) => volunteerShiftRowFromData(shift.data(), shift.id)),
          );
          syncVolunteerShiftsFromListenerRows();
          connectVolunteerSignups();
          connectVolunteerAttendances();
          resetVolunteerShiftEditor();
          renderRealtime();
        },
        (error) => {
          state.error = error.message;
          volunteerShiftListenerRows.delete(listenerKey);
          syncVolunteerShiftsFromListenerRows();
          connectVolunteerSignups();
          connectVolunteerAttendances();
          renderRealtime();
        },
      ),
    );
  }
}

function connectVolunteerSignups(): void {
  unsubscribeVolunteerSignupListeners();
  volunteerSignupListenerRows = new Map();
  state.volunteerSignups = [];

  if (!selectedContextCanManageVolunteers()) {
    return;
  }

  for (const shift of state.volunteerShifts) {
    const listenerKey = `${shift.opportunityId}/${shift.shiftId}`;
    unsubscribeVolunteerSignups.push(
      onSnapshot(
        collection(
          db,
          "organizations",
          state.organizationId,
          "volunteerOpportunities",
          shift.opportunityId,
          "shifts",
          shift.shiftId,
          "signups",
        ),
        (snapshot) => {
          volunteerSignupListenerRows.set(
            listenerKey,
            snapshot.docs.map((signup) => volunteerSignupRowFromData(signup.data(), signup.id)),
          );
          syncVolunteerSignupsFromListenerRows();
          renderRealtime();
        },
        (error) => {
          state.error = error.message;
          volunteerSignupListenerRows.delete(listenerKey);
          syncVolunteerSignupsFromListenerRows();
          renderRealtime();
        },
      ),
    );
  }
}

function connectVolunteerAttendances(): void {
  unsubscribeVolunteerAttendanceListeners();
  volunteerAttendanceListenerRows = new Map();
  state.volunteerAttendances = [];

  if (!selectedContextCanManageVolunteers()) {
    return;
  }

  for (const shift of state.volunteerShifts) {
    const listenerKey = `${shift.opportunityId}/${shift.shiftId}`;
    unsubscribeVolunteerAttendances.push(
      onSnapshot(
        collection(
          db,
          "organizations",
          state.organizationId,
          "volunteerOpportunities",
          shift.opportunityId,
          "shifts",
          shift.shiftId,
          "attendance",
        ),
        (snapshot) => {
          volunteerAttendanceListenerRows.set(
            listenerKey,
            snapshot.docs.map((attendance) =>
              volunteerAttendanceRowFromData(attendance.data(), attendance.id),
            ),
          );
          syncVolunteerAttendancesFromListenerRows();
          renderRealtime();
        },
        (error) => {
          state.error = error.message;
          volunteerAttendanceListenerRows.delete(listenerKey);
          syncVolunteerAttendancesFromListenerRows();
          renderRealtime();
        },
      ),
    );
  }
}

function connectVolunteerSummaries(): void {
  unsubscribeVolunteerSummaryListeners();
  state.volunteerRecognitionAwards = [];
  state.volunteerServiceSummaries = [];

  if (!selectedContextCanManageVolunteers()) {
    return;
  }

  unsubscribeVolunteerServiceSummaries = onSnapshot(
    collection(db, "organizations", state.organizationId, "volunteerServiceSummaries"),
    (snapshot) => {
      state.volunteerServiceSummaries = snapshot.docs
        .map((summary) => volunteerServiceSummaryRowFromData(summary.data(), summary.id))
        .sort((left, right) => right.totalVerifiedMinutes - left.totalVerifiedMinutes);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.volunteerServiceSummaries = [];
      renderRealtime();
    },
  );

  unsubscribeVolunteerRecognitionAwards = onSnapshot(
    query(
      collectionGroup(db, "awards"),
      where("organizationId", "==", state.organizationId),
      where("source", "==", "experience_forty_three"),
    ),
    (snapshot) => {
      state.volunteerRecognitionAwards = snapshot.docs
        .map((award) => volunteerRecognitionAwardRowFromData(award.data(), award.id))
        .sort((left, right) => right.thresholdMinutes - left.thresholdMinutes);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.volunteerRecognitionAwards = [];
      renderRealtime();
    },
  );
}

function connectSpaces(): void {
  unsubscribeSpaces?.();
  unsubscribeSpaceAvailabilityBlockListeners();
  unsubscribeSpaceReservationRequestListeners();
  spaceAvailabilityBlockListenerRows = new Map();
  spaceReservationRequestListenerRows = new Map();
  state.spaceAvailabilityBlocks = [];
  state.spaceReservationRequests = [];
  const spacesQuery = query(collection(db, "organizations", state.organizationId, "spaces"));

  unsubscribeSpaces = onSnapshot(
    spacesQuery,
    (snapshot) => {
      state.spaces = snapshot.docs
        .map((space) => {
          const data = space.data();
          const accessibility = isRecord(data.accessibility) ? data.accessibility : {};
          const primaryImage = isRecord(data.primaryImage) ? data.primaryImage : {};
          const pricing = isRecord(data.pricing) ? data.pricing : {};
          const baseRateMinor =
            typeof pricing.baseRateMinor === "number" ? pricing.baseRateMinor : null;
          const depositMinor = typeof pricing.depositMinor === "number" ? pricing.depositMinor : 0;
          return {
            accessibilityFeatures: stringArrayFromData(accessibility.features),
            amenities: stringArrayFromData(data.amenities),
            campusId: typeof data.campusId === "string" ? data.campusId : null,
            id: space.id,
            capacity: typeof data.capacity === "number" ? data.capacity : 0,
            description: typeof data.description === "string" ? data.description : "-",
            descriptionAr: typeof data.descriptionAr === "string" ? data.descriptionAr : null,
            hourlyPriceCents:
              typeof data.hourlyPriceCents === "number" ? data.hourlyPriceCents : null,
            locationName: typeof data.locationName === "string" ? data.locationName : "-",
            name: typeof data.name === "string" ? data.name : space.id,
            nameAr: typeof data.nameAr === "string" ? data.nameAr : null,
            operatingHours: spaceOperatingHoursFromData(data.operatingHours),
            pricingEnabled: pricing.pricingEnabled === true,
            pricingType: typeof pricing.pricingType === "string" ? pricing.pricingType : "none",
            pricingVersion: typeof pricing.pricingVersion === "number" ? pricing.pricingVersion : 0,
            pricingTotalPreviewCents: baseRateMinor === null ? null : baseRateMinor + depositMinor,
            primaryImageStoragePath:
              typeof primaryImage.storagePath === "string" ? primaryImage.storagePath : null,
            source: typeof data.source === "string" ? data.source : "-",
            status: typeof data.status === "string" ? data.status : "-",
            timezone: typeof data.timezone === "string" ? data.timezone : "America/New_York",
            type: typeof data.type === "string" ? data.type : "-",
            visibility: typeof data.visibility === "string" ? data.visibility : "-",
          };
        })
        .sort((left, right) => left.name.localeCompare(right.name));
      connectSpaceAvailabilityBlocks();
      connectSpaceReservationRequests();
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.spaces = [];
      unsubscribeSpaceAvailabilityBlockListeners();
      unsubscribeSpaceReservationRequestListeners();
      spaceAvailabilityBlockListenerRows = new Map();
      spaceReservationRequestListenerRows = new Map();
      state.spaceAvailabilityBlocks = [];
      state.spaceReservationRequests = [];
      renderRealtime();
    },
  );
}

function unsubscribeSpaceAvailabilityBlockListeners(): void {
  for (const unsubscribe of unsubscribeSpaceAvailabilityBlocks) {
    unsubscribe();
  }
  unsubscribeSpaceAvailabilityBlocks = [];
}

function spaceAvailabilityBlockRowFromData(
  data: Record<string, unknown>,
  id: string,
): SpaceAvailabilityBlockRow {
  const label = isRecord(data.label) ? data.label : {};

  return {
    blockId: typeof data.blockId === "string" ? data.blockId : id,
    endsAt: optionalDateFromFirestore(data.endsAt),
    id,
    internalReason: typeof data.internalReason === "string" ? data.internalReason : null,
    label: typeof label.en === "string" ? label.en : id,
    labelAr: typeof label.ar === "string" ? label.ar : null,
    publicReason: data.publicReason === true,
    spaceId: typeof data.spaceId === "string" ? data.spaceId : "-",
    startsAt: optionalDateFromFirestore(data.startsAt),
    type: typeof data.type === "string" ? data.type : "-",
  };
}

function syncSpaceAvailabilityBlocksFromListenerRows(): void {
  state.spaceAvailabilityBlocks = Array.from(spaceAvailabilityBlockListenerRows.values())
    .flat()
    .sort((left, right) => {
      const leftTime = left.startsAt?.getTime() ?? 0;
      const rightTime = right.startsAt?.getTime() ?? 0;
      return leftTime - rightTime;
    });
}

function connectSpaceAvailabilityBlocks(): void {
  unsubscribeSpaceAvailabilityBlockListeners();
  spaceAvailabilityBlockListenerRows = new Map();
  state.spaceAvailabilityBlocks = [];

  for (const space of state.spaces) {
    const listenerKey = space.id;
    const blocksCollection = collection(
      db,
      "organizations",
      state.organizationId,
      "spaces",
      space.id,
      "availabilityBlocks",
    );

    unsubscribeSpaceAvailabilityBlocks.push(
      onSnapshot(
        blocksCollection,
        (snapshot) => {
          spaceAvailabilityBlockListenerRows.set(
            listenerKey,
            snapshot.docs.map((block) => spaceAvailabilityBlockRowFromData(block.data(), block.id)),
          );
          syncSpaceAvailabilityBlocksFromListenerRows();
          renderRealtime();
        },
        (error) => {
          state.error = error.message;
          spaceAvailabilityBlockListenerRows.delete(listenerKey);
          syncSpaceAvailabilityBlocksFromListenerRows();
          renderRealtime();
        },
      ),
    );
  }
}

function unsubscribeSpaceReservationRequestListeners(): void {
  for (const unsubscribe of unsubscribeSpaceReservationRequests) {
    unsubscribe();
  }
  unsubscribeSpaceReservationRequests = [];
}

function spaceReservationRequestRowFromData(
  data: Record<string, unknown>,
  id: string,
): SpaceReservationRequestRow {
  const requesterSnapshot = isRecord(data.requesterSnapshot) ? data.requesterSnapshot : {};
  const pricingSnapshot = isRecord(data.pricingSnapshot) ? data.pricingSnapshot : {};

  return {
    agreementAccepted: isRecord(data.agreementAcceptance),
    agreementRequired: pricingSnapshot.agreementRequired === true,
    approvedAt: optionalDateFromFirestore(data.approvedAt),
    cancelledAt: optionalDateFromFirestore(data.cancelledAt),
    endsAt: optionalDateFromFirestore(data.endsAt),
    guestCount: typeof data.guestCount === "number" ? data.guestCount : 0,
    id,
    notes: typeof data.notes === "string" ? data.notes : null,
    paymentStatus: typeof data.paymentStatus === "string" ? data.paymentStatus : "notRequired",
    purpose: typeof data.purpose === "string" ? data.purpose : "-",
    pricingSnapshotId:
      typeof pricingSnapshot.pricingSnapshotId === "string"
        ? pricingSnapshot.pricingSnapshotId
        : null,
    pricingTotalCents:
      typeof pricingSnapshot.totalAmountMinor === "number"
        ? pricingSnapshot.totalAmountMinor
        : null,
    rejectedAt: optionalDateFromFirestore(data.rejectedAt),
    requesterEmail: typeof requesterSnapshot.email === "string" ? requesterSnapshot.email : null,
    requesterName:
      typeof requesterSnapshot.displayName === "string" ? requesterSnapshot.displayName : "-",
    requesterPersonId: typeof data.requesterPersonId === "string" ? data.requesterPersonId : "-",
    reservationId: typeof data.reservationId === "string" ? data.reservationId : id,
    receiptId: typeof data.receiptId === "string" ? data.receiptId : null,
    reviewedAt: optionalDateFromFirestore(data.reviewedAt),
    reviewedBy: typeof data.reviewedBy === "string" ? data.reviewedBy : null,
    spaceId: typeof data.spaceId === "string" ? data.spaceId : "-",
    startsAt: optionalDateFromFirestore(data.startsAt),
    status: typeof data.status === "string" ? data.status : "-",
    transactionId: typeof data.transactionId === "string" ? data.transactionId : null,
  };
}

function syncSpaceReservationRequestsFromListenerRows(): void {
  state.spaceReservationRequests = Array.from(spaceReservationRequestListenerRows.values())
    .flat()
    .sort((left, right) => {
      const statusRank = (status: string): number =>
        status === "pending" ? 0 : status === "approved" ? 1 : 2;
      const statusDifference = statusRank(left.status) - statusRank(right.status);
      if (statusDifference !== 0) {
        return statusDifference;
      }
      const leftTime = left.startsAt?.getTime() ?? 0;
      const rightTime = right.startsAt?.getTime() ?? 0;
      return leftTime - rightTime;
    });
}

function connectSpaceReservationRequests(): void {
  unsubscribeSpaceReservationRequestListeners();
  spaceReservationRequestListenerRows = new Map();
  state.spaceReservationRequests = [];

  if (!selectedContextCanManageSpaces()) {
    return;
  }

  for (const space of state.spaces) {
    const listenerKey = space.id;
    const reservationsCollection = collection(
      db,
      "organizations",
      state.organizationId,
      "spaces",
      space.id,
      "reservations",
    );

    unsubscribeSpaceReservationRequests.push(
      onSnapshot(
        reservationsCollection,
        (snapshot) => {
          spaceReservationRequestListenerRows.set(
            listenerKey,
            snapshot.docs.map((reservation) =>
              spaceReservationRequestRowFromData(reservation.data(), reservation.id),
            ),
          );
          syncSpaceReservationRequestsFromListenerRows();
          renderRealtime();
        },
        (error) => {
          state.error = error.message;
          spaceReservationRequestListenerRows.delete(listenerKey);
          syncSpaceReservationRequestsFromListenerRows();
          renderRealtime();
        },
      ),
    );
  }
}

function connectSpaceAvailabilitySlots(): void {
  unsubscribeSpaceSlots?.();
  const slotsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "spaceAvailabilitySlots",
  );

  unsubscribeSpaceSlots = onSnapshot(
    slotsQuery,
    (snapshot) => {
      state.spaceSlots = snapshot.docs
        .map((slot) => {
          const data = slot.data();
          const startsAt = data.startsAt instanceof Timestamp ? data.startsAt.toDate() : undefined;
          const endsAt = data.endsAt instanceof Timestamp ? data.endsAt.toDate() : undefined;
          return {
            id: slot.id,
            amountCents: typeof data.amountCents === "number" ? data.amountCents : 0,
            endsAt,
            label: typeof data.label === "string" ? data.label : slot.id,
            labelAr: typeof data.labelAr === "string" ? data.labelAr : slot.id,
            requesterUid: typeof data.requesterUid === "string" ? data.requesterUid : "-",
            spaceId: typeof data.spaceId === "string" ? data.spaceId : "-",
            startsAt,
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.startsAt?.getTime() ?? 0;
          const rightTime = right.startsAt?.getTime() ?? 0;
          return leftTime - rightTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.spaceSlots = [];
      renderRealtime();
    },
  );
}

function connectSpaceReservationIntents(): void {
  unsubscribeSpaceReservations?.();

  if (!selectedContextCanReadFinance()) {
    state.spaceReservations = [];
    unsubscribeSpaceReservations = undefined;
    return;
  }

  const reservationsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "spaceReservationIntents",
  );

  unsubscribeSpaceReservations = onSnapshot(
    reservationsQuery,
    (snapshot) => {
      state.spaceReservations = snapshot.docs
        .map((reservation) => {
          const data = reservation.data();
          const createdAt =
            data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined;
          return {
            id: reservation.id,
            amountCents: typeof data.amountCents === "number" ? data.amountCents : 0,
            createdAt,
            providerEventId: typeof data.providerEventId === "string" ? data.providerEventId : "-",
            requesterUid: typeof data.requesterUid === "string" ? data.requesterUid : "-",
            slotId: typeof data.slotId === "string" ? data.slotId : "-",
            spaceId: typeof data.spaceId === "string" ? data.spaceId : "-",
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.spaceReservations = [];
      renderRealtime();
    },
  );
}

function connectAnnouncements(): void {
  unsubscribeAnnouncements?.();
  const announcementsQuery = collection(db, "organizations", state.organizationId, "announcements");

  unsubscribeAnnouncements = onSnapshot(
    announcementsQuery,
    (snapshot) => {
      state.announcements = snapshot.docs
        .map((announcement) => {
          const data = announcement.data();
          const expiresAt =
            data.expiresAt instanceof Timestamp ? data.expiresAt.toDate() : undefined;
          const publishedAt =
            data.publishedAt instanceof Timestamp ? data.publishedAt.toDate() : undefined;
          return {
            id: announcement.id,
            body: typeof data.body === "string" ? data.body : "-",
            bodyAr: typeof data.bodyAr === "string" ? data.bodyAr : "-",
            expiresAt,
            createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
            pushNotificationEnabled: data.pushNotificationEnabled === true,
            priority: typeof data.priority === "string" ? data.priority : "-",
            publishedAt,
            status: typeof data.status === "string" ? data.status : "-",
            title: typeof data.title === "string" ? data.title : announcement.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : announcement.id,
            updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.publishedAt?.getTime() ?? 0;
          const rightTime = right.publishedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.announcements = [];
      renderRealtime();
    },
  );
}

function organizationContentMediaReferenceFromValue(
  value: unknown,
): OrganizationContentMediaReference | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const storagePath = typeof record.storagePath === "string" ? record.storagePath : null;
  const altText = typeof record.altText === "string" ? record.altText : null;
  const altTextAr = typeof record.altTextAr === "string" ? record.altTextAr : null;

  if (!storagePath && !altText && !altTextAr) {
    return null;
  }

  return {
    altText,
    altTextAr,
    storagePath,
  };
}

function organizationContentYoutubeReferenceFromValue(
  value: unknown,
): OrganizationContentYoutubeReference | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const canonicalUrl = typeof record.canonicalUrl === "string" ? record.canonicalUrl : null;
  const videoId = typeof record.videoId === "string" ? record.videoId : null;

  if (!canonicalUrl || !videoId) {
    return null;
  }

  return {
    canonicalUrl,
    videoId,
  };
}

function connectImamContent(): void {
  unsubscribeImamContent?.();
  state.imamContents = [];
  state.imamContentImageDownloadUrls = {};
  const contentQuery = query(collection(db, "organizations", state.organizationId, "content"));

  unsubscribeImamContent = onSnapshot(
    contentQuery,
    (snapshot) => {
      state.imamContents = snapshot.docs
        .map((content) => {
          const data = content.data();
          const image = data.image;
          const publishedAt =
            data.publishedAt instanceof Timestamp ? data.publishedAt.toDate() : undefined;
          const imageRecord =
            typeof image === "object" && image !== null && !Array.isArray(image)
              ? (image as Record<string, unknown>)
              : {};

          return {
            body: typeof data.body === "string" ? data.body : "",
            bodyAr: typeof data.bodyAr === "string" ? data.bodyAr : null,
            contentId: typeof data.contentId === "string" ? data.contentId : content.id,
            type: typeof data.type === "string" ? data.type : "imam_profile",
            featured: data.featured === true,
            galleryImage1: organizationContentMediaReferenceFromValue(data.galleryImage1),
            galleryImage2: organizationContentMediaReferenceFromValue(data.galleryImage2),
            galleryImage3: organizationContentMediaReferenceFromValue(data.galleryImage3),
            imageAltText: typeof imageRecord.altText === "string" ? imageRecord.altText : null,
            imageAltTextAr:
              typeof imageRecord.altTextAr === "string" ? imageRecord.altTextAr : null,
            imageStoragePath:
              typeof imageRecord.storagePath === "string" ? imageRecord.storagePath : null,
            organizationId:
              typeof data.organizationId === "string" ? data.organizationId : state.organizationId,
            publishedAt,
            sortOrder: typeof data.sortOrder === "number" ? data.sortOrder : 0,
            status: typeof data.status === "string" ? data.status : "-",
            subtitle: typeof data.subtitle === "string" ? data.subtitle : null,
            subtitleAr: typeof data.subtitleAr === "string" ? data.subtitleAr : null,
            title: typeof data.title === "string" ? data.title : content.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : null,
            updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "-",
            youtube: organizationContentYoutubeReferenceFromValue(data.youtube),
          };
        })
        .filter((content) => content.organizationId === state.organizationId)
        .sort((left, right) => {
          if (left.sortOrder !== right.sortOrder) {
            return left.sortOrder - right.sortOrder;
          }

          return left.contentId.localeCompare(right.contentId);
        });
      void refreshImamContentImageDownloadUrls(state.organizationId, state.imamContents);
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.imamContents = [];
      renderRealtime();
    },
  );
}

async function refreshImamContentImageDownloadUrls(
  organizationId: string,
  contents: ImamContentRow[],
): Promise<void> {
  const previewTargets = contents
    .filter((content) => content.imageStoragePath)
    .map((content) => ({
      contentId: content.contentId,
      storagePath: content.imageStoragePath!,
    }));

  if (previewTargets.length === 0) {
    if (state.organizationId === organizationId) {
      state.imamContentImageDownloadUrls = {};
      renderRealtime();
    }
    return;
  }

  const entries = await Promise.all(
    previewTargets.map(async (target) => {
      try {
        const url = await getDownloadURL(storageRef(storage, target.storagePath));
        return [target.contentId, url] as const;
      } catch {
        return [target.contentId, null] as const;
      }
    }),
  );

  if (state.organizationId !== organizationId) {
    return;
  }

  state.imamContentImageDownloadUrls = Object.fromEntries(entries);
  renderRealtime();
}

function connectTimelineEntries(): void {
  unsubscribeTimelineEntries?.();
  const timelineQuery = collection(db, "organizations", state.organizationId, "timelineEntries");

  unsubscribeTimelineEntries = onSnapshot(
    timelineQuery,
    (snapshot) => {
      state.timelineEntries = snapshot.docs
        .map((entry) => {
          const data = entry.data();
          const publishedAt =
            data.publishedAt instanceof Timestamp ? data.publishedAt.toDate() : undefined;
          return {
            id: entry.id,
            announcementId: typeof data.announcementId === "string" ? data.announcementId : "-",
            body: typeof data.body === "string" ? data.body : "-",
            bodyAr: typeof data.bodyAr === "string" ? data.bodyAr : "-",
            priority: typeof data.priority === "string" ? data.priority : "-",
            publishedAt,
            title: typeof data.title === "string" ? data.title : entry.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : entry.id,
          };
        })
        .sort((left, right) => {
          const leftTime = left.publishedAt?.getTime() ?? 0;
          const rightTime = right.publishedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.timelineEntries = [];
      renderRealtime();
    },
  );
}

function connectEngagementDeliveryLogs(): void {
  unsubscribeEngagementDeliveryLogs?.();
  const deliveryQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "engagementDeliveryLogs",
  );

  unsubscribeEngagementDeliveryLogs = onSnapshot(
    deliveryQuery,
    (snapshot) => {
      state.engagementDeliveryLogs = snapshot.docs
        .map((delivery) => {
          const data = delivery.data();
          return {
            id: delivery.id,
            announcementId: typeof data.announcementId === "string" ? data.announcementId : "-",
            channel: typeof data.channel === "string" ? data.channel : "-",
            providerEventId: typeof data.providerEventId === "string" ? data.providerEventId : "-",
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => left.announcementId.localeCompare(right.announcementId));
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.engagementDeliveryLogs = [];
      renderRealtime();
    },
  );
}

function connectAnnouncementAcknowledgements(): void {
  unsubscribeAnnouncementAcknowledgements?.();
  const acknowledgementsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "announcementAcknowledgements",
  );

  unsubscribeAnnouncementAcknowledgements = onSnapshot(
    acknowledgementsQuery,
    (snapshot) => {
      state.announcementAcknowledgements = snapshot.docs
        .map((acknowledgement) => {
          const data = acknowledgement.data();
          const createdAt =
            data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined;
          return {
            id: acknowledgement.id,
            announcementId: typeof data.announcementId === "string" ? data.announcementId : "-",
            createdAt,
            status: typeof data.status === "string" ? data.status : "-",
            uid: typeof data.uid === "string" ? data.uid : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.createdAt?.getTime() ?? 0;
          const rightTime = right.createdAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.announcementAcknowledgements = [];
      renderRealtime();
    },
  );
}

function connectIntelligenceRequests(): void {
  unsubscribeIntelligenceRequests?.();
  const requestsQuery = collection(
    db,
    "organizations",
    state.organizationId,
    "intelligenceRequests",
  );

  unsubscribeIntelligenceRequests = onSnapshot(
    requestsQuery,
    (snapshot) => {
      state.intelligenceRequests = snapshot.docs
        .map((request) => {
          const data = request.data();
          const requestedAt =
            data.requestedAt instanceof Timestamp ? data.requestedAt.toDate() : undefined;
          const processedAt =
            data.processedAt instanceof Timestamp ? data.processedAt.toDate() : undefined;
          return {
            id: request.id,
            processedAt,
            requestedAt,
            requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "-",
            snapshotId: typeof data.snapshotId === "string" ? data.snapshotId : "-",
            status: typeof data.status === "string" ? data.status : "-",
          };
        })
        .sort((left, right) => {
          const leftTime = left.requestedAt?.getTime() ?? 0;
          const rightTime = right.requestedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.intelligenceRequests = [];
      renderRealtime();
    },
  );
}

function connectIntelligenceSnapshots(): void {
  unsubscribeIntelligenceSnapshots?.();
  const snapshotsQuery = query(
    collection(db, "organizations", state.organizationId, "intelligenceSnapshots"),
    where("snapshotType", "==", "operational_pulse"),
    where("status", "==", "ready"),
  );

  unsubscribeIntelligenceSnapshots = onSnapshot(
    snapshotsQuery,
    (snapshot) => {
      state.intelligenceSnapshots = snapshot.docs
        .map((insight) => {
          const data = insight.data();
          const generatedAt =
            data.generatedAt instanceof Timestamp ? data.generatedAt.toDate() : undefined;
          return {
            id: insight.id,
            acknowledgementCount:
              typeof data.acknowledgementCount === "number" ? data.acknowledgementCount : 0,
            activeSpacesCount:
              typeof data.activeSpacesCount === "number" ? data.activeSpacesCount : 0,
            availableIftarSlotsCount:
              typeof data.availableIftarSlotsCount === "number" ? data.availableIftarSlotsCount : 0,
            availableSpaceSlotsCount:
              typeof data.availableSpaceSlotsCount === "number" ? data.availableSpaceSlotsCount : 0,
            connectedPeopleCount:
              typeof data.connectedPeopleCount === "number" ? data.connectedPeopleCount : 0,
            confirmedDonationTotalCents:
              typeof data.confirmedDonationTotalCents === "number"
                ? data.confirmedDonationTotalCents
                : 0,
            generatedAt,
            publishedAnnouncementsCount:
              typeof data.publishedAnnouncementsCount === "number"
                ? data.publishedAnnouncementsCount
                : 0,
            recommendation: typeof data.recommendation === "string" ? data.recommendation : "-",
            recommendationAr:
              typeof data.recommendationAr === "string" ? data.recommendationAr : "-",
            riskLevel: typeof data.riskLevel === "string" ? data.riskLevel : "-",
            summary: typeof data.summary === "string" ? data.summary : "-",
            summaryAr: typeof data.summaryAr === "string" ? data.summaryAr : "-",
            title: typeof data.title === "string" ? data.title : insight.id,
            titleAr: typeof data.titleAr === "string" ? data.titleAr : insight.id,
            upcomingEventsCount:
              typeof data.upcomingEventsCount === "number" ? data.upcomingEventsCount : 0,
          };
        })
        .sort((left, right) => {
          const leftTime = left.generatedAt?.getTime() ?? 0;
          const rightTime = right.generatedAt?.getTime() ?? 0;
          return rightTime - leftTime;
        });
      renderRealtime();
    },
    (error) => {
      state.error = error.message;
      state.intelligenceSnapshots = [];
      renderRealtime();
    },
  );
}

async function switchContext(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    discoverAuthorizedOrganizations(state.userUid);
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function connectOrganizationAdminPortal(): void {
  const roles = canonicalRoleIdsForSelectedContext();
  const can = (page: string): boolean => canOpenOrganizationPage(page, roles);
  connectOrganizationProfile();
  if (can("settings")) {
    connectPlatformIslamicComponents();
    connectOrganizationProfileRequests();
    connectOrganizationIslamicComponents();
    connectOrganizationPrayerSettings();
    connectBusinessDirectory();
    connectBusinessAffiliations();
    connectOrganizationRoleAssignments();
  }
  if (can("people")) {
    connectOrganizationConnections();
    connectConnectionRequests();
    connectConnectionDecisionRequests();
  }
  if (can("member-support")) {
    void loadOrganizationSupportStaff();
    connectMemberSupportTickets();
    connectShdeedSupportTickets();
  }
  if (useEmulator) connectRealtimeStatus();
  if (can("events")) {
    connectEvents();
    connectEventMedia();
    connectEventRegistrations();
    connectEventAttendance();
  }
  if (selectedContextCanReadFinance()) {
    connectDonations();
    connectDonationRefunds();
    connectCommerceReconciliation();
    connectGivingDestinations();
    connectCommerceStatus();
  }
  if (selectedContextCanReadBilling()) connectOrganizationBilling();
  if (can("ramadan")) {
    connectRamadanModes();
    connectRamadanSetups();
    if (can("settings")) connectRamadanHistory();
    connectIftarSponsorshipConfiguration();
    connectIftarSponsorshipSlots();
    connectIftarSponsorshipIntents();
    if (selectedContextCanReadFinance()) connectIftarSponsorshipFinance();
    connectIftarTransfers();
    if (can("settings")) connectRamadanAdminNotes();
  }
  if (can("spaces")) {
    connectSpaces();
    connectSpaceAvailabilitySlots();
    connectSpaceReservationIntents();
  }
  if (can("volunteers")) connectVolunteerOpportunities();
  if (can("content")) {
    connectAnnouncements();
    connectImamContent();
    connectTimelineEntries();
    connectEngagementDeliveryLogs();
    connectAnnouncementAcknowledgements();
  }
  if (release1OrganizationIntelligenceEnabled && can("settings")) {
    connectIntelligenceRequests();
    connectIntelligenceSnapshots();
  } else {
    unsubscribeIntelligenceRequests?.();
    unsubscribeIntelligenceRequests = undefined;
    unsubscribeIntelligenceSnapshots?.();
    unsubscribeIntelligenceSnapshots = undefined;
    state.intelligenceRequests = [];
    state.intelligenceSnapshots = [];
  }
}

async function updateStatus(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const organization = selectedOrganization();
    const person = selectedPerson();
    const ref = doc(db, "organizations", state.organizationId, "demoRealtime", "status");

    await updateDoc(ref, {
      message: `${organization.displayName} synced at ${new Date().toLocaleTimeString()}`,
      organizationId: state.organizationId,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
      version: increment(1),
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveBusinessAffiliationFromForm(): Promise<boolean> {
  const form = document.querySelector<HTMLFormElement>("#business-affiliation-form");
  if (!form) {
    return false;
  }
  const data = new FormData(form);
  const businessId = String(data.get("businessId") ?? "");
  const status = String(data.get("status") ?? "active");
  const affiliationType = String(data.get("affiliationType") ?? "listed");
  const displayOrder = Number(data.get("displayOrder") ?? 100);

  state.busy = true;
  state.error = "";
  state.businessDirectoryMessage = "";
  render();

  try {
    await postAuthenticatedFunction("setBusinessOrganizationAffiliation", {
      affiliationType,
      businessId,
      displayOrder,
      organizationId: state.organizationId,
      status,
    });
    state.businessDirectoryMessage = `Saved ${businessId} affiliation for ${state.organizationId}.`;
    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function changeOrganizationRoleFromForm(action: "assign" | "revoke"): Promise<void> {
  const form = document.querySelector<HTMLFormElement>("#role-management-form");
  if (!form) {
    return;
  }

  const data = new FormData(form);
  const targetPersonId = String(data.get("targetPersonId") ?? "").trim();
  const roleId = String(data.get("roleId") ?? "").trim();
  if (!targetPersonId || !isOrganizationRoleId(roleId)) {
    state.error = "Choose a connected person and fixed V1 role.";
    render();
    return;
  }

  state.busy = true;
  state.error = "";
  state.roleManagementMessage = "";
  render();

  try {
    await postAuthenticatedFunction(
      action === "assign" ? "assignOrganizationRoles" : "revokeOrganizationRoles",
      {
        organizationId: state.organizationId,
        roleIds: [roleId],
        targetPersonId,
      },
    );
    state.roleManagementMessage = `${action === "assign" ? "Assigned" : "Revoked"} ${roleLabel(
      roleId,
    )} for ${targetPersonId}.`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function openPeopleRoleWindow(personId: string): void {
  if (!peopleRowForPersonId(personId)) {
    state.error = "Selected person is not connected to this organization.";
    render();
    return;
  }

  state.peopleManagerSelectedPersonId = personId;
  activeTaskWindowId = "people-role-editor-form";
  render();
}

async function savePeopleRolesFromForm(form: HTMLFormElement): Promise<boolean> {
  const targetPersonId = String(new FormData(form).get("targetPersonId") ?? "").trim();
  const roleIds = Array.from(
    form.querySelectorAll<HTMLInputElement>("input[name='roleIds']:checked"),
  )
    .map((input) => input.value)
    .filter(isOrganizationRoleId);
  const checkedCount = form.querySelectorAll<HTMLInputElement>(
    "input[name='roleIds']:checked",
  ).length;

  if (!targetPersonId || checkedCount !== roleIds.length) {
    state.error = "Choose a connected person and fixed V1 roles.";
    render();
    return false;
  }

  const existingAssignment = roleAssignmentForPerson(targetPersonId);
  state.busy = true;
  state.error = "";
  state.roleManagementMessage = "";
  render();

  try {
    if (roleIds.length > 0) {
      await postAuthenticatedFunction("assignOrganizationRoles", {
        organizationId: state.organizationId,
        roleIds,
        targetPersonId,
      });
      state.roleManagementMessage = `Saved roles for ${targetPersonId}.`;
    } else if (existingAssignment?.status === "active" && existingAssignment.roleIds.length > 0) {
      await postAuthenticatedFunction("revokeOrganizationRoles", {
        organizationId: state.organizationId,
        roleIds: existingAssignment.roleIds,
        targetPersonId,
      });
      state.roleManagementMessage = `Cleared privileged roles for ${targetPersonId}.`;
    } else {
      state.roleManagementMessage = `${targetPersonId} already has connected-person access only.`;
    }

    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function disablePeopleAccountFromButton(button: HTMLButtonElement): Promise<void> {
  const targetPersonId = button.dataset.peopleDisable ?? "";
  const row = peopleRowForPersonId(targetPersonId);
  if (!row) {
    state.error = "Selected person is not connected to this organization.";
    render();
    return;
  }

  const personLabel = peopleRowPersonLabel(row);
  const confirmed = window.confirm(
    `Disable ${personLabel}? This blocks sign-in to the mobile app and suspends this organization connection.`,
  );
  if (!confirmed) {
    return;
  }

  state.busy = true;
  state.error = "";
  state.roleManagementMessage = "";
  render();

  try {
    await postAuthenticatedFunction("disableOrganizationPersonAccount", {
      organizationId: state.organizationId,
      reason: "Disabled from Org Admin People",
      targetPersonId,
    });
    state.roleManagementMessage = `Disabled ${personLabel}. Mobile sign-in is blocked.`;
    activeTaskWindowId = null;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function postAuthenticatedFunction(
  functionName: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  await signInSelectedPerson();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("Sign in before using staff actions.");
  }

  const token = await currentUser.getIdToken();
  const response = await backendFetch(
    `${environmentConfig.apiBaseUrl.replace(/\/+$/u, "")}/${functionName}`,
    {
      body: JSON.stringify(body),
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "POST",
    },
  );
  const responseBody = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    const error = typeof responseBody.error === "string" ? responseBody.error : response.statusText;
    throw new Error(error);
  }

  return responseBody;
}

async function postCurrentUserAuthenticatedFunction(
  functionName: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("Sign in before updating your profile.");
  }

  const token = await currentUser.getIdToken();
  const response = await backendFetch(
    `${environmentConfig.apiBaseUrl.replace(/\/+$/u, "")}/${functionName}`,
    {
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "POST",
    },
  );
  const responseBody = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    const error = typeof responseBody.error === "string" ? responseBody.error : response.statusText;
    throw new Error(error);
  }

  return responseBody;
}

async function uploadOrganizationProfileLogo(file: File): Promise<string> {
  validateOrganizationLogoFile(file);
  const dataBase64 = await fileToBase64(file);
  const response = await postAuthenticatedFunction("uploadOrganizationBrandingLogo", {
    contentType: file.type || "image/png",
    dataBase64,
    fileName: file.name,
    organizationId: state.organizationId,
  });
  const storagePath = typeof response.storagePath === "string" ? response.storagePath : "";

  if (!storagePath) {
    throw new Error("Logo upload did not return a Storage path.");
  }

  return storagePath;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener("load", () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const [, dataBase64] = result.split(",", 2);

      if (!dataBase64) {
        reject(new Error("Logo file could not be read."));
        return;
      }

      resolve(dataBase64);
    });
    reader.addEventListener("error", () => {
      reject(new Error("Logo file could not be read."));
    });
    reader.readAsDataURL(file);
  });
}

async function startStripeConnectOnboarding(): Promise<void> {
  state.busy = true;
  state.error = "";
  state.commerceMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("startStripeConnectOnboarding", {
      organizationId: state.organizationId,
    });
    const url = typeof result.url === "string" ? result.url : "";

    if (!url) {
      throw new Error("Stripe onboarding URL was not returned.");
    }

    state.commerceMessage = "Opening Stripe onboarding.";
    window.location.assign(url);
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function refreshStripeConnectStatus(): Promise<void> {
  state.busy = true;
  state.error = "";
  state.commerceMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("refreshStripeConnectStatus", {
      organizationId: state.organizationId,
    });
    const readiness = typeof result.readiness === "string" ? result.readiness : "unknown";
    state.commerceMessage = `Stripe Connect status refreshed: ${readiness}.`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function requestDonationRefundFromButton(button: HTMLButtonElement): Promise<void> {
  const transactionId = button.dataset.refundTransactionId ?? "";
  const refundMode = button.dataset.refundMode === "full" ? "full" : "partial";
  const donation = state.donations.find((candidate) => candidate.id === transactionId);
  if (!donation) {
    state.error = "Donation transaction could not be found.";
    render();
    return;
  }

  const amountInput = button
    .closest("li")
    ?.querySelector<HTMLInputElement>("[data-refund-amount-input]");
  const amountMinor =
    refundMode === "full" ? null : Math.round(Number.parseFloat(amountInput?.value ?? "") * 100);

  if (amountMinor !== null && (!Number.isSafeInteger(amountMinor) || amountMinor <= 0)) {
    state.error = "Enter a refund amount greater than zero.";
    render();
    return;
  }

  const displayAmount =
    amountMinor === null
      ? formatAmount(donation.remainingRefundableAmountMinor)
      : formatAmount(amountMinor);
  if (!window.confirm(`Request ${displayAmount} refund for ${transactionId}?`)) {
    return;
  }

  state.busy = true;
  state.error = "";
  state.commerceMessage = "";
  render();

  try {
    const currentUser = auth.currentUser;
    const idempotencyKey = [
      "admin-refund",
      currentUser?.uid ?? state.userUid,
      state.organizationId,
      transactionId,
      amountMinor ?? "full",
      Date.now(),
    ].join(":");
    const result = await postAuthenticatedFunction("requestDonationRefund", {
      amountMinor,
      idempotencyKey,
      organizationId: state.organizationId,
      reason: "requested_by_organization_owner",
      transactionId,
    });
    const refundId = typeof result.refundId === "string" ? result.refundId : transactionId;
    const status = typeof result.status === "string" ? result.status : "requested";
    state.commerceMessage = `Refund ${status}: ${refundId}.`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function reviewSpaceReservationFromButton(button: HTMLButtonElement): Promise<void> {
  const action = button.dataset.spaceReservationAction;
  const reservationId = button.dataset.spaceReservationId ?? "";
  const spaceId = button.dataset.spaceId ?? "";
  const functionName =
    action === "approve"
      ? "approveSpaceReservation"
      : action === "reject"
        ? "rejectSpaceReservation"
        : action === "cancel"
          ? "cancelSpaceReservation"
          : "";

  if (!functionName || !reservationId || !spaceId) {
    state.error = "Space reservation action is missing its reservation context.";
    render();
    return;
  }

  const reason =
    action === "approve"
      ? null
      : window.prompt(
          action === "reject"
            ? "Reason for rejecting this space request?"
            : "Reason for cancelling this space reservation?",
          "",
        );
  if (reason === null && action !== "approve") {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    const result = await postAuthenticatedFunction(functionName, {
      organizationId: state.organizationId,
      reason,
      reservationId,
      spaceId,
    });
    const status = typeof result.status === "string" ? result.status : action;
    state.status = `Space reservation ${status}: ${reservationId}.`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function runCommerceReconciliation(targetTransactionId: string | null = null): Promise<void> {
  state.busy = true;
  state.error = "";
  state.commerceMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("runCommerceReconciliation", {
      organizationId: state.organizationId,
      targetTransactionId,
    });
    const status = typeof result.status === "string" ? result.status : "completed";
    const mismatchCount = typeof result.mismatchCount === "number" ? result.mismatchCount : 0;
    state.commerceMessage = `Reconciliation ${status}: ${mismatchCount} mismatch(es).`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function submitQrCheckIn(): Promise<void> {
  state.busy = true;
  state.error = "";
  state.eventCheckInMessage = "";
  render();

  try {
    const credential = state.eventCheckInCredential.trim();
    if (!credential) {
      throw new Error("Enter the credential decoded from the registrant QR.");
    }

    const result = await postAuthenticatedFunction("checkInEventRegistration", {
      credential,
      eventId: state.eventEditor.eventId.trim(),
      method: "qr",
      organizationId: state.organizationId,
      stationLabel: nullableFormText(state.eventCheckInStationLabel),
    });
    state.eventCheckInCredential = "";
    state.eventCheckInMessage = checkInResultMessage(result);
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function submitManualCheckIn(registrationId: string): Promise<void> {
  state.busy = true;
  state.error = "";
  state.eventCheckInMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("checkInEventRegistration", {
      credential: null,
      eventId: state.eventEditor.eventId.trim(),
      method: "manual",
      organizationId: state.organizationId,
      registrationId,
      stationLabel: nullableFormText(state.eventCheckInStationLabel),
    });
    state.eventCheckInMessage = checkInResultMessage(result);
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function promoteWaitlistedRegistration(registrationId: string): Promise<void> {
  state.busy = true;
  state.error = "";
  state.eventCheckInMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("promoteEventWaitlistRegistration", {
      eventId: state.eventEditor.eventId.trim(),
      organizationId: state.organizationId,
      registrationId,
    });
    const status = typeof result.status === "string" ? result.status : "unknown";
    const confirmedCount = typeof result.confirmedCount === "number" ? result.confirmedCount : "-";
    const waitlistCount = typeof result.waitlistCount === "number" ? result.waitlistCount : "-";
    state.eventCheckInMessage = `${shellCopy[state.locale].eventWaitlistPromoted}: ${registrationId} (${status}). ${shellCopy[state.locale].eventRegistrationCount}: ${confirmedCount}. ${shellCopy[state.locale].eventWaitlistCount}: ${waitlistCount}.`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function sendEventCommunicationFromEditor(input: {
  audience: string;
  channels: string[];
  eventId?: string;
  subject: string;
  message: string;
  testRecipientEmail?: string;
  type?: EventCommunicationDeliveryType;
}): Promise<void> {
  if (state.busy) return;
  const scope = currentModeScope();
  const revision = eventEditorRevision;
  state.busy = true;
  state.error = "";
  state.eventCheckInMessage = "";
  state.eventEmailTestStatus = null;
  renderPreservingTaskWindowScroll();

  try {
    const result = await postAuthenticatedFunction("sendEventCommunication", {
      audience: input.audience,
      channels: input.channels.filter((channel) => channel === "push" || channel === "email"),
      eventId: input.eventId ?? state.eventEditor.eventId.trim(),
      idempotencyKey: `${Date.now()}`,
      message: input.message,
      organizationId: state.organizationId,
      subject: input.subject,
      ...(input.audience === "test"
        ? { previewEventType: state.eventEditor.eventType, previewLocale: state.locale }
        : {}),
      ...(input.testRecipientEmail ? { testRecipientEmail: input.testRecipientEmail } : {}),
      type: input.type ?? "materialUpdate",
    });
    if (scope !== currentModeScope() || revision !== eventEditorRevision) return;
    const feedback = eventDeliveryFeedback(result, state.locale, input.audience === "test");
    if (input.audience === "test") state.eventEmailTestStatus = feedback;
    else state.eventCheckInMessage = feedback.message;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (scope !== currentModeScope() || revision !== eventEditorRevision) return;
    if (input.audience === "test")
      state.eventEmailTestStatus = { message: eventEmailCopy[state.locale].failed, error: true };
    else state.error = message;
  } finally {
    state.busy = false;
    renderPreservingTaskWindowScroll();
  }
}

async function exportEventAttendeesFromEditor(input: { includeResponses: boolean }): Promise<void> {
  state.busy = true;
  state.error = "";
  state.eventCheckInMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("exportEventAttendees", {
      eventId: state.eventEditor.eventId.trim(),
      includeContactInformation: false,
      includeResponses: input.includeResponses,
      organizationId: state.organizationId,
    });
    const csv = typeof result.csv === "string" ? result.csv : "";
    const recordCount = typeof result.recordCount === "number" ? result.recordCount : 0;
    if (csv) {
      downloadTextFile(
        `${state.eventEditor.eventId.trim() || "event"}-attendees.csv`,
        csv,
        "text/csv;charset=utf-8",
      );
    }
    state.eventCheckInMessage = `Attendee export created: ${recordCount} registration record(s).`;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function downloadTextFile(fileName: string, content: string, contentType: string): void {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function safeDownloadFileStem(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "download"
  );
}

function exportIftarSponsorshipCalendar(): void {
  const organization = selectedOrganization();
  const days = [...state.iftarSponsorshipDays].sort((left, right) => {
    if (left.ramadanDay !== right.ramadanDay) {
      return left.ramadanDay - right.ramadanDay;
    }

    return left.gregorianDate.localeCompare(right.gregorianDate);
  });
  if (days.length === 0) {
    state.error = "Build the Ramadan sponsorship calendar before exporting sponsors.";
    render();
    return;
  }

  const loadedSponsorRamadanId = state.sponsorships.find((sponsorship) =>
    days.some((day) => day.id === sponsorship.iftarDayId),
  )?.ramadanId;
  const ramadanId =
    state.iftarSponsorshipDefaultsEditor.ramadanId.trim() || loadedSponsorRamadanId || "ramadan";
  const generatedAt = new Date();
  const sponsorsByDay = new Map<string, IftarSponsorshipAdminRow[]>();
  for (const sponsorship of state.sponsorships) {
    if (sponsorship.ramadanId !== ramadanId) {
      continue;
    }
    const existing = sponsorsByDay.get(sponsorship.iftarDayId) ?? [];
    existing.push(sponsorship);
    sponsorsByDay.set(sponsorship.iftarDayId, existing);
  }
  for (const sponsors of sponsorsByDay.values()) {
    sponsors.sort((left, right) => {
      const leftTime = left.createdAt?.getTime() ?? 0;
      const rightTime = right.createdAt?.getTime() ?? 0;
      return leftTime - rightTime || left.safeSponsorLabel.localeCompare(right.safeSponsorLabel);
    });
  }

  const confirmedSpots = days.reduce((total, day) => total + day.confirmedSpots, 0);
  const reservedSpots = days.reduce((total, day) => total + day.reservedSpots, 0);
  const configuredSpots = days.reduce((total, day) => total + day.capacitySpots, 0);
  const sponsorRecordCount = Array.from(sponsorsByDay.values()).reduce(
    (total, sponsors) => total + sponsors.length,
    0,
  );
  const firstDay = days[0]!;
  const lastDay = days[days.length - 1]!;
  const dateRange =
    days.length > 1
      ? `${firstDay.gregorianDate} to ${lastDay.gregorianDate}`
      : firstDay.gregorianDate;
  const fileStem = `${safeDownloadFileStem(organization.displayName)}-${safeDownloadFileStem(
    ramadanId,
  )}-iftar-sponsors`;
  const htmlFileName = `${fileStem}.html`;
  const csvFileName = `${fileStem}.csv`;
  const sponsorRows = days
    .map((day) => {
      const sponsors = sponsorsByDay.get(day.id) ?? [];
      const sponsorList =
        sponsors.length === 0
          ? `<span class="empty">No sponsors yet</span>`
          : `<table class="nested">
              <thead>
                <tr>
                  <th>Sponsor</th>
                  <th>Booked</th>
                  <th>Spots</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Cost</th>
                  <th>Spot details</th>
                </tr>
              </thead>
              <tbody>
                ${sponsors
                  .map((sponsorship) => {
                    const bookedAt = sponsorship.createdAt
                      ? sponsorship.createdAt.toLocaleString(state.locale)
                      : "-";
                    const sponsorName =
                      sponsorship.publicDisplayNameSnapshot || sponsorship.safeSponsorLabel;
                    return `<tr>
                      <td>
                        <strong>${escapeHtml(sponsorName)}</strong>
                        <small>${escapeHtml(sponsorship.recognitionPreference)}</small>
                      </td>
                      <td>${escapeHtml(bookedAt)}</td>
                      <td>${sponsorship.quantitySpots}</td>
                      <td>${escapeHtml(sponsorship.status)}</td>
                      <td>${escapeHtml(iftarSponsorshipPaymentSummary(sponsorship))}</td>
                      <td>${escapeHtml(iftarSponsorshipCostSummary(sponsorship))}</td>
                      <td>${escapeHtml(iftarSponsorshipSpotSummary(sponsorship))}</td>
                    </tr>`;
                  })
                  .join("")}
              </tbody>
            </table>`;
      const availableSpots = availableIftarSponsorshipSpots(day);
      return `<tr>
        <td>
          <strong>${escapeHtml(titleCaseWeekday(day.weekday))}</strong>
          <span>Ramadan day ${day.ramadanDay}</span>
          <small>${escapeHtml(day.gregorianDate)}</small>
        </td>
        <td><span class="pill">${escapeHtml(day.sponsorshipEnabled ? day.status : "disabled")}</span></td>
        <td>
          <strong>${availableSpots} available</strong>
          <small>${day.capacitySpots} configured, ${day.reservedSpots} reserved, ${day.confirmedSpots} confirmed</small>
        </td>
        <td>${sponsorList}</td>
        <td>${day.publicNotes ? escapeHtml(day.publicNotes) : ""}</td>
      </tr>`;
    })
    .join("");
  const html = `<!doctype html>
<html lang="${escapeHtml(state.locale)}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(organization.displayName)} ${escapeHtml(ramadanId)} Iftar Sponsors</title>
  <style>
    :root { color: #123c3a; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
    body { background: #f6f7f3; margin: 0; padding: 32px; }
    main { background: #ffffff; border: 1px solid #d8dfd4; border-radius: 8px; box-shadow: 0 12px 32px rgba(18, 60, 58, 0.08); margin: 0 auto; max-width: 1180px; overflow: hidden; }
    header { background: #0a6f61; color: #ffffff; padding: 28px 32px; }
    h1 { font-size: 28px; line-height: 1.15; margin: 0 0 8px; }
    h2 { font-size: 16px; margin: 0; opacity: 0.88; }
    .summary { display: grid; gap: 12px; grid-template-columns: repeat(4, minmax(0, 1fr)); padding: 22px 32px; }
    .metric { background: #f2f5ef; border: 1px solid #dfe7db; border-radius: 8px; padding: 14px; }
    .metric strong { display: block; font-size: 22px; }
    .metric span, small { color: #4c625d; display: block; font-size: 12px; margin-top: 4px; }
    .table-wrap { overflow-x: auto; padding: 0 32px 32px; }
    table { border-collapse: collapse; inline-size: 100%; }
    th { background: #eff4ec; color: #2c4742; font-size: 11px; letter-spacing: 0.05em; padding: 10px; text-align: left; text-transform: uppercase; }
    td { border-top: 1px solid #e3e9df; padding: 12px 10px; vertical-align: top; }
    td > strong, td > span { display: block; }
    .nested { border: 1px solid #e3e9df; border-radius: 8px; overflow: hidden; }
    .nested th { background: #f8faf6; }
    .nested td { padding: 8px; }
    .empty { color: #6a7d78; font-style: italic; }
    .pill { background: #dff1eb; border-radius: 999px; color: #075b51; display: inline-block; font-size: 12px; font-weight: 700; padding: 4px 9px; }
    footer { border-top: 1px solid #e3e9df; color: #5d706b; font-size: 12px; padding: 16px 32px; }
    @media print { body { background: #fff; padding: 0; } main { border: 0; box-shadow: none; } }
    @media (max-width: 760px) { body { padding: 12px; } .summary { grid-template-columns: 1fr 1fr; padding: 16px; } .table-wrap { padding: 0 16px 16px; } header { padding: 22px 16px; } }
  </style>
</head>
<body>
  <main>
    <header>
      <h1>${escapeHtml(organization.displayName)} Iftar Sponsors</h1>
      <h2>${escapeHtml(ramadanId)} · ${escapeHtml(dateRange)}</h2>
    </header>
    <section class="summary" aria-label="Export summary">
      <div class="metric"><strong>${days.length}</strong><span>Ramadan days</span></div>
      <div class="metric"><strong>${configuredSpots}</strong><span>Configured spots</span></div>
      <div class="metric"><strong>${confirmedSpots}</strong><span>Confirmed spots</span></div>
      <div class="metric"><strong>${sponsorRecordCount}</strong><span>Sponsor records (${reservedSpots} reserved spots)</span></div>
    </section>
    <section class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Status</th>
            <th>Capacity</th>
            <th>Sponsors</th>
            <th>Mobile day message</th>
          </tr>
        </thead>
        <tbody>${sponsorRows}</tbody>
      </table>
    </section>
    <footer>Generated from Shdeed Org Admin on ${escapeHtml(
      generatedAt.toLocaleString(state.locale),
    )}. This export uses the admin calendar currently loaded in the browser.</footer>
  </main>
</body>
</html>`;

  const csvRows = [
    [
      "iftarDayId",
      "gregorianDate",
      "ramadanDay",
      "dayStatus",
      "availableSpots",
      "capacitySpots",
      "reservedSpots",
      "confirmedSpots",
      "sponsorshipId",
      "sponsor",
      "quantitySpots",
      "sponsorshipStatus",
      "paymentStatus",
      "reservationStatus",
      "paymentSource",
      "totalPaymentAmount",
      "costSummary",
      "spotId",
      "hostingMethod",
      "cuisine",
      "recognitionNames",
      "privateNote",
    ],
    ...days.flatMap((day) => {
      const sponsors = sponsorsByDay.get(day.id) ?? [];
      const availableSpots = availableIftarSponsorshipSpots(day);
      if (sponsors.length === 0) {
        return [
          [
            day.id,
            day.gregorianDate,
            String(day.ramadanDay),
            day.status,
            String(availableSpots),
            String(day.capacitySpots),
            String(day.reservedSpots),
            String(day.confirmedSpots),
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ],
        ];
      }
      return sponsors.flatMap((sponsorship) => {
        const details =
          sponsorship.spotDetails.length > 0
            ? sponsorship.spotDetails
            : [
                {
                  cuisineId: null,
                  hostingMethod: null,
                  note: null,
                  otherCuisineText: null,
                  recognitionNames: [],
                  spotId: "",
                },
              ];
        return details.map((spot) => [
          day.id,
          day.gregorianDate,
          String(day.ramadanDay),
          day.status,
          String(availableSpots),
          String(day.capacitySpots),
          String(day.reservedSpots),
          String(day.confirmedSpots),
          sponsorship.sponsorshipId,
          sponsorship.publicDisplayNameSnapshot || sponsorship.safeSponsorLabel,
          String(sponsorship.quantitySpots),
          sponsorship.status,
          sponsorship.paymentStatus,
          sponsorship.reservationStatus,
          sponsorship.paymentSource,
          sponsorship.totalPaymentAmountMinor === null
            ? ""
            : formatAmount(sponsorship.totalPaymentAmountMinor),
          iftarSponsorshipCostSummary(sponsorship),
          spot.spotId,
          spot.hostingMethod || "",
          spot.cuisineId === "other" && spot.otherCuisineText
            ? spot.otherCuisineText
            : spot.cuisineId || "",
          spot.recognitionNames.join("; "),
          spot.note || "",
        ]);
      });
    }),
  ];
  const csv = csvRows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");

  downloadTextFile(htmlFileName, html, "text/html;charset=utf-8");
  downloadTextFile(csvFileName, csv, "text/csv;charset=utf-8");
  state.iftarSponsorshipMessage = `Sponsor calendar export downloaded: ${htmlFileName} and ${csvFileName}.`;
  state.error = "";
  render();
}

function checkInResultMessage(result: Record<string, unknown>): string {
  const copy = shellCopy[state.locale];
  const outcome = typeof result.outcome === "string" ? result.outcome : "unknown";
  const status = typeof result.status === "string" ? result.status : "unknown";
  const count = typeof result.checkedInCount === "number" ? result.checkedInCount : "-";
  const method = typeof result.method === "string" ? result.method : "-";
  const registrationId =
    typeof result.registrationId === "string" ? result.registrationId : "unknown";
  const label =
    outcome === "already_checked_in" ? copy.eventAlreadyCheckedIn : copy.eventCheckInSubmit;

  return `${label}: ${registrationId} (${method}, ${status}). ${copy.eventAttendanceCount}: ${count}.`;
}

function nullableFormText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

async function publishEvent(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const organization = selectedOrganization();
    const event = demoCommunityEvents.find(
      (candidate) => candidate.organizationId === state.organizationId,
    );

    if (!event) {
      throw new Error(`Missing development event fixture for ${state.organizationId}`);
    }

    const eventRef = doc(db, "organizations", state.organizationId, "events", event.eventId);
    const existingEvent = await getDoc(eventRef);
    const existingData = existingEvent.data();
    const createdAt = existingEvent.exists() ? existingEvent.data().createdAt : serverTimestamp();
    const createdBy = existingEvent.exists()
      ? typeof existingEvent.data().createdBy === "string"
        ? existingEvent.data().createdBy
        : person.uid
      : person.uid;
    const publishedAt =
      existingData?.status === "published" && existingData.publishedAt
        ? existingData.publishedAt
        : serverTimestamp();
    const notificationIntentId = existingEvent.exists()
      ? typeof existingData?.notificationIntentId === "string"
        ? existingData.notificationIntentId
        : null
      : null;
    const notificationIntentStatus = existingEvent.exists()
      ? typeof existingData?.notificationIntentStatus === "string"
        ? existingData.notificationIntentStatus
        : "none"
      : "none";
    const registrationConfirmedCount =
      existingEvent.exists() && typeof existingData?.registrationConfirmedCount === "number"
        ? existingData.registrationConfirmedCount
        : event.registrationConfirmedCount;
    const waitlistCount =
      existingEvent.exists() && typeof existingData?.waitlistCount === "number"
        ? existingData.waitlistCount
        : 0;
    const attendanceCheckedInCount =
      existingEvent.exists() && typeof existingData?.attendanceCheckedInCount === "number"
        ? existingData.attendanceCheckedInCount
        : event.attendanceCheckedInCount;
    const timestamp = new Date().toLocaleTimeString();

    await setDoc(eventRef, {
      allDay: event.allDay,
      archivedAt: null,
      campusId: event.campusId,
      campusNameSnapshot: event.campusNameSnapshot,
      description: event.description,
      descriptionAr: event.descriptionAr,
      endsAt: Timestamp.fromDate(new Date(event.endsAt)),
      eventId: event.eventId,
      image: event.image,
      locationName: event.locationName,
      registrationCancellationAllowed: event.registrationCancellationAllowed,
      registrationCapacity: event.registrationCapacity,
      registrationClosesAt: event.registrationClosesAt
        ? Timestamp.fromDate(new Date(event.registrationClosesAt))
        : null,
      registrationConfirmedCount,
      registrationEnabled: event.registrationEnabled,
      registrationOpensAt: event.registrationOpensAt
        ? Timestamp.fromDate(new Date(event.registrationOpensAt))
        : null,
      waitlistCount,
      waitlistEnabled:
        typeof existingData?.waitlistEnabled === "boolean" ? existingData.waitlistEnabled : false,
      attendanceCheckedInCount,
      notificationIntentId,
      notificationIntentStatus,
      notificationRequestedOnPublish: event.notificationRequestedOnPublish,
      organizationId: state.organizationId,
      publishedAt,
      schemaVersion: organizationEventSchemaVersion,
      source: event.source,
      startsAt: Timestamp.fromDate(new Date(event.startsAt)),
      status: "published",
      title: event.title,
      titleAr: event.titleAr,
      summary: `${event.summary} Updated from ${organization.displayName} admin at ${timestamp}.`,
      summaryAr: `${event.summaryAr} تم التحديث من بوابة ${organization.displayName} في ${timestamp}.`,
      timezone: event.timezone,
      visibility: event.visibility,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveEventFromEditor(): Promise<void> {
  syncEventEditorInputs();
  syncEventEmailTemplateInputs();
  const revision = eventEditorRevision;
  const closeWizardOnSuccess = activeTaskWindowId === "event-editor-form";
  let savedSuccessfully = false;
  state.busy = true;
  state.error = "";
  state.eventEditorError = "";
  state.eventEditorMessage = "";
  renderPreservingTaskWindowScroll();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateEventEditorInput(state.eventEditor, state.organizationId);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    let image = result.draft.image;
    const pendingMedia = await uploadPendingEventMedia({
      eventId: result.draft.eventId,
      existingMedia: state.eventMedia,
      imageAltText: result.draft.image.altText,
      imageAltTextAr: result.draft.image.altTextAr,
      imageFiles:
        state.eventImageFiles.length > 0
          ? state.eventImageFiles
          : state.eventImageFile
            ? [state.eventImageFile]
            : [],
      personId: person.uid,
      videoFile: state.eventVideoFile,
    });

    if (pendingMedia.coverImagePath && !image.storagePath) {
      image = {
        ...image,
        storagePath: pendingMedia.coverImagePath,
      };
    }

    const eventRef = doc(db, "organizations", state.organizationId, "events", result.draft.eventId);
    const existingEvent = await getDoc(eventRef);
    const existingData = existingEvent.data();
    const createdAt = existingEvent.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingEvent.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;
    const publishedAt =
      result.draft.status === "published"
        ? existingData?.status === "published" && existingData?.publishedAt
          ? existingData.publishedAt
          : serverTimestamp()
        : null;
    const archivedAt =
      result.draft.status === "archived" || result.draft.status === "cancelled"
        ? (existingData?.status === "archived" || existingData?.status === "cancelled") &&
          existingData?.archivedAt
          ? existingData.archivedAt
          : serverTimestamp()
        : null;
    const eventRequiresPublishedAt =
      result.draft.status === "published" ||
      result.draft.status === "registration_closed" ||
      result.draft.status === "completed";
    const normalizedPublishedAt = eventRequiresPublishedAt
      ? existingData?.publishedAt || serverTimestamp()
      : publishedAt;
    const notificationIntentId =
      typeof existingData?.notificationIntentId === "string"
        ? existingData.notificationIntentId
        : null;
    const notificationIntentStatus =
      typeof existingData?.notificationIntentStatus === "string"
        ? existingData.notificationIntentStatus
        : "none";
    const registrationConfirmedCount =
      typeof existingData?.registrationConfirmedCount === "number"
        ? existingData.registrationConfirmedCount
        : result.draft.registrationConfirmedCount;
    const waitlistCount =
      typeof existingData?.waitlistCount === "number"
        ? existingData.waitlistCount
        : result.draft.waitlistCount;
    const attendanceCheckedInCount =
      typeof existingData?.attendanceCheckedInCount === "number"
        ? existingData.attendanceCheckedInCount
        : result.draft.attendanceCheckedInCount;

    await setDoc(eventRef, {
      allDay: result.draft.allDay,
      archivedAt,
      campusId: result.draft.campusId,
      campusNameSnapshot: result.draft.campusNameSnapshot,
      description: result.draft.description,
      descriptionAr: result.draft.descriptionAr,
      endsAt: Timestamp.fromDate(result.draft.endsAt),
      eventId: result.draft.eventId,
      eventType: result.draft.eventType,
      image,
      addressLine1: result.draft.addressLine1,
      coverMediaId:
        pendingMedia.coverMediaId ??
        (typeof existingData?.coverMediaId === "string" ? existingData.coverMediaId : null),
      locationType: result.draft.locationType,
      locationName: result.draft.locationName,
      onlineUrl: result.draft.onlineUrl,
      registrationMode: result.draft.registrationMode,
      registrationCancellationAllowed: result.draft.registrationCancellationAllowed,
      guestRegistrationEnabled: result.draft.guestRegistrationEnabled,
      dependentRegistrationEnabled: result.draft.dependentRegistrationEnabled,
      registrationCapacity: result.draft.registrationCapacity,
      registrationClosesAt: result.draft.registrationClosesAt
        ? Timestamp.fromDate(result.draft.registrationClosesAt)
        : null,
      registrationConfirmedCount,
      registrationEnabled: result.draft.registrationEnabled,
      registrationOpensAt: result.draft.registrationOpensAt
        ? Timestamp.fromDate(result.draft.registrationOpensAt)
        : null,
      registrationQuestionCount: result.draft.registrationQuestionCount,
      registrationQuestions: result.draft.registrationQuestions,
      waitlistCount,
      waitlistEnabled: result.draft.waitlistEnabled,
      waiverRequired: result.draft.waiverRequired,
      waiver: result.draft.waiver
        ? {
            ...result.draft.waiver,
            publishedAt: Timestamp.fromDate(result.draft.waiver.publishedAt),
            updatedAt: Timestamp.fromDate(result.draft.waiver.updatedAt),
            updatedBy: person.uid,
          }
        : null,
      reminderPresets: result.draft.reminderPresets,
      attendanceCheckedInCount,
      notificationIntentId,
      notificationIntentStatus,
      notificationRequestedOnPublish: result.draft.notificationRequestedOnPublish,
      emailRequestedOnPublish: result.draft.emailRequestedOnPublish,
      publishEmailSubject: result.draft.emailRequestedOnPublish
        ? currentEventEmailTemplate().subject
        : null,
      publishEmailMessage: result.draft.emailRequestedOnPublish
        ? currentEventEmailTemplate().message
        : null,
      organizationId: state.organizationId,
      publishedAt: normalizedPublishedAt,
      scheduledPublishAt: result.draft.scheduledPublishAt
        ? Timestamp.fromDate(result.draft.scheduledPublishAt)
        : null,
      schemaVersion: organizationEventSchemaVersion,
      source: result.draft.source,
      startsAt: Timestamp.fromDate(result.draft.startsAt),
      status: result.draft.status,
      summary: result.draft.summary,
      summaryAr: result.draft.summaryAr,
      timezone: result.draft.timezone,
      title: result.draft.title,
      titleAr: result.draft.titleAr,
      visibility: result.draft.visibility,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === eventEditorRevision) {
      state.eventEditor.eventId = result.draft.eventId;
      state.eventEditor.imageStoragePath = image.storagePath ?? "";
      state.eventImageFile = undefined;
      state.eventImageFiles = [];
      state.eventVideoFile = undefined;
      clearEventPendingMediaPreviews();
      state.eventMediaMessage =
        pendingMedia.uploadedCount > 0
          ? `Saved ${pendingMedia.uploadedCount} event media item(s).`
          : "";
      state.eventEditorError = "";
      state.eventEditorMessage = `Saved ${result.draft.title}.`;
      state.status = `Event saved: ${result.draft.title}.`;
      savedSuccessfully = true;
      if (closeWizardOnSuccess) {
        state.eventWizardOpen = false;
        activeTaskWindowId = null;
      }
      connectEventMedia();
      connectEventRegistrations();
      connectEventAttendance();
    }
  } catch (error) {
    if (revision === eventEditorRevision) {
      const message = error instanceof Error ? error.message : String(error);
      state.error = message;
      state.eventEditorError = message;
    }
  } finally {
    if (revision === eventEditorRevision) {
      state.busy = false;
      if (closeWizardOnSuccess && savedSuccessfully) {
        render();
      } else {
        renderPreservingTaskWindowScroll();
      }
    }
  }
}

async function uploadPendingEventMedia(input: {
  eventId: string;
  existingMedia: EventMediaRow[];
  imageAltText: string | null;
  imageAltTextAr: string | null;
  imageFiles: File[];
  personId: string;
  videoFile: File | undefined;
}): Promise<{ coverImagePath: string | null; coverMediaId: string | null; uploadedCount: number }> {
  const timestamp = Date.now();
  let nextPosition =
    input.existingMedia.reduce((max, media) => Math.max(max, media.position), -1) + 1;
  let coverImagePath: string | null = null;
  let coverMediaId: string | null = null;
  let uploadedCount = 0;

  for (const [index, file] of input.imageFiles.entries()) {
    validateEventImageFile(file);
    const fileName = safeEventImageFileName(file.name);
    const mediaId = safeEventMediaId(`image-${timestamp}-${index}-${fileName}`);
    const imageStoragePath = `organizations/${state.organizationId}/events/${input.eventId}/images/${mediaId}-${fileName}`;
    const upload = await uploadBytes(storageRef(storage, imageStoragePath), file, {
      contentType: file.type || "image/jpeg",
    });
    const downloadUrl = await getDownloadURL(upload.ref).catch(() => null);

    await setDoc(
      doc(db, "organizations", state.organizationId, "events", input.eventId, "media", mediaId),
      {
        altText: index === 0 ? input.imageAltText : null,
        altTextAr: index === 0 ? input.imageAltTextAr : null,
        canonicalUrl: null,
        caption: null,
        captionAr: null,
        contentType: file.type || "image/jpeg",
        createdAt: serverTimestamp(),
        createdBy: input.personId,
        durationSeconds: null,
        eventId: input.eventId,
        fileSizeBytes: file.size,
        height: null,
        mediaId,
        organizationId: state.organizationId,
        originalUrl: downloadUrl,
        playbackUrl: null,
        position: nextPosition,
        posterStoragePath: null,
        source: "p13_events",
        storagePath: imageStoragePath,
        thumbnailUrl: downloadUrl,
        title: file.name,
        titleAr: null,
        type: "image",
        updatedAt: serverTimestamp(),
        updatedBy: input.personId,
        videoId: null,
        width: null,
      },
    );

    coverImagePath ??= imageStoragePath;
    coverMediaId ??= mediaId;
    nextPosition += 1;
    uploadedCount += 1;
  }

  if (input.videoFile) {
    validateEventVideoFile(input.videoFile);
    for (const media of input.existingMedia.filter((item) => item.type === "uploadedVideo")) {
      await removeEventMediaItem(media, { suppressRender: true });
    }

    const fileName = safeEventVideoFileName(input.videoFile.name);
    const mediaId = "uploaded-video";
    const videoStoragePath = `organizations/${state.organizationId}/events/${input.eventId}/videos/${timestamp}-${fileName}`;
    const upload = await uploadBytes(storageRef(storage, videoStoragePath), input.videoFile, {
      contentType: input.videoFile.type || "video/mp4",
    });
    const playbackUrl = await getDownloadURL(upload.ref).catch(() => null);

    await setDoc(
      doc(db, "organizations", state.organizationId, "events", input.eventId, "media", mediaId),
      {
        altText: null,
        altTextAr: null,
        canonicalUrl: null,
        caption: null,
        captionAr: null,
        contentType: input.videoFile.type || "video/mp4",
        createdAt: serverTimestamp(),
        createdBy: input.personId,
        durationSeconds: null,
        eventId: input.eventId,
        fileSizeBytes: input.videoFile.size,
        height: null,
        mediaId,
        organizationId: state.organizationId,
        originalUrl: null,
        playbackUrl,
        position: nextPosition,
        posterStoragePath: null,
        source: "p13_events",
        storagePath: videoStoragePath,
        thumbnailUrl: null,
        title: input.videoFile.name,
        titleAr: null,
        type: "uploadedVideo",
        updatedAt: serverTimestamp(),
        updatedBy: input.personId,
        videoId: null,
        width: null,
      },
    );
    uploadedCount += 1;
  }

  return { coverImagePath, coverMediaId, uploadedCount };
}

async function addEventYoutubeMedia(): Promise<void> {
  syncEventEditorInputs();
  state.busy = true;
  state.error = "";
  state.eventMediaMessage = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const eventId = state.eventEditor.eventId.trim();
    if (!eventId) {
      throw new Error("Save or select an event before adding YouTube media.");
    }

    const normalized = normalizeYouTubeVideoUrl(state.eventYoutubeUrl);
    if (!normalized) {
      throw new Error("Enter a supported YouTube watch, short, live, embed, or youtu.be URL.");
    }

    const mediaId = safeEventMediaId(`youtube-${normalized.videoId}`);
    const nextPosition =
      state.eventMedia.reduce((max, media) => Math.max(max, media.position), -1) + 1;

    await setDoc(
      doc(db, "organizations", state.organizationId, "events", eventId, "media", mediaId),
      {
        altText: null,
        altTextAr: null,
        canonicalUrl: normalized.canonicalUrl,
        caption: null,
        captionAr: null,
        contentType: null,
        createdAt: serverTimestamp(),
        createdBy: person.uid,
        durationSeconds: null,
        eventId,
        fileSizeBytes: null,
        height: null,
        mediaId,
        organizationId: state.organizationId,
        originalUrl: state.eventYoutubeUrl.trim(),
        playbackUrl: null,
        position: nextPosition,
        posterStoragePath: null,
        source: "p13_events",
        storagePath: null,
        thumbnailUrl: `https://i.ytimg.com/vi/${normalized.videoId}/hqdefault.jpg`,
        title: null,
        titleAr: null,
        type: "youtube",
        updatedAt: serverTimestamp(),
        updatedBy: person.uid,
        videoId: normalized.videoId,
        width: null,
      },
    );

    state.eventYoutubeUrl = "";
    state.eventMediaMessage = "YouTube video added.";
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function reorderEventMedia(mediaId: string, direction: -1 | 1): Promise<void> {
  const ordered = [...state.eventMedia].sort((left, right) => left.position - right.position);
  const index = ordered.findIndex((media) => media.mediaId === mediaId);
  const swapIndex = index + direction;
  if (index < 0 || swapIndex < 0 || swapIndex >= ordered.length) {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const left = ordered[index]!;
    const right = ordered[swapIndex]!;
    await Promise.all([
      updateEventMediaPosition(left, right.position, person.uid),
      updateEventMediaPosition(right, left.position, person.uid),
    ]);
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function updateEventMediaPosition(
  media: EventMediaRow,
  position: number,
  personId: string,
): Promise<void> {
  await updateDoc(
    doc(db, "organizations", state.organizationId, "events", media.eventId, "media", media.mediaId),
    {
      position,
      updatedAt: serverTimestamp(),
      updatedBy: personId,
    },
  );
}

async function setEventMediaCover(mediaId: string): Promise<void> {
  const media = state.eventMedia.find((item) => item.mediaId === mediaId);
  if (!media || media.type !== "image" || !media.storagePath) {
    state.error = "Only image media can be used as the event cover.";
    render();
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    await updateDoc(doc(db, "organizations", state.organizationId, "events", media.eventId), {
      coverMediaId: media.mediaId,
      image: {
        altText: media.altText,
        altTextAr: media.altTextAr,
        storagePath: media.storagePath,
      },
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
    state.eventEditor.imageStoragePath = media.storagePath;
    state.eventMediaMessage = "Cover image updated.";
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function removeEventMediaItem(
  media: EventMediaRow,
  options: { suppressRender?: boolean } = {},
): Promise<void> {
  if (!options.suppressRender) {
    state.busy = true;
    state.error = "";
    render();
  }

  try {
    await deleteDoc(
      doc(
        db,
        "organizations",
        state.organizationId,
        "events",
        media.eventId,
        "media",
        media.mediaId,
      ),
    );
    if (media.storagePath) {
      await deleteObject(storageRef(storage, media.storagePath)).catch(() => undefined);
    }
    state.eventMediaMessage = "Media removed.";
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    if (!options.suppressRender) {
      state.busy = false;
      render();
    }
  }
}

function safeEventMediaId(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
    .slice(0, 110);

  return normalized || `media-${Date.now()}`;
}

async function createDonation(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    await postAuthenticatedFunction("createDonationIntent", {
      amountMinor: 2500,
      currency: "usd",
      idempotencyKey: `admin-donation-${person.uid}-${state.organizationId}-${Date.now()}`,
      organizationId: state.organizationId,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveGivingFundFromForm(form: HTMLFormElement): Promise<boolean> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const data = new FormData(form);
    const fundId = formValue(data, "fundId", "general");
    const fundRef = doc(db, "organizations", state.organizationId, "givingFunds", fundId);
    const existing = await getDoc(fundRef);
    const existingData = existing.data();
    const type = formValue(data, "type", "custom");

    await setDoc(fundRef, {
      aggregateUpdatedAt: existingData?.aggregateUpdatedAt ?? null,
      confirmedAmountMinor:
        typeof existingData?.confirmedAmountMinor === "number"
          ? existingData.confirmedAmountMinor
          : 0,
      confirmedDonationCount:
        typeof existingData?.confirmedDonationCount === "number"
          ? existingData.confirmedDonationCount
          : 0,
      createdAt: existingData?.createdAt ?? serverTimestamp(),
      createdBy: existingData?.createdBy ?? person.uid,
      description: formValue(data, "description", "Community giving fund."),
      descriptionAr: nullableFormValue(data, "descriptionAr"),
      designation: type,
      displayOrder: formIntegerValue(data, "displayOrder", 50),
      fundId,
      image: {
        altText: null,
        altTextAr: null,
        storagePath: null,
      },
      name: formValue(data, "name", "Giving Fund"),
      nameAr: nullableFormValue(data, "nameAr"),
      organizationId: state.organizationId,
      source: "experience_twenty_eight",
      status: formValue(data, "status", "draft"),
      type,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
      visibility: formValue(data, "visibility", "connected"),
    });
    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function saveCampaignFromForm(form: HTMLFormElement): Promise<boolean> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const data = new FormData(form);
    const campaignId = formValue(data, "campaignId", "campaign");
    const campaignRef = doc(db, "organizations", state.organizationId, "campaigns", campaignId);
    const existing = await getDoc(campaignRef);
    const existingData = existing.data();
    const status = formValue(data, "status", "draft");

    await setDoc(campaignRef, {
      aggregateUpdatedAt: existingData?.aggregateUpdatedAt ?? null,
      archivedAt: status === "archived" ? (existingData?.archivedAt ?? serverTimestamp()) : null,
      campaignId,
      completedAt: status === "completed" ? (existingData?.completedAt ?? serverTimestamp()) : null,
      confirmedAmountMinor:
        typeof existingData?.confirmedAmountMinor === "number"
          ? existingData.confirmedAmountMinor
          : 0,
      confirmedDonationCount:
        typeof existingData?.confirmedDonationCount === "number"
          ? existingData.confirmedDonationCount
          : 0,
      createdAt: existingData?.createdAt ?? serverTimestamp(),
      createdBy: existingData?.createdBy ?? person.uid,
      currency: "usd",
      description: formValue(data, "description", "Campaign description."),
      descriptionAr: nullableFormValue(data, "descriptionAr"),
      displayOrder: formIntegerValue(data, "displayOrder", 50),
      endsAt: timestampFromFormDate(data, "endsAt"),
      fundId: nullableFormValue(data, "fundId"),
      goalAmountMinor: formNullableUsdMinorValue(data, "goalUsd"),
      image: {
        altText: null,
        altTextAr: null,
        storagePath: null,
      },
      organizationId: state.organizationId,
      publishedAt: status === "draft" ? null : (existingData?.publishedAt ?? serverTimestamp()),
      source: "experience_twenty_eight",
      startsAt: timestampFromFormDate(data, "startsAt"),
      status,
      title: formValue(data, "title", "Campaign"),
      titleAr: nullableFormValue(data, "titleAr"),
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
      visibility: formValue(data, "visibility", "connected"),
    });
    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function saveRamadanModeFromEditor(): Promise<boolean> {
  syncRamadanModeEditorInputs();
  const revision = ramadanModeEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateRamadanModeEditorInput(state.ramadanModeEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const modeRef = doc(
      db,
      "organizations",
      state.organizationId,
      "ramadanModes",
      result.draft.modeId,
    );
    const existingMode = await getDoc(modeRef);
    const existingData = existingMode.data();
    const createdAt = existingMode.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingMode.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(modeRef, {
      organizationId: state.organizationId,
      modeId: result.draft.modeId,
      seasonYear: result.draft.seasonYear,
      status: result.draft.status,
      title: result.draft.title,
      titleAr: result.draft.titleAr,
      summary: result.draft.summary,
      summaryAr: result.draft.summaryAr,
      sponsorshipsEnabled: result.draft.sponsorshipsEnabled,
      slotPriceCents: result.draft.slotPriceCents,
      currency: result.draft.currency,
      dayCount: result.draft.dayCount,
      source: result.draft.source,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === ramadanModeEditorRevision) {
      state.ramadanModeEditor.modeId = result.draft.modeId;
    }
    return true;
  } catch (error) {
    if (revision === ramadanModeEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === ramadanModeEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveRamadanSetupFromEditor(): Promise<boolean> {
  syncRamadanSetupEditorInputs();
  syncIftarSponsorshipDefaultsEditorInputs();
  const revision = ramadanSetupEditorRevision;
  state.busy = true;
  state.error = "";
  state.ramadanSetupEditConfirmationPending = false;
  state.ramadanSetupMessage = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    const hijriYear = requireInteger(state.ramadanSetupEditor.hijriYear, "Hijri year");
    const ramadanId = `ramadan-${hijriYear}`;
    state.iftarSponsorshipDefaultsEditor.ramadanId = ramadanId;
    const request = ramadanSetupRequestFromEditor(state.ramadanSetupEditor);
    const sponsorshipDefaultsRequest = iftarSponsorshipDefaultsRequestFromEditor(
      state.iftarSponsorshipDefaultsEditor,
    );
    const setupResult = await postAuthenticatedFunction("saveRamadanSetup", request);
    const sponsorshipResult = await postAuthenticatedFunction(
      "saveIftarSponsorshipDefaults",
      sponsorshipDefaultsRequest,
    );
    state.ramadanSetupMessage = [
      typeof setupResult.outcome === "string" ? setupResult.outcome : "ramadan_setup_saved",
      typeof sponsorshipResult.outcome === "string"
        ? sponsorshipResult.outcome
        : "iftar_sponsorship_defaults_saved",
    ].join(" · ");

    return true;
  } catch (error) {
    if (revision === ramadanSetupEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === ramadanSetupEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function previewRamadanDatesFromProvider(): Promise<void> {
  syncRamadanSetupEditorInputs();
  syncOrganizationPrayerSettingsInputs();
  const revision = ramadanSetupEditorRevision;
  state.busy = true;
  state.error = "";
  state.ramadanSetupMessage = "";
  render();

  try {
    const hijriYear = requireInteger(state.ramadanSetupEditor.hijriYear, "Hijri year");
    const request = {
      ...organizationPrayerSettingsRequestFromEditor(state.organizationPrayerSettingsEditor),
      hijriYear,
    };
    const result = await postAuthenticatedFunction("previewRamadanDates", request);
    const startsOn = typeof result.startsOn === "string" ? result.startsOn : "";
    const endsOn = typeof result.endsOn === "string" ? result.endsOn : "";
    if (!startsOn || !endsOn) {
      throw new Error("AlAdhan did not return usable Ramadan dates.");
    }
    if (revision === ramadanSetupEditorRevision) {
      state.ramadanSetupEditor.startsOn = startsOn;
      state.ramadanSetupEditor.endsOn = endsOn;
      state.ramadanSetupEditor.lastTenStartsOn = addDaysToLocalDate(endsOn, -9);
      state.ramadanSetupEditor.tahajjudSchedule = buildDefaultTahajjudSchedule(
        state.ramadanSetupEditor.lastTenStartsOn,
        endsOn,
        "03:45",
      );
      const dayCount =
        typeof result.dayCount === "number" ? result.dayCount : ramadanDayCount(startsOn, endsOn);
      state.ramadanSetupMessage = `AlAdhan returned Ramadan ${hijriYear}: ${startsOn} to ${endsOn} (${dayCount} days). Review and save the setup.`;
    }
  } catch (error) {
    if (revision === ramadanSetupEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
  } finally {
    if (revision === ramadanSetupEditorRevision) {
      state.busy = false;
      render();
    }
  }
}

async function saveOperationalModeFromEditor(): Promise<void> {
  if (
    operationalModeBusy ||
    operationalModeEditorScope !== currentModeScope() ||
    !selectedContextCanManageOrganizationSettings()
  )
    return;
  syncOperationalModeEditorInputs();
  const revision = operationalModeEditorRevision;
  const scope = operationalModeEditorScope;
  const original = operationalModeOriginal;
  const organizationId = state.organizationId;
  const stillCurrent = (): boolean =>
    revision === operationalModeEditorRevision && scope === currentModeScope();
  state.operationalModeError = "";
  state.operationalModeMessage = "";
  operationalModeBusy = true;
  try {
    const live = state.operationalModes.find((mode) => mode.modeId === original?.modeId);
    if (
      original &&
      (!live ||
        live.status !== original.status ||
        live.updatedAt?.getTime() !== original.updatedAt?.getTime())
    )
      throw new Error("mode_changed");
    const request = buildModeSaveRequest({
      editor: { ...state.operationalModeEditor },
      original,
      organizationId,
      newModeId: operationalModeNewId,
    });
    renderOperationalModeEditorUpdate();
    const result = await postAuthenticatedFunction("saveOperationalMode", request);
    if (!stillCurrent()) return;
    operationalModeEditorDirty = false;
    operationalModeDiscardPending = false;
    genericListUiState.delete("operational-mode-list");
    activeTaskWindowId = null;
    state.operationalModeMessage =
      request.status === "active"
        ? "mode_published"
        : request.status === "draft"
          ? "mode_draft_saved"
          : "mode_saved";
    if (
      request.status === "active" &&
      (result.projectionStatus !== "active" || result.projectionModeId !== request.modeId)
    ) {
      state.operationalModeError = "mode_projection_pending";
      state.operationalModeMessage = "";
    }
  } catch (error) {
    if (stillCurrent())
      state.operationalModeError = error instanceof Error ? error.message : "mode_save_failed";
  } finally {
    if (stillCurrent()) {
      operationalModeBusy = false;
      renderOperationalModeEditorUpdate(() => {
        if (!stillCurrent()) return;
        const alert = document.querySelector<HTMLElement>(
          '#task-window-body #operational-mode-editor-form [role="alert"]',
        );
        alert?.focus({ preventScroll: true });
        alert?.scrollIntoView({ block: "nearest" });
      });
    }
  }
}

async function requestOperationalModeStateChange(
  functionName: "activateOperationalMode" | "deactivateOperationalMode" | "archiveOperationalMode",
  modeId: string,
): Promise<void> {
  if (operationalModeBusy || !selectedContextCanManageOrganizationSettings()) return;
  const scope = currentModeScope();
  const organizationId = state.organizationId;
  const mode = state.operationalModes.find((candidate) => candidate.modeId === modeId);
  const action =
    functionName === "activateOperationalMode"
      ? "activate"
      : functionName === "deactivateOperationalMode"
        ? "deactivate"
        : "archive";
  if (!mode || !modeActions(mode.status).includes(action)) return;
  operationalModeBusy = true;
  state.operationalModeError = "";
  state.operationalModeMessage = "";
  render();
  try {
    if (action === "activate" && mode.endsAt && mode.endsAt.getTime() <= Date.now())
      throw new Error("mode_expired");
    const result = await postAuthenticatedFunction(functionName, { modeId, organizationId });
    if (scope !== currentModeScope()) return;
    operationalModePendingAction = null;
    state.operationalModeMessage =
      action === "activate"
        ? "mode_activated"
        : action === "deactivate"
          ? "mode_ended"
          : "mode_archived";
    if (
      action === "activate" &&
      (result.projectionStatus !== "active" || result.projectionModeId !== modeId)
    ) {
      state.operationalModeMessage = "";
      state.operationalModeError = "mode_projection_pending";
    }
  } catch (error) {
    if (scope === currentModeScope())
      state.operationalModeError = error instanceof Error ? error.message : "mode_save_failed";
  } finally {
    if (scope === currentModeScope()) {
      operationalModeBusy = false;
      render();
    }
  }
}

async function requestRamadanSetupStateChange(
  functionName: "markRamadanSetupReady" | "activateRamadanSetup" | "deactivateRamadanSetup",
  ramadanId: string,
): Promise<void> {
  state.busy = true;
  state.error = "";
  state.ramadanSetupMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction(functionName, {
      organizationId: state.organizationId,
      ramadanId,
    });
    state.ramadanSetupMessage = typeof result.outcome === "string" ? result.outcome : functionName;
    if (functionName === "deactivateRamadanSetup") {
      connectRamadanHistory();
    }
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function generateIftarSponsorshipDaysFromEditor(): Promise<void> {
  syncIftarSponsorshipDefaultsEditorInputs();
  const revision = iftarSponsorshipDefaultsEditorRevision;
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
    if (!ramadanId) {
      throw new Error("Ramadan ID is required before generating Iftar Days.");
    }

    const result = await postAuthenticatedFunction("generateIftarSponsorshipDays", {
      organizationId: state.organizationId,
      ramadanId,
    });
    const createdCount = typeof result.createdCount === "number" ? result.createdCount : 0;
    const existingCount = typeof result.existingCount === "number" ? result.existingCount : 0;
    const updatedCount = typeof result.updatedCount === "number" ? result.updatedCount : 0;
    state.iftarSponsorshipMessage = `Built ${createdCount} new Iftar Day config(s); updated ${updatedCount} existing config(s). ${existingCount} existing day(s) were checked.`;
    connectIftarSponsorshipConfiguration();
  } catch (error) {
    if (revision === iftarSponsorshipDefaultsEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
  } finally {
    if (revision === iftarSponsorshipDefaultsEditorRevision) {
      state.busy = false;
      render();
    }
  }
}

async function saveIftarSponsorshipDayFromEditor(): Promise<boolean> {
  syncIftarSponsorshipDefaultsEditorInputs();
  syncIftarSponsorshipDayEditorInputs();
  const revision = iftarSponsorshipDayEditorRevision;
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    const request = iftarSponsorshipDayRequestFromEditor(
      state.iftarSponsorshipDayEditor,
      state.iftarSponsorshipDefaultsEditor,
    );
    const result = await postAuthenticatedFunction("saveIftarSponsorshipDay", request);
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string" ? result.outcome : "iftar_day_saved";

    connectIftarSponsorshipConfiguration();
    return true;
  } catch (error) {
    if (revision === iftarSponsorshipDayEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === iftarSponsorshipDayEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function requestIftarSponsorshipDayStateChange(
  functionName: "openIftarSponsorshipDay" | "closeIftarSponsorshipDay",
  iftarDayId: string,
  closeNote?: string,
): Promise<void> {
  syncIftarSponsorshipDefaultsEditorInputs();
  const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction(functionName, {
      iftarDayId,
      organizationId: state.organizationId,
      ...(functionName === "closeIftarSponsorshipDay"
        ? { publicNotes: localizedTextOrNull(closeNote ?? "", "") }
        : {}),
      ramadanId,
    });
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string" ? result.outcome : functionName;
    state.iftarSponsorshipCloseNote = "";
    state.iftarSponsorshipClosingDayId = "";
    connectIftarSponsorshipConfiguration();
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function createManualIftarSponsorshipFromEditor(): Promise<boolean> {
  syncIftarSponsorshipDefaultsEditorInputs();
  syncManualIftarSponsorshipEditorInputs();
  const editor = state.iftarSponsorshipManualEditor;
  const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  const quantitySpots = Number.parseInt(editor.quantitySpots, 10);
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    if (!ramadanId || !editor.iftarDayId.trim() || !editor.personId.trim()) {
      throw new Error("Ramadan ID, Iftar day, and connected person are required.");
    }
    const selectedDay = state.iftarSponsorshipDays.find((day) => day.id === editor.iftarDayId);
    if (!selectedDay || selectedDay.status !== "open" || !selectedDay.sponsorshipEnabled) {
      throw new Error("Choose an open sponsorship day before adding a sponsor.");
    }
    const sponsorPeople = peopleManagerRows();
    const selectedPersonIndex = sponsorPeople.findIndex(
      (row) => row.connection.personId === editor.personId.trim(),
    );
    const selectedPersonRow =
      selectedPersonIndex >= 0 ? sponsorPeople[selectedPersonIndex] : undefined;
    const selectedPersonLabel =
      selectedPersonRow !== undefined
        ? peopleRowManualSponsorLabel(selectedPersonRow, selectedPersonIndex)
        : "Connected person";
    const sponsorLabel = editor.sponsorLabel.trim() || selectedPersonLabel;
    const availableSpots = availableIftarSponsorshipSpots(selectedDay);
    if (!Number.isSafeInteger(quantitySpots) || quantitySpots < 1) {
      throw new Error("Manual sponsorship quantity must be at least one spot.");
    }
    if (quantitySpots > availableSpots) {
      throw new Error(`Only ${availableSpots} spot(s) are available for the selected day.`);
    }
    const result = await postAuthenticatedFunction("createManualIftarSponsorship", {
      anonymousPublicly: editor.recognitionPreference === "anonymous",
      dedicationText: editor.dedicationText.trim() || null,
      dedicationType: editor.dedicationText.trim() ? "general" : null,
      idempotencyKey: `manual:${state.organizationId}:${ramadanId}:${editor.iftarDayId}:${editor.personId}:${Date.now()}`,
      iftarDayId: editor.iftarDayId.trim(),
      manualPaymentMethod: editor.manualPaymentMethod,
      organizationId: state.organizationId,
      personId: editor.personId.trim(),
      publicDisplayName: editor.recognitionPreference === "publicName" ? sponsorLabel : null,
      quantitySpots,
      ramadanId,
      recognitionPreference: editor.recognitionPreference,
      sponsorLabel,
    });
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string" ? result.outcome : "manual_iftar_sponsorship_created";
    connectIftarSponsorshipConfiguration();
    connectIftarSponsorshipIntents();
    connectIftarSponsorshipFinance();

    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function transferIftarSponsorshipFromAdmin(
  sponsorshipId: string,
  destinationIftarDayIdInput?: string,
): Promise<void> {
  const sponsorship = state.sponsorships.find(
    (candidate) => candidate.sponsorshipId === sponsorshipId,
  );
  const destinationSelect = document.querySelector<HTMLSelectElement>(
    `[data-iftar-transfer-destination="${sponsorshipId}"]`,
  );
  const destinationIftarDayId =
    destinationIftarDayIdInput?.trim() ?? destinationSelect?.value.trim() ?? "";
  const destinationDay = state.iftarSponsorshipDays.find(
    (candidate) => candidate.id === destinationIftarDayId,
  );
  const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage =
    sponsorship && destinationDay
      ? `Moving ${sponsorship.safeSponsorLabel} to Ramadan day ${destinationDay.ramadanDay}.`
      : "";
  render();

  try {
    if (!sponsorship || !ramadanId || !destinationIftarDayId || !destinationDay) {
      throw new Error("Sponsorship and destination Iftar Day are required.");
    }
    const result = await postAuthenticatedFunction("transferIftarSponsorship", {
      destinationIftarDayId,
      idempotencyKey: `admin-transfer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      organizationId: state.organizationId,
      ramadanId,
      reason: "admin_transfer",
      sponsorshipId,
    });
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string"
        ? `Moved ${sponsorship.safeSponsorLabel} to Ramadan day ${destinationDay.ramadanDay}.`
        : "Iftar sponsorship moved.";
    state.iftarSponsorshipTransferPromptId = "";
    connectIftarSponsorshipConfiguration();
    connectIftarSponsorshipIntents();
  } catch (error) {
    state.error = friendlyIftarTransferError(error);
  } finally {
    state.busy = false;
    render();
  }
}

function friendlyIftarTransferError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  switch (message) {
    case "iftar_transfer_destination_not_available":
      return "Move failed: choose an open Ramadan day with enough available spots.";
    case "iftar_sponsorship_capacity_unavailable":
      return "Move failed: the selected day no longer has enough open spots.";
    case "iftar_transfer_destination_matches_source":
      return "Move failed: choose a different Ramadan day.";
    case "iftar_sponsorship_not_transferable":
      return "Move failed: only active reservations and confirmed sponsorships can be moved.";
    case "iftar_sponsorship_refund_state_ineligible":
      return "Move failed: this paid sponsorship has refund activity and needs finance review first.";
    case "iftar_transfer_destination_window_closed":
      return "Move failed: the selected day is outside the public sponsorship window.";
    default:
      return message;
  }
}

function canAdjustIftarSponsorshipSpotCount(sponsorship: IftarSponsorshipAdminRow): boolean {
  return sponsorship.paymentSource === "manual" && sponsorship.status === "confirmed";
}

function maxAdjustableIftarSponsorshipSpots(sponsorship: IftarSponsorshipAdminRow): number {
  const day = state.iftarSponsorshipDays.find(
    (candidate) => candidate.id === sponsorship.iftarDayId,
  );
  if (!day || !canAdjustIftarSponsorshipSpotCount(sponsorship)) {
    return sponsorship.quantitySpots;
  }

  return sponsorship.quantitySpots + availableIftarSponsorshipSpots(day);
}

function friendlyIftarSponsorDetailsUpdateError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  switch (message) {
    case "iftar_sponsorship_capacity_unavailable":
      return "Sponsor update failed: this day does not have enough available spots.";
    case "iftar_sponsorship_paid_quantity_adjustment_requires_refund":
      return "Sponsor update failed: paid sponsorship spot counts need a refund/payment adjustment first.";
    case "invalid_iftar_sponsorship_update":
      return "Sponsor update failed: check the sponsor name and spot count.";
    default:
      return message;
  }
}

async function updateIftarSponsorshipSponsorDetailsFromAdmin(
  sponsorshipId: string,
  inputRoot: ParentNode = document,
): Promise<void> {
  const sponsorship = state.sponsorships.find(
    (candidate) => candidate.sponsorshipId === sponsorshipId,
  );
  if (!sponsorship) {
    state.error = "Sponsorship is missing from the current Ramadan list.";
    render();
    return;
  }

  const sponsorLabel =
    inputRoot
      .querySelector<HTMLInputElement>(
        `[data-iftar-sponsor-edit-label="${CSS.escape(sponsorshipId)}"]`,
      )
      ?.value.trim() ?? "";
  if (!sponsorLabel) {
    state.error = "Sponsor display name is required.";
    render();
    return;
  }

  const quantityInput = inputRoot.querySelector<HTMLInputElement>(
    `[data-iftar-sponsor-edit-spots="${CSS.escape(sponsorshipId)}"]`,
  );
  const quantitySpots = Number.parseInt(quantityInput?.value ?? "", 10);
  if (!Number.isSafeInteger(quantitySpots) || quantitySpots < 1) {
    state.error = "Sponsor spot count must be at least 1.";
    render();
    return;
  }
  const maxSpots = maxAdjustableIftarSponsorshipSpots(sponsorship);
  if (quantitySpots > maxSpots) {
    state.error = `Sponsor update failed: this day has room for ${maxSpots} spot(s) for this sponsor.`;
    render();
    return;
  }

  const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = "";
  render();

  try {
    const publicDisplayName =
      sponsorship.recognitionPreference === "publicName" ? sponsorLabel : null;
    const result = await postAuthenticatedFunction("updateIftarSponsorshipSponsorName", {
      organizationId: state.organizationId,
      publicDisplayName,
      quantitySpots,
      ramadanId,
      sponsorLabel,
      sponsorshipId,
    });
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string"
        ? result.outcome
        : "iftar_sponsorship_sponsor_details_updated";
    state.iftarSponsorshipEditPromptId = "";
    connectIftarSponsorshipConfiguration();
    connectIftarSponsorshipIntents();
  } catch (error) {
    state.error = friendlyIftarSponsorDetailsUpdateError(error);
  } finally {
    state.busy = false;
    render();
  }
}

function defaultManualIftarSponsorshipDayId(): string {
  const selectedDayId = state.iftarSponsorshipDayEditor.iftarDayId.trim();
  if (
    selectedDayId &&
    state.iftarSponsorshipDays.some(
      (day) =>
        day.id === selectedDayId &&
        day.status === "open" &&
        day.sponsorshipEnabled &&
        availableIftarSponsorshipSpots(day) > 0,
    )
  ) {
    return selectedDayId;
  }

  if (
    state.iftarSponsorshipDayFilter &&
    state.iftarSponsorshipDays.some(
      (day) =>
        day.id === state.iftarSponsorshipDayFilter &&
        day.status === "open" &&
        day.sponsorshipEnabled &&
        availableIftarSponsorshipSpots(day) > 0,
    )
  ) {
    return state.iftarSponsorshipDayFilter;
  }

  return (
    state.iftarSponsorshipDays.find(
      (day) =>
        day.status === "open" && day.sponsorshipEnabled && availableIftarSponsorshipSpots(day) > 0,
    )?.id ??
    state.iftarSponsorshipDays[0]?.id ??
    ""
  );
}

function availableIftarSponsorshipSpots(day: IftarSponsorshipDayRow): number {
  return Math.max(day.capacitySpots - day.confirmedSpots - day.reservedSpots, 0);
}

function openManualIftarSponsorshipFormForDay(iftarDayIdInput?: string): void {
  const iftarDayId = iftarDayIdInput?.trim() || defaultManualIftarSponsorshipDayId();
  if (!iftarDayId) {
    state.error = "Build the Ramadan sponsorship calendar before adding a sponsor.";
    render();
    return;
  }
  const day = state.iftarSponsorshipDays.find((candidate) => candidate.id === iftarDayId);
  if (!day) {
    state.error = `Iftar Day ${iftarDayId} is not in the current Ramadan collection.`;
    render();
    return;
  }
  if (
    day.status !== "open" ||
    !day.sponsorshipEnabled ||
    availableIftarSponsorshipSpots(day) <= 0
  ) {
    state.error = "Choose an open Ramadan day with available sponsorship spots.";
    render();
    return;
  }

  state.iftarSponsorshipManualEditor.iftarDayId = iftarDayId;
  const availableSpots = availableIftarSponsorshipSpots(day);
  const requestedSpots = Number.parseInt(state.iftarSponsorshipManualEditor.quantitySpots, 10);
  if (!Number.isSafeInteger(requestedSpots) || requestedSpots < 1) {
    state.iftarSponsorshipManualEditor.quantitySpots = "1";
  } else if (requestedSpots > availableSpots) {
    state.iftarSponsorshipManualEditor.quantitySpots = String(availableSpots);
  }
  state.iftarSponsorshipMessage = `Adding sponsor for ${iftarDayId}.`;
  activeTaskWindowId = "manual-iftar-sponsorship-form";
  render();
}

async function deleteIftarSponsorshipFromAdmin(sponsorshipId: string): Promise<void> {
  const sponsorship = state.sponsorships.find(
    (candidate) => candidate.sponsorshipId === sponsorshipId,
  );
  if (!sponsorship) {
    state.error = "Sponsorship is missing from the current Ramadan list.";
    render();
    return;
  }
  const isReservation =
    sponsorship.status === "paymentPending" || sponsorship.status === "processing";
  const deleteSubject = isReservation
    ? `the reservation for ${sponsorship.safeSponsorLabel}`
    : sponsorship.safeSponsorLabel;

  const ramadanId = state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  state.busy = true;
  state.error = "";
  state.iftarSponsorshipMessage = `Deleting ${deleteSubject} from ${sponsorship.gregorianDate}.`;
  render();

  try {
    const result = await postAuthenticatedFunction("deleteIftarSponsorship", {
      organizationId: state.organizationId,
      ramadanId,
      sponsorshipId,
    });
    state.iftarSponsorshipMessage =
      typeof result.outcome === "string" ? result.outcome : "iftar_sponsorship_deleted";
    state.iftarSponsorshipDeletePromptId = "";
    state.iftarSponsorshipTransferPromptId = "";
    connectIftarSponsorshipConfiguration();
    connectIftarSponsorshipIntents();
    connectIftarSponsorshipFinance();
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveRamadanAdminNoteFromEditor(): Promise<boolean> {
  syncRamadanAdminNoteEditorInputs();
  const revision = ramadanAdminNoteEditorRevision;
  state.busy = true;
  state.error = "";
  state.ramadanAdminNoteMessage = "";
  render();

  try {
    const request = ramadanAdminNoteRequestFromEditor(state.ramadanAdminNoteEditor);
    const result = await postAuthenticatedFunction(
      request.noteId ? "updateRamadanAdminNote" : "createRamadanAdminNote",
      request.noteId
        ? request
        : {
            ...request,
            idempotencyKey: `admin-note:${state.organizationId}:${request.ramadanId}:${Date.now()}`,
          },
    );

    if (revision === ramadanAdminNoteEditorRevision) {
      state.ramadanAdminNoteMessage =
        typeof result.outcome === "string" ? result.outcome : "ramadan_admin_note_saved";
      if (typeof result.noteId === "string") {
        state.ramadanAdminNoteEditor.noteId = result.noteId;
      }

      connectRamadanAdminNotes();
    }
    return true;
  } catch (error) {
    if (revision === ramadanAdminNoteEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === ramadanAdminNoteEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function archiveRamadanAdminNoteFromAdmin(noteId: string): Promise<void> {
  const note = state.ramadanAdminNotes.find((candidate) => candidate.noteId === noteId);
  const ramadanId = note?.ramadanId ?? currentRamadanAdminNoteRamadanId();
  state.busy = true;
  state.error = "";
  state.ramadanAdminNoteMessage = "";
  render();

  try {
    const result = await postAuthenticatedFunction("archiveRamadanAdminNote", {
      noteId,
      organizationId: state.organizationId,
      ramadanId,
    });
    state.ramadanAdminNoteMessage =
      typeof result.outcome === "string" ? result.outcome : "ramadan_admin_note_archived";
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function selectIftarSponsorshipDayForEditor(
  iftarDayId: string,
  mode: "edit" | "view" = "edit",
): void {
  const day = state.iftarSponsorshipDays.find((candidate) => candidate.id === iftarDayId);
  if (!day) {
    state.error = `Iftar Day ${iftarDayId} is not in the current Ramadan collection.`;
    render();
    return;
  }

  iftarSponsorshipDayEditorRevision += 1;
  state.iftarSponsorshipDayEditor = iftarSponsorshipDayEditorInputFromRow(day);
  state.iftarSponsorshipDayEditorMode = mode;
  state.iftarSponsorshipManualEditor.iftarDayId = day.id;
  state.iftarSponsorshipTransferPromptId = "";
  state.iftarSponsorshipMessage = `${mode === "view" ? "Viewing" : "Editing"} ${iftarDayId}.`;
  activeTaskWindowId = "iftar-sponsorship-day-form";
  render();
}

function selectRamadanAdminNoteForEditor(noteId: string): void {
  const note = state.ramadanAdminNotes.find((candidate) => candidate.noteId === noteId);
  if (!note) {
    state.error = `Ramadan Admin Note ${noteId} is not in the current note list.`;
    render();
    return;
  }

  ramadanAdminNoteEditorRevision += 1;
  state.ramadanAdminNoteEditor = {
    body: note.body,
    category: note.category,
    iftarDayId: note.iftarDayId ?? "",
    noteId: note.noteId,
    ramadanId: note.ramadanId,
    sponsorshipId: note.sponsorshipId ?? "",
  };
  state.ramadanAdminNoteMessage = `Editing ${noteId}.`;
  activeTaskWindowId = "ramadan-admin-note-form";
  render();
}

function ramadanAdminNoteRequestFromEditor(
  editor: RamadanAdminNoteEditorInput,
): Record<string, unknown> & { noteId?: string; ramadanId: string } {
  const ramadanId = requiredText(editor.ramadanId, "Ramadan ID");
  const body = requiredText(editor.body, "Internal note");
  const iftarDayId = nullableText(editor.iftarDayId);
  const sponsorshipId = nullableText(editor.sponsorshipId);

  if (body.length > 2000) {
    throw new Error("Internal notes must be 2,000 characters or fewer.");
  }
  if (sponsorshipId && !iftarDayId) {
    throw new Error("A sponsorship-linked note must also select an Iftar Day.");
  }

  return {
    body,
    category: editor.category,
    iftarDayId,
    noteId: nullableText(editor.noteId) ?? undefined,
    organizationId: state.organizationId,
    ramadanId,
    sponsorshipId,
  };
}

function iftarSponsorshipDefaultsRequestFromEditor(
  editor: IftarSponsorshipDefaultsEditorInput,
): Record<string, unknown> {
  const capacityByWeekday = iftarSponsorshipCapacityByWeekdayFromEditor(editor);
  const setupSponsorshipOpensAt =
    nullableText(state.ramadanSetupEditor.sponsorshipOpensAt) ??
    nullableText(editor.sponsorshipOpensAt);
  return {
    capacityByWeekday,
    currency: "usd",
    dedication: iftarSponsorshipDedicationPolicyFromEditor(editor),
    defaultPricePerSpotMinor: parseDollarInputToMinor(editor.defaultPriceDollars),
    fridayCapacitySpots: capacityByWeekday.friday,
    organizationId: state.organizationId,
    programEnabled: editor.programEnabled === "true",
    publicNotes: localizedTextOrNull(editor.publicNotes, editor.publicNotesAr),
    ramadanId: requiredText(editor.ramadanId, "Ramadan ID"),
    recognition: iftarSponsorshipRecognitionPolicyFromEditor(editor),
    saturdayCapacitySpots: capacityByWeekday.saturday,
    sponsorshipClosesDayOffset: requireInteger(
      editor.sponsorshipClosesDayOffset,
      "Close day offset",
    ),
    sponsorshipClosesLocalTime: requiredText(editor.sponsorshipClosesLocalTime, "Close time"),
    sponsorshipOpensAt: setupSponsorshipOpensAt,
    standardCapacitySpots: capacityByWeekday.monday,
  };
}

function iftarSponsorshipDayRequestFromEditor(
  editor: IftarSponsorshipDayEditorInput,
  defaultsEditor: IftarSponsorshipDefaultsEditorInput,
): Record<string, unknown> {
  const setupSponsorshipOpensAt =
    nullableText(state.ramadanSetupEditor.sponsorshipOpensAt) ??
    nullableText(defaultsEditor.sponsorshipOpensAt);
  return {
    capacitySpots: requireInteger(editor.capacitySpots, "Iftar Day capacity"),
    currency: "usd",
    dedication: iftarSponsorshipDedicationPolicyFromEditor(defaultsEditor),
    gregorianDate: requiredText(editor.gregorianDate, "Gregorian date"),
    iftarDayId: requiredText(editor.iftarDayId, "Iftar Day ID"),
    organizationId: state.organizationId,
    pricePerSpotMinor: parseDollarInputToMinor(editor.priceDollars),
    publicNotes: localizedTextOrNull(editor.publicNotes, editor.publicNotesAr),
    ramadanId: requiredText(defaultsEditor.ramadanId, "Ramadan ID"),
    recognition: iftarSponsorshipRecognitionPolicyFromEditor(defaultsEditor),
    sponsorshipClosesAt: requiredText(editor.sponsorshipClosesAt, "Iftar sponsorship deadline"),
    sponsorshipEnabled: editor.sponsorshipEnabled === "true",
    sponsorshipOpensAt: setupSponsorshipOpensAt,
    status: editor.status,
  };
}

function iftarSponsorshipRecognitionPolicyFromEditor(
  editor: IftarSponsorshipDefaultsEditorInput,
): Record<string, boolean> {
  const publicRecognitionAllowed = editor.publicRecognitionAllowed === "true";
  return {
    anonymousPublicRecognitionAllowed:
      publicRecognitionAllowed && editor.anonymousPublicRecognitionAllowed === "true",
    publicRecognitionAllowed,
    sponsorDisplayNameAllowed:
      publicRecognitionAllowed && editor.sponsorDisplayNameAllowed === "true",
  };
}

function iftarSponsorshipDedicationPolicyFromEditor(
  editor: IftarSponsorshipDefaultsEditorInput,
): Record<string, unknown> {
  const enabled = editor.dedicationEnabled === "true";
  return {
    allowedTypes: enabled ? ["general", "inHonorOf", "inMemoryOf"] : [],
    enabled,
    maxLength: requireInteger(editor.dedicationMaxLength, "Dedication max length"),
    publicDisplayAllowed: enabled && editor.dedicationPublicDisplayAllowed === "true",
  };
}

function localizedTextOrNull(en: string, ar: string): Record<string, string | null> | null {
  const enText = en.trim();
  const arText = ar.trim();
  if (enText.length === 0 && arText.length === 0) {
    return null;
  }

  return {
    ar: arText.length > 0 ? arText : null,
    en: enText,
  };
}

function requiredText(value: string, label: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error(`${label} is required.`);
  }

  return trimmed;
}

function ramadanSetupRequestFromEditor(editor: RamadanSetupEditorInput): Record<string, unknown> {
  const hijriYear = requireInteger(editor.hijriYear, "Hijri year");
  const startAdjustmentDays = requireInteger(editor.startAdjustmentDays, "Start adjustment days");
  const endAdjustmentDays = requireInteger(editor.endAdjustmentDays, "End adjustment days");
  const defaultCampusId = editor.defaultCampusId.trim();
  const linkedOperationalModeId = editor.linkedOperationalModeId.trim();
  const ramadanId = `ramadan-${hijriYear}`;
  const nullableDate = (value: string): string | null =>
    value.trim().length > 0 ? value.trim() : null;
  const nullableInteger = (value: string): number | null => {
    if (value.trim().length === 0) {
      return null;
    }
    return requireInteger(value, "Optional integer");
  };

  if (defaultCampusId.length === 0 || linkedOperationalModeId.length === 0) {
    throw new Error("Default campus and linked operational mode are required.");
  }

  return {
    campusPrayerConfigs: [
      {
        campusId: defaultCampusId,
        prayerOverrides: [
          ramadanPrayerOverride("fajr", editor.fajrIqama),
          ramadanPrayerOverride("maghrib", editor.maghribIqama),
          ramadanPrayerOverride("isha", editor.ishaIqama),
        ],
        sunrise: { display: true, label: { ar: "الشروق", en: "Sunrise" } },
        timezone: requiredText(editor.timezone, "Ramadan timezone"),
      },
    ],
    defaultCampusId,
    endAdjustmentDays,
    endsOn: editor.endsOn.trim(),
    experienceOpensAt: nullableText(editor.experienceOpensAt),
    featuredRamadanContentId: nullableText(editor.featuredRamadanContentId),
    hijriYear,
    home: {
      message: { ar: nullableText(editor.messageAr), en: editor.message.trim() },
      title: { ar: nullableText(editor.titleAr), en: editor.title.trim() },
    },
    guestImams: guestImamsRequestFromEditor(editor),
    iqamaPhases: iqamaPhasesRequestFromEditor(editor),
    lastTenNights: {
      approvedStartsOn: nullableDate(editor.lastTenStartsOn),
      enabled: true,
      homeMessage: {
        ar: "تبدأ العشر الأواخر حسب تقويم رمضان المعتمد للمؤسسة.",
        en: "The last ten nights follow the organization's approved Ramadan dates.",
      },
      homeTitle: { ar: "العشر الأواخر", en: "Last ten nights" },
      qiyamPriority: true,
    },
    linkedOperationalModeId,
    organizationId: state.organizationId,
    qiyam: [
      {
        campusId: defaultCampusId,
        enabled: false,
        endsOn: null,
        notes: null,
        startsOn: null,
        startTime: null,
      },
    ],
    ramadanId,
    startAdjustmentDays,
    startsOn: editor.startsOn.trim(),
    status: editor.status,
    sponsorshipOpensAt: nullableText(editor.sponsorshipOpensAt),
    tahajjudNights: tahajjudNightsRequestFromEditor(editor),
    taraweeh: [
      {
        campusId: defaultCampusId,
        enabled: true,
        notes: { ar: "تبدأ التراويح بعد صلاة العشاء.", en: "Taraweeh follows Isha." },
        rakahCount: nullableInteger(editor.taraweehRakahCount),
        relativeToIshaMinutes: nullableInteger(editor.taraweehRelativeToIshaMinutes),
        startTime: null,
      },
    ],
    timezone: requiredText(editor.timezone, "Ramadan timezone"),
  };
}

function iqamaPhasesRequestFromEditor(
  editor: RamadanSetupEditorInput,
): RamadanIqamaPhaseConfiguration[] {
  const startsOn = requiredText(editor.startsOn, "Ramadan start");
  const endsOn = requiredText(editor.endsOn, "Ramadan end");
  const phaseBoundaries = [
    {
      endsOn: minLocalDate(addDaysToLocalDate(startsOn, 9), endsOn),
      label: { ar: "الأيام العشرة الأولى", en: "First 10 days" },
      phaseId: "firstTen" as const,
      startsOn,
      times: {
        fajr: editor.phase1FajrIqama,
        isha: editor.phase1IshaIqama,
        maghrib: editor.phase1MaghribIqama,
      },
    },
    {
      endsOn: minLocalDate(addDaysToLocalDate(startsOn, 19), endsOn),
      label: { ar: "الأيام العشرة الثانية", en: "Second 10 days" },
      phaseId: "secondTen" as const,
      startsOn: addDaysToLocalDate(startsOn, 10),
      times: {
        fajr: editor.phase2FajrIqama,
        isha: editor.phase2IshaIqama,
        maghrib: editor.phase2MaghribIqama,
      },
    },
    {
      endsOn,
      label: { ar: "العشر الأواخر", en: "Last 10 days" },
      phaseId: "lastTen" as const,
      startsOn: addDaysToLocalDate(startsOn, 20),
      times: {
        fajr: editor.phase3FajrIqama,
        isha: editor.phase3IshaIqama,
        maghrib: editor.phase3MaghribIqama,
      },
    },
  ];

  return phaseBoundaries
    .filter((phase) => phase.startsOn <= phase.endsOn)
    .map((phase) => ({
      endsOn: phase.endsOn,
      label: phase.label,
      phaseId: phase.phaseId,
      prayerOverrides: [
        ramadanPrayerOverride("fajr", phase.times.fajr),
        ramadanPrayerOverride("maghrib", phase.times.maghrib),
        ramadanPrayerOverride("isha", phase.times.isha),
      ] as RamadanIqamaPhaseConfiguration["prayerOverrides"],
      startsOn: phase.startsOn,
    }));
}

function tahajjudNightsRequestFromEditor(
  editor: RamadanSetupEditorInput,
): RamadanTahajjudNightConfiguration[] {
  const lines = editor.tahajjudSchedule
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, 10);

  return lines.map((line, index) => {
    const match = /^(\d{4}-\d{2}-\d{2})\s+([0-2]\d:[0-5]\d)(?:\s+(.*))?$/u.exec(line);
    if (!match) {
      throw new Error("Tahajjud rows must use YYYY-MM-DD HH:mm optional notes.");
    }
    const note = match[3]?.trim();
    return {
      enabled: true,
      gregorianDate: match[1]!,
      nightId: `tahajjud-${match[1]!.replaceAll("-", "")}-${index + 1}`,
      notes: note ? { ar: null, en: note } : null,
      startTime: normalizeTimeInput(match[2]!, "Tahajjud time"),
    };
  });
}

function guestImamsRequestFromEditor(
  editor: RamadanSetupEditorInput,
): RamadanGuestImamConfiguration {
  const profiles = editor.guestImamProfiles
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, 12)
    .map((line, index) => {
      const [displayNameRaw = "", titleRaw = "", bioRaw = "", photoUrlRaw = ""] = line
        .split("|")
        .map((part) => part.trim());
      const displayName = requiredText(displayNameRaw, "Guest imam name");
      return {
        bio: bioRaw ? { ar: null, en: bioRaw } : null,
        displayName: { ar: null, en: displayName },
        enabled: true,
        imamId: `imam-${safeRamadanEditorId(displayName)}-${index + 1}`,
        photoUrl: photoUrlRaw || null,
        sortOrder: index,
        title: titleRaw ? { ar: null, en: titleRaw } : null,
      };
    });

  return {
    enabled: editor.guestImamsEnabled === "true" && profiles.length > 0,
    profiles,
  };
}

function minLocalDate(left: string, right: string): string {
  return left <= right ? left : right;
}

function normalizeTimeInput(value: string, label: string): string {
  const normalized = optionalPrayerTime(value, label);
  if (!normalized) {
    throw new Error(`${label} is required.`);
  }
  return normalized;
}

function safeRamadanEditorId(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
      .slice(0, 60) || "profile"
  );
}

function titleCaseWeekday(value: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "-";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

function ramadanPrayerOverride(prayerId: string, time: string): Record<string, unknown> {
  const trimmed = time.trim();
  return {
    displayTime: trimmed.length > 0 ? trimmed : null,
    enabled: trimmed.length > 0,
    iqamaTime: trimmed.length > 0 ? trimmed : null,
    note: null,
    prayerId,
  };
}

function requireInteger(value: string, label: string): number {
  const parsed = Number.parseInt(value.trim(), 10);
  if (!Number.isSafeInteger(parsed)) {
    throw new Error(`${label} must be a whole number.`);
  }
  return parsed;
}

function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

async function saveIftarSlotFromEditor(): Promise<boolean> {
  syncIftarSlotEditorInputs();
  const revision = iftarSlotEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateIftarSlotEditorInput(state.iftarSlotEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const visibleSlot = state.iftarSlots.find((slot) => slot.id === result.draft.slotId);

    if (visibleSlot && visibleSlot.status !== "available") {
      throw new Error("Reserved iftar sponsorship slots cannot be edited.");
    }

    const slotRef = doc(
      db,
      "organizations",
      state.organizationId,
      "iftarSponsorshipSlots",
      result.draft.slotId,
    );
    const existingSlot = await getDoc(slotRef);
    const existingData = existingSlot.data();

    if (
      existingSlot.exists() &&
      typeof existingData?.status === "string" &&
      existingData.status !== "available"
    ) {
      throw new Error("Reserved iftar sponsorship slots cannot be edited.");
    }

    const createdAt = existingSlot.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingSlot.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(slotRef, {
      amountCents: result.draft.amountCents,
      currency: result.draft.currency,
      dayNumber: result.draft.dayNumber,
      label: result.draft.label,
      labelAr: result.draft.labelAr,
      modeId: result.draft.modeId,
      organizationId: state.organizationId,
      source: result.draft.source,
      status: result.draft.status,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === iftarSlotEditorRevision) {
      state.iftarSlotEditor.slotId = result.draft.slotId;
      state.iftarSlotEditor.modeId = result.draft.modeId;
      state.iftarSlotEditor.dayNumber = String(result.draft.dayNumber);
    }
    return true;
  } catch (error) {
    if (revision === iftarSlotEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === iftarSlotEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveSpaceFromEditor(): Promise<boolean> {
  syncSpaceEditorInputs();
  const revision = spaceEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateSpaceEditorInput(state.spaceEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const spaceRef = doc(db, "organizations", state.organizationId, "spaces", result.draft.spaceId);
    const existingSpace = await getDoc(spaceRef);
    const existingData = existingSpace.data();
    const existingPricing = isRecord(existingData?.pricing) ? existingData.pricing : {};
    const existingPricingVersion =
      typeof existingPricing.pricingVersion === "number" ? existingPricing.pricingVersion : 0;
    const createdAt = existingSpace.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingSpace.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(spaceRef, {
      accessibility: result.draft.accessibility,
      amenities: result.draft.amenities,
      campusId: result.draft.campusId,
      capacity: result.draft.capacity,
      currency: result.draft.currency,
      description: result.draft.description,
      descriptionAr: result.draft.descriptionAr,
      galleryImages: result.draft.galleryImages,
      hourlyPriceCents: result.draft.hourlyPriceCents,
      locationName: result.draft.locationName,
      name: result.draft.name,
      nameAr: result.draft.nameAr,
      operatingHours: result.draft.operatingHours,
      organizationId: state.organizationId,
      pricing:
        result.draft.hourlyPriceCents === null
          ? null
          : {
              activeAgreementId:
                typeof existingPricing.activeAgreementId === "string"
                  ? existingPricing.activeAgreementId
                  : null,
              agreementRequired: existingPricing.agreementRequired === true,
              baseRateMinor: result.draft.hourlyPriceCents,
              currency: "usd",
              depositMinor:
                typeof existingPricing.depositMinor === "number" ? existingPricing.depositMinor : 0,
              effectiveFrom: existingPricing.effectiveFrom ?? serverTimestamp(),
              fixedFees: Array.isArray(existingPricing.fixedFees) ? existingPricing.fixedFees : [],
              minimumChargeMinor: result.draft.hourlyPriceCents,
              pricingEnabled: true,
              pricingType: "hourly",
              pricingVersion: existingPricingVersion + 1,
              rateUnit: "hour",
              source: "experience_forty_one",
              updatedAt: serverTimestamp(),
              updatedBy: person.uid,
            },
      primaryImage: result.draft.primaryImage,
      source: result.draft.source,
      spaceId: result.draft.spaceId,
      status: result.draft.status,
      timezone: result.draft.timezone,
      type: result.draft.type,
      visibility: result.draft.visibility,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === spaceEditorRevision) {
      state.spaceEditor.spaceId = result.draft.spaceId;
    }
    return true;
  } catch (error) {
    if (revision === spaceEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === spaceEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveSpaceAvailabilityBlockFromEditor(): Promise<boolean> {
  syncSpaceAvailabilityBlockEditorInputs();
  const revision = spaceAvailabilityBlockEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateSpaceAvailabilityBlockEditorInput(state.spaceAvailabilityBlockEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const blockRef = doc(
      db,
      "organizations",
      state.organizationId,
      "spaces",
      result.draft.spaceId,
      "availabilityBlocks",
      result.draft.blockId,
    );
    const existingBlock = await getDoc(blockRef);
    const existingData = existingBlock.data();
    const createdAt = existingBlock.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingBlock.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(blockRef, {
      blockId: result.draft.blockId,
      endsAt: Timestamp.fromDate(result.draft.endsAt),
      internalReason: result.draft.internalReason,
      label: result.draft.label,
      organizationId: state.organizationId,
      publicReason: result.draft.publicReason,
      source: result.draft.source,
      spaceId: result.draft.spaceId,
      startsAt: Timestamp.fromDate(result.draft.startsAt),
      type: result.draft.type,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === spaceAvailabilityBlockEditorRevision) {
      state.spaceAvailabilityBlockEditor.blockId = result.draft.blockId;
      state.spaceAvailabilityBlockEditor.spaceId = result.draft.spaceId;
    }
    return true;
  } catch (error) {
    if (revision === spaceAvailabilityBlockEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === spaceAvailabilityBlockEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveSpaceSlotFromEditor(): Promise<boolean> {
  syncSpaceSlotEditorInputs();
  const revision = spaceSlotEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateSpaceSlotEditorInput(state.spaceSlotEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const visibleSlot = state.spaceSlots.find((slot) => slot.id === result.draft.slotId);

    if (visibleSlot && visibleSlot.status !== "available") {
      throw new Error("Reserved space slots cannot be edited.");
    }

    const slotRef = doc(
      db,
      "organizations",
      state.organizationId,
      "spaceAvailabilitySlots",
      result.draft.slotId,
    );
    const existingSlot = await getDoc(slotRef);
    const existingData = existingSlot.data();

    if (
      existingSlot.exists() &&
      typeof existingData?.status === "string" &&
      existingData.status !== "available"
    ) {
      throw new Error("Reserved space slots cannot be edited.");
    }

    const createdAt = existingSlot.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingSlot.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(slotRef, {
      amountCents: result.draft.amountCents,
      currency: result.draft.currency,
      endsAt: Timestamp.fromDate(result.draft.endsAt),
      label: result.draft.label,
      labelAr: result.draft.labelAr,
      organizationId: state.organizationId,
      source: result.draft.source,
      spaceId: result.draft.spaceId,
      startsAt: Timestamp.fromDate(result.draft.startsAt),
      status: result.draft.status,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === spaceSlotEditorRevision) {
      state.spaceSlotEditor.slotId = result.draft.slotId;
      state.spaceSlotEditor.spaceId = result.draft.spaceId;
    }
    return true;
  } catch (error) {
    if (revision === spaceSlotEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === spaceSlotEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

function safeVolunteerDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
    .slice(0, 100);
}

function parseRequiredVolunteerDate(value: string, label: string): Date {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) {
    throw new Error(`${label} is required.`);
  }
  return date;
}

function parseOptionalVolunteerDateRange(
  startsAtInput: string,
  endsAtInput: string,
): { startsAt: Date | null; endsAt: Date | null } {
  if (!startsAtInput.trim() && !endsAtInput.trim()) {
    return { endsAt: null, startsAt: null };
  }
  const startsAt = parseRequiredVolunteerDate(startsAtInput, "Opportunity start");
  const endsAt = parseRequiredVolunteerDate(endsAtInput, "Opportunity end");
  if (endsAt <= startsAt) {
    throw new Error("Opportunity end must be after start.");
  }
  return { endsAt, startsAt };
}

async function saveVolunteerOpportunityFromEditor(): Promise<boolean> {
  syncVolunteerOpportunityEditorInputs();
  const revision = volunteerOpportunityEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const editor = state.volunteerOpportunityEditor;
    const opportunityId = safeVolunteerDocumentId(editor.opportunityId);
    const title = editor.title.trim();
    const description = editor.description.trim();
    const instructions = editor.instructions.trim();
    const locationName = editor.locationName.trim();
    const category = editor.category.trim();
    const { endsAt, startsAt } = parseOptionalVolunteerDateRange(editor.startsAt, editor.endsAt);

    if (!opportunityId) {
      throw new Error("Volunteer opportunity ID is required.");
    }
    if (!title || !description || !instructions || !locationName || !category) {
      throw new Error(
        "Volunteer opportunity title, description, instructions, location, and category are required.",
      );
    }

    const opportunityRef = doc(
      db,
      "organizations",
      state.organizationId,
      "volunteerOpportunities",
      opportunityId,
    );
    const existingOpportunity = await getDoc(opportunityRef);
    const existingData = existingOpportunity.data();
    const createdAt = existingOpportunity.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingOpportunity.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;
    const previousStatus = typeof existingData?.status === "string" ? existingData.status : "draft";
    const existingPublishedAt = existingData?.publishedAt;
    const existingCompletedAt = existingData?.completedAt;
    const existingArchivedAt = existingData?.archivedAt;
    const publishedAt =
      editor.status === "draft"
        ? null
        : previousStatus === "published" ||
            previousStatus === "completed" ||
            previousStatus === "archived"
          ? existingPublishedAt
          : serverTimestamp();
    const completedAt =
      editor.status === "completed"
        ? previousStatus === "completed" && existingCompletedAt
          ? existingCompletedAt
          : serverTimestamp()
        : null;
    const archivedAt =
      editor.status === "archived"
        ? previousStatus === "archived" && existingArchivedAt
          ? existingArchivedAt
          : serverTimestamp()
        : null;

    await setDoc(opportunityRef, {
      archivedAt,
      campusId: editor.campusId.trim() || null,
      category,
      completedAt,
      createdAt,
      createdBy,
      description,
      descriptionAr: editor.descriptionAr.trim() || null,
      endsAt: endsAt ? Timestamp.fromDate(endsAt) : null,
      image: {
        altText: editor.imageAltText.trim() || null,
        altTextAr: editor.imageAltTextAr.trim() || null,
        storagePath: editor.imageStoragePath.trim() || null,
      },
      instructions,
      instructionsAr: editor.instructionsAr.trim() || null,
      locationName,
      opportunityId,
      organizationId: state.organizationId,
      publishedAt,
      requirements: {
        agePolicy: editor.agePolicy.trim() || null,
        dressCode: editor.dressCode.trim() || null,
        physicalNotes: editor.physicalNotes.trim() || null,
        trainingRequired: editor.trainingRequired === "true",
      },
      source: "experience_forty_two",
      startsAt: startsAt ? Timestamp.fromDate(startsAt) : null,
      status: editor.status,
      title,
      titleAr: editor.titleAr.trim() || null,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
      visibility: "connected",
    });

    if (revision === volunteerOpportunityEditorRevision) {
      state.volunteerOpportunityEditor.opportunityId = opportunityId;
    }
    return true;
  } catch (error) {
    if (revision === volunteerOpportunityEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === volunteerOpportunityEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveVolunteerShiftFromEditor(): Promise<boolean> {
  syncVolunteerShiftEditorInputs();
  const revision = volunteerShiftEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const editor = state.volunteerShiftEditor;
    const opportunityId = safeVolunteerDocumentId(editor.opportunityId);
    const shiftId = safeVolunteerDocumentId(editor.shiftId);
    const startsAt = parseRequiredVolunteerDate(editor.startsAt, "Shift start");
    const endsAt = parseRequiredVolunteerDate(editor.endsAt, "Shift end");
    const capacity = Number.parseInt(editor.capacity, 10);

    if (!opportunityId || !shiftId) {
      throw new Error("Volunteer opportunity ID and shift ID are required.");
    }
    if (endsAt <= startsAt) {
      throw new Error("Shift end must be after start.");
    }
    if (!Number.isInteger(capacity) || capacity < 0) {
      throw new Error("Shift capacity must be a non-negative whole number.");
    }

    const shiftRef = doc(
      db,
      "organizations",
      state.organizationId,
      "volunteerOpportunities",
      opportunityId,
      "shifts",
      shiftId,
    );
    const existingShift = await getDoc(shiftRef);
    const existingData = existingShift.data();
    const confirmedCount =
      existingShift.exists() && typeof existingData?.confirmedCount === "number"
        ? existingData.confirmedCount
        : 0;

    if (capacity < confirmedCount) {
      throw new Error("Capacity cannot be lower than confirmed signups.");
    }

    const createdAt = existingShift.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingShift.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;

    await setDoc(shiftRef, {
      capacity,
      confirmedCount,
      createdAt,
      createdBy,
      endsAt: Timestamp.fromDate(endsAt),
      instructions: editor.instructions.trim() || null,
      instructionsAr: editor.instructionsAr.trim() || null,
      notes: editor.notes.trim() || null,
      notesAr: editor.notesAr.trim() || null,
      opportunityId,
      organizationId: state.organizationId,
      shiftId,
      source: "experience_forty_two",
      startsAt: Timestamp.fromDate(startsAt),
      status: editor.status,
      timezone: editor.timezone.trim() || "America/New_York",
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === volunteerShiftEditorRevision) {
      state.volunteerShiftEditor.opportunityId = opportunityId;
      state.volunteerShiftEditor.shiftId = shiftId;
    }
    return true;
  } catch (error) {
    if (revision === volunteerShiftEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === volunteerShiftEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function adminCancelVolunteerSignupFromButton(button: HTMLButtonElement): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await postAuthenticatedFunction("adminCancelVolunteerShiftSignup", {
      organizationId: state.organizationId,
      opportunityId: button.dataset.volunteerOpportunityId ?? "",
      personId: button.dataset.volunteerPersonId ?? "",
      shiftId: button.dataset.volunteerShiftId ?? "",
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function adminVolunteerAttendanceFromButton(button: HTMLButtonElement): Promise<void> {
  const action = button.dataset.volunteerAttendanceAction ?? "";
  const functionName =
    action === "check-in" ? "adminCheckInVolunteerShift" : "adminCheckOutVolunteerShift";
  state.busy = true;
  state.error = "";
  render();

  try {
    await postAuthenticatedFunction(functionName, {
      organizationId: state.organizationId,
      opportunityId: button.dataset.volunteerOpportunityId ?? "",
      personId: button.dataset.volunteerPersonId ?? "",
      shiftId: button.dataset.volunteerShiftId ?? "",
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function adminVolunteerCorrectionFromButton(button: HTMLButtonElement): Promise<void> {
  const personId = button.dataset.volunteerPersonId ?? "";
  const currentMinutes = button.dataset.volunteerVerifiedMinutes ?? "0";
  const minutesText = window.prompt("Verified service minutes", currentMinutes);
  if (minutesText === null) {
    return;
  }
  const verifiedMinutes = Number.parseInt(minutesText, 10);
  const adjustmentReason = window.prompt("Correction reason");
  if (!Number.isInteger(verifiedMinutes) || verifiedMinutes < 0 || !adjustmentReason) {
    state.error = "Volunteer correction requires non-negative minutes and a reason.";
    render();
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await postAuthenticatedFunction("adjustVolunteerServiceMinutes", {
      adjustmentReason,
      organizationId: state.organizationId,
      opportunityId: button.dataset.volunteerOpportunityId ?? "",
      personId,
      shiftId: button.dataset.volunteerShiftId ?? "",
      verifiedMinutes,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveOrganizationProfileFromEditor(): Promise<boolean> {
  syncOrganizationProfileEditorInputs();
  const revision = organizationProfileEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateOrganizationProfileEditorInput(state.organizationProfileEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    let logoStoragePath = result.draft.logoStoragePath;
    const selectedLogoFile = state.organizationProfileLogoFile;

    if (selectedLogoFile) {
      logoStoragePath = await uploadOrganizationProfileLogo(selectedLogoFile);
    }

    const requestId = `profile_${Date.now()}`;
    const requestRef = doc(
      db,
      "organizations",
      state.organizationId,
      "organizationProfileUpdateRequests",
      requestId,
    );

    await setDoc(requestRef, {
      alternateName: result.draft.alternateName ?? "",
      primaryLeader: organizationPublicLeaderFromEditor(result.draft),
      addressLine1: result.draft.addressLine1,
      addressLine2: result.draft.addressLine2,
      brandColor: result.draft.brandColor,
      category: result.draft.category,
      city: result.draft.city,
      connectionPolicy: result.draft.connectionPolicy,
      country: result.draft.country,
      displayName: result.draft.displayName,
      logoStoragePath,
      openConnection: result.draft.openConnection,
      organizationId: state.organizationId,
      postalCode: result.draft.postalCode,
      publicEmail: result.draft.publicEmail,
      publicPhone: result.draft.publicPhone,
      publicVisibility: {
        directory: result.draft.publicVisibilityDirectory,
        discovery: result.draft.publicVisibilityDiscovery,
        profile: result.draft.publicVisibilityProfile,
      },
      region: result.draft.region,
      requestedAt: serverTimestamp(),
      requestedBy: person.uid,
      source: result.draft.source,
      status: result.draft.status,
      timeZone: result.draft.timeZone,
      websiteUrl: result.draft.websiteUrl,
      welcomeHeadline: result.draft.welcomeHeadline,
      welcomeHeadlineAr: result.draft.welcomeHeadlineAr,
      welcomeSummary: result.draft.welcomeSummary,
      welcomeSummaryAr: result.draft.welcomeSummaryAr,
    });

    if (revision === organizationProfileEditorRevision) {
      const optimisticProfile = organizationProfileRowFromDraft(
        state.organizationId,
        result.draft,
        logoStoragePath,
      );
      state.organizationProfile = optimisticProfile;
      state.organizationProfileEditor = organizationProfileEditorInputFromRow(optimisticProfile);
      state.organizationProfileMessage =
        "Profile update request saved. Showing submitted values while the backend publishes the accepted profile.";
    }
    return true;
  } catch (error) {
    if (revision === organizationProfileEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
      state.organizationProfileMessage = "Profile update was not saved.";
    }
    return false;
  } finally {
    if (revision === organizationProfileEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

function organizationPrayerSettingsRequestFromEditor(
  editor: OrganizationPrayerSettingsEditorInput,
): Record<string, unknown> {
  const calculationMethod = requireInteger(editor.calculationMethod, "Calculation method");
  const school = requireInteger(editor.school, "Asr school");
  const latitudeAdjustmentMethod = requireInteger(
    editor.latitudeAdjustmentMethod,
    "Higher-latitude adjustment",
  );
  const address = requiredText(editor.address, "Prayer address");

  if (address.length < 5 || address.length > 240) {
    throw new Error("Prayer address must be between 5 and 240 characters.");
  }
  if (school !== 0 && school !== 1) {
    throw new Error("Asr school must be Standard or Hanafi.");
  }
  if (
    latitudeAdjustmentMethod !== 1 &&
    latitudeAdjustmentMethod !== 2 &&
    latitudeAdjustmentMethod !== 3
  ) {
    throw new Error("Higher-latitude adjustment is invalid.");
  }

  return {
    address,
    calculationMethod,
    enabled: editor.enabled,
    iqamaTimes: {
      asr: optionalPrayerTime(editor.asrIqama, "Asr Iqama"),
      dhuhr: optionalPrayerTime(editor.dhuhrIqama, "Dhuhr Iqama"),
      fajr: optionalPrayerTime(editor.fajrIqama, "Fajr Iqama"),
      isha: optionalPrayerTime(editor.ishaIqama, "Isha Iqama"),
      jumuah: optionalPrayerTime(editor.jumuahIqama, "Jumuah Iqama"),
      maghrib: optionalPrayerTime(editor.maghribIqama, "Maghrib Iqama"),
    },
    latitudeAdjustmentMethod,
    organizationId: state.organizationId,
    provider: "aladhan",
    school,
    timeZone: editor.timeZone,
  };
}

function optionalPrayerTime(value: string, label: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const match = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/u.exec(trimmed);
  if (!match) {
    throw new Error(`${label} must use HH:mm time.`);
  }

  return `${(match[1] ?? "0").padStart(2, "0")}:${match[2] ?? "00"}`;
}

async function saveOrganizationPrayerSettingsFromEditor(): Promise<boolean> {
  syncOrganizationPrayerSettingsInputs();
  const revision = organizationPrayerSettingsEditorRevision;
  state.busy = true;
  state.error = "";
  state.organizationPrayerSettingsMessage = "";
  render();

  try {
    const request = organizationPrayerSettingsRequestFromEditor(
      state.organizationPrayerSettingsEditor,
    );
    await postAuthenticatedFunction("saveOrganizationPrayerSettings", request);

    if (revision === organizationPrayerSettingsEditorRevision) {
      state.organizationPrayerSettingsMessage =
        "Prayer settings saved. Mobile prayer times will update from the public organization profile.";
    }
    return true;
  } catch (error) {
    if (revision === organizationPrayerSettingsEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
      state.organizationPrayerSettingsMessage = "Prayer settings were not saved.";
    }
    return false;
  } finally {
    if (revision === organizationPrayerSettingsEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function saveOrganizationIslamicComponentsFromEditor(): Promise<boolean> {
  syncOrganizationIslamicComponentsInputs();
  const revision = organizationIslamicComponentsEditorRevision;
  state.busy = true;
  state.error = "";
  state.organizationIslamicComponentsMessage = "";
  render();

  try {
    const request = organizationIslamicComponentsRequestFromEditor(
      state.organizationIslamicComponentsEditor,
      state.organizationId,
    );
    await postAuthenticatedFunction("saveOrganizationIslamicComponentsSettings", request);

    if (revision === organizationIslamicComponentsEditorRevision) {
      state.organizationIslamicComponentsMessage =
        "Islamic component settings saved. Mobile Home will update from the public organization profile.";
    }
    return true;
  } catch (error) {
    if (revision === organizationIslamicComponentsEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
      state.organizationIslamicComponentsMessage = "Islamic component settings were not saved.";
    }
    return false;
  } finally {
    if (revision === organizationIslamicComponentsEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function requestConnectionDecision(input: ConnectionDecisionInput): Promise<void> {
  const revision = organizationProfileEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateConnectionDecisionInput(input);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    const safeTargetRequestId = result.draft.targetRequestId.replace(/[^a-zA-Z0-9_-]+/g, "-");
    const requestRef = doc(
      db,
      "organizations",
      state.organizationId,
      "connectionDecisionRequests",
      `${safeTargetRequestId}_${result.draft.decision}_${Date.now()}`,
    );

    await setDoc(requestRef, {
      decision: result.draft.decision,
      organizationId: state.organizationId,
      requestedAt: serverTimestamp(),
      requestedBy: person.uid,
      source: result.draft.source,
      status: result.draft.status,
      targetRequestId: result.draft.targetRequestId,
    });
  } catch (error) {
    if (revision === organizationProfileEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
  } finally {
    if (revision === organizationProfileEditorRevision) {
      state.busy = false;
      render();
    }
  }
}

async function reserveIftarSlot(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const slot =
      state.iftarSlots.find((candidate) => candidate.status === "available") ??
      demoIftarSponsorshipSlots.find(
        (candidate) => candidate.organizationId === state.organizationId,
      );

    if (!slot) {
      throw new Error(
        `No available development iftar sponsorship slot for ${state.organizationId}`,
      );
    }

    const sponsorshipId = `${person.uid}_${slot.id}_${Date.now()}`;
    const sponsorshipRef = doc(
      db,
      "organizations",
      state.organizationId,
      "iftarSponsorshipIntents",
      sponsorshipId,
    );

    await setDoc(sponsorshipRef, {
      organizationId: state.organizationId,
      slotId: slot.id,
      sponsorUid: person.uid,
      dayNumber: slot.dayNumber,
      amountCents: slot.amountCents,
      currency: "usd",
      status: "pending_mock_confirmation",
      provider: "mock_stripe",
      providerEventId: "pending",
      source: "experience_four",
      createdAt: serverTimestamp(),
      createdBy: person.uid,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function publishSpace(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const organization = selectedOrganization();
    const space = demoSpaces.find((candidate) => candidate.organizationId === state.organizationId);

    if (!space) {
      throw new Error(`Missing development space fixture for ${state.organizationId}`);
    }

    const spaceRef = doc(db, "organizations", state.organizationId, "spaces", space.id);
    const existingSpace = await getDoc(spaceRef);
    const createdAt = existingSpace.exists() ? existingSpace.data().createdAt : serverTimestamp();
    const createdBy = existingSpace.exists()
      ? typeof existingSpace.data().createdBy === "string"
        ? existingSpace.data().createdBy
        : person.uid
      : person.uid;
    const timestamp = new Date().toLocaleTimeString();

    await setDoc(spaceRef, {
      organizationId: state.organizationId,
      name: space.name,
      nameAr: space.nameAr,
      description: `${space.description} Updated from ${organization.displayName} admin at ${timestamp}.`,
      descriptionAr: `${space.descriptionAr} تم التحديث من بوابة ${organization.displayName} في ${timestamp}.`,
      locationName: space.locationName,
      capacity: space.capacity,
      hourlyPriceCents: space.hourlyPriceCents,
      currency: space.currency,
      status: space.status,
      visibility: space.visibility,
      source: space.source,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function reserveSpace(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const liveSlot = state.spaceSlots.find((candidate) => candidate.status === "available");
    const fixtureSlot = demoSpaceAvailabilitySlots.find(
      (candidate) => candidate.organizationId === state.organizationId,
    );
    const slotId = liveSlot?.id ?? fixtureSlot?.id;
    const spaceId = liveSlot?.spaceId ?? fixtureSlot?.spaceId;
    const amountCents = liveSlot?.amountCents ?? fixtureSlot?.amountCents;
    const startsAt =
      liveSlot?.startsAt ?? (fixtureSlot ? new Date(fixtureSlot.startsAt) : undefined);
    const endsAt = liveSlot?.endsAt ?? (fixtureSlot ? new Date(fixtureSlot.endsAt) : undefined);

    if (!slotId || !spaceId || !amountCents || !startsAt || !endsAt) {
      throw new Error(`No available development space slot for ${state.organizationId}`);
    }

    const reservationId = `${person.uid}_${slotId}_${Date.now()}`;
    const reservationRef = doc(
      db,
      "organizations",
      state.organizationId,
      "spaceReservationIntents",
      reservationId,
    );

    await setDoc(reservationRef, {
      organizationId: state.organizationId,
      spaceId,
      slotId,
      requesterUid: person.uid,
      startsAt: Timestamp.fromDate(startsAt),
      endsAt: Timestamp.fromDate(endsAt),
      amountCents,
      currency: "usd",
      status: "pending_mock_confirmation",
      provider: "mock_space_scheduler",
      providerEventId: "pending",
      source: "experience_five",
      createdAt: serverTimestamp(),
      createdBy: person.uid,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function publishAnnouncement(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const announcement = demoAnnouncements.find(
      (candidate) => candidate.organizationId === state.organizationId,
    );

    if (!announcement) {
      throw new Error(`Missing development announcement fixture for ${state.organizationId}`);
    }

    const announcementId = `${announcement.id}-${Date.now()}`;
    const announcementRef = doc(
      db,
      "organizations",
      state.organizationId,
      "announcements",
      announcementId,
    );
    const timestamp = new Date().toLocaleTimeString();

    await setDoc(announcementRef, {
      organizationId: state.organizationId,
      title: announcement.title,
      titleAr: announcement.titleAr,
      body: `${announcement.body} Published from ${selectedOrganization().displayName} admin at ${timestamp}.`,
      bodyAr: `${announcement.bodyAr} تم النشر من بوابة ${selectedOrganization().displayName} في ${timestamp}.`,
      audience: announcement.audience,
      expiresAt: null,
      priority: announcement.priority,
      status: announcement.status,
      visibility: announcement.visibility,
      source: announcement.source,
      publishedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      createdBy: person.uid,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveAnnouncementFromComposer(): Promise<boolean> {
  syncAnnouncementComposerInputs();
  if (!state.announcementComposer.announcementId.trim()) {
    state.announcementComposer.announcementId = `announcement-${crypto.randomUUID()}`;
    const idField = document.querySelector<HTMLInputElement>(
      '[data-announcement-composer-field="announcementId"]',
    );
    if (idField) idField.value = state.announcementComposer.announcementId;
  }
  const revision = announcementComposerRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateAnnouncementComposerInput(state.announcementComposer);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    if (
      state.announcementComposerEditingId &&
      result.draft.announcementId !== state.announcementComposerEditingId
    ) {
      throw new Error("Announcement ID cannot be changed while editing an existing announcement.");
    }

    const announcementRef = doc(
      db,
      "organizations",
      state.organizationId,
      "announcements",
      result.draft.announcementId,
    );
    // New UUID records need no read: rules intentionally deny reads of nonexistent content.
    const existingAnnouncement = state.announcementComposerEditingId
      ? await getDoc(announcementRef)
      : undefined;
    const existingData = existingAnnouncement?.data();
    const createdAt = existingAnnouncement?.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingAnnouncement?.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;
    const savedStatus =
      state.announcementComposerEditingId && existingData?.status === "archived"
        ? "archived"
        : result.draft.status;
    const publishedAt = existingAnnouncement?.exists()
      ? (existingData?.publishedAt ?? serverTimestamp())
      : serverTimestamp();

    await setDoc(announcementRef, {
      audience: result.draft.audience,
      pushNotificationEnabled: result.draft.pushNotificationEnabled,
      body: result.draft.body,
      bodyAr: result.draft.bodyAr,
      expiresAt: result.draft.expiresAt ? Timestamp.fromDate(result.draft.expiresAt) : null,
      organizationId: state.organizationId,
      priority: result.draft.priority,
      publishedAt,
      source: result.draft.source,
      status: savedStatus,
      title: result.draft.title,
      titleAr: result.draft.titleAr,
      visibility: result.draft.visibility,
      createdAt,
      createdBy,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (revision === announcementComposerRevision) {
      state.announcementComposerEditingId = null;
    }
    return true;
  } catch (error) {
    if (revision === announcementComposerRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === announcementComposerRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function requestAnnouncementStatusChange(
  announcementId: string,
  status: "published" | "archived",
): Promise<void> {
  const announcement = state.announcements.find((item) => item.id === announcementId);
  if (!announcement) {
    return;
  }

  const title = state.locale === "ar" ? announcement.titleAr : announcement.title;

  if (
    status === "archived" &&
    !window.confirm(
      state.locale === "ar"
        ? `أرشفة الإعلان "${title}"؟ سيختفي من موجز تطبيق الجوال ويمكن إعادة نشره لاحقاً.`
        : `Archive "${title}"? It will be hidden from the mobile app feed and can be republished later.`,
    )
  ) {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const update: Record<string, unknown> = {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    };

    if (status === "published" && !announcement.publishedAt) {
      update.publishedAt = serverTimestamp();
    }

    await updateDoc(
      doc(db, "organizations", state.organizationId, "announcements", announcementId),
      update,
    );
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function saveImamContentFromEditor(): Promise<boolean> {
  syncImamContentEditorInputs();
  const revision = imamContentEditorRevision;
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const result = evaluateImamContentEditorInput(state.organizationId, state.imamContentEditor);

    if (!result.ok) {
      throw new Error(result.errors.join(" "));
    }

    let image = result.draft.image;
    let galleryImage1 = result.draft.galleryImage1;
    let galleryImage2 = result.draft.galleryImage2;
    let galleryImage3 = result.draft.galleryImage3;
    const selectedImageFile = state.imamContentImageFile;
    const selectedGalleryImageFiles = state.imamContentGalleryImageFiles.slice(0, 3);

    if (selectedImageFile) {
      validateContentImageFile(selectedImageFile);
      const fileName = safeContentImageFileName(selectedImageFile.name);
      const imageStoragePath = `organizations/${state.organizationId}/content/${
        result.draft.contentId
      }/${Date.now()}-${fileName}`;

      await uploadBytes(storageRef(storage, imageStoragePath), selectedImageFile, {
        contentType: selectedImageFile.type || "image/jpeg",
      });
      image = {
        ...image,
        storagePath: imageStoragePath,
      };
    }

    if (selectedGalleryImageFiles.length > 0) {
      const galleryImages = [galleryImage1, galleryImage2, galleryImage3];
      for (const [index, selectedGalleryImageFile] of selectedGalleryImageFiles.entries()) {
        validateContentImageFile(selectedGalleryImageFile);
        const fileName = safeContentImageFileName(selectedGalleryImageFile.name);
        const imageStoragePath = `organizations/${state.organizationId}/content/${
          result.draft.contentId
        }/gallery/${Date.now()}-${index + 1}-${fileName}`;

        await uploadBytes(storageRef(storage, imageStoragePath), selectedGalleryImageFile, {
          contentType: selectedGalleryImageFile.type || "image/jpeg",
        });
        galleryImages[index] = {
          ...(galleryImages[index] ?? {
            altText: null,
            altTextAr: null,
          }),
          storagePath: imageStoragePath,
        };
      }
      galleryImage1 = galleryImages[0] ?? null;
      galleryImage2 = galleryImages[1] ?? null;
      galleryImage3 = galleryImages[2] ?? null;
    }

    const contentRef = doc(
      db,
      "organizations",
      state.organizationId,
      "content",
      result.draft.contentId,
    );
    const existingContent = await getDoc(contentRef);
    const existingData = existingContent.data();
    const createdAt = existingContent.exists() ? existingData?.createdAt : serverTimestamp();
    const createdBy = existingContent.exists()
      ? typeof existingData?.createdBy === "string"
        ? existingData.createdBy
        : person.uid
      : person.uid;
    const existingPublishedAt = existingData?.publishedAt;
    const publishedAt =
      result.draft.status === "published"
        ? existingData?.status === "published" && existingPublishedAt
          ? existingPublishedAt
          : serverTimestamp()
        : null;
    const savedFeatured = result.draft.status === "archived" ? false : result.draft.featured;

    await setDoc(contentRef, {
      body: result.draft.body,
      bodyAr: result.draft.bodyAr,
      contentId: result.draft.contentId,
      createdAt,
      createdBy,
      featured: savedFeatured,
      galleryImage1,
      galleryImage2,
      galleryImage3,
      image,
      organizationId: state.organizationId,
      publishedAt,
      sortOrder: result.draft.sortOrder,
      source: result.draft.source,
      status: result.draft.status,
      subtitle: result.draft.subtitle,
      subtitleAr: result.draft.subtitleAr,
      title: result.draft.title,
      titleAr: result.draft.titleAr,
      type: result.draft.type,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
      visibility: result.draft.visibility,
      youtube: result.draft.youtube,
    });

    if (savedFeatured) {
      await clearOtherFeaturedImamContents(result.draft.contentId, result.draft.type, person.uid);
    }

    if (revision === imamContentEditorRevision) {
      state.imamContentEditor.contentId = result.draft.contentId;
      state.imamContentEditor.galleryImageStoragePath1 = galleryImage1?.storagePath ?? "";
      state.imamContentEditor.galleryImageStoragePath2 = galleryImage2?.storagePath ?? "";
      state.imamContentEditor.galleryImageStoragePath3 = galleryImage3?.storagePath ?? "";
      state.imamContentEditor.imageStoragePath = image.storagePath ?? "";
      state.imamContentGalleryImageFiles = [];
      state.imamContentImageFile = undefined;
    }
    return true;
  } catch (error) {
    if (revision === imamContentEditorRevision) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    return false;
  } finally {
    if (revision === imamContentEditorRevision) {
      state.busy = false;
      renderRealtime();
    }
  }
}

async function requestImamContentStatusChange(
  contentId: string,
  status: "published" | "archived",
): Promise<void> {
  const content = state.imamContents.find((item) => item.contentId === contentId);
  if (!content) {
    return;
  }

  const title = imamContentTitle(content);
  if (
    status === "archived" &&
    !window.confirm(
      state.locale === "ar"
        ? `تعطيل ملف القيادة "${title}"؟ سيختفي من تطبيق الجوال ويمكن نشره لاحقاً.`
        : `Disable "${title}"? It will be hidden from the mobile app and can be published again later.`,
    )
  ) {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    await updateDoc(doc(db, "organizations", state.organizationId, "content", contentId), {
      featured: status === "archived" ? false : content.featured,
      publishedAt: status === "published" ? serverTimestamp() : null,
      status,
      updatedAt: serverTimestamp(),
      updatedBy: person.uid,
    });

    if (status === "published" && content.featured) {
      await clearOtherFeaturedImamContents(contentId, content.type, person.uid);
    }
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function clearOtherFeaturedImamContents(
  featuredContentId: string,
  contentType: string,
  updatedBy: string,
): Promise<void> {
  await Promise.all(
    state.imamContents
      .filter(
        (content) =>
          content.contentId !== featuredContentId &&
          content.featured &&
          content.type === contentType,
      )
      .map((content) =>
        updateDoc(doc(db, "organizations", state.organizationId, "content", content.contentId), {
          featured: false,
          updatedAt: serverTimestamp(),
          updatedBy,
        }),
      ),
  );
}

async function requestImamContentDelete(contentId: string): Promise<void> {
  const content = state.imamContents.find((item) => item.contentId === contentId);
  if (!content) {
    return;
  }

  const title = imamContentTitle(content);
  if (
    !window.confirm(
      state.locale === "ar"
        ? `حذف ملف القيادة "${title}"؟ استخدم التعطيل إذا كنت قد تحتاج إلى إعادة نشره لاحقاً.`
        : `Delete "${title}"? Use disable instead if you may need to publish it again later.`,
    )
  ) {
    return;
  }

  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const storagePaths = [
      content.imageStoragePath,
      content.galleryImage1?.storagePath,
      content.galleryImage2?.storagePath,
      content.galleryImage3?.storagePath,
    ].filter((path): path is string => typeof path === "string" && path.length > 0);

    await Promise.all(
      storagePaths.map((path) => deleteObject(storageRef(storage, path)).catch(() => undefined)),
    );
    await deleteDoc(doc(db, "organizations", state.organizationId, "content", content.contentId));

    if (state.imamContentEditor.contentId === content.contentId) {
      clearImamContentEditor();
      activeTaskWindowId = null;
    }
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function validateContentImageFile(file: File): void {
  if (!file.type.startsWith("image/")) {
    throw new Error("Imam content image must be an image file.");
  }

  if (file.size >= 5 * 1024 * 1024) {
    throw new Error("Imam content image must be smaller than 5 MB.");
  }
}

function validateEventImageFile(file: File): void {
  if (!file.type.startsWith("image/")) {
    throw new Error("Event image must be an image file.");
  }

  if (file.size >= 10 * 1024 * 1024) {
    throw new Error("Event image must be smaller than 10 MB.");
  }
}

function validateEventVideoFile(file: File): void {
  if (!["video/mp4", "video/quicktime", "video/webm"].includes(file.type)) {
    throw new Error("Event video must be MP4, MOV, or WebM.");
  }

  if (file.size >= 250 * 1024 * 1024) {
    throw new Error("Event video must be smaller than 250 MB.");
  }
}

function validateOrganizationLogoFile(file: File): void {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("Organization logo must be a PNG, JPG, or WebP image.");
  }

  if (file.size >= 5 * 1024 * 1024) {
    throw new Error("Organization logo must be smaller than 5 MB.");
  }
}

function safeContentImageFileName(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return normalized || "imam-content-image.jpg";
}

function safeEventImageFileName(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return normalized || "event-image.jpg";
}

function safeEventVideoFileName(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return normalized || "event-video.mp4";
}

async function requestIntelligenceSnapshot(): Promise<void> {
  state.busy = true;
  state.error = "";
  render();

  try {
    await signInSelectedPerson();
    const person = selectedPerson();
    const requestId = `${person.uid}_${state.organizationId}_${Date.now()}`;
    const requestRef = doc(
      db,
      "organizations",
      state.organizationId,
      "intelligenceRequests",
      requestId,
    );

    await setDoc(requestRef, {
      organizationId: state.organizationId,
      requestType: "operational_pulse",
      status: "requested",
      source: "experience_seven",
      requestedAt: serverTimestamp(),
      requestedBy: person.uid,
    });
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

function syncEventEditorInputs(): void {
  const root = document.querySelector<HTMLElement>("#event-editor-form") ?? document;
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    "[data-event-editor-field]",
  );

  for (const field of fields) {
    const key = field.dataset.eventEditorField as keyof EventEditorInput | undefined;

    if (!key) {
      continue;
    }

    if (
      (key === "notificationRequestedOnPublish" ||
        key === "emailRequestedOnPublish" ||
        key === "registrationEnabled" ||
        key === "registrationCancellationAllowed" ||
        key === "guestRegistrationEnabled" ||
        key === "dependentRegistrationEnabled" ||
        key === "waitlistEnabled" ||
        key === "registrationQuestionRequired" ||
        key === "waiverRequired" ||
        key === "reminderOneDayBefore" ||
        key === "reminderTwoHoursBefore") &&
      field instanceof HTMLInputElement
    ) {
      setEventEditorCheckboxField(key, field.checked);
      continue;
    }

    setEventEditorField(key, field.value);
  }
}

function syncEventEmailTemplateInputs(root?: Document | HTMLElement): void {
  const source = root ?? document.querySelector<HTMLElement>("#event-editor-form") ?? document;
  const fields = source.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-event-email-template-field]");

  fields.forEach((field) => {
    const key = field.dataset.eventEmailTemplateField;
    if (key === "subject" || key === "message") {
      setEventEmailTemplateField(key, field.value);
    } else if (key === "deliveryType") {
      setEventEmailDeliveryType(field.value);
    } else if (key === "testAddress") {
      state.eventEmailTestAddress = field.value.trim();
    }
  });
}

type RenderScrollContainerSnapshot = {
  selector: string;
  scrollLeft: number;
  scrollTop: number;
  windowId: string;
};

type RenderScrollSnapshot = {
  containers: RenderScrollContainerSnapshot[];
  pageScrollLeft: number;
  pageScrollTop: number;
};

const renderScrollContainerSelectors = [
  "#task-window-body",
  "[data-event-operations-overlay] .task-window-body",
  "[data-account-profile-overlay] .task-window-body",
] as const;

function captureRenderScrollSnapshot(): RenderScrollSnapshot {
  const pageScroller = document.scrollingElement;
  const seenContainers = new Set<HTMLElement>();
  const containers: RenderScrollContainerSnapshot[] = [];

  for (const selector of renderScrollContainerSelectors) {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element || seenContainers.has(element)) {
      continue;
    }

    const windowId = element.dataset.scrollWindowId;
    if (!windowId) {
      continue;
    }

    seenContainers.add(element);
    containers.push({
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop,
      selector,
      windowId,
    });
  }

  return {
    containers,
    pageScrollLeft: pageScroller?.scrollLeft ?? window.scrollX,
    pageScrollTop: pageScroller?.scrollTop ?? window.scrollY,
  };
}

function restoreRenderScrollSnapshot(
  snapshot: RenderScrollSnapshot,
  options: { forcePageRestore?: boolean } = {},
): void {
  let restoredContainer = false;

  for (const containerSnapshot of snapshot.containers) {
    const element = document.querySelector<HTMLElement>(containerSnapshot.selector);
    if (!element || element.dataset.scrollWindowId !== containerSnapshot.windowId) {
      continue;
    }

    element.scrollTop = containerSnapshot.scrollTop;
    element.scrollLeft = containerSnapshot.scrollLeft;
    restoredContainer = true;
  }

  if (!restoredContainer && !options.forcePageRestore) {
    return;
  }

  const pageScroller = document.scrollingElement;
  if (pageScroller) {
    pageScroller.scrollTop = snapshot.pageScrollTop;
    pageScroller.scrollLeft = snapshot.pageScrollLeft;
    return;
  }

  window.scrollTo(snapshot.pageScrollLeft, snapshot.pageScrollTop);
}

function scheduleRenderScrollRestore(
  snapshot: RenderScrollSnapshot,
  options: { forcePageRestore?: boolean; afterRestore?: () => void } = {},
): void {
  requestAnimationFrame(() => {
    restoreRenderScrollSnapshot(snapshot, options);
    window.setTimeout(() => {
      restoreRenderScrollSnapshot(snapshot, options);
      options.afterRestore?.();
    }, 0);
  });
}

let eventEditorRenderBypass = false;
let renderedEventEditorScope = "";

function renderPreservingTaskWindowScroll(): void {
  const snapshot = captureRenderScrollSnapshot();
  eventEditorRenderBypass = true;
  try {
    render();
  } finally {
    eventEditorRenderBypass = false;
  }
  scheduleRenderScrollRestore(snapshot, { forcePageRestore: true });
}

function shouldRestorePageScrollAfterRender(snapshot: RenderScrollSnapshot): boolean {
  return snapshot.containers.length > 0;
}

function bindEventEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-event-editor-field]");

  for (const field of fields) {
    const eventName =
      field instanceof HTMLSelectElement ||
      (field instanceof HTMLInputElement && field.type === "checkbox")
        ? "change"
        : "input";

    field.addEventListener(eventName, () => {
      const key = field.dataset.eventEditorField as keyof EventEditorInput | undefined;

      if (!key) {
        return;
      }

      if (
        (key === "notificationRequestedOnPublish" ||
          key === "emailRequestedOnPublish" ||
          key === "registrationEnabled" ||
          key === "registrationCancellationAllowed" ||
          key === "guestRegistrationEnabled" ||
          key === "dependentRegistrationEnabled" ||
          key === "waitlistEnabled" ||
          key === "registrationQuestionRequired" ||
          key === "waiverRequired" ||
          key === "reminderOneDayBefore" ||
          key === "reminderTwoHoursBefore") &&
        field instanceof HTMLInputElement
      ) {
        setEventEditorCheckboxField(key, field.checked);
        if (state.eventWizardOpen) {
          syncEventEmailTemplateFromEditor();
        }
        if (
          key === "registrationEnabled" ||
          key === "waiverRequired" ||
          key === "emailRequestedOnPublish"
        ) {
          renderPreservingTaskWindowScroll();
        }
        return;
      }

      setEventEditorField(key, field.value);
      if (state.eventWizardOpen) {
        syncEventEmailTemplateFromEditor();
      }
      if (key === "eventType" || key === "registrationMode" || key === "status") {
        renderPreservingTaskWindowScroll();
      }
    });
  }

  appRoot.querySelectorAll<HTMLInputElement>("#event-image-file").forEach((field) => {
    field.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      state.eventImageFile = input.files?.item(0) ?? undefined;
      if (state.eventImageFile) {
        state.eventImageFiles = [];
      }
      state.eventEditorError = "";
      state.eventMediaMessage = state.eventImageFile ? "Image ready to upload on Save Event." : "";
      refreshEventPendingMediaPreviews();
      renderPreservingTaskWindowScroll();
    });
  });

  appRoot.querySelectorAll<HTMLInputElement>("#event-images-file").forEach((field) => {
    field.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      state.eventImageFiles = Array.from(input.files ?? []);
      if (state.eventImageFiles.length > 0) {
        state.eventImageFile = undefined;
      }
      state.eventEditorError = "";
      state.eventMediaMessage =
        state.eventImageFiles.length > 0
          ? `${state.eventImageFiles.length} image(s) ready to upload on Save Event.`
          : "";
      refreshEventPendingMediaPreviews();
      renderPreservingTaskWindowScroll();
    });
  });

  appRoot.querySelectorAll<HTMLInputElement>("#event-video-file").forEach((field) => {
    field.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      state.eventVideoFile = input.files?.item(0) ?? undefined;
      state.eventEditorError = "";
      state.eventMediaMessage = state.eventVideoFile
        ? "Uploaded video ready to save. Existing uploaded event video will be replaced."
        : "";
      refreshEventPendingMediaPreviews();
      renderPreservingTaskWindowScroll();
    });
  });

  appRoot.querySelectorAll<HTMLInputElement>("#event-youtube-url").forEach((field) => {
    field.addEventListener("input", (event) => {
      state.eventYoutubeUrl = (event.target as HTMLInputElement).value;
    });
  });

  appRoot.querySelectorAll<HTMLButtonElement>("#add-event-youtube-media").forEach((button) => {
    button.addEventListener("click", () => void addEventYoutubeMedia());
  });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-media-cover]").forEach((button) => {
    button.addEventListener("click", () => {
      const mediaId = button.dataset.eventMediaCover;
      if (mediaId) {
        void setEventMediaCover(mediaId);
      }
    });
  });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-media-remove]").forEach((button) => {
    button.addEventListener("click", () => {
      const mediaId = button.dataset.eventMediaRemove;
      const media = state.eventMedia.find((item) => item.mediaId === mediaId);
      if (media) {
        void removeEventMediaItem(media);
      }
    });
  });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-media-move]").forEach((button) => {
    button.addEventListener("click", () => {
      const mediaId = button.dataset.eventMediaMove;
      const direction = button.dataset.eventMediaDirection === "up" ? -1 : 1;
      if (mediaId) {
        void reorderEventMedia(mediaId, direction);
      }
    });
  });
}

function bindEventWizardControls(appRoot: HTMLElement): void {
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-wizard-template]").forEach((button) => {
    button.addEventListener("click", () => {
      const eventType = button.dataset.eventWizardTemplate;
      setEventEditorField(
        "eventType",
        eventType === "friday_program" || eventType === "janazah" ? eventType : "standard",
      );
      state.eventWizardStepIndex = Math.min(
        state.eventWizardStepIndex,
        eventWizardStepsForEditor().length - 1,
      );
      syncEventEmailTemplateFromEditor({ force: true });
      renderPreservingTaskWindowScroll();
    });
  });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-wizard-step]").forEach((button) => {
    button.addEventListener("click", () => {
      const nextIndex = Number(button.dataset.eventWizardStep ?? state.eventWizardStepIndex);
      if (Number.isFinite(nextIndex)) {
        moveEventWizardToStep(nextIndex);
      }
    });
  });

  appRoot
    .querySelector<HTMLButtonElement>("[data-event-wizard-prev]")
    ?.addEventListener("click", () => {
      moveEventWizardToStep(state.eventWizardStepIndex - 1);
    });

  appRoot
    .querySelector<HTMLButtonElement>("[data-event-wizard-next]")
    ?.addEventListener("click", () => {
      moveEventWizardToStep(state.eventWizardStepIndex + 1);
    });

  appRoot
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "[data-event-email-template-field]",
    )
    .forEach((field) => {
      const eventName = field instanceof HTMLSelectElement ? "change" : "input";
      field.addEventListener(eventName, () => {
        const key = field.dataset.eventEmailTemplateField;
        if (key === "subject" || key === "message") {
          setEventEmailTemplateField(key, field.value);
          refreshEventEmailPreview();
        } else if (key === "deliveryType") {
          setEventEmailDeliveryType(field.value, { refreshTemplate: true });
          renderPreservingTaskWindowScroll();
        } else if (key === "testAddress") {
          state.eventEmailTestAddress = field.value.trim();
        }
      });
    });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-email-reset]").forEach((button) => {
    button.addEventListener("click", () => {
      syncEventEmailTemplateFromEditor({ force: true });
      renderPreservingTaskWindowScroll();
    });
  });

  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-email-test-send]").forEach((button) => {
    button.addEventListener("click", () => {
      syncEventEditorInputs();
      syncEventEmailTemplateInputs(
        button.closest<HTMLElement>(".event-email-template-panel") ?? undefined,
      );
      const template = currentEventEmailTemplate();
      const email = state.eventEmailTestAddress.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/u.test(email)) {
        state.eventEmailTestStatus = { message: eventEmailCopy[state.locale].invalid, error: true };
        renderPreservingTaskWindowScroll();
        return;
      }
      if (!template.subject.trim() || !template.message.trim()) {
        state.eventEmailTestStatus = {
          message: eventEmailCopy[state.locale].required,
          error: true,
        };
        renderPreservingTaskWindowScroll();
        return;
      }
      void sendEventCommunicationFromEditor({
        audience: "test",
        channels: ["email"],
        message: template.message,
        subject: template.subject,
        testRecipientEmail: email,
        type: state.eventEmailDeliveryType,
      });
    });
  });
}

function moveEventWizardToStep(nextIndex: number): void {
  const steps = eventWizardStepsForEditor();
  const boundedIndex = Math.min(Math.max(nextIndex, 0), steps.length - 1);
  const movingForward = boundedIndex > state.eventWizardStepIndex;
  syncEventEditorInputs();
  syncEventEmailTemplateInputs();

  if (movingForward) {
    const currentStep = steps[state.eventWizardStepIndex] ?? steps[0];
    const errors = currentStep ? validateEventWizardStep(currentStep.id) : [];
    if (errors.length > 0) {
      state.eventEditorError = errors.join(" ");
      renderPreservingTaskWindowScroll();
      return;
    }
  }

  state.eventEditorError = "";
  state.eventWizardStepIndex = boundedIndex;
  renderPreservingTaskWindowScroll();
}

function validateEventWizardStep(stepId: EventWizardStepId): string[] {
  const errors: string[] = [];

  if (stepId === "template") {
    if (state.eventEditor.status === "scheduled" && !state.eventEditor.scheduledPublishDate) {
      errors.push("Scheduled publish date is required.");
    }
  }

  if (stepId === "details") {
    if (!state.eventEditor.title.trim()) {
      errors.push("English title is required.");
    }
    if (!state.eventEditor.summary.trim()) {
      errors.push("English summary is required.");
    }
    if (!state.eventEditor.description.trim()) {
      errors.push("English description is required.");
    }
  }

  if (stepId === "schedule") {
    const startsAt = state.eventEditor.startsAt ? new Date(state.eventEditor.startsAt) : undefined;
    const endsAt = state.eventEditor.endsAt ? new Date(state.eventEditor.endsAt) : undefined;
    if (!startsAt || !Number.isFinite(startsAt.getTime())) {
      errors.push("Start date and time is required.");
    }
    if (!endsAt || !Number.isFinite(endsAt.getTime())) {
      errors.push("End date and time is required.");
    }
    if (startsAt && endsAt && Number.isFinite(startsAt.getTime()) && startsAt >= endsAt) {
      errors.push("End date and time must be after the start date and time.");
    }
    if (!state.eventEditor.timezone.trim()) {
      errors.push("Timezone is required.");
    }
    if (!state.eventEditor.locationName.trim()) {
      errors.push("Location is required.");
    }
  }

  if (stepId === "registration") {
    const capacity = Number(state.eventEditor.registrationCapacity);
    if (
      state.eventEditor.registrationEnabled &&
      (!Number.isFinite(capacity) || capacity <= 0 || capacity > 100000)
    ) {
      errors.push("Registration capacity is required when registration is enabled.");
    }
    if (state.eventEditor.waiverRequired) {
      if (!state.eventEditor.waiverTitle.trim()) {
        errors.push("Waiver title is required.");
      }
      if (!state.eventEditor.waiverBody.trim()) {
        errors.push("Waiver body is required.");
      }
    }
  }

  if (stepId === "communication") {
    const template = currentEventEmailTemplate();
    if (!template.subject.trim()) {
      errors.push("Email subject is required.");
    }
    if (!template.message.trim()) {
      errors.push("Email body is required.");
    }
  }

  if (stepId === "review") {
    const result = evaluateEventEditorInput(state.eventEditor, state.organizationId);
    if (!result.ok) {
      errors.push(...result.errors);
    }
  }

  return errors;
}

function setEventEditorField(key: keyof EventEditorInput, value: string): void {
  state.eventEditorError = "";
  state.eventEditorMessage = "";

  if (key === "status") {
    state.eventEditor.status =
      value === "archived" ||
      value === "cancelled" ||
      value === "completed" ||
      value === "draft" ||
      value === "published" ||
      value === "registration_closed" ||
      value === "scheduled"
        ? value
        : "published";
    if (state.eventEditor.status !== "scheduled") {
      state.eventEditor.scheduledPublishDate = "";
    }
    return;
  }

  if (key === "eventType") {
    applyEventTypeDefaults(value === "friday_program" || value === "janazah" ? value : "standard");
    return;
  }

  if (key === "registrationMode") {
    state.eventEditor.registrationMode = value === "none" ? "none" : "free";
    state.eventEditor.registrationEnabled = state.eventEditor.registrationMode !== "none";
    return;
  }

  if (key === "campusId") {
    const campus = seedOrganizationCampuses.find(
      (candidate) =>
        candidate.organizationId === state.organizationId && candidate.campusId === value,
    );
    state.eventEditor.campusId = campus?.campusId ?? "";
    state.eventEditor.campusNameSnapshot = campus?.name ?? "";
    return;
  }

  if (
    key !== "notificationRequestedOnPublish" &&
    key !== "emailRequestedOnPublish" &&
    key !== "registrationEnabled" &&
    key !== "registrationCancellationAllowed" &&
    key !== "guestRegistrationEnabled" &&
    key !== "dependentRegistrationEnabled" &&
    key !== "waitlistEnabled" &&
    key !== "registrationQuestionRequired" &&
    key !== "waiverRequired" &&
    key !== "reminderOneDayBefore" &&
    key !== "reminderTwoHoursBefore"
  ) {
    if (key === "eventId" && state.eventEditor.eventId !== value) {
      state.eventEditor.eventId = value;
      state.eventCheckInCredential = "";
      state.eventCheckInMessage = "";
      state.eventManualRegistrationSearch = "";
      state.eventMediaMessage = "";
      connectEventMedia();
      connectEventRegistrations();
      connectEventAttendance();
      return;
    }
    (state.eventEditor as unknown as Record<string, string>)[key] = value;
  }
}

function setEventEditorCheckboxField(key: keyof EventEditorInput, value: boolean): void {
  state.eventEditorError = "";
  state.eventEditorMessage = "";
  if (key === "emailRequestedOnPublish") {
    state.eventEditor.emailRequestedOnPublish = value;
  }

  if (key === "notificationRequestedOnPublish") {
    state.eventEditor.notificationRequestedOnPublish = value;
  }
  if (key === "registrationEnabled") {
    state.eventEditor.registrationEnabled = value;
    if (value && state.eventEditor.registrationMode === "none") {
      state.eventEditor.registrationMode = "free";
    }
    if (!value) {
      state.eventEditor.registrationMode = "none";
    }
  }
  if (key === "registrationCancellationAllowed") {
    state.eventEditor.registrationCancellationAllowed = value;
  }
  if (key === "guestRegistrationEnabled") {
    state.eventEditor.guestRegistrationEnabled = value;
  }
  if (key === "dependentRegistrationEnabled") {
    state.eventEditor.dependentRegistrationEnabled = value;
  }
  if (key === "waitlistEnabled") {
    state.eventEditor.waitlistEnabled = value;
  }
  if (key === "registrationQuestionRequired") {
    state.eventEditor.registrationQuestionRequired = value;
  }
  if (key === "waiverRequired") {
    state.eventEditor.waiverRequired = value;
  }
  if (key === "reminderOneDayBefore") {
    state.eventEditor.reminderOneDayBefore = value;
  }
  if (key === "reminderTwoHoursBefore") {
    state.eventEditor.reminderTwoHoursBefore = value;
  }
}

function setAnnouncementComposerField(key: keyof AnnouncementComposerInput, value: string): void {
  if (key === "pushNotificationEnabled") {
    state.announcementComposer.pushNotificationEnabled = value === "true";
    return;
  }
  if (key === "priority") {
    state.announcementComposer.priority = value === "urgent" ? "urgent" : "normal";
    return;
  }

  state.announcementComposer[key] = value;
}

function syncAnnouncementComposerInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-announcement-composer-field]");

  for (const field of fields) {
    const key = field.dataset.announcementComposerField as
      keyof AnnouncementComposerInput | undefined;

    if (key) {
      setAnnouncementComposerField(
        key,
        field instanceof HTMLInputElement && field.type === "checkbox"
          ? String(field.checked)
          : field.value,
      );
    }
  }
}

function bindAnnouncementComposerInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-announcement-composer-field]");

  for (const field of fields) {
    const eventName = field instanceof HTMLSelectElement ? "change" : "input";

    field.addEventListener(eventName, () => {
      const key = field.dataset.announcementComposerField as
        keyof AnnouncementComposerInput | undefined;

      if (key) {
        setAnnouncementComposerField(
          key,
          field instanceof HTMLInputElement && field.type === "checkbox"
            ? String(field.checked)
            : field.value,
        );
      }
    });
  }
}

function setImamContentEditorField(key: keyof ImamContentEditorInput, value: string): void {
  if (key === "status") {
    state.imamContentEditor.status =
      value === "published" || value === "archived" ? value : "draft";
    return;
  }

  if (key === "featured") {
    state.imamContentEditor.featured = value === "true";
    return;
  }

  if (key === "contentType") {
    state.imamContentEditor.contentType = isOrganizationContentTypeId(value)
      ? value
      : "imam_profile";
    return;
  }

  state.imamContentEditor[key] = value;
}

function syncImamContentEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-imam-content-editor-field]");

  for (const field of fields) {
    const key = field.dataset.imamContentEditorField as keyof ImamContentEditorInput | undefined;

    if (!key) {
      continue;
    }

    if (key === "featured" && field instanceof HTMLInputElement) {
      state.imamContentEditor.featured = field.checked;
      continue;
    }

    setImamContentEditorField(key, field.value);
  }
}

function bindImamContentEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-imam-content-editor-field]");

  for (const field of fields) {
    const eventName =
      field instanceof HTMLSelectElement ||
      (field instanceof HTMLInputElement && field.type === "checkbox")
        ? "change"
        : "input";

    field.addEventListener(eventName, () => {
      const key = field.dataset.imamContentEditorField as keyof ImamContentEditorInput | undefined;

      if (!key) {
        return;
      }

      if (key === "featured" && field instanceof HTMLInputElement) {
        state.imamContentEditor.featured = field.checked;
        return;
      }

      setImamContentEditorField(key, field.value);
    });
  }

  appRoot
    .querySelector<HTMLInputElement>("#imam-content-image-file")
    ?.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      state.imamContentImageFile = input.files?.item(0) ?? undefined;
    });
  appRoot
    .querySelector<HTMLInputElement>("#imam-content-gallery-image-files")
    ?.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      state.imamContentGalleryImageFiles = Array.from(input.files ?? []).slice(0, 3);
    });
}

function syncRamadanModeEditorInputs(): void {
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-ramadan-mode-editor-field]",
  );

  for (const field of fields) {
    const key = field.dataset.ramadanModeEditorField as keyof RamadanModeEditorInput | undefined;

    if (key) {
      state.ramadanModeEditor[key] = field.value;
    }
  }
}

function bindRamadanModeEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-ramadan-mode-editor-field]",
  );

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.ramadanModeEditorField as keyof RamadanModeEditorInput | undefined;

      if (key) {
        state.ramadanModeEditor[key] = field.value;
      }
    });
  }
}

function syncRamadanSetupEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-ramadan-setup-editor-field]");

  for (const field of fields) {
    const key = field.dataset.ramadanSetupEditorField as keyof RamadanSetupEditorInput | undefined;

    if (key) {
      setRamadanSetupEditorField(key, field.value);
    }
  }
}

function bindRamadanSetupEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-ramadan-setup-editor-field]");

  for (const field of fields) {
    const eventName = field instanceof HTMLSelectElement ? "change" : "input";
    field.addEventListener(eventName, () => {
      const key = field.dataset.ramadanSetupEditorField as
        keyof RamadanSetupEditorInput | undefined;

      if (key) {
        setRamadanSetupEditorField(key, field.value);
      }
    });
  }
}

function setRamadanSetupEditorField(key: keyof RamadanSetupEditorInput, value: string): void {
  if (key === "status") {
    state.ramadanSetupEditor.status = value === "draft" ? "draft" : "ready";
    return;
  }
  if (key === "guestImamsEnabled") {
    state.ramadanSetupEditor.guestImamsEnabled = value === "true" ? "true" : "false";
    return;
  }

  state.ramadanSetupEditor[key] = value as never;
  if (key === "hijriYear") {
    const hijriYear = Number.parseInt(value, 10);
    if (Number.isSafeInteger(hijriYear)) {
      const ramadanId = `ramadan-${hijriYear}`;
      state.ramadanSetupEditor.linkedOperationalModeId = `ramadan-${hijriYear}-mode`;
      state.ramadanSetupEditor.title = `Ramadan ${hijriYear}`;
      state.iftarAttendanceDefaultsEditor.ramadanId = ramadanId;
      state.iftarSponsorshipDefaultsEditor.ramadanId = ramadanId;
      if (state.ramadanSetupEditor.titleAr.trim().length === 0) {
        state.ramadanSetupEditor.titleAr = `رمضان ${hijriYear}`;
      }
    }
  }
}

function syncIftarSponsorshipDefaultsEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-iftar-sponsorship-defaults-field]");

  for (const field of fields) {
    const key = field.dataset.iftarSponsorshipDefaultsField as
      keyof IftarSponsorshipDefaultsEditorInput | undefined;

    if (key) {
      setIftarSponsorshipDefaultsEditorField(key, field.value);
    }
  }
}

function bindIftarSponsorshipDefaultsEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-iftar-sponsorship-defaults-field]");

  for (const field of fields) {
    const eventName = field instanceof HTMLSelectElement ? "change" : "input";
    field.addEventListener(eventName, () => {
      const key = field.dataset.iftarSponsorshipDefaultsField as
        keyof IftarSponsorshipDefaultsEditorInput | undefined;

      if (key) {
        setIftarSponsorshipDefaultsEditorField(key, field.value);
      }
    });
  }
}

function setIftarSponsorshipDefaultsEditorField(
  key: keyof IftarSponsorshipDefaultsEditorInput,
  value: string,
): void {
  if (
    key === "programEnabled" ||
    key === "publicRecognitionAllowed" ||
    key === "sponsorDisplayNameAllowed" ||
    key === "anonymousPublicRecognitionAllowed" ||
    key === "dedicationEnabled" ||
    key === "dedicationPublicDisplayAllowed"
  ) {
    state.iftarSponsorshipDefaultsEditor[key] = value === "false" ? "false" : "true";
    return;
  }

  state.iftarSponsorshipDefaultsEditor[key] = value as never;
}

function syncIftarSponsorshipDayEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-iftar-sponsorship-day-field]");

  for (const field of fields) {
    const key = field.dataset.iftarSponsorshipDayField as
      keyof IftarSponsorshipDayEditorInput | undefined;

    if (key) {
      setIftarSponsorshipDayEditorField(key, field.value);
    }
  }
}

function bindIftarSponsorshipDayEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-iftar-sponsorship-day-field]");

  for (const field of fields) {
    const eventName = field instanceof HTMLSelectElement ? "change" : "input";
    field.addEventListener(eventName, () => {
      const key = field.dataset.iftarSponsorshipDayField as
        keyof IftarSponsorshipDayEditorInput | undefined;

      if (key) {
        setIftarSponsorshipDayEditorField(key, field.value);
      }
    });
  }
}

function setIftarSponsorshipDayEditorField(
  key: keyof IftarSponsorshipDayEditorInput,
  value: string,
): void {
  if (key === "status") {
    state.iftarSponsorshipDayEditor.status =
      value === "open" || value === "closed" ? value : "draft";
    return;
  }

  if (key === "sponsorshipEnabled") {
    state.iftarSponsorshipDayEditor.sponsorshipEnabled = value === "false" ? "false" : "true";
    return;
  }

  state.iftarSponsorshipDayEditor[key] = value as never;
  if (key === "gregorianDate" && /^\d{4}-\d{2}-\d{2}$/u.test(value)) {
    state.iftarSponsorshipDayEditor.iftarDayId = `iftar-${value}`;
    if (state.iftarSponsorshipDayEditor.sponsorshipClosesAt.trim().length === 0) {
      state.iftarSponsorshipDayEditor.sponsorshipClosesAt = `${value}T17:00`;
    }
  }
}

function syncRamadanAdminNoteEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-ramadan-admin-note-field]");

  for (const field of fields) {
    const key = field.dataset.ramadanAdminNoteField as
      keyof RamadanAdminNoteEditorInput | undefined;

    if (key) {
      setRamadanAdminNoteEditorField(key, field.value);
    }
  }
}

function bindRamadanAdminNoteEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-ramadan-admin-note-field]");

  for (const field of fields) {
    const key = field.dataset.ramadanAdminNoteField as
      keyof RamadanAdminNoteEditorInput | undefined;
    const eventName =
      field instanceof HTMLSelectElement || key === "ramadanId" ? "change" : "input";
    field.addEventListener(eventName, () => {
      if (key) {
        setRamadanAdminNoteEditorField(key, field.value);
        if (key === "ramadanId") {
          connectRamadanAdminNotes();
        }
      }
    });
  }

  appRoot
    .querySelector<HTMLSelectElement>("#ramadan-admin-note-status-filter")
    ?.addEventListener("change", (event) => {
      const value = (event.target as HTMLSelectElement).value;
      state.ramadanAdminNoteStatusFilter =
        value === "archived" || value === "all" ? value : "active";
      connectRamadanAdminNotes();
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#ramadan-admin-note-category-filter")
    ?.addEventListener("change", (event) => {
      const value = (event.target as HTMLSelectElement).value;
      state.ramadanAdminNoteCategoryFilter =
        value === "all" || ramadanAdminNoteCategories.includes(value as RamadanAdminNoteCategory)
          ? (value as RamadanAdminNoteCategory | "all")
          : "all";
      connectRamadanAdminNotes();
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#ramadan-admin-note-day-filter")
    ?.addEventListener("change", (event) => {
      state.ramadanAdminNoteDayFilter = (event.target as HTMLSelectElement).value;
      render();
    });
}

function setRamadanAdminNoteEditorField(
  key: keyof RamadanAdminNoteEditorInput,
  value: string,
): void {
  if (key === "category") {
    state.ramadanAdminNoteEditor.category = ramadanAdminNoteCategories.includes(
      value as RamadanAdminNoteCategory,
    )
      ? (value as RamadanAdminNoteCategory)
      : "operations";
    return;
  }

  state.ramadanAdminNoteEditor[key] = value as never;
}

function syncManualIftarSponsorshipEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-manual-iftar-sponsorship-field]");

  for (const field of fields) {
    const key = field.dataset.manualIftarSponsorshipField as
      keyof ManualIftarSponsorshipEditorInput | undefined;

    if (key) {
      setManualIftarSponsorshipEditorField(key, field.value);
    }
  }
}

function bindManualIftarSponsorshipEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-manual-iftar-sponsorship-field]");

  for (const field of fields) {
    const eventName = field instanceof HTMLSelectElement ? "change" : "input";
    field.addEventListener(eventName, () => {
      const key = field.dataset.manualIftarSponsorshipField as
        keyof ManualIftarSponsorshipEditorInput | undefined;

      if (key) {
        setManualIftarSponsorshipEditorField(key, field.value);
        if (key === "iftarDayId" || key === "quantitySpots") {
          render();
        }
      }
    });
  }

  appRoot
    .querySelector<HTMLInputElement>("#iftar-sponsorship-search")
    ?.addEventListener("input", (event) => {
      state.iftarSponsorshipSearch = (event.target as HTMLInputElement).value;
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#iftar-sponsorship-status-filter")
    ?.addEventListener("change", (event) => {
      state.iftarSponsorshipStatusFilter = (event.target as HTMLSelectElement).value;
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#iftar-sponsorship-day-filter")
    ?.addEventListener("change", (event) => {
      state.iftarSponsorshipDayFilter = (event.target as HTMLSelectElement).value;
      render();
    });
}

function setManualIftarSponsorshipEditorField(
  key: keyof ManualIftarSponsorshipEditorInput,
  value: string,
): void {
  if (key === "manualPaymentMethod") {
    state.iftarSponsorshipManualEditor.manualPaymentMethod =
      value === "check" || value === "external" || value === "officeCommitment" ? value : "cash";
    return;
  }
  if (key === "recognitionPreference") {
    state.iftarSponsorshipManualEditor.recognitionPreference =
      value === "anonymous" || value === "none" ? value : "publicName";
    return;
  }
  if (key === "iftarDayId") {
    state.iftarSponsorshipManualEditor.iftarDayId = value;
    const day = state.iftarSponsorshipDays.find((candidate) => candidate.id === value);
    if (day) {
      const availableSpots = availableIftarSponsorshipSpots(day);
      const requestedSpots = Number.parseInt(state.iftarSponsorshipManualEditor.quantitySpots, 10);
      if (availableSpots > 0 && (!Number.isSafeInteger(requestedSpots) || requestedSpots < 1)) {
        state.iftarSponsorshipManualEditor.quantitySpots = "1";
      } else if (availableSpots > 0 && requestedSpots > availableSpots) {
        state.iftarSponsorshipManualEditor.quantitySpots = String(availableSpots);
      }
    }
    return;
  }
  if (key === "quantitySpots") {
    const requestedSpots = Number.parseInt(value, 10);
    const day = state.iftarSponsorshipDays.find(
      (candidate) => candidate.id === state.iftarSponsorshipManualEditor.iftarDayId,
    );
    const availableSpots = day ? availableIftarSponsorshipSpots(day) : 0;
    if (availableSpots > 0 && Number.isSafeInteger(requestedSpots)) {
      state.iftarSponsorshipManualEditor.quantitySpots = String(
        Math.min(Math.max(requestedSpots, 1), availableSpots),
      );
      return;
    }
  }

  state.iftarSponsorshipManualEditor[key] = value as never;
}

function syncOperationalModeEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-operational-mode-editor-field]");

  for (const field of fields) {
    const key = field.dataset.operationalModeEditorField as
      keyof OperationalModeEditorInput | undefined;

    if (key) {
      state.operationalModeEditor[key] = field.value as never;
    }
  }
}

function bindOperationalModeEditorInputs(appRoot: HTMLElement): void {
  appRoot.querySelector<HTMLElement>(".mode-task-window")?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeOperationalModeEditor();
    } else if (event.key === "Tab") {
      const elements = Array.from(
        (event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), summary, [tabindex="0"]',
        ),
      ).filter((element) => element.offsetParent !== null);
      const first = elements[0];
      const last = elements.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  });
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-operational-mode-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.operationalModeEditorField as
        keyof OperationalModeEditorInput | undefined;

      if (key) {
        operationalModeEditorDirty = true;
        state.operationalModeEditor[key] = field.value as never;
      }
    });
  }
}

function syncIftarSlotEditorInputs(): void {
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-iftar-slot-editor-field]",
  );

  for (const field of fields) {
    const key = field.dataset.iftarSlotEditorField as keyof IftarSlotEditorInput | undefined;

    if (key) {
      state.iftarSlotEditor[key] = field.value;
    }
  }
}

function bindIftarSlotEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-iftar-slot-editor-field]",
  );

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.iftarSlotEditorField as keyof IftarSlotEditorInput | undefined;

      if (key) {
        state.iftarSlotEditor[key] = field.value;
      }
    });
  }
}

function syncSpaceEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-space-editor-field]");

  for (const field of fields) {
    const key = field.dataset.spaceEditorField as keyof SpaceEditorInput | undefined;

    if (key) {
      state.spaceEditor[key] = field.value;
    }
  }
}

function bindSpaceEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-space-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.spaceEditorField as keyof SpaceEditorInput | undefined;

      if (key) {
        state.spaceEditor[key] = field.value;
      }
    });
  }
}

function syncSpaceAvailabilityBlockEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-space-availability-block-editor-field]");

  for (const field of fields) {
    const key = field.dataset.spaceAvailabilityBlockEditorField as
      keyof SpaceAvailabilityBlockEditorInput | undefined;

    if (key) {
      state.spaceAvailabilityBlockEditor[key] = field.value;
    }
  }
}

function bindSpaceAvailabilityBlockEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-space-availability-block-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.spaceAvailabilityBlockEditorField as
        keyof SpaceAvailabilityBlockEditorInput | undefined;

      if (key) {
        state.spaceAvailabilityBlockEditor[key] = field.value;
      }
    });
  }
}

function syncSpaceSlotEditorInputs(): void {
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-space-slot-editor-field]",
  );

  for (const field of fields) {
    const key = field.dataset.spaceSlotEditorField as keyof SpaceSlotEditorInput | undefined;

    if (key) {
      state.spaceSlotEditor[key] = field.value;
    }
  }
}

function bindSpaceSlotEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "[data-space-slot-editor-field]",
  );

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.spaceSlotEditorField as keyof SpaceSlotEditorInput | undefined;

      if (key) {
        state.spaceSlotEditor[key] = field.value;
      }
    });
  }
}

function syncVolunteerOpportunityEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-volunteer-opportunity-editor-field]");

  for (const field of fields) {
    const key = field.dataset.volunteerOpportunityEditorField as
      keyof VolunteerOpportunityEditorInput | undefined;
    if (key) {
      state.volunteerOpportunityEditor[key] = field.value;
    }
  }
}

function bindVolunteerOpportunityEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-volunteer-opportunity-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.volunteerOpportunityEditorField as
        keyof VolunteerOpportunityEditorInput | undefined;
      if (key) {
        state.volunteerOpportunityEditor[key] = field.value;
      }
    });
  }
}

function syncVolunteerShiftEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-volunteer-shift-editor-field]");

  for (const field of fields) {
    const key = field.dataset.volunteerShiftEditorField as
      keyof VolunteerShiftEditorInput | undefined;
    if (key) {
      state.volunteerShiftEditor[key] = field.value;
    }
  }
}

function bindVolunteerShiftEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("[data-volunteer-shift-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.volunteerShiftEditorField as
        keyof VolunteerShiftEditorInput | undefined;
      if (key) {
        state.volunteerShiftEditor[key] = field.value;
      }
    });
  }
}

function syncOrganizationProfileEditorInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-organization-profile-editor-field]");

  for (const field of fields) {
    const key = field.dataset.organizationProfileEditorField as
      keyof OrganizationProfileEditorInput | undefined;

    if (key) {
      setOrganizationProfileEditorField(key, profileEditorFieldValue(field));
    }
  }
}

function bindOrganizationProfileEditorInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-organization-profile-editor-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.organizationProfileEditorField as
        keyof OrganizationProfileEditorInput | undefined;

      if (key) {
        const value = normalizedOrganizationProfileFieldValue(key, field);
        if (key === "publicPhone" && typeof value === "string" && field.value !== value) {
          field.value = value;
        }
        setOrganizationProfileEditorField(key, value);
      }
    });
    field.addEventListener("change", () => {
      const key = field.dataset.organizationProfileEditorField as
        keyof OrganizationProfileEditorInput | undefined;

      if (key) {
        const value = normalizedOrganizationProfileFieldValue(key, field);
        if (key === "publicPhone" && typeof value === "string" && field.value !== value) {
          field.value = value;
        }
        setOrganizationProfileEditorField(key, value);
      }
    });
  }

  appRoot
    .querySelector<HTMLInputElement>("#organization-profile-logo-file")
    ?.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.item(0) ?? undefined;
      try {
        if (file) {
          validateOrganizationLogoFile(file);
        }
        setOrganizationProfileLogoFile(file);
        state.organizationProfileLogoError = "";
      } catch (error) {
        input.value = "";
        setOrganizationProfileLogoFile(undefined);
        state.organizationProfileLogoError =
          error instanceof Error ? error.message : "Organization logo could not be selected.";
      }
      render();
    });
}

function syncOrganizationPrayerSettingsInputs(): void {
  const fields = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-organization-prayer-settings-field]");

  for (const field of fields) {
    const key = field.dataset.organizationPrayerSettingsField as
      keyof OrganizationPrayerSettingsEditorInput | undefined;

    if (key) {
      setOrganizationPrayerSettingsEditorField(key, organizationPrayerSettingsFieldValue(field));
    }
  }
}

function bindOrganizationPrayerSettingsInputs(appRoot: HTMLElement): void {
  const fields = appRoot.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-organization-prayer-settings-field]");

  for (const field of fields) {
    field.addEventListener("input", () => {
      const key = field.dataset.organizationPrayerSettingsField as
        keyof OrganizationPrayerSettingsEditorInput | undefined;

      if (key) {
        setOrganizationPrayerSettingsEditorField(key, organizationPrayerSettingsFieldValue(field));
      }
    });
    field.addEventListener("change", () => {
      const key = field.dataset.organizationPrayerSettingsField as
        keyof OrganizationPrayerSettingsEditorInput | undefined;

      if (key) {
        setOrganizationPrayerSettingsEditorField(key, organizationPrayerSettingsFieldValue(field));
      }
    });
  }
}

function syncOrganizationIslamicComponentsInputs(): void {
  const enabledComponentIds: IslamicComponentId[] = [];
  const homeEnabledComponentIds: IslamicComponentId[] = [];

  document
    .querySelectorAll<HTMLInputElement>("[data-islamic-component-enabled]")
    .forEach((field) => {
      const componentId = field.dataset.islamicComponentEnabled;
      if (
        componentId &&
        islamicComponentIds.includes(componentId as IslamicComponentId) &&
        field.checked
      ) {
        enabledComponentIds.push(componentId as IslamicComponentId);
      }
    });
  document
    .querySelectorAll<HTMLInputElement>("[data-islamic-component-home-enabled]")
    .forEach((field) => {
      const componentId = field.dataset.islamicComponentHomeEnabled;
      if (
        componentId &&
        islamicComponentIds.includes(componentId as IslamicComponentId) &&
        field.checked
      ) {
        homeEnabledComponentIds.push(componentId as IslamicComponentId);
      }
    });

  const hijriDateDisplay = document.querySelector<HTMLSelectElement>(
    "[data-islamic-components-field='hijriDateDisplay']",
  )?.value;
  const dailyContentMode = document.querySelector<HTMLSelectElement>(
    "[data-islamic-components-field='dailyContentMode']",
  )?.value;
  const dailyContentDate =
    document.querySelector<HTMLInputElement>("[data-islamic-components-field='dailyContentDate']")
      ?.value ?? state.organizationIslamicComponentsEditor.dailyContentDate;
  const dailyAyahReference =
    document.querySelector<HTMLSelectElement>(
      "[data-islamic-components-field='dailyAyahReference']",
    )?.value ?? state.organizationIslamicComponentsEditor.dailyAyahReference;
  const dailyHadithReference =
    document.querySelector<HTMLSelectElement>(
      "[data-islamic-components-field='dailyHadithReference']",
    )?.value ?? state.organizationIslamicComponentsEditor.dailyHadithReference;
  const dailyDuaReference =
    document.querySelector<HTMLSelectElement>("[data-islamic-components-field='dailyDuaReference']")
      ?.value ?? state.organizationIslamicComponentsEditor.dailyDuaReference;

  state.organizationIslamicComponentsEditor = {
    dailyAyahReference,
    dailyContentDate,
    dailyContentMode:
      dailyContentMode === "off" ||
      dailyContentMode === "automatic" ||
      dailyContentMode === "curated"
        ? dailyContentMode
        : state.organizationIslamicComponentsEditor.dailyContentMode,
    dailyDuaReference,
    dailyHadithReference,
    enabledComponentIds,
    hijriDateDisplay:
      hijriDateDisplay === "gregorianOnly" ||
      hijriDateDisplay === "hijriSecondary" ||
      hijriDateDisplay === "hijriPrimary"
        ? hijriDateDisplay
        : state.organizationIslamicComponentsEditor.hijriDateDisplay,
    homeEnabledComponentIds,
  };
}

function bindOrganizationIslamicComponentsInputs(appRoot: HTMLElement): void {
  appRoot
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>(
      "[data-islamic-component-enabled], [data-islamic-component-home-enabled], [data-islamic-components-field]",
    )
    .forEach((field) => {
      field.addEventListener("change", () => {
        syncOrganizationIslamicComponentsInputs();
      });
    });
}

function organizationPrayerSettingsFieldValue(
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
): boolean | string {
  if (field instanceof HTMLInputElement && field.type === "checkbox") {
    return field.checked;
  }

  return field.value;
}

function setOrganizationPrayerSettingsEditorField(
  key: keyof OrganizationPrayerSettingsEditorInput,
  value: boolean | string,
): void {
  if (key === "enabled") {
    state.organizationPrayerSettingsEditor.enabled = value === true;
    return;
  }

  if (typeof value !== "string") {
    return;
  }

  state.organizationPrayerSettingsEditor[key] = value;
}

function setOrganizationProfileEditorField(
  key: keyof OrganizationProfileEditorInput,
  value: boolean | string,
): void {
  type OrganizationProfileEditorBooleanField =
    | "publicVisibilityDirectory"
    | "publicVisibilityDiscovery"
    | "publicVisibilityProfile"
    | "leaderPublished";
  type OrganizationProfileEditorTextField = Exclude<
    keyof OrganizationProfileEditorInput,
    OrganizationProfileEditorBooleanField
  >;

  if (
    key === "publicVisibilityDirectory" ||
    key === "publicVisibilityDiscovery" ||
    key === "publicVisibilityProfile" ||
    key === "leaderPublished"
  ) {
    state.organizationProfileEditor[key] = value === true;
    return;
  }

  if (typeof value !== "string") {
    return;
  }

  if (key === "category") {
    state.organizationProfileEditor.category =
      value === "Community center" ? "Community center" : "Islamic center";
    return;
  }

  if (key === "connectionPolicy") {
    state.organizationProfileEditor.connectionPolicy =
      value === "approval_required" ? "approval_required" : "open";
    return;
  }

  let nextValue = value;
  if (key === "country" || key === "region" || key === "postalCode") {
    nextValue = value.toUpperCase();
  } else if (key === "publicPhone") {
    nextValue = formatOrganizationProfilePhone(value);
  }

  const editor = state.organizationProfileEditor as Record<
    OrganizationProfileEditorTextField,
    string
  >;
  editor[key as OrganizationProfileEditorTextField] = nextValue;
}

function normalizedOrganizationProfileFieldValue(
  key: keyof OrganizationProfileEditorInput,
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
): boolean | string {
  const value = profileEditorFieldValue(field);

  if (typeof value !== "string") {
    return value;
  }

  return key === "publicPhone" ? formatOrganizationProfilePhone(value) : value;
}

function profileEditorFieldValue(
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
): boolean | string {
  return field instanceof HTMLInputElement && field.type === "checkbox"
    ? field.checked
    : field.value;
}

function renderAuthenticationGate(appRoot: HTMLElement, direction: "ltr" | "rtl"): void {
  const l = (english: string, arabic: string) => (state.locale === "ar" ? arabic : english);
  const isBusy = authenticationForm.busy;
  const isResetMode = authenticationForm.resetSent;
  const googleUnavailable = !environmentConfig.authProviders.google;
  const appleUnavailable = !environmentConfig.authProviders.apple;

  appRoot.innerHTML = `
    <div class="shell auth-shell">
      <section class="workspace auth-workspace" dir="${direction}">
        <header class="topbar">
          ${
            environmentConfig.bannerLabel
              ? `<span class="banner">${environmentConfig.bannerLabel} · ${projectId}</span>`
              : ""
          }
          <div class="controls">
            <label>
              ${l("Language", "اللغة")}
              <select id="locale">
                <option value="en" ${state.locale === "en" ? "selected" : ""}>English</option>
                <option value="ar" ${state.locale === "ar" ? "selected" : ""}>العربية</option>
              </select>
            </label>
          </div>
        </header>
        <main class="content auth-content">
          <article class="panel auth-panel">
            <div class="auth-card-header">
              ${renderShdeedBrand(state.locale)}
              <div>
                <p class="eyebrow">${l("Organization workspace", "إدارة المؤسسة")}</p>
                <h1>${l("Organization Admin Portal", "إدارة المؤسسة")}</h1>
                <p class="muted">${l("Welcome back to your community workspace.", "مرحبا بك في مساحة عمل مجتمعك.")}</p>
              </div>
            </div>
            ${
              authenticationState.status === "initializing"
                ? `<p class="status">${l("Checking session...", "جار التحقق من الجلسة...")}</p>`
                : ""
            }
            ${
              authenticationState.status === "error"
                ? `<p class="error">${authErrorText(authenticationState.error)}</p>`
                : ""
            }
            ${authenticationForm.error ? `<p class="error">${authenticationForm.error}</p>` : ""}
            ${
              isResetMode
                ? `<p class="status">${l("Check your email for a password reset link.", "تحقق من بريدك الإلكتروني للحصول على رابط إعادة تعيين كلمة المرور.")}</p>`
                : ""
            }
            <div class="provider-actions">
              <button class="provider-button provider-button-google" id="auth-google" type="button" ${
                isBusy || googleUnavailable ? "disabled" : ""
              }>
                <span class="provider-mark" aria-hidden="true">G</span>
                <span>${l("Continue with Google", "المتابعة باستخدام Google")}</span>
              </button>
            </div>
            <div class="auth-divider"><span>${l("Email sign-in", "الدخول بالبريد الإلكتروني")}</span></div>
            <form id="auth-form" class="editor-form">
              <label>
                ${l("Email", "البريد الإلكتروني")}
                <input id="auth-email" autocomplete="username" type="email" inputmode="email" required value="${escapeHtml(authenticationForm.email)}" />
              </label>
              <label>
                ${l("Password", "كلمة المرور")}
                <input id="auth-password" autocomplete="current-password" type="password" required value="${escapeHtml(authenticationForm.password)}" />
              </label>
              <div class="actions">
                <button id="auth-sign-in" type="submit" ${isBusy ? "disabled" : ""}>${
                  isBusy ? l("Signing in...", "جار تسجيل الدخول...") : l("Sign in", "تسجيل الدخول")
                }</button>
                <button id="auth-reset" type="button" ${isBusy ? "disabled" : ""}>${l("Send reset email", "نسيت كلمة المرور؟")}</button>
              </div>
            </form>
            ${
              appleUnavailable
                ? ""
                : `<div class="secondary-auth-row">
                    <button id="auth-apple" type="button" ${isBusy ? "disabled" : ""}>${l("Continue with Apple", "المتابعة باستخدام Apple")}</button>
                  </div>`
            }
          </article>
        </main>
      </section>
    </div>
  `;

  enhanceCarbonDesignSystem(appRoot);
  bindAuthenticationHandlers(appRoot);
}

async function resolveCurrentPerson(expectedUid: string): Promise<void> {
  const requestId = currentPersonRequestId + 1;
  currentPersonRequestId = requestId;
  currentPersonState = { status: "loading" };
  renderRealtime();

  try {
    const person = await ensureCurrentPersonViaHttp({
      apiBaseUrl: environmentConfig.apiBaseUrl,
      fetchClient: (input, init) => backendFetch(input, init),
      getIdToken: async () => {
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.uid !== expectedUid) {
          return undefined;
        }
        return currentUser.getIdToken();
      },
    });

    if (requestId !== currentPersonRequestId) {
      return;
    }

    if (person.uid !== expectedUid) {
      throw new PersonIdentityError("invalid_response");
    }

    selectAuthenticatedIdentity(person.uid);
    currentPersonState = { status: "resolved", person };
    discoverAuthorizedOrganizations(person.uid);
  } catch (error) {
    if (requestId !== currentPersonRequestId) {
      return;
    }

    currentPersonState = {
      status: "error",
      message:
        error instanceof PersonIdentityError
          ? personIdentityErrorMessage(error.code)
          : personIdentityErrorMessage("unknown"),
    };
  } finally {
    if (requestId === currentPersonRequestId) {
      renderRealtime();
    }
  }
}

function renderAuthenticatedPortalShell(appRoot: HTMLElement, direction: "ltr" | "rtl"): void {
  if (authenticationState.status !== "authenticated") {
    return;
  }

  const user = authenticationState.user;
  const currentPersonMarkup =
    currentPersonState.status === "loading"
      ? `<p class="status">Resolving Global Person...</p>`
      : currentPersonState.status === "error"
        ? `
            <p class="error">${escapeHtml(currentPersonState.message)}</p>
            <button id="auth-retry-person" type="button">Try again</button>
          `
        : currentPersonState.status === "resolved"
          ? `
            <p class="status">Global Person: ${escapeHtml(currentPersonState.person.displayName)}</p>
            <p class="muted">Shdeed UMID: ${escapeHtml(currentPersonState.person.umid)}</p>
          `
          : `<p class="status">Resolving Global Person...</p>`;

  appRoot.innerHTML = `
    <div class="shell auth-shell">
      <section class="workspace auth-workspace" dir="${direction}">
        <header class="topbar">
          ${
            environmentConfig.bannerLabel
              ? `<span class="banner">${environmentConfig.bannerLabel} · ${projectId}</span>`
              : ""
          }
          <div class="controls">
            <label>
              Language
              <select id="locale">
                <option value="en" ${state.locale === "en" ? "selected" : ""}>English</option>
                <option value="ar" ${state.locale === "ar" ? "selected" : ""}>العربية</option>
              </select>
            </label>
            <button id="auth-sign-out" type="button">Sign out</button>
          </div>
        </header>
        <main class="content auth-content">
          <article class="panel auth-panel">
            <div class="auth-card-header">
              ${renderShdeedBrand(state.locale)}
              <div>
                <p class="eyebrow">Session check</p>
                <h1>Organization Admin Portal</h1>
                <p class="status">Signed in as ${escapeHtml(user.email ?? user.uid)}</p>
              </div>
            </div>
            ${currentPersonMarkup}
            <p class="muted">Firebase UID: ${escapeHtml(user.uid)}</p>
            <p class="muted">Organization authorization is checked through server-owned role assignment records.</p>
            ${authenticationForm.error ? `<p class="error">${authenticationForm.error}</p>` : ""}
          </article>
        </main>
      </section>
    </div>
  `;

  enhanceCarbonDesignSystem(appRoot);
  bindAuthenticationHandlers(appRoot);
}

function bindAuthenticationHandlers(appRoot: HTMLElement): void {
  appRoot.querySelector<HTMLSelectElement>("#locale")?.addEventListener("change", (event) => {
    state.locale = ((event.target as HTMLSelectElement).value as LocaleCode) || "en";
    render();
  });
  appRoot.querySelector<HTMLInputElement>("#auth-email")?.addEventListener("input", (event) => {
    authenticationForm.email = (event.target as HTMLInputElement).value;
    authenticationForm.resetSent = false;
  });
  appRoot.querySelector<HTMLInputElement>("#auth-password")?.addEventListener("input", (event) => {
    authenticationForm.password = (event.target as HTMLInputElement).value;
    authenticationForm.resetSent = false;
  });
  appRoot.querySelector<HTMLFormElement>("#auth-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void signInWithEmailPassword();
  });
  appRoot.querySelector<HTMLButtonElement>("#auth-reset")?.addEventListener("click", () => {
    void sendPasswordReset();
  });
  appRoot.querySelector<HTMLButtonElement>("#auth-google")?.addEventListener("click", () => {
    void signInWithProvider("google");
  });
  appRoot.querySelector<HTMLButtonElement>("#auth-apple")?.addEventListener("click", () => {
    void signInWithProvider("apple");
  });
  appRoot.querySelector<HTMLButtonElement>("#auth-sign-out")?.addEventListener("click", () => {
    void signOutOfPortal();
  });
  appRoot.querySelector<HTMLButtonElement>("#auth-retry-person")?.addEventListener("click", () => {
    if (authenticationState.status === "authenticated") {
      void resolveCurrentPerson(authenticationState.user.uid);
    }
  });
}

async function signInWithEmailPassword(): Promise<void> {
  authenticationForm.busy = true;
  authenticationForm.error = "";
  authenticationForm.resetSent = false;
  render();

  try {
    await authenticationService.signInWithEmailPassword({
      email: authenticationForm.email,
      password: authenticationForm.password,
    });
    authenticationForm.password = "";
  } catch (error) {
    authenticationForm.error = authErrorText(toAuthenticationError(error));
  } finally {
    authenticationForm.busy = false;
    if (auth.currentUser) renderRealtime();
    else render();
  }
}

async function sendPasswordReset(): Promise<void> {
  authenticationForm.busy = true;
  authenticationForm.error = "";
  authenticationForm.resetSent = false;
  render();

  try {
    await authenticationService.sendPasswordReset(authenticationForm.email);
    authenticationForm.resetSent = true;
  } catch (error) {
    authenticationForm.error = authErrorText(toAuthenticationError(error));
  } finally {
    authenticationForm.busy = false;
    render();
  }
}

async function signInWithProvider(provider: "apple" | "google"): Promise<void> {
  authenticationForm.busy = true;
  authenticationForm.error = "";
  authenticationForm.resetSent = false;
  render();

  try {
    if (provider === "google") {
      await authenticationService.signInWithGoogle();
    } else {
      await authenticationService.signInWithApple();
    }
  } catch (error) {
    authenticationForm.error = authErrorText(toAuthenticationError(error));
  } finally {
    authenticationForm.busy = false;
    if (auth.currentUser) renderRealtime();
    else render();
  }
}

async function signOutOfPortal(): Promise<void> {
  authenticationForm.busy = true;
  authenticationForm.error = "";
  accountProfileEditorOpen = false;
  activeEventOperationsWindowEventId = null;
  activeTaskWindowId = null;
  render();

  try {
    await authenticationService.signOut();
  } catch (error) {
    authenticationForm.error = authErrorText(toAuthenticationError(error));
  } finally {
    authenticationForm.busy = false;
    render();
  }
}

function resolvedCurrentPerson(): GlobalPerson | undefined {
  return currentPersonState.status === "resolved" ? currentPersonState.person : undefined;
}

function currentAccountEmailLabel(): string {
  const person = resolvedCurrentPerson();
  if (person?.email.default) {
    return person.email.default;
  }
  if (authenticationState.status === "authenticated" && authenticationState.user.email) {
    return authenticationState.user.email;
  }
  return "Authenticated account";
}

function currentAccountDisplayName(): string {
  const person = resolvedCurrentPerson();
  if (person?.displayName) {
    return person.displayName;
  }
  if (authenticationState.status === "authenticated" && authenticationState.user.email) {
    return authenticationState.user.email;
  }
  return "Signed-in admin";
}

function currentAccountInitials(): string {
  const displayName = currentAccountDisplayName();
  const initials = displayName
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "AC";
}

function renderAccountMenuControl(): string {
  const person = resolvedCurrentPerson();
  const profileDisabled = person ? "" : "disabled";
  const umidLine = person
    ? `<p class="sidebar-account-umid">UMID ${escapeHtml(person.umid)}</p>`
    : `<p class="sidebar-account-umid">Profile loading</p>`;

  return `
    <section class="sidebar-account-card" aria-label="Signed in account">
      <div class="sidebar-account-summary">
        <span class="sidebar-account-avatar" aria-hidden="true">${escapeHtml(currentAccountInitials())}</span>
        <div>
          <p class="sidebar-account-kicker">Logged in</p>
          <p class="sidebar-account-name">${escapeHtml(currentAccountDisplayName())}</p>
          <p class="sidebar-account-email">${escapeHtml(currentAccountEmailLabel())}</p>
          ${umidLine}
        </div>
      </div>
      <div class="sidebar-account-actions">
        <button class="secondary-action inline-action" data-account-profile-open type="button" ${profileDisabled}>
          Edit profile
        </button>
        <button class="secondary-action inline-action" data-auth-sign-out type="button">
          Log out
        </button>
      </div>
    </section>
  `;
}

function renderAccountProfileEditor(): string {
  if (!accountProfileEditorOpen) {
    return "";
  }

  const person = resolvedCurrentPerson();
  const identityLine = person
    ? `${person.email.default ?? "No email"} · ${person.umid}`
    : currentAccountEmailLabel();

  return `
    <section class="task-window-overlay account-profile-overlay" data-account-profile-overlay role="presentation">
      <article
        aria-labelledby="account-profile-title"
        aria-modal="true"
        class="task-window account-profile-window"
        role="dialog"
      >
        <header class="task-window-header">
          <div>
            <p class="meta">Your account</p>
            <h2 id="account-profile-title">Edit profile</h2>
            <p class="muted">${escapeHtml(identityLine)}</p>
          </div>
          <button
            aria-label="Close"
            class="secondary-action inline-action icon-action task-window-close"
            data-account-profile-close
            data-tooltip="Close"
            title="Close"
            type="button"
          >${renderCloseIcon()}</button>
        </header>
        <div class="task-window-body" data-scroll-window-id="account-profile">
          <form class="editor-form" id="account-profile-editor-form">
            <label>
              Name
              <input
                autocomplete="name"
                id="account-display-name"
                maxlength="120"
                minlength="2"
                required
                type="text"
                value="${escapeHtml(accountProfileEditor.displayName)}"
              />
            </label>
            <label>
              Phone
              <input
                autocomplete="tel"
                id="account-phone-number"
                inputmode="tel"
                placeholder="(914) 528-1626"
                type="tel"
                value="${escapeHtml(accountProfileEditor.phoneNumber)}"
              />
            </label>
            <label>
              Photo URL
              <input
                autocomplete="photo"
                id="account-photo-url"
                placeholder="https://example.org/profile.jpg"
                type="url"
                value="${escapeHtml(accountProfileEditor.photoUrl)}"
              />
            </label>
            ${accountProfileEditor.error ? `<p class="error">${escapeHtml(accountProfileEditor.error)}</p>` : ""}
            ${accountProfileEditor.message ? `<p class="status">${escapeHtml(accountProfileEditor.message)}</p>` : ""}
            <div class="editor-actions">
              <button type="submit" ${accountProfileEditor.busy ? "disabled" : ""}>
                ${accountProfileEditor.busy ? "Saving..." : "Save profile"}
              </button>
              <button class="secondary-action" data-account-profile-close type="button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </article>
    </section>
  `;
}

function renderEventMediaManager(selectedEvent: CommunityEventRow | undefined): string {
  const isArabic = state.locale === "ar";
  const labels = {
    addYoutube: isArabic ? "إضافة YouTube" : "Add YouTube",
    cover: isArabic ? "الغلاف" : "Cover",
    makeCover: isArabic ? "تعيين كغلاف" : "Set cover",
    media: isArabic ? "وسائط الفعالية" : "Event media",
    mediaHelp: isArabic
      ? "ارفع الصور أو الفيديو أو روابط YouTube هنا. أول صورة أو الصورة المحددة كغلاف تظهر في قوائم الجوال."
      : "Upload images, video, or YouTube links here. The first or selected image becomes the mobile list cover.",
    moveDown: isArabic ? "تحريك للأسفل" : "Move down",
    moveUp: isArabic ? "تحريك للأعلى" : "Move up",
    noMedia: isArabic ? "لا توجد وسائط مرتبة بعد." : "No ordered media yet.",
    pendingMedia: isArabic ? "جاهز للرفع" : "Ready to upload",
    remove: isArabic ? "إزالة" : "Remove",
    uploadImages: isArabic ? "رفع صور متعددة" : "Upload images",
    uploadVideo: isArabic ? "رفع فيديو واحد" : "Upload one video",
    youtube: isArabic ? "رابط YouTube" : "YouTube URL",
  };
  const coverMediaId = selectedEvent?.coverMediaId ?? null;
  const rows = state.eventMedia
    .map((media, index) => {
      const title = mediaMediaTitle(media, state.locale);
      const detail = eventMediaDetail(media);
      const isCover =
        media.type === "image" &&
        (media.mediaId === coverMediaId ||
          (!!media.storagePath && media.storagePath === selectedEvent?.imageStoragePath));
      const preview = mediaMediaPreview(media);

      return `<li class="event-media-row">
        <span class="event-media-preview">${preview}</span>
        <span>
          <strong>${escapeHtml(String(index + 1))}. ${escapeHtml(title)}</strong>
          <br><small>${escapeHtml(detail)}</small>
          ${isCover ? `<br><small class="status">${labels.cover}</small>` : ""}
        </span>
        <span class="event-media-actions">
          <button data-event-media-move="${escapeHtml(media.mediaId)}" data-event-media-direction="up" type="button" ${
            index === 0 || state.busy ? "disabled" : ""
          }>${labels.moveUp}</button>
          <button data-event-media-move="${escapeHtml(media.mediaId)}" data-event-media-direction="down" type="button" ${
            index === state.eventMedia.length - 1 || state.busy ? "disabled" : ""
          }>${labels.moveDown}</button>
          ${
            media.type === "image"
              ? `<button data-event-media-cover="${escapeHtml(media.mediaId)}" type="button" ${
                  isCover || state.busy ? "disabled" : ""
                }>${labels.makeCover}</button>`
              : ""
          }
          <button data-event-media-remove="${escapeHtml(media.mediaId)}" type="button" ${
            state.busy ? "disabled" : ""
          }>${labels.remove}</button>
        </span>
      </li>`;
    })
    .join("");
  const pendingRows = renderPendingEventMediaPreviews(labels.pendingMedia);

  return `<section class="event-media-manager">
    <h3>${labels.media}</h3>
    <p class="meta">${labels.mediaHelp}</p>
    <div class="editor-grid">
      <label>
        ${labels.uploadImages}
        <input id="event-images-file" accept="image/*" multiple type="file" />
      </label>
      <label>
        ${labels.uploadVideo}
        <input id="event-video-file" accept="video/mp4,video/quicktime,video/webm" type="file" />
      </label>
    </div>
    <div class="inline-field">
      <label>
        ${labels.youtube}
        <input id="event-youtube-url" maxlength="1000" value="${escapeHtml(
          state.eventYoutubeUrl,
        )}" />
      </label>
      <button id="add-event-youtube-media" type="button" ${state.busy ? "disabled" : ""}>${
        labels.addYoutube
      }</button>
    </div>
    ${state.eventMediaMessage ? `<p class="status">${escapeHtml(state.eventMediaMessage)}</p>` : ""}
    ${pendingRows}
    ${
      state.eventMedia.length > 0
        ? `<ul class="event-media-list">${rows}</ul>`
        : state.eventPendingMediaPreviews.length === 0
          ? `<p class="meta">${labels.noMedia}</p>`
          : ""
    }
  </section>`;
}

function renderPendingEventMediaPreviews(label: string): string {
  if (state.eventPendingMediaPreviews.length === 0) {
    return "";
  }

  const rows = state.eventPendingMediaPreviews
    .map((preview) => {
      const media =
        preview.kind === "image"
          ? `<img alt="${escapeHtml(preview.name)}" src="${escapeHtml(preview.url)}" />`
          : `<video aria-label="${escapeHtml(
              preview.name,
            )}" muted playsinline preload="metadata" src="${escapeHtml(preview.url)}"></video>`;
      const detail = `${label} · ${preview.type || preview.kind} · ${formatFileSize(preview.size)}`;
      return `<li class="event-media-row event-media-row-pending">
        <span class="event-media-preview">${media}</span>
        <span>
          <strong>${escapeHtml(preview.name)}</strong>
          <br><small>${escapeHtml(detail)}</small>
        </span>
        <span class="status-pill status-pending">${escapeHtml(label)}</span>
      </li>`;
    })
    .join("");

  return `<ul class="event-media-list event-pending-media-list">${rows}</ul>`;
}

function imamContentTitle(content: ImamContentRow): string {
  return state.locale === "ar" && content.titleAr ? content.titleAr : content.title;
}

function imamContentBody(content: ImamContentRow): string {
  return state.locale === "ar" && content.bodyAr ? content.bodyAr : content.body;
}

function imamContentTypeLabel(content: ImamContentRow): string {
  if (content.type === "ramadan_message") {
    return state.locale === "ar" ? "رسالة رمضان" : "Ramadan message";
  }
  return state.locale === "ar" ? "ملف قيادة" : "Leadership profile";
}

function imamContentImageAlt(content: ImamContentRow): string {
  return (
    (state.locale === "ar" && content.imageAltTextAr
      ? content.imageAltTextAr
      : content.imageAltText) ?? imamContentTitle(content)
  );
}

function imamContentInitials(content: ImamContentRow): string {
  return imamContentTitle(content)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function renderImamContentPhotoPreview(content: ImamContentRow): string {
  const url = state.imamContentImageDownloadUrls[content.contentId];
  if (url) {
    return `<img alt="${escapeHtml(imamContentImageAlt(content))}" src="${escapeHtml(url)}" />`;
  }

  return `<span aria-hidden="true">${escapeHtml(imamContentInitials(content) || "IP")}</span>`;
}

function imamContentMediaSummary(content: ImamContentRow): string {
  const imageCount = [
    content.imageStoragePath,
    content.galleryImage1?.storagePath,
    content.galleryImage2?.storagePath,
    content.galleryImage3?.storagePath,
  ].filter(Boolean).length;
  const parts = [
    `${imageCount} ${state.locale === "ar" ? "صور" : imageCount === 1 ? "image" : "images"}`,
  ];
  if (content.youtube?.canonicalUrl) {
    parts.push(state.locale === "ar" ? "فيديو" : "video");
  }

  return parts.join(" · ");
}

function renderRamadanFeaturedContentEditor(): string {
  const publishedRamadanMessages = state.imamContents.filter(
    (content) => content.type === "ramadan_message" && content.status === "published",
  );
  const selectedId = state.ramadanSetupEditor.featuredRamadanContentId.trim();
  const selectedContent = state.imamContents.find((content) => content.contentId === selectedId);
  const selectedUnavailable =
    selectedId.length > 0 &&
    (!selectedContent ||
      selectedContent.type !== "ramadan_message" ||
      selectedContent.status !== "published");

  return `
    <section class="ramadan-editor-section">
      <h3>Featured Ramadan message</h3>
      <p class="meta">Select a published Ramadan Message content item. Create or edit the content in the shared Content editor.</p>
      <label>
        Featured content
        <select data-ramadan-setup-editor-field="featuredRamadanContentId">
          <option value="" ${selectedId.length === 0 ? "selected" : ""}>No featured message</option>
          ${publishedRamadanMessages
            .map(
              (content) => `
                <option value="${escapeHtml(content.contentId)}" ${
                  content.contentId === selectedId ? "selected" : ""
                }>${escapeHtml(imamContentTitle(content))}</option>
              `,
            )
            .join("")}
        </select>
      </label>
      ${
        selectedContent
          ? `<div class="inline-summary">
              <strong>${escapeHtml(imamContentTitle(selectedContent))}</strong>
              <small>${escapeHtml(imamContentMediaSummary(selectedContent))} · ${escapeHtml(
                selectedContent.status,
              )}</small>
              <p>${escapeHtml(imamContentBody(selectedContent).slice(0, 220))}</p>
            </div>`
          : selectedUnavailable
            ? `<p class="status error">Selected content is unavailable, unpublished, or not a Ramadan Message.</p>`
            : `<p class="meta">No featured Ramadan message selected.</p>`
      }
      <div class="editor-actions compact-actions">
        <button
          class="secondary-action"
          data-ramadan-featured-content-action="create"
          type="button"
          ${state.busy ? "disabled" : ""}
        >Create Ramadan message</button>
        <button
          class="secondary-action"
          data-ramadan-featured-content-action="edit"
          data-content-id="${escapeHtml(selectedContent?.contentId ?? "")}"
          type="button"
          ${state.busy || !selectedContent ? "disabled" : ""}
        >Edit selected</button>
        <button
          class="secondary-action"
          data-ramadan-featured-content-action="clear"
          type="button"
          ${state.busy || selectedId.length === 0 ? "disabled" : ""}
        >Clear selection</button>
      </div>
    </section>
  `;
}

function renderImamContentSection(): string {
  const copy = shellCopy[state.locale];

  return `
    <article id="section-content" class="panel leadership-content-panel">
      <div class="list-toolbar">
        <div>
          <h2>${copy.imamContent}</h2>
          <p class="meta">${
            state.imamContents.length
          } ${state.locale === "ar" ? "عناصر محتوى" : "content items"}</p>
        </div>
        <button
          class="secondary-action inline-action"
          data-imam-content-create
          type="button"
          ${state.busy ? "disabled" : ""}
        >
          ${copy.newImamContent}
        </button>
      </div>
      ${
        state.imamContents.length === 0
          ? `<p class="meta">${copy.imamContentEmpty}</p>`
          : `<div class="table-scroll leadership-table-scroll">
              <table class="data-table leadership-table">
                <thead>
                  <tr>
	                    <th>${copy.imamContentImagePreview}</th>
	                    <th>${copy.imamContentTitle}</th>
	                    <th>${state.locale === "ar" ? "النوع" : "Type"}</th>
	                    <th>${copy.imamContentStatus}</th>
                    <th>${copy.imamContentMedia}</th>
                    <th>${copy.imamContentSortOrder}</th>
                    <th>${copy.imamContentActions}</th>
                  </tr>
                </thead>
                <tbody>
                  ${state.imamContents
                    .map((content) => {
                      const title = imamContentTitle(content);
                      const body = imamContentBody(content);
                      const publishedAt = content.publishedAt
                        ? content.publishedAt.toLocaleString(state.locale)
                        : "-";
                      const statusClass =
                        content.status === "published"
                          ? "status-accepted"
                          : content.status === "archived"
                            ? "status-rejected"
                            : "status-pending";
                      const statusLabel =
                        content.status === "published"
                          ? copy.imamContentPublished
                          : content.status === "archived"
                            ? copy.imamContentArchived
                            : copy.imamContentDraft;
                      const publishDisabled = state.busy || content.status === "published";
                      const disableDisabled = state.busy || content.status === "archived";

                      return `
                        <tr>
                          <td>
                            <span class="leadership-photo-preview">
                              ${renderImamContentPhotoPreview(content)}
                            </span>
                          </td>
	                          <td>
	                            <strong>${escapeHtml(title)}</strong>
	                            <small>${escapeHtml(body)}</small>
                            ${
                              content.featured
                                ? `<span class="status-pill status-accepted">${copy.imamContentFeatured}</span>`
                                : ""
                            }
	                          </td>
	                          <td>${escapeHtml(imamContentTypeLabel(content))}</td>
	                          <td>
                            <span class="status-pill ${statusClass}">${escapeHtml(statusLabel)}</span>
                            <small>${escapeHtml(publishedAt)}</small>
                          </td>
                          <td><small>${escapeHtml(imamContentMediaSummary(content))}</small></td>
                          <td>${content.sortOrder}</td>
                          <td>
                            <div class="table-actions">
                              <button
                                aria-label="${escapeHtml(`${copy.editImamContent}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-imam-content-edit="${escapeHtml(content.contentId)}"
                                data-tooltip="${escapeHtml(copy.editImamContent)}"
                                title="${escapeHtml(copy.editImamContent)}"
                                type="button"
                                ${state.busy ? "disabled" : ""}
                              >${renderEventActionIcon("edit")}</button>
                              <button
                                aria-label="${escapeHtml(`${copy.publishImamContent}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-imam-content-status-action="published"
                                data-imam-content-status-id="${escapeHtml(content.contentId)}"
                                data-tooltip="${escapeHtml(copy.publishImamContent)}"
                                title="${escapeHtml(copy.publishImamContent)}"
                                type="button"
                                ${publishDisabled ? "disabled" : ""}
                              >${renderEventActionIcon("read")}</button>
                              <button
                                aria-label="${escapeHtml(`${copy.disableImamContent}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-imam-content-status-action="archived"
                                data-imam-content-status-id="${escapeHtml(content.contentId)}"
                                data-tooltip="${escapeHtml(copy.disableImamContent)}"
                                title="${escapeHtml(copy.disableImamContent)}"
                                type="button"
                                ${disableDisabled ? "disabled" : ""}
                              >${renderEventActionIcon("delist")}</button>
                              <button
                                aria-label="${escapeHtml(`${copy.deleteImamContent}: ${title}`)}"
                                class="danger-action inline-action icon-action"
                                data-imam-content-delete="${escapeHtml(content.contentId)}"
                                data-tooltip="${escapeHtml(copy.deleteImamContent)}"
                                title="${escapeHtml(copy.deleteImamContent)}"
                                type="button"
                                ${state.busy ? "disabled" : ""}
                              >${renderEventActionIcon("delete")}</button>
                            </div>
                          </td>
                        </tr>
                      `;
                    })
                    .join("")}
                </tbody>
              </table>
            </div>`
      }
      ${renderImamContentEditorForm()}
    </article>
  `;
}

function renderImamContentEditorForm(): string {
  const copy = shellCopy[state.locale];
  const selectedPrimaryFile = state.imamContentImageFile
    ? `${state.imamContentImageFile.name} · ${formatFileSize(state.imamContentImageFile.size)}`
    : "";
  const selectedGalleryFiles = state.imamContentGalleryImageFiles
    .map((file) => `${file.name} · ${formatFileSize(file.size)}`)
    .join(", ");

  return `
    <form
      id="imam-content-editor-form"
      class="editor-form"
      data-task-window-launch="manual"
    >
      <div class="editor-grid">
        <label>
          ${copy.imamContentId}
          <input
            data-imam-content-editor-field="contentId"
            maxlength="80"
            value="${escapeHtml(state.imamContentEditor.contentId)}"
          />
        </label>
        <label>
          Content type
          <select data-imam-content-editor-field="contentType">
            <option value="imam_profile" ${
              state.imamContentEditor.contentType === "imam_profile" ? "selected" : ""
            }>Leadership profile</option>
            <option value="ramadan_message" ${
              state.imamContentEditor.contentType === "ramadan_message" ? "selected" : ""
            }>Ramadan message</option>
          </select>
        </label>
        <label>
          ${copy.imamContentStatus}
          <select data-imam-content-editor-field="status">
            <option value="draft" ${
              state.imamContentEditor.status === "draft" ? "selected" : ""
            }>${copy.imamContentDraft}</option>
            <option value="published" ${
              state.imamContentEditor.status === "published" ? "selected" : ""
            }>${copy.imamContentPublished}</option>
            <option value="archived" ${
              state.imamContentEditor.status === "archived" ? "selected" : ""
            }>${copy.imamContentArchived}</option>
          </select>
        </label>
      </div>
      <div class="editor-grid">
        <label>
          ${copy.imamContentTitle}
          <input
            data-imam-content-editor-field="title"
            maxlength="140"
            required
            value="${escapeHtml(state.imamContentEditor.title)}"
          />
        </label>
        <label>
          ${copy.imamContentTitleAr}
          <input
            data-imam-content-editor-field="titleAr"
            maxlength="140"
            value="${escapeHtml(state.imamContentEditor.titleAr)}"
          />
        </label>
      </div>
      <label>
        ${copy.imamContentSubtitle}
        <input
          data-imam-content-editor-field="subtitle"
          maxlength="180"
          value="${escapeHtml(state.imamContentEditor.subtitle)}"
        />
      </label>
      <label>
        ${copy.imamContentSubtitleAr}
        <input
          data-imam-content-editor-field="subtitleAr"
          maxlength="180"
          value="${escapeHtml(state.imamContentEditor.subtitleAr)}"
        />
      </label>
      <label>
        ${copy.imamContentBody}
        <textarea
          data-imam-content-editor-field="body"
          maxlength="2400"
          required
        >${escapeHtml(state.imamContentEditor.body)}</textarea>
      </label>
      <label>
        ${copy.imamContentBodyAr}
        <textarea
          data-imam-content-editor-field="bodyAr"
          maxlength="2400"
        >${escapeHtml(state.imamContentEditor.bodyAr)}</textarea>
      </label>
      <section class="form-section">
        <h3>${copy.imamContentMedia}</h3>
        <input
          data-imam-content-editor-field="imageStoragePath"
          type="hidden"
          value="${escapeHtml(state.imamContentEditor.imageStoragePath)}"
        />
        <input
          data-imam-content-editor-field="galleryImageStoragePath1"
          type="hidden"
          value="${escapeHtml(state.imamContentEditor.galleryImageStoragePath1)}"
        />
        <input
          data-imam-content-editor-field="galleryImageStoragePath2"
          type="hidden"
          value="${escapeHtml(state.imamContentEditor.galleryImageStoragePath2)}"
        />
        <input
          data-imam-content-editor-field="galleryImageStoragePath3"
          type="hidden"
          value="${escapeHtml(state.imamContentEditor.galleryImageStoragePath3)}"
        />
        <label>
          ${copy.imamContentImageFile}
          <input id="imam-content-image-file" accept="image/*" type="file" />
        </label>
        ${selectedPrimaryFile ? `<p class="meta">${escapeHtml(selectedPrimaryFile)}</p>` : ""}
        <label>
          ${copy.imamContentGalleryImageFiles}
          <input id="imam-content-gallery-image-files" accept="image/*" multiple type="file" />
        </label>
        ${selectedGalleryFiles ? `<p class="meta">${escapeHtml(selectedGalleryFiles)}</p>` : ""}
        <label>
          ${copy.imamContentYoutubeUrl}
          <input
            data-imam-content-editor-field="youtubeUrl"
            inputmode="url"
            maxlength="1000"
            value="${escapeHtml(state.imamContentEditor.youtubeUrl)}"
          />
        </label>
      </section>
      <div class="editor-grid">
        <label>
          ${copy.imamContentSortOrder}
          <input
            data-imam-content-editor-field="sortOrder"
            min="0"
            max="10000"
            step="1"
            type="number"
            value="${escapeHtml(state.imamContentEditor.sortOrder)}"
          />
        </label>
        <label class="checkbox-row">
          <input
            data-imam-content-editor-field="featured"
            type="checkbox"
            ${state.imamContentEditor.featured ? "checked" : ""}
          />
          ${copy.imamContentFeatured}
        </label>
      </div>
      <div class="editor-actions">
        <button type="submit" ${state.busy ? "disabled" : ""}>${copy.saveImamContent}</button>
        <button id="reset-imam-content-form" type="button">${copy.resetImamContentForm}</button>
      </div>
    </form>
  `;
}

function announcementActorLabel(personId: string): string {
  if (currentPersonState.status === "resolved" && currentPersonState.person.uid === personId) {
    return currentPersonState.person.displayName;
  }
  const name = state.personProfiles[personId]?.displayName?.trim();
  if (name) return name;
  return state.locale === "ar" ? "فريق المؤسسة" : "Organization staff";
}

function renderCommunityEngagementSection(organizationDisplayName: string): string {
  const copy = shellCopy[state.locale];
  const readLabel = state.locale === "ar" ? "عرض" : "Read";
  const updatedByLabel = state.locale === "ar" ? "تم التحديث بواسطة" : "Updated by";

  return `
    <article id="section-engagement" class="panel engagement-listings-panel">
      <div class="list-toolbar">
        <div>
          <h2>${copy.communityEngagement}</h2>
          <p class="meta">${copy.announcements}: ${state.announcements.length}</p>
        </div>
        <button
          class="secondary-action inline-action"
          data-announcement-create
          type="button"
          ${state.busy ? "disabled" : ""}
        >
          ${copy.newAnnouncement}
        </button>
      </div>
      ${
        state.engagementDetailMessage
          ? `<p class="status status-inline">${escapeHtml(state.engagementDetailMessage)}</p>`
          : ""
      }
      <section class="engagement-listing-section">
        <h3>${copy.announcements}</h3>
        ${
          state.announcements.length === 0
            ? `<p class="meta">No announcements for ${escapeHtml(organizationDisplayName)}.</p>`
            : `<div class="table-scroll" role="region" aria-label="${escapeHtml(copy.announcements)}">
                <table class="data-table engagement-table">
                  <thead>
                    <tr>
                      <th>${copy.announcements}</th>
                      <th>${copy.announcementStatus}</th>
                      <th>${copy.announcementPriority}</th>
                      <th>${copy.eventPublished}</th>
                      <th>${copy.announcementExpiresAt}</th>
                      <th>${updatedByLabel}</th>
                      <th>${copy.announcementActions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${state.announcements
                      .map((announcement) => {
                        const title =
                          state.locale === "ar"
                            ? announcement.titleAr || announcement.title
                            : announcement.title;
                        const body =
                          state.locale === "ar"
                            ? announcement.bodyAr || announcement.body
                            : announcement.body;
                        const publishedAt = announcement.publishedAt
                          ? announcement.publishedAt.toLocaleString(state.locale)
                          : "-";
                        const isArchived = announcement.status === "archived";
                        const archiveActionLabel = isArchived
                          ? copy.republishAnnouncement
                          : copy.archiveAnnouncement;
                        const archiveAction = isArchived ? "published" : "archived";
                        return `<tr>
                          <td>
                            <strong>${escapeHtml(title)}</strong>
                            <small>${escapeHtml(body)}</small>
                          </td>
                          <td>
                            <span class="status-pill ${announcementStatusPillClass(
                              announcement,
                            )}">${escapeHtml(announcementStatusLabel(announcement))}</span>
                          </td>
                          <td>
                            <span class="status-pill ${
                              announcement.priority === "urgent"
                                ? "status-rejected"
                                : "status-accepted"
                            }">${escapeHtml(announcement.priority)}</span>
                          </td>
                          <td>${escapeHtml(publishedAt)}<small>${escapeHtml(announcementActorLabel(announcement.createdBy))}</small></td>
                          <td>${escapeHtml(announcementExpirationLabel(announcement))}</td>
                          <td>${escapeHtml(announcementActorLabel(announcement.updatedBy))}</td>
                          <td>
                            <div class="table-actions">
                              <button
                                aria-label="${escapeHtml(`${readLabel}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-announcement-read="${escapeHtml(announcement.id)}"
                                data-tooltip="${escapeHtml(readLabel)}"
                                title="${escapeHtml(readLabel)}"
                                type="button"
                              >${renderEventActionIcon("read")}</button>
                              <button
                                aria-label="${escapeHtml(`${copy.editAnnouncement}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-announcement-edit="${escapeHtml(announcement.id)}"
                                data-tooltip="${escapeHtml(copy.editAnnouncement)}"
                                title="${escapeHtml(copy.editAnnouncement)}"
                                type="button"
                                ${state.busy ? "disabled" : ""}
                              >${renderEventActionIcon("edit")}</button>
                              <button
                                aria-label="${escapeHtml(`${archiveActionLabel}: ${title}`)}"
                                class="secondary-action inline-action icon-action"
                                data-announcement-status-action="${escapeHtml(archiveAction)}"
                                data-announcement-status-id="${escapeHtml(announcement.id)}"
                                data-tooltip="${escapeHtml(archiveActionLabel)}"
                                title="${escapeHtml(archiveActionLabel)}"
                                type="button"
                                ${state.busy ? "disabled" : ""}
                              >${renderEventActionIcon(isArchived ? "checkIn" : "delist")}</button>
                            </div>
                          </td>
                        </tr>`;
                      })
                      .join("")}
                  </tbody>
                </table>
              </div>`
        }
      </section>
      <form
        id="announcement-composer-form"
        class="editor-form"
        data-task-window-launch="manual"
      >
          <input type="hidden"
            data-announcement-composer-field="announcementId"
            name="announcementId"
            maxlength="80"
            value="${escapeHtml(state.announcementComposer.announcementId)}"
            ${state.announcementComposerEditingId ? "readonly" : ""}
          />
        <label>
          ${copy.announcementTitle}
          <input
            data-announcement-composer-field="title"
            name="title"
            maxlength="140"
            required
            value="${escapeHtml(state.announcementComposer.title)}"
          />
        </label>
        <label>
          ${copy.announcementTitleAr}
          <input
            data-announcement-composer-field="titleAr"
            name="titleAr"
            maxlength="140"
            value="${escapeHtml(state.announcementComposer.titleAr)}"
          />
        </label>
        <label>
          ${copy.announcementBody}
          <textarea
            data-announcement-composer-field="body"
            name="body"
            maxlength="1200"
            required
          >${escapeHtml(state.announcementComposer.body)}</textarea>
        </label>
        <label>
          ${copy.announcementBodyAr}
          <textarea
            data-announcement-composer-field="bodyAr"
            name="bodyAr"
            maxlength="1200"
          >${escapeHtml(state.announcementComposer.bodyAr)}</textarea>
        </label>
        <label>
          ${copy.announcementPriority}
          <select
            data-announcement-composer-field="priority"
            name="priority"
          >
            <option value="normal" ${
              state.announcementComposer.priority === "normal" ? "selected" : ""
            }>${copy.priorityNormal}</option>
            <option value="urgent" ${
              state.announcementComposer.priority === "urgent" ? "selected" : ""
            }>${copy.priorityUrgent}</option>
          </select>
        </label>
        <label>
          ${copy.announcementExpiresAt}
          <input
            data-announcement-composer-field="expiresAt"
            name="expiresAt"
            type="datetime-local"
            value="${escapeHtml(state.announcementComposer.expiresAt)}"
          />
        </label>
        <label class="checkbox-row">
          <input type="checkbox" name="pushNotificationEnabled" data-announcement-composer-field="pushNotificationEnabled" ${state.announcementComposer.pushNotificationEnabled ? "checked" : ""} />
          <span>${state.locale === "ar" ? "إرسال إشعار دفع إلى التطبيق" : "Send a push notification to the app"}</span>
        </label>
        <div class="editor-actions">
          <button type="submit" ${state.busy ? "disabled" : ""}>${copy.saveAnnouncement}</button>
          <button id="reset-announcement-form" type="button">${copy.resetAnnouncementForm}</button>
        </div>
      </form>
    </article>
  `;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 KB";
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function mediaMediaTitle(media: EventMediaRow, locale: LocaleCode): string {
  if (locale === "ar" && media.titleAr) {
    return media.titleAr;
  }

  return media.title ?? media.videoId ?? media.mediaId;
}

function mediaMediaPreview(media: EventMediaRow): string {
  if (media.type === "youtube" && media.thumbnailUrl) {
    return `<img alt="" src="${escapeHtml(media.thumbnailUrl)}" />`;
  }

  if (media.type === "image" && media.originalUrl) {
    return `<img alt="" src="${escapeHtml(media.originalUrl)}" />`;
  }

  const icon =
    media.type === "uploadedVideo"
      ? "play_circle"
      : media.type === "youtube"
        ? "smart_display"
        : "image";
  return `<span class="material-symbol">${icon}</span>`;
}

function eventMediaDetail(media: EventMediaRow): string {
  const parts = [media.type, media.storagePath ?? media.canonicalUrl ?? media.originalUrl ?? ""]
    .filter(Boolean)
    .join(" · ");
  return parts || media.mediaId;
}

function resetAccountProfileEditor(): void {
  const person = resolvedCurrentPerson();
  accountProfileEditor.displayName = person?.displayName ?? currentAccountDisplayName();
  accountProfileEditor.phoneNumber = person?.phoneNumber ?? "";
  accountProfileEditor.photoUrl = person?.photoUrl ?? "";
  accountProfileEditor.error = "";
  accountProfileEditor.message = "";
}

function bindAccountProfileControls(appRoot: HTMLElement): void {
  appRoot.querySelectorAll<HTMLButtonElement>("[data-auth-sign-out]").forEach((button) => {
    button.addEventListener("click", () => {
      void signOutOfPortal();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-account-profile-open]").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.disabled) {
        return;
      }
      resetAccountProfileEditor();
      activeTaskWindowId = null;
      accountProfileEditorOpen = true;
      render();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-account-profile-close]").forEach((button) => {
    button.addEventListener("click", () => {
      accountProfileEditorOpen = false;
      render();
    });
  });
  appRoot
    .querySelector<HTMLElement>("[data-account-profile-overlay]")
    ?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) {
        accountProfileEditorOpen = false;
        render();
      }
    });
  appRoot
    .querySelector<HTMLInputElement>("#account-display-name")
    ?.addEventListener("input", (event) => {
      accountProfileEditor.displayName = (event.target as HTMLInputElement).value;
    });
  appRoot
    .querySelector<HTMLInputElement>("#account-phone-number")
    ?.addEventListener("input", (event) => {
      accountProfileEditor.phoneNumber = (event.target as HTMLInputElement).value;
    });
  appRoot
    .querySelector<HTMLInputElement>("#account-photo-url")
    ?.addEventListener("input", (event) => {
      accountProfileEditor.photoUrl = (event.target as HTMLInputElement).value;
    });
  appRoot
    .querySelector<HTMLFormElement>("#account-profile-editor-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveAccountProfileFromEditor();
    });
}

async function saveAccountProfileFromEditor(): Promise<void> {
  accountProfileEditor.busy = true;
  accountProfileEditor.error = "";
  accountProfileEditor.message = "";
  render();

  try {
    const response = await postCurrentUserAuthenticatedFunction("updateCurrentPersonProfile", {
      displayName: accountProfileEditor.displayName,
      phoneNumber: optionalAccountProfileField(accountProfileEditor.phoneNumber),
      photoUrl: optionalAccountProfileField(accountProfileEditor.photoUrl),
    });
    currentPersonState = {
      status: "resolved",
      person: globalPersonFromApiResponse(response.person),
    };
    accountProfileEditor.message = "Profile saved.";
    accountProfileEditorOpen = false;
  } catch (error) {
    accountProfileEditor.error = accountProfileUpdateErrorText(error);
  } finally {
    accountProfileEditor.busy = false;
    render();
  }
}

function optionalAccountProfileField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function accountProfileUpdateErrorText(error: unknown): string {
  if (error instanceof PersonIdentityError) {
    return personIdentityErrorMessage(error.code);
  }
  if (error instanceof Error && error.message === "invalid_person_profile_update") {
    return "Check the name, phone number, and photo URL before saving.";
  }
  return error instanceof Error ? error.message : String(error);
}

function shouldRenderAuthenticationOnlyShell(): boolean {
  return currentPersonState.status !== "resolved";
}

type WorkspacePageDefinition = WorkspaceRouteDefinition & {
  description: string;
  groupLabel: string;
  label: string;
  liveSummary: string;
  panelIds: readonly string[];
};

function workspacePageLabel(locale: LocaleCode, english: string, arabic: string): string {
  return locale === "ar" ? arabic : english;
}

const supportAttachmentAccept = supportAttachmentMimeTypes.join(",");

function activeSupportTicketCount(tickets: ReadonlyArray<{ status: SupportTicketStatus }>): number {
  return tickets.filter((ticket) => ticket.status !== "Resolved" && ticket.status !== "Closed")
    .length;
}

async function loadOrganizationSupportStaff(): Promise<void> {
  const scope = currentModeScope();
  try {
    const response = await postAuthenticatedFunction("listOrganizationSupportStaff", {
      organizationId: state.organizationId,
    });
    if (retiringTenant || scope !== currentModeScope()) return;
    supportStaff =
      Array.isArray(response.staff) && response.organizationId === state.organizationId
        ? response.staff.filter(
            (row): row is { personId: string; displayName: string } =>
              isRecord(row) &&
              typeof row.personId === "string" &&
              typeof row.displayName === "string",
          )
        : [];
  } catch (error) {
    if (retiringTenant || scope !== currentModeScope()) return;
    supportStaff = [];
    state.memberSupportError = error instanceof Error ? error.message : "support_staff_unavailable";
  }
  renderRealtime();
}

function supportAssigneeLabel(personId: string): string {
  if (!personId) {
    return "Unassigned";
  }
  const row = supportStaff.find((candidate) => candidate.personId === personId);
  return row?.displayName ?? (state.locale === "ar" ? "موظف الدعم" : "Support staff");
}

function renderSupportStatusOptions(selected: SupportStatusFilter): string {
  return [
    `<option value="all" ${selected === "all" ? "selected" : ""}>All statuses</option>`,
    ...supportTicketStatuses.map(
      (status) =>
        `<option value="${escapeHtml(status)}" ${selected === status ? "selected" : ""}>${escapeHtml(
          status,
        )}</option>`,
    ),
  ].join("");
}

function renderSupportPriorityOptions(selected: SupportPriorityFilter): string {
  return [
    `<option value="all" ${selected === "all" ? "selected" : ""}>All priorities</option>`,
    ...supportTicketPriorities.map(
      (priority) =>
        `<option value="${escapeHtml(priority)}" ${
          selected === priority ? "selected" : ""
        }>${escapeHtml(priority)}</option>`,
    ),
  ].join("");
}

function renderSupportEditablePriorityOptions(selected: SupportTicketPriority): string {
  return supportTicketPriorities
    .map(
      (priority) =>
        `<option value="${escapeHtml(priority)}" ${
          selected === priority ? "selected" : ""
        }>${escapeHtml(priority)}</option>`,
    )
    .join("");
}

function renderSupportEditableStatusOptions(selected: SupportTicketStatus): string {
  return supportTicketStatuses
    .map(
      (status) =>
        `<option value="${escapeHtml(status)}" ${selected === status ? "selected" : ""}>${escapeHtml(
          status,
        )}</option>`,
    )
    .join("");
}

function renderSupportCategoryOptions(
  categories: readonly string[],
  selected: string,
  includeAll = true,
): string {
  return [
    ...(includeAll
      ? [`<option value="all" ${selected === "all" ? "selected" : ""}>All categories</option>`]
      : []),
    ...categories.map(
      (category) =>
        `<option value="${escapeHtml(category)}" ${
          selected === category ? "selected" : ""
        }>${escapeHtml(category)}</option>`,
    ),
  ].join("");
}

function renderSupportAssignmentFilterOptions(selected: SupportAssignmentFilter): string {
  return (
    [
      ["all", "All assignments"],
      ["unassigned", "Unassigned"],
      ["assigned", "Assigned"],
      ["mine", "Assigned to me"],
    ] as const
  )
    .map(
      ([value, label]) =>
        `<option value="${escapeHtml(value)}" ${
          selected === value ? "selected" : ""
        }>${escapeHtml(label)}</option>`,
    )
    .join("");
}

function renderSupportAssigneeOptions(selectedPersonId: string): string {
  return [
    `<option value="" ${selectedPersonId ? "" : "selected"}>Unassigned</option>`,
    ...supportStaff.map((row) => {
      const personId = row.personId;
      return `<option value="${escapeHtml(personId)}" ${
        personId === selectedPersonId ? "selected" : ""
      }>${escapeHtml(row.displayName)}</option>`;
    }),
  ].join("");
}

function filteredMemberSupportTickets(): MemberSupportTicketRow[] {
  const query = state.memberSupportSearch.trim().toLowerCase();
  return state.memberSupportTickets.filter((ticket) => {
    if (
      state.memberSupportStatusFilter !== "all" &&
      ticket.status !== state.memberSupportStatusFilter
    ) {
      return false;
    }
    if (
      state.memberSupportCategoryFilter !== "all" &&
      ticket.category !== state.memberSupportCategoryFilter
    ) {
      return false;
    }
    if (
      state.memberSupportPriorityFilter !== "all" &&
      ticket.internalPriority !== state.memberSupportPriorityFilter
    ) {
      return false;
    }
    if (
      !supportAssignmentFilterMatches(
        ticket.assignedToPersonId,
        state.memberSupportAssignmentFilter,
      )
    ) {
      return false;
    }
    if (!query) {
      return true;
    }
    return [
      ticket.ticketNumber,
      ticket.subject,
      ticket.category,
      ticket.requesterDisplayName,
      ticket.requesterEmail,
      ticket.status,
      ticket.internalPriority,
      ticket.requesterUrgency,
      ticket.lastMessagePreview,
      supportAssigneeLabel(ticket.assignedToPersonId),
    ].some((value) => value.toLowerCase().includes(query));
  });
}

function filteredShdeedSupportTickets(): ShdeedSupportTicketRow[] {
  const query = state.shdeedSupportSearch.trim().toLowerCase();
  return state.shdeedSupportTickets.filter((ticket) => {
    if (
      state.shdeedSupportStatusFilter !== "all" &&
      ticket.status !== state.shdeedSupportStatusFilter
    ) {
      return false;
    }
    if (
      state.shdeedSupportCategoryFilter !== "all" &&
      ticket.category !== state.shdeedSupportCategoryFilter
    ) {
      return false;
    }
    if (
      state.shdeedSupportPriorityFilter !== "all" &&
      ticket.internalPriority !== state.shdeedSupportPriorityFilter
    ) {
      return false;
    }
    if (
      !supportAssignmentFilterMatches(
        ticket.assignedToPersonId,
        state.shdeedSupportAssignmentFilter,
      )
    ) {
      return false;
    }
    if (!query) {
      return true;
    }
    return [
      ticket.ticketNumber,
      ticket.subject,
      ticket.category,
      ticket.organizationNameSnapshot,
      ticket.requesterAdminDisplayName,
      ticket.requesterAdminEmail,
      ticket.status,
      ticket.internalPriority,
      ticket.requesterUrgency,
      ticket.lastMessagePreview,
      supportAssigneeLabel(ticket.assignedToPersonId),
    ].some((value) => value.toLowerCase().includes(query));
  });
}

function supportAssignmentFilterMatches(
  assignedToPersonId: string,
  filter: SupportAssignmentFilter,
): boolean {
  switch (filter) {
    case "assigned":
      return assignedToPersonId.length > 0;
    case "mine":
      return assignedToPersonId === state.userUid;
    case "unassigned":
      return assignedToPersonId.length === 0;
    case "all":
      return true;
  }
}

function renderMemberSupportPanel(): string {
  const canManageSupport = selectedContextCanManageSupport();
  const tickets = filteredMemberSupportTickets();
  const selectedTicket = state.memberSupportTickets.find(
    (ticket) => ticket.ticketId === state.memberSupportSelectedTicketId,
  );

  if (canManageSupport && selectedTicket) {
    return `${renderSupportBackButton("member")}
      ${state.memberSupportError ? `<p class="error" role="alert">${escapeHtml(adminFeedback(state.memberSupportError, state.locale))}</p>` : ""}
      ${state.memberSupportMessage ? `<p role="status">${escapeHtml(state.memberSupportMessage)}</p>` : ""}
      ${renderMemberSupportTicketDetail(selectedTicket)}`;
  }

  return `
    <div class="list-toolbar">
      <div>
        <h2>Member Support</h2>
        <p class="meta">${state.locale === "ar" ? "طلبات خاصة من الأشخاص المتصلين بمؤسستك." : "Private requests from people connected to your organization."}</p>
      </div>
      <div class="list-toolbar-actions support-filter-grid">
        <label class="list-search">
          <span>Search member tickets</span>
          <input id="member-support-search" maxlength="160" placeholder="Ticket, requester, subject..." value="${escapeHtml(
            state.memberSupportSearch,
          )}" />
        </label>
        <label>
          Status
          <select id="member-support-status-filter">${renderSupportStatusOptions(
            state.memberSupportStatusFilter,
          )}</select>
        </label>
        <label>
          Category
          <select id="member-support-category-filter">${renderSupportCategoryOptions(
            memberSupportTicketCategories,
            state.memberSupportCategoryFilter,
          )}</select>
        </label>
        <label>
          Priority
          <select id="member-support-priority-filter">${renderSupportPriorityOptions(
            state.memberSupportPriorityFilter,
          )}</select>
        </label>
        <label>
          Assignment
          <select id="member-support-assignment-filter">${renderSupportAssignmentFilterOptions(
            state.memberSupportAssignmentFilter,
          )}</select>
        </label>
      </div>
    </div>
    ${state.memberSupportError ? `<p class="error" role="alert">${escapeHtml(adminFeedback(state.memberSupportError, state.locale))}</p>` : ""}
    ${state.memberSupportMessage ? `<p class="status-inline">${escapeHtml(state.memberSupportMessage)}</p>` : ""}
    ${
      !canManageSupport
        ? `<p class="meta">Organization owner, admin, or communications admin access is required to manage member support tickets.</p>`
        : renderMemberSupportQueue(tickets, selectedTicket)
    }
  `;
}

function renderMemberSupportQueue(
  tickets: MemberSupportTicketRow[],
  selectedTicket: MemberSupportTicketRow | undefined,
): string {
  return `
    ${
      tickets.length === 0
        ? state.memberSupportError
          ? ""
          : `<p class="meta">No member support requests match the current filters.</p>`
        : `<div class="table-scroll" role="region" aria-label="Member support tickets">
          <table class="data-table support-table">
            <thead>
              <tr>
                <th scope="col">Ticket</th>
                <th scope="col">Requester</th>
                <th scope="col">Category</th>
                <th scope="col">Priority</th>
                <th scope="col">Assigned</th>
                <th scope="col">Last activity</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${tickets.map((ticket) => renderMemberSupportTicketRow(ticket)).join("")}
            </tbody>
          </table>
        </div>`
    }
    ${selectedTicket ? renderMemberSupportTicketDetail(selectedTicket) : ""}
  `;
}

function renderMemberSupportTicketRow(ticket: MemberSupportTicketRow): string {
  const selected = ticket.ticketId === state.memberSupportSelectedTicketId;
  return `<tr class="${selected ? "selected-row" : ""}">
    <td><button class="inline-action" type="button" data-member-support-select="${escapeHtml(ticket.ticketId)}">${escapeHtml(ticket.ticketNumber)}</button><br><small>${escapeHtml(
      ticket.subject,
    )}</small><br><span class="status-pill ${supportStatusClass(ticket.status)}">${escapeHtml(
      ticket.status,
    )}</span></td>
    <td>${escapeHtml(ticket.requesterDisplayName)}<br><small>${escapeHtml(
      ticket.requesterEmail || "-",
    )}</small></td>
    <td>${escapeHtml(ticket.category)}<br><small>Requester: ${escapeHtml(
      ticket.requesterUrgency,
    )}</small></td>
    <td><span class="status-pill ${supportPriorityClass(ticket.internalPriority)}">${escapeHtml(
      ticket.internalPriority,
    )}</span></td>
    <td>${escapeHtml(supportAssigneeLabel(ticket.assignedToPersonId))}</td>
    <td>${escapeHtml(ticket.lastActivityAt?.toLocaleString(state.locale) ?? "-")}</td>
    <td><button class="inline-action" data-member-support-select="${escapeHtml(
      ticket.ticketId,
    )}" type="button">Open</button></td>
  </tr>`;
}

function renderMemberSupportTicketDetail(ticket: MemberSupportTicketRow): string {
  const closed = ticket.status === "Closed";
  return `<section class="form-section support-detail">
    <div class="panel-header-actions">
      <div>
        <h3 data-support-detail-heading tabindex="-1">${escapeHtml(ticket.ticketNumber)} · ${escapeHtml(ticket.subject)}</h3>
        <p class="meta">${escapeHtml(ticket.category)} · requester urgency: ${escapeHtml(
          ticket.requesterUrgency,
        )}</p>
      </div>
      <button data-member-support-assign-self="${escapeHtml(ticket.ticketId)}" type="button" ${
        state.busy || closed ? "disabled" : ""
      }>Assign to me</button>
    </div>
    <dl class="event-action-summary people-detail-summary">
      <div><dt>Requester</dt><dd>${escapeHtml(ticket.requesterDisplayName)}</dd></div>
      <div><dt>Email</dt><dd>${escapeHtml(ticket.requesterEmail || "-")}</dd></div>
      <div><dt>Status</dt><dd>${escapeHtml(ticket.status)}</dd></div>
      <div><dt>Priority</dt><dd>${escapeHtml(ticket.internalPriority)}</dd></div>
      <div><dt>Assigned to</dt><dd>${escapeHtml(supportAssigneeLabel(ticket.assignedToPersonId))}</dd></div>
      <div><dt>Created</dt><dd>${escapeHtml(ticket.createdAt?.toLocaleString(state.locale) ?? "-")}</dd></div>
    </dl>
    <form id="member-support-update-form" class="editor-form compact-form">
      <input name="ticketId" type="hidden" value="${escapeHtml(ticket.ticketId)}" />
      <div class="editor-grid">
        <label>
          Status
          <select name="status" ${state.busy || closed ? "disabled" : ""}>${renderSupportEditableStatusOptions(
            ticket.status,
          )}</select>
        </label>
        <label>
          Internal priority
          <select name="internalPriority" ${state.busy || closed ? "disabled" : ""}>${renderSupportEditablePriorityOptions(
            ticket.internalPriority,
          )}</select>
        </label>
      </div>
      <label>
        Assigned support staff
        <select name="assignedToPersonId" ${state.busy || closed ? "disabled" : ""}>${renderSupportAssigneeOptions(
          ticket.assignedToPersonId,
        )}</select>
      </label>
      <div class="editor-actions">
        <button type="submit" ${state.busy || closed ? "disabled" : ""}>Save ticket fields</button>
      </div>
    </form>
    ${renderSupportConversation(state.memberSupportMessages)}
    <form id="member-support-reply-form" class="editor-form compact-form">
      <input name="ticketId" type="hidden" value="${escapeHtml(ticket.ticketId)}" />
      <label>
        Reply
        <textarea maxlength="2000" name="body" required ${state.busy || closed ? "disabled" : ""}></textarea>
      </label>
      <label>
        Images
        <input accept="${escapeHtml(supportAttachmentAccept)}" multiple name="images" type="file" ${
          state.busy || closed ? "disabled" : ""
        } />
      </label>
      <div class="editor-actions">
        <button type="submit" ${state.busy || closed ? "disabled" : ""}>Send reply</button>
      </div>
      ${closed ? `<p class="meta">Closed tickets are final for Release 1. Create a new request for a new issue.</p>` : ""}
    </form>
  </section>`;
}

function renderShdeedSupportPanel(): string {
  const canManageSupport = selectedContextCanManageSupport();
  const tickets = filteredShdeedSupportTickets();
  const selectedTicket = state.shdeedSupportTickets.find(
    (ticket) => ticket.ticketId === state.shdeedSupportSelectedTicketId,
  );

  if (canManageSupport && selectedTicket) {
    return `${renderSupportBackButton("shdeed")}
      ${state.shdeedSupportError ? `<p class="error" role="alert">${escapeHtml(adminFeedback(state.shdeedSupportError, state.locale))}</p>` : ""}
      ${state.shdeedSupportMessage ? `<p role="status">${escapeHtml(state.shdeedSupportMessage)}</p>` : ""}
      ${renderShdeedSupportTicketDetail(selectedTicket)}`;
  }

  return `
    <div class="list-toolbar">
      <div>
        <h2>Shdeed Support</h2>
        <p class="meta">Organization-to-Shdeed cases for ${escapeHtml(selectedOrganization().displayName)}.</p>
      </div>
      <div class="list-toolbar-actions support-filter-grid">
        <label class="list-search">
          <span>Search Shdeed tickets</span>
          <input id="shdeed-support-search" maxlength="160" placeholder="Ticket, subject, category..." value="${escapeHtml(
            state.shdeedSupportSearch,
          )}" />
        </label>
        <label>
          Status
          <select id="shdeed-support-status-filter">${renderSupportStatusOptions(
            state.shdeedSupportStatusFilter,
          )}</select>
        </label>
        <label>
          Category
          <select id="shdeed-support-category-filter">${renderSupportCategoryOptions(
            shdeedSupportTicketCategories,
            state.shdeedSupportCategoryFilter,
          )}</select>
        </label>
        <label>
          Priority
          <select id="shdeed-support-priority-filter">${renderSupportPriorityOptions(
            state.shdeedSupportPriorityFilter,
          )}</select>
        </label>
        <label>
          Assignment
          <select id="shdeed-support-assignment-filter">${renderSupportAssignmentFilterOptions(
            state.shdeedSupportAssignmentFilter,
          )}</select>
        </label>
      </div>
    </div>
    ${state.shdeedSupportError ? `<p class="error">${escapeHtml(state.shdeedSupportError)}</p>` : ""}
    ${state.shdeedSupportMessage ? `<p class="status-inline">${escapeHtml(state.shdeedSupportMessage)}</p>` : ""}
    ${
      !canManageSupport
        ? `<p class="meta">Organization owner, admin, or communications admin access is required to contact Shdeed support.</p>`
        : `${renderCreateShdeedSupportForm()}${renderShdeedSupportQueue(tickets, selectedTicket)}`
    }
  `;
}

function renderCreateShdeedSupportForm(): string {
  return `<form id="shdeed-support-create-form" class="editor-form">
    <section class="form-section">
      <h3>Create Shdeed support case</h3>
      <div class="editor-grid">
        <label>
          Category
          <select name="category" required>${renderSupportCategoryOptions(
            shdeedSupportTicketCategories,
            shdeedSupportTicketCategories[0],
            false,
          )}</select>
        </label>
        <label>
          How urgent is this?
          <select name="requesterUrgency">
            <option value="Normal">Normal</option>
            <option value="Urgent">Urgent</option>
          </select>
        </label>
      </div>
      <label>
        Subject
        <input maxlength="140" name="subject" required />
      </label>
      <label>
        Description
        <textarea maxlength="2000" name="description" required></textarea>
      </label>
      <label>
        Images
        <input accept="${escapeHtml(supportAttachmentAccept)}" multiple name="images" type="file" />
      </label>
      <div class="editor-actions">
        <button type="submit" ${state.busy ? "disabled" : ""}>Create Shdeed case</button>
      </div>
    </section>
  </form>`;
}

function renderShdeedSupportQueue(
  tickets: ShdeedSupportTicketRow[],
  selectedTicket: ShdeedSupportTicketRow | undefined,
): string {
  return `
    ${
      tickets.length === 0
        ? `<p class="meta">No Shdeed support cases match the current filters.</p>`
        : `<div class="table-scroll" role="region" aria-label="Shdeed support tickets">
          <table class="data-table support-table">
            <thead>
              <tr>
                <th scope="col">Ticket</th>
                <th scope="col">Requester</th>
                <th scope="col">Category</th>
                <th scope="col">Priority</th>
                <th scope="col">Assigned</th>
                <th scope="col">Last activity</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${tickets.map((ticket) => renderShdeedSupportTicketRow(ticket)).join("")}
            </tbody>
          </table>
        </div>`
    }
    ${selectedTicket ? renderShdeedSupportTicketDetail(selectedTicket) : ""}
  `;
}

function renderShdeedSupportTicketRow(ticket: ShdeedSupportTicketRow): string {
  const selected = ticket.ticketId === state.shdeedSupportSelectedTicketId;
  return `<tr class="${selected ? "selected-row" : ""}">
    <td><button class="inline-action" type="button" data-shdeed-support-select="${escapeHtml(ticket.ticketId)}">${escapeHtml(ticket.ticketNumber)}</button><br><small>${escapeHtml(
      ticket.subject,
    )}</small><br><span class="status-pill ${supportStatusClass(ticket.status)}">${escapeHtml(
      ticket.status,
    )}</span></td>
    <td>${escapeHtml(ticket.requesterAdminDisplayName)}<br><small>${escapeHtml(
      ticket.requesterAdminEmail || "-",
    )}</small></td>
    <td>${escapeHtml(ticket.category)}<br><small>Requester: ${escapeHtml(
      ticket.requesterUrgency,
    )}</small></td>
    <td><span class="status-pill ${supportPriorityClass(ticket.internalPriority)}">${escapeHtml(
      ticket.internalPriority,
    )}</span></td>
    <td>${escapeHtml(supportAssigneeLabel(ticket.assignedToPersonId))}</td>
    <td>${escapeHtml(ticket.lastActivityAt?.toLocaleString(state.locale) ?? "-")}</td>
    <td><button class="inline-action" data-shdeed-support-select="${escapeHtml(
      ticket.ticketId,
    )}" type="button">Open</button></td>
  </tr>`;
}

function renderShdeedSupportTicketDetail(ticket: ShdeedSupportTicketRow): string {
  const closed = ticket.status === "Closed";
  return `<section class="form-section support-detail">
    <h3 data-support-detail-heading tabindex="-1">${escapeHtml(ticket.ticketNumber)} · ${escapeHtml(ticket.subject)}</h3>
    <dl class="event-action-summary people-detail-summary">
      <div><dt>Organization</dt><dd>${escapeHtml(ticket.organizationNameSnapshot)}</dd></div>
      <div><dt>Requester</dt><dd>${escapeHtml(ticket.requesterAdminDisplayName)}</dd></div>
      <div><dt>Status</dt><dd>${escapeHtml(ticket.status)}</dd></div>
      <div><dt>Priority</dt><dd>${escapeHtml(ticket.internalPriority)}</dd></div>
      <div><dt>Assigned to Shdeed</dt><dd>${escapeHtml(supportAssigneeLabel(ticket.assignedToPersonId))}</dd></div>
      <div><dt>Created</dt><dd>${escapeHtml(ticket.createdAt?.toLocaleString(state.locale) ?? "-")}</dd></div>
    </dl>
    ${renderSupportConversation(state.shdeedSupportMessages)}
    <form id="shdeed-support-reply-form" class="editor-form compact-form">
      <input name="ticketId" type="hidden" value="${escapeHtml(ticket.ticketId)}" />
      <label>
        Reply to Shdeed
        <textarea maxlength="2000" name="body" required ${state.busy || closed ? "disabled" : ""}></textarea>
      </label>
      <label>
        Images
        <input accept="${escapeHtml(supportAttachmentAccept)}" multiple name="images" type="file" ${
          state.busy || closed ? "disabled" : ""
        } />
      </label>
      <div class="editor-actions">
        <button type="submit" ${state.busy || closed ? "disabled" : ""}>Send reply</button>
      </div>
    </form>
  </section>`;
}

function renderSupportConversation(messages: SupportTicketMessageRow[]): string {
  return `<section class="support-conversation" aria-label="Support conversation">
    <h4>Conversation</h4>
    ${
      messages.length === 0
        ? `<p class="meta">No conversation messages yet.</p>`
        : messages
            .map(
              (message) => `<article class="support-message-row">
                <div>
                  <strong>${escapeHtml(message.senderDisplayName)}</strong>
                  <small>${escapeHtml(message.senderRole || "support")} · ${escapeHtml(
                    message.createdAt?.toLocaleString(state.locale) ?? "-",
                  )}</small>
                </div>
                <p>${escapeHtml(message.body)}</p>
                ${renderSupportAttachments(message.attachments)}
              </article>`,
            )
            .join("")
    }
  </section>`;
}

function renderSupportAttachments(attachments: SupportAttachmentRow[]): string {
  if (attachments.length === 0) {
    return "";
  }

  return `<div class="support-attachments">
    ${attachments
      .map(
        (attachment) =>
          `<button class="inline-action" data-support-attachment-path="${escapeHtml(
            attachment.storagePath,
          )}" type="button">Open ${escapeHtml(attachment.fileName)} (${escapeHtml(
            formatBytes(attachment.sizeBytes),
          )})</button>`,
      )
      .join("")}
  </div>`;
}

function supportStatusClass(status: SupportTicketStatus): string {
  if (status === "Closed") {
    return "status-rejected";
  }
  if (status === "Resolved") {
    return "status-accepted";
  }
  if (status === "Waiting on Requester") {
    return "status-needs-review";
  }
  return "status-pending";
}

function supportPriorityClass(priority: SupportTicketPriority): string {
  if (priority === "Urgent" || priority === "High") {
    return "status-needs-review";
  }
  return "status-accepted";
}

async function updateMemberSupportTicketFromForm(form: HTMLFormElement): Promise<boolean> {
  const data = new FormData(form);
  state.busy = true;
  state.memberSupportError = "";
  state.memberSupportMessage = "";
  render();

  try {
    await postAuthenticatedFunction("updateMemberSupportTicket", {
      assignedToPersonId: formStringOrNull(data, "assignedToPersonId"),
      internalPriority: String(data.get("internalPriority") ?? "Normal"),
      organizationId: state.organizationId,
      status: String(data.get("status") ?? "Open"),
      ticketId: String(data.get("ticketId") ?? ""),
    });
    state.memberSupportMessage = "Member support ticket updated.";
    return true;
  } catch (error) {
    state.memberSupportError = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function assignMemberSupportTicketToSelf(ticketId: string): Promise<void> {
  state.busy = true;
  state.memberSupportError = "";
  state.memberSupportMessage = "";
  render();

  try {
    await postAuthenticatedFunction("updateMemberSupportTicket", {
      assignedToPersonId: state.userUid,
      organizationId: state.organizationId,
      ticketId,
    });
    state.memberSupportMessage = "Assigned ticket to you.";
  } catch (error) {
    state.memberSupportError = error instanceof Error ? error.message : String(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function replyMemberSupportTicketFromForm(form: HTMLFormElement): Promise<boolean> {
  const data = new FormData(form);
  const ticketId = String(data.get("ticketId") ?? "");
  const body = String(data.get("body") ?? "").trim();
  const ticket = state.memberSupportTickets.find((candidate) => candidate.ticketId === ticketId);
  state.busy = true;
  state.memberSupportError = "";
  state.memberSupportMessage = "";
  render();

  try {
    if (!ticket) {
      throw new Error("Select a member support ticket before replying.");
    }
    const attachments = await uploadSupportAttachmentsFromForm(form, {
      kind: "member",
      organizationId: state.organizationId,
      requesterUid: ticket.requesterUid,
      ticketId,
    });
    await postAuthenticatedFunction("replyMemberSupportTicket", {
      attachments,
      body,
      organizationId: state.organizationId,
      ticketId,
    });
    state.memberSupportMessage = "Reply sent.";
    return true;
  } catch (error) {
    state.memberSupportError = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function createShdeedSupportTicketFromForm(form: HTMLFormElement): Promise<boolean> {
  const data = new FormData(form);
  state.busy = true;
  state.shdeedSupportError = "";
  state.shdeedSupportMessage = "";
  render();

  try {
    const response = await postAuthenticatedFunction("createShdeedSupportTicket", {
      category: String(data.get("category") ?? ""),
      description: String(data.get("description") ?? "").trim(),
      organizationId: state.organizationId,
      requesterUrgency: String(data.get("requesterUrgency") ?? "Normal"),
      subject: String(data.get("subject") ?? "").trim(),
    });
    const ticketId = typeof response.ticketId === "string" ? response.ticketId : "";
    const ticketNumber =
      typeof response.ticketNumber === "string" ? response.ticketNumber : "new case";
    if (!ticketId) {
      throw new Error("Shdeed support did not return a ticket.");
    }
    const attachments = await uploadSupportAttachmentsFromForm(form, {
      kind: "platform",
      organizationId: state.organizationId,
      ticketId,
    });
    if (attachments.length > 0) {
      await postAuthenticatedFunction("replyShdeedSupportTicket", {
        attachments,
        body: "Image attachments",
        ticketId,
      });
    }
    state.shdeedSupportSelectedTicketId = ticketId;
    state.shdeedSupportMessage = `Created ${ticketNumber}.`;
    return true;
  } catch (error) {
    state.shdeedSupportError = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function replyShdeedSupportTicketFromForm(form: HTMLFormElement): Promise<boolean> {
  const data = new FormData(form);
  const ticketId = String(data.get("ticketId") ?? "");
  const body = String(data.get("body") ?? "").trim();
  state.busy = true;
  state.shdeedSupportError = "";
  state.shdeedSupportMessage = "";
  render();

  try {
    const attachments = await uploadSupportAttachmentsFromForm(form, {
      kind: "platform",
      organizationId: state.organizationId,
      ticketId,
    });
    await postAuthenticatedFunction("replyShdeedSupportTicket", {
      attachments,
      body,
      ticketId,
    });
    state.shdeedSupportMessage = "Reply sent to Shdeed.";
    return true;
  } catch (error) {
    state.shdeedSupportError = error instanceof Error ? error.message : String(error);
    return false;
  } finally {
    state.busy = false;
    renderRealtime();
  }
}

async function uploadSupportAttachmentsFromForm(
  form: HTMLFormElement,
  input:
    | { kind: "member"; organizationId: string; requesterUid: string; ticketId: string }
    | { kind: "platform"; organizationId: string; ticketId: string },
): Promise<SupportAttachmentRow[]> {
  const fileInput = form.querySelector<HTMLInputElement>("input[type='file'][name='images']");
  const files = Array.from(fileInput?.files ?? []);
  return uploadSupportImages(
    files,
    { kind: input.kind, organizationId: input.organizationId, ticketId: input.ticketId },
    supportImageContext(),
  );
}

function supportImageContext() {
  const uid = auth.currentUser?.uid;
  const organizationId = state.organizationId;
  return {
    locale: state.locale,
    post: postAuthenticatedFunction,
    isCurrent: () =>
      Boolean(
        uid &&
        auth.currentUser?.uid === uid &&
        state.organizationId === organizationId &&
        selectedContextCanManageSupport(),
      ),
  };
}

function formStringOrNull(form: FormData, key: string): string | null {
  const value = form.get(key);
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

async function openSupportAttachmentPath(storagePath: string): Promise<void> {
  openSupportImage(storagePath, state.organizationId, supportImageContext());
}

function buildOrgAdminWorkspacePages(input: {
  canManageCommerce: boolean;
  canManageRamadanAdminNotes: boolean;
  canManageSupport: boolean;
  canManageVolunteers: boolean;
  canReadBilling: boolean;
  locale: LocaleCode;
  openMemberSupportTickets: number;
  openShdeedSupportTickets: number;
  organizationDisplayName: string;
  pendingConnectionRequests: number;
  pendingSpaceReservationRequests: number;
}): WorkspacePageDefinition[] {
  const l = (english: string, arabic: string) => workspacePageLabel(input.locale, english, arabic);
  return [
    {
      id: "overview",
      label: l("Dashboard", "لوحة التحكم"),
      groupLabel: l("Dashboard", "لوحة التحكم"),
      description: l(
        "Live operating posture for the selected organization.",
        "ملخص تشغيلي مباشر للمؤسسة المحددة.",
      ),
      liveSummary: l(
        `${input.pendingConnectionRequests} connection request(s) need review`,
        `${input.pendingConnectionRequests} طلب اتصال يحتاج إلى مراجعة`,
      ),
      legacyIds: ["section-home", "section-intelligence", "intelligence"],
      panelIds: [
        ...(useEmulator ? ["section-home"] : []),
        "dashboard-roles",
        ...(release1OrganizationIntelligenceEnabled ? ["section-intelligence"] : []),
      ],
    },
    {
      id: "organization",
      label: l("Organization", "المؤسسة"),
      groupLabel: l("Organization", "المؤسسة"),
      description: l(
        "Profile, public discovery, branding, and organization directory affiliation controls.",
        "الملف العام، الظهور، الهوية، وروابط دليل الأعمال الخاصة بالمؤسسة.",
      ),
      liveSummary: l(
        "Profile and directory records are scoped to this organization.",
        "سجلات الملف والدليل مرتبطة بهذه المؤسسة فقط.",
      ),
      legacyIds: ["section-directory"],
      panelIds: ["section-directory"],
    },
    {
      id: "people",
      label: l("People", "الأشخاص"),
      groupLabel: l("Community", "المجتمع"),
      description: l(
        "Connection requests, connected people, and server-owned role visibility.",
        "طلبات الاتصال والأشخاص المتصلون وحالة الأدوار المملوكة للخادم.",
      ),
      liveSummary: l(
        `${input.pendingConnectionRequests} request(s) waiting for review`,
        `${input.pendingConnectionRequests} طلب ينتظر المراجعة`,
      ),
      legacyIds: ["section-welcome", "section-people"],
      panelIds: ["section-welcome", "section-people"],
    },
    {
      id: "member-support",
      label: l("Member Support", "دعم الأعضاء"),
      groupLabel: l("Support", "الدعم"),
      description: l(
        "Private member requests for this organization.",
        "طلبات الأعضاء الخاصة بهذه المؤسسة.",
      ),
      liveSummary: input.canManageSupport
        ? l(
            `${input.openMemberSupportTickets} open member request(s)`,
            `${input.openMemberSupportTickets} طلب عضو مفتوح`,
          )
        : l("Support access requires an authorized role.", "الدعم يتطلب دورا مصرحا."),
      legacyIds: ["section-member-support"],
      panelIds: ["section-member-support"],
    },
    {
      id: "shdeed-support",
      label: l("Shdeed Support", "دعم شديد"),
      groupLabel: l("Support", "الدعم"),
      description: l("Organization-to-Shdeed support cases.", "حالات دعم المؤسسة مع شديد."),
      liveSummary: input.canManageSupport
        ? l(
            `${input.openShdeedSupportTickets} open Shdeed case(s)`,
            `${input.openShdeedSupportTickets} حالة شديدة مفتوحة`,
          )
        : l("Support access requires an authorized role.", "الدعم يتطلب دورا مصرحا."),
      legacyIds: ["section-shdeed-support"],
      panelIds: ["section-shdeed-support"],
    },
    {
      id: "events",
      label: l("Events", "الفعاليات"),
      groupLabel: l("Community", "المجتمع"),
      description: l(
        "Event creation, media, registration operations, check-in, and attendee communications.",
        "إنشاء الفعاليات والوسائط والتسجيل والتحقق والتواصل مع الحضور.",
      ),
      liveSummary: l(
        "Event lifecycle, registration, and check-in are current.",
        "دورة الفعاليات والتسجيل والتحقق محدثة.",
      ),
      legacyIds: ["section-events", "section-event-editor", "section-event-operations"],
      panelIds: ["section-events", "section-event-editor", "section-event-operations"],
    },
    {
      id: "content",
      label: l("Announcements", "الإعلانات"),
      groupLabel: l("Community", "المجتمع"),
      description: l("Community announcements.", "إعلانات المجتمع."),
      liveSummary: l(
        "Announcements, priority, and scheduling are current.",
        "الإعلانات والأولوية والجدولة محدثة.",
      ),
      legacyIds: ["section-engagement"],
      panelIds: ["section-engagement"],
    },
    {
      id: "commerce",
      label: l("Commerce", "التجارة"),
      groupLabel: l("Commerce", "التجارة"),
      description: l(
        "Donations, refunds, reconciliation, Stripe Connect, plans, and entitlement visibility.",
        "التبرعات والاسترداد والمطابقة و Stripe Connect والخطط والصلاحيات.",
      ),
      liveSummary: input.canManageCommerce
        ? l("Finance controls are available for your role.", "أدوات مالية متاحة لدورك.")
        : l("Finance controls require an authorized role.", "الأدوات المالية تتطلب دورا مصرحا."),
      legacyIds: ["section-commerce", "section-billing"],
      panelIds: ["commerce-donations", "section-commerce", "section-billing"],
    },
    {
      id: "modes",
      label: l("Modes", "الأوضاع"),
      groupLabel: l("Operations", "العمليات"),
      description: l(
        "Operational mode status and editor for existing experience emphasis.",
        "حالة ومحرر الأوضاع التشغيلية للتجارب الحالية.",
      ),
      liveSummary: l(
        "Mode changes are reviewed through controlled operations.",
        "تغييرات الأوضاع تتم عبر عمليات مضبوطة.",
      ),
      legacyIds: ["section-operational-modes"],
      panelIds: ["section-operational-modes"],
    },
    {
      id: "ramadan",
      label: l("Ramadan", "رمضان"),
      groupLabel: l("Operations", "العمليات"),
      description: l(
        "Setup, iftar sponsorship days, notes, sponsors, and legacy slot tools.",
        "الإعداد وأيام رعاية الإفطار والملاحظات والرعاة وأدوات الخانات.",
      ),
      liveSummary: input.canManageRamadanAdminNotes
        ? l(
            "Ramadan operations are ready for day-by-day review.",
            "عمليات رمضان جاهزة للمراجعة اليومية.",
          )
        : l(
            "Admin notes require owner or admin access.",
            "ملاحظات الإدارة تتطلب صلاحية مالك أو مدير.",
          ),
      legacyIds: [
        "section-ramadan",
        "ramadan-setup-panel",
        "iftar-day-configuration-panel",
        "ramadan-admin-notes-panel",
        "ramadan-sponsor-administration-panel",
      ],
      panelIds: [
        "section-ramadan",
        "ramadan-setup-panel",
        "iftar-day-configuration-panel",
        "ramadan-admin-notes-panel",
        "ramadan-mode-editor",
        "iftar-slot-editor",
        "ramadan-sponsor-administration-panel",
      ],
    },
    {
      id: "spaces",
      label: l("Spaces", "المساحات"),
      groupLabel: l("Operations", "العمليات"),
      description: l(
        "Space catalog, pricing, availability, reservation review, and schedule controls.",
        "كتالوج المساحات والتسعير والتوفر ومراجعة الحجوزات والجدولة.",
      ),
      liveSummary: l(
        `${input.pendingSpaceReservationRequests} space request(s) waiting for review`,
        `${input.pendingSpaceReservationRequests} طلب مساحة ينتظر المراجعة`,
      ),
      legacyIds: ["section-spaces"],
      panelIds: [
        "section-spaces",
        "space-editor",
        "space-availability-blocks",
        "pending-space-reservation-requests",
        "approved-space-reservation-schedule",
        "space-availability-block-editor",
        "available-space-slots",
        "space-slot-editor",
        "latest-space-reservations",
      ],
    },
    {
      id: "volunteers",
      label: l("Volunteers", "المتطوعون"),
      groupLabel: l("Operations", "العمليات"),
      description: l(
        "Opportunities, shifts, roster, attendance, hours, and recognition.",
        "الفرص والمناوبات والقوائم والحضور والساعات والتقدير.",
      ),
      liveSummary: input.canManageVolunteers
        ? l("Volunteer operations are available for your role.", "عمليات المتطوعين متاحة لدورك.")
        : l(
            "Volunteer controls require owner, admin, or coordinator access.",
            "أدوات المتطوعين تتطلب صلاحية مناسبة.",
          ),
      legacyIds: ["section-volunteers"],
      panelIds: [
        "section-volunteers",
        "volunteer-opportunity-editor",
        "volunteer-shifts",
        "volunteer-shift-editor",
        "volunteer-roster",
        "volunteer-recognition",
      ],
    },
    {
      id: "settings",
      label: l("Settings", "الإعدادات"),
      groupLabel: l("Organization", "المؤسسة"),
      description: l(
        "Organization details, branding, and public profile settings.",
        "تفاصيل المؤسسة والهوية وإعدادات الملف العام.",
      ),
      liveSummary: input.canReadBilling
        ? l(
            "Profile, prayer, leadership, and mobile settings are editable.",
            "الملف والصلاة والقيادة وإعدادات التطبيق قابلة للتحرير.",
          )
        : l(
            "Profile access is limited by current role.",
            "الوصول إلى الملف محدود حسب الدور الحالي.",
          ),
      legacyIds: [
        "section-settings",
        "section-content",
        "section-prayer-settings",
        "section-islamic-components",
      ],
      panelIds: [
        "section-settings",
        "organization-profile-editor",
        "section-content",
        "section-prayer-settings",
        "section-islamic-components",
        "organization-islamic-components-editor",
        "organization-prayer-settings-editor",
      ],
    },
    {
      id: "profile-audit",
      label: l("Profile audit", "سجل الملف"),
      groupLabel: l("Organization", "المؤسسة"),
      description: l(
        "Organization profile update requests and backend review outcomes.",
        "طلبات تحديث ملف المؤسسة ونتائج مراجعة الخادم.",
      ),
      liveSummary: l(
        "Profile change history is separate from editable settings.",
        "سجل تحديثات الملف منفصل عن إعدادات التحرير.",
      ),
      legacyIds: ["section-profile-audit"],
      panelIds: ["section-profile-audit"],
    },
  ];
}

function renderOrganizationProfileSelectOptions(
  options: readonly OrganizationProfileSelectOption[],
  selectedValue: string,
): string {
  return options
    .map(
      (option) =>
        `<option value="${escapeHtml(option.value)}" ${
          option.value === selectedValue ? "selected" : ""
        }>${escapeHtml(option.label)}</option>`,
    )
    .join("");
}

function renderOrganizationPrayerMethodOptions(selectedValue: string): string {
  const options = [
    { label: "Islamic Society of North America", value: "2" },
    { label: "Muslim World League", value: "3" },
    { label: "University of Islamic Sciences, Karachi", value: "1" },
    { label: "Umm Al-Qura University, Makkah", value: "4" },
    { label: "Egyptian General Authority of Survey", value: "5" },
    { label: "Diyanet Isleri Baskanligi, Turkey", value: "13" },
    { label: "Moonsighting Committee Worldwide", value: "15" },
    { label: "Gulf Region", value: "8" },
    { label: "Kuwait", value: "9" },
    { label: "Qatar", value: "10" },
  ];

  return options
    .map(
      (option) =>
        `<option value="${escapeHtml(option.value)}" ${
          option.value === selectedValue ? "selected" : ""
        }>${escapeHtml(option.label)}</option>`,
    )
    .join("");
}

function renderOrganizationPrayerTimingList(
  copy: (typeof shellCopy)[LocaleCode],
  settings: OrganizationPrayerSettingsRow | undefined,
): string {
  if (!settings || settings.timings.length === 0) {
    return `<p class="meta">Prayer times have not been configured yet.</p>`;
  }

  const rows = organizationPrayerOrder.flatMap((prayerId) => {
    const timing = settings.timings.find((candidate) => candidate.prayerId === prayerId);
    return timing ? [timing] : [];
  });

  return `<ul class="request-list compact-list prayer-settings-list">
    ${rows
      .map(
        (timing) => `<li>
          <span>${escapeHtml(organizationPrayerLabel(copy, timing.prayerId))}<br><small>${
            copy.adhan
          }: ${escapeHtml(timing.displayTime ?? "-")} · ${copy.prayerSource}: ${escapeHtml(
            timing.source,
          )}</small></span>
          <span class="status-pill ${
            timing.enabled ? "status-accepted" : "status-pending"
          }">${copy.iqamah}: ${escapeHtml(timing.iqamaTime ?? "-")}</span>
        </li>`,
      )
      .join("")}
  </ul>`;
}

type IslamicComponentGroupDefinition = {
  componentIds: IslamicComponentId[];
  summary: string;
  title: string;
};

const orgAdminIslamicComponentGroups: IslamicComponentGroupDefinition[] = [
  {
    componentIds: ["prayer"],
    summary: "Prayer schedule, iqamah, notifications, and Qibla.",
    title: "Prayer",
  },
  {
    componentIds: ["quran", "quranAudio", "quranWordByWord", "tafsir"],
    summary: "Reader, audio, study modes, saved ayat, and offline Quran content.",
    title: "Quran",
  },
  {
    componentIds: ["dailyAyah", "dailyHadith", "dailyDua"],
    summary: "Unified Daily Home content with provider-backed Ayah, Hadith, and Dua.",
    title: "Daily Content",
  },
  {
    componentIds: ["namesOfAllah", "hijriCalendar"],
    summary: "Explore resources: 99 Names of Allah and calculated Hijri Calendar.",
    title: "Islamic Resources",
  },
];

function renderIslamicComponentStatusGroups(
  enabled: Set<IslamicComponentId>,
  homeEnabled: Set<IslamicComponentId>,
  planEntitlements: Set<string>,
): string {
  return orgAdminIslamicComponentGroups
    .map((group) => {
      const rows = group.componentIds
        .flatMap((componentId) => {
          const component = state.platformIslamicComponents.find(
            (candidate) => candidate.componentId === componentId,
          );
          return component ? [component] : [];
        })
        .map((component) =>
          renderIslamicComponentStatusRow(component, enabled, homeEnabled, planEntitlements),
        )
        .join("");
      if (!rows) {
        return "";
      }
      return `<li class="islamic-component-group-heading">
        <span><strong>${escapeHtml(group.title)}</strong><br><small>${escapeHtml(
          group.summary,
        )}</small></span>
        <span class="status-pill status-pending">Group</span>
      </li>${rows}`;
    })
    .join("");
}

function renderIslamicComponentStatusRow(
  component: PlatformIslamicComponentRow,
  enabled: Set<IslamicComponentId>,
  homeEnabled: Set<IslamicComponentId>,
  planEntitlements: Set<string>,
): string {
  const dependencyMissing = component.dependencyComponentIds.some(
    (dependencyId) => !enabled.has(dependencyId),
  );
  const entitlementMissing = component.requiredEntitlementIds.some(
    (entitlementId) => !planEntitlements.has(entitlementId),
  );
  const statusClass =
    enabled.has(component.componentId) && !dependencyMissing && !entitlementMissing
      ? "status-accepted"
      : "status-pending";
  const dependencyLabel =
    component.dependencyComponentIds.length === 0
      ? "No dependency"
      : `Requires ${component.dependencyComponentIds
          .map((dependencyId) =>
            islamicComponentLabel(dependencyId, state.platformIslamicComponents),
          )
          .join(", ")}`;
  const planLabel =
    component.requiredEntitlementIds.length === 0
      ? "Included"
      : entitlementMissing
        ? `Plan missing ${component.requiredEntitlementIds.join(", ")}`
        : "Plan included";

  return `<li class="islamic-component-row">
    <span>${escapeHtml(component.displayName)}<br><small>${escapeHtml(
      component.description,
    )}</small><br><small>${escapeHtml(dependencyLabel)} · ${escapeHtml(planLabel)} · ${
      component.homeEligible ? "Home eligible" : "Explore only"
    }</small></span>
    <span class="status-pill ${statusClass}">${
      enabled.has(component.componentId) ? "Enabled" : "Disabled"
    }${homeEnabled.has(component.componentId) ? "<br><small>Home</small>" : ""}</span>
  </li>`;
}

function renderIslamicComponentEditorGroups(
  enabled: Set<IslamicComponentId>,
  homeEnabled: Set<IslamicComponentId>,
): string {
  return `<div class="islamic-component-groups">
    ${orgAdminIslamicComponentGroups
      .map((group) => {
        const controls = group.componentIds
          .flatMap((componentId) => {
            const component = state.platformIslamicComponents.find(
              (candidate) => candidate.componentId === componentId,
            );
            return component ? [component] : [];
          })
          .map((component) => renderIslamicComponentEditorRow(component, enabled, homeEnabled))
          .join("");
        if (!controls) {
          return "";
        }
        return `<section class="islamic-component-group">
          <div>
            <h4>${escapeHtml(group.title)}</h4>
            <p class="meta">${escapeHtml(group.summary)}</p>
          </div>
          ${controls}
        </section>`;
      })
      .join("")}
  </div>`;
}

function renderIslamicComponentEditorRow(
  component: PlatformIslamicComponentRow,
  enabled: Set<IslamicComponentId>,
  homeEnabled: Set<IslamicComponentId>,
): string {
  const dependencyMissing = component.dependencyComponentIds.some(
    (dependencyId) => !enabled.has(dependencyId),
  );
  const disabled = component.platformEnabled === false || component.status !== "active";
  return `<div class="toggle-row islamic-component-toggle-row">
    <label class="checkbox-row">
      <input
        data-islamic-component-enabled="${escapeHtml(component.componentId)}"
        type="checkbox"
        ${enabled.has(component.componentId) ? "checked" : ""}
        ${disabled ? "disabled" : ""}
      />
      <span>${escapeHtml(component.displayName)}<br><small>${escapeHtml(
        component.description,
      )}</small></span>
    </label>
    ${
      component.homeEligible
        ? `<label class="checkbox-row compact-checkbox">
            <input
              data-islamic-component-home-enabled="${escapeHtml(component.componentId)}"
              type="checkbox"
              ${homeEnabled.has(component.componentId) ? "checked" : ""}
              ${disabled || !enabled.has(component.componentId) ? "disabled" : ""}
            />
            Home
          </label>`
        : `<span class="meta">Explore only</span>`
    }
    <small class="meta">${escapeHtml(
      component.dependencyComponentIds.length === 0
        ? "No dependency"
        : `Requires ${component.dependencyComponentIds
            .map((dependencyId) =>
              islamicComponentLabel(dependencyId, state.platformIslamicComponents),
            )
            .join(", ")}${dependencyMissing ? " (currently off)" : ""}`,
    )}</small>
  </div>`;
}

function renderDailyIslamicCurationPreview(
  editor: OrganizationIslamicComponentsEditorInput,
): string {
  const ayah = approvedDailyAyahOptions.find(
    (option) => option.reference === editor.dailyAyahReference,
  );
  const hadith = approvedDailyHadithOptions.find(
    (option) => option.hadithId === editor.dailyHadithReference,
  );
  const dua = approvedDailyDuaOptions.find((option) => option.duaId === editor.dailyDuaReference);
  const modeLabel =
    editor.dailyContentMode === "automatic"
      ? "Automatic"
      : editor.dailyContentMode === "curated"
        ? "Curated"
        : "Off";
  return `<div class="islamic-daily-preview">
    <p class="meta"><strong>Preview</strong> · ${escapeHtml(modeLabel)} · ${escapeHtml(
      dailyContentDateLabel(editor.dailyContentDate),
    )}</p>
    ${renderDailyPreviewRow(
      "Ayah",
      dailyPreviewTitle(
        editor.dailyContentMode,
        ayah?.verseKey,
        "Automatic Ayah",
        "No featured Ayah",
      ),
      ayah?.translation,
    )}
    ${renderDailyPreviewRow(
      "Hadith",
      dailyPreviewTitle(
        editor.dailyContentMode,
        hadith?.reference,
        "Automatic Hadith",
        "No featured Hadith",
      ),
      hadith?.translation,
    )}
    ${renderDailyPreviewRow(
      "Dua",
      dailyPreviewTitle(editor.dailyContentMode, dua?.title, "Automatic Dua", "No featured Dua"),
      dua?.translation,
    )}
  </div>`;
}

function renderDailyPreviewRow(label: string, title: string, detail: string | undefined): string {
  return `<div class="islamic-daily-preview-row">
    <span>${escapeHtml(label)}</span>
    <strong>${escapeHtml(title)}</strong>
    ${detail ? `<small>${escapeHtml(detail)}</small>` : ""}
  </div>`;
}

function dailyPreviewTitle(
  mode: OrganizationIslamicComponentsEditorInput["dailyContentMode"],
  selectedTitle: string | undefined,
  automaticTitle: string,
  curatedEmptyTitle: string,
): string {
  if (selectedTitle) {
    return selectedTitle;
  }
  if (mode === "curated") {
    return curatedEmptyTitle;
  }
  return mode === "off" ? "Off" : automaticTitle;
}

function renderHijriDateDisplayPreview(preference: HijriDateDisplayPreference): string {
  const today = new Date();
  const gregorian = today.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    timeZone: "America/New_York",
    year: "numeric",
  });
  const hijri = calculatedHijriDateLabel(today);
  switch (preference) {
    case "gregorianOnly":
      return gregorian;
    case "hijriPrimary":
      return `${hijri} · ${gregorian}`;
    case "hijriSecondary":
    default:
      return `${gregorian} · ${hijri}`;
  }
}

function dailyContentDateLabel(date: string | null | undefined): string {
  return date ? `Feature date: ${date}` : "Ongoing daily setting";
}

function calculatedHijriDateLabel(date: Date): string {
  const hijri = hijriFromJdn(gregorianToJdn(date));
  return `${hijri.day} ${hijri.monthName} ${hijri.year}`;
}

function gregorianToJdn(date: Date): number {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

function hijriFromJdn(jdn: number): { day: number; monthName: string; year: number } {
  let l = jdn - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  l = l - 10631 * n + 354;
  const j =
    Math.floor((10985 - l) / 5316) * Math.floor((50 * l) / 17719) +
    Math.floor(l / 5670) * Math.floor((43 * l) / 15238);
  l =
    l -
    Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
    Math.floor(j / 16) * Math.floor((15238 * j) / 43) +
    29;
  const month = Math.floor((24 * l) / 709);
  const day = l - Math.floor((709 * month) / 24);
  const year = 30 * n + j - 30;
  return { day, monthName: hijriMonthNames[month - 1] ?? "Hijri", year };
}

const hijriMonthNames = [
  "Muharram",
  "Safar",
  "Rabi' al-Awwal",
  "Rabi' al-Thani",
  "Jumada al-Awwal",
  "Jumada al-Thani",
  "Rajab",
  "Sha'ban",
  "Ramadan",
  "Shawwal",
  "Dhu al-Qi'dah",
  "Dhu al-Hijjah",
];

function renderOrganizationIslamicComponentsPanel(): string {
  const enabled = new Set(
    state.organizationIslamicComponents
      ? islamicComponentIds.filter(
          (componentId) => state.organizationIslamicComponents?.components[componentId].enabled,
        )
      : state.organizationIslamicComponentsEditor.enabledComponentIds,
  );
  const homeEnabled = new Set(
    state.organizationIslamicComponents
      ? islamicComponentIds.filter(
          (componentId) => state.organizationIslamicComponents?.components[componentId].homeEnabled,
        )
      : state.organizationIslamicComponentsEditor.homeEnabledComponentIds,
  );
  const currentPlan = state.organizationPlanCatalog.find(
    (plan) => plan.planId === state.organizationSubscription?.planId,
  );
  const planEntitlements = new Set(currentPlan?.enabledEntitlementIds ?? []);
  const updatedAt = state.organizationIslamicComponents?.updatedAt
    ? state.organizationIslamicComponents.updatedAt.toLocaleString(state.locale)
    : "-";

  return `<article id="section-islamic-components" class="panel">
    <div class="panel-header-actions">
      <div>
        <h2>Mobile app - Islamic Components</h2>
        <p class="meta">Enable platform-supported Islamic foundations for this organization. Mobile reads the effective projection from the public organization profile.</p>
      </div>
      <button
        class="secondary-action"
        data-task-window-open="organization-islamic-components-form"
        type="button"
      >Configure Islamic Components</button>
    </div>
    ${
      state.organizationIslamicComponentsMessage
        ? `<p class="status status-inline">${escapeHtml(
            state.organizationIslamicComponentsMessage,
          )}</p>`
        : ""
    }
    <ul class="request-list compact-list">
      <li>
        <span>Display settings<br><small>Hijri date: ${escapeHtml(
          state.organizationIslamicComponents?.hijriDateDisplay ??
            state.organizationIslamicComponentsEditor.hijriDateDisplay,
        )} · Daily content: ${escapeHtml(
          state.organizationIslamicComponents?.dailyContentMode ??
            state.organizationIslamicComponentsEditor.dailyContentMode,
        )} · Schedule: ${escapeHtml(
          dailyContentDateLabel(
            state.organizationIslamicComponents?.dailyContentDate ??
              state.organizationIslamicComponentsEditor.dailyContentDate,
          ),
        )}</small><br><small>${escapeHtml(
          dailyIslamicContentSummary(
            state.organizationIslamicComponents?.dailyContentMode ??
              state.organizationIslamicComponentsEditor.dailyContentMode,
            state.organizationIslamicComponents?.dailyAyahReference ??
              state.organizationIslamicComponentsEditor.dailyAyahReference,
            state.organizationIslamicComponents?.dailyHadithReference ??
              state.organizationIslamicComponentsEditor.dailyHadithReference,
            state.organizationIslamicComponents?.dailyDuaReference ??
              state.organizationIslamicComponentsEditor.dailyDuaReference,
          ),
        )}</small></span>
        <span class="status-pill status-pending">Updated ${escapeHtml(updatedAt)}</span>
      </li>
      <li>
        <span>Hijri preview<br><small>${renderHijriDateDisplayPreview(
          state.organizationIslamicComponents?.hijriDateDisplay ??
            state.organizationIslamicComponentsEditor.hijriDateDisplay,
        )}</small></span>
        <span class="status-pill status-accepted">Calculated</span>
      </li>
      ${renderIslamicComponentStatusGroups(enabled, homeEnabled, planEntitlements)}
    </ul>
  </article>`;
}

function renderOrganizationIslamicComponentsEditor(): string {
  const editor = state.organizationIslamicComponentsEditor;
  const enabled = new Set(editor.enabledComponentIds);
  const homeEnabled = new Set(editor.homeEnabledComponentIds);

  return `<article class="panel editor-panel task-only-panel" data-workspace-panel-id="organization-islamic-components-editor">
    <h2>Mobile app - Islamic Components</h2>
    <form
      id="organization-islamic-components-form"
      class="editor-form"
      data-task-window-launch="manual"
    >
      <section class="form-section">
        <h3>Organization availability</h3>
        <p class="meta">Qibla is included with Prayer Times. Hadith and Dua libraries are not V1 destinations; only Daily Hadith and Daily Dua are member-facing. 99 Names and Hijri Calendar are Explore resources.</p>
        ${renderIslamicComponentEditorGroups(enabled, homeEnabled)}
      </section>
      <section class="form-section">
        <h3>Default display</h3>
        <div class="editor-grid">
          <label>
            Hijri date display
            <select data-islamic-components-field="hijriDateDisplay">
              <option value="gregorianOnly" ${
                editor.hijriDateDisplay === "gregorianOnly" ? "selected" : ""
              }>Gregorian only</option>
              <option value="hijriSecondary" ${
                editor.hijriDateDisplay === "hijriSecondary" ? "selected" : ""
              }>Hijri secondary</option>
              <option value="hijriPrimary" ${
                editor.hijriDateDisplay === "hijriPrimary" ? "selected" : ""
              }>Hijri primary</option>
            </select>
          </label>
          <label>
            Daily Islamic content
            <select data-islamic-components-field="dailyContentMode">
              <option value="automatic" ${
                editor.dailyContentMode === "automatic" ? "selected" : ""
              }>Automatic daily rotation</option>
              <option value="curated" ${
                editor.dailyContentMode === "curated" ? "selected" : ""
              }>Organization featured picks</option>
              <option value="off" ${editor.dailyContentMode === "off" ? "selected" : ""}>Off</option>
            </select>
          </label>
          <label>
            Feature date (optional)
            <input
              data-islamic-components-field="dailyContentDate"
              type="date"
              value="${escapeHtml(editor.dailyContentDate)}"
            />
            <small>Leave blank to apply selected featured picks every day.</small>
          </label>
          <label>
            Daily Ayah
            <select data-islamic-components-field="dailyAyahReference">
              <option value="">Automatic / clear curated Ayah</option>
              ${approvedDailyAyahOptions
                .map(
                  (ayah) =>
                    `<option value="${escapeHtml(ayah.reference)}" ${
                      editor.dailyAyahReference === ayah.reference ? "selected" : ""
                    }>${escapeHtml(`${ayah.verseKey} - ${ayah.surahName}`)}</option>`,
                )
                .join("")}
            </select>
          </label>
          <label>
            Daily Hadith
            <select data-islamic-components-field="dailyHadithReference">
              <option value="">Automatic / clear curated Hadith</option>
              ${approvedDailyHadithOptions
                .map(
                  (hadith) =>
                    `<option value="${escapeHtml(hadith.hadithId)}" ${
                      editor.dailyHadithReference === hadith.hadithId ? "selected" : ""
                    }>${escapeHtml(hadith.reference)}</option>`,
                )
                .join("")}
            </select>
          </label>
          <label>
            Daily Dua
            <select data-islamic-components-field="dailyDuaReference">
              <option value="">Automatic / clear curated Dua</option>
              ${approvedDailyDuaOptions
                .map(
                  (dua) =>
                    `<option value="${escapeHtml(dua.duaId)}" ${
                      editor.dailyDuaReference === dua.duaId ? "selected" : ""
                    }>${escapeHtml(dua.title)}</option>`,
                )
                .join("")}
            </select>
          </label>
        </div>
        ${renderDailyIslamicCurationPreview(editor)}
        <div class="islamic-daily-preview">
          <p class="meta"><strong>Hijri preview</strong> · calculated civil calendar</p>
          ${renderDailyPreviewRow("Display", editor.hijriDateDisplay, renderHijriDateDisplayPreview(editor.hijriDateDisplay))}
        </div>
      </section>
      <div class="editor-actions">
        <button type="submit" ${state.busy ? "disabled" : ""}>Save Islamic Components</button>
        <button id="reset-organization-islamic-components-form" type="button">Reset</button>
      </div>
    </form>
  </article>`;
}

function dailyIslamicContentSummary(
  mode: OrganizationIslamicComponentsEditorInput["dailyContentMode"],
  ayahReference: string | null,
  hadithReference: string | null,
  duaReference: string | null,
): string {
  const ayah =
    approvedDailyAyahOptions.find((option) => option.reference === ayahReference)?.verseKey ??
    dailyPreviewTitle(mode, undefined, "Automatic Ayah", "No featured Ayah");
  const hadith =
    approvedDailyHadithOptions.find((option) => option.hadithId === hadithReference)?.reference ??
    dailyPreviewTitle(mode, undefined, "Automatic Hadith", "No featured Hadith");
  const dua =
    approvedDailyDuaOptions.find((option) => option.duaId === duaReference)?.title ??
    dailyPreviewTitle(mode, undefined, "Automatic Dua", "No featured Dua");
  return `Ayah: ${ayah} · Hadith: ${hadith} · Dua: ${dua}`;
}

function organizationPrayerLabel(
  copy: (typeof shellCopy)[LocaleCode],
  prayerId: OrganizationPrayerId,
): string {
  switch (prayerId) {
    case "asr":
      return copy.prayerAsr;
    case "dhuhr":
      return copy.prayerDhuhr;
    case "fajr":
      return copy.prayerFajr;
    case "isha":
      return copy.prayerIsha;
    case "jumuah":
      return copy.prayerJumuah;
    case "maghrib":
      return copy.prayerMaghrib;
    case "sunrise":
      return copy.prayerSunrise;
  }
}

const preferredNavigationGroupLabels = [
  "Dashboard",
  "لوحة التحكم",
  "Overview",
  "نظرة عامة",
  "Tenants",
  "المؤسسات",
  "Community",
  "المجتمع",
  "Commerce",
  "التجارة",
  "Revenue",
  "الإيرادات",
  "Operations",
  "العمليات",
  "Product",
  "المنتج",
  "Insights",
  "الرؤى",
  "Organization",
  "المؤسسة",
];

function navigationGroupRank(groupLabel: string): number {
  const index = preferredNavigationGroupLabels.indexOf(groupLabel);
  return index === -1 ? preferredNavigationGroupLabels.length : Math.floor(index / 2);
}

function renderWorkspaceNavigation(
  pages: readonly WorkspacePageDefinition[],
  activePageId: string,
): string {
  const groups = new Map<string, WorkspacePageDefinition[]>();
  for (const page of pages) {
    groups.set(page.groupLabel, [...(groups.get(page.groupLabel) ?? []), page]);
  }

  return Array.from(groups.entries())
    .sort(([left], [right]) => navigationGroupRank(left) - navigationGroupRank(right))
    .map(
      ([groupLabel, groupPages]) =>
        `<p class="nav-group-label">${escapeHtml(groupLabel)}</p>${groupPages
          .map(
            (page) =>
              `<a href="#${page.id}" class="${page.id === activePageId ? "is-active" : ""}" ${
                page.id === activePageId ? `aria-current="page"` : ""
              }><span>${escapeHtml(page.label)}</span></a>`,
          )
          .join("")}`,
    )
    .join("");
}

function organizeWorkspacePages(
  appRoot: HTMLElement,
  pages: readonly WorkspacePageDefinition[],
  activePageId: string,
): void {
  const grid = appRoot.querySelector<HTMLElement>(".grid");
  if (!grid) {
    return;
  }

  const panels = Array.from(grid.querySelectorAll<HTMLElement>(":scope > article.panel"));
  const panelsById = new Map<string, HTMLElement>();
  for (const panel of panels) {
    const panelId = panel.dataset.workspacePanelId || panel.id;
    if (panelId) {
      panelsById.set(panelId, panel);
    }
  }
  const fragment = document.createDocumentFragment();

  for (const page of pages) {
    const section = document.createElement("section");
    section.className = `workspace-page workspace-page-${page.id}${
      page.id === activePageId ? " is-active" : ""
    }`;
    section.id = `page-${page.id}`;
    section.setAttribute("aria-labelledby", `page-title-${page.id}`);

    const pageGrid = document.createElement("div");
    pageGrid.className = "page-grid";
    for (const panelId of page.panelIds) {
      const panel = panelsById.get(panelId);
      if (panel) {
        pageGrid.append(panel);
      }
    }

    const header = document.createElement("header");
    header.className = "page-header";
    header.innerHTML = `
      <div>
        <p class="page-kicker">${escapeHtml(page.groupLabel)}</p>
        <h2 id="page-title-${page.id}">${escapeHtml(page.label)}</h2>
        <p class="muted">${escapeHtml(page.description)}</p>
      </div>
      <div class="page-status-pills" aria-label="Page status">
        <span class="status-pill status-accepted">Realtime</span>
        <span class="page-context-note">${escapeHtml(page.liveSummary)}</span>
      </div>
    `;

    section.append(header, pageGrid);
    fragment.append(section);
  }

  grid.className = "workspace-pages";
  grid.replaceChildren(fragment);
}

function shouldDeferOperationalModeEditorRender(): boolean {
  if (
    operationalModeRenderBypass ||
    authenticationState.status !== "authenticated" ||
    operationalModeEditorScope !== currentModeScope() ||
    activeTaskWindowId !== "operational-mode-editor-form" ||
    !selectedContextCanManageOrganizationSettings()
  ) {
    return false;
  }

  return document.querySelector("#task-window-body #operational-mode-editor-form") !== null;
}

function renderOperationalModeEditorUpdate(afterScrollRestore?: () => void): void {
  operationalModeRenderBypass = true;
  try {
    render({ afterScrollRestore });
  } finally {
    operationalModeRenderBypass = false;
  }
}

function render(options: { afterScrollRestore?: () => void } = {}): void {
  if (retiringTenant) return;
  if (formSaves.holdsRender()) return;
  realtimeRenderer.cancel();
  renderedFormScope = formInteractionScope();
  if (shouldDeferOperationalModeEditorRender()) {
    return;
  }
  const eventScope = `${currentModeScope()}/${state.eventEditor.eventId}/${state.locale}`;
  const focused = document.activeElement;
  // Snapshot traffic must not replace the input and caret while composing.
  // Explicit wizard actions, auth/tenant changes and role revocation still render.
  if (
    !eventEditorRenderBypass &&
    !state.busy &&
    authenticationState.status === "authenticated" &&
    renderedEventEditorScope === eventScope &&
    selectedContextCanManageEvents() &&
    focused instanceof HTMLElement &&
    focused.closest("#task-window-body #event-editor-form") &&
    focused.matches("[data-event-editor-field], [data-event-email-template-field]")
  )
    return;
  renderedEventEditorScope = eventScope;

  const copy = shellCopy[state.locale];
  const direction = directionForLocale(state.locale);
  document.documentElement.lang = state.locale;
  document.documentElement.dir = direction;

  const appRoot = document.querySelector<HTMLElement>("#app");
  if (!appRoot) {
    throw new Error("Missing app root.");
  }
  const renderScrollSnapshot = captureRenderScrollSnapshot();

  if (authenticationState.status !== "authenticated") {
    renderAuthenticationGate(appRoot, direction);
    return;
  }

  if (shouldRenderAuthenticationOnlyShell()) {
    renderAuthenticatedPortalShell(appRoot, direction);
    return;
  }

  if (
    !state.organizationId ||
    !organizationAccess.organizations.some(
      (organization) => organization.id === state.organizationId,
    )
  ) {
    renderOrganizationAccessGate(appRoot, direction);
    return;
  }

  const organization = selectedOrganization();
  const organizationCampuses = seedOrganizationCampuses.filter(
    (campus) => campus.organizationId === state.organizationId,
  );
  const organizationProfile = state.organizationProfile;
  const organizationDisplayName = organizationProfile?.displayName ?? organization.displayName;
  const organizationCity = organizationProfile?.city ?? organization.city;
  const organizationLocationSummary = organizationProfile
    ? [
        organizationProfile.addressLine1,
        organizationProfile.city,
        organizationProfile.region,
        organizationProfile.postalCode,
        organizationProfile.country,
      ]
        .filter((part) => typeof part === "string" && part.length > 0)
        .join(", ")
    : organizationCity;
  const organizationBrandColor = organizationProfile?.brandColor ?? organization.brandColor;
  const organizationHeadline = organizationProfile
    ? state.locale === "ar"
      ? organizationProfile.welcomeHeadlineAr || organizationProfile.welcomeHeadline
      : organizationProfile.welcomeHeadline
    : "-";
  const organizationSummary = organizationProfile
    ? state.locale === "ar"
      ? organizationProfile.welcomeSummaryAr || organizationProfile.welcomeSummary
      : organizationProfile.welcomeSummary
    : "-";
  const selectedLogoFileLabel = state.locale === "ar" ? "الملف المحدد" : "Selected file";
  const organizationLogoStoragePath =
    organizationProfile?.logoStoragePath ??
    (state.organizationProfileEditor.logoStoragePath || null);
  ensureOrganizationProfileLogoDownloadUrl(organizationLogoStoragePath);
  const organizationSettingsLogoUrl =
    state.organizationProfileLogoPreviewUrl ?? state.organizationProfileLogoDownloadUrl;
  const requestSummary = summarizeConnectionRequests(state.connectionRequests);
  const requestSummaryLabels = summaryLabels[state.locale];
  const selectedConnectionRequestFilter = state.connectionRequestFilter;
  const filteredConnectionRequests = state.connectionRequests.filter((request) =>
    connectionRequestMatchesFilter(request, selectedConnectionRequestFilter),
  );
  const connectionRequestEmptyMessage =
    state.connectionRequests.length === 0
      ? noConnectionRequestsMessage[state.locale]
      : noConnectionRequestsForFilterMessage[state.locale][selectedConnectionRequestFilter];
  const renderConnectionRequestFilterButton = (
    filter: ConnectionRequestFilter,
    count: number,
  ): string => {
    const selected = filter === selectedConnectionRequestFilter;
    return `<button
      aria-pressed="${selected}"
      class="request-summary-filter${selected ? " is-active" : ""}"
      data-connection-request-filter="${filter}"
      type="button"
    ><strong>${count}</strong>${requestSummaryLabels[filter]}</button>`;
  };
  const succeededDonations = state.donations.filter((donation) => donation.status === "confirmed");
  const donationTotal = succeededDonations.reduce(
    (total, donation) => total + donation.amountMinor,
    0,
  );
  const donationRefundedTotal = succeededDonations.reduce(
    (total, donation) => total + donation.refundedAmountMinor,
    0,
  );
  const donationNetTotal = donationTotal - donationRefundedTotal;
  const canManageCommerce = selectedContextCanReadFinance();
  const canManageEvents = selectedContextCanManageEvents();
  const canCheckInEvents = selectedContextCanCheckInEvents();
  const eventActionCopy = eventLifecycleActionCopy(state.locale);
  const canManageBusinessDirectory = selectedContextCanManageBusinessDirectory();
  const canRequestRefunds = selectedContextCanRequestRefund();
  const canManageVolunteers = selectedContextCanManageVolunteers();
  const canManageRamadanAdminNotes = selectedContextCanManageRamadanAdminNotes();
  const canManageSupport = selectedContextCanManageSupport();
  const canReadBilling = selectedContextCanReadBilling();
  const commerceStatus = state.commerceStatus;
  const commerceActionLabel =
    commerceStatus?.readiness === "notConnected"
      ? copy.startStripeConnectOnboarding
      : copy.resumeStripeConnectOnboarding;
  const commerceLastSync = commerceStatus?.lastProviderSyncAt
    ? commerceStatus.lastProviderSyncAt.toLocaleString(state.locale)
    : "-";
  const organizationSubscription = state.organizationSubscription;
  const currentPlan = state.organizationPlanCatalog.find(
    (plan) => plan.planId === organizationSubscription?.planId,
  );
  const foundingSlot = state.foundingSlot;
  const usage = state.organizationUsage;
  const entitlementOverrideById = new Map(
    state.organizationEntitlementOverrides.map((override) => [override.entitlementId, override]),
  );
  const entitlementRows = organizationEntitlementIds.map((entitlementId) => {
    const override = entitlementOverrideById.get(entitlementId);
    const planEnabled = currentPlan?.enabledEntitlementIds.includes(entitlementId) ?? false;
    return {
      entitlementId,
      mode: override?.mode ?? "inherit",
      status:
        override?.mode === "forceEnabled"
          ? "enabled"
          : override?.mode === "forceDisabled"
            ? "disabled"
            : planEnabled
              ? "enabled"
              : "disabled",
      source: override ? "override" : "plan",
    };
  });
  const selectedEditorEvent = state.events.find(
    (event) => event.eventId === state.eventEditor.eventId,
  );
  const eventEmailTemplate = currentEventEmailTemplate();
  const sortedEvents = sortedCommunityEventsForDisplay();
  const paginatedEvents = paginateItems(sortedEvents, eventListPage, eventListPageSize);
  eventListPage = paginatedEvents.page;
  const attendanceByRegistrationId = new Map(
    state.eventAttendance.map((attendance) => [attendance.registrationId, attendance]),
  );
  const registrationSearch = state.eventManualRegistrationSearch.trim().toLowerCase();
  const filteredRegistrations = state.eventRegistrations.filter((registration) => {
    if (registration.status !== "confirmed") {
      return false;
    }
    if (!registrationSearch) {
      return true;
    }
    return (
      registration.personId.toLowerCase().includes(registrationSearch) ||
      registration.registrationId.toLowerCase().includes(registrationSearch)
    );
  });
  const filteredWaitlistedRegistrations = state.eventRegistrations.filter((registration) => {
    if (registration.status !== "waitlisted") {
      return false;
    }
    if (!registrationSearch) {
      return true;
    }
    return (
      registration.personId.toLowerCase().includes(registrationSearch) ||
      registration.registrationId.toLowerCase().includes(registrationSearch)
    );
  });
  const paginatedRegistrations = paginateItems(
    filteredRegistrations,
    eventRegistrationPage,
    eventOperationsPageSize,
  );
  eventRegistrationPage = paginatedRegistrations.page;
  const paginatedWaitlistedRegistrations = paginateItems(
    filteredWaitlistedRegistrations,
    eventWaitlistPage,
    eventOperationsPageSize,
  );
  eventWaitlistPage = paginatedWaitlistedRegistrations.page;
  const paginatedAttendance = paginateItems(
    state.eventAttendance,
    eventAttendancePage,
    eventOperationsPageSize,
  );
  eventAttendancePage = paginatedAttendance.page;
  const activeRamadanMode = state.ramadanModes.find((mode) => mode.status === "active");
  const currentRamadanSetup = selectedRamadanSetup();
  const ramadanSetupEditorHijriYear = Number.parseInt(state.ramadanSetupEditor.hijriYear, 10);
  const ramadanSetupEditorRamadanId = Number.isSafeInteger(ramadanSetupEditorHijriYear)
    ? `ramadan-${ramadanSetupEditorHijriYear}`
    : state.iftarSponsorshipDefaultsEditor.ramadanId.trim();
  const ramadanSetupEditorHasSavedSetup = state.ramadanSetups.some(
    (setup) => setup.ramadanId === ramadanSetupEditorRamadanId,
  );
  const canBuildSponsorshipCalendarFromSetup =
    ramadanSetupEditorHasSavedSetup &&
    currentRamadanIdForAdmin() === ramadanSetupEditorRamadanId &&
    state.iftarSponsorshipDefaults !== undefined &&
    !state.busy;
  const iftarSponsorshipDefaults = state.iftarSponsorshipDefaults;
  const openIftarSponsorshipDays = state.iftarSponsorshipDays.filter(
    (day) => day.status === "open" && day.sponsorshipEnabled,
  );
  const totalIftarSponsorshipCapacity = state.iftarSponsorshipDays.reduce(
    (total, day) => total + day.capacitySpots,
    0,
  );
  const totalIftarSponsorshipReserved = state.iftarSponsorshipDays.reduce(
    (total, day) => total + day.reservedSpots,
    0,
  );
  const totalIftarSponsorshipConfirmed = state.iftarSponsorshipDays.reduce(
    (total, day) => total + day.confirmedSpots,
    0,
  );
  const totalIftarSponsorshipAvailable = state.iftarSponsorshipDays.reduce(
    (total, day) => total + Math.max(day.capacitySpots - day.reservedSpots - day.confirmedSpots, 0),
    0,
  );
  const isReservedIftarSponsorship = (sponsorship: IftarSponsorshipAdminRow): boolean =>
    sponsorship.status === "paymentPending" || sponsorship.status === "processing";
  const isReservedOrConfirmedIftarSponsorship = (sponsorship: IftarSponsorshipAdminRow): boolean =>
    isReservedIftarSponsorship(sponsorship) || sponsorship.status === "confirmed";
  const activeIftarSponsorships = state.sponsorships.filter(isReservedOrConfirmedIftarSponsorship);
  const sponsorsByIftarDay = new Map<string, IftarSponsorshipAdminRow[]>();
  for (const sponsorship of activeIftarSponsorships) {
    sponsorsByIftarDay.set(sponsorship.iftarDayId, [
      ...(sponsorsByIftarDay.get(sponsorship.iftarDayId) ?? []),
      sponsorship,
    ]);
  }
  const selectedIftarSponsorshipDayId = state.iftarSponsorshipDayEditor.iftarDayId.trim();
  const selectedIftarSponsorshipDay = state.iftarSponsorshipDays.find(
    (day) => day.id === selectedIftarSponsorshipDayId,
  );
  const selectedIftarSponsorshipDaySponsors =
    selectedIftarSponsorshipDayId.length > 0
      ? (sponsorsByIftarDay.get(selectedIftarSponsorshipDayId) ?? [])
      : [];
  const iftarSponsorshipDayEditorDisabled =
    state.iftarSponsorshipDayEditorMode === "view" ? "disabled" : "";
  const iftarSponsorshipFinanceById = new Map(
    state.iftarSponsorshipFinance.map((row) => [row.sponsorshipId, row]),
  );
  const sponsorshipSearch = state.iftarSponsorshipSearch.trim().toLowerCase();
  const filteredIftarSponsorships = state.sponsorships.filter((sponsorship) => {
    if (
      state.iftarSponsorshipDayFilter &&
      sponsorship.iftarDayId !== state.iftarSponsorshipDayFilter
    ) {
      return false;
    }
    if (
      state.iftarSponsorshipStatusFilter !== "all" &&
      sponsorship.status !== state.iftarSponsorshipStatusFilter
    ) {
      return false;
    }
    if (!sponsorshipSearch) {
      return true;
    }
    return (
      sponsorship.safeSponsorLabel.toLowerCase().includes(sponsorshipSearch) ||
      sponsorship.sponsorshipId.toLowerCase().includes(sponsorshipSearch) ||
      sponsorship.iftarDayId.toLowerCase().includes(sponsorshipSearch)
    );
  });
  const ramadanAdminNoteCountByIftarDay = new Map<string, number>();
  const ramadanAdminNoteCountBySponsorship = new Map<string, number>();
  for (const note of state.ramadanAdminNotes) {
    if (note.iftarDayId) {
      ramadanAdminNoteCountByIftarDay.set(
        note.iftarDayId,
        (ramadanAdminNoteCountByIftarDay.get(note.iftarDayId) ?? 0) + 1,
      );
    }
    if (note.sponsorshipId) {
      ramadanAdminNoteCountBySponsorship.set(
        note.sponsorshipId,
        (ramadanAdminNoteCountBySponsorship.get(note.sponsorshipId) ?? 0) + 1,
      );
    }
  }
  const filteredRamadanAdminNotes = state.ramadanAdminNotes.filter((note) => {
    if (state.ramadanAdminNoteDayFilter === "ramadan") {
      return note.iftarDayId === null;
    }
    if (state.ramadanAdminNoteDayFilter) {
      return note.iftarDayId === state.ramadanAdminNoteDayFilter;
    }
    return true;
  });
  const ramadanAdminNoteSponsorshipOptions = state.sponsorships.filter(
    (sponsorship) =>
      !state.ramadanAdminNoteEditor.iftarDayId ||
      sponsorship.iftarDayId === state.ramadanAdminNoteEditor.iftarDayId,
  );
  const renderIftarSponsorManagementActions = (
    sponsorship: IftarSponsorshipAdminRow,
    currentDayId: string,
  ): string => {
    const canTransfer =
      selectedContextCanTransferIftarSponsorship() &&
      isReservedOrConfirmedIftarSponsorship(sponsorship);
    const transferDestinations = canTransfer
      ? openIftarSponsorshipDays
          .filter(
            (targetDay) =>
              targetDay.id !== currentDayId &&
              availableIftarSponsorshipSpots(targetDay) >= sponsorship.quantitySpots,
          )
          .sort((left, right) => left.ramadanDay - right.ramadanDay)
      : [];
    const transferPromptOpen = state.iftarSponsorshipTransferPromptId === sponsorship.sponsorshipId;
    const editPromptOpen = state.iftarSponsorshipEditPromptId === sponsorship.sponsorshipId;
    const canAdjustSpots = canAdjustIftarSponsorshipSpotCount(sponsorship);
    const maxSpotCount = maxAdjustableIftarSponsorshipSpots(sponsorship);
    const transferControls =
      transferDestinations.length > 0
        ? transferPromptOpen
          ? `<span class="ramadan-transfer-picker">
              <span class="ramadan-transfer-label">Move to</span>
              <select class="inline-select" data-iftar-transfer-destination="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" aria-label="Move sponsor to another day">
                ${transferDestinations
                  .map(
                    (targetDay) =>
                      `<option value="${escapeHtml(targetDay.id)}">Day ${
                        targetDay.ramadanDay
                      } · ${escapeHtml(targetDay.gregorianDate)} · ${availableIftarSponsorshipSpots(
                        targetDay,
                      )} open</option>`,
                  )
                  .join("")}
              </select>
              <button class="primary-action inline-action icon-action" type="button" data-iftar-transfer-sponsorship="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" data-tooltip="Confirm move" title="Confirm move" ${
                state.busy ? "disabled" : ""
              }>${renderEventActionIcon("checkIn")}</button>
              <button class="secondary-action inline-action icon-action" type="button" data-iftar-transfer-prompt-cancel="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" data-tooltip="Cancel move" title="Cancel move" ${
                state.busy ? "disabled" : ""
              }>${renderCloseIcon()}</button>
            </span>`
          : `<button class="secondary-action inline-action icon-action" type="button" data-iftar-transfer-prompt="${escapeHtml(
              sponsorship.sponsorshipId,
            )}" data-tooltip="Move sponsor" title="Move sponsor" ${
              state.busy ? "disabled" : ""
            }>${renderEventActionIcon("move")}</button>`
        : "";

    const canDelete =
      selectedContextCanTransferIftarSponsorship() &&
      (isReservedIftarSponsorship(sponsorship) ||
        (sponsorship.status === "confirmed" && sponsorship.paymentSource === "manual"));
    const deleteLabel = isReservedIftarSponsorship(sponsorship)
      ? "Delete reservation"
      : "Delete sponsor";
    const deletePromptOpen = state.iftarSponsorshipDeletePromptId === sponsorship.sponsorshipId;
    const deleteControl = canDelete
      ? deletePromptOpen
        ? `<span class="ramadan-delete-confirmation">
            <button class="danger-action inline-action icon-action" type="button" data-iftar-sponsorship-delete-confirm="${escapeHtml(
              sponsorship.sponsorshipId,
            )}" data-tooltip="Confirm delete" title="Confirm delete" ${
              state.busy ? "disabled" : ""
            }>${renderEventActionIcon("delete")}</button>
            <button class="secondary-action inline-action icon-action" type="button" data-iftar-sponsorship-delete-cancel="${escapeHtml(
              sponsorship.sponsorshipId,
            )}" data-tooltip="Cancel delete" title="Cancel delete" ${
              state.busy ? "disabled" : ""
            }>${renderCloseIcon()}</button>
          </span>`
        : `<button class="danger-action inline-action icon-action" type="button" data-iftar-sponsorship-delete-prompt="${escapeHtml(
            sponsorship.sponsorshipId,
          )}" data-tooltip="${deleteLabel}" title="${deleteLabel}" ${
            state.busy ? "disabled" : ""
          }>${renderEventActionIcon("delete")}</button>`
      : "";

    const editControl = selectedContextCanTransferIftarSponsorship()
      ? editPromptOpen
        ? `<span class="ramadan-sponsor-edit-controls">
	            <input class="inline-input" data-iftar-sponsor-edit-label="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" aria-label="Sponsor display name" maxlength="120" value="${escapeHtml(
                sponsorship.safeSponsorLabel,
              )}" />
	            <input class="inline-input spots-input" data-iftar-sponsor-edit-spots="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" aria-label="Sponsor spots" type="number" min="1" max="${maxSpotCount}" value="${
                sponsorship.quantitySpots
              }" ${canAdjustSpots ? "" : "disabled"} title="${
                canAdjustSpots
                  ? "Adjust manual confirmed sponsor spots"
                  : "Spot count changes for this record need payment review"
              }" />
	            <button class="primary-action inline-action icon-action" type="button" data-iftar-sponsor-edit-submit="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" data-tooltip="Save sponsor" title="Save sponsor" ${
                state.busy ? "disabled" : ""
              }>${renderEventActionIcon("checkIn")}</button>
	            <button class="secondary-action inline-action icon-action" type="button" data-iftar-sponsor-edit-cancel="${escapeHtml(
                sponsorship.sponsorshipId,
              )}" data-tooltip="Cancel edit" title="Cancel edit" ${
                state.busy ? "disabled" : ""
              }>${renderCloseIcon()}</button>
	          </span>`
        : `<button class="secondary-action inline-action icon-action" type="button" data-iftar-sponsor-edit-prompt="${escapeHtml(
            sponsorship.sponsorshipId,
          )}" data-tooltip="Edit sponsor" title="Edit sponsor" ${
            state.busy ? "disabled" : ""
          }>${renderEventActionIcon("edit")}</button>`
      : "";

    return `<div class="table-actions ramadan-sponsor-actions" data-iftar-transfer-row>
      ${editControl}
      ${transferControls}
      ${deleteControl}
    </div>`;
  };
  const availableSpaceSlots = state.spaceSlots.filter((slot) => slot.status === "available");
  const pendingSpaceReservationRequests = state.spaceReservationRequests.filter(
    (reservation) => reservation.status === "pending",
  );
  const approvedSpaceReservationRequests = state.spaceReservationRequests.filter(
    (reservation) => reservation.status === "approved",
  );
  const publishedVolunteerOpportunities = state.volunteerOpportunities.filter(
    (opportunity) => opportunity.status === "published",
  );
  const openVolunteerShifts = state.volunteerShifts.filter((shift) => shift.status === "open");
  const confirmedVolunteerSignups = state.volunteerSignups.filter(
    (signup) => signup.status === "confirmed",
  );
  const checkedInVolunteerAttendances = state.volunteerAttendances.filter(
    (attendance) => attendance.status === "checkedIn",
  );
  const totalVolunteerVerifiedMinutes = state.volunteerServiceSummaries.reduce(
    (total, summary) => total + summary.totalVerifiedMinutes,
    0,
  );
  const workspacePages = buildOrgAdminWorkspacePages({
    canManageCommerce,
    canManageRamadanAdminNotes,
    canManageSupport,
    canManageVolunteers,
    canReadBilling,
    locale: state.locale,
    openMemberSupportTickets: activeSupportTicketCount(state.memberSupportTickets),
    openShdeedSupportTickets: activeSupportTicketCount(state.shdeedSupportTickets),
    organizationDisplayName,
    pendingConnectionRequests: requestSummary.pending,
    pendingSpaceReservationRequests: pendingSpaceReservationRequests.length,
  }).filter((page) => canOpenOrganizationPage(page.id, canonicalRoleIdsForSelectedContext()));
  const activePageId = resolveWorkspaceRouteId(window.location.hash, workspacePages);
  const manualIftarSponsorPeople = peopleManagerRows().filter(
    (row) => row.connection.status === "active",
  );
  const personLabelById = new Map(
    manualIftarSponsorPeople.map((row) => [row.connection.personId, peopleRowPersonLabel(row)]),
  );
  const manualIftarSponsorDayOptions = state.iftarSponsorshipDays.filter(
    (day) =>
      day.status === "open" && day.sponsorshipEnabled && availableIftarSponsorshipSpots(day) > 0,
  );
  const manualIftarSponsorSelectedDay = state.iftarSponsorshipDays.find(
    (day) => day.id === state.iftarSponsorshipManualEditor.iftarDayId,
  );
  const manualIftarSponsorAvailableSpots = manualIftarSponsorSelectedDay
    ? availableIftarSponsorshipSpots(manualIftarSponsorSelectedDay)
    : 0;
  const latestInsight = state.intelligenceSnapshots[0];
  const linkedRamadanOperationalModeActive =
    currentRamadanSetup !== undefined &&
    (state.operationalModes.some(
      (mode) =>
        mode.modeId === currentRamadanSetup.linkedOperationalModeId && mode.status === "active",
    ) ||
      state.operationalModeProjection?.modeId === currentRamadanSetup.linkedOperationalModeId);
  const ramadanOperationStateLabel = linkedRamadanOperationalModeActive
    ? "On"
    : currentRamadanSetup?.status === "active"
      ? "Setup active"
      : "Off";
  const ramadanOperationStateClass = linkedRamadanOperationalModeActive
    ? "status-accepted"
    : currentRamadanSetup?.status === "active"
      ? "status-needs-review"
      : "status-pending";
  const ramadanSetupLifecycleClass =
    currentRamadanSetup?.status === "active" || currentRamadanSetup?.status === "ready"
      ? "status-accepted"
      : currentRamadanSetup?.status === "completed"
        ? "status-pending"
        : "status-needs-review";

  appRoot.innerHTML = `
    <div class="shell">
      <aside class="sidebar" style="--brand-color: ${organizationBrandColor}">
        <div class="sidebar-brand-identity">
        ${renderShdeedBrand(state.locale)}
        <div class="sidebar-brand-block">
          ${renderOrganizationLogo({
            brandColor: organizationBrandColor,
            className: "organization-logo-sidebar",
            displayName: organizationDisplayName,
            logoStoragePath: organizationLogoStoragePath,
            logoUrl: state.organizationProfileLogoDownloadUrl,
          })}
          <div>
            <p class="sidebar-eyebrow">${escapeHtml(organizationDisplayName)}</p>
          </div>
        </div>
        </div>
        <nav class="nav" aria-label="Admin sections">
          ${renderWorkspaceNavigation(workspacePages, activePageId)}
        </nav>
        ${renderAccountMenuControl()}
        ${renderCloudPixelEndorsement(state.locale)}
      </aside>
      <section class="workspace">
        <header class="topbar compact-org-header">
          <div class="compact-org-identity">
            ${renderOrganizationLogo({
              brandColor: organizationBrandColor,
              className: "organization-logo-title",
              displayName: organizationDisplayName,
              logoStoragePath: organizationLogoStoragePath,
              logoUrl: state.organizationProfileLogoDownloadUrl,
            })}
            <div>
              <p class="page-kicker">${copy.organization}</p>
              <h1>${organizationDisplayName}</h1>
              <p class="muted">${escapeHtml(organizationLocationSummary)}</p>
            </div>
          </div>
          <div class="controls compact-context-controls">
            ${
              environmentConfig.bannerLabel
                ? `<span class="banner">${environmentConfig.bannerLabel} · ${projectId}</span>`
                : ""
            }
            <label>
              ${copy.organization}
              <select id="organization" ${state.busy || operationalModeBusy ? "disabled" : ""}>
                ${organizationAccess.organizations
                  .map(
                    (candidate) =>
                      `<option value="${escapeHtml(candidate.id)}" ${
                        candidate.id === state.organizationId ? "selected" : ""
                      }>${escapeHtml(candidate.displayName)}</option>`,
                  )
                  .join("")}
              </select>
            </label>
            <label>
              ${copy.language}
              <select id="locale">
                <option value="en" ${state.locale === "en" ? "selected" : ""}>English</option>
                <option value="ar" ${state.locale === "ar" ? "selected" : ""}>العربية</option>
              </select>
            </label>
            ${
              useEmulator
                ? `<label>
                    ${copy.connectedAs}
                    <select id="identity">
                      ${
                        !organizationUsers.some((candidate) => candidate.uid === state.userUid)
                          ? `<option value="${escapeHtml(state.userUid)}" selected>${escapeHtml(currentAccountEmailLabel())}</option>`
                          : ""
                      }
                      ${organizationUsers
                        .map(
                          (candidate) =>
                            `<option value="${candidate.uid}" ${
                              candidate.uid === state.userUid ? "selected" : ""
                            }>${candidate.displayName}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>`
                : ""
            }
          </div>
        </header>
        <section class="content">
          <div class="grid">
            ${
              useEmulator
                ? `<article id="section-home" class="panel">
                    <h2>${copy.realTimeStatus}</h2>
                    <p class="status">${state.status}</p>
                    <p class="meta">v${state.version} · ${escapeHtml(currentAccountEmailLabel())}</p>
                    ${state.error ? `<p class="meta error" role="alert">${escapeHtml(adminFeedback(state.error, state.locale))}</p>` : ""}
                  </article>`
                : ""
            }
            <article class="panel" data-workspace-panel-id="dashboard-roles">
              <h2>${state.locale === "ar" ? "صلاحياتك" : "Your access"}</h2>
              <p class="status">${
                canonicalRoleIdsForSelectedContext().map(roleLabel).join(", ") ||
                (state.locale === "ar" ? "لا يوجد اتصال نشط" : "No active connection")
              }</p>
              <p class="meta">${escapeHtml(organizationDisplayName)}</p>
            </article>
            <article id="section-welcome" class="panel">
              <h2>${copy.connectionRequests}</h2>
              <div class="request-summary" aria-label="${copy.connectionRequests}">
                ${renderConnectionRequestFilterButton("total", requestSummary.total)}
                ${renderConnectionRequestFilterButton("pending", requestSummary.pending)}
                ${renderConnectionRequestFilterButton("accepted", requestSummary.accepted)}
                ${renderConnectionRequestFilterButton("needsReview", requestSummary.needsReview)}
              </div>
              ${
                filteredConnectionRequests.length === 0
                  ? `<p class="meta">${connectionRequestEmptyMessage}</p>`
                  : `<ul class="request-list">
                    ${filteredConnectionRequests
                      .map(
                        (request) => `<li>
                          <span>${request.uid}<br><small>${request.requestedRole} · ${
                            request.locale
                          }</small></span>
                          <span>
                            <span class="status-pill ${requestStatusClass(request.status)}">
                              ${requestStatusLabel(request.status, state.locale)}
                            </span>
                            ${
                              request.status === "needs_review" ||
                              request.status === "pending_auto_accept"
                                ? `<br><button
                                    class="inline-action"
                                    data-connection-decision="approve"
                                    data-connection-request-id="${escapeHtml(request.id)}"
                                    type="button"
                                    ${state.busy ? "disabled" : ""}
	                                  >${copy.approveConnection}</button>
	                                  <button
	                                    class="danger-action inline-action"
	                                    data-connection-decision="reject"
	                                    data-connection-request-id="${escapeHtml(request.id)}"
	                                    type="button"
                                    ${state.busy ? "disabled" : ""}
                                  >${copy.rejectConnection}</button>`
                                : ""
                            }
                          </span>
                        </li>`,
                      )
                      .join("")}
                  </ul>`
              }
            </article>
            <article id="section-people" class="panel">
              ${renderPeopleManagerPanel(copy.peopleManager, copy.connectionDecisions, organizationDisplayName)}
            </article>
            <article id="section-member-support" class="panel support-panel">
              ${renderMemberSupportPanel()}
            </article>
            <article id="section-shdeed-support" class="panel support-panel">
              ${renderShdeedSupportPanel()}
            </article>
            <article id="section-directory" class="panel editor-panel">
              <h2>Business directory</h2>
              <p class="meta">Global businesses are platform-owned. This organization can only manage its own affiliation records.</p>
              ${
                state.businessAffiliations.length === 0
                  ? `<p class="meta">No business affiliations for ${organizationDisplayName}.</p>`
                  : `<ul class="request-list">
                    ${state.businessAffiliations
                      .map(
                        (affiliation) => `<li>
                          <span>${escapeHtml(affiliation.businessNameSnapshot)}<br><small>${escapeHtml(
                            affiliation.businessId,
                          )} · ${escapeHtml(affiliation.affiliationType)} · ${escapeHtml(
                            affiliation.verificationState,
                          )}</small></span>
                          <span class="status-pill ${
                            affiliation.status === "active" ? "status-accepted" : "status-pending"
                          }">${escapeHtml(affiliation.status)}</span>
                        </li>`,
                      )
                      .join("")}
                  </ul>`
              }
              ${
                state.businessDirectory.length === 0
                  ? `<p class="meta">No active global business records are available.</p>`
                  : `<ul class="request-list compact-list">
                    ${state.businessDirectory
                      .slice(0, 4)
                      .map(
                        (business) => `<li>
                          <span>${escapeHtml(business.name)}<br><small>${escapeHtml(
                            business.city,
                          )} · ${escapeHtml(business.categoryIds.join(", "))}</small></span>
                          <span class="status-pill ${
                            business.verificationState === "verified"
                              ? "status-accepted"
                              : "status-pending"
                          }">${escapeHtml(
                            business.featured
                              ? `featured · ${business.verificationState}`
                              : business.verificationState,
                          )}</span>
                        </li>`,
                      )
                      .join("")}
                  </ul>`
              }
              ${
                canManageBusinessDirectory
                  ? `<form id="business-affiliation-form" class="editor-form">
                    <div class="editor-grid">
                      <label>
                        Business
                        <select name="businessId" ${state.businessDirectory.length === 0 ? "disabled" : ""}>
                          ${state.businessDirectory
                            .map(
                              (business) =>
                                `<option value="${escapeHtml(business.businessId)}">${escapeHtml(
                                  business.name,
                                )}</option>`,
                            )
                            .join("")}
                        </select>
                      </label>
                      <label>
                        Status
                        <select name="status">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="removed">Removed</option>
                        </select>
                      </label>
                    </div>
                    <div class="editor-grid">
                      <label>
                        Type
                        <select name="affiliationType">
                          <option value="listed">Listed</option>
                          <option value="recommended">Recommended</option>
                          <option value="sponsor">Sponsor</option>
                          <option value="vendor">Vendor</option>
                          <option value="partner">Partner</option>
                        </select>
                      </label>
                      <label>
                        Display order
                        <input min="0" max="9999" name="displayOrder" type="number" value="100" />
                      </label>
                    </div>
                    <button type="submit" ${state.busy || state.businessDirectory.length === 0 ? "disabled" : ""}>Save affiliation</button>
                  </form>`
                  : `<p class="meta">Owner or organization admin access is required to manage business affiliations.</p>`
              }
              ${
                state.businessDirectoryMessage
                  ? `<p class="status">${escapeHtml(state.businessDirectoryMessage)}</p>`
                  : ""
              }
            </article>
            <article id="section-settings" class="panel">
              <div class="panel-header-actions">
                <div>
                  <h2>${copy.organizationProfileEditor}</h2>
                  <p class="meta">Public organization details update the mobile app through the organization directory.</p>
                </div>
                <button
                  class="secondary-action"
                  data-task-window-open="organization-profile-editor-form"
                  type="button"
                >${copy.editOrganizationProfile}</button>
              </div>
              <div class="organization-profile-summary">
                ${renderOrganizationLogo({
                  brandColor: organizationBrandColor,
                  className: "organization-logo-profile",
                  displayName: organizationDisplayName,
                  logoStoragePath: organizationLogoStoragePath,
                  logoUrl: organizationSettingsLogoUrl,
                })}
                <div>
                  <p class="status">${organizationHeadline}</p>
                  <p class="meta">${organizationSummary}</p>
                </div>
              </div>
              ${
                state.organizationProfileMessage
                  ? `<p class="status status-inline">${escapeHtml(state.organizationProfileMessage)}</p>`
                  : ""
              }
              ${
                state.organizationProfileLogoError
                  ? `<p class="meta error">${escapeHtml(state.organizationProfileLogoError)}</p>`
                  : ""
              }
              ${renderOrganizationProfileDetails({
                copy,
                locale: state.locale,
                locationSummary: organizationLocationSummary,
                profile: organizationProfile,
              })}
            </article>
            <article class="panel editor-panel task-only-panel" data-workspace-panel-id="organization-profile-editor">
              <h2>${copy.organizationProfileEditor}</h2>
              <form
                id="organization-profile-editor-form"
                class="editor-form"
                data-task-window-launch="manual"
              >
                <section class="form-section">
                  <h3>${copy.organizationIdentityDetails}</h3>
                  <div class="organization-logo-editor-preview">
                    ${renderOrganizationLogo({
                      brandColor:
                        state.organizationProfileEditor.brandColor || organizationBrandColor,
                      className: "organization-logo-profile",
                      displayName:
                        state.organizationProfileEditor.displayName || organizationDisplayName,
                      logoStoragePath: organizationLogoStoragePath,
                      logoUrl: organizationSettingsLogoUrl,
                    })}
                    <div>
                      <p class="status">${escapeHtml(
                        state.organizationProfileEditor.displayName || organizationDisplayName,
                      )}</p>
                      ${
                        state.organizationProfileLogoFile
                          ? `<p class="meta">${selectedLogoFileLabel}: ${escapeHtml(
                              state.organizationProfileLogoFile.name,
                            )}</p>`
                          : ""
                      }
                    </div>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationDisplayName}
                      <input
                        data-organization-profile-editor-field="displayName"
                        maxlength="120"
                        required
                        value="${escapeHtml(state.organizationProfileEditor.displayName)}"
                      />
                    </label>
                    <label>
                      ${copy.organizationBrandColor}
                      <input
                        data-organization-profile-editor-field="brandColor"
                        maxlength="7"
                        pattern="#[0-9a-fA-F]{6}"
                        placeholder="#116466"
                        required
                        value="${escapeHtml(state.organizationProfileEditor.brandColor)}"
                      />
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationLogoFile}
                      <input
                        accept="image/png,image/jpeg,image/webp"
                        id="organization-profile-logo-file"
                        type="file"
                      />
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationCategory}
                      <select data-organization-profile-editor-field="category">
                        <option value="Islamic center" ${
                          state.organizationProfileEditor.category === "Islamic center"
                            ? "selected"
                            : ""
                        }>Islamic center</option>
                        <option value="Community center" ${
                          state.organizationProfileEditor.category === "Community center"
                            ? "selected"
                            : ""
                        }>Community center</option>
                      </select>
                    </label>
                    <label>
                      ${copy.organizationConnectionPolicy}
                      <select data-organization-profile-editor-field="connectionPolicy">
                        <option value="open" ${
                          state.organizationProfileEditor.connectionPolicy === "open"
                            ? "selected"
                            : ""
                        }>${copy.policyOpen}</option>
                        <option value="approval_required" ${
                          state.organizationProfileEditor.connectionPolicy === "approval_required"
                            ? "selected"
                            : ""
                        }>${copy.policyApprovalRequired}</option>
                      </select>
                    </label>
                  </div>
                </section>
                <section class="form-section">
                  <h3>${copy.organizationLocationDetails}</h3>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationAddressLine1}
                      <input
                        data-organization-profile-editor-field="addressLine1"
                        maxlength="120"
                        value="${escapeHtml(state.organizationProfileEditor.addressLine1)}"
                      />
                    </label>
                    <label>
                      ${copy.organizationAddressLine2}
                      <input
                        data-organization-profile-editor-field="addressLine2"
                        maxlength="120"
                        value="${escapeHtml(state.organizationProfileEditor.addressLine2)}"
                      />
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationCity}
                      <input
                        data-organization-profile-editor-field="city"
                        maxlength="120"
                        required
                        value="${escapeHtml(state.organizationProfileEditor.city)}"
                      />
                    </label>
                    <label>
                      ${copy.organizationRegion}
                      <select
                        data-organization-profile-editor-field="region"
                        required
                      >
                        ${renderOrganizationProfileSelectOptions(
                          organizationRegionOptions,
                          state.organizationProfileEditor.region.toUpperCase(),
                        )}
                      </select>
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationPostalCode}
                      <input
                        data-organization-profile-editor-field="postalCode"
                        maxlength="24"
                        placeholder="19006"
                        required
                        value="${escapeHtml(state.organizationProfileEditor.postalCode)}"
                      />
                    </label>
                    <label>
                      ${copy.organizationCountry}
                      <select
                        data-organization-profile-editor-field="country"
                        required
                      >
                        ${renderOrganizationProfileSelectOptions(
                          organizationCountryOptions,
                          state.organizationProfileEditor.country.toUpperCase(),
                        )}
                      </select>
                    </label>
                  </div>
                  <label>
                    ${copy.organizationTimeZone}
                    <select
                      data-organization-profile-editor-field="timeZone"
                      required
                    >
                      ${renderOrganizationProfileSelectOptions(
                        organizationTimeZoneOptions,
                        state.organizationProfileEditor.timeZone,
                      )}
                    </select>
                  </label>
                </section>
                <section class="form-section">
                  <h3>${copy.organizationPublicContact}</h3>
                  <div class="editor-grid">
                    <label>
                      ${copy.organizationPublicEmail}
                      <input
                        data-organization-profile-editor-field="publicEmail"
                        maxlength="254"
                        type="email"
                        value="${escapeHtml(state.organizationProfileEditor.publicEmail)}"
                      />
                    </label>
                    <label>
                      ${copy.organizationPublicPhone}
                      <input
                        data-organization-profile-editor-field="publicPhone"
                        inputmode="numeric"
                        maxlength="32"
                        pattern="\\([0-9]{3}\\)[0-9]{3}-[0-9]{4}"
                        placeholder="(215)555-0100"
                        type="tel"
                        value="${escapeHtml(state.organizationProfileEditor.publicPhone)}"
                      />
                    </label>
                  </div>
                  <label>
                    ${copy.organizationWebsiteUrl}
                    <input
                      data-organization-profile-editor-field="websiteUrl"
                      maxlength="2048"
                      type="url"
                      value="${escapeHtml(state.organizationProfileEditor.websiteUrl)}"
                    />
                  </label>
                </section>
                <section class="form-section">
                  <h3>${copy.organizationPublicVisibility}</h3>
                  <label>${state.locale === "ar" ? "الاسم البديل العام" : "Public alternate name"}
                    <input data-organization-profile-editor-field="alternateName" maxlength="120" value="${escapeHtml(state.organizationProfileEditor.alternateName ?? "")}" />
                  </label>
                  <div class="checkbox-row">
                    <label>
                      <input
                        data-organization-profile-editor-field="publicVisibilityDirectory"
                        type="checkbox"
                        ${state.organizationProfileEditor.publicVisibilityDirectory ? "checked" : ""}
                      />
                      ${copy.organizationVisibilityDirectory}
                    </label>
                    <label>
                      <input
                        data-organization-profile-editor-field="publicVisibilityDiscovery"
                        type="checkbox"
                        ${state.organizationProfileEditor.publicVisibilityDiscovery ? "checked" : ""}
                      />
                      ${copy.organizationVisibilityDiscovery}
                    </label>
                    <label>
                      <input
                        data-organization-profile-editor-field="publicVisibilityProfile"
                        type="checkbox"
                        ${state.organizationProfileEditor.publicVisibilityProfile ? "checked" : ""}
                      />
                      ${copy.organizationVisibilityProfile}
                    </label>
                  </div>
                </section>
                <section class="form-section">
                  <h3>${copy.organizationWelcomeCopy}</h3>
                  <label>
                    ${copy.organizationHeadline}
                    <input
                      data-organization-profile-editor-field="welcomeHeadline"
                      maxlength="180"
                      required
                      value="${escapeHtml(state.organizationProfileEditor.welcomeHeadline)}"
                    />
                  </label>
                  <label>
                    ${copy.organizationHeadlineAr}
                    <input
                      data-organization-profile-editor-field="welcomeHeadlineAr"
                      maxlength="180"
                      value="${escapeHtml(state.organizationProfileEditor.welcomeHeadlineAr)}"
                    />
                  </label>
                  <label>
                    ${copy.organizationSummary}
                    <textarea
                      data-organization-profile-editor-field="welcomeSummary"
                      maxlength="600"
                      required
                    >${escapeHtml(state.organizationProfileEditor.welcomeSummary)}</textarea>
                  </label>
                  <label>
                    ${copy.organizationSummaryAr}
                    <textarea
                      data-organization-profile-editor-field="welcomeSummaryAr"
                      maxlength="600"
                    >${escapeHtml(state.organizationProfileEditor.welcomeSummaryAr)}</textarea>
                  </label>
                </section>
                <section class="form-section">
                  <h3>${state.locale === "ar" ? "الإمام أو القائد العام" : "Public Imam / primary leader"}</h3>
                  <label class="checkbox-row"><input type="checkbox" data-organization-profile-editor-field="leaderPublished" ${state.organizationProfileEditor.leaderPublished ? "checked" : ""} />${state.locale === "ar" ? "نشر معلومات هذا القائد في اكتشاف المؤسسات" : "Publish this leader in organization discovery"}</label>
                  <div class="form-grid">
                    <label>${state.locale === "ar" ? "الاسم العام" : "Public name"}<input data-organization-profile-editor-field="leaderDisplayName" maxlength="120" value="${escapeHtml(state.organizationProfileEditor.leaderDisplayName ?? "")}" /></label>
                    <label>${state.locale === "ar" ? "الصفة" : "Title"}<input data-organization-profile-editor-field="leaderTitle" maxlength="120" value="${escapeHtml(state.organizationProfileEditor.leaderTitle ?? "")}" /></label>
                  </div>
                  <label>${state.locale === "ar" ? "رابط الصورة العامة" : "Public photo URL"}<input type="url" data-organization-profile-editor-field="leaderPhotoUrl" maxlength="2048" value="${escapeHtml(state.organizationProfileEditor.leaderPhotoUrl ?? "")}" /></label>
                  <label>${state.locale === "ar" ? "نبذة عامة قصيرة" : "Short public bio"}<textarea data-organization-profile-editor-field="leaderShortBio" maxlength="600" rows="3">${escapeHtml(state.organizationProfileEditor.leaderShortBio ?? "")}</textarea></label>
                </section>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.saveOrganizationProfile
                  }</button>
                  <button id="reset-organization-profile-form" type="button">${
                    copy.resetOrganizationProfileForm
                  }</button>
                </div>
              </form>
            </article>
            ${renderImamContentSection()}
            <article class="panel editor-panel task-only-panel" data-workspace-panel-id="imam-content-editor" aria-hidden="true"></article>
            <article id="section-events" class="panel event-summary-panel">
              <div class="list-toolbar">
                <div>
                  <h2>${copy.communityEvents}</h2>
                  <p class="meta">${
                    sortedEvents.length
                  } / ${state.events.length} ${state.locale === "ar" ? "فعاليات" : "events"}</p>
                </div>
                <div class="list-toolbar-actions">
                  <label class="list-search">
                    <span>Search events</span>
                    <input
                      id="event-list-search"
                      maxlength="160"
                      placeholder="Title, type, status, location..."
                      value="${escapeHtml(eventListSearch)}"
                    />
                  </label>
                  ${
                    canManageEvents
                      ? `<button data-event-create type="button">Create event</button>`
                      : ""
                  }
                </div>
              </div>
              ${
                state.events.length === 0
                  ? `<p class="meta">No community events for ${organization.displayName}.</p>`
                  : sortedEvents.length === 0
                    ? `<p class="meta">No events match "${escapeHtml(eventListSearch)}".</p>`
                    : `<div class="table-scroll" role="region" aria-label="Community events">
                    <table class="data-table events-table">
                      <thead>
                        <tr>
                          ${eventTableSortableHeader("title", "Event")}
                          ${eventTableSortableHeader("startsAt", "When")}
                          ${eventTableSortableHeader("location", "Location")}
                          ${eventTableSortableHeader("registration", "Registration")}
                          ${eventTableSortableHeader("attendance", "Check-in")}
                          ${eventTableSortableHeader("status", "Status")}
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${paginatedEvents.items
                          .map((event) => {
                            const title =
                              state.locale === "ar" && event.titleAr ? event.titleAr : event.title;
                            const summary =
                              state.locale === "ar" && event.summaryAr
                                ? event.summaryAr
                                : event.summary;
                            const start = event.startsAt
                              ? event.startsAt.toLocaleString(state.locale)
                              : "-";
                            const campus = event.campusNameSnapshot ?? copy.eventNoCampus;
                            const location = [campus, event.locationName]
                              .filter((part) => part && part !== "-")
                              .join(" · ");
                            const notification =
                              event.notificationIntentStatus === "requested"
                                ? (event.notificationIntentId ?? "requested")
                                : event.notificationRequestedOnPublish
                                  ? "pending backend intent"
                                  : "no notification";
                            const typeLabel = eventTypeLabel(event.eventType);
                            const registration = event.registrationEnabled
                              ? `${event.registrationConfirmedCount}/${event.registrationCapacity} · ${event.waitlistCount} waitlisted`
                              : "No registration";
                            const attendance = `${event.attendanceCheckedInCount}/${event.registrationConfirmedCount}`;
                            const delistDisabled =
                              state.busy || event.status === "draft" || event.status === "archived";
                            const deleteDisabled = state.busy || event.status === "archived";
                            const checkInDisabled = state.busy || !event.registrationEnabled;
                            const checkInTooltip = event.registrationEnabled
                              ? "Event check-in"
                              : "Check-in requires registration";
                            const notifyDisabled = state.busy || event.status !== "published";
                            const notifyTooltip =
                              event.status === "published"
                                ? "Resend notifications"
                                : "Publish before sending notifications";
                            return `<tr>
                              <td>
                                <strong>${escapeHtml(title)}</strong>
                                <small>${escapeHtml(typeLabel)}</small>
                                <small>${escapeHtml(summary)}</small>
                                <small>${escapeHtml(notification)}</small>
                              </td>
                              <td>${escapeHtml(start)}</td>
                              <td>${escapeHtml(location || "-")}</td>
                              <td>${escapeHtml(registration)}</td>
                              <td>${escapeHtml(attendance)}</td>
                              <td>
                                <span class="status-pill ${
                                  event.status === "published"
                                    ? "status-accepted"
                                    : "status-pending"
                                }">${escapeHtml(event.status)}</span>
                              </td>
                              <td>
                                <div class="table-actions">
                                  <button
                                    aria-label="${escapeHtml(`Read ${title}`)}"
                                    class="secondary-action inline-action icon-action"
                                    data-event-read="${escapeHtml(event.eventId)}"
                                    data-tooltip="Read"
                                    title="Read"
                                    type="button"
                                  >${renderEventActionIcon("read")}</button>
                                  ${
                                    canManageEvents
                                      ? `<button
                                          aria-label="${escapeHtml(`Edit ${title}`)}"
                                          class="secondary-action inline-action icon-action"
                                          data-event-edit="${escapeHtml(event.eventId)}"
                                          data-tooltip="Edit"
                                          title="Edit"
                                          type="button"
                                        >${renderEventActionIcon("edit")}</button>`
                                      : ""
                                  }
                                  ${
                                    canManageEvents
                                      ? `<button
                                          aria-label="${escapeHtml(`Resend notifications for ${title}`)}"
                                          class="secondary-action inline-action icon-action"
                                          data-event-notify="${escapeHtml(event.eventId)}"
                                          data-tooltip="${escapeHtml(notifyTooltip)}"
                                          title="${escapeHtml(notifyTooltip)}"
                                          type="button"
                                          ${notifyDisabled ? "disabled" : ""}
                                        >${renderEventActionIcon("notify")}</button>`
                                      : ""
                                  }
                                  ${
                                    canCheckInEvents
                                      ? `<button
                                          aria-label="${escapeHtml(`Open check-in actions for ${title}`)}"
                                          class="secondary-action inline-action icon-action"
                                          data-event-check-in="${escapeHtml(event.eventId)}"
                                          data-tooltip="${escapeHtml(checkInTooltip)}"
                                          title="${escapeHtml(checkInTooltip)}"
                                          type="button"
                                          ${checkInDisabled ? "disabled" : ""}
                                        >${renderEventActionIcon("checkIn")}</button>`
                                      : ""
                                  }
                                  ${
                                    canManageEvents
                                      ? `<button
                                          aria-label="${escapeHtml(`${eventActionCopy.makeInvisible}: ${title}`)}"
                                          class="secondary-action inline-action icon-action"
                                          data-event-delist="${escapeHtml(event.eventId)}"
                                          data-tooltip="${escapeHtml(eventActionCopy.makeInvisible)}"
                                          title="${escapeHtml(eventActionCopy.makeInvisible)}"
                                          type="button"
                                          ${delistDisabled ? "disabled" : ""}
                                        >${renderEventActionIcon("delist")}</button>
                                        <button
                                          aria-label="${escapeHtml(`${eventActionCopy.delete}: ${title}`)}"
                                          class="danger-action inline-action icon-action"
                                          data-event-delete="${escapeHtml(event.eventId)}"
                                          data-tooltip="${escapeHtml(eventActionCopy.delete)}"
                                          title="${escapeHtml(eventActionCopy.delete)}"
                                          type="button"
                                          ${deleteDisabled ? "disabled" : ""}
                                        >${renderEventActionIcon("delete")}</button>`
                                      : ""
                                  }
                                </div>
                              </td>
                            </tr>`;
                          })
                          .join("")}
                      </tbody>
                    </table>
                  </div>
                  ${renderPaginationControls("events", paginatedEvents, "events")}`
              }
            </article>
            <article
              id="section-event-editor"
              class="panel editor-panel task-only-panel event-editor-panel"
              aria-hidden="true"
              hidden
            >
              <h2>${copy.eventEditor}</h2>
              ${
                state.eventWizardOpen
                  ? renderEventCreationWizard({ organizationCampuses, selectedEditorEvent })
                  : ""
              }
              <form
                id="${state.eventWizardOpen ? "event-editor-advanced-form" : "event-editor-form"}"
                class="editor-form ${state.eventWizardOpen ? "is-hidden" : ""}"
                ${state.eventWizardOpen ? 'data-task-window-launch="manual"' : ""}
                novalidate
              >
                <div class="editor-grid">
                  <label>
                    ${copy.eventId}
                    <input
                      data-event-editor-field="eventId"
                      name="eventId"
                      maxlength="100"
                      required
                      ${selectedEditorEvent ? "readonly" : ""}
                      value="${escapeHtml(state.eventEditor.eventId)}"
                    />
                  </label>
                  <label>
                    Event type
                    <select data-event-editor-field="eventType" name="eventType" required>
                      <option value="standard" ${
                        state.eventEditor.eventType === "standard" ? "selected" : ""
                      }>Standard event</option>
                      <option value="friday_program" ${
                        state.eventEditor.eventType === "friday_program" ? "selected" : ""
                      }>Friday program</option>
                      <option value="janazah" ${
                        state.eventEditor.eventType === "janazah" ? "selected" : ""
                      }>Janazah / urgent funeral</option>
                    </select>
                  </label>
	                  <label>
	                    ${copy.eventStatus}
	                    <select data-event-editor-field="status" name="status" required>
                      <option value="draft" ${
                        state.eventEditor.status === "draft" ? "selected" : ""
                      }>${copy.eventDraft}</option>
                      <option value="scheduled" ${
                        state.eventEditor.status === "scheduled" ? "selected" : ""
                      }>${copy.eventScheduled}</option>
                      <option value="published" ${
                        state.eventEditor.status === "published" ? "selected" : ""
                      }>${copy.eventPublished}</option>
                      <option value="registration_closed" ${
                        state.eventEditor.status === "registration_closed" ? "selected" : ""
                      }>${copy.eventRegistrationClosedStatus}</option>
                      <option value="cancelled" ${
                        state.eventEditor.status === "cancelled" ? "selected" : ""
                      }>${copy.eventCancelled}</option>
                      <option value="completed" ${
                        state.eventEditor.status === "completed" ? "selected" : ""
                      }>${copy.eventCompleted}</option>
                      <option value="archived" ${
                        state.eventEditor.status === "archived" ? "selected" : ""
                      }>${copy.eventArchived}</option>
	                    </select>
	                  </label>
	                  ${
                      state.eventEditor.status === "scheduled"
                        ? `<label>
	                          ${copy.eventScheduledPublishDate}
	                          <input
	                            data-event-editor-field="scheduledPublishDate"
	                            name="scheduledPublishDate"
	                            required
	                            type="date"
	                            value="${escapeHtml(state.eventEditor.scheduledPublishDate)}"
	                          />
	                          <small>${copy.eventScheduledPublishDateHelp}</small>
	                        </label>`
                        : ""
                    }
	                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.eventTitle}
                    <input
                      data-event-editor-field="title"
                      name="title"
                      maxlength="140"
                      required
                      value="${escapeHtml(state.eventEditor.title)}"
                    />
                  </label>
	                  <label>
	                    ${copy.eventTitleAr}
	                    <input
	                      data-event-editor-field="titleAr"
	                      dir="rtl"
	                      name="titleAr"
                      maxlength="140"
                      value="${escapeHtml(state.eventEditor.titleAr)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.eventSummary}
                  <textarea
                    data-event-editor-field="summary"
                    name="summary"
                    maxlength="300"
                    required
                  >${escapeHtml(state.eventEditor.summary)}</textarea>
                </label>
                <label>
                  ${copy.eventSummaryAr}
	                  <textarea
	                    data-event-editor-field="summaryAr"
	                    dir="rtl"
	                    name="summaryAr"
                    maxlength="300"
                  >${escapeHtml(state.eventEditor.summaryAr)}</textarea>
                </label>
                <label>
                  ${copy.eventDescription}
                  <textarea
                    data-event-editor-field="description"
                    name="description"
                    maxlength="3000"
                    required
                  >${escapeHtml(state.eventEditor.description)}</textarea>
                </label>
                <label>
                  ${copy.eventDescriptionAr}
	                  <textarea
	                    data-event-editor-field="descriptionAr"
	                    dir="rtl"
	                    name="descriptionAr"
                    maxlength="3000"
                  >${escapeHtml(state.eventEditor.descriptionAr)}</textarea>
                </label>
                <label>
                  ${copy.eventLocation}
                  <input
                    data-event-editor-field="locationName"
                    name="locationName"
                    maxlength="160"
                    required
                    value="${escapeHtml(state.eventEditor.locationName)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    Location type
                    <select data-event-editor-field="locationType" name="locationType" required>
                      <option value="physical" ${
                        state.eventEditor.locationType === "physical" ? "selected" : ""
                      }>Physical</option>
                      <option value="virtual" ${
                        state.eventEditor.locationType === "virtual" ? "selected" : ""
                      }>Virtual</option>
                      <option value="hybrid" ${
                        state.eventEditor.locationType === "hybrid" ? "selected" : ""
                      }>Hybrid</option>
                    </select>
                  </label>
                  <label>
                    Address / directions
                    <input
                      data-event-editor-field="addressLine1"
                      maxlength="240"
                      value="${escapeHtml(state.eventEditor.addressLine1)}"
                    />
                  </label>
                </div>
                <label>
                  Online URL
                  <input
                    data-event-editor-field="onlineUrl"
                    maxlength="1000"
                    value="${escapeHtml(state.eventEditor.onlineUrl)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.eventCampus}
                    <select data-event-editor-field="campusId" name="campusId">
                      <option value="">${copy.eventNoCampus}</option>
                      ${organizationCampuses
                        .map(
                          (campus) =>
                            `<option value="${escapeHtml(campus.campusId)}" ${
                              state.eventEditor.campusId === campus.campusId ? "selected" : ""
                            }>${escapeHtml(campus.name)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
	                  <label>
	                    ${copy.eventTimezone}
	                    <select
	                      data-event-editor-field="timezone"
	                      name="timezone"
	                      required
	                    >
	                      ${renderOrganizationProfileSelectOptions(
                          organizationTimeZoneOptions,
                          state.eventEditor.timezone,
                        )}
	                    </select>
	                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.eventStartsAt}
                    <input
                      data-event-editor-field="startsAt"
                      name="startsAt"
                      required
                      type="datetime-local"
                      value="${escapeHtml(state.eventEditor.startsAt)}"
                    />
                  </label>
                  <label>
                    ${copy.eventEndsAt}
                    <input
                      data-event-editor-field="endsAt"
                      name="endsAt"
                      required
                      type="datetime-local"
                      value="${escapeHtml(state.eventEditor.endsAt)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Registration mode
                    <select data-event-editor-field="registrationMode" name="registrationMode" required>
                      <option value="none" ${
                        state.eventEditor.registrationMode === "none" ? "selected" : ""
                      }>No registration</option>
                      <option value="free" ${
                        state.eventEditor.registrationMode === "free" ? "selected" : ""
                      }>Free registration</option>
                    </select>
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="registrationEnabled"
                      type="checkbox"
                      ${state.eventEditor.registrationEnabled ? "checked" : ""}
                    />
                    ${copy.eventRegistrationEnabled}
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="registrationCancellationAllowed"
                      type="checkbox"
                      ${state.eventEditor.registrationCancellationAllowed ? "checked" : ""}
                    />
                    ${copy.eventRegistrationCancellationAllowed}
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="waitlistEnabled"
                      type="checkbox"
                      ${state.eventEditor.waitlistEnabled ? "checked" : ""}
                    />
                    ${copy.eventWaitlistEnabled}
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="guestRegistrationEnabled"
                      type="checkbox"
                      ${state.eventEditor.guestRegistrationEnabled ? "checked" : ""}
                    />
                    Allow guests
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="dependentRegistrationEnabled"
                      type="checkbox"
                      ${state.eventEditor.dependentRegistrationEnabled ? "checked" : ""}
                    />
                    Allow linked dependents
                  </label>
                </div>
                <section class="form-section">
                  <h3>Registration operations</h3>
                  <label>
                    Registration question
                    <input
                      data-event-editor-field="registrationQuestionLabel"
                      maxlength="180"
                      placeholder="Dietary notes, accessibility needs, etc."
                      value="${escapeHtml(state.eventEditor.registrationQuestionLabel)}"
                    />
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="registrationQuestionRequired"
                      type="checkbox"
                      ${state.eventEditor.registrationQuestionRequired ? "checked" : ""}
                    />
                    Required question
                  </label>
                  <label class="checkbox-row">
                    <input
                      data-event-editor-field="waiverRequired"
                      type="checkbox"
                      ${state.eventEditor.waiverRequired ? "checked" : ""}
                    />
                    Require event waiver
                  </label>
                  <div class="editor-grid">
                    <label>
                      Waiver title
                      <input
                        data-event-editor-field="waiverTitle"
                        maxlength="180"
                        ${state.eventEditor.waiverRequired ? "required" : ""}
                        value="${escapeHtml(state.eventEditor.waiverTitle)}"
                      />
                    </label>
                    <label>
                      Reminder presets
                      <span class="checkbox-row">
                        <input
                          data-event-editor-field="reminderOneDayBefore"
                          type="checkbox"
                          ${state.eventEditor.reminderOneDayBefore ? "checked" : ""}
                        />
                        1 day before
                      </span>
                      <span class="checkbox-row">
                        <input
                          data-event-editor-field="reminderTwoHoursBefore"
                          type="checkbox"
                          ${state.eventEditor.reminderTwoHoursBefore ? "checked" : ""}
                        />
                        2 hours before
                      </span>
                    </label>
                  </div>
                  <label>
                    Waiver body
                    <textarea
                      data-event-editor-field="waiverBody"
                      maxlength="12000"
                      ${state.eventEditor.waiverRequired ? "required" : ""}
                    >${escapeHtml(state.eventEditor.waiverBody)}</textarea>
                  </label>
                </section>
                <div class="editor-grid">
                  <label>
                    ${copy.eventRegistrationCapacity}
                    <input
                      data-event-editor-field="registrationCapacity"
	                      min="0"
	                      max="100000"
	                      ${state.eventEditor.registrationEnabled ? "required" : ""}
                      step="1"
                      type="number"
                      value="${escapeHtml(state.eventEditor.registrationCapacity)}"
                    />
                  </label>
                  <label>
                    ${copy.eventRegistrationOpensAt}
                    <input
                      data-event-editor-field="registrationOpensAt"
                      type="datetime-local"
                      value="${escapeHtml(state.eventEditor.registrationOpensAt)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.eventRegistrationClosesAt}
                  <input
                    data-event-editor-field="registrationClosesAt"
                    type="datetime-local"
                    value="${escapeHtml(state.eventEditor.registrationClosesAt)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.eventImageAlt}
                    <input
                      data-event-editor-field="imageAltText"
                      name="imageAltText"
                      maxlength="180"
                      value="${escapeHtml(state.eventEditor.imageAltText)}"
                    />
                  </label>
                  <label>
                    ${copy.eventImageAltAr}
	                    <input
	                      data-event-editor-field="imageAltTextAr"
	                      dir="rtl"
	                      name="imageAltTextAr"
                      maxlength="180"
                      value="${escapeHtml(state.eventEditor.imageAltTextAr)}"
                    />
                  </label>
                </div>
                ${renderEventMediaManager(selectedEditorEvent)}
                <label class="checkbox-row">
                  <input
                    data-event-editor-field="notificationRequestedOnPublish"
                    type="checkbox"
                    ${state.eventEditor.notificationRequestedOnPublish ? "checked" : ""}
                  />
                  <span>
                    ${state.locale === "ar" ? "إرسال إشعار دفع عند النشر" : "Send a push notification when published"}
                  </span>
                </label>
                <label class="checkbox-row">
                  <input data-event-editor-field="emailRequestedOnPublish" type="checkbox" ${state.eventEditor.emailRequestedOnPublish ? "checked" : ""} />
                  <span>${state.locale === "ar" ? "إرسال بريد إلكتروني عند النشر" : "Send an email when published"}</span>
                </label>
                ${state.eventEditor.emailRequestedOnPublish ? renderEventEmailTemplateEditor(eventEmailTemplate, "event-edit-email") : ""}
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${copy.saveEvent}</button>
                  <button id="reset-event-form" data-event-reset-form type="button">${
                    copy.resetEventForm
                  }</button>
                </div>
	                ${
                    state.eventEditorError
                      ? `<p class="error status-inline" role="alert">${escapeHtml(state.eventEditorError)}</p>`
                      : ""
                  }
	                ${
                    state.eventEditorMessage
                      ? `<p class="status status-inline">${escapeHtml(state.eventEditorMessage)}</p>`
                      : ""
                  }
              </form>
            </article>
            <article
              id="section-event-operations"
              class="panel task-only-panel event-operations-panel"
              aria-hidden="true"
              hidden
            >
              <h2>${copy.eventAttendance}</h2>
              <p class="status">
                ${copy.eventRegistrationCount}: ${
                  selectedEditorEvent?.registrationConfirmedCount ?? 0
                }/${selectedEditorEvent?.registrationCapacity ?? state.eventEditor.registrationCapacity}
                · ${copy.eventWaitlistCount}: ${selectedEditorEvent?.waitlistCount ?? 0}
              </p>
              <p class="status">
                ${copy.eventAttendanceCount}: ${selectedEditorEvent?.attendanceCheckedInCount ?? 0}/${
                  selectedEditorEvent?.registrationConfirmedCount ?? 0
                }
              </p>
              <p class="meta">Scoped event: ${escapeHtml(state.eventEditor.eventId)}</p>
              <form id="event-communication-form" class="editor-form">
                <h3>Event communication</h3>
                <div class="editor-grid">
                  <label>
                    Audience
                    <select id="event-communication-audience">
                      <option value="connectedPeople">Connected community</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="waitlisted">Waitlisted</option>
                      <option value="allRegistrants">All registrants</option>
                      <option value="checkedIn">Checked in</option>
                      <option value="notCheckedIn">Not checked in</option>
                    </select>
                  </label>
                  <label>
                    Channels
                    <select id="event-communication-channel">
                      <option value="push,email">Push + Email</option>
                      <option value="push">Push</option>
                      <option value="email">Email</option>
                    </select>
                  </label>
                  <label>
                    Delivery type
                    <select id="event-communication-type">
                      ${eventEmailDeliveryOptionsHtml()}
                    </select>
                  </label>
                </div>
                <label>
                  Subject
                  <input
                    id="event-communication-subject"
                    maxlength="180"
                    required
                    value="${escapeHtml(eventEmailTemplate.subject)}"
                  />
                </label>
                <label>
                  Message
                  <textarea id="event-communication-message" maxlength="4000" required>${escapeHtml(
                    eventEmailTemplate.message,
                  )}</textarea>
                </label>
                ${renderEventEmailPreview(eventEmailTemplate)}
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>Send event update</button>
                </div>
              </form>
              <div class="editor-actions">
                <button id="event-export-attendees" type="button" ${state.busy ? "disabled" : ""}>
                  Export attendees CSV
                </button>
                <button
                  id="event-export-attendees-responses"
                  type="button"
                  ${state.busy ? "disabled" : ""}
                >
                  Export with responses
                </button>
              </div>
              <form id="event-qr-check-in-form" class="editor-form">
                <label>
                  ${copy.eventCheckInStation}
                  <input
                    id="event-check-in-station"
                    maxlength="120"
                    value="${escapeHtml(state.eventCheckInStationLabel)}"
                  />
                </label>
                <label>
                  ${copy.eventCheckInCredential}
                  <textarea
                    id="event-check-in-credential"
                    maxlength="4096"
                    required
                  >${escapeHtml(state.eventCheckInCredential)}</textarea>
                </label>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.eventCheckInSubmit
                  }</button>
                </div>
              </form>
              ${
                state.eventCheckInMessage
                  ? `<p class="status">${escapeHtml(state.eventCheckInMessage)}</p>`
                  : ""
              }
              <label>
                ${copy.eventManualSearch}
                <input
                  id="event-manual-registration-search"
                  maxlength="150"
                  value="${escapeHtml(state.eventManualRegistrationSearch)}"
                />
              </label>
              ${
                filteredRegistrations.length === 0
                  ? `<p class="meta">${copy.eventNoRegistrations}</p>`
                  : `<div class="table-scroll" role="region" aria-label="Confirmed event registrations">
                    <table class="data-table compact-table">
                      <thead>
                        <tr>
                          <th scope="col">Registration</th>
                          <th scope="col">Attendees</th>
                          <th scope="col">Registered</th>
                          <th scope="col">Check-in</th>
                        </tr>
                      </thead>
                      <tbody>
                    ${paginatedRegistrations.items
                      .map((registration) => {
                        const registeredAt = registration.registeredAt
                          ? registration.registeredAt.toLocaleString(state.locale)
                          : "-";
                        const attendance = attendanceByRegistrationId.get(
                          registration.registrationId,
                        );
                        return `<tr>
                          <td>
                            <strong>${escapeHtml(registration.personId)}</strong>
                            <small>${escapeHtml(registration.registrationId)}</small>
                          </td>
                          <td>${registration.attendeeCount}</td>
                          <td>${escapeHtml(registeredAt)}</td>
                          <td>
                            ${
                              attendance
                                ? `<span class="status-pill status-accepted">${copy.eventAlreadyCheckedIn}<br><small>${escapeHtml(
                                    attendance.method,
                                  )}</small></span>`
                                : `<button class="secondary-action inline-action" data-manual-check-in="${escapeHtml(
                                    registration.registrationId,
                                  )}" type="button" ${state.busy ? "disabled" : ""}>${
                                    copy.eventManualCheckIn
                                  }</button>`
                            }
                          </td>
                        </tr>`;
                      })
                      .join("")}
                      </tbody>
                    </table>
                  </div>
                  ${renderPaginationControls(
                    "eventRegistrations",
                    paginatedRegistrations,
                    "confirmed registrations",
                  )}`
              }
              <h3>${copy.eventWaitlistCount}</h3>
              ${
                filteredWaitlistedRegistrations.length === 0
                  ? `<p class="meta">${copy.eventWaitlisted}: 0</p>`
                  : `<div class="table-scroll" role="region" aria-label="Waitlisted event registrations">
                    <table class="data-table compact-table">
                      <thead>
                        <tr>
                          <th scope="col">Registration</th>
                          <th scope="col">Attendees</th>
                          <th scope="col">Registered</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                    ${paginatedWaitlistedRegistrations.items
                      .map((registration) => {
                        const registeredAt = registration.registeredAt
                          ? registration.registeredAt.toLocaleString(state.locale)
                          : "-";
                        return `<tr>
                          <td>
                            <strong>${escapeHtml(registration.personId)}</strong>
                            <small>${escapeHtml(registration.registrationId)}</small>
                          </td>
                          <td>${registration.attendeeCount}</td>
                          <td>${escapeHtml(registeredAt)}</td>
                          <td>
                            ${
                              selectedContextCanManageEvents()
                                ? `<button class="secondary-action inline-action" data-promote-waitlist="${escapeHtml(
                                    registration.registrationId,
                                  )}" type="button" ${state.busy ? "disabled" : ""}>${
                                    copy.eventPromoteWaitlist
                                  }</button>`
                                : `<span class="status-pill status-pending">${copy.eventWaitlisted}</span>`
                            }
                          </td>
                        </tr>`;
                      })
                      .join("")}
                      </tbody>
                    </table>
                  </div>
                  ${renderPaginationControls(
                    "eventWaitlist",
                    paginatedWaitlistedRegistrations,
                    "waitlisted registrations",
                  )}`
              }
              <h3>${copy.eventAttendanceCount}</h3>
              ${
                state.eventAttendance.length === 0
                  ? `<p class="meta">${copy.eventNoAttendance}</p>`
                  : `<div class="table-scroll" role="region" aria-label="Event attendance">
                    <table class="data-table compact-table">
                      <thead>
                        <tr>
                          <th scope="col">Person</th>
                          <th scope="col">Checked in</th>
                          <th scope="col">Method</th>
                          <th scope="col">Staff</th>
                        </tr>
                      </thead>
                      <tbody>
                    ${paginatedAttendance.items
                      .map((attendance) => {
                        const checkedInAt = attendance.checkedInAt
                          ? attendance.checkedInAt.toLocaleString(state.locale)
                          : "-";
                        return `<tr>
                          <td>${escapeHtml(attendance.personId)}</td>
                          <td>${escapeHtml(checkedInAt)}</td>
                          <td><span class="status-pill status-accepted">${escapeHtml(
                            attendance.method,
                          )}</span></td>
                          <td>${escapeHtml(attendance.checkedInBy)}</td>
                        </tr>`;
                      })
                      .join("")}
                      </tbody>
                    </table>
                  </div>
                  ${renderPaginationControls("eventAttendance", paginatedAttendance, "attendance records")}`
              }
            </article>
            <article class="panel" data-workspace-panel-id="commerce-donations">
              <h2>${copy.firstDonation}</h2>
              <p class="status">${copy.donationTotal}: ${formatAmount(donationNetTotal)}</p>
              <p class="meta">Gross ${formatAmount(donationTotal)} · refunded ${formatAmount(
                donationRefundedTotal,
              )}</p>
              ${
                state.donations.length === 0
                  ? `<p class="meta">No donations for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.donations
                      .map((donation) => {
                        const createdAt = donation.createdAt
                          ? donation.createdAt.toLocaleString(state.locale)
                          : "-";
                        const remaining = Math.max(0, donation.remainingRefundableAmountMinor);
                        const canRefundDonation =
                          canRequestRefunds && donation.status === "confirmed" && remaining > 0;
                        const receipt = donation.receiptId ?? "no receipt";
                        return `<li>
	                          <span>${formatAmount(donation.amountMinor)}${renderTechnicalId(
                              donation.id,
                              "Transaction ID",
                            )}${renderTechnicalId(donation.personId, "Person ID")}${renderTechnicalId(
                              donation.providerPaymentIntentId,
                              "Payment intent",
                            )}<br><small>${escapeHtml(
                              donation.destinationLabel,
                            )} · ${escapeHtml(receipt)}</small><br><small>refunded ${formatAmount(
                              donation.refundedAmountMinor,
                            )} · pending ${formatAmount(
                              donation.refundPendingAmountMinor,
                            )} · remaining ${formatAmount(remaining)}</small>${
                              canRefundDonation
                                ? `<br><label class="inline-field">
                                  Refund USD
                                  <input data-refund-amount-input min="0.01" max="${(
                                    remaining / 100
                                  ).toFixed(2)}" step="0.01" type="number" value="${(
                                    remaining / 100
                                  ).toFixed(2)}" />
                                </label>
	                                <button class="danger-action inline-action" data-refund-mode="partial" data-refund-transaction-id="${escapeHtml(
                                    donation.id,
                                  )}" type="button" ${state.busy ? "disabled" : ""}>Refund amount</button>
	                                <button class="danger-action inline-action" data-refund-mode="full" data-refund-transaction-id="${escapeHtml(
                                    donation.id,
                                  )}" type="button" ${state.busy ? "disabled" : ""}>Refund full</button>`
                                : ""
                            }<br><button class="secondary-action inline-action" data-reconcile-transaction-id="${escapeHtml(
                              donation.id,
                            )}" type="button" ${state.busy || !canManageCommerce ? "disabled" : ""}>Reconcile</button></span>
                          <span class="status-pill ${
                            donation.status === "confirmed" ? "status-accepted" : "status-pending"
                          }">${donation.status}<br><small>${escapeHtml(
                            donation.refundStatus,
                          )}</small><br><small>${createdAt}</small></span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article id="section-commerce" class="panel">
              <h2>${copy.stripeConnect}</h2>
              ${renderCommerceTestModeChecklist(commerceStatus)}
              ${
                !canManageCommerce
                  ? `<p class="meta">${copy.commerceFinanceOnly}</p>`
                  : commerceStatus
                    ? `<p class="status">
                        ${copy.commerceReadiness}:
                        <span class="status-pill ${commerceReadinessClass(
                          commerceStatus.readiness,
                        )}">${escapeHtml(commerceStatus.readiness)}</span>
                      </p>
                      <ul class="request-list">
                        <li>
                          <span>${copy.commerceOnboardingStatus}</span>
                          <span>${escapeHtml(commerceStatus.onboardingStatus)}</span>
                        </li>
                        <li>
                          <span>${copy.commerceCharges}</span>
                          <span>${yesNo(commerceStatus.chargesEnabled, state.locale)}</span>
                        </li>
                        <li>
                          <span>${copy.commercePayouts}</span>
                          <span>${yesNo(commerceStatus.payoutsEnabled, state.locale)}</span>
                        </li>
                        <li>
                          <span>${copy.commerceDetailsSubmitted}</span>
                          <span>${yesNo(commerceStatus.detailsSubmitted, state.locale)}</span>
                        </li>
                        <li>
                          <span>${copy.commerceRequirementsDue}</span>
                          <span>${commerceStatus.currentlyDueCount} current · ${
                            commerceStatus.eventuallyDueCount
                          } eventual</span>
                        </li>
                        <li>
                          <span>${copy.commerceLastSync}</span>
                          <span>${commerceLastSync}</span>
                        </li>
                      </ul>
                      ${
                        commerceStatus.restrictedReasonCode
                          ? `<p class="error">${escapeHtml(
                              commerceStatus.restrictedReasonCode,
                            )}</p>`
                          : ""
                      }
                      <button id="start-stripe-onboarding" ${
                        state.busy || commerceStatus.readiness === "ready" ? "disabled" : ""
                      }>${commerceActionLabel}</button>
                      <button id="refresh-stripe-connect" ${state.busy ? "disabled" : ""}>${
                        copy.refreshStripeConnectStatus
                      }</button>`
                    : `<p class="meta">${escapeHtml(
                        state.commerceMessage || "Commerce status unavailable.",
                      )}</p>`
              }
              ${
                state.commerceMessage
                  ? `<p class="status">${escapeHtml(state.commerceMessage)}</p>`
                  : ""
              }
              ${
                canManageCommerce
                  ? `<h3>${state.locale === "ar" ? "المبالغ المستردة" : "Refunds"}</h3>
                    ${
                      state.donationRefunds.length === 0
                        ? `<p class="meta">${
                            state.locale === "ar" ? "لا توجد مبالغ مستردة." : "No refunds."
                          }</p>`
                        : `<ul class="request-list">
                          ${state.donationRefunds
                            .map((refund) => {
                              const createdAt = refund.createdAt
                                ? refund.createdAt.toLocaleString(state.locale)
                                : "-";
                              return `<li>
	                                <span>${formatAmount(refund.amountMinor)}${renderTechnicalId(
                                    refund.originalTransactionId,
                                    "Transaction ID",
                                  )}${renderTechnicalId(refund.personId, "Person ID")}${
                                    refund.providerRefundId
                                      ? renderTechnicalId(
                                          refund.providerRefundId,
                                          "Provider refund",
                                        )
                                      : `<small>Provider refund pending</small>`
                                  }</span>
                                <span class="status-pill ${commerceRecordStatusClass(
                                  refund.status,
                                )}">${escapeHtml(refund.status)}<br><small>${createdAt}</small></span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }
                    <h3>${state.locale === "ar" ? "المطابقة" : "Reconciliation"}</h3>
	                    <button class="secondary-action" id="run-commerce-reconciliation" type="button" ${
                        state.busy ? "disabled" : ""
                      }>Run reconciliation</button>
                    ${
                      state.reconciliationRuns.length === 0
                        ? `<p class="meta">${
                            state.locale === "ar"
                              ? "لا توجد عمليات مطابقة."
                              : "No reconciliation runs."
                          }</p>`
                        : `<ul class="request-list">
                          ${state.reconciliationRuns
                            .map((run) => {
                              const createdAt = run.createdAt
                                ? run.createdAt.toLocaleString(state.locale)
                                : "-";
                              return `<li>
	                                <span><strong>Reconciliation run</strong>${renderTechnicalId(
                                    run.id,
                                    "Run ID",
                                  )}<br><small>${
                                    run.checkedTransactionCount
                                  } transaction(s) · ${run.mismatchCount} mismatch(es)</small><br><small>${escapeHtml(
                                    run.targetTransactionId ?? "organization-wide",
                                  )} · ${escapeHtml(run.requestedBy)}</small></span>
                                <span class="status-pill ${commerceRecordStatusClass(
                                  run.status,
                                )}">${escapeHtml(run.status)}<br><small>${createdAt}</small></span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }
                    ${
                      state.reconciliationMismatches.length === 0
                        ? `<p class="meta">${
                            state.locale === "ar"
                              ? "لا توجد اختلافات مطابقة مفتوحة."
                              : "No reconciliation mismatches."
                          }</p>`
                        : `<ul class="request-list">
                          ${state.reconciliationMismatches
                            .map((mismatch) => {
                              const detectedAt = mismatch.detectedAt
                                ? mismatch.detectedAt.toLocaleString(state.locale)
                                : "-";
                              return `<li>
	                                <span>${escapeHtml(mismatch.type)}${
                                    mismatch.transactionId
                                      ? renderTechnicalId(mismatch.transactionId, "Transaction ID")
                                      : `<small>Provider-only mismatch</small>`
                                  }${
                                    mismatch.providerPaymentIntentId
                                      ? renderTechnicalId(
                                          mismatch.providerPaymentIntentId,
                                          "Payment intent",
                                        )
                                      : `<small>No payment intent</small>`
                                  }<br><small>${escapeHtml(
                                    mismatch.safeDiagnosticCode,
                                  )} · ${escapeHtml(mismatch.runId)}</small></span>
                                <span class="status-pill ${commerceRecordStatusClass(
                                  mismatch.status,
                                )}">${escapeHtml(mismatch.status)}<br><small>${detectedAt}</small></span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }`
                  : ""
              }
              ${
                canManageCommerce
                  ? `<h3>${state.locale === "ar" ? "صناديق العطاء" : "Giving funds"}</h3>
                    ${
                      state.givingFunds.length === 0
                        ? `<p class="meta">${
                            state.locale === "ar" ? "لا توجد صناديق عطاء." : "No giving funds."
                          }</p>`
                        : `<ul class="request-list">
                          ${state.givingFunds
                            .map((fund) => {
                              const updatedAt = fund.updatedAt
                                ? fund.updatedAt.toLocaleString(state.locale)
                                : "-";
                              return `<li>
                                <span>${escapeHtml(
                                  state.locale === "ar" && fund.nameAr ? fund.nameAr : fund.name,
                                )}<br><small>${escapeHtml(fund.type)} · ${escapeHtml(
                                  fund.visibility,
                                )} · ${formatAmount(fund.confirmedAmountMinor)} · ${
                                  fund.confirmedDonationCount
                                }</small></span>
                                <span class="status-pill ${
                                  fund.status === "active" ? "status-accepted" : "status-pending"
                                }">${escapeHtml(fund.status)}<br><small>${updatedAt}</small></span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }
                    <form id="giving-fund-form" class="editor-form">
                      <div class="editor-grid">
                        <label>
                          Fund ID
                          <input name="fundId" maxlength="100" value="custom-fund" />
                        </label>
                        <label>
                          Type
                          <select name="type">
                            <option value="general">general</option>
                            <option value="zakat">zakat</option>
                            <option value="sadaqah">sadaqah</option>
                            <option value="custom" selected>custom</option>
                          </select>
                        </label>
                      </div>
                      <div class="editor-grid">
                        <label>
                          Status
                          <select name="status">
                            <option value="draft">draft</option>
                            <option value="active">active</option>
                            <option value="archived">archived</option>
                          </select>
                        </label>
                        <label>
                          Visibility
                          <select name="visibility">
                            <option value="connected">connected</option>
                            <option value="private">private</option>
                          </select>
                        </label>
                      </div>
                      <label>
                        Name
                        <input name="name" maxlength="140" required value="Custom Fund" />
                      </label>
                      <label>
                        Arabic name
                        <input name="nameAr" maxlength="140" value="صندوق مخصص" />
                      </label>
                      <label>
                        Description
                        <textarea name="description" maxlength="1200" required>Community giving fund.</textarea>
                      </label>
                      <label>
                        Arabic description
                        <textarea name="descriptionAr" maxlength="1200">صندوق عطاء مجتمعي.</textarea>
                      </label>
                      <label>
                        Display order
                        <input name="displayOrder" min="0" max="10000" type="number" value="50" />
                      </label>
                      <div class="editor-actions">
                        <button type="submit" ${state.busy ? "disabled" : ""}>${
                          state.locale === "ar" ? "حفظ الصندوق" : "Save fund"
                        }</button>
                      </div>
                    </form>
                    <h3>${state.locale === "ar" ? "الحملات" : "Campaigns"}</h3>
                    ${
                      state.campaigns.length === 0
                        ? `<p class="meta">${
                            state.locale === "ar" ? "لا توجد حملات." : "No campaigns."
                          }</p>`
                        : `<ul class="request-list">
                          ${state.campaigns
                            .map((campaign) => {
                              const updatedAt = campaign.updatedAt
                                ? campaign.updatedAt.toLocaleString(state.locale)
                                : "-";
                              const goal =
                                campaign.goalAmountMinor == null
                                  ? "no goal"
                                  : formatAmount(campaign.goalAmountMinor);
                              return `<li>
                                <span>${escapeHtml(
                                  state.locale === "ar" && campaign.titleAr
                                    ? campaign.titleAr
                                    : campaign.title,
                                )}<br><small>${escapeHtml(
                                  campaign.fundId ?? "general",
                                )} · ${formatAmount(
                                  campaign.confirmedAmountMinor,
                                )}/${goal} · ${campaign.confirmedDonationCount}</small></span>
                                <span class="status-pill ${
                                  campaign.status === "published"
                                    ? "status-accepted"
                                    : "status-pending"
                                }">${escapeHtml(campaign.status)}<br><small>${updatedAt}</small></span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }
                    <form id="campaign-form" class="editor-form">
                      <div class="editor-grid">
                        <label>
                          Campaign ID
                          <input name="campaignId" maxlength="100" value="campaign" />
                        </label>
                        <label>
                          Fund
                          <select name="fundId">
                            <option value="">General donation</option>
                            ${state.givingFunds
                              .map(
                                (fund) =>
                                  `<option value="${escapeHtml(fund.fundId)}">${escapeHtml(
                                    fund.name,
                                  )}</option>`,
                              )
                              .join("")}
                          </select>
                        </label>
                      </div>
                      <div class="editor-grid">
                        <label>
                          Status
                          <select name="status">
                            <option value="draft">draft</option>
                            <option value="published">published</option>
                            <option value="completed">completed</option>
                            <option value="archived">archived</option>
                          </select>
                        </label>
                        <label>
                          Visibility
                          <select name="visibility">
                            <option value="connected">connected</option>
                            <option value="private">private</option>
                          </select>
                        </label>
                      </div>
                      <label>
                        Title
                        <input name="title" maxlength="140" required value="Campaign" />
                      </label>
                      <label>
                        Arabic title
                        <input name="titleAr" maxlength="140" value="حملة" />
                      </label>
                      <label>
                        Description
                        <textarea name="description" maxlength="2400" required>Campaign description.</textarea>
                      </label>
                      <label>
                        Arabic description
                        <textarea name="descriptionAr" maxlength="2400">وصف الحملة.</textarea>
                      </label>
                      <div class="editor-grid">
                        <label>
                          Goal USD
                          <input name="goalUsd" min="0" step="1" type="number" value="50000" />
                        </label>
                        <label>
                          Display order
                          <input name="displayOrder" min="0" max="10000" type="number" value="50" />
                        </label>
                      </div>
                      <div class="editor-grid">
                        <label>
                          Starts at
                          <input name="startsAt" type="datetime-local" />
                        </label>
                        <label>
                          Ends at
                          <input name="endsAt" type="datetime-local" />
                        </label>
                      </div>
                      <div class="editor-actions">
                        <button type="submit" ${state.busy ? "disabled" : ""}>${
                          state.locale === "ar" ? "حفظ الحملة" : "Save campaign"
                        }</button>
                      </div>
                    </form>`
                  : ""
              }
              <p class="meta">${copy.stripeConnectNoPayments}</p>
            </article>
            <article id="section-billing" class="panel">
              <h2>Billing</h2>
              ${
                state.billingError
                  ? `<p class="meta error">${escapeHtml(state.billingError)}</p>`
                  : ""
              }
              ${
                !selectedContextCanReadBilling()
                  ? `<p class="meta">Plan, trial, founding, entitlement, and usage state is visible to organization admins and finance admins.</p>`
                  : `<p class="status">${
                      currentPlan
                        ? `${escapeHtml(currentPlan.displayName)} · ${formatAmount(
                            currentPlan.priceMinor,
                          )}/${escapeHtml(currentPlan.billingInterval)}`
                        : "No plan assigned"
                    }</p>
                    <ul class="request-list">
                      <li>
                        <span>Subscription status</span>
                        <span>${escapeHtml(organizationSubscription?.status ?? "-")} · ${escapeHtml(
                          organizationSubscription?.billingState ?? "-",
                        )}</span>
                      </li>
                      <li>
                        <span>Trial</span>
                        <span>${escapeHtml(organizationSubscription?.trialStatus ?? "-")} · ends ${
                          organizationSubscription?.trialEndsAt
                            ? organizationSubscription.trialEndsAt.toLocaleDateString(state.locale)
                            : "-"
                        }</span>
                      </li>
                      <li>
                        <span>Founding 100</span>
                        <span>${
                          foundingSlot
                            ? `slot ${foundingSlot.slotNumber} · ${escapeHtml(
                                foundingSlot.status,
                              )} · ${foundingSlot.discountBasisPoints / 100}%`
                            : escapeHtml(organizationSubscription?.foundingStatus ?? "none")
                        }</span>
                      </li>
                      <li>
                        <span>Effective subscription discount</span>
                        <span>${
                          (organizationSubscription?.effectiveSubscriptionDiscountBasisPoints ??
                            0) / 100
                        }%</span>
                      </li>
                      <li>
                        <span>Storage</span>
                        <span>${
                          usage
                            ? `${formatBytes(usage.storageBytesUsed)} / ${formatBytes(
                                usage.storageBytesLimit,
                              )} · ${escapeHtml(usage.storageStatus)}`
                            : "-"
                        }</span>
                      </li>
                      <li>
                        <span>Cloud AI</span>
                        <span>${
                          usage
                            ? `${usage.cloudAiMonthlyActionsUsed} / ${usage.cloudAiMonthlyActionsLimit}`
                            : "-"
                        }</span>
                      </li>
                      <li>
                        <span>Locations</span>
                        <span>${
                          usage
                            ? `${usage.organizationLocationsUsed} / ${usage.organizationLocationsLimit}`
                            : (currentPlan?.organizationLocations ?? "-")
                        }</span>
                      </li>
                    </ul>
                    <h3>Entitlements</h3>
                    <ul class="request-list compact-list">
                      ${entitlementRows
                        .map(
                          (entry) => `<li>
                            <span>${escapeHtml(entry.entitlementId)}<br><small>${escapeHtml(
                              entry.source,
                            )} · ${escapeHtml(entry.mode)}</small></span>
                            <span class="status-pill ${
                              entry.status === "enabled" ? "status-accepted" : "status-pending"
                            }">${escapeHtml(entry.status)}</span>
                          </li>`,
                        )
                        .join("")}
                    </ul>`
              }
              <p class="meta">Server-owned records. Organization admins cannot self-assign plans, trials, founding slots, discounts, entitlements, usage limits, or platform fees.</p>
            </article>
            ${renderModeWorkspace({
              locale: state.locale,
              modes: state.operationalModes,
              editor: state.operationalModeEditor,
              original: operationalModeOriginal,
              busy: operationalModeBusy,
              loading: operationalModesLoading,
              loadError: operationalModesLoadError,
              error: state.operationalModeError,
              message: state.operationalModeMessage,
              canManage: selectedContextCanManageOrganizationSettings(),
              projection: state.operationalModeProjection,
              pendingAction: operationalModePendingAction,
              discardPending: operationalModeDiscardPending,
            })}
            <article id="section-ramadan" class="panel">
              <div class="ramadan-ops-header">
                <div>
                  <p class="page-kicker">Ramadan operations</p>
                  <h2>${copy.ramadanOperations}</h2>
                  <p class="meta">Ramadan setup, mode status, Iftar sponsorship, attendance, and operating history.</p>
                </div>
                <div class="ramadan-state-strip" aria-label="Ramadan operating state">
                  <span class="status-pill ${ramadanOperationStateClass}">${ramadanOperationStateLabel}</span>
                  ${
                    currentRamadanSetup
                      ? `<span class="status-pill ${ramadanSetupLifecycleClass}">${escapeHtml(
                          currentRamadanSetup.status,
                        )}</span>`
                      : `<span class="status-pill status-rejected">Setup missing</span>`
                  }
                </div>
              </div>
              ${
                state.ramadanSetupMessage
                  ? `<p class="status">${escapeHtml(state.ramadanSetupMessage)}</p>`
                  : ""
              }
              ${
                state.ramadanSetupEditConfirmationPending
                  ? `<div class="ramadan-active-edit-confirmation" role="alert">
                    <div>
                      <strong>Ramadan is active</strong>
                      <p class="meta">Confirm before reopening setup. Changes can affect member-facing Ramadan dates, capacity, prayer overrides, and mobile display.</p>
                    </div>
                    <div class="button-row">
                      <button class="danger-action inline-action" type="button" data-ramadan-setup-edit-confirm="confirm">Open setup</button>
                      <button class="secondary-action inline-action" type="button" data-ramadan-setup-edit-confirm="cancel">Cancel</button>
                    </div>
                  </div>`
                  : ""
              }
              <div class="ramadan-command-links" aria-label="Ramadan workspace shortcuts">
                <button class="secondary-action inline-action" type="button" data-task-window-open="ramadan-setup-editor-form">Ramadan setup</button>
                <button class="secondary-action inline-action" type="button" data-open-manual-iftar-sponsorship>Add sponsor</button>
                <a href="#ramadan-history-panel">History</a>
                <a href="#ramadan-admin-notes-panel">Notes</a>
              </div>
              <div class="ramadan-metric-grid" aria-label="Ramadan operating summary">
                <section>
                  <span>Mode</span>
                  <strong>${ramadanOperationStateLabel}</strong>
                  <small>${
                    linkedRamadanOperationalModeActive
                      ? "Mobile Home is prioritizing Ramadan."
                      : "Ramadan priority is not active on mobile."
                  }</small>
                </section>
                <section>
                  <span>Season</span>
                  <strong>${escapeHtml(currentRamadanSetup?.title ?? "Not configured")}</strong>
                  <small>${
                    currentRamadanSetup
                      ? `${escapeHtml(currentRamadanSetup.startsOn)} to ${escapeHtml(
                          currentRamadanSetup.endsOn,
                        )}${
                          currentRamadanSetup.ramadanDay
                            ? ` · day ${currentRamadanSetup.ramadanDay}`
                            : ""
                        }`
                      : `No Ramadan setup for ${escapeHtml(organization.displayName)}.`
                  }</small>
                </section>
                <section>
                  <span>Sponsorship</span>
                  <strong>${totalIftarSponsorshipAvailable} available spot(s)</strong>
                  <small>${totalIftarSponsorshipConfirmed} confirmed · ${totalIftarSponsorshipReserved} reserved of ${totalIftarSponsorshipCapacity}</small>
                </section>
              </div>
              <div class="ramadan-chart-grid" aria-label="Ramadan capacity graphs">
                ${renderRamadanCapacityBar({
                  available: totalIftarSponsorshipAvailable,
                  confirmed: totalIftarSponsorshipConfirmed,
                  label: "Iftar sponsorship spots",
                  reserved: totalIftarSponsorshipReserved,
                  total: totalIftarSponsorshipCapacity,
                })}
              </div>
              <div class="ramadan-current-setup">
                ${
                  currentRamadanSetup
                    ? `<div>
                        <strong>${escapeHtml(currentRamadanSetup.title)}</strong>
                        <p class="meta">${escapeHtml(currentRamadanSetup.startsOn)} to ${escapeHtml(
                          currentRamadanSetup.endsOn,
                        )}${
                          currentRamadanSetup.ramadanDay
                            ? ` · Ramadan day ${currentRamadanSetup.ramadanDay}`
                            : ""
                        }</p>
                        <p class="technical-id">Setup <code>${escapeHtml(
                          currentRamadanSetup.id,
                        )}</code><span>Mode</span><code>${escapeHtml(
                          currentRamadanSetup.linkedOperationalModeId,
                        )}</code></p>
                      </div>`
                    : `<div>
                        <strong>No Ramadan setup</strong>
                        <p class="meta">Save a setup before activating Ramadan mode or generating attendance days.</p>
                      </div>`
                }
                <div class="ramadan-inline-actions">
                  ${
                    state.ramadanSetups.length === 0
                      ? ""
                      : state.ramadanSetups
                          .slice(0, 3)
                          .map(
                            (setup) => `<div class="ramadan-setup-action-row">
                              <span>
                                <strong>${escapeHtml(setup.title)}</strong>
                                <small>${escapeHtml(setup.startsOn)} to ${escapeHtml(
                                  setup.endsOn,
                                )}</small>
                              </span>
                              <span class="status-pill ${
                                setup.status === "active" || setup.status === "ready"
                                  ? "status-accepted"
                                  : "status-pending"
                              }">${escapeHtml(setup.status)}</span>
                              <div class="button-row">
                                <button class="secondary-action inline-action" type="button" data-ramadan-setup-action="ready" data-ramadan-setup-id="${escapeHtml(
                                  setup.id,
                                )}" ${state.busy ? "disabled" : ""}>Mark ready</button>
                                <button class="primary-action inline-action" type="button" data-ramadan-setup-action="activate" data-ramadan-setup-id="${escapeHtml(
                                  setup.id,
                                )}" ${
                                  state.busy || setup.status === "active" ? "disabled" : ""
                                }>Turn on</button>
                                <button class="danger-action inline-action" type="button" data-ramadan-setup-action="deactivate" data-ramadan-setup-id="${escapeHtml(
                                  setup.id,
                                )}" ${
                                  state.busy || setup.status !== "active" ? "disabled" : ""
                                }>Turn off</button>
                              </div>
                            </div>`,
                          )
                          .join("")
                  }
                </div>
              </div>
              <section id="ramadan-history-panel" class="ramadan-history-panel">
                <div class="ramadan-subsection-heading">
                  <div>
                    <h3>Ramadan history</h3>
                    <p class="meta">Completed Ramadan years are saved here when Ramadan mode is turned off.</p>
                  </div>
                </div>
                ${
                  state.ramadanHistory.length === 0
                    ? `<p class="meta">No completed Ramadan history yet.</p>`
                    : `<div class="table-scroll" role="region" aria-label="Ramadan history">
                      <table class="data-table compact-table">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Dates</th>
                            <th>Days</th>
                            <th>Status</th>
                            <th>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${state.ramadanHistory
                            .slice(0, 8)
                            .map(
                              (history) => `<tr>
                                <td><strong>${escapeHtml(history.title)}</strong><small>${history.hijriYear} AH</small></td>
                                <td>${escapeHtml(history.startsOn)} to ${escapeHtml(history.endsOn)}</td>
                                <td>${history.dayCount}</td>
                                <td><span class="status-pill ${commerceRecordStatusClass(
                                  history.status,
                                )}">${escapeHtml(history.status)}</span></td>
                                <td>${history.completedAt ? history.completedAt.toLocaleString(state.locale) : "-"}</td>
                              </tr>`,
                            )
                            .join("")}
                        </tbody>
                      </table>
                    </div>`
                }
              </section>
              ${
                activeRamadanMode
                  ? `<div class="ramadan-mode-summary">
                    <div>
                      <strong>${
                        state.locale === "ar"
                          ? escapeHtml(activeRamadanMode.titleAr || activeRamadanMode.title)
                          : escapeHtml(activeRamadanMode.title)
                      }</strong>
                      <p class="meta">${
                        state.locale === "ar"
                          ? escapeHtml(activeRamadanMode.summaryAr)
                          : escapeHtml(activeRamadanMode.summary)
                      }</p>
                    </div>
                    <span class="status-pill status-accepted">${copy.availableSlots}: ${
                      totalIftarSponsorshipAvailable
                    } · ${totalIftarSponsorshipCapacity} total</span>
                  </div>`
                  : `<p class="meta">No active Ramadan mode for ${escapeHtml(
                      organization.displayName,
                    )}.</p>`
              }
              <p class="technical-id">Organization scope <code>${escapeHtml(
                state.organizationId,
              )}</code></p>
            </article>
            <article id="ramadan-setup-panel" class="panel editor-panel task-only-panel">
              <h2>Ramadan setup</h2>
              <form id="ramadan-setup-editor-form" class="editor-form" data-task-window-launch="manual">
                <input data-ramadan-setup-editor-field="defaultCampusId" type="hidden" value="${escapeHtml(
                  state.ramadanSetupEditor.defaultCampusId,
                )}" />
                <input data-ramadan-setup-editor-field="linkedOperationalModeId" type="hidden" value="${escapeHtml(
                  state.ramadanSetupEditor.linkedOperationalModeId,
                )}" />
                <div class="editor-grid">
                  <label>
                    Hijri year
                    <input data-ramadan-setup-editor-field="hijriYear" type="number" min="1300" max="1600" value="${escapeHtml(
                      state.ramadanSetupEditor.hijriYear,
                    )}" />
                  </label>
                  <label>
                    Status
                    <select data-ramadan-setup-editor-field="status">
                      <option value="draft" ${
                        state.ramadanSetupEditor.status === "draft" ? "selected" : ""
                      }>draft</option>
                      <option value="ready" ${
                        state.ramadanSetupEditor.status === "ready" ? "selected" : ""
                      }>ready</option>
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Starts on
                    <input data-ramadan-setup-editor-field="startsOn" type="date" value="${escapeHtml(
                      state.ramadanSetupEditor.startsOn,
                    )}" />
                  </label>
                  <label>
                    Ends on
                    <input data-ramadan-setup-editor-field="endsOn" type="date" value="${escapeHtml(
                      state.ramadanSetupEditor.endsOn,
                    )}" />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Experience opens
                    <input data-ramadan-setup-editor-field="experienceOpensAt" type="datetime-local" value="${escapeHtml(
                      state.ramadanSetupEditor.experienceOpensAt,
                    )}" />
                  </label>
                  <label>
                    Sponsorship opens
                    <input data-ramadan-setup-editor-field="sponsorshipOpensAt" type="datetime-local" value="${escapeHtml(
                      state.ramadanSetupEditor.sponsorshipOpensAt,
                    )}" />
                  </label>
                  <label>
                    Organization timezone
                    <input data-ramadan-setup-editor-field="timezone" value="${escapeHtml(
                      state.ramadanSetupEditor.timezone,
                    )}" />
                  </label>
                </div>
                <div class="editor-actions compact-actions">
                  <button id="preview-ramadan-dates" class="secondary-action" type="button" ${
                    state.busy ? "disabled" : ""
                  }>Use AlAdhan Ramadan dates</button>
                  <p class="meta">Uses the current organization prayer address, method, school, and timezone.</p>
                </div>
                <div class="editor-grid">
                  <label>
                    Start adjustment days
                    <input data-ramadan-setup-editor-field="startAdjustmentDays" type="number" min="-3" max="3" value="${escapeHtml(
                      state.ramadanSetupEditor.startAdjustmentDays,
                    )}" />
                  </label>
                  <label>
                    End adjustment days
                    <input data-ramadan-setup-editor-field="endAdjustmentDays" type="number" min="-3" max="3" value="${escapeHtml(
                      state.ramadanSetupEditor.endAdjustmentDays,
                    )}" />
                  </label>
                </div>
                <label>
                  Mobile Ramadan card title
                  <input data-ramadan-setup-editor-field="title" maxlength="140" value="${escapeHtml(
                    state.ramadanSetupEditor.title,
                  )}" />
                </label>
                <label>
                  Arabic title
                  <input data-ramadan-setup-editor-field="titleAr" maxlength="140" dir="rtl" value="${escapeHtml(
                    state.ramadanSetupEditor.titleAr,
                  )}" />
                </label>
                <label>
                  Mobile Ramadan card message
                  <textarea data-ramadan-setup-editor-field="message" maxlength="500">${escapeHtml(
                    state.ramadanSetupEditor.message,
                  )}</textarea>
                </label>
                <label>
                  Arabic message
                  <textarea data-ramadan-setup-editor-field="messageAr" maxlength="500" dir="rtl">${escapeHtml(
                    state.ramadanSetupEditor.messageAr,
                  )}</textarea>
                </label>
                ${renderRamadanFeaturedContentEditor()}
                <div class="editor-grid">
                  <label>
                    Default Fajr iqama
                    <input data-ramadan-setup-editor-field="fajrIqama" type="time" value="${escapeHtml(
                      state.ramadanSetupEditor.fajrIqama,
                    )}" />
                  </label>
                  <label>
                    Default Maghrib iqama
                    <input data-ramadan-setup-editor-field="maghribIqama" type="time" value="${escapeHtml(
                      state.ramadanSetupEditor.maghribIqama,
                    )}" />
                  </label>
                  <label>
                    Default Isha iqama
                    <input data-ramadan-setup-editor-field="ishaIqama" type="time" value="${escapeHtml(
                      state.ramadanSetupEditor.ishaIqama,
                    )}" />
                  </label>
                </div>
                <p class="meta">AlAdhan provides the Gregorian Ramadan dates and prayer-time provider data; these are organization iqama overrides.</p>
                <section class="ramadan-editor-section">
                  <h3>Iqama phases</h3>
                  <p class="meta">Set the member-facing Ramadan iqama overrides for days 1-10, 11-20, and 21 through the end.</p>
                  <div class="ramadan-phase-grid">
                    ${renderRamadanIqamaPhaseEditor("First 10 days", "phase1")}
                    ${renderRamadanIqamaPhaseEditor("Second 10 days", "phase2")}
                    ${renderRamadanIqamaPhaseEditor("Last 10 days", "phase3")}
                  </div>
                </section>
                <div class="editor-grid">
                  <label>
                    Taraweeh rakah
                    <input data-ramadan-setup-editor-field="taraweehRakahCount" type="number" min="0" max="40" value="${escapeHtml(
                      state.ramadanSetupEditor.taraweehRakahCount,
                    )}" />
                  </label>
                  <label>
                    Taraweeh minutes after Isha
                    <input data-ramadan-setup-editor-field="taraweehRelativeToIshaMinutes" type="number" min="0" max="180" value="${escapeHtml(
                      state.ramadanSetupEditor.taraweehRelativeToIshaMinutes,
                    )}" />
                  </label>
                </div>
                <label>
                  Last ten starts on
                  <input data-ramadan-setup-editor-field="lastTenStartsOn" type="date" value="${escapeHtml(
                    state.ramadanSetupEditor.lastTenStartsOn,
                  )}" />
                </label>
                <section class="ramadan-editor-section">
                  <h3>Tahajjud nights</h3>
                  <p class="meta">Enter up to 10 rows as YYYY-MM-DD HH:mm optional note.</p>
                  <textarea data-ramadan-setup-editor-field="tahajjudSchedule" rows="6">${escapeHtml(
                    state.ramadanSetupEditor.tahajjudSchedule,
                  )}</textarea>
                </section>
                <section class="ramadan-editor-section">
                  <h3>Guest imams</h3>
                  <div class="editor-grid">
                    <label>
                      Mobile display
                      <select data-ramadan-setup-editor-field="guestImamsEnabled">
                        <option value="true" ${
                          state.ramadanSetupEditor.guestImamsEnabled === "true" ? "selected" : ""
                        }>Enabled</option>
                        <option value="false" ${
                          state.ramadanSetupEditor.guestImamsEnabled === "false" ? "selected" : ""
                        }>Off</option>
                      </select>
                    </label>
                  </div>
                  <p class="meta">One imam per row: Name | Title | Bio | Photo URL</p>
                  <textarea data-ramadan-setup-editor-field="guestImamProfiles" rows="5">${escapeHtml(
                    state.ramadanSetupEditor.guestImamProfiles,
                  )}</textarea>
                </section>
                ${renderRamadanSetupSponsorshipDefaultsEditor()}
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>Save Ramadan setup</button>
                  <button id="reset-ramadan-setup-form" type="button">Reset setup</button>
                  <button id="generate-iftar-sponsorship-days" class="secondary-action" type="button" ${
                    canBuildSponsorshipCalendarFromSetup ? "" : "disabled"
                  }>Build sponsorship calendar</button>
                </div>
                <p class="meta">Build the sponsorship calendar after saving setup/defaults. Sponsorship rebuild overwrites day configuration while preserving committed sponsor counters.</p>
              </form>
              <p class="meta">Setup saves through server functions and links to the Ramadan operational mode.</p>
            </article>
            <article id="iftar-day-configuration-panel" class="panel editor-panel">
              <h2>Iftar Sponsorship Day Configuration</h2>
              ${
                state.iftarSponsorshipMessage
                  ? `<p class="status">${escapeHtml(state.iftarSponsorshipMessage)}</p>`
                  : ""
              }
              <p class="meta">Configuration only: no sponsor reservations, payment holds, checkout, sponsor records, or fulfillment history are created here.</p>
              <h3>Defaults</h3>
              ${
                iftarSponsorshipDefaults
                  ? `<ul class="request-list compact-list">
                    <li>
                      <span>Program</span>
                      <span class="status-pill ${
                        iftarSponsorshipDefaults.programEnabled
                          ? "status-accepted"
                          : "status-pending"
                      }">${iftarSponsorshipDefaults.programEnabled ? "enabled" : "disabled"}</span>
                    </li>
                    <li>
                      <span>Capacity</span>
                      <span>${escapeHtml(
                        renderIftarCapacityWeekdaySummary(
                          iftarSponsorshipDefaults.capacityByWeekday,
                        ),
                      )}</span>
                    </li>
                    <li>
                      <span>Price and deadline</span>
                      <span>${formatAmount(
                        iftarSponsorshipDefaults.defaultPricePerSpotMinor,
                      )} ${escapeHtml(
                        iftarSponsorshipDefaults.currency.toUpperCase(),
                      )} · day ${iftarSponsorshipDefaults.sponsorshipClosesDayOffset} at ${escapeHtml(
                        iftarSponsorshipDefaults.sponsorshipClosesLocalTime,
                      )}</span>
                    </li>
                  </ul>`
                  : `<p class="meta">${workspacePageLabel(state.locale, "No saved sponsorship defaults.", "لا توجد إعدادات رعاية محفوظة.")}</p>`
              }
	              <div class="ramadan-subsection-heading">
	                <div>
	                  <h3>Daily Calendar</h3>
	                  <p class="meta">${openIftarSponsorshipDays.length} open of ${
                      state.iftarSponsorshipDays.length
                    } configured sponsorship day(s).</p>
	                </div>
	                <div class="button-row">
	                  <button class="secondary-action action-with-icon" id="export-iftar-sponsorship-calendar" type="button" ${
                      state.busy || state.iftarSponsorshipDays.length === 0 ? "disabled" : ""
                    }>${renderEventActionIcon("download")} Export sponsors</button>
	                </div>
	              </div>
              ${
                state.iftarSponsorshipDays.length === 0
                  ? `<p class="meta">Save Ramadan setup, then build one Iftar Day config per Ramadan date.</p>`
                  : `<div class="table-scroll ramadan-calendar-scroll" role="region" aria-label="Iftar sponsorship daily calendar">
                    <table class="data-table ramadan-sponsorship-calendar-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Capacity</th>
                          <th>Price</th>
                          <th>Sponsors</th>
                          <th>Notes</th>
                          <th scope="col" data-table-no-sort="true">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                    ${state.iftarSponsorshipDays
                      .slice(0, 35)
                      .map((day) => {
                        const availableSpots = availableIftarSponsorshipSpots(day);
                        const noteCount = ramadanAdminNoteCountByIftarDay.get(day.id) ?? 0;
                        const daySponsors = sponsorsByIftarDay.get(day.id) ?? [];
                        const sponsorMarkup =
                          daySponsors.length === 0
                            ? `<span class="meta">No sponsors yet</span>`
                            : `<div class="ramadan-sponsor-chip-list">
                              ${daySponsors
                                .map((sponsorship) => {
                                  const bookedAt = sponsorship.createdAt
                                    ? sponsorship.createdAt.toLocaleString(state.locale)
                                    : "-";
                                  return `<div class="ramadan-sponsor-chip" data-iftar-transfer-row>
                                    <span><strong>${escapeHtml(
                                      sponsorship.publicDisplayNameSnapshot ||
                                        sponsorship.safeSponsorLabel,
                                    )}</strong><small>${sponsorship.quantitySpots} spot(s) · ${escapeHtml(
                                      sponsorship.status,
                                    )}</small><small>Booked ${escapeHtml(bookedAt)}</small></span>
                                  </div>`;
                                })
                                .join("")}
                            </div>`;
                        const closeNoteEditor =
                          state.iftarSponsorshipClosingDayId === day.id
                            ? `<tr class="ramadan-close-note-row">
                                <td colspan="7">
                                  <label>
                                    Close note
                                    <textarea id="iftar-sponsorship-close-note" maxlength="240">${escapeHtml(
                                      state.iftarSponsorshipCloseNote,
                                    )}</textarea>
                                  </label>
                                  <div class="button-row">
                                    <button class="danger-action inline-action" type="button" data-iftar-sponsorship-day-close-confirm="${escapeHtml(
                                      day.id,
                                    )}" ${state.busy ? "disabled" : ""}>Close day</button>
                                    <button class="secondary-action inline-action" type="button" data-iftar-sponsorship-day-close-cancel>Cancel</button>
                                  </div>
                                </td>
                              </tr>`
                            : "";
                        return `<tr>
                          <td>
                            <strong>${escapeHtml(titleCaseWeekday(day.weekday))} · Ramadan day ${
                              day.ramadanDay
                            }</strong>
                            <small>${escapeHtml(day.gregorianDate)}</small>
                            <span class="technical-id">Day <code>${escapeHtml(day.id)}</code></span>
                          </td>
                          <td>
                            <span class="status-pill ${commerceRecordStatusClass(day.status)}">${
                              day.sponsorshipEnabled ? escapeHtml(day.status) : "disabled"
                            }</span>
                          </td>
                          <td>
                            <strong>${availableSpots} available</strong>
                            <small>${day.capacitySpots} configured · ${day.reservedSpots} reserved · ${day.confirmedSpots} confirmed</small>
                          </td>
                          <td>
                            <strong>${formatAmount(day.pricePerSpotMinor)}</strong>
                            <small>${escapeHtml(day.currency.toUpperCase())}</small>
                          </td>
                          <td>${sponsorMarkup}</td>
                          <td>
                            ${
                              day.publicNotes
                                ? `<strong>${escapeHtml(day.publicNotes)}</strong>`
                                : `<strong>${noteCount}</strong>`
                            }
                            <small>${day.publicNotes ? `${noteCount} internal note(s)` : "internal note(s)"}</small>
                          </td>
                          <td>
                            <div class="table-actions">
                              <button class="secondary-action inline-action icon-action" type="button" data-iftar-sponsorship-day-view="${escapeHtml(
                                day.id,
                              )}" data-tooltip="View day" title="View day" ${
                                state.busy ? "disabled" : ""
                              }>${renderEventActionIcon("read")}</button>
                              <button class="secondary-action inline-action icon-action" type="button" data-iftar-sponsorship-day-edit="${escapeHtml(
                                day.id,
                              )}" data-tooltip="Edit day" title="Edit day" ${
                                state.busy ? "disabled" : ""
                              }>${renderEventActionIcon("edit")}</button>
                              <button class="primary-action inline-action icon-action" type="button" data-iftar-sponsorship-day-action="open" data-iftar-sponsorship-day-id="${escapeHtml(
                                day.id,
                              )}" data-tooltip="Open day" title="Open day" ${
                                state.busy || day.status === "open" ? "disabled" : ""
                              }>${renderEventActionIcon("open")}</button>
                              <button class="danger-action inline-action icon-action" type="button" data-iftar-sponsorship-day-action="close" data-iftar-sponsorship-day-id="${escapeHtml(
                                day.id,
                              )}" data-tooltip="Close day" title="Close day" ${
                                state.busy || day.status === "closed" ? "disabled" : ""
                              }>${renderEventActionIcon("disable")}</button>
                            </div>
                          </td>
                        </tr>${closeNoteEditor}`;
                      })
                      .join("")}
                      </tbody>
                    </table>
                  </div>`
              }
              <h3 class="task-only-panel">${
                state.iftarSponsorshipDayEditorMode === "view" ? "Day details" : "Day override"
              }</h3>
              <form id="iftar-sponsorship-day-form" class="editor-form" data-task-window-launch="manual">
                <div class="editor-grid">
                  <label>
                    Iftar Day ID
                    <input data-iftar-sponsorship-day-field="iftarDayId" maxlength="120" value="${escapeHtml(
                      state.iftarSponsorshipDayEditor.iftarDayId,
                    )}" ${iftarSponsorshipDayEditorDisabled} />
                  </label>
                  <label>
                    Gregorian date
                    <input data-iftar-sponsorship-day-field="gregorianDate" type="date" value="${escapeHtml(
                      state.iftarSponsorshipDayEditor.gregorianDate,
                    )}" ${iftarSponsorshipDayEditorDisabled} />
                  </label>
                  <label>
                    Status
                    <select data-iftar-sponsorship-day-field="status" ${iftarSponsorshipDayEditorDisabled}>
                      ${["draft", "open", "closed"]
                        .map(
                          (status) =>
                            `<option value="${status}" ${
                              state.iftarSponsorshipDayEditor.status === status ? "selected" : ""
                            }>${status}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Sponsorship enabled
                    <select data-iftar-sponsorship-day-field="sponsorshipEnabled" ${iftarSponsorshipDayEditorDisabled}>
                      <option value="true" ${
                        state.iftarSponsorshipDayEditor.sponsorshipEnabled === "true"
                          ? "selected"
                          : ""
                      }>true</option>
                      <option value="false" ${
                        state.iftarSponsorshipDayEditor.sponsorshipEnabled === "false"
                          ? "selected"
                          : ""
                      }>false</option>
                    </select>
                  </label>
                  <label>
                    Capacity
                    <input data-iftar-sponsorship-day-field="capacitySpots" type="number" min="0" max="1000" value="${escapeHtml(
                      state.iftarSponsorshipDayEditor.capacitySpots,
                    )}" ${iftarSponsorshipDayEditorDisabled} />
                  </label>
                  <label>
                    Price USD
                    <input data-iftar-sponsorship-day-field="priceDollars" type="number" min="0" max="100000" step="0.01" value="${escapeHtml(
                      state.iftarSponsorshipDayEditor.priceDollars,
                    )}" ${iftarSponsorshipDayEditorDisabled} />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Deadline
                    <input data-iftar-sponsorship-day-field="sponsorshipClosesAt" type="datetime-local" value="${escapeHtml(
                      state.iftarSponsorshipDayEditor.sponsorshipClosesAt,
                    )}" ${iftarSponsorshipDayEditorDisabled} />
                  </label>
                </div>
                <label>
                  Mobile day message
                  <textarea data-iftar-sponsorship-day-field="publicNotes" maxlength="240" ${iftarSponsorshipDayEditorDisabled}>${escapeHtml(
                    state.iftarSponsorshipDayEditor.publicNotes,
                  )}</textarea>
                </label>
                <label>
                  Arabic mobile day message
                  <textarea data-iftar-sponsorship-day-field="publicNotesAr" maxlength="240" dir="rtl" ${iftarSponsorshipDayEditorDisabled}>${escapeHtml(
                    state.iftarSponsorshipDayEditor.publicNotesAr,
                  )}</textarea>
                </label>
                <div class="editor-actions">
                  ${
                    state.iftarSponsorshipDayEditorMode === "view"
                      ? `<button class="secondary-action" type="button" data-iftar-sponsorship-day-edit="${escapeHtml(
                          state.iftarSponsorshipDayEditor.iftarDayId,
                        )}" ${state.busy ? "disabled" : ""}>Edit day</button>`
                      : `<button type="submit" ${state.busy ? "disabled" : ""}>Save day config</button>
                        <button id="reset-iftar-sponsorship-day-form" type="button">Reset day</button>`
                  }
                  <button class="secondary-action" type="button" data-open-manual-iftar-sponsorship="${escapeHtml(
                    state.iftarSponsorshipDayEditor.iftarDayId,
                  )}" ${state.busy || !state.iftarSponsorshipDayEditor.iftarDayId.trim() ? "disabled" : ""}>Add sponsor</button>
                </div>
                <section class="ramadan-day-sponsors-panel">
                  <div class="ramadan-subsection-heading">
                    <div>
                      <h3>Reservations and sponsors</h3>
                      <p class="meta">${
                        selectedIftarSponsorshipDay
                          ? `${selectedIftarSponsorshipDaySponsors.length} reserved or confirmed record(s) for ${escapeHtml(
                              selectedIftarSponsorshipDay.gregorianDate,
                            )}.`
                          : "Select a Ramadan day to view reservations and sponsors."
                      }</p>
                    </div>
                  </div>
                  ${
                    !selectedIftarSponsorshipDay
                      ? `<p class="meta">Select a day from the calendar to manage its reserved and confirmed spots.</p>`
                      : selectedIftarSponsorshipDaySponsors.length === 0
                        ? `<p class="meta">No reserved or confirmed spots are attached to this day yet.</p>`
                        : `<div class="table-scroll" role="region" aria-label="Reservations and sponsors for selected Iftar day">
                          <table class="data-table compact-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Booked</th>
                                <th>Spots</th>
                                <th>Status</th>
                                <th>Payment</th>
                                <th>Cost</th>
                                <th>Spot details</th>
                                <th scope="col" data-table-no-sort="true">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${selectedIftarSponsorshipDaySponsors
                                .map((sponsorship) => {
                                  const bookedAt = sponsorship.createdAt
                                    ? sponsorship.createdAt.toLocaleString(state.locale)
                                    : "-";
                                  return `<tr>
                                    <td>
                                      <strong>${escapeHtml(
                                        sponsorship.publicDisplayNameSnapshot ||
                                          sponsorship.safeSponsorLabel,
                                      )}</strong>
                                      <small>${escapeHtml(sponsorship.safeSponsorLabel)}</small>
                                    </td>
                                    <td>${escapeHtml(bookedAt)}</td>
                                    <td>${sponsorship.quantitySpots}</td>
                                    <td><span class="status-pill ${commerceRecordStatusClass(
                                      sponsorship.status,
                                    )}">${escapeHtml(sponsorship.status)}</span></td>
                                    <td>
                                      <strong>${escapeHtml(sponsorship.paymentStatus)}</strong>
                                      <small>${escapeHtml(
                                        iftarSponsorshipPaymentSummary(sponsorship),
                                      )}</small>
                                    </td>
                                    <td>${escapeHtml(iftarSponsorshipCostSummary(sponsorship))}</td>
                                    <td>${escapeHtml(iftarSponsorshipSpotSummary(sponsorship))}</td>
                                    <td>${renderIftarSponsorManagementActions(
                                      sponsorship,
                                      selectedIftarSponsorshipDay.id,
                                    )}</td>
                                  </tr>`;
                                })
                                .join("")}
                            </tbody>
                          </table>
                        </div>`
                  }
                </section>
              </form>
              <p class="meta">Open days become visible to connected members only after the parent Ramadan setup is active.</p>
            </article>
            <article id="ramadan-admin-notes-panel" class="panel editor-panel">
              <h2>${copy.ramadanAdminNotes}</h2>
              ${
                state.ramadanAdminNoteMessage
                  ? `<p class="status">${escapeHtml(state.ramadanAdminNoteMessage)}</p>`
                  : ""
              }
              ${
                !canManageRamadanAdminNotes
                  ? `<p class="meta">Owner or admin access required.</p>`
                  : `
                    <div class="editor-form compact-form">
                      <label>
                        Status
                        <select id="ramadan-admin-note-status-filter">
                          ${(["active", "archived", "all"] as const)
                            .map(
                              (status) =>
                                `<option value="${status}" ${
                                  state.ramadanAdminNoteStatusFilter === status ? "selected" : ""
                                }>${status}</option>`,
                            )
                            .join("")}
                        </select>
                      </label>
                      <label>
                        Category
                        <select id="ramadan-admin-note-category-filter">
                          <option value="all" ${
                            state.ramadanAdminNoteCategoryFilter === "all" ? "selected" : ""
                          }>all</option>
                          ${ramadanAdminNoteCategories
                            .map(
                              (category) =>
                                `<option value="${escapeHtml(category)}" ${
                                  state.ramadanAdminNoteCategoryFilter === category
                                    ? "selected"
                                    : ""
                                }>${escapeHtml(category)}</option>`,
                            )
                            .join("")}
                        </select>
                      </label>
                      <label>
                        Iftar day
                        <select id="ramadan-admin-note-day-filter">
                          <option value="" ${
                            state.ramadanAdminNoteDayFilter === "" ? "selected" : ""
                          }>all days</option>
                          <option value="ramadan" ${
                            state.ramadanAdminNoteDayFilter === "ramadan" ? "selected" : ""
                          }>Ramadan-level</option>
                          ${state.iftarSponsorshipDays
                            .map(
                              (day) =>
                                `<option value="${escapeHtml(day.id)}" ${
                                  state.ramadanAdminNoteDayFilter === day.id ? "selected" : ""
                                }>${escapeHtml(day.gregorianDate)} · day ${day.ramadanDay}</option>`,
                            )
                            .join("")}
                        </select>
                      </label>
                    </div>
                    <form id="ramadan-admin-note-form" class="editor-form">
                      <div class="editor-grid">
                        <label>
                          Note ID
                          <input data-ramadan-admin-note-field="noteId" maxlength="120" readonly value="${escapeHtml(
                            state.ramadanAdminNoteEditor.noteId,
                          )}" />
                        </label>
                        <label>
                          Ramadan ID
                          <input data-ramadan-admin-note-field="ramadanId" maxlength="120" value="${escapeHtml(
                            state.ramadanAdminNoteEditor.ramadanId,
                          )}" />
                        </label>
                        <label>
                          Category
                          <select data-ramadan-admin-note-field="category">
                            ${ramadanAdminNoteCategories
                              .map(
                                (category) =>
                                  `<option value="${escapeHtml(category)}" ${
                                    state.ramadanAdminNoteEditor.category === category
                                      ? "selected"
                                      : ""
                                  }>${escapeHtml(category)}</option>`,
                              )
                              .join("")}
                          </select>
                        </label>
                      </div>
                      <div class="editor-grid">
                        <label>
                          Iftar day
                          <select data-ramadan-admin-note-field="iftarDayId">
                            <option value="" ${
                              state.ramadanAdminNoteEditor.iftarDayId === "" ? "selected" : ""
                            }>Ramadan-level</option>
                            ${state.iftarSponsorshipDays
                              .map(
                                (day) =>
                                  `<option value="${escapeHtml(day.id)}" ${
                                    state.ramadanAdminNoteEditor.iftarDayId === day.id
                                      ? "selected"
                                      : ""
                                  }>${escapeHtml(day.gregorianDate)} · day ${
                                    day.ramadanDay
                                  }</option>`,
                              )
                              .join("")}
                          </select>
                        </label>
                        <label>
                          Sponsorship
                          <select data-ramadan-admin-note-field="sponsorshipId">
                            <option value="" ${
                              state.ramadanAdminNoteEditor.sponsorshipId === "" ? "selected" : ""
                            }>No sponsorship</option>
                            ${ramadanAdminNoteSponsorshipOptions
                              .map(
                                (sponsorship) =>
                                  `<option value="${escapeHtml(sponsorship.sponsorshipId)}" ${
                                    state.ramadanAdminNoteEditor.sponsorshipId ===
                                    sponsorship.sponsorshipId
                                      ? "selected"
                                      : ""
                                  }>${escapeHtml(sponsorship.safeSponsorLabel)} · ${escapeHtml(
                                    sponsorship.iftarDayId,
                                  )}</option>`,
                              )
                              .join("")}
                          </select>
                        </label>
                      </div>
                      <label>
                        Note
                        <textarea data-ramadan-admin-note-field="body" maxlength="2000" required>${escapeHtml(
                          state.ramadanAdminNoteEditor.body,
                        )}</textarea>
                      </label>
                      <div class="editor-actions">
                        <button type="submit" ${state.busy ? "disabled" : ""}>${
                          copy.saveRamadanAdminNote
                        }</button>
                        <button id="reset-ramadan-admin-note-form" type="button">${
                          copy.resetRamadanAdminNoteForm
                        }</button>
                      </div>
                    </form>
                    ${
                      filteredRamadanAdminNotes.length === 0
                        ? `<p class="meta">${workspacePageLabel(state.locale, "No Ramadan admin notes.", "لا توجد ملاحظات إدارية لرمضان.")}</p>`
                        : `<ul class="request-list">
                          ${filteredRamadanAdminNotes
                            .slice(0, 20)
                            .map((note) => {
                              const updatedAt = note.updatedAt
                                ? note.updatedAt.toLocaleString(state.locale)
                                : "-";
                              const createdAt = note.createdAt
                                ? note.createdAt.toLocaleString(state.locale)
                                : "-";
                              const day = note.iftarDayId
                                ? state.iftarSponsorshipDays.find(
                                    (candidate) => candidate.id === note.iftarDayId,
                                  )
                                : undefined;
                              const sponsorship = note.sponsorshipId
                                ? state.sponsorships.find(
                                    (candidate) => candidate.sponsorshipId === note.sponsorshipId,
                                  )
                                : undefined;
                              const association = note.iftarDayId
                                ? `${day?.gregorianDate ?? note.iftarDayId} · ${note.iftarDayId}`
                                : "Ramadan-level";
                              const sponsorLabel = note.sponsorshipId
                                ? (sponsorship?.safeSponsorLabel ?? note.sponsorshipId)
                                : "No sponsorship";
                              const bodyPreview =
                                note.body.length > 360
                                  ? `${note.body.slice(0, 357)}...`
                                  : note.body;
                              return `<li>
                                <span>${escapeHtml(bodyPreview)}<br><small>${escapeHtml(
                                  note.category,
                                )} · ${escapeHtml(association)} · ${escapeHtml(
                                  sponsorLabel,
                                )}</small><br><small>created ${escapeHtml(
                                  createdAt,
                                )} by ${escapeHtml(note.createdBy)} · updated ${escapeHtml(
                                  updatedAt,
                                )} by ${escapeHtml(note.updatedBy)} · edit ${note.editCount}</small></span>
                                <span class="button-row">
                                  <span class="status-pill ${commerceRecordStatusClass(
                                    note.status,
                                  )}">${escapeHtml(note.status)}</span>
                                  <button type="button" data-ramadan-admin-note-edit="${escapeHtml(
                                    note.noteId,
                                  )}" ${state.busy ? "disabled" : ""}>Edit</button>
                                  <button type="button" data-ramadan-admin-note-archive="${escapeHtml(
                                    note.noteId,
                                  )}" ${
                                    state.busy || note.status === "archived" ? "disabled" : ""
                                  }>Archive</button>
                                </span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }
                  `
              }
            </article>
            <article class="panel editor-panel task-only-panel" data-workspace-panel-id="ramadan-mode-editor">
              <h2>${copy.ramadanModeEditor}</h2>
              <form id="ramadan-mode-editor-form" class="editor-form" data-task-window-launch="manual">
                <input
                  data-ramadan-mode-editor-field="modeId"
                  name="modeId"
                  type="hidden"
                  value="${escapeHtml(state.ramadanModeEditor.modeId)}"
                />
                <input
                  data-ramadan-mode-editor-field="title"
                  name="title"
                  type="hidden"
                  value="${escapeHtml(state.ramadanModeEditor.title)}"
                />
                <input
                  data-ramadan-mode-editor-field="titleAr"
                  name="titleAr"
                  type="hidden"
                  value="${escapeHtml(state.ramadanModeEditor.titleAr)}"
                />
                <input
                  data-ramadan-mode-editor-field="summary"
                  name="summary"
                  type="hidden"
                  value="${escapeHtml(state.ramadanModeEditor.summary)}"
                />
                <input
                  data-ramadan-mode-editor-field="summaryAr"
                  name="summaryAr"
                  type="hidden"
                  value="${escapeHtml(state.ramadanModeEditor.summaryAr)}"
                />
                <label>
                  ${copy.ramadanSlotPriceUsd}
                  <input
                    data-ramadan-mode-editor-field="slotPriceUsd"
                    name="slotPriceUsd"
                    min="0.01"
                    max="1000"
                    step="0.01"
                    type="number"
                    required
                    value="${escapeHtml(state.ramadanModeEditor.slotPriceUsd)}"
                  />
                </label>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.saveRamadanMode
                  }</button>
                  <button id="reset-ramadan-mode-form" type="button">${
                    copy.resetRamadanModeForm
                  }</button>
                </div>
              </form>
            </article>
            <article class="panel editor-panel task-only-panel" data-workspace-panel-id="iftar-slot-editor">
              <h2>${copy.iftarSlotEditor}</h2>
              <form id="iftar-slot-editor-form" class="editor-form" data-task-window-launch="manual">
                <div class="editor-grid">
                  <label>
                    ${copy.iftarSlotId}
                    <input
                      data-iftar-slot-editor-field="slotId"
                      name="slotId"
                      maxlength="80"
                      value="${escapeHtml(state.iftarSlotEditor.slotId)}"
                    />
                  </label>
                  <label>
                    ${copy.iftarModeId}
                    <input
                      data-iftar-slot-editor-field="modeId"
                      name="modeId"
                      maxlength="80"
                      required
                      value="${escapeHtml(state.iftarSlotEditor.modeId)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.iftarDayNumber}
                    <input
                      data-iftar-slot-editor-field="dayNumber"
                      name="dayNumber"
                      min="1"
                      max="30"
                      step="1"
                      type="number"
                      required
                      value="${escapeHtml(state.iftarSlotEditor.dayNumber)}"
                    />
                  </label>
                  <label>
                    ${copy.iftarAmountUsd}
                    <input
                      data-iftar-slot-editor-field="amountUsd"
                      name="amountUsd"
                      min="0.01"
                      max="1000"
                      step="0.01"
                      type="number"
                      required
                      value="${escapeHtml(state.iftarSlotEditor.amountUsd)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.iftarSlotLabel}
                  <input
                    data-iftar-slot-editor-field="label"
                    name="label"
                    maxlength="120"
                    required
                    value="${escapeHtml(state.iftarSlotEditor.label)}"
                  />
                </label>
                <label>
                  ${copy.iftarSlotLabelAr}
                  <input
                    data-iftar-slot-editor-field="labelAr"
                    name="labelAr"
                    maxlength="120"
                    value="${escapeHtml(state.iftarSlotEditor.labelAr)}"
                  />
                </label>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.saveIftarSlot
                  }</button>
                  <button id="reset-iftar-slot-form" type="button">${
                    copy.resetIftarSlotForm
                  }</button>
                </div>
              </form>
            </article>
            <article id="ramadan-sponsor-administration-panel" class="panel">
              <h2>${copy.iftarSponsorships}</h2>
              <div class="editor-form compact-form">
                <label>
                  Iftar day
                  <select id="iftar-sponsorship-day-filter">
                    <option value="" ${state.iftarSponsorshipDayFilter === "" ? "selected" : ""}>All days</option>
                    ${state.iftarSponsorshipDays
                      .map(
                        (day) =>
                          `<option value="${escapeHtml(day.id)}" ${
                            state.iftarSponsorshipDayFilter === day.id ? "selected" : ""
                          }>${escapeHtml(day.gregorianDate)} · day ${day.ramadanDay}</option>`,
                      )
                      .join("")}
                  </select>
                </label>
                <label>
                  Status
                  <select id="iftar-sponsorship-status-filter">
                    ${[
                      "all",
                      "paymentPending",
                      "processing",
                      "confirmed",
                      "paymentFailed",
                      "expired",
                      "cancelled",
                      "latePaymentException",
                    ]
                      .map(
                        (status) =>
                          `<option value="${status}" ${
                            state.iftarSponsorshipStatusFilter === status ? "selected" : ""
                          }>${status}</option>`,
                      )
                      .join("")}
                  </select>
                </label>
                <label>
                  Search
                  <input id="iftar-sponsorship-search" maxlength="120" value="${escapeHtml(
                    state.iftarSponsorshipSearch,
                  )}" />
                </label>
              </div>
              ${
                filteredIftarSponsorships.length === 0
                  ? `<p class="meta">No Iftar sponsorship records for ${organization.displayName}.</p>`
                  : `<ul class="request-list ramadan-sponsorship-record-list">
                    ${filteredIftarSponsorships
                      .slice(0, 12)
                      .map((sponsorship) => {
                        const day = state.iftarSponsorshipDays.find(
                          (candidate) => candidate.id === sponsorship.iftarDayId,
                        );
                        const capacity = day
                          ? `${day.confirmedSpots} confirmed · ${day.reservedSpots} reserved · ${Math.max(
                              day.capacitySpots - day.confirmedSpots - day.reservedSpots,
                              0,
                            )} available`
                          : "capacity unavailable";
                        const createdAt = sponsorship.createdAt
                          ? sponsorship.createdAt.toLocaleString(state.locale)
                          : "-";
                        const finance = iftarSponsorshipFinanceById.get(sponsorship.sponsorshipId);
                        const noteCount =
                          ramadanAdminNoteCountBySponsorship.get(sponsorship.sponsorshipId) ?? 0;
                        return `<li>
                          <span>
                            ${escapeHtml(sponsorship.safeSponsorLabel)} · ${sponsorship.quantitySpots} spot(s)<br>
                            <small>${escapeHtml(sponsorship.gregorianDate)} · Ramadan day ${
                              sponsorship.ramadanDay
                            } · ${escapeHtml(capacity)}</small><br>
                            <small>${escapeHtml(sponsorship.recognitionPreference)} · ${escapeHtml(
                              iftarSponsorshipPaymentSummary(sponsorship),
                            )} · ${noteCount} internal note(s)</small><br>
                            <small>${escapeHtml(iftarSponsorshipCostSummary(sponsorship))}</small><br>
                            <small>${escapeHtml(iftarSponsorshipSpotSummary(sponsorship))}</small>
                            ${
                              sponsorship.dedicationText
                                ? `<br><small>${escapeHtml(sponsorship.dedicationText)}</small>`
                                : ""
                            }
                            ${
                              selectedContextCanReadFinance() && finance
                                ? `<br><small>${formatAmount(finance.amountMinor)} · ${escapeHtml(
                                    finance.status,
                                  )} · refund ${escapeHtml(finance.refundStatus)} · receipt ${escapeHtml(
                                    finance.receiptId ?? "-",
                                  )}</small>`
                                : ""
                            }
                          </span>
                          ${renderIftarSponsorManagementActions(sponsorship, sponsorship.iftarDayId)}
                          <span class="status-pill ${
                            sponsorship.status === "confirmed"
                              ? "status-accepted"
                              : "status-pending"
                          }">${escapeHtml(sponsorship.status)}<br><small>${createdAt}</small></span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
              <p class="meta">Capacity is read from approved Iftar Day counters. Operational rows omit provider, receipt, and transaction identifiers.</p>
              ${
                selectedContextCanCreateManualIftarSponsorship()
                  ? `<form id="manual-iftar-sponsorship-form" class="editor-form">
                    <label>
                      Iftar day
                      <select data-manual-iftar-sponsorship-field="iftarDayId">
                        ${
                          manualIftarSponsorDayOptions.length === 0
                            ? `<option value="">No open days with available spots</option>`
                            : manualIftarSponsorDayOptions
                                .map(
                                  (day) =>
                                    `<option value="${escapeHtml(day.id)}" ${
                                      state.iftarSponsorshipManualEditor.iftarDayId === day.id
                                        ? "selected"
                                        : ""
                                    }>Day ${day.ramadanDay} · ${escapeHtml(
                                      day.gregorianDate,
                                    )} · ${availableIftarSponsorshipSpots(day)} available</option>`,
                                )
                                .join("")
                        }
                      </select>
                    </label>
                    <label>
                      Connected person
                      <select data-manual-iftar-sponsorship-field="personId">
                        ${
                          manualIftarSponsorPeople.length === 0
                            ? `<option value="">No active connected people</option>`
                            : manualIftarSponsorPeople
                                .map(
                                  (row, index) =>
                                    `<option value="${escapeHtml(row.connection.personId)}" ${
                                      state.iftarSponsorshipManualEditor.personId ===
                                      row.connection.personId
                                        ? "selected"
                                        : ""
                                    }>${escapeHtml(
                                      peopleRowManualSponsorLabel(row, index),
                                    )}</option>`,
                                )
                                .join("")
                        }
                      </select>
                    </label>
                    <label>
                      Sponsor label
                      <input data-manual-iftar-sponsorship-field="sponsorLabel" maxlength="120" value="${escapeHtml(
                        state.iftarSponsorshipManualEditor.sponsorLabel,
                      )}" />
                    </label>
                    <label>
                      Spots
                      <input data-manual-iftar-sponsorship-field="quantitySpots" type="number" min="1" max="${Math.max(
                        manualIftarSponsorAvailableSpots,
                        1,
                      )}" value="${escapeHtml(state.iftarSponsorshipManualEditor.quantitySpots)}" ${
                        manualIftarSponsorAvailableSpots <= 0 ||
                        manualIftarSponsorPeople.length === 0
                          ? "disabled"
                          : ""
                      } />
                      <small>${manualIftarSponsorAvailableSpots} spot(s) available on the selected day.</small>
                    </label>
                    <label>
                      Method
                      <select data-manual-iftar-sponsorship-field="manualPaymentMethod">
                        ${["cash", "check", "external", "officeCommitment"]
                          .map(
                            (method) =>
                              `<option value="${method}" ${
                                state.iftarSponsorshipManualEditor.manualPaymentMethod === method
                                  ? "selected"
                                  : ""
                              }>${method}</option>`,
                          )
                          .join("")}
                      </select>
                    </label>
                    <label>
                      Recognition
                      <select data-manual-iftar-sponsorship-field="recognitionPreference">
                        ${["publicName", "anonymous", "none"]
                          .map(
                            (preference) =>
                              `<option value="${preference}" ${
                                state.iftarSponsorshipManualEditor.recognitionPreference ===
                                preference
                                  ? "selected"
                                  : ""
                              }>${preference}</option>`,
                          )
                          .join("")}
                      </select>
                    </label>
                    <label>
                      Dedication
                      <textarea data-manual-iftar-sponsorship-field="dedicationText" maxlength="280">${escapeHtml(
                        state.iftarSponsorshipManualEditor.dedicationText,
                      )}</textarea>
                    </label>
                    <div class="editor-actions">
                      <button type="submit" ${
                        state.busy ||
                        manualIftarSponsorAvailableSpots <= 0 ||
                        manualIftarSponsorPeople.length === 0
                          ? "disabled"
                          : ""
                      }>Add manual sponsorship</button>
                    </div>
                  </form>`
                  : `<p class="meta">Manual sponsorship creation requires organization owner or admin authority.</p>`
              }
            </article>
            <article id="section-spaces" class="panel">
              <h2>${copy.spaces}</h2>
              ${
                state.spaces.length === 0
                  ? `<p class="meta">No reservable spaces for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.spaces
                      .map((space) => {
                        const name =
                          state.locale === "ar" ? (space.nameAr ?? space.name) : space.name;
                        const description =
                          state.locale === "ar"
                            ? (space.descriptionAr ?? space.description)
                            : space.description;
                        const details = [
                          space.type,
                          space.visibility,
                          space.campusId ?? "no campus",
                          `capacity ${space.capacity}`,
                          space.timezone,
                        ].join(" · ");
                        const amenities =
                          space.amenities.length > 0 ? space.amenities.join(", ") : "no amenities";
                        const accessibility =
                          space.accessibilityFeatures.length > 0
                            ? space.accessibilityFeatures.join(", ")
                            : "accessibility not set";
                        const pricingSummary = space.pricingEnabled
                          ? `${space.pricingType} v${space.pricingVersion} · ${formatAmount(
                              space.pricingTotalPreviewCents ?? 0,
                            )} preview`
                          : "pricing disabled";
                        return `<li>
                          <span>${escapeHtml(name)}<br><small>${escapeHtml(
                            description,
                          )}</small><br><small>${escapeHtml(
                            space.locationName,
                          )} · ${escapeHtml(details)}</small><br><small>${escapeHtml(
                            amenities,
                          )}</small><br><small>${escapeHtml(
                            accessibility,
                          )}</small><br><small>${escapeHtml(
                            compactSpaceHours(space.operatingHours),
                          )}</small><br><small>${escapeHtml(pricingSummary)}</small>${
                            space.primaryImageStoragePath
                              ? `<br><small>${escapeHtml(space.primaryImageStoragePath)}</small>`
                              : ""
                          }</span>
                          <span class="status-pill status-accepted">${escapeHtml(
                            space.status,
                          )} · ${escapeHtml(formatOptionalAmount(space.hourlyPriceCents))}</span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
              <p class="meta">${copy.availableSpaceSlots}: ${availableSpaceSlots.length}</p>
            </article>
            <article class="panel editor-panel" data-workspace-panel-id="space-editor">
              <h2>${copy.spaceEditor}</h2>
              <form id="space-editor-form" class="editor-form">
                <label>
                  ${copy.spaceId}
                  <input
                    data-space-editor-field="spaceId"
                    maxlength="80"
                    value="${escapeHtml(state.spaceEditor.spaceId)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceType}
                    <select data-space-editor-field="type">
                      ${spaceEditorTypeOptions
                        .map(
                          (type) =>
                            `<option value="${escapeHtml(type)}" ${
                              state.spaceEditor.type === type ? "selected" : ""
                            }>${escapeHtml(type)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    ${copy.spaceStatus}
                    <select data-space-editor-field="status">
                      ${spaceEditorStatusOptions
                        .map(
                          (status) =>
                            `<option value="${escapeHtml(status)}" ${
                              state.spaceEditor.status === status ? "selected" : ""
                            }>${escapeHtml(status)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    ${copy.spaceVisibility}
                    <select data-space-editor-field="visibility">
                      ${spaceEditorVisibilityOptions
                        .map(
                          (visibility) =>
                            `<option value="${escapeHtml(visibility)}" ${
                              state.spaceEditor.visibility === visibility ? "selected" : ""
                            }>${escapeHtml(visibility)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceCampus}
                    <select data-space-editor-field="campusId">
                      <option value="">${copy.spaceNoCampus}</option>
                      ${organizationCampuses
                        .map(
                          (campus) =>
                            `<option value="${escapeHtml(campus.campusId)}" ${
                              state.spaceEditor.campusId === campus.campusId ? "selected" : ""
                            }>${escapeHtml(campus.name)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    ${copy.spaceTimezone}
                    <input
                      data-space-editor-field="timezone"
                      maxlength="80"
                      required
                      value="${escapeHtml(state.spaceEditor.timezone)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceName}
                    <input
                      data-space-editor-field="name"
                      maxlength="120"
                      required
                      value="${escapeHtml(state.spaceEditor.name)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceNameAr}
                    <input
                      data-space-editor-field="nameAr"
                      maxlength="120"
                      value="${escapeHtml(state.spaceEditor.nameAr)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.spaceDescription}
                  <textarea
                    data-space-editor-field="description"
                    maxlength="800"
                    required
                  >${escapeHtml(state.spaceEditor.description)}</textarea>
                </label>
                <label>
                  ${copy.spaceDescriptionAr}
                  <textarea
                    data-space-editor-field="descriptionAr"
                    maxlength="800"
                  >${escapeHtml(state.spaceEditor.descriptionAr)}</textarea>
                </label>
                <label>
                  ${copy.spaceLocation}
                  <input
                    data-space-editor-field="locationName"
                    maxlength="160"
                    required
                    value="${escapeHtml(state.spaceEditor.locationName)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceCapacity}
                    <input
                      data-space-editor-field="capacity"
                      min="1"
                      max="1000"
                      step="1"
                      type="number"
                      required
                      value="${escapeHtml(state.spaceEditor.capacity)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceHourlyPriceUsd}
                    <input
                      data-space-editor-field="hourlyPriceUsd"
                      min="0.01"
                      max="1000"
                      step="0.01"
                      type="number"
                      value="${escapeHtml(state.spaceEditor.hourlyPriceUsd)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAmenities}
                    <input
                      data-space-editor-field="amenities"
                      value="${escapeHtml(state.spaceEditor.amenities)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceAccessibilityFeatures}
                    <input
                      data-space-editor-field="accessibilityFeatures"
                      value="${escapeHtml(state.spaceEditor.accessibilityFeatures)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAccessibilityNotes}
                    <textarea
                      data-space-editor-field="accessibilityNotes"
                      maxlength="300"
                    >${escapeHtml(state.spaceEditor.accessibilityNotes)}</textarea>
                  </label>
                  <label>
                    ${copy.spaceAccessibilityNotesAr}
                    <textarea
                      data-space-editor-field="accessibilityNotesAr"
                      maxlength="300"
                    >${escapeHtml(state.spaceEditor.accessibilityNotesAr)}</textarea>
                  </label>
                </div>
                <label>
                  ${copy.spacePrimaryImageStoragePath}
                  <input
                    data-space-editor-field="primaryImageStoragePath"
                    maxlength="260"
                    value="${escapeHtml(state.spaceEditor.primaryImageStoragePath)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.spacePrimaryImageAltText}
                    <input
                      data-space-editor-field="primaryImageAltText"
                      maxlength="160"
                      value="${escapeHtml(state.spaceEditor.primaryImageAltText)}"
                    />
                  </label>
                  <label>
                    ${copy.spacePrimaryImageAltTextAr}
                    <input
                      data-space-editor-field="primaryImageAltTextAr"
                      maxlength="160"
                      value="${escapeHtml(state.spaceEditor.primaryImageAltTextAr)}"
                    />
                  </label>
                </div>
                <fieldset class="editor-fieldset">
                  <legend>${copy.spaceOperatingHours}</legend>
                  <div class="editor-grid">
                    <label>
                      ${copy.spaceMondayHours}
                      <input
                        data-space-editor-field="mondayHours"
                        value="${escapeHtml(state.spaceEditor.mondayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceTuesdayHours}
                      <input
                        data-space-editor-field="tuesdayHours"
                        value="${escapeHtml(state.spaceEditor.tuesdayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceWednesdayHours}
                      <input
                        data-space-editor-field="wednesdayHours"
                        value="${escapeHtml(state.spaceEditor.wednesdayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceThursdayHours}
                      <input
                        data-space-editor-field="thursdayHours"
                        value="${escapeHtml(state.spaceEditor.thursdayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceFridayHours}
                      <input
                        data-space-editor-field="fridayHours"
                        value="${escapeHtml(state.spaceEditor.fridayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceSaturdayHours}
                      <input
                        data-space-editor-field="saturdayHours"
                        value="${escapeHtml(state.spaceEditor.saturdayHours)}"
                      />
                    </label>
                    <label>
                      ${copy.spaceSundayHours}
                      <input
                        data-space-editor-field="sundayHours"
                        value="${escapeHtml(state.spaceEditor.sundayHours)}"
                      />
                    </label>
                  </div>
                </fieldset>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${copy.saveSpace}</button>
                  <button id="reset-space-form" type="button">${copy.resetSpaceForm}</button>
                </div>
              </form>
            </article>
            <article class="panel" data-workspace-panel-id="space-availability-blocks">
              <h2>${copy.spaceAvailabilityBlocks}</h2>
              ${
                state.spaceAvailabilityBlocks.length === 0
                  ? `<p class="meta">No availability blocks for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.spaceAvailabilityBlocks
                      .slice(0, 6)
                      .map((block) => {
                        const label =
                          state.locale === "ar" ? (block.labelAr ?? block.label) : block.label;
                        const startsAt = block.startsAt
                          ? block.startsAt.toLocaleString(state.locale)
                          : "-";
                        const endsAt = block.endsAt
                          ? block.endsAt.toLocaleString(state.locale)
                          : "-";
                        return `<li>
                          <span>${escapeHtml(label)}<br><small>${escapeHtml(
                            block.spaceId,
                          )} · ${escapeHtml(startsAt)} to ${escapeHtml(endsAt)}</small>${
                            block.internalReason
                              ? `<br><small>${escapeHtml(block.internalReason)}</small>`
                              : ""
                          }</span>
                          <span class="status-pill status-pending">${escapeHtml(
                            block.type,
                          )} · ${block.publicReason ? "public" : "private"}</span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article class="panel" data-workspace-panel-id="pending-space-reservation-requests">
              <h2>${copy.pendingSpaceReservationRequests}</h2>
              ${
                pendingSpaceReservationRequests.length === 0
                  ? `<p class="meta">No pending canonical space requests for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${pendingSpaceReservationRequests
                      .slice(0, 8)
                      .map((reservation) => {
                        const startsAt = reservation.startsAt
                          ? reservation.startsAt.toLocaleString(state.locale)
                          : "-";
                        const endsAt = reservation.endsAt
                          ? reservation.endsAt.toLocaleString(state.locale)
                          : "-";
                        const requester = [
                          reservation.requesterName,
                          reservation.requesterEmail,
                          reservation.requesterPersonId,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return `<li>
                          <span>${escapeHtml(reservation.purpose)}<br><small>${escapeHtml(
                            reservation.spaceId,
                          )} · ${escapeHtml(startsAt)} to ${escapeHtml(
                            endsAt,
                          )}</small><br><small>${escapeHtml(
                            requester,
                          )}</small><br><small>Guests: ${reservation.guestCount}${
                            reservation.notes ? ` · ${escapeHtml(reservation.notes)}` : ""
                          }</small></span>
                          <span class="status-pill status-pending">${escapeHtml(
                            reservation.status,
                          )}</span>
                          <span class="row-actions">
                            <button data-space-reservation-action="approve" data-space-reservation-id="${escapeHtml(
                              reservation.reservationId,
                            )}" data-space-id="${escapeHtml(reservation.spaceId)}" ${
                              state.busy ? "disabled" : ""
                            }>${copy.approveSpaceReservation}</button>
	                            <button class="danger-action inline-action" data-space-reservation-action="reject" data-space-reservation-id="${escapeHtml(
                                reservation.reservationId,
                              )}" data-space-id="${escapeHtml(reservation.spaceId)}" ${
                                state.busy ? "disabled" : ""
                              }>${copy.rejectSpaceReservation}</button>
                          </span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
              <p class="meta">${copy.canonicalSpaceReservations}: ${
                state.spaceReservationRequests.length
              } · pending requests do not block availability.</p>
            </article>
            <article class="panel" data-workspace-panel-id="approved-space-reservation-schedule">
              <h2>${copy.approvedSpaceReservationSchedule}</h2>
              ${
                approvedSpaceReservationRequests.length === 0
                  ? `<p class="meta">No approved canonical space reservations for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${approvedSpaceReservationRequests
                      .slice(0, 8)
                      .map((reservation) => {
                        const startsAt = reservation.startsAt
                          ? reservation.startsAt.toLocaleString(state.locale)
                          : "-";
                        const endsAt = reservation.endsAt
                          ? reservation.endsAt.toLocaleString(state.locale)
                          : "-";
                        const reviewed = reservation.reviewedAt
                          ? reservation.reviewedAt.toLocaleString(state.locale)
                          : "-";
                        const paymentSummary =
                          reservation.pricingTotalCents === null
                            ? "Payment not required"
                            : `${formatAmount(reservation.pricingTotalCents)} · ${
                                reservation.paymentStatus
                              } · agreement ${
                                reservation.agreementRequired
                                  ? reservation.agreementAccepted
                                    ? "accepted"
                                    : "pending"
                                  : "not required"
                              }`;
                        const financeSummary = selectedContextCanReadFinance()
                          ? ` · txn ${reservation.transactionId ?? "-"} · receipt ${
                              reservation.receiptId ?? "-"
                            }`
                          : "";
                        return `<li>
                          <span>${escapeHtml(reservation.purpose)}<br><small>${escapeHtml(
                            reservation.spaceId,
                          )} · ${escapeHtml(startsAt)} to ${escapeHtml(
                            endsAt,
                          )}</small><br><small>${escapeHtml(
                            reservation.requesterName,
                          )} · approved by ${escapeHtml(
                            reservation.reviewedBy ?? "-",
                          )} · ${escapeHtml(reviewed)}</small><br><small>${escapeHtml(
                            paymentSummary + financeSummary,
                          )}</small></span>
                          <span class="status-pill status-accepted">${escapeHtml(
                            reservation.status,
                          )}</span>
                          <span class="row-actions">
	                            <button class="danger-action inline-action" data-space-reservation-action="cancel" data-space-reservation-id="${escapeHtml(
                                reservation.reservationId,
                              )}" data-space-id="${escapeHtml(reservation.spaceId)}" ${
                                state.busy ? "disabled" : ""
                              }>${copy.cancelSpaceReservation}</button>
                          </span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
              <p class="meta">Approved reservations block availability through backend-owned conflict locks.</p>
            </article>
            <article class="panel editor-panel" data-workspace-panel-id="space-availability-block-editor">
              <h2>${copy.spaceAvailabilityBlockEditor}</h2>
              <form id="space-availability-block-editor-form" class="editor-form">
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAvailabilityBlockId}
                    <input
                      data-space-availability-block-editor-field="blockId"
                      maxlength="80"
                      value="${escapeHtml(state.spaceAvailabilityBlockEditor.blockId)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceSlotSpaceId}
                    <select data-space-availability-block-editor-field="spaceId">
                      ${state.spaces
                        .map(
                          (space) =>
                            `<option value="${escapeHtml(space.id)}" ${
                              state.spaceAvailabilityBlockEditor.spaceId === space.id
                                ? "selected"
                                : ""
                            }>${escapeHtml(space.name)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAvailabilityBlockType}
                    <select data-space-availability-block-editor-field="type">
                      ${spaceAvailabilityBlockTypeOptions
                        .map(
                          (type) =>
                            `<option value="${escapeHtml(type)}" ${
                              state.spaceAvailabilityBlockEditor.type === type ? "selected" : ""
                            }>${escapeHtml(type)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    ${copy.spaceAvailabilityBlockPublicReason}
                    <select data-space-availability-block-editor-field="publicReason">
                      <option value="false" ${
                        state.spaceAvailabilityBlockEditor.publicReason === "false"
                          ? "selected"
                          : ""
                      }>false</option>
                      <option value="true" ${
                        state.spaceAvailabilityBlockEditor.publicReason === "true" ? "selected" : ""
                      }>true</option>
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAvailabilityBlockStartsAt}
                    <input
                      data-space-availability-block-editor-field="startsAt"
                      type="datetime-local"
                      value="${escapeHtml(state.spaceAvailabilityBlockEditor.startsAt)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceAvailabilityBlockEndsAt}
                    <input
                      data-space-availability-block-editor-field="endsAt"
                      type="datetime-local"
                      value="${escapeHtml(state.spaceAvailabilityBlockEditor.endsAt)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceAvailabilityBlockLabel}
                    <input
                      data-space-availability-block-editor-field="label"
                      maxlength="120"
                      value="${escapeHtml(state.spaceAvailabilityBlockEditor.label)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceAvailabilityBlockLabelAr}
                    <input
                      data-space-availability-block-editor-field="labelAr"
                      maxlength="120"
                      value="${escapeHtml(state.spaceAvailabilityBlockEditor.labelAr)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.spaceAvailabilityBlockInternalReason}
                  <textarea
                    data-space-availability-block-editor-field="internalReason"
                    maxlength="500"
                  >${escapeHtml(state.spaceAvailabilityBlockEditor.internalReason)}</textarea>
                </label>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.saveSpaceAvailabilityBlock
                  }</button>
                  <button id="reset-space-availability-block-form" type="button">${
                    copy.resetSpaceAvailabilityBlockForm
                  }</button>
                </div>
              </form>
            </article>
            <article class="panel" data-workspace-panel-id="available-space-slots">
              <h2>${copy.availableSpaceSlots}</h2>
              ${
                state.spaceSlots.length === 0
                  ? `<p class="meta">No synthetic space slots for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.spaceSlots
                      .slice(0, 4)
                      .map((slot) => {
                        const label =
                          state.locale === "ar" ? slot.labelAr || slot.label : slot.label;
                        const start = slot.startsAt
                          ? slot.startsAt.toLocaleString(state.locale)
                          : "-";
                        return `<li>
                          <span>${label}<br><small>${start} · ${slot.requesterUid}</small></span>
                          <span class="status-pill ${
                            slot.status === "available" ? "status-pending" : "status-accepted"
                          }">${formatAmount(slot.amountCents)} · ${slot.status}</span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article class="panel editor-panel" data-workspace-panel-id="space-slot-editor">
              <h2>${copy.spaceSlotEditor}</h2>
              <form id="space-slot-editor-form" class="editor-form">
                <div class="editor-grid">
                  <label>
                    ${copy.spaceSlotId}
                    <input
                      data-space-slot-editor-field="slotId"
                      maxlength="80"
                      value="${escapeHtml(state.spaceSlotEditor.slotId)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceSlotSpaceId}
                    <input
                      data-space-slot-editor-field="spaceId"
                      maxlength="120"
                      required
                      value="${escapeHtml(state.spaceSlotEditor.spaceId)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.spaceSlotLabel}
                  <input
                    data-space-slot-editor-field="label"
                    maxlength="160"
                    required
                    value="${escapeHtml(state.spaceSlotEditor.label)}"
                  />
                </label>
                <label>
                  ${copy.spaceSlotLabelAr}
                  <input
                    data-space-slot-editor-field="labelAr"
                    maxlength="160"
                    value="${escapeHtml(state.spaceSlotEditor.labelAr)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    ${copy.spaceSlotStartsAt}
                    <input
                      data-space-slot-editor-field="startsAt"
                      required
                      type="datetime-local"
                      value="${escapeHtml(state.spaceSlotEditor.startsAt)}"
                    />
                  </label>
                  <label>
                    ${copy.spaceSlotEndsAt}
                    <input
                      data-space-slot-editor-field="endsAt"
                      required
                      type="datetime-local"
                      value="${escapeHtml(state.spaceSlotEditor.endsAt)}"
                    />
                  </label>
                </div>
                <label>
                  ${copy.spaceSlotAmountUsd}
                  <input
                    data-space-slot-editor-field="amountUsd"
                    min="0.01"
                    max="1000"
                    step="0.01"
                    type="number"
                    required
                    value="${escapeHtml(state.spaceSlotEditor.amountUsd)}"
                  />
                </label>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.saveSpaceSlot
                  }</button>
                  <button id="reset-space-slot-form" type="button">${
                    copy.resetSpaceSlotForm
                  }</button>
                </div>
              </form>
            </article>
            <article class="panel" data-workspace-panel-id="latest-space-reservations">
              <h2>${copy.latestReservation}</h2>
              ${
                state.spaceReservations.length === 0
                  ? `<p class="meta">No synthetic space reservations for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.spaceReservations
                      .slice(0, 4)
                      .map((reservation) => {
                        const createdAt = reservation.createdAt
                          ? reservation.createdAt.toLocaleString(state.locale)
                          : "-";
                        return `<li>
                          <span>${reservation.spaceId}<br><small>${reservation.requesterUid} · ${
                            reservation.providerEventId
                          }</small></span>
                          <span class="status-pill ${
                            reservation.status === "confirmed"
                              ? "status-accepted"
                              : "status-pending"
                          }">${reservation.status}<br><small>${formatAmount(
                            reservation.amountCents,
                          )} · ${createdAt}</small></span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article id="section-volunteers" class="panel">
              <h2>Volunteers</h2>
              ${
                selectedContextCanManageVolunteers()
                  ? `<p class="meta">${publishedVolunteerOpportunities.length} published opportunities · ${
                      openVolunteerShifts.length
                    } open shifts · ${confirmedVolunteerSignups.length} confirmed signups · ${
                      checkedInVolunteerAttendances.length
                    } checked in · ${(totalVolunteerVerifiedMinutes / 60).toFixed(
                      1,
                    )} verified hours</p>
                    ${
                      state.volunteerOpportunities.length === 0
                        ? `<p class="meta">No volunteer opportunities for ${organization.displayName}.</p>`
                        : `<ul class="request-list">
                          ${state.volunteerOpportunities
                            .map((opportunity) => {
                              const title =
                                state.locale === "ar"
                                  ? (opportunity.titleAr ?? opportunity.title)
                                  : opportunity.title;
                              const description =
                                state.locale === "ar"
                                  ? (opportunity.descriptionAr ?? opportunity.description)
                                  : opportunity.description;
                              const windowLabel =
                                opportunity.startsAt && opportunity.endsAt
                                  ? `${opportunity.startsAt.toLocaleString(
                                      state.locale,
                                    )} to ${opportunity.endsAt.toLocaleString(state.locale)}`
                                  : "shift-based schedule";
                              const imageLabel = opportunity.imageStoragePath
                                ? "Image attached"
                                : "No image";
                              return `<li>
                                <span>${escapeHtml(title)}<br><small>${escapeHtml(
                                  description,
                                )}</small><br><small>${escapeHtml(
                                  opportunity.locationName,
                                )} · ${escapeHtml(opportunity.category)} · ${escapeHtml(
                                  windowLabel,
                                )}</small><br><small>${escapeHtml(imageLabel)}</small></span>
                                <span class="status-pill ${
                                  opportunity.status === "published"
                                    ? "status-accepted"
                                    : "status-pending"
                                }">${escapeHtml(opportunity.status)} · ${escapeHtml(
                                  opportunity.visibility,
                                )}</span>
                                <span class="row-actions">
                                  <button type="button" data-volunteer-opportunity-edit="${escapeHtml(
                                    opportunity.opportunityId,
                                  )}">Edit</button>
                                </span>
                              </li>`;
                            })
                            .join("")}
                        </ul>`
                    }`
                  : `<p class="meta">Volunteer management requires organization owner, organization admin, or volunteer coordinator authority.</p>`
              }
            </article>
            <article class="panel editor-panel" data-workspace-panel-id="volunteer-opportunity-editor">
              <h2>Volunteer Opportunity Editor</h2>
              <form id="volunteer-opportunity-editor-form" class="editor-form">
                <div class="editor-grid">
                  <label>
                    Opportunity ID
                    <input
                      data-volunteer-opportunity-editor-field="opportunityId"
                      maxlength="100"
                      value="${escapeHtml(state.volunteerOpportunityEditor.opportunityId)}"
                    />
                  </label>
                  <label>
                    Status
                    <select data-volunteer-opportunity-editor-field="status">
                      ${["draft", "published", "completed", "archived"]
                        .map(
                          (status) =>
                            `<option value="${status}" ${
                              state.volunteerOpportunityEditor.status === status ? "selected" : ""
                            }>${status}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Campus
                    <select data-volunteer-opportunity-editor-field="campusId">
                      <option value="">No campus</option>
                      ${organizationCampuses
                        .map(
                          (campus) =>
                            `<option value="${escapeHtml(campus.campusId)}" ${
                              state.volunteerOpportunityEditor.campusId === campus.campusId
                                ? "selected"
                                : ""
                            }>${escapeHtml(campus.name)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    Category
                    <input
                      data-volunteer-opportunity-editor-field="category"
                      maxlength="80"
                      value="${escapeHtml(state.volunteerOpportunityEditor.category)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Title
                    <input
                      data-volunteer-opportunity-editor-field="title"
                      maxlength="140"
                      required
                      value="${escapeHtml(state.volunteerOpportunityEditor.title)}"
                    />
                  </label>
                  <label>
                    Arabic title
                    <input
                      data-volunteer-opportunity-editor-field="titleAr"
                      maxlength="140"
                      value="${escapeHtml(state.volunteerOpportunityEditor.titleAr)}"
                    />
                  </label>
                </div>
                <label>
                  Description
                  <textarea
                    data-volunteer-opportunity-editor-field="description"
                    maxlength="3000"
                    required
                  >${escapeHtml(state.volunteerOpportunityEditor.description)}</textarea>
                </label>
                <label>
                  Arabic description
                  <textarea
                    data-volunteer-opportunity-editor-field="descriptionAr"
                    maxlength="3000"
                  >${escapeHtml(state.volunteerOpportunityEditor.descriptionAr)}</textarea>
                </label>
                <label>
                  Instructions
                  <textarea
                    data-volunteer-opportunity-editor-field="instructions"
                    maxlength="2000"
                    required
                  >${escapeHtml(state.volunteerOpportunityEditor.instructions)}</textarea>
                </label>
                <label>
                  Arabic instructions
                  <textarea
                    data-volunteer-opportunity-editor-field="instructionsAr"
                    maxlength="2000"
                  >${escapeHtml(state.volunteerOpportunityEditor.instructionsAr)}</textarea>
                </label>
                <label>
                  Location
                  <input
                    data-volunteer-opportunity-editor-field="locationName"
                    maxlength="180"
                    required
                    value="${escapeHtml(state.volunteerOpportunityEditor.locationName)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    Starts
                    <input
                      data-volunteer-opportunity-editor-field="startsAt"
                      type="datetime-local"
                      value="${escapeHtml(state.volunteerOpportunityEditor.startsAt)}"
                    />
                  </label>
                  <label>
                    Ends
                    <input
                      data-volunteer-opportunity-editor-field="endsAt"
                      type="datetime-local"
                      value="${escapeHtml(state.volunteerOpportunityEditor.endsAt)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Age policy
                    <input
                      data-volunteer-opportunity-editor-field="agePolicy"
                      maxlength="300"
                      value="${escapeHtml(state.volunteerOpportunityEditor.agePolicy)}"
                    />
                  </label>
                  <label>
                    Training required
                    <select data-volunteer-opportunity-editor-field="trainingRequired">
                      <option value="false" ${
                        state.volunteerOpportunityEditor.trainingRequired === "false"
                          ? "selected"
                          : ""
                      }>false</option>
                      <option value="true" ${
                        state.volunteerOpportunityEditor.trainingRequired === "true"
                          ? "selected"
                          : ""
                      }>true</option>
                    </select>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Physical notes
                    <input
                      data-volunteer-opportunity-editor-field="physicalNotes"
                      maxlength="300"
                      value="${escapeHtml(state.volunteerOpportunityEditor.physicalNotes)}"
                    />
                  </label>
                  <label>
                    Dress code
                    <input
                      data-volunteer-opportunity-editor-field="dressCode"
                      maxlength="300"
                      value="${escapeHtml(state.volunteerOpportunityEditor.dressCode)}"
                    />
                  </label>
                </div>
                <input
                  data-volunteer-opportunity-editor-field="imageStoragePath"
                  type="hidden"
                  value="${escapeHtml(state.volunteerOpportunityEditor.imageStoragePath)}"
                />
                <div class="editor-grid">
                  <label>
                    Image alt text
                    <input
                      data-volunteer-opportunity-editor-field="imageAltText"
                      maxlength="180"
                      value="${escapeHtml(state.volunteerOpportunityEditor.imageAltText)}"
                    />
                  </label>
                  <label>
                    Arabic image alt text
                    <input
                      data-volunteer-opportunity-editor-field="imageAltTextAr"
                      maxlength="180"
                      value="${escapeHtml(state.volunteerOpportunityEditor.imageAltTextAr)}"
                    />
                  </label>
                </div>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>Save Opportunity</button>
                  <button id="reset-volunteer-opportunity-form" type="button">Reset</button>
                </div>
              </form>
            </article>
            <article class="panel" data-workspace-panel-id="volunteer-shifts">
              <h2>Volunteer Shifts</h2>
              ${
                state.volunteerShifts.length === 0
                  ? `<p class="meta">No volunteer shifts for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.volunteerShifts
                      .map((shift) => {
                        const opportunity = state.volunteerOpportunities.find(
                          (candidate) => candidate.opportunityId === shift.opportunityId,
                        );
                        const startsAt = shift.startsAt
                          ? shift.startsAt.toLocaleString(state.locale)
                          : "-";
                        const endsAt = shift.endsAt
                          ? shift.endsAt.toLocaleString(state.locale)
                          : "-";
                        const remaining = Math.max(shift.capacity - shift.confirmedCount, 0);
                        return `<li>
                          <span>${escapeHtml(
                            opportunity?.title ?? shift.opportunityId,
                          )}<br><small>${escapeHtml(startsAt)} to ${escapeHtml(
                            endsAt,
                          )} · ${escapeHtml(shift.timezone)}</small>${
                            shift.notes ? `<br><small>${escapeHtml(shift.notes)}</small>` : ""
                          }</span>
                          <span class="status-pill ${
                            shift.status === "open" ? "status-accepted" : "status-pending"
                          }">${escapeHtml(shift.status)} · ${shift.confirmedCount}/${
                            shift.capacity
                          } · ${remaining} left</span>
                          <span class="row-actions">
                            <button type="button" data-volunteer-shift-edit="${escapeHtml(
                              `${shift.opportunityId}/${shift.shiftId}`,
                            )}">Edit</button>
                          </span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article class="panel editor-panel" data-workspace-panel-id="volunteer-shift-editor">
              <h2>Volunteer Shift Editor</h2>
              <form id="volunteer-shift-editor-form" class="editor-form">
                <div class="editor-grid">
                  <label>
                    Opportunity
                    <select data-volunteer-shift-editor-field="opportunityId">
                      ${state.volunteerOpportunities
                        .map(
                          (opportunity) =>
                            `<option value="${escapeHtml(opportunity.opportunityId)}" ${
                              state.volunteerShiftEditor.opportunityId === opportunity.opportunityId
                                ? "selected"
                                : ""
                            }>${escapeHtml(opportunity.title)}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    Shift ID
                    <input
                      data-volunteer-shift-editor-field="shiftId"
                      maxlength="100"
                      value="${escapeHtml(state.volunteerShiftEditor.shiftId)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Status
                    <select data-volunteer-shift-editor-field="status">
                      ${["open", "closed", "cancelled"]
                        .map(
                          (status) =>
                            `<option value="${status}" ${
                              state.volunteerShiftEditor.status === status ? "selected" : ""
                            }>${status}</option>`,
                        )
                        .join("")}
                    </select>
                  </label>
                  <label>
                    Timezone
                    <input
                      data-volunteer-shift-editor-field="timezone"
                      maxlength="80"
                      value="${escapeHtml(state.volunteerShiftEditor.timezone)}"
                    />
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Starts
                    <input
                      data-volunteer-shift-editor-field="startsAt"
                      type="datetime-local"
                      required
                      value="${escapeHtml(state.volunteerShiftEditor.startsAt)}"
                    />
                  </label>
                  <label>
                    Ends
                    <input
                      data-volunteer-shift-editor-field="endsAt"
                      type="datetime-local"
                      required
                      value="${escapeHtml(state.volunteerShiftEditor.endsAt)}"
                    />
                  </label>
                </div>
                <label>
                  Capacity
                  <input
                    data-volunteer-shift-editor-field="capacity"
                    min="0"
                    step="1"
                    type="number"
                    value="${escapeHtml(state.volunteerShiftEditor.capacity)}"
                  />
                </label>
                <div class="editor-grid">
                  <label>
                    Notes
                    <textarea data-volunteer-shift-editor-field="notes" maxlength="1000">${escapeHtml(
                      state.volunteerShiftEditor.notes,
                    )}</textarea>
                  </label>
                  <label>
                    Arabic notes
                    <textarea data-volunteer-shift-editor-field="notesAr" maxlength="1000">${escapeHtml(
                      state.volunteerShiftEditor.notesAr,
                    )}</textarea>
                  </label>
                </div>
                <div class="editor-grid">
                  <label>
                    Instructions
                    <textarea data-volunteer-shift-editor-field="instructions" maxlength="2000">${escapeHtml(
                      state.volunteerShiftEditor.instructions,
                    )}</textarea>
                  </label>
                  <label>
                    Arabic instructions
                    <textarea data-volunteer-shift-editor-field="instructionsAr" maxlength="2000">${escapeHtml(
                      state.volunteerShiftEditor.instructionsAr,
                    )}</textarea>
                  </label>
                </div>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>Save Shift</button>
                  <button id="reset-volunteer-shift-form" type="button">Reset</button>
                </div>
              </form>
            </article>
            <article class="panel" data-workspace-panel-id="volunteer-roster">
              <h2>Volunteer Roster</h2>
              ${
                state.volunteerSignups.length === 0
                  ? `<p class="meta">No volunteer signups for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.volunteerSignups
                      .map((signup) => {
                        const personLabel = personLabelById.get(signup.personId) ?? signup.personId;
                        const attendance = state.volunteerAttendances.find(
                          (candidate) =>
                            candidate.opportunityId === signup.opportunityId &&
                            candidate.shiftId === signup.shiftId &&
                            candidate.personId === signup.personId,
                        );
                        const summary = state.volunteerServiceSummaries.find(
                          (candidate) => candidate.personId === signup.personId,
                        );
                        const startsAt = signup.shiftStartsAt
                          ? signup.shiftStartsAt.toLocaleString(state.locale)
                          : "-";
                        const attendanceStatus = attendance?.status ?? "not checked in";
                        return `<li>
                          <span>${escapeHtml(personLabel)}<br><small>${escapeHtml(
                            signup.opportunityTitleSnapshot,
                          )} · ${escapeHtml(startsAt)}</small><br><small>${escapeHtml(
                            signup.personId,
                          )}</small><br><small>${escapeHtml(attendanceStatus)} · ${(
                            (attendance?.verifiedMinutes ?? summary?.totalVerifiedMinutes ?? 0) / 60
                          ).toFixed(1)} verified hours</small></span>
                          <span class="status-pill ${
                            attendance?.status === "verified"
                              ? "status-accepted"
                              : attendance?.status === "checkedIn"
                                ? "status-pending"
                                : signup.status === "confirmed"
                                  ? "status-accepted"
                                  : "status-pending"
                          }">${escapeHtml(attendance?.status ?? signup.status)}</span>
                          ${
                            signup.status === "confirmed"
                              ? `<span class="row-actions">
                                  ${
                                    attendance
                                      ? ""
                                      : `<button
                                          type="button"
                                          data-volunteer-attendance-action="check-in"
                                          data-volunteer-opportunity-id="${escapeHtml(
                                            signup.opportunityId,
                                          )}"
                                          data-volunteer-shift-id="${escapeHtml(signup.shiftId)}"
                                          data-volunteer-person-id="${escapeHtml(signup.personId)}"
                                          ${state.busy ? "disabled" : ""}
                                        >Check in</button>`
                                  }
                                  ${
                                    attendance?.status === "checkedIn"
                                      ? `<button
                                          type="button"
                                          data-volunteer-attendance-action="check-out"
                                          data-volunteer-opportunity-id="${escapeHtml(
                                            signup.opportunityId,
                                          )}"
                                          data-volunteer-shift-id="${escapeHtml(signup.shiftId)}"
                                          data-volunteer-person-id="${escapeHtml(signup.personId)}"
                                          ${state.busy ? "disabled" : ""}
                                        >Check out</button>`
                                      : ""
                                  }
                                  ${
                                    attendance?.checkedOutAt
                                      ? `<button
                                          type="button"
                                          data-volunteer-attendance-action="correct"
                                          data-volunteer-opportunity-id="${escapeHtml(
                                            signup.opportunityId,
                                          )}"
                                          data-volunteer-shift-id="${escapeHtml(signup.shiftId)}"
                                          data-volunteer-person-id="${escapeHtml(signup.personId)}"
                                          data-volunteer-verified-minutes="${escapeHtml(
                                            String(attendance.verifiedMinutes),
                                          )}"
                                          ${state.busy ? "disabled" : ""}
                                        >Correct</button>`
                                      : ""
                                  }
	                                  <button
	                                    class="danger-action inline-action"
	                                    type="button"
	                                    data-volunteer-signup-action="cancel"
                                    data-volunteer-opportunity-id="${escapeHtml(
                                      signup.opportunityId,
                                    )}"
                                    data-volunteer-shift-id="${escapeHtml(signup.shiftId)}"
                                    data-volunteer-person-id="${escapeHtml(signup.personId)}"
                                    ${state.busy ? "disabled" : ""}
                                  >Cancel</button>
                                </span>`
                              : ""
                          }
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            <article class="panel" data-workspace-panel-id="volunteer-recognition">
              <h2>Volunteer Recognition</h2>
              ${
                state.volunteerRecognitionAwards.length === 0
                  ? `<p class="meta">No volunteer recognition awards yet.</p>`
                  : `<ul class="request-list">
                    ${state.volunteerRecognitionAwards
                      .map((award) => {
                        const personLabel = personLabelById.get(award.personId) ?? award.personId;
                        return `<li>
                          <span>${escapeHtml(personLabel)}<br><small>${escapeHtml(
                            state.locale === "ar" ? award.labelAr : award.label,
                          )}</small></span>
                          <span class="status-pill status-accepted">${(
                            award.verifiedMinutesAtAward / 60
                          ).toFixed(1)} hours</span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>
            ${renderCommunityEngagementSection(organization.displayName)}
            ${
              release1OrganizationIntelligenceEnabled
                ? `<article id="section-intelligence" class="panel">
              <h2>${copy.organizationIntelligence}</h2>
              ${
                latestInsight
                  ? `<p class="status">${
                      state.locale === "ar" ? latestInsight.titleAr : latestInsight.title
                    }</p>
                    <p class="meta">${
                      state.locale === "ar" ? latestInsight.summaryAr : latestInsight.summary
                    }</p>
                    <p class="meta">${copy.recommendation}: ${
                      state.locale === "ar"
                        ? latestInsight.recommendationAr
                        : latestInsight.recommendation
                    }</p>
                    <ul class="request-list">
                      <li>
                        <span>${copy.connectedPeople}: ${
                          latestInsight.connectedPeopleCount
                        }<br><small>${copy.communityEvents}: ${
                          latestInsight.upcomingEventsCount
                        } · ${copy.announcements}: ${
                          latestInsight.publishedAnnouncementsCount
                        }</small></span>
                        <span class="status-pill ${
                          latestInsight.riskLevel === "steady"
                            ? "status-accepted"
                            : "status-pending"
                        }">${copy.riskLevel}: ${latestInsight.riskLevel}</span>
                      </li>
                      <li>
                        <span>${copy.firstDonation}<br><small>${copy.availableSlots}: ${
                          latestInsight.availableIftarSlotsCount
                        } · ${copy.availableSpaceSlots}: ${
                          latestInsight.availableSpaceSlotsCount
                        }</small></span>
                        <span class="status-pill status-accepted">${formatAmount(
                          latestInsight.confirmedDonationTotalCents,
                        )}</span>
                      </li>
                    </ul>`
                  : `<p class="meta">No operational pulse for ${organization.displayName}.</p>`
              }
              ${
                state.intelligenceRequests.length === 0
                  ? `<p class="meta">No pulse requests for ${organization.displayName}.</p>`
                  : `<ul class="request-list">
                    ${state.intelligenceRequests
                      .slice(0, 3)
                      .map((request) => {
                        const requestedAt = request.requestedAt
                          ? request.requestedAt.toLocaleString(state.locale)
                          : "-";
                        const processedAt = request.processedAt
                          ? request.processedAt.toLocaleString(state.locale)
                          : "-";
                        return `<li>
                          <span>${request.requestedBy}<br><small>${requestedAt} · ${
                            request.snapshotId
                          }</small></span>
                          <span class="status-pill ${
                            request.status === "completed" ? "status-accepted" : "status-pending"
                          }">${request.status}<br><small>${processedAt}</small></span>
                        </li>`;
                      })
                      .join("")}
                  </ul>`
              }
            </article>`
                : ""
            }
            <article id="section-prayer-settings" class="panel">
              <div class="panel-header-actions">
                <div>
                  <h2>${copy.prayerTimesSettings}</h2>
                  <p class="meta">AlAdhan calculates daily Adhan times from the configured address; Iqama times are entered by the organization.</p>
                </div>
                <button
                  class="secondary-action"
                  data-task-window-open="organization-prayer-settings-form"
                  type="button"
                >${copy.configurePrayerTimes}</button>
              </div>
              ${
                state.organizationPrayerSettingsMessage
                  ? `<p class="status status-inline">${escapeHtml(
                      state.organizationPrayerSettingsMessage,
                    )}</p>`
                  : ""
              }
              <ul class="request-list compact-list">
                <li>
                  <span>${copy.prayerProvider}<br><small>${escapeHtml(
                    state.organizationPrayerSettings?.address ??
                      organizationPrayerAddressFromProfile(
                        state.organizationId,
                        organizationProfile,
                      ),
                  )}</small></span>
                  <span class="status-pill status-accepted">${escapeHtml(
                    state.organizationPrayerSettings?.provider ?? "aladhan",
                  )}</span>
                </li>
                <li>
                  <span>${copy.prayerTimesEnabled}<br><small>${copy.prayerTimesMobileVisibility}</small></span>
                  <span class="status-pill ${
                    state.organizationPrayerSettings?.enabled !== false
                      ? "status-accepted"
                      : "status-pending"
                  }">${yesNo(state.organizationPrayerSettings?.enabled !== false, state.locale)}</span>
                </li>
                <li>
                  <span>${copy.prayerCalculationMethod}<br><small>${copy.prayerSchool}: ${
                    state.organizationPrayerSettings?.school === 1 ? "Hanafi" : "Standard"
                  }</small></span>
                  <span class="status-pill status-pending">${escapeHtml(
                    String(state.organizationPrayerSettings?.calculationMethod ?? 2),
                  )}</span>
                </li>
                <li>
                  <span>${copy.organizationTimeZone}<br><small>${copy.prayerTimingsDate}: ${escapeHtml(
                    state.organizationPrayerSettings?.timingsDate ?? "-",
                  )}</small></span>
                  <span class="status-pill status-accepted">${escapeHtml(
                    state.organizationPrayerSettings?.timeZone ??
                      organizationProfile?.timeZone ??
                      "-",
                  )}</span>
                </li>
              </ul>
              ${renderOrganizationPrayerTimingList(copy, state.organizationPrayerSettings)}
            </article>
            ${renderOrganizationIslamicComponentsPanel()}
            ${renderOrganizationIslamicComponentsEditor()}
            <article class="panel editor-panel task-only-panel" data-workspace-panel-id="organization-prayer-settings-editor">
              <h2>${copy.prayerTimesSettings}</h2>
              <form
                id="organization-prayer-settings-form"
                class="editor-form"
                data-task-window-launch="manual"
              >
                <section class="form-section">
                  <h3>${copy.prayerProvider}</h3>
                  <label class="checkbox-row">
                    <input
                      data-organization-prayer-settings-field="enabled"
                      type="checkbox"
                      ${state.organizationPrayerSettingsEditor.enabled ? "checked" : ""}
                    />
                    ${copy.prayerTimesEnabled}
                  </label>
                  <label>
                    ${copy.prayerAddress}
                    <textarea
                      data-organization-prayer-settings-field="address"
                      maxlength="240"
                      required
                    >${escapeHtml(state.organizationPrayerSettingsEditor.address)}</textarea>
                  </label>
                  <div class="editor-grid">
                    <label>
                      ${copy.prayerCalculationMethod}
                      <select data-organization-prayer-settings-field="calculationMethod" required>
                        ${renderOrganizationPrayerMethodOptions(
                          state.organizationPrayerSettingsEditor.calculationMethod,
                        )}
                      </select>
                    </label>
                    <label>
                      ${copy.prayerSchool}
                      <select data-organization-prayer-settings-field="school" required>
                        <option value="0" ${
                          state.organizationPrayerSettingsEditor.school === "0" ? "selected" : ""
                        }>Standard / Shafi</option>
                        <option value="1" ${
                          state.organizationPrayerSettingsEditor.school === "1" ? "selected" : ""
                        }>Hanafi</option>
                      </select>
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.prayerLatitudeAdjustment}
                      <select
                        data-organization-prayer-settings-field="latitudeAdjustmentMethod"
                        required
                      >
                        <option value="3" ${
                          state.organizationPrayerSettingsEditor.latitudeAdjustmentMethod === "3"
                            ? "selected"
                            : ""
                        }>Angle based</option>
                        <option value="1" ${
                          state.organizationPrayerSettingsEditor.latitudeAdjustmentMethod === "1"
                            ? "selected"
                            : ""
                        }>Middle of the night</option>
                        <option value="2" ${
                          state.organizationPrayerSettingsEditor.latitudeAdjustmentMethod === "2"
                            ? "selected"
                            : ""
                        }>One seventh</option>
                      </select>
                    </label>
                    <label>
                      ${copy.organizationTimeZone}
                      <select data-organization-prayer-settings-field="timeZone" required>
                        ${renderOrganizationProfileSelectOptions(
                          organizationTimeZoneOptions,
                          state.organizationPrayerSettingsEditor.timeZone,
                        )}
                      </select>
                    </label>
                  </div>
                </section>
                <section class="form-section">
                  <h3>${copy.prayerIqamaTimes}</h3>
                  <div class="editor-grid">
                    <label>
                      ${copy.prayerFajr}
                      <input
                        data-organization-prayer-settings-field="fajrIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.fajrIqama)}"
                      />
                    </label>
                    <label>
                      ${copy.prayerDhuhr}
                      <input
                        data-organization-prayer-settings-field="dhuhrIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.dhuhrIqama)}"
                      />
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.prayerAsr}
                      <input
                        data-organization-prayer-settings-field="asrIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.asrIqama)}"
                      />
                    </label>
                    <label>
                      ${copy.prayerMaghrib}
                      <input
                        data-organization-prayer-settings-field="maghribIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.maghribIqama)}"
                      />
                    </label>
                  </div>
                  <div class="editor-grid">
                    <label>
                      ${copy.prayerIsha}
                      <input
                        data-organization-prayer-settings-field="ishaIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.ishaIqama)}"
                      />
                    </label>
                    <label>
                      ${copy.prayerJumuah}
                      <input
                        data-organization-prayer-settings-field="jumuahIqama"
                        type="time"
                        value="${escapeHtml(state.organizationPrayerSettingsEditor.jumuahIqama)}"
                      />
                    </label>
                  </div>
                </section>
                <div class="editor-actions">
                  <button type="submit" ${state.busy ? "disabled" : ""}>${
                    copy.savePrayerSettings
                  }</button>
                  <button id="reset-organization-prayer-settings-form" type="button">${
                    copy.resetPrayerSettingsForm
                  }</button>
                </div>
              </form>
            </article>
	            <article id="section-profile-audit" class="panel">
	              <h2>${copy.organizationProfileAudit}</h2>
	              ${
                  state.organizationProfileRequests.length === 0
                    ? `<p class="meta">No profile update requests for ${organizationDisplayName}.</p>`
                    : `<div class="table-scroll" role="region" aria-label="${copy.organizationProfileAudit}">
	                    <table class="data-table audit-table">
	                      <thead>
	                        <tr>
	                          <th scope="col">Change</th>
	                          <th scope="col">Requested by</th>
	                          <th scope="col">Requested</th>
	                          <th scope="col">Reviewed</th>
	                          <th scope="col">Status</th>
	                        </tr>
	                      </thead>
	                      <tbody>
	                        ${state.organizationProfileRequests
                            .slice(0, 12)
                            .map((request) => {
                              const requestedAt = request.requestedAt
                                ? request.requestedAt.toLocaleString(state.locale)
                                : "-";
                              const processedAt = request.processedAt
                                ? request.processedAt.toLocaleString(state.locale)
                                : "Pending review";
                              const statusClass =
                                request.status === "completed"
                                  ? "status-accepted"
                                  : request.status === "rejected"
                                    ? "status-rejected"
                                    : "status-pending";
                              return `<tr>
	                              <td>
	                                <strong>${escapeHtml(profileAuditRequestLabel(request.id))}</strong>
	                                ${renderTechnicalId(request.id, "Request ID")}
	                                ${
                                    request.reviewReason
                                      ? `<small>${escapeHtml(request.reviewReason)}</small>`
                                      : ""
                                  }
	                              </td>
	                              <td>${escapeHtml(request.requestedBy)}</td>
	                              <td>${escapeHtml(requestedAt)}</td>
	                              <td>${escapeHtml(processedAt)}</td>
	                              <td><span class="status-pill ${statusClass}">${escapeHtml(
                                  request.status,
                                )}</span></td>
	                            </tr>`;
                            })
                            .join("")}
	                      </tbody>
	                    </table>
	                  </div>`
                }
	            </article>
          </div>
        </section>
      </section>
      ${renderTaskWindowShell()}
      ${renderEventOperationsWindow()}
      ${renderAccountProfileEditor()}
    </div>
  `;

  organizeWorkspacePages(appRoot, workspacePages, activePageId);
  prepareTaskWindows(appRoot);
  bindTaskWindowControls(appRoot);
  bindFormValidationFeedback(appRoot, () => state.locale);
  bindEventOperationsWindowControls(appRoot);
  bindAccountProfileControls(appRoot);
  bindSupportControls(appRoot);
  enhanceCarbonDesignSystem(appRoot);
  enhanceArabicTranslation(appRoot, {
    locale: state.locale,
    scope: () => `${auth.currentUser?.uid ?? ""}/${state.organizationId}`,
    translate: async (text) => {
      const result = await postAuthenticatedFunction(
        "translateOrganizationText",
        { organizationId: state.organizationId, text },
        AbortSignal.timeout(45_000),
      );
      return typeof result.translatedText === "string" ? result.translatedText : "";
    },
  });
  enhanceRequiredFieldIndicators(appRoot);
  enhanceLongRequestLists(appRoot);
  enhanceSearchableDataCollections(appRoot);

  bindEventEditorInputs(appRoot);
  bindEventWizardControls(appRoot);
  bindOperationalModeEditorInputs(appRoot);
  bindRamadanModeEditorInputs(appRoot);
  bindRamadanSetupEditorInputs(appRoot);
  bindIftarSponsorshipDefaultsEditorInputs(appRoot);
  bindIftarSponsorshipDayEditorInputs(appRoot);
  bindRamadanAdminNoteEditorInputs(appRoot);
  bindManualIftarSponsorshipEditorInputs(appRoot);
  bindIftarSlotEditorInputs(appRoot);
  bindAnnouncementComposerInputs(appRoot);
  bindImamContentEditorInputs(appRoot);
  bindSpaceEditorInputs(appRoot);
  bindSpaceAvailabilityBlockEditorInputs(appRoot);
  bindSpaceSlotEditorInputs(appRoot);
  bindVolunteerOpportunityEditorInputs(appRoot);
  bindVolunteerShiftEditorInputs(appRoot);
  bindOrganizationProfileEditorInputs(appRoot);
  bindOrganizationPrayerSettingsInputs(appRoot);
  bindOrganizationIslamicComponentsInputs(appRoot);
  appRoot
    .querySelector<HTMLFormElement>("#event-editor-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveEventFromEditor();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-event-reset-form]").forEach((button) => {
    button.addEventListener("click", () => {
      if (state.eventWizardOpen) {
        resetEventCreationWizard();
        return;
      }
      resetEventEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  });
  appRoot
    .querySelector<HTMLInputElement>("#event-check-in-station")
    ?.addEventListener("input", (event) => {
      state.eventCheckInStationLabel = (event.target as HTMLInputElement).value;
    });
  appRoot
    .querySelector<HTMLTextAreaElement>("#event-check-in-credential")
    ?.addEventListener("input", (event) => {
      state.eventCheckInCredential = (event.target as HTMLTextAreaElement).value;
    });
  appRoot
    .querySelector<HTMLInputElement>("#event-manual-registration-search")
    ?.addEventListener("input", (event) => {
      state.eventManualRegistrationSearch = (event.target as HTMLInputElement).value;
      eventRegistrationPage = 1;
      eventWaitlistPage = 1;
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#event-qr-check-in-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitQrCheckIn();
    });
  appRoot
    .querySelector<HTMLFormElement>("#event-communication-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      const root = event.currentTarget as HTMLFormElement;
      const audience = root.querySelector<HTMLSelectElement>(
        "#event-communication-audience",
      )?.value;
      const channelValue = root.querySelector<HTMLSelectElement>(
        "#event-communication-channel",
      )?.value;
      const communicationType = root.querySelector<HTMLSelectElement>(
        "#event-communication-type",
      )?.value;
      const subject = root.querySelector<HTMLInputElement>("#event-communication-subject")?.value;
      const message = root.querySelector<HTMLTextAreaElement>(
        "#event-communication-message",
      )?.value;
      void sendEventCommunicationFromEditor({
        audience: audience ?? "confirmed",
        channels: (channelValue ?? "push,email").split(","),
        message: message ?? "",
        subject: subject ?? "",
        type:
          communicationType === "eventPublished" ||
          communicationType === "urgentNotice" ||
          communicationType === "materialUpdate"
            ? communicationType
            : "materialUpdate",
      });
    });
  appRoot
    .querySelector<HTMLButtonElement>("#event-export-attendees")
    ?.addEventListener("click", () => {
      void exportEventAttendeesFromEditor({ includeResponses: false });
    });
  appRoot
    .querySelector<HTMLButtonElement>("#event-export-attendees-responses")
    ?.addEventListener("click", () => {
      void exportEventAttendeesFromEditor({ includeResponses: true });
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-manual-check-in]").forEach((button) => {
    button.addEventListener("click", () => {
      const registrationId = button.dataset.manualCheckIn ?? "";
      void submitManualCheckIn(registrationId);
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-promote-waitlist]").forEach((button) => {
    button.addEventListener("click", () => {
      const registrationId = button.dataset.promoteWaitlist ?? "";
      void promoteWaitlistedRegistration(registrationId);
    });
  });
  appRoot
    .querySelector<HTMLFormElement>("#operational-mode-editor-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveOperationalModeFromEditor();
    });
  appRoot
    .querySelector<HTMLButtonElement>("[data-mode-create]")
    ?.addEventListener("click", () => openOperationalMode(null));
  appRoot.querySelector<HTMLButtonElement>("[data-mode-retry]")?.addEventListener("click", () => {
    connectOrganizationRoleAssignments();
    render();
  });
  appRoot
    .querySelector<HTMLButtonElement>("[data-mode-close]")
    ?.addEventListener("click", () => closeOperationalModeEditor());
  appRoot
    .querySelector<HTMLButtonElement>("[data-mode-discard]")
    ?.addEventListener("click", () => closeOperationalModeEditor(true));
  appRoot.querySelector<HTMLButtonElement>("[data-mode-keep]")?.addEventListener("click", () => {
    operationalModeDiscardPending = false;
    renderOperationalModeEditorUpdate();
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-operational-mode-edit]").forEach((button) => {
    button.addEventListener("click", () =>
      editOperationalMode(button.dataset.operationalModeEdit ?? ""),
    );
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-operational-mode-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        operationalModePendingAction = {
          modeId: button.dataset.operationalModeId ?? "",
          action: button.dataset.operationalModeAction as ModeAction,
        };
        state.operationalModeError = "";
        state.operationalModeMessage = "";
        render();
        appRoot.querySelector<HTMLElement>("[data-mode-confirm]")?.focus();
      });
    });
  appRoot
    .querySelector<HTMLButtonElement>("[data-mode-cancel-action]")
    ?.addEventListener("click", () => {
      operationalModePendingAction = null;
      render();
    });
  appRoot.querySelector<HTMLButtonElement>("[data-mode-confirm]")?.addEventListener("click", () => {
    const pending = operationalModePendingAction;
    if (!pending) return;
    void requestOperationalModeStateChange(
      pending.action === "activate"
        ? "activateOperationalMode"
        : pending.action === "deactivate"
          ? "deactivateOperationalMode"
          : "archiveOperationalMode",
      pending.modeId,
    );
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-space-reservation-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void reviewSpaceReservationFromButton(button);
      });
    });
  appRoot
    .querySelector<HTMLFormElement>("#ramadan-mode-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveRamadanModeFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-ramadan-mode-form")
    ?.addEventListener("click", () => {
      clearRamadanModeEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#ramadan-setup-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveRamadanSetupFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-ramadan-setup-form")
    ?.addEventListener("click", () => {
      resetRamadanSetupEditor();
      state.busy = false;
      state.error = "";
      state.ramadanSetupMessage = "";
      render();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#preview-ramadan-dates")
    ?.addEventListener("click", () => {
      void previewRamadanDatesFromProvider();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-ramadan-setup-action]").forEach((button) => {
    button.addEventListener("click", () => {
      const ramadanId = button.dataset.ramadanSetupId ?? "";
      const action = button.dataset.ramadanSetupAction;
      if (action === "ready") {
        void requestRamadanSetupStateChange("markRamadanSetupReady", ramadanId);
      } else if (action === "activate") {
        void requestRamadanSetupStateChange("activateRamadanSetup", ramadanId);
      } else if (action === "deactivate") {
        void requestRamadanSetupStateChange("deactivateRamadanSetup", ramadanId);
      }
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-ramadan-setup-edit-confirm]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.ramadanSetupEditConfirm;
        state.ramadanSetupEditConfirmationPending = false;
        if (action === "confirm") {
          activeTaskWindowId = "ramadan-setup-editor-form";
          state.ramadanSetupMessage = "Ramadan setup is open for confirmed active-season changes.";
        } else {
          state.ramadanSetupMessage = "";
        }
        render();
      });
    });
  appRoot
    .querySelector<HTMLButtonElement>("#generate-iftar-sponsorship-days")
    ?.addEventListener("click", () => {
      void generateIftarSponsorshipDaysFromEditor();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#generate-iftar-sponsorship-days-command")
    ?.addEventListener("click", () => {
      void generateIftarSponsorshipDaysFromEditor();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#export-iftar-sponsorship-calendar")
    ?.addEventListener("click", () => {
      exportIftarSponsorshipCalendar();
    });
  appRoot
    .querySelector<HTMLFormElement>("#iftar-sponsorship-day-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveIftarSponsorshipDayFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-iftar-sponsorship-day-form")
    ?.addEventListener("click", () => {
      iftarSponsorshipDayEditorRevision += 1;
      state.iftarSponsorshipDayEditor = buildDefaultIftarSponsorshipDayEditorInput(
        state.organizationId,
      );
      state.iftarSponsorshipDayEditorMode = "edit";
      state.iftarSponsorshipMessage = "";
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-iftar-sponsorship-day-view]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        selectIftarSponsorshipDayForEditor(button.dataset.iftarSponsorshipDayView ?? "", "view");
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-iftar-sponsorship-day-edit]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        selectIftarSponsorshipDayForEditor(button.dataset.iftarSponsorshipDayEdit ?? "", "edit");
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-open-manual-iftar-sponsorship]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        openManualIftarSponsorshipFormForDay(button.dataset.openManualIftarSponsorship);
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-iftar-sponsorship-day-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const dayId = button.dataset.iftarSponsorshipDayId ?? "";
        const action = button.dataset.iftarSponsorshipDayAction;
        if (action === "open") {
          state.iftarSponsorshipCloseNote = "";
          state.iftarSponsorshipClosingDayId = "";
          void requestIftarSponsorshipDayStateChange("openIftarSponsorshipDay", dayId);
        } else if (action === "close") {
          state.iftarSponsorshipClosingDayId = dayId;
          state.iftarSponsorshipCloseNote = "";
          state.iftarSponsorshipMessage = "Add a public note before closing this day.";
          render();
        }
      });
    });
  appRoot
    .querySelector<HTMLTextAreaElement>("#iftar-sponsorship-close-note")
    ?.addEventListener("input", (event) => {
      state.iftarSponsorshipCloseNote = (event.target as HTMLTextAreaElement).value;
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-iftar-sponsorship-day-close-confirm]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const noteInput = appRoot.querySelector<HTMLTextAreaElement>(
          "#iftar-sponsorship-close-note",
        );
        const note = noteInput?.value.trim() ?? "";
        if (!note) {
          state.error = "A close note is required before closing an Iftar day.";
          render();
          return;
        }
        state.iftarSponsorshipCloseNote = note;
        void requestIftarSponsorshipDayStateChange(
          "closeIftarSponsorshipDay",
          button.dataset.iftarSponsorshipDayCloseConfirm ?? "",
          note,
        );
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-iftar-sponsorship-day-close-cancel]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        state.iftarSponsorshipCloseNote = "";
        state.iftarSponsorshipClosingDayId = "";
        state.iftarSponsorshipMessage = "";
        render();
      });
    });
  appRoot
    .querySelector<HTMLFormElement>("#ramadan-admin-note-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveRamadanAdminNoteFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-ramadan-admin-note-form")
    ?.addEventListener("click", () => {
      ramadanAdminNoteEditorRevision += 1;
      state.ramadanAdminNoteEditor = buildDefaultRamadanAdminNoteEditorInput(state.organizationId);
      state.ramadanAdminNoteMessage = "";
      state.busy = false;
      state.error = "";
      connectRamadanAdminNotes();
      render();
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-ramadan-admin-note-edit]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        selectRamadanAdminNoteForEditor(button.dataset.ramadanAdminNoteEdit ?? "");
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-ramadan-admin-note-archive]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void archiveRamadanAdminNoteFromAdmin(button.dataset.ramadanAdminNoteArchive ?? "");
      });
    });
  appRoot
    .querySelector<HTMLFormElement>("#manual-iftar-sponsorship-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => createManualIftarSponsorshipFromEditor());
    });
  if (appRoot.dataset.ramadanSponsorDelegatedActionsBound !== "true") {
    appRoot.dataset.ramadanSponsorDelegatedActionsBound = "true";
    appRoot.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) {
        return;
      }

      const transferButton = target.closest<HTMLButtonElement>("[data-iftar-transfer-sponsorship]");
      if (transferButton && appRoot.contains(transferButton)) {
        event.preventDefault();
        const destination = transferButton
          .closest<HTMLElement>("[data-iftar-transfer-row]")
          ?.querySelector<HTMLSelectElement>("[data-iftar-transfer-destination]")?.value;
        void transferIftarSponsorshipFromAdmin(
          transferButton.dataset.iftarTransferSponsorship ?? "",
          destination,
        );
        return;
      }

      const transferPromptButton = target.closest<HTMLButtonElement>(
        "[data-iftar-transfer-prompt]",
      );
      if (transferPromptButton && appRoot.contains(transferPromptButton)) {
        event.preventDefault();
        state.iftarSponsorshipTransferPromptId =
          transferPromptButton.dataset.iftarTransferPrompt ?? "";
        state.iftarSponsorshipDeletePromptId = "";
        state.iftarSponsorshipEditPromptId = "";
        render();
        return;
      }

      const transferCancelButton = target.closest<HTMLButtonElement>(
        "[data-iftar-transfer-prompt-cancel]",
      );
      if (transferCancelButton && appRoot.contains(transferCancelButton)) {
        event.preventDefault();
        if (
          state.iftarSponsorshipTransferPromptId ===
          (transferCancelButton.dataset.iftarTransferPromptCancel ?? "")
        ) {
          state.iftarSponsorshipTransferPromptId = "";
        }
        render();
        return;
      }

      const sponsorEditPromptButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsor-edit-prompt]",
      );
      if (sponsorEditPromptButton && appRoot.contains(sponsorEditPromptButton)) {
        event.preventDefault();
        state.iftarSponsorshipEditPromptId =
          sponsorEditPromptButton.dataset.iftarSponsorEditPrompt ?? "";
        state.iftarSponsorshipDeletePromptId = "";
        state.iftarSponsorshipTransferPromptId = "";
        render();
        return;
      }

      const sponsorEditCancelButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsor-edit-cancel]",
      );
      if (sponsorEditCancelButton && appRoot.contains(sponsorEditCancelButton)) {
        event.preventDefault();
        if (
          state.iftarSponsorshipEditPromptId ===
          (sponsorEditCancelButton.dataset.iftarSponsorEditCancel ?? "")
        ) {
          state.iftarSponsorshipEditPromptId = "";
        }
        render();
        return;
      }

      const sponsorEditSubmitButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsor-edit-submit]",
      );
      if (sponsorEditSubmitButton && appRoot.contains(sponsorEditSubmitButton)) {
        event.preventDefault();
        void updateIftarSponsorshipSponsorDetailsFromAdmin(
          sponsorEditSubmitButton.dataset.iftarSponsorEditSubmit ?? "",
          sponsorEditSubmitButton.closest<HTMLElement>("[data-iftar-transfer-row]") ?? undefined,
        );
        return;
      }

      const deletePromptButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsorship-delete-prompt]",
      );
      if (deletePromptButton && appRoot.contains(deletePromptButton)) {
        event.preventDefault();
        state.iftarSponsorshipDeletePromptId =
          deletePromptButton.dataset.iftarSponsorshipDeletePrompt ?? "";
        state.iftarSponsorshipEditPromptId = "";
        state.iftarSponsorshipTransferPromptId = "";
        render();
        return;
      }

      const deleteCancelButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsorship-delete-cancel]",
      );
      if (deleteCancelButton && appRoot.contains(deleteCancelButton)) {
        event.preventDefault();
        if (
          state.iftarSponsorshipDeletePromptId ===
          (deleteCancelButton.dataset.iftarSponsorshipDeleteCancel ?? "")
        ) {
          state.iftarSponsorshipDeletePromptId = "";
        }
        render();
        return;
      }

      const deleteConfirmButton = target.closest<HTMLButtonElement>(
        "[data-iftar-sponsorship-delete-confirm]",
      );
      if (deleteConfirmButton && appRoot.contains(deleteConfirmButton)) {
        event.preventDefault();
        void deleteIftarSponsorshipFromAdmin(
          deleteConfirmButton.dataset.iftarSponsorshipDeleteConfirm ?? "",
        );
      }
    });
  }
  appRoot
    .querySelector<HTMLFormElement>("#iftar-slot-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveIftarSlotFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-iftar-slot-form")
    ?.addEventListener("click", () => {
      clearIftarSlotEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#announcement-composer-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveAnnouncementFromComposer());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-announcement-form")
    ?.addEventListener("click", () => {
      clearAnnouncementComposer();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-announcement-create]").forEach((button) => {
    button.addEventListener("click", () => {
      openNewAnnouncementComposer();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-announcement-read]").forEach((button) => {
    button.addEventListener("click", () => {
      openAnnouncementDetail(button.dataset.announcementRead ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-announcement-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      openAnnouncementComposerForEdit(button.dataset.announcementEdit ?? "");
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-announcement-status-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const announcementId = button.dataset.announcementStatusId ?? "";
        const status = button.dataset.announcementStatusAction;

        if (status === "published" || status === "archived") {
          void requestAnnouncementStatusChange(announcementId, status);
        }
      });
    });
  appRoot
    .querySelector<HTMLFormElement>("#imam-content-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveImamContentFromEditor());
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-imam-content-create]").forEach((button) => {
    button.addEventListener("click", () => {
      openNewImamContentEditor();
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-imam-content-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      openImamContentEditor(button.dataset.imamContentEdit ?? "");
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-ramadan-featured-content-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.ramadanFeaturedContentAction;
        if (action === "create") {
          openNewImamContentEditor("ramadan_message");
          return;
        }
        if (action === "edit") {
          openImamContentEditor(button.dataset.contentId ?? "");
          return;
        }
        if (action === "clear") {
          state.ramadanSetupEditor.featuredRamadanContentId = "";
          render();
        }
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-imam-content-status-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const contentId = button.dataset.imamContentStatusId ?? "";
        const action = button.dataset.imamContentStatusAction;
        if (action === "published" || action === "archived") {
          void requestImamContentStatusChange(contentId, action);
        }
      });
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-imam-content-delete]").forEach((button) => {
    button.addEventListener("click", () => {
      void requestImamContentDelete(button.dataset.imamContentDelete ?? "");
    });
  });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-imam-content-form")
    ?.addEventListener("click", () => {
      clearImamContentEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#space-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveSpaceFromEditor());
    });
  appRoot.querySelector<HTMLButtonElement>("#reset-space-form")?.addEventListener("click", () => {
    clearSpaceEditor();
    state.busy = false;
    state.error = "";
    render();
  });
  appRoot
    .querySelector<HTMLFormElement>("#space-availability-block-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveSpaceAvailabilityBlockFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-space-availability-block-form")
    ?.addEventListener("click", () => {
      clearSpaceAvailabilityBlockEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#space-slot-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveSpaceSlotFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-space-slot-form")
    ?.addEventListener("click", () => {
      clearSpaceSlotEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#organization-profile-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveOrganizationProfileFromEditor());
    });
  appRoot
    .querySelector<HTMLFormElement>("#organization-prayer-settings-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveOrganizationPrayerSettingsFromEditor());
    });
  appRoot
    .querySelector<HTMLFormElement>("#organization-islamic-components-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveOrganizationIslamicComponentsFromEditor());
    });
  appRoot
    .querySelector<HTMLFormElement>("#business-affiliation-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveBusinessAffiliationFromForm());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-organization-profile-form")
    ?.addEventListener("click", () => {
      clearOrganizationProfileEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-organization-prayer-settings-form")
    ?.addEventListener("click", () => {
      clearOrganizationPrayerSettingsEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-organization-islamic-components-form")
    ?.addEventListener("click", () => {
      resetOrganizationIslamicComponentsEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#volunteer-opportunity-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveVolunteerOpportunityFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-volunteer-opportunity-form")
    ?.addEventListener("click", () => {
      resetVolunteerOpportunityEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelector<HTMLFormElement>("#volunteer-shift-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveVolunteerShiftFromEditor());
    });
  appRoot
    .querySelector<HTMLButtonElement>("#reset-volunteer-shift-form")
    ?.addEventListener("click", () => {
      resetVolunteerShiftEditor();
      state.busy = false;
      state.error = "";
      render();
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-volunteer-opportunity-edit]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const opportunity = state.volunteerOpportunities.find(
          (candidate) => candidate.opportunityId === button.dataset.volunteerOpportunityEdit,
        );
        if (opportunity) {
          state.volunteerOpportunityEditor = volunteerOpportunityEditorInputFromRow(opportunity);
          activeTaskWindowId = "volunteer-opportunity-editor-form";
          render();
        }
      });
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-volunteer-shift-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      const [opportunityId, shiftId] = (button.dataset.volunteerShiftEdit ?? "").split("/");
      const shift = state.volunteerShifts.find(
        (candidate) => candidate.opportunityId === opportunityId && candidate.shiftId === shiftId,
      );
      if (shift) {
        state.volunteerShiftEditor = volunteerShiftEditorInputFromRow(shift);
        activeTaskWindowId = "volunteer-shift-editor-form";
        render();
      }
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-volunteer-signup-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        if (button.dataset.volunteerSignupAction === "cancel") {
          void adminCancelVolunteerSignupFromButton(button);
        }
      });
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-volunteer-attendance-action]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        if (button.dataset.volunteerAttendanceAction === "correct") {
          void adminVolunteerCorrectionFromButton(button);
          return;
        }
        void adminVolunteerAttendanceFromButton(button);
      });
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-connection-decision]").forEach((button) => {
    button.addEventListener("click", () => {
      const decision = button.dataset.connectionDecision === "reject" ? "reject" : "approve";
      const targetRequestId = button.dataset.connectionRequestId ?? "";
      void requestConnectionDecision({ decision, targetRequestId });
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-connection-request-filter]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const filter = button.dataset.connectionRequestFilter ?? "";
        if (!isConnectionRequestFilter(filter) || filter === state.connectionRequestFilter) {
          return;
        }
        state.connectionRequestFilter = filter;
        render();
      });
    });
  appRoot
    .querySelector<HTMLInputElement>("#people-list-search")
    ?.addEventListener("input", (event) => {
      state.peopleListSearch = (event.target as HTMLInputElement).value;
      render();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-people-view]").forEach((button) => {
    button.addEventListener("click", () => {
      openPeopleRoleWindow(button.dataset.peopleView ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-people-edit]").forEach((button) => {
    button.addEventListener("click", () => {
      openPeopleRoleWindow(button.dataset.peopleEdit ?? "");
    });
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-people-disable]").forEach((button) => {
    button.addEventListener("click", () => {
      void disablePeopleAccountFromButton(button);
    });
  });
  appRoot
    .querySelector<HTMLFormElement>("#people-role-editor-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => savePeopleRolesFromForm(event.currentTarget as HTMLFormElement));
    });
  appRoot
    .querySelector<HTMLFormElement>("#role-management-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      const action = submitter?.dataset.roleAction === "revoke" ? "revoke" : "assign";
      void changeOrganizationRoleFromForm(action);
    });
  appRoot.querySelector<HTMLSelectElement>("#locale")?.addEventListener("change", (event) => {
    state.locale = ((event.target as HTMLSelectElement).value as LocaleCode) || "en";
    render();
  });
  appRoot.querySelector<HTMLSelectElement>("#identity")?.addEventListener("change", (event) => {
    state.userUid = (event.target as HTMLSelectElement).value;
    void switchContext();
  });
  appRoot.querySelector<HTMLSelectElement>("#organization")?.addEventListener("change", (event) => {
    selectAuthorizedOrganization((event.target as HTMLSelectElement).value);
  });
  appRoot.querySelector<HTMLButtonElement>("#update")?.addEventListener("click", () => {
    void updateStatus();
  });
  appRoot.querySelector<HTMLButtonElement>("#publish-event")?.addEventListener("click", () => {
    void publishEvent();
  });
  appRoot.querySelector<HTMLButtonElement>("#create-donation")?.addEventListener("click", () => {
    void createDonation();
  });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-refund-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      void requestDonationRefundFromButton(button);
    });
  });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-reconcile-transaction-id]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void runCommerceReconciliation(button.dataset.reconcileTransactionId ?? null);
      });
    });
  appRoot
    .querySelector<HTMLButtonElement>("#run-commerce-reconciliation")
    ?.addEventListener("click", () => {
      void runCommerceReconciliation();
    });
  appRoot
    .querySelector<HTMLFormElement>("#giving-fund-form")
    ?.addEventListener("submit", (event) => {
      submitTaskForm(event, () => saveGivingFundFromForm(event.currentTarget as HTMLFormElement));
    });
  appRoot.querySelector<HTMLFormElement>("#campaign-form")?.addEventListener("submit", (event) => {
    submitTaskForm(event, () => saveCampaignFromForm(event.currentTarget as HTMLFormElement));
  });
  appRoot
    .querySelector<HTMLButtonElement>("#start-stripe-onboarding")
    ?.addEventListener("click", () => {
      void startStripeConnectOnboarding();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#refresh-stripe-connect")
    ?.addEventListener("click", () => {
      void refreshStripeConnectStatus();
    });
  appRoot.querySelector<HTMLButtonElement>("#reserve-iftar")?.addEventListener("click", () => {
    void reserveIftarSlot();
  });
  appRoot.querySelector<HTMLButtonElement>("#publish-space")?.addEventListener("click", () => {
    void publishSpace();
  });
  appRoot.querySelector<HTMLButtonElement>("#reserve-space")?.addEventListener("click", () => {
    void reserveSpace();
  });
  appRoot
    .querySelector<HTMLButtonElement>("#publish-announcement")
    ?.addEventListener("click", () => {
      void publishAnnouncement();
    });
  appRoot
    .querySelector<HTMLButtonElement>("#request-intelligence")
    ?.addEventListener("click", () => {
      void requestIntelligenceSnapshot();
    });
  scheduleRenderScrollRestore(renderScrollSnapshot, {
    forcePageRestore: shouldRestorePageScrollAfterRender(renderScrollSnapshot),
    afterRestore: options.afterScrollRestore,
  });
}

function renderSupportBackButton(kind: "member" | "shdeed"): string {
  return `<button class="secondary-action" type="button" data-support-back="${kind}">${state.locale === "ar" ? "العودة إلى الطلبات" : "Back to tickets"}</button>`;
}

function renderSupportNavigation(): void {
  render({
    afterScrollRestore: () => {
      const target = document.querySelector<HTMLElement>(
        ".workspace-page.is-active [data-support-detail-heading], .workspace-page.is-active .list-search input",
      );
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ block: "center" });
    },
  });
}

function bindSupportControls(appRoot: HTMLElement): void {
  appRoot.querySelectorAll<HTMLButtonElement>("[data-support-back]").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.supportBack === "member") {
        state.memberSupportSelectedTicketId = "";
        connectMemberSupportMessages();
      } else {
        state.shdeedSupportSelectedTicketId = "";
        connectShdeedSupportMessages();
      }
      renderSupportNavigation();
    });
  });
  appRoot
    .querySelector<HTMLInputElement>("#member-support-search")
    ?.addEventListener("input", (event) => {
      state.memberSupportSearch = (event.target as HTMLInputElement).value;
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#member-support-status-filter")
    ?.addEventListener("change", (event) => {
      state.memberSupportStatusFilter = supportStatusFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#member-support-category-filter")
    ?.addEventListener("change", (event) => {
      state.memberSupportCategoryFilter = (event.target as HTMLSelectElement).value || "all";
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#member-support-priority-filter")
    ?.addEventListener("change", (event) => {
      state.memberSupportPriorityFilter = supportPriorityFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#member-support-assignment-filter")
    ?.addEventListener("change", (event) => {
      state.memberSupportAssignmentFilter = supportAssignmentFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-member-support-select]").forEach((button) => {
    button.addEventListener("click", () => {
      state.memberSupportSelectedTicketId = button.dataset.memberSupportSelect ?? "";
      connectMemberSupportMessages();
      void loadOrganizationSupportStaff();
      renderSupportNavigation();
    });
  });
  appRoot
    .querySelector<HTMLFormElement>("#member-support-update-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      submitTaskForm(event, () =>
        updateMemberSupportTicketFromForm(event.currentTarget as HTMLFormElement),
      );
    });
  appRoot
    .querySelector<HTMLFormElement>("#member-support-reply-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      submitTaskForm(event, () =>
        replyMemberSupportTicketFromForm(event.currentTarget as HTMLFormElement),
      );
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-member-support-assign-self]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void assignMemberSupportTicketToSelf(button.dataset.memberSupportAssignSelf ?? "");
      });
    });

  appRoot
    .querySelector<HTMLInputElement>("#shdeed-support-search")
    ?.addEventListener("input", (event) => {
      state.shdeedSupportSearch = (event.target as HTMLInputElement).value;
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#shdeed-support-status-filter")
    ?.addEventListener("change", (event) => {
      state.shdeedSupportStatusFilter = supportStatusFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#shdeed-support-category-filter")
    ?.addEventListener("change", (event) => {
      state.shdeedSupportCategoryFilter = (event.target as HTMLSelectElement).value || "all";
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#shdeed-support-priority-filter")
    ?.addEventListener("change", (event) => {
      state.shdeedSupportPriorityFilter = supportPriorityFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot
    .querySelector<HTMLSelectElement>("#shdeed-support-assignment-filter")
    ?.addEventListener("change", (event) => {
      state.shdeedSupportAssignmentFilter = supportAssignmentFilterFromValue(
        (event.target as HTMLSelectElement).value,
      );
      render();
    });
  appRoot.querySelectorAll<HTMLButtonElement>("[data-shdeed-support-select]").forEach((button) => {
    button.addEventListener("click", () => {
      state.shdeedSupportSelectedTicketId = button.dataset.shdeedSupportSelect ?? "";
      connectShdeedSupportMessages();
      renderSupportNavigation();
    });
  });
  appRoot
    .querySelector<HTMLFormElement>("#shdeed-support-create-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      submitTaskForm(event, () =>
        createShdeedSupportTicketFromForm(event.currentTarget as HTMLFormElement),
      );
    });
  appRoot
    .querySelector<HTMLFormElement>("#shdeed-support-reply-form")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      submitTaskForm(event, () =>
        replyShdeedSupportTicketFromForm(event.currentTarget as HTMLFormElement),
      );
    });
  appRoot
    .querySelectorAll<HTMLButtonElement>("[data-support-attachment-path]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        void openSupportAttachmentPath(button.dataset.supportAttachmentPath ?? "");
      });
    });
}

function supportStatusFilterFromValue(value: string): SupportStatusFilter {
  return (supportTicketStatuses as readonly string[]).includes(value)
    ? (value as SupportTicketStatus)
    : "all";
}

function supportPriorityFilterFromValue(value: string): SupportPriorityFilter {
  return (supportTicketPriorities as readonly string[]).includes(value)
    ? (value as SupportTicketPriority)
    : "all";
}

function supportAssignmentFilterFromValue(value: string): SupportAssignmentFilter {
  return value === "assigned" || value === "unassigned" || value === "mine" ? value : "all";
}

const unsubscribeAuthentication = authenticationService.watchAuthState(
  (user) => {
    const previousUid =
      authenticationState.status === "authenticated" ? authenticationState.user.uid : undefined;
    if (state.organizationId && (!user || previousUid !== user.uid)) {
      retireOrganizationContext();
      return;
    }
    if (!user || previousUid !== user.uid) {
      resetOperationalModeEditor();
      unsubscribeModeWorkspace?.();
      unsubscribeModeWorkspace = undefined;
    }
    if (user && !useEmulator && isDevelopmentOnlyAuthenticationUser(user, projectId)) {
      const message =
        state.locale === "ar"
          ? "انتهت جلسة الاختبار المحلية. سجّل الدخول بحساب Google الإداري المصرّح به."
          : "The local test session has been removed. Sign in with your authorized Google administrator account.";
      authenticationSafetyMessage = message;
      authenticationState = authenticationStateFromUser(undefined);
      authenticationForm.error = message;
      currentPersonRequestId += 1;
      currentPersonState = { status: "idle" };
      accountProfileEditorOpen = false;
      void authenticationService.signOut().catch((error) => {
        authenticationSafetyMessage = "";
        authenticationForm.error = authErrorText(toAuthenticationError(error));
        render();
      });
      render();
      return;
    }

    authenticationState = authenticationStateFromUser(user);
    authenticationForm.error = authenticationSafetyMessage;
    authenticationSafetyMessage = "";
    if (user) {
      void resolveCurrentPerson(user.uid);
    } else {
      unsubscribeOrganizationAccess?.();
      unsubscribeOrganizationAccess = undefined;
      organizationAccess = { organizations: [], loading: false, error: "" };
      currentPersonRequestId += 1;
      currentPersonState = { status: "idle" };
      accountProfileEditorOpen = false;
    }
    render();
  },
  (error) => {
    if (state.organizationId) {
      retireOrganizationContext();
      return;
    }
    resetOperationalModeEditor();
    unsubscribeModeWorkspace?.();
    unsubscribeModeWorkspace = undefined;
    authenticationState = { status: "error", error };
    currentPersonRequestId += 1;
    currentPersonState = { status: "idle" };
    accountProfileEditorOpen = false;
    render();
  },
);

window.addEventListener("hashchange", () => {
  render();
});

document.addEventListener("focusout", () => {
  queueMicrotask(() => {
    realtimeRenderer.resume();
  });
});

document.addEventListener("visibilitychange", () => realtimeRenderer.resume());

render();

window.addEventListener("beforeunload", () => {
  realtimeRenderer.dispose();
  unsubscribeOrganizationAccess?.();
  unsubscribeIftarSponsorshipDefaults?.();
  unsubscribeIftarAttendanceDefaults?.();
  tenantListeners.dispose();
  unsubscribeModeWorkspace?.();
  unsubscribeAuthentication();
  unsubscribeVolunteerOpportunities?.();
  unsubscribeVolunteerShiftListeners();
  unsubscribeVolunteerSignupListeners();
  unsubscribeVolunteerAttendanceListeners();
  unsubscribeVolunteerSummaryListeners();
});
